// This file is auto-generated by @hey-api/openapi-ts

export type ClientOptions = {
    baseURL: 'https://get.heyapi.dev' | (string & {});
};

/**
 * AccessToken
 */
export type AccessToken = {
    /**
     * Access Token
     */
    access_token: string;
    /**
     * Refresh Token
     */
    refresh_token: string;
    /**
     * Token Type
     */
    token_type: 'bearer';
};

/**
 * ActivateExecutivePayload
 */
export type ActivateExecutivePayload = {
    /**
     * Login Email
     *
     * Login email for platform access. Defaults to candidate's email if not provided.
     */
    login_email?: string | null;
    /**
     * Silent
     *
     * When true, create DB rows only — no Clerk invite, no email. Use for pre-launch activation; flip individual invitations later via the send-invitation endpoint.
     */
    silent?: boolean;
};

/**
 * ActivityEventResponse
 */
export type ActivityEventResponse = {
    /**
     * Action
     *
     * Which activity event was performed?
     */
    action: EvaluationAction;
    /**
     * Advisor
     *
     * If this activity event was made on behalf of person, advisor is who made it
     */
    advisor?: PersonBasicResponse | null;
    /**
     * Candidate
     *
     * Candidate for which this activity event was made
     */
    candidate: UuidResponse;
    /**
     * Created
     *
     * Date when this activity event was created
     */
    created: string;
    /**
     * Activity event ID
     *
     * The unique identifier for this activity event
     */
    id: number;
    /**
     * Last modified
     *
     * Date when this activity event was modified
     */
    last_modified: string;
    /**
     * Person
     *
     * Person who performed this activity event
     */
    person?: PersonBasicResponse | null;
    /**
     * Pipeline
     *
     * Pipeline in which this activity event was made
     */
    pipeline?: UuidResponse | null;
    /**
     * Recommendation
     *
     * Recommendation for which this activity event was made
     */
    recommendation?: UuidResponse | null;
    /**
     * Team
     *
     * Team in which this activity event was made
     */
    team: UuidResponse;
};

/**
 * ActivityFeedCompanyRef
 *
 * A company reference on a B4B-source synthetic.
 *
 * Populated on ``kind=pipeline`` / ``kind=trending`` / ``kind=global_intro``
 * — the parent company of the introducing search team. The pipeline's
 * own name is deliberately *not* exposed to the exec (it is internal
 * B4B-side terminology); the exec sees the company that introduced
 * them instead.
 *
 * ``Company`` uses a bigint primary key (no ``uuid`` column), so the
 * routing identity is the ``slug``.
 */
export type ActivityFeedCompanyRef = {
    /**
     * Id
     */
    id: number;
    /**
     * Name
     */
    name?: string | null;
    /**
     * Slug
     */
    slug?: string | null;
};

/**
 * ActivityFeedEventRef
 *
 * An event reference on a ``kind=event`` synthetic.
 */
export type ActivityFeedEventRef = {
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Title
     */
    title?: string | null;
    /**
     * Event Date
     */
    event_date?: string | null;
};

/**
 * ActivityFeedItem
 *
 * A single row in the unified exec activity feed.
 *
 * Mixes `CandidateActivityLog` rows (kind="activity_log") with synthetic
 * connection-source rows derived from Event / Peer / SubmissionCandidate /
 * Referrer joins. Activity-log items carry the rich fields (actor,
 * candidate, message, event_type); connection-source items carry
 * structured ``contact`` / ``company`` / ``event_ref`` refs.
 *
 * Message composition is the webapp's responsibility — backend exposes
 * only the components (ids, names, slugs) and the webapp builds the
 * localized string with linkable participants from i18n keys.
 */
export type ActivityFeedItem = {
    /**
     * Kind
     */
    kind: 'activity_log' | 'event' | 'peer' | 'pipeline' | 'trending' | 'global_intro' | 'referral' | 'comment';
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Date
     */
    date: string;
    /**
     * Event Type
     */
    event_type?: string | null;
    actor?: PersonBasicResponse | null;
    candidate?: CandidateBasicResponse | null;
    message?: ActivityLogMessage | null;
    /**
     * Reference Type
     */
    reference_type?: string | null;
    /**
     * Reference Id
     */
    reference_id?: string | null;
    contact?: ActivityFeedParticipant | null;
    company?: ActivityFeedCompanyRef | null;
    event_ref?: ActivityFeedEventRef | null;
    referrer?: ActivityFeedParticipant | null;
    referred?: ActivityFeedParticipant | null;
    /**
     * Content
     */
    content?: string | null;
};

/**
 * ActivityFeedParticipant
 *
 * A participant on a synthetic activity-feed row.
 *
 * Carries just enough identity for the webapp to render a clickable link
 * to the contact's profile and compose an i18n message. ``person_id``
 * routes to ``/people`` (B4B user) or ``/users`` (exec); ``candidate_uuid``
 * routes to ``/candidates``. Both may be set when the participant is a
 * candidate who also has a Person record.
 */
export type ActivityFeedParticipant = {
    /**
     * Person Id
     */
    person_id?: string | null;
    /**
     * Candidate Uuid
     */
    candidate_uuid?: string | null;
    /**
     * Name
     */
    name?: string | null;
};

/**
 * ActivityLogEventType
 */
export const ActivityLogEventType = { PIPELINE_CREATED: 'PIPELINE_CREATED', COMMENT_ADDED_PIPELINE: 'COMMENT_ADDED_PIPELINE' } as const;

/**
 * ActivityLogEventType
 */
export type ActivityLogEventType = typeof ActivityLogEventType[keyof typeof ActivityLogEventType];

/**
 * ActivityLogMessage
 */
export type ActivityLogMessage = {
    /**
     * Action
     *
     * Action that was performed
     */
    action: string;
    /**
     * Count
     *
     * Count for when it is needed
     */
    count?: number | null;
    /**
     * Reference Date
     *
     * When the event is scheduled to happen or when it happened
     */
    reference_date?: string | null;
    /**
     * Reference Name
     *
     * Name of the reference entity
     */
    reference_name?: string | null;
    /**
     * Reference Type
     *
     * Type of the reference entity
     */
    reference_type?: string | null;
    /**
     * Company Name
     *
     * Company Name
     */
    company_name?: string | null;
    /**
     * Team Name
     *
     * Team name
     */
    team_name?: string | null;
    /**
     * Team Slug
     *
     * Team Slug
     */
    team_slug?: string | null;
    /**
     * Pipeline Slug
     *
     * Pipeline slug
     */
    pipeline_slug?: string | null;
    /**
     * External person info
     *
     * External person info
     */
    external_introduction_request_person?: ExternalPerson | null;
};

/**
 * ActivityTimePeriod
 */
export const ActivityTimePeriod = {
    PER_QUARTER: 'per_quarter',
    PER_YEAR: 'per_year',
    AS_NEEDED: 'as_needed'
} as const;

/**
 * ActivityTimePeriod
 */
export type ActivityTimePeriod = typeof ActivityTimePeriod[keyof typeof ActivityTimePeriod];

/**
 * AddGuestItem
 *
 * One guest to add to an event. Provide person_uuid, candidate_uuid, or standalone fields.
 */
export type AddGuestItem = {
    /**
     * Person UUID
     *
     * Link to a B4B user by person UUID
     */
    person_uuid?: string | null;
    /**
     * Candidate UUID
     *
     * Link to a candidate by candidate UUID
     */
    candidate_uuid?: string | null;
    /**
     * Guest First Name
     */
    guest_first_name?: string | null;
    /**
     * Guest Last Name
     */
    guest_last_name?: string | null;
    /**
     * Guest Role
     */
    guest_role?: string | null;
    /**
     * Guest Company
     */
    guest_company?: string | null;
    /**
     * Guest Email
     */
    guest_email?: string | null;
    /**
     * Status
     *
     * Guest RSVP status. Defaults to need_invite when omitted. Does not send emails by itself.
     */
    status?: EventGuestStatus | null;
    /**
     * Invite Sent At
     *
     * When the invite was sent (e.g. backfill). Guests with this set are skipped by bulk invite send; use with historical imports.
     */
    invite_sent_at?: string | null;
};

/**
 * AddGuestsPayload
 *
 * Payload for adding guests to an event.
 */
export type AddGuestsPayload = {
    /**
     * Guests
     *
     * List of guests to add
     */
    guests: Array<AddGuestItem>;
};

/**
 * AddGuestsResponse
 *
 * Response after adding guests to an event.
 */
export type AddGuestsResponse = {
    /**
     * Added
     *
     * The guests that were added (polymorphic on source type)
     */
    added: Array<EventGuestItemResponse>;
};

/**
 * AdminResumeView
 */
export type AdminResumeView = {
    /**
     * Awards And Honors
     */
    awards_and_honors?: Array<string>;
    /**
     * Candidate Image
     */
    candidate_image?: string | null;
    /**
     * Candidate Name
     */
    candidate_name: string;
    /**
     * Certifications
     */
    certifications?: Array<string>;
    contact: Contact;
    /**
     * Experience
     */
    experience?: Array<CompanyResponse>;
    header: ResumeHeader;
    /**
     * Is Client
     */
    is_client?: boolean;
    /**
     * Languages
     */
    languages?: Array<string>;
    /**
     * Last Updated
     */
    last_updated?: string | null;
    /**
     * Patents
     */
    patents?: Array<string>;
    /**
     * Profile Image Urn
     */
    profile_image_urn?: string | null;
    /**
     * Publications
     */
    publications?: Array<string>;
    /**
     * Referrals Only
     */
    referrals_only?: boolean;
    /**
     * Schools
     */
    schools?: Array<SchoolResponse>;
    /**
     * Skills
     */
    skills?: Array<string>;
    summary?: Summary | null;
    /**
     * Uuid
     */
    uuid: string;
    work_history?: CandidateExperience | null;
};

/**
 * AdminUserDetailedResponse
 *
 * Admin-only detailed user response including invitation history.
 */
export type AdminUserDetailedResponse = {
    /**
     * ID
     *
     * Same as uuid
     */
    id: string;
    /**
     * UUID
     *
     * UUID uniquely identifying a user
     */
    uuid: string;
    /**
     * Email
     *
     * User email address
     */
    email: string;
    /**
     * First Name
     *
     * User's first name
     */
    first_name?: string | null;
    /**
     * Last Name
     *
     * User's last name
     */
    last_name?: string | null;
    /**
     * Full Name
     *
     * User's full name
     */
    full_name?: string | null;
    /**
     * Company
     *
     * Company the user belongs to (includes payment status)
     */
    company?: CompanyExtendedResponse | null;
    /**
     * Created
     *
     * Timestamp when the user record was created
     */
    created: string;
    /**
     * First Login
     *
     * Timestamp when the user record was first logged in
     */
    first_login?: string | null;
    /**
     * Last Login
     *
     * Timestamp of user's last login
     */
    last_login?: string | null;
    /**
     * Active
     *
     * Whether the user is active
     */
    active: boolean;
    /**
     * Pending
     *
     * Whether the user's invite is still pending (no clerk_id)
     */
    pending: boolean;
    /**
     * Permit Preregistration Notifications
     *
     * Whether user can receive marketing emails before completing signup
     */
    permit_preregistration_notifications: boolean;
    /**
     * Notes External
     *
     * External notes for this user profile
     */
    notes_external?: string | null;
    /**
     * Notes Internal
     *
     * Internal notes for this user profile
     */
    notes_internal?: string | null;
    /**
     * LinkedIn URL
     *
     * LinkedIn URL for this user profile
     */
    linkedin_url?: string | null;
    /**
     * Wants Intros
     *
     * Whether this user currently wants intros
     */
    wants_intros?: boolean | null;
    /**
     * Wants Intro Followups
     *
     * Whether follow-up on intro requests is wanted internally
     */
    wants_intro_followups?: boolean | null;
    /**
     * Handle Intros As Exec
     *
     * Whether intro handling should be done as executive
     */
    handle_intros_as_exec?: boolean | null;
    /**
     * Teams Count
     *
     * Total number of teams the user belongs to
     */
    teams_count: number;
    /**
     * Teams
     *
     * List of teams the user belongs to
     */
    teams?: Array<UserTeamBasicResponse>;
    /**
     * Roles
     *
     * List of role objects the user has
     */
    roles?: Array<UserRoleResponse>;
    /**
     * Identity UUID
     *
     * UUID of the identity this login belongs to: the one row per real human that also holds the candidate, if there is one.
     */
    identity_uuid?: string | null;
    /**
     * Candidate UUID
     *
     * UUID of the candidate that shares this person's identity (executives and similar).
     */
    candidate_uuid?: string | null;
    /**
     * Candidate
     *
     * Basic profile of the candidate that shares this person's identity (executives and similar; same as candidate_uuid).
     */
    candidate?: CandidateBasicResponse | null;
    /**
     * User type
     *
     * executive/admin/enterprise
     */
    user_type: UserType;
    /**
     * Search Metadata
     *
     * Search result metadata, including highlighted fields
     */
    search_metadata?: EsMeta | null;
    /**
     * Invitations
     *
     * Invitation history records for this user
     */
    invitations?: Array<UserInvitationResponse>;
    /**
     * Undo executive activation blocker
     *
     * For an activated executive, why DELETE /api/v2/admin/executives/{uuid}/activation would be refused; null when it would succeed, and for anyone else
     */
    undo_executive_activation_blocker?: UndoExecutiveActivationBlocker | null;
};

/**
 * AllIntrosResponse
 */
export type AllIntrosResponse = {
    /**
     * Intro UUID
     *
     * UUID of the intro
     */
    uuid: string;
    /**
     * Intro type
     *
     * Type of the intro
     */
    intro_type: IntroTypeEnum;
    /**
     * Intro time
     *
     * Time intro is scheduled
     */
    time: string;
    /**
     * Candidate
     *
     * Information about the candidate
     */
    candidate: CandidateBasicResponse;
    /**
     * Target Candidate
     *
     * Information about the target candidate
     */
    candidate_to?: CandidateBasicResponse | null;
    /**
     * Team
     *
     * Information about the team
     */
    team?: TeamBasicUuidResponse | null;
};

/**
 * AnnualBudgetRange
 */
export const AnnualBudgetRange = {
    '<_100K': '<$100K',
    $100K_250K: '$100K-$250K',
    $250K_500K: '$250K-$500K',
    $500K_1M: '$500K-$1M',
    $1M_5M: '$1M-$5M',
    $5M_10M: '$5M-$10M',
    $10M_100M: '$10M-$100M',
    '>_100M': '>$100M'
} as const;

/**
 * AnnualBudgetRange
 */
export type AnnualBudgetRange = typeof AnnualBudgetRange[keyof typeof AnnualBudgetRange];

/**
 * AssetsManagedRange
 */
export const AssetsManagedRange = {
    '<_100M': '<$100M',
    $100M_1B: '$100M-$1B',
    $1B_10B: '$1B-$10B',
    '>_10B': '>$10B'
} as const;

/**
 * AssetsManagedRange
 */
export type AssetsManagedRange = typeof AssetsManagedRange[keyof typeof AssetsManagedRange];

/**
 * AutotagPreviewQueued
 */
export type AutotagPreviewQueued = {
    /**
     * Task Id
     */
    task_id: string;
    /**
     * Status
     */
    status?: string;
    /**
     * Status Url
     */
    status_url: string;
};

/**
 * AutotagPreviewRequest
 */
export type AutotagPreviewRequest = {
    /**
     * Call Notes
     */
    call_notes?: string | null;
    /**
     * Notion Page Id
     */
    notion_page_id?: string | null;
    /**
     * Email
     */
    email?: string | null;
    /**
     * Linkedin Url
     */
    linkedin_url?: string | null;
    /**
     * First Name
     */
    first_name?: string | null;
    /**
     * Last Name
     */
    last_name?: string | null;
    /**
     * Company
     */
    company?: string | null;
};

/**
 * AutotagPreviewStatus
 */
export type AutotagPreviewStatus = {
    /**
     * Task Id
     */
    task_id: string;
    /**
     * State
     */
    state: string;
    /**
     * Proposal
     */
    proposal?: {
        [key: string]: unknown;
    } | null;
    /**
     * Message
     */
    message?: string | null;
    /**
     * Error
     */
    error?: string | null;
};

/**
 * Body_post_candidates_import_from_csv
 */
export type BodyPostCandidatesImportFromCsv = {
    /**
     * File
     */
    file?: Blob | File | null;
};

/**
 * Body_post_event_landing_image
 */
export type BodyPostEventLandingImage = {
    /**
     * File
     *
     * Landing page hero image
     */
    file: Blob | File;
};

/**
 * Body_post_files_image
 */
export type BodyPostFilesImage = {
    /**
     * Image
     *
     * Image to use as a profile picture
     */
    file: Blob | File;
};

/**
 * Body_post_files_linkedin_resume
 */
export type BodyPostFilesLinkedinResume = {
    /**
     * Resume
     *
     * LinkedIn resume downloaded from the website in the .pdf format
     */
    file: Blob | File;
};

/**
 * Body_post_person_image
 */
export type BodyPostPersonImage = {
    /**
     * Image
     *
     * Image to use as a profile picture
     */
    image: Blob | File;
};

/**
 * Body_post_token
 */
export type BodyPostToken = {
    /**
     * Grant Type
     */
    grant_type: string;
    /**
     * Username
     */
    username?: string | null;
    /**
     * Password
     */
    password?: string | null;
    /**
     * Scope
     */
    scope?: string;
    /**
     * Client Id
     */
    client_id?: string | null;
    /**
     * Client Secret
     */
    client_secret?: string | null;
    /**
     * Expired Token
     */
    expired_token?: string | null;
    /**
     * Refresh Token
     */
    refresh_token?: string | null;
};

/**
 * Body_post_utils_mail_send
 */
export type BodyPostUtilsMailSend = {
    /**
     * Attachments
     */
    attachments?: Array<Blob | File> | null;
    /**
     * Recipients
     */
    recipients: string;
    /**
     * Template Id
     */
    template_id: string;
    /**
     * From Email
     */
    from_email: string;
    /**
     * From Name
     */
    from_name?: string | null;
    /**
     * Subject
     */
    subject?: string | null;
};

/**
 * BulkActivateExecutivePayload
 */
export type BulkActivateExecutivePayload = {
    /**
     * Candidate Uuids
     */
    candidate_uuids: Array<string>;
    /**
     * Silent
     */
    silent?: boolean;
};

/**
 * BulkActivateResponse
 */
export type BulkActivateResponse = {
    /**
     * Results
     */
    results: Array<BulkActivateResultItem>;
    /**
     * Activated Count
     */
    activated_count: number;
    /**
     * Error Count
     */
    error_count: number;
};

/**
 * BulkActivateResultItem
 */
export type BulkActivateResultItem = {
    /**
     * Candidate Uuid
     */
    candidate_uuid: string;
    /**
     * Status
     */
    status: string;
    /**
     * Error
     */
    error?: string | null;
    executive?: ExecutiveResponse | null;
};

/**
 * CandidateActivityEvent
 */
export type CandidateActivityEvent = {
    /**
     * Comment UUID
     *
     * UUID of the comment if present
     */
    comment_uuid?: string | null;
    /**
     * Event context
     *
     * Context of the event
     */
    context?: string | null;
    /**
     * External person
     *
     * External person related to this action
     */
    external_introduction_request_person?: ExternalPerson | null;
    /**
     * Event date
     *
     * The date of the event
     */
    date: string;
    /**
     * Event type
     *
     * Type of the event
     */
    event_type: CandidateActivityEventType;
    /**
     * Pipeline
     *
     * The related pipeline if present
     */
    pipeline?: PipelineBasicV2Response | null;
    /**
     * Recommendation uuid
     *
     * The related recommendation's uuid
     */
    recommendation_uuid?: string | null;
    /**
     * User
     *
     * Information about the person who made activity
     */
    user: PersonBasicResponse;
    /**
     * UUID
     *
     * Event UUID
     */
    uuid: string;
    /**
     * Team UUID
     *
     * Team UUID responsible for action
     */
    team_uuid?: string | null;
};

/**
 * CandidateActivityEventType
 */
export const CandidateActivityEventType = {
    COMMENT: 'comment',
    INTRODUCTION_FACILITATED: 'introduction_facilitated',
    INTRODUCTION_NOT_FACILITATED: 'introduction_not_facilitated',
    INTRODUCTION_REQUEST: 'introduction_request',
    NOT_LIKED: 'not_liked',
    NOT_LIKED_UNDO: 'not_liked_undo',
    RECOMMENDATION_ADDED: 'recommendation_added'
} as const;

/**
 * CandidateActivityEventType
 */
export type CandidateActivityEventType = typeof CandidateActivityEventType[keyof typeof CandidateActivityEventType];

/**
 * CandidateActivityLatestRecommendation
 */
export type CandidateActivityLatestRecommendation = {
    /**
     * Event date
     *
     * Latest intro completed or requested date
     */
    event_date: string;
    /**
     * Event type
     *
     * Event type
     */
    event_type: CandidateActivityLogEventType;
    /**
     * Recommendation UUID
     *
     * Recommendation UUID
     */
    recommendation_uuid: string;
    /**
     * Activity log ID
     *
     * Activity log ID
     */
    activity_log_id?: string | null;
};

/**
 * CandidateActivityLogEventType
 */
export const CandidateActivityLogEventType = {
    MODULE_CREATED: 'MODULE_CREATED',
    MODULE_COMPLETED: 'MODULE_COMPLETED',
    MODULE_DELETED: 'MODULE_DELETED',
    MODULE_COMPLETED_UNDO: 'MODULE_COMPLETED_UNDO',
    TRENDING_CAMPAIGN_SCHEDULED: 'TRENDING_CAMPAIGN_SCHEDULED',
    TRENDING_CAMPAIGN_SENT: 'TRENDING_CAMPAIGN_SENT',
    INTRO_REQUESTED_TRENDING: 'INTRO_REQUESTED_TRENDING',
    INTRO_COMPLETED_TRENDING: 'INTRO_COMPLETED_TRENDING',
    INTRO_NOT_FACILITATED_TRENDING: 'INTRO_NOT_FACILITATED_TRENDING',
    LIKED_TRENDING: 'LIKED_TRENDING',
    NOT_LIKED_TRENDING: 'NOT_LIKED_TRENDING',
    NOT_LIKED_UNDO_TRENDING: 'NOT_LIKED_UNDO_TRENDING',
    P2P_SCHEDULED: 'P2P_SCHEDULED',
    P2P_INTRO_MADE: 'P2P_INTRO_MADE',
    ADDED_TO_PIPELINE: 'ADDED_TO_PIPELINE',
    INTRO_REQUESTED_PIPELINE: 'INTRO_REQUESTED_PIPELINE',
    INTRO_COMPLETED_PIPELINE: 'INTRO_COMPLETED_PIPELINE',
    INTRO_NOT_REQUESTED_PIPELINE: 'INTRO_NOT_REQUESTED_PIPELINE',
    INTRO_NOT_FACILITATED_PIPELINE: 'INTRO_NOT_FACILITATED_PIPELINE',
    LIKED_PIPELINE: 'LIKED_PIPELINE',
    NOT_LIKED_PIPELINE: 'NOT_LIKED_PIPELINE',
    NOT_LIKED_UNDO_PIPELINE: 'NOT_LIKED_UNDO_PIPELINE',
    INTRO_REQUESTED_GLOBAL: 'INTRO_REQUESTED_GLOBAL',
    INTRO_COMPLETED_GLOBAL: 'INTRO_COMPLETED_GLOBAL',
    INTRO_NOT_FACILITATED_GLOBAL: 'INTRO_NOT_FACILITATED_GLOBAL'
} as const;

/**
 * CandidateActivityLogEventType
 */
export type CandidateActivityLogEventType = typeof CandidateActivityLogEventType[keyof typeof CandidateActivityLogEventType];

/**
 * CandidateActivityLogEventTypeHidden
 */
export const CandidateActivityLogEventTypeHidden = {
    P2P_APPROVED: 'P2P_APPROVED',
    P2P_DECLINED_ADVISOR: 'P2P_DECLINED_ADVISOR',
    P2P_INFO_NEEDED: 'P2P_INFO_NEEDED',
    P2P_PUBLISHED: 'P2P_PUBLISHED'
} as const;

/**
 * CandidateActivityLogEventTypeHidden
 */
export type CandidateActivityLogEventTypeHidden = typeof CandidateActivityLogEventTypeHidden[keyof typeof CandidateActivityLogEventTypeHidden];

/**
 * CandidateActivityLogGroupMember
 */
export type CandidateActivityLogGroupMember = {
    /**
     * Actor
     *
     * Actor
     */
    actor?: PersonBasicResponse | null;
    /**
     * Event date
     *
     * The date of the event
     */
    date: string;
    /**
     * Event Type
     *
     * Type of event associated with the log
     */
    event_type: CandidateActivityLogEventType | CandidateActivityLogEventTypeHidden;
    /**
     * UUID
     *
     * Event UUID
     */
    uuid: string;
    /**
     * Message
     *
     * Message associated with the event
     */
    message: ActivityLogMessage;
    /**
     * Reference ID
     *
     * UUID of the entity that triggered this event
     */
    reference_id?: string | null;
    /**
     * Reference Type
     *
     * Which entity triggered the event
     */
    reference_type?: CandidateActivityLogReferenceType | null;
    /**
     * Latest recommendation
     *
     * Latest recommendation activity log
     */
    latest_recommendation?: CandidateActivityLatestRecommendation | null;
};

/**
 * CandidateActivityLogPayload
 */
export type CandidateActivityLogPayload = {
    /**
     * Actor ID
     *
     * Person who triggered the event
     */
    actor_id?: string | null;
    /**
     * Event Type
     *
     * Type of event associated with the log
     */
    event_type: CandidateActivityLogEventType;
    /**
     * Reference ID
     *
     * UUID of the entity that triggered this event
     */
    reference_id?: string | null;
    /**
     * Reference Type
     *
     * Which entity triggered the event
     */
    reference_type?: CandidateActivityLogReferenceType | null;
};

/**
 * CandidateActivityLogReferenceGroups
 */
export type CandidateActivityLogReferenceGroups = {
    /**
     * Global
     *
     * global activity logs
     */
    global?: Array<CandidateActivityLogGroupMember> | null;
    /**
     * Peer
     *
     * p2p related activity logs
     */
    peer?: Array<CandidateActivityLogGroupMember> | null;
    /**
     * Pipeline
     *
     * pipeline related activity logs
     */
    pipeline?: Array<CandidateActivityLogGroupMember> | null;
    /**
     * Trending
     *
     * trending activity logs
     */
    trending?: Array<CandidateActivityLogGroupMember> | null;
};

/**
 * CandidateActivityLogReferenceType
 */
export const CandidateActivityLogReferenceType = {
    EVALUATION_ACTION: 'EVALUATION_ACTION',
    MODULE: 'MODULE',
    PEER: 'PEER',
    PIPELINE: 'PIPELINE',
    SUBMISSION: 'SUBMISSION'
} as const;

/**
 * CandidateActivityLogReferenceType
 */
export type CandidateActivityLogReferenceType = typeof CandidateActivityLogReferenceType[keyof typeof CandidateActivityLogReferenceType];

/**
 * CandidateActivityLogResponse
 */
export type CandidateActivityLogResponse = {
    /**
     * Actor
     *
     * Actor
     */
    actor?: PersonBasicResponse | null;
    /**
     * Candidate
     *
     * Information about the candidate
     */
    candidate: CandidateBasicResponse;
    /**
     * Event date
     *
     * The date of the event
     */
    date: string;
    /**
     * Event Type
     *
     * Type of event associated with the log
     */
    event_type: CandidateActivityLogEventType;
    /**
     * UUID
     *
     * Event UUID
     */
    uuid: string;
    /**
     * Message
     *
     * Message associated with the event
     */
    message: ActivityLogMessage;
    /**
     * Reference ID
     *
     * UUID of the entity that triggered this event
     */
    reference_id?: string | null;
    /**
     * Reference Type
     *
     * Which entity triggered the event
     */
    reference_type?: CandidateActivityLogReferenceType | null;
    /**
     * Recommendation UUID
     */
    recommendation_uuid?: string | null;
};

/**
 * CandidateActivityLogResponseWithHidden
 */
export type CandidateActivityLogResponseWithHidden = {
    /**
     * Actor
     *
     * Actor
     */
    actor?: PersonBasicResponse | null;
    /**
     * Candidate
     *
     * Information about the candidate
     */
    candidate: CandidateBasicResponse;
    /**
     * Event date
     *
     * The date of the event
     */
    date: string;
    /**
     * Event type
     *
     * Event type
     */
    event_type: CandidateActivityLogEventType | CandidateActivityLogEventTypeHidden;
    /**
     * UUID
     *
     * Event UUID
     */
    uuid: string;
    /**
     * Message
     *
     * Message associated with the event
     */
    message: ActivityLogMessage;
    /**
     * Reference ID
     *
     * UUID of the entity that triggered this event
     */
    reference_id?: string | null;
    /**
     * Reference Type
     *
     * Which entity triggered the event
     */
    reference_type?: CandidateActivityLogReferenceType | null;
    /**
     * Recommendation UUID
     */
    recommendation_uuid?: string | null;
    /**
     * Latest recommendation
     *
     * Latest recommendation activity log
     */
    latest_recommendation?: CandidateActivityLatestRecommendation | null;
};

/**
 * CandidateActivityLogTotals
 */
export type CandidateActivityLogTotals = {
    /**
     * all
     *
     * Total number of logs
     */
    all: number;
    /**
     * Global
     *
     * Total number of global logs
     */
    global?: number | null;
    /**
     * p2p
     *
     * Total number of P2P logs
     */
    peer?: number | null;
    /**
     * pipelines
     *
     * Total number of pipeline logs
     */
    pipelines?: number | null;
    /**
     * trending
     *
     * Total number of trending logs
     */
    trending?: number | null;
};

/**
 * CandidateAsyncImportResponse
 */
export type CandidateAsyncImportResponse = {
    /**
     * Detail
     *
     * Message indicating that the import process has successfully started
     */
    detail?: string;
    /**
     * File Name
     *
     * Name of the file that was uploaded for import
     */
    file_name?: string;
};

/**
 * CandidateBasicResponse
 */
export type CandidateBasicResponse = {
    /**
     * Image
     *
     * Path to candidate image
     */
    image?: string | null;
    /**
     * Name
     *
     * Candidate's full name
     */
    name: string;
    /**
     * UUID
     *
     * UUID uniquely identifying a person
     */
    uuid: string;
    /**
     * Profile Image Url
     */
    readonly profile_image_url: string | null;
};

/**
 * CandidateCommentary
 */
export type CandidateCommentary = {
    /**
     * Highlights
     *
     * List of highlights associated with the candidate
     */
    highlights: Array<CandidateHighlight>;
    /**
     * Introduction Templates
     *
     * List of introduction templates associated with the candidate
     */
    introduction_templates: Array<CandidateIntroductionTemplatePayload>;
};

/**
 * CandidateCommentaryResponseSchema
 */
export type CandidateCommentaryResponseSchema = {
    /**
     * Highlights
     *
     * List of highlights associated with the candidate
     */
    highlights: Array<Highlight>;
    /**
     * Introduction Templates
     *
     * List of introduction templates associated with the candidate
     */
    introduction_templates: Array<CandidateIntroductionTemplateResponse>;
};

/**
 * CandidateCompanyMetadataImportItem
 *
 * One metadata row to import. Frontend resolves ``candidate_uuid`` before upload.
 */
export type CandidateCompanyMetadataImportItem = {
    /**
     * Candidate Uuid
     *
     * Candidate UUID (resolved on the client)
     */
    candidate_uuid: string;
    /**
     * Custom Keywords
     *
     * Company tag labels for this candidate
     */
    custom_keywords?: Array<string>;
    /**
     * Custom Notes
     *
     * Free-text comments stored as custom_notes
     */
    custom_notes?: string | null;
};

/**
 * CandidateCompanyMetadataImportPayload
 */
export type CandidateCompanyMetadataImportPayload = {
    /**
     * Rows
     *
     * Parsed metadata rows from the client upload
     */
    rows: Array<CandidateCompanyMetadataImportItem>;
};

/**
 * CandidateCompanyMetadataImportResponse
 */
export type CandidateCompanyMetadataImportResponse = {
    total_counts: TotalCountsCandidateCompanyMetadataImportResponse;
    /**
     * Failed Rows
     */
    failed_rows?: Array<FailedCandidateCompanyMetadataRow>;
};

/**
 * CandidateCompanyMetadataPayload
 */
export type CandidateCompanyMetadataPayload = {
    /**
     * Custom Keywords
     *
     * Company shared tag labels for this candidate. Replaces all existing tag associations and syncs vocabulary (same behavior as member tag APIs).
     */
    custom_keywords?: Array<string>;
    /**
     * Custom Notes
     */
    custom_notes?: string | null;
};

/**
 * CandidateCompanyMetadataResponse
 */
export type CandidateCompanyMetadataResponse = {
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Candidate Uuid
     */
    candidate_uuid: string;
    /**
     * Company Id
     */
    company_id: number;
    /**
     * Custom Keywords
     */
    custom_keywords?: Array<string>;
    /**
     * Custom Notes
     */
    custom_notes?: string | null;
    /**
     * Created
     */
    created: string;
    /**
     * Last Modified
     */
    last_modified: string;
};

/**
 * CandidateCompanyMetadataWithCandidateResponse
 */
export type CandidateCompanyMetadataWithCandidateResponse = {
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Candidate Uuid
     */
    candidate_uuid: string;
    /**
     * Company Id
     */
    company_id: number;
    /**
     * Custom Keywords
     */
    custom_keywords?: Array<string>;
    /**
     * Custom Notes
     */
    custom_notes?: string | null;
    /**
     * Created
     */
    created: string;
    /**
     * Last Modified
     */
    last_modified: string;
    candidate: CompanyPeopleCandidateSummary;
};

/**
 * CandidateCompanyRoleResponse
 */
export type CandidateCompanyRoleResponse = {
    /**
     * Client
     *
     * Indicates whether a person is a Banff client
     */
    is_client: boolean;
    /**
     * Company
     *
     * Candidate's current company
     */
    company?: string | null;
    /**
     * Role
     *
     * Candidate's current role
     */
    role?: string | null;
    /**
     * Image
     *
     * Path to candidate image
     */
    image?: string | null;
    /**
     * Name
     *
     * Candidate's full name
     */
    name: string;
    /**
     * UUID
     *
     * UUID uniquely identifying a person
     */
    uuid: string;
    /**
     * Tier
     *
     * Candidate tier
     */
    tier: CandidateTier;
    /**
     * Profile Image Url
     */
    readonly profile_image_url: string | null;
};

/**
 * CandidateCompanyRoleWithAdvisorResponse
 */
export type CandidateCompanyRoleWithAdvisorResponse = {
    /**
     * Company
     *
     * Candidate's current company
     */
    company?: string | null;
    /**
     * Role
     *
     * Candidate's current role
     */
    role?: string | null;
    /**
     * Image
     *
     * Path to candidate image
     */
    image?: string | null;
    /**
     * Name
     *
     * Candidate's full name
     */
    name: string;
    /**
     * UUID
     *
     * UUID uniquely identifying a person
     */
    uuid: string;
    /**
     * Advisor
     *
     * Information about the advisor assigned to this candidate
     */
    advisor?: PersonBasicResponse | null;
    /**
     * First name
     *
     * Candidate's first name
     */
    first_name?: string | null;
    /**
     * Client
     *
     * Indicates whether a person is a Banff client
     */
    is_client: boolean;
    /**
     * Last name
     *
     * Candidate's last name
     */
    last_name?: string | null;
    /**
     * LinkedIn URL
     *
     * Link to the person's LinkedIn profile
     */
    linkedin_url?: string | null;
    /**
     * Profile Image Url
     */
    readonly profile_image_url: string | null;
};

/**
 * CandidateCompareBackgroundResponse
 */
export type CandidateCompareBackgroundResponse = {
    /**
     * Image
     *
     * Path to candidate image
     */
    image?: string | null;
    /**
     * Name
     *
     * Candidate's full name
     */
    name: string;
    /**
     * UUID
     *
     * UUID uniquely identifying a person
     */
    uuid: string;
    /**
     * Role
     *
     * Current role
     */
    role?: string | null;
    /**
     * Company
     *
     * Current company
     */
    company?: string | null;
    /**
     * Industry Expertise
     *
     * Industry experience
     */
    industry_expertise?: Array<Industry> | null;
    /**
     * Function Expertise
     *
     * Function expertise
     */
    function_expertise?: Array<Function> | null;
    /**
     * Interest role operating
     */
    interest_role_operating?: InterestRole | null;
    /**
     * Interest role board advisory
     */
    interest_role_board_advisory?: InterestRole | null;
    /**
     * Resume
     *
     * Response from the resume upload endpoint
     */
    resume?: LinkedInResumeResponse | null;
};

/**
 * CandidateComparePerformanceResponse
 */
export type CandidateComparePerformanceResponse = {
    /**
     * UUID
     *
     * Unique identifier for the candidate
     */
    candidate_uuid: string;
    /**
     * Full Name
     *
     * Candidate's full name
     */
    name: string;
    /**
     * Profile Image
     *
     * Path to candidate image
     */
    image?: string | null;
    /**
     * Completed Intros
     *
     * Number of completed introductions
     */
    intro_completed: number;
    /**
     * Intro Requests
     *
     * Number of introduction requests
     */
    intro_requests: number;
    /**
     * Not Interesting
     *
     * Number of not interesting marks
     */
    not_interesting: number;
    /**
     * Trendings
     *
     * Number of trendings
     */
    trendings: number;
    /**
     * Pipelines
     *
     * Number of pipelines
     */
    pipelines: number;
};

/**
 * CandidateCompareRecommendationResponse
 */
export type CandidateCompareRecommendationResponse = {
    /**
     * UUID
     *
     * Unique identifier for the candidate
     */
    candidate_uuid: string;
    /**
     * Full Name
     *
     * Candidate's full name
     */
    name: string;
    /**
     * Profile Image
     *
     * Path to candidate profile image
     */
    image?: string | null;
    /**
     * Teams
     *
     * Number of teams
     */
    teams_count: number;
    /**
     * Aggregations
     */
    aggregations: CandidateRecommendationAggregations;
    /**
     * Candidate Teams
     *
     * List of teams
     */
    candidate_teams: Array<CandidateTeamResponse>;
};

/**
 * CandidateExperience
 *
 * Attributes
 * ----------
 * current_company : str
 * the current company a candidate is employed in some capacity at
 * current_role : str
 * the current role a candidate occupies
 * prior_companies : list[PriorCompany]
 * these are the companies a candidate has worked at prior to their current
 * company
 */
export type CandidateExperience = {
    /**
     * Current Company
     */
    current_company?: string | null;
    /**
     * Current Role
     */
    current_role?: string | null;
    /**
     * Prior Companies
     */
    prior_companies?: Array<PriorCompany>;
};

/**
 * CandidateExtendedResponse
 */
export type CandidateExtendedResponse = {
    /**
     * Client
     *
     * Indicates whether a person is a Banff client
     */
    is_client: boolean;
    /**
     * Company
     *
     * Candidate's current company
     */
    company?: string | null;
    /**
     * Role
     *
     * Candidate's current role
     */
    role?: string | null;
    /**
     * Image
     *
     * Path to candidate image
     */
    image?: string | null;
    /**
     * Name
     *
     * Candidate's full name
     */
    name: string;
    /**
     * UUID
     *
     * UUID uniquely identifying a person
     */
    uuid: string;
    /**
     * First name
     *
     * Candidate's first name
     */
    first_name?: string | null;
    /**
     * Functional expertise
     *
     * List of functions candidate has experience in
     */
    function_expertise: Array<Function>;
    /**
     * Target functions
     *
     * Functional expertise candidate would like to get
     */
    function_target: Array<Function>;
    /**
     * Industry expertise
     *
     * List of industries candidate has experience in
     */
    industry_expertise: Array<Industry>;
    /**
     * Target industries
     *
     * Functional expertise candidate would like to get
     */
    industry_target: Array<Industry>;
    /**
     * Board/advisory role interest
     *
     * Interest in getting board or advisory roles
     */
    interest_role_board_advisory?: InterestRole | null;
    /**
     * Operating role interest
     *
     * Interest in getting operating roles
     */
    interest_role_operating?: InterestRole | null;
    /**
     * Personal interests
     *
     * List of personal interests
     */
    interests_personal: Array<InterestPersonal>;
    /**
     * Professional interests
     *
     * List of professional interests
     */
    interests_professional: Array<InterestProfessional>;
    /**
     * Keywords
     *
     * List of keywords
     */
    keywords: Array<string>;
    /**
     * Last name
     *
     * Candidate's last name
     */
    last_name?: string | null;
    /**
     * LinkedIn URL
     *
     * Link to the person's LinkedIn profile
     */
    linkedin_url?: string | null;
    /**
     * Tier
     *
     * Candidate tier
     */
    tier: CandidateTier;
    /**
     * Type
     *
     * Candidate type
     */
    type?: ClientType | null;
    /**
     * Pathway
     *
     * Banff service pathway for the candidate
     */
    pathway?: CandidatePathway | null;
    /**
     * Profile Image Url
     */
    readonly profile_image_url: string | null;
};

/**
 * CandidateExternalFullResponse
 */
export type CandidateExternalFullResponse = {
    /**
     * LinkedIn profile last synced
     *
     * Last time resume was checked against LinkedIn
     */
    __linkedin_profile_last_synced__DO_NOT_USE__?: string | null;
    /**
     * Manual last update
     *
     * This field is used only to calculate the profile_updated_date value
     */
    __manual_last_update__DO_NOT_USE__?: string | null;
    /**
     * Profile updated date
     *
     * Date when the candidate's profile was last updated
     */
    profile_updated_date?: string | null;
    /**
     * Resume last uploaded
     *
     * This field is used only to calculate the profile_updated_date value
     */
    __resume_last_uploaded__DO_NOT_USE__?: string | null;
    /**
     * Company
     *
     * Candidate's current company
     */
    company?: string | null;
    /**
     * Role
     *
     * Candidate's current role
     */
    role?: string | null;
    /**
     * Image
     *
     * Name of the uploaded image from the response
     */
    image?: string | null;
    /**
     * Client
     *
     * Indicates whether a person is a Banff client
     */
    is_client: boolean;
    /**
     * LinkedIn URL
     *
     * Link to the person's LinkedIn profile
     */
    linkedin_url?: string | null;
    /**
     * Name
     *
     * Candidate's full name
     */
    name: string;
    /**
     * UUID
     *
     * UUID uniquely identifying a candidate
     */
    uuid: string;
    /**
     * Formatted role
     *
     * Readable role combining current company and role
     */
    formatted_role?: string | null;
    /**
     * Location
     *
     * Candidate's location (from their resume)
     */
    location?: string | null;
    /**
     * Resume
     *
     * Response from the resume upload endpoint
     */
    resume?: LinkedInResumeResponse | null;
    /**
     * Identity UUID
     *
     * UUID of the identity this candidate belongs to: the key for the person page and for the team's contact comments
     */
    identity_uuid?: string | null;
    /**
     * Board/advisory role interest
     *
     * Interest in getting board or advisory roles
     */
    interest_role_board_advisory?: InterestRole | null;
    /**
     * Operating role interest
     *
     * Interest in getting operating roles
     */
    interest_role_operating?: InterestRole | null;
    /**
     * Functional expertise
     *
     * List of functions candidate has experience in
     */
    function_expertise: Array<Function>;
    /**
     * Industry expertise
     *
     * List of industries candidate has experience in
     */
    industry_expertise: Array<Industry>;
    /**
     * Operating Highlights
     *
     * List of operating highlights
     */
    operating_highlights: Array<OperatingHighlights>;
    /**
     * Source company tags
     *
     * List of custom B4B tags. Currently only available for Acrew
     */
    source_company_tags?: Array<SourceCompanyTags> | null;
    /**
     * Personal interests
     *
     * List of personal interests
     */
    interests_personal: Array<InterestPersonal>;
    /**
     * Professional interests
     *
     * List of professional interests
     */
    interests_professional: Array<InterestProfessional>;
    /**
     * Interests notes
     *
     * Notes on interests
     */
    interests_notes?: string | null;
    /**
     * Wants intros
     *
     * Whether the candidate wants introductions
     */
    wants_intros?: boolean | null;
    /**
     * Interest in relocation
     *
     * Openness to relocation
     */
    interest_relocation?: boolean | null;
    /**
     * Relocation notes
     *
     * Relocation preferences or constraints
     */
    interest_relocation_notes?: string | null;
};

/**
 * CandidateExternalFullResponseWithTeamContext
 *
 * Candidate response with team-specific context fields.
 *
 * This extends CandidateExternalFullResponse with fields that are only
 * meaningful in the context of a specific team. Use this schema for
 * endpoints that return a candidate in a team context.
 */
export type CandidateExternalFullResponseWithTeamContext = {
    /**
     * LinkedIn profile last synced
     *
     * Last time resume was checked against LinkedIn
     */
    __linkedin_profile_last_synced__DO_NOT_USE__?: string | null;
    /**
     * Manual last update
     *
     * This field is used only to calculate the profile_updated_date value
     */
    __manual_last_update__DO_NOT_USE__?: string | null;
    /**
     * Profile updated date
     *
     * Date when the candidate's profile was last updated
     */
    profile_updated_date?: string | null;
    /**
     * Resume last uploaded
     *
     * This field is used only to calculate the profile_updated_date value
     */
    __resume_last_uploaded__DO_NOT_USE__?: string | null;
    /**
     * Company
     *
     * Candidate's current company
     */
    company?: string | null;
    /**
     * Role
     *
     * Candidate's current role
     */
    role?: string | null;
    /**
     * Image
     *
     * Name of the uploaded image from the response
     */
    image?: string | null;
    /**
     * Client
     *
     * Indicates whether a person is a Banff client
     */
    is_client: boolean;
    /**
     * LinkedIn URL
     *
     * Link to the person's LinkedIn profile
     */
    linkedin_url?: string | null;
    /**
     * Name
     *
     * Candidate's full name
     */
    name: string;
    /**
     * UUID
     *
     * UUID uniquely identifying a candidate
     */
    uuid: string;
    /**
     * Formatted role
     *
     * Readable role combining current company and role
     */
    formatted_role?: string | null;
    /**
     * Location
     *
     * Candidate's location (from their resume)
     */
    location?: string | null;
    /**
     * Resume
     *
     * Response from the resume upload endpoint
     */
    resume?: LinkedInResumeResponse | null;
    /**
     * Identity UUID
     *
     * UUID of the identity this candidate belongs to: the key for the person page and for the team's contact comments
     */
    identity_uuid?: string | null;
    /**
     * Board/advisory role interest
     *
     * Interest in getting board or advisory roles
     */
    interest_role_board_advisory?: InterestRole | null;
    /**
     * Operating role interest
     *
     * Interest in getting operating roles
     */
    interest_role_operating?: InterestRole | null;
    /**
     * Functional expertise
     *
     * List of functions candidate has experience in
     */
    function_expertise: Array<Function>;
    /**
     * Industry expertise
     *
     * List of industries candidate has experience in
     */
    industry_expertise: Array<Industry>;
    /**
     * Operating Highlights
     *
     * List of operating highlights
     */
    operating_highlights: Array<OperatingHighlights>;
    /**
     * Source company tags
     *
     * List of custom B4B tags. Currently only available for Acrew
     */
    source_company_tags?: Array<SourceCompanyTags> | null;
    /**
     * Personal interests
     *
     * List of personal interests
     */
    interests_personal: Array<InterestPersonal>;
    /**
     * Professional interests
     *
     * List of professional interests
     */
    interests_professional: Array<InterestProfessional>;
    /**
     * Interests notes
     *
     * Notes on interests
     */
    interests_notes?: string | null;
    /**
     * Wants intros
     *
     * Whether the candidate wants introductions
     */
    wants_intros?: boolean | null;
    /**
     * Interest in relocation
     *
     * Openness to relocation
     */
    interest_relocation?: boolean | null;
    /**
     * Relocation notes
     *
     * Relocation preferences or constraints
     */
    interest_relocation_notes?: string | null;
    /**
     * Email
     *
     * Candidate's email address
     */
    email?: string | null;
    /**
     * Team UUID
     *
     * UUID of the team this candidate is viewed in context of
     */
    team_uuid: string;
    /**
     * Global introduction status
     *
     * Global introduction status between this candidate and the team
     */
    global_introduction_status?: IntroductionStatus | null;
    /**
     * Global introduction request person
     *
     * Person who requested the global introduction
     */
    global_introduction_request_person?: PersonBasicResponse | null;
    /**
     * Pipelines count
     *
     * Number of pipelines and Trending for this team where this candidate appears
     */
    pipelines_count?: number;
    /**
     * Date last recommended
     *
     * When was this candidate last recommended to the team?
     */
    date_recommended_last?: string | null;
    /**
     * Date introduction last facilitated
     *
     * When was this candidate last introduced to the team?
     */
    date_introduction_facilitated_last?: string | null;
    /**
     * Is favorite
     *
     * Whether the candidate is favorited by the current user
     */
    is_favorite?: boolean;
    /**
     * Company metadata
     *
     * Per-company custom metadata for this candidate, filtered to the team's company. Either 0 or 1 entry.
     */
    company_metadata?: Array<CandidateCompanyMetadataResponse>;
    /**
     * Personal tags
     *
     * Personal tags on this contact for the viewing context owner
     */
    personal_tags?: Array<PersonalContactTagItem>;
};

/**
 * CandidateExternalResponse
 */
export type CandidateExternalResponse = {
    /**
     * LinkedIn profile last synced
     *
     * Last time resume was checked against LinkedIn
     */
    __linkedin_profile_last_synced__DO_NOT_USE__?: string | null;
    /**
     * Manual last update
     *
     * This field is used only to calculate the profile_updated_date value
     */
    __manual_last_update__DO_NOT_USE__?: string | null;
    /**
     * Profile updated date
     *
     * Date when the candidate's profile was last updated
     */
    profile_updated_date?: string | null;
    /**
     * Resume last uploaded
     *
     * This field is used only to calculate the profile_updated_date value
     */
    __resume_last_uploaded__DO_NOT_USE__?: string | null;
    /**
     * Company
     *
     * Candidate's current company
     */
    company?: string | null;
    /**
     * Role
     *
     * Candidate's current role
     */
    role?: string | null;
    /**
     * Image
     *
     * Name of the uploaded image from the response
     */
    image?: string | null;
    /**
     * Client
     *
     * Indicates whether a person is a Banff client
     */
    is_client: boolean;
    /**
     * LinkedIn URL
     *
     * Link to the person's LinkedIn profile
     */
    linkedin_url?: string | null;
    /**
     * Name
     *
     * Candidate's full name
     */
    name: string;
    /**
     * UUID
     *
     * UUID uniquely identifying a candidate
     */
    uuid: string;
    /**
     * Formatted role
     *
     * Readable role combining current company and role
     */
    formatted_role?: string | null;
    /**
     * Location
     *
     * Candidate's location (from their resume)
     */
    location?: string | null;
    /**
     * Resume
     *
     * Response from the resume upload endpoint
     */
    resume?: LinkedInResumeResponse | null;
};

/**
 * CandidateFavoritePayload
 */
export type CandidateFavoritePayload = {
    /**
     * Is Favorite
     *
     * Does the user want to favorite this candidate?
     */
    is_favorite: boolean;
};

/**
 * CandidateFavoriteResponse
 */
export type CandidateFavoriteResponse = {
    /**
     * Id
     *
     * The candidate id
     */
    id: number;
    /**
     * Is favorite
     *
     * Whether the candidate is favorited by the current user
     */
    is_favorite: boolean;
    /**
     * UUID
     *
     * The candidate uuid
     */
    uuid: string;
};

/**
 * CandidateFeedbackFilterPayload
 */
export type CandidateFeedbackFilterPayload = {
    /**
     * Phrase
     *
     * Search phrase
     */
    phrase?: string | null;
    /**
     * Search ID
     *
     * Search session ID
     */
    search_id?: string | null;
    /**
     * Page
     *
     * Page number
     */
    page?: number;
    /**
     * Page size
     *
     * Page size
     */
    page_size?: number;
};

/**
 * CandidateFeedbackFilterResponse
 */
export type CandidateFeedbackFilterResponse = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Results
     */
    results: Array<CandidateFeedbackResult>;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCountsResponse;
};

/**
 * CandidateFeedbackPayload
 */
export type CandidateFeedbackPayload = {
    /**
     * Candidate UUID
     */
    candidate_uuid?: string | null;
    /**
     * Comment
     */
    comment: string;
    /**
     * Company ID
     */
    company_id?: number | null;
    /**
     * Company Name
     *
     * Name for the new company record
     */
    company_name?: string | null;
    /**
     * Person Email
     *
     * Email for the new person record
     */
    person_email?: string | null;
    /**
     * Person First Name
     *
     * First name for the new person record
     */
    person_first_name?: string | null;
    /**
     * Person Last Name
     *
     * Last name for the new person record
     */
    person_last_name?: string | null;
    /**
     * From Identity UUID
     *
     * Identity of the person whose feedback this is (a row from contacts/find)
     */
    from_identity_uuid?: string | null;
    /**
     * Person ID
     *
     * Deprecated: send from_identity_uuid. A person uuid, resolved to that person's identity.
     *
     * @deprecated
     */
    person_id?: string | null;
    /**
     * Recommendation UUID
     */
    recommendation_uuid?: string | null;
    /**
     * Team UUID
     */
    team_uuid?: string | null;
};

/**
 * CandidateFeedbackPersonResponse
 *
 * The commenter as displayed, filled from the from-identity: the candidate's
 * name when the identity has a candidate, else the earliest login's. ``uuid``
 * and ``email`` are null for a candidate without a login.
 */
export type CandidateFeedbackPersonResponse = {
    /**
     * Email
     */
    email?: string | null;
    /**
     * Name
     *
     * Person's full name
     */
    name: string;
    /**
     * First Name
     *
     * Person's first name
     */
    first_name?: string | null;
    /**
     * Last Name
     *
     * Person's last name
     */
    last_name?: string | null;
    /**
     * Image
     *
     * Path to person image
     */
    image?: string | null;
    /**
     * UUID
     */
    uuid?: string | null;
    /**
     * Company
     */
    company?: CompanyBasicTypeResponse | null;
    /**
     * Profile Image Url
     */
    readonly profile_image_url: string | null;
};

/**
 * CandidateFeedbackResult
 */
export type CandidateFeedbackResult = {
    /**
     * Advisor
     *
     * Advisor who added this feedback
     */
    advisor?: PersonBasicEmailResponse | null;
    /**
     * Candidate
     */
    candidate?: CandidateBasicResponse;
    /**
     * Comment
     */
    comment: string;
    /**
     * Company
     */
    company?: CompanyBasicTypeResponse | null;
    /**
     * Published On
     */
    published_on: string;
    /**
     * Person
     */
    person?: CandidateFeedbackPersonResponse | null;
    /**
     * Pipeline
     */
    pipeline?: PipelineBasicResponse | null;
    /**
     * Team
     */
    team?: TeamBasicUuidResponse | null;
    /**
     * Source
     */
    source: FeedbackSource;
    /**
     * Recommendation
     */
    recommendation?: EsRecommendationCandidateIndexResult | null;
    /**
     * Type
     */
    type: FeedbackType;
    /**
     * UUID
     */
    uuid: string;
    /**
     * From Identity UUID
     *
     * Identity of the person whose feedback this is (null for anonymous feedback)
     */
    from_identity_uuid?: string | null;
    /**
     * From Candidate UUID
     *
     * The commenter's candidate record, when their identity has one
     */
    from_candidate_uuid?: string | null;
    /**
     * Subject Identity UUID
     *
     * Identity of the person this feedback is about
     */
    subject_identity_uuid?: string | null;
};

/**
 * CandidateGoal
 */
export type CandidateGoal = {
    /**
     * Activities
     *
     * List of activities associated with the goal
     */
    activities?: Array<CandidateActivityLogResponseWithHidden> | null;
    /**
     * Completion date
     */
    completion_date?: string | null;
    /**
     * Created date
     */
    created: string;
    /**
     * Due date
     */
    due_date?: string | null;
    /**
     * Goal ID
     *
     * UUID uniquely identifying a goal
     */
    id: string;
    /**
     * Intro Stats
     */
    intro_stats?: Array<IntroStats> | null;
    /**
     * Priority
     *
     * Goal priority
     */
    priority?: GoalPriority | null;
    /**
     * Program
     *
     * Program type
     */
    program: CandidateType;
    /**
     * Notes
     *
     * Goal notes
     */
    notes?: Array<CandidateGoalNotes> | null;
    /**
     * Latest Note
     *
     * Latest note
     */
    latest_note: string | null;
    /**
     * Status
     *
     * Goal status
     */
    status: GoalStatus;
    /**
     * Tasks
     */
    tasks?: Array<CandidateGoalTask> | null;
    /**
     * Title
     *
     * Goal title
     */
    title: string;
    /**
     * Last modified
     *
     * Date when the goal was last modified
     */
    last_modified: string;
};

/**
 * CandidateGoalActivitiesPayload
 */
export type CandidateGoalActivitiesPayload = {
    /**
     * Activities UUIDs
     *
     * Activities associated with the goal
     */
    activities_uuids: Array<string> | null;
    /**
     * Reference ID
     *
     * Reference ID
     */
    reference_id?: string | null;
    /**
     * Reference Type
     *
     * Reference type
     */
    reference_type?: CandidateActivityLogReferenceType | null;
};

/**
 * CandidateGoalLegacy
 */
export type CandidateGoalLegacy = {
    /**
     * Type
     *
     * Type of goal
     */
    type: GoalType;
    /**
     * Text
     *
     * Goal text
     */
    text: string;
    /**
     * Last modified
     *
     * Date when the goal was last modified
     */
    last_modified: string;
};

/**
 * CandidateGoalNotes
 */
export type CandidateGoalNotes = {
    /**
     * Text
     *
     * Note text
     */
    text: string;
};

/**
 * CandidateGoalResponse
 */
export type CandidateGoalResponse = {
    /**
     * Candidate UUID
     *
     * UUID uniquely identifying a candidate
     */
    candidate_uuid: string;
    /**
     * Goals
     *
     * List of goals
     */
    goals: Array<CandidateGoalLegacy>;
};

/**
 * CandidateGoalTask
 */
export type CandidateGoalTask = {
    /**
     * Candidate
     */
    assignee: PersonBasicEmailResponse;
    /**
     * Created date
     */
    created: string;
    /**
     * Candidate
     */
    creator: PersonBasicEmailResponse;
    /**
     * Completion date
     */
    completion_date?: string | null;
    /**
     * Description
     */
    description?: string | null;
    /**
     * Due date
     */
    due_date?: string | null;
    /**
     * Task ID
     */
    id: string;
    /**
     * Is completed?
     */
    is_completed: boolean;
    /**
     * Last modified
     */
    last_modified: string;
    /**
     * Title
     */
    title: string;
    /**
     * Overdue
     */
    readonly overdue: boolean;
};

/**
 * CandidateGoalsLegacyPayload
 */
export type CandidateGoalsLegacyPayload = {
    /**
     * Long-term
     *
     * Candidate's long-term goals
     */
    long_term?: string | null;
    /**
     * Short-term
     *
     * Candidate's short-term goals
     */
    short_term?: string | null;
};

/**
 * CandidateGoalsPatchPayload
 */
export type CandidateGoalsPatchPayload = {
    /**
     * Due Date
     *
     * When is goal task due? Set to null to clear.
     */
    due_date?: string | unknown | null;
    /**
     * Notes
     */
    notes?: string | null;
    /**
     * Priority
     *
     * Priority of the goal
     */
    priority?: GoalPriority | null;
    /**
     * Program
     */
    program?: CandidateType | null;
    /**
     * Status
     *
     * Status of the goal
     */
    status?: GoalStatus | null;
    /**
     * Title
     */
    title?: string | null;
};

/**
 * CandidateGoalsPayload
 */
export type CandidateGoalsPayload = {
    /**
     * Due Date
     *
     * When is goal task due?
     */
    due_date?: string | null;
    /**
     * Notes
     */
    notes?: string | null;
    /**
     * Program
     */
    program: CandidateType;
    /**
     * Priority
     *
     * Priority of the goal
     */
    priority?: GoalPriority | null;
    /**
     * Status
     *
     * Status of the goal
     */
    status?: GoalStatus | null;
    /**
     * Tasks
     *
     * Tasks associated with the goal
     */
    tasks?: Array<TaskCreateUpdatePayload> | null;
    /**
     * Title
     */
    title: string;
};

/**
 * CandidateGoalsResponse
 */
export type CandidateGoalsResponse = {
    /**
     * Candidate UUID
     *
     * UUID uniquely identifying a candidate
     */
    candidate_uuid: string;
    /**
     * Goals
     *
     * List of goals
     */
    goals: Array<CandidateGoal>;
    /**
     * Total Goals
     */
    readonly total_goals: number;
};

/**
 * CandidateHighlight
 */
export type CandidateHighlight = {
    /**
     * Highlight
     *
     * Text describing a candidate highlight
     */
    highlight: string;
    /**
     * UUID
     *
     * UUID uniquely identifying a candidate highlight
     */
    uuid?: string | null;
};

/**
 * CandidateHighlightCreate
 */
export type CandidateHighlightCreate = {
    /**
     * Highlight
     *
     * Text describing a candidate highlight
     */
    highlight: string;
};

/**
 * CandidateHighlightResponse
 */
export type CandidateHighlightResponse = {
    /**
     * Highlight
     *
     * Text describing candidate's highlight
     */
    highlight: string;
    /**
     * Selected
     *
     * Was this highlight selected for this candidate submission?
     */
    selected: boolean;
    /**
     * UUID
     *
     * UUID uniquely identifying a highlight
     */
    uuid: string;
};

/**
 * CandidateInternalFullResponse
 */
export type CandidateInternalFullResponse = {
    /**
     * First Name
     *
     * Person's first name
     */
    first_name?: string | null;
    /**
     * Last Name
     *
     * Person's last name
     */
    last_name?: string | null;
    /**
     * Company
     *
     * Candidate's current company
     */
    company?: string | null;
    /**
     * Role
     *
     * Candidate's current role
     */
    role?: string | null;
    /**
     * Image
     *
     * Name of the uploaded image from the response
     */
    image?: string | null;
    /**
     * Client
     *
     * Indicates whether a person is a Banff client
     */
    is_client: boolean;
    /**
     * LinkedIn URL
     *
     * Link to the person's LinkedIn profile
     */
    linkedin_url?: string | null;
    /**
     * Name
     *
     * Candidate's full name
     */
    name: string;
    /**
     * UUID
     *
     * UUID uniquely identifying a candidate
     */
    uuid: string;
    /**
     * Active
     *
     * Is this candidate marked as active?
     */
    active: boolean;
    /**
     * Merged into
     *
     * Set when this record was merged into another: its rows live on that candidate now, and this one is only a signpost. The webapp redirects an old profile URL to it.
     */
    merged_into_uuid?: string | null;
    /**
     * Advisor
     *
     * Information about the advisor assigned to this candidate
     */
    advisor?: PersonBasicResponse | null;
    /**
     * Client owner
     *
     * Information about the client owner of the candidate
     */
    client_owner?: PersonBasicResponse | null;
    /**
     * URG
     *
     * Is this candidate from an under-represented group?
     */
    is_urg?: boolean | null;
    /**
     * Email
     *
     * Email associated with the person
     */
    email: string;
    /**
     * Functional expertise
     *
     * List of functions candidate has experience in
     */
    function_expertise: Array<Function>;
    /**
     * Target functions
     *
     * Functional expertise candidate would like to get
     */
    function_target: Array<Function>;
    /**
     * Long-term goals
     *
     * Long-term goals
     */
    long_term_goals: Array<string>;
    /**
     * ID
     *
     * ID uniquely identifying a candidate
     */
    id: number;
    /**
     * Person ID
     *
     * UUID of the candidate's executive login, if any (derived from the identity, not from the frozen person_id column)
     */
    person_id?: string | null;
    /**
     * Identity UUID
     *
     * UUID of the identity this candidate belongs to: the one row per real human that also holds the candidate's logins
     */
    identity_uuid?: string | null;
    /**
     * Industry expertise
     *
     * List of industries candidate has experience in
     */
    industry_expertise: Array<Industry>;
    /**
     * Target industries
     *
     * Functional expertise candidate would like to get
     */
    industry_target: Array<Industry>;
    /**
     * Board/advisory role interest
     *
     * Interest in getting board or advisory roles
     */
    interest_role_board_advisory?: InterestRole | null;
    /**
     * Operating role interest
     *
     * Interest in getting operating roles
     */
    interest_role_operating?: InterestRole | null;
    /**
     * Personal interests
     *
     * List of personal interests
     */
    interests_personal: Array<InterestPersonal>;
    /**
     * Professional interests
     *
     * List of professional interests
     */
    interests_professional: Array<InterestProfessional>;
    /**
     * Interests notes
     *
     * Notes on interests
     */
    interests_notes?: string | null;
    /**
     * Invoice Status
     *
     * HubSpot invoice status (admin-facing; tier 1/1b only)
     */
    invoice_status?: HubSpotInvoiceStatus | null;
    /**
     * HubSpot ID
     *
     * HubSpot contact id linked by the reconcile job (debugging)
     */
    hubspot_id?: string | null;
    /**
     * Wants intros
     *
     * Whether the candidate wants introductions
     */
    wants_intros?: boolean | null;
    /**
     * Interest in relocation
     *
     * Self-reported openness to relocation (external profile)
     */
    interest_relocation?: boolean | null;
    /**
     * Relocation notes
     *
     * Self-reported relocation preferences or constraints (external profile)
     */
    interest_relocation_notes?: string | null;
    /**
     * Introduction email type
     *
     * Introduction email type (automatic or manual)
     */
    intro_email_type: IntroEmailType;
    /**
     * Accepting Intros
     *
     * Whether candidate is accepting intros during current period
     */
    is_accepting_intros: boolean;
    /**
     * Keywords
     *
     * List of keywords
     */
    keywords: Array<string>;
    /**
     * Kickoff Date
     *
     * Kickoff date
     */
    kickoff_date?: string | null;
    /**
     * Location
     *
     * Candidate's location (from their resume)
     */
    location?: string | null;
    /**
     * Manual last update
     *
     * Timestamp when resume was last updated manually
     */
    manual_last_update?: string | null;
    /**
     * Resume manual last update person UUID
     *
     * Person UUID when resume was last updated manually
     */
    manual_update_person_id?: string | null;
    /**
     * Target network
     *
     * Areas in which this person wants to build their network
     */
    network_target?: string | null;
    /**
     * Not accepting start date
     *
     * Date from which the candidate is not accepting intros
     */
    not_accepting_start_date?: string | null;
    /**
     * Not accepting end date
     *
     * Date until which the candidate is not accepting intros
     */
    not_accepting_end_date?: string | null;
    /**
     * Notes
     *
     * Internal advisor notes on the candidate
     */
    notes?: string | null;
    /**
     * Operating Highlights
     *
     * List of operating highlights
     */
    operating_highlights: Array<OperatingHighlights>;
    /**
     * Passions
     *
     * Hobbies, locations, niche industries, personal interests, etc.
     */
    passions?: string | null;
    /**
     * Referrers
     *
     * List of people who referred this candidate
     */
    referrers: Array<ReferrerBasicResponse>;
    /**
     * Resume
     *
     * Response from the resume upload endpoint
     */
    resume?: LinkedInResumeResponse | null;
    /**
     * Profile updated date
     *
     * Date when the candidate's profile was last updated
     */
    profile_updated_date?: string | null;
    /**
     * Resume last uploaded
     *
     * Timestamp when resume was last updated
     */
    resume_last_uploaded?: string | null;
    /**
     * Short-term goals
     *
     * Short-term goals
     */
    short_term_goals: Array<string>;
    /**
     * Source company tags
     *
     * List of custom B4B tags, if and only if the candidate has a source company
     */
    source_company_tags?: Array<SourceCompanyTags> | null;
    /**
     * Tier
     *
     * Candidate tier
     */
    tier: CandidateTier;
    /**
     * Type
     *
     * Candidate type
     */
    type?: ClientType | null;
    /**
     * Pathway
     *
     * Banff service pathway for the candidate
     */
    pathway?: CandidatePathway | null;
};

/**
 * CandidateInternalFullResponseWithLinkedPerson
 */
export type CandidateInternalFullResponseWithLinkedPerson = {
    /**
     * First Name
     *
     * Person's first name
     */
    first_name?: string | null;
    /**
     * Last Name
     *
     * Person's last name
     */
    last_name?: string | null;
    /**
     * Company
     *
     * Candidate's current company
     */
    company?: string | null;
    /**
     * Role
     *
     * Candidate's current role
     */
    role?: string | null;
    /**
     * Image
     *
     * Name of the uploaded image from the response
     */
    image?: string | null;
    /**
     * Client
     *
     * Indicates whether a person is a Banff client
     */
    is_client: boolean;
    /**
     * LinkedIn URL
     *
     * Link to the person's LinkedIn profile
     */
    linkedin_url?: string | null;
    /**
     * Name
     *
     * Candidate's full name
     */
    name: string;
    /**
     * UUID
     *
     * UUID uniquely identifying a candidate
     */
    uuid: string;
    /**
     * Active
     *
     * Is this candidate marked as active?
     */
    active: boolean;
    /**
     * Merged into
     *
     * Set when this record was merged into another: its rows live on that candidate now, and this one is only a signpost. The webapp redirects an old profile URL to it.
     */
    merged_into_uuid?: string | null;
    /**
     * Advisor
     *
     * Information about the advisor assigned to this candidate
     */
    advisor?: PersonBasicResponse | null;
    /**
     * Client owner
     *
     * Information about the client owner of the candidate
     */
    client_owner?: PersonBasicResponse | null;
    /**
     * URG
     *
     * Is this candidate from an under-represented group?
     */
    is_urg?: boolean | null;
    /**
     * Email
     *
     * Email associated with the person
     */
    email: string;
    /**
     * Functional expertise
     *
     * List of functions candidate has experience in
     */
    function_expertise: Array<Function>;
    /**
     * Target functions
     *
     * Functional expertise candidate would like to get
     */
    function_target: Array<Function>;
    /**
     * Long-term goals
     *
     * Long-term goals
     */
    long_term_goals: Array<string>;
    /**
     * ID
     *
     * ID uniquely identifying a candidate
     */
    id: number;
    /**
     * Person ID
     *
     * UUID of the candidate's executive login, if any (derived from the identity, not from the frozen person_id column)
     */
    person_id?: string | null;
    /**
     * Identity UUID
     *
     * UUID of the identity this candidate belongs to: the one row per real human that also holds the candidate's logins
     */
    identity_uuid?: string | null;
    /**
     * Industry expertise
     *
     * List of industries candidate has experience in
     */
    industry_expertise: Array<Industry>;
    /**
     * Target industries
     *
     * Functional expertise candidate would like to get
     */
    industry_target: Array<Industry>;
    /**
     * Board/advisory role interest
     *
     * Interest in getting board or advisory roles
     */
    interest_role_board_advisory?: InterestRole | null;
    /**
     * Operating role interest
     *
     * Interest in getting operating roles
     */
    interest_role_operating?: InterestRole | null;
    /**
     * Personal interests
     *
     * List of personal interests
     */
    interests_personal: Array<InterestPersonal>;
    /**
     * Professional interests
     *
     * List of professional interests
     */
    interests_professional: Array<InterestProfessional>;
    /**
     * Interests notes
     *
     * Notes on interests
     */
    interests_notes?: string | null;
    /**
     * Invoice Status
     *
     * HubSpot invoice status (admin-facing; tier 1/1b only)
     */
    invoice_status?: HubSpotInvoiceStatus | null;
    /**
     * HubSpot ID
     *
     * HubSpot contact id linked by the reconcile job (debugging)
     */
    hubspot_id?: string | null;
    /**
     * Wants intros
     *
     * Whether the candidate wants introductions
     */
    wants_intros?: boolean | null;
    /**
     * Interest in relocation
     *
     * Self-reported openness to relocation (external profile)
     */
    interest_relocation?: boolean | null;
    /**
     * Relocation notes
     *
     * Self-reported relocation preferences or constraints (external profile)
     */
    interest_relocation_notes?: string | null;
    /**
     * Introduction email type
     *
     * Introduction email type (automatic or manual)
     */
    intro_email_type: IntroEmailType;
    /**
     * Accepting Intros
     *
     * Whether candidate is accepting intros during current period
     */
    is_accepting_intros: boolean;
    /**
     * Keywords
     *
     * List of keywords
     */
    keywords: Array<string>;
    /**
     * Kickoff Date
     *
     * Kickoff date
     */
    kickoff_date?: string | null;
    /**
     * Location
     *
     * Candidate's location (from their resume)
     */
    location?: string | null;
    /**
     * Manual last update
     *
     * Timestamp when resume was last updated manually
     */
    manual_last_update?: string | null;
    /**
     * Resume manual last update person UUID
     *
     * Person UUID when resume was last updated manually
     */
    manual_update_person_id?: string | null;
    /**
     * Target network
     *
     * Areas in which this person wants to build their network
     */
    network_target?: string | null;
    /**
     * Not accepting start date
     *
     * Date from which the candidate is not accepting intros
     */
    not_accepting_start_date?: string | null;
    /**
     * Not accepting end date
     *
     * Date until which the candidate is not accepting intros
     */
    not_accepting_end_date?: string | null;
    /**
     * Notes
     *
     * Internal advisor notes on the candidate
     */
    notes?: string | null;
    /**
     * Operating Highlights
     *
     * List of operating highlights
     */
    operating_highlights: Array<OperatingHighlights>;
    /**
     * Passions
     *
     * Hobbies, locations, niche industries, personal interests, etc.
     */
    passions?: string | null;
    /**
     * Referrers
     *
     * List of people who referred this candidate
     */
    referrers: Array<ReferrerBasicResponse>;
    /**
     * Resume
     *
     * Response from the resume upload endpoint
     */
    resume?: LinkedInResumeResponse | null;
    /**
     * Profile updated date
     *
     * Date when the candidate's profile was last updated
     */
    profile_updated_date?: string | null;
    /**
     * Resume last uploaded
     *
     * Timestamp when resume was last updated
     */
    resume_last_uploaded?: string | null;
    /**
     * Short-term goals
     *
     * Short-term goals
     */
    short_term_goals: Array<string>;
    /**
     * Source company tags
     *
     * List of custom B4B tags, if and only if the candidate has a source company
     */
    source_company_tags?: Array<SourceCompanyTags> | null;
    /**
     * Tier
     *
     * Candidate tier
     */
    tier: CandidateTier;
    /**
     * Type
     *
     * Candidate type
     */
    type?: ClientType | null;
    /**
     * Pathway
     *
     * Banff service pathway for the candidate
     */
    pathway?: CandidatePathway | null;
    /**
     * Person
     *
     * Linked person record for activated candidates (only present when ExecutiveSettings exists)
     */
    person?: CandidateLinkedPersonResponse | null;
    /**
     * Company metadata
     *
     * Per-company custom metadata for this candidate. Advisors see all active rows across companies.
     */
    company_metadata?: Array<CandidateCompanyMetadataResponse>;
};

/**
 * CandidateIntroductionTemplateCreate
 */
export type CandidateIntroductionTemplateCreate = {
    /**
     * Body
     *
     * The body text associated with the introduction template
     */
    body: string;
    /**
     * Title
     *
     * The name of the introduction template, for reference
     */
    title: string;
};

/**
 * CandidateIntroductionTemplatePayload
 */
export type CandidateIntroductionTemplatePayload = {
    /**
     * Body
     *
     * The body text associated with the introduction template
     */
    body: string;
    /**
     * Title
     *
     * The name of the introduction template, for reference
     */
    title: string;
    /**
     * UUID
     *
     * UUID uniquely identifying a candidate introduction template
     */
    uuid?: string | null;
};

/**
 * CandidateIntroductionTemplateResponse
 */
export type CandidateIntroductionTemplateResponse = {
    /**
     * Body
     *
     * The body text associated with the introduction template
     */
    body: string;
    /**
     * Title
     *
     * The name of the introduction template, for reference
     */
    title: string;
    /**
     * Last modified
     *
     * Date when this template was modified
     */
    last_modified: string;
    /**
     * Created
     *
     * Date when this template was created
     */
    created: string;
    /**
     * Hidden
     *
     * Is this template hidden?
     */
    hidden: boolean;
    /**
     * Soft delete
     *
     * [DEPRECATED] Use 'hidden' instead. Is this template soft deleted?
     *
     * @deprecated
     */
    soft_delete: boolean;
    /**
     * UUID
     *
     * UUID uniquely identifying an introduction template
     */
    uuid: string;
};

/**
 * CandidateItem
 */
export type CandidateItem = {
    /**
     * Meta
     *
     * Metadata about the search result
     */
    search_metadata?: EsMeta | null;
    /**
     * Is favorite
     *
     * Is the candidate favorited by the current user?
     */
    is_favorite: boolean;
    /**
     * Activity count
     *
     * Activity count for the recommendation
     */
    activity_count: number;
    /**
     * Activity last on
     *
     * Date of the last activity event
     */
    activity_last_on?: string | null;
    /**
     * Activity last viewed on
     *
     * Date when the user last viewed an activity on this candidate
     */
    activity_last_viewed_on?: string | null;
    /**
     * Candidate
     *
     * Candidate submitted in this recommendation
     */
    candidate: CandidateExtendedResponse;
    /**
     * Expires on
     *
     * Ephemeral recommendations may expire at some date
     */
    expires_on?: string | null;
    /**
     * Is viewed?
     *
     * Has this recommendation been viewed by the user loading it?
     */
    is_viewed: boolean;
    /**
     * Created
     *
     * Date when this recommendation was published to the team
     */
    published_on: string;
    /**
     * Review status
     *
     * This recommendation's review status
     */
    status: RecommendationReviewState;
    /**
     * Recommendation UUID
     *
     * Recommendation UUID
     */
    uuid: string;
    /**
     * Draft mode
     *
     * Whether this recommendation is in draft mode
     */
    draft_mode?: RecommendationDraftMode | null;
    /**
     * Draft assignee
     *
     * Person assigned to this draft recommendation
     */
    draft_assignee?: PersonBasicResponse | null;
};

/**
 * CandidateLinkedPersonResponse
 */
export type CandidateLinkedPersonResponse = {
    /**
     * UUID
     *
     * UUID uniquely identifying a linked person
     */
    uuid: string;
    /**
     * Email
     *
     * Linked person's email address
     */
    email: string;
    /**
     * First name
     *
     * Linked person's first name
     */
    first_name?: string | null;
    /**
     * Last name
     *
     * Linked person's last name
     */
    last_name?: string | null;
    /**
     * Full name
     *
     * Linked person's full name
     */
    name?: string | null;
    /**
     * Invited
     *
     * Whether this pending person has an invitation record
     */
    invited: boolean;
    /**
     * Registered
     *
     * Whether this person has accepted and registered
     */
    registered: boolean;
    /**
     * Pending
     *
     * Whether this person's invite is still pending
     */
    pending: boolean;
};

/**
 * CandidateMergePayload
 */
export type CandidateMergePayload = {
    /**
     * Source Candidate Uuid
     */
    source_candidate_uuid: string;
    /**
     * Dry Run
     */
    dry_run?: boolean;
};

/**
 * CandidateMergeResponse
 *
 * What a candidate merge would do (dry run), did (applied) or why it was
 * refused (blockers). Same shape in all three cases.
 */
export type CandidateMergeResponse = {
    /**
     * Applied
     */
    applied: boolean;
    /**
     * Source Uuid
     */
    source_uuid: string;
    /**
     * Target Uuid
     */
    target_uuid: string;
    /**
     * Blockers
     */
    blockers?: Array<string>;
    /**
     * Counts Before
     */
    counts_before?: {
        [key: string]: number;
    };
    /**
     * Moved
     */
    moved?: {
        [key: string]: number;
    };
    /**
     * Dropped As Duplicate
     */
    dropped_as_duplicate?: {
        [key: string]: number;
    };
    /**
     * Self Peers Dropped
     */
    self_peers_dropped?: number;
    /**
     * Identity
     */
    identity?: {
        [key: string]: unknown;
    };
    /**
     * Merged Into Uuid
     */
    merged_into_uuid?: string | null;
    /**
     * Es Task Id
     */
    es_task_id?: string | null;
};

/**
 * CandidateOffLimitsCompany
 */
export type CandidateOffLimitsCompany = {
    /**
     * Company ID
     *
     * The ID of the company that is off-limits to this candidate
     */
    company_id: number;
    /**
     * Reason
     *
     * The reason that the company is off-limits to this candidate
     */
    reason: OffLimitsReason;
};

/**
 * CandidateOffLimitsIdentity
 */
export type CandidateOffLimitsIdentity = {
    /**
     * Identity UUID
     *
     * The identity (human) this candidate must not be shown to
     */
    identity_uuid: string;
    /**
     * Reason
     *
     * The reason that the human is off-limits to this candidate
     */
    reason: OffLimitsReason;
};

/**
 * CandidateOnboardingStatus
 */
export type CandidateOnboardingStatus = {
    /**
     * Show Onboarding Section
     *
     * Show onboarding section
     */
    show_onboarding_section?: boolean | null;
    /**
     * Candidate
     */
    candidate: CandidateBasicResponse;
    /**
     * Tasks
     *
     * List of onboarding tasks
     */
    tasks?: Array<TaskBasicResponse> | null;
    readonly total_counts: TaskTotal;
    /**
     * Onboarding Complete
     */
    readonly onboarding_complete: boolean;
};

/**
 * CandidatePathway
 *
 * Banff client pathways (API/DB values are lowercase snake_case).
 */
export const CandidatePathway = {
    TRANSITION_SERVICES: 'transition_services',
    NETWORK_SERVICES: 'network_services',
    CONCIERGE: 'concierge',
    BANFF_ACCESS: 'banff_access',
    PRO_BONO: 'pro_bono'
} as const;

/**
 * CandidatePathway
 *
 * Banff client pathways (API/DB values are lowercase snake_case).
 */
export type CandidatePathway = typeof CandidatePathway[keyof typeof CandidatePathway];

/**
 * CandidatePathwayActivityTargetPutItem
 */
export type CandidatePathwayActivityTargetPutItem = {
    activity_type: PathwayActivityType;
    /**
     * Target Number
     */
    target_number: number;
    time_period: ActivityTimePeriod;
};

/**
 * CandidatePathwayActivityTargetRow
 *
 * One activity row: effective values plus pathway defaults for reset/compare.
 */
export type CandidatePathwayActivityTargetRow = {
    /**
     * Executive activity metric
     */
    activity_type: PathwayActivityType;
    /**
     * Target Number
     *
     * Effective target (override or pathway default)
     */
    target_number: number;
    /**
     * Effective period
     */
    time_period: ActivityTimePeriod;
    /**
     * Uses Pathway Default
     *
     * True when there is no row in candidate_pathway_targets for this activity
     */
    uses_pathway_default: boolean;
    /**
     * Pathway Default Target Number
     *
     * Default target_number from pathway_activity_defaults for this pathway
     */
    pathway_default_target_number: number;
    /**
     * Default time_period for the candidate's pathway
     */
    pathway_default_time_period: ActivityTimePeriod;
};

/**
 * CandidatePathwayActivityTargetsPutPayload
 */
export type CandidatePathwayActivityTargetsPutPayload = {
    /**
     * Targets
     *
     * Upsert per-candidate overrides for each activity_type
     */
    targets: Array<CandidatePathwayActivityTargetPutItem>;
};

/**
 * CandidatePathwayActivityTargetsResponse
 *
 * Merged view of pathway defaults and per-candidate overrides.
 */
export type CandidatePathwayActivityTargetsResponse = {
    /**
     * Candidate Pathway
     *
     * Pathway stored on the candidate (snake_case), if any
     */
    candidate_pathway: string | null;
    /**
     * Defaults Pathway
     *
     * Pathway key for pathway_activity_defaults, if candidate has a pathway
     */
    defaults_pathway: string | null;
    /**
     * Targets
     *
     * One row per known activity for that pathway, ordered by activity type
     */
    targets: Array<CandidatePathwayActivityTargetRow>;
};

/**
 * CandidatePeerBackgroundComparisonResponse
 */
export type CandidatePeerBackgroundComparisonResponse = {
    candidate: CandidateCompareBackgroundResponse;
    peer: CandidateCompareBackgroundResponse;
};

/**
 * CandidatePeerPerformanceComparisonResponse
 */
export type CandidatePeerPerformanceComparisonResponse = {
    candidate: CandidateComparePerformanceResponse;
    peer: CandidateComparePerformanceResponse;
};

/**
 * CandidatePeerRecommendationComparisonResponse
 */
export type CandidatePeerRecommendationComparisonResponse = {
    candidate: CandidateCompareRecommendationResponse;
    peer: CandidateCompareRecommendationResponse;
};

/**
 * CandidatePerformanceFilterSortPayload
 */
export type CandidatePerformanceFilterSortPayload = {
    /**
     * Page
     *
     * Page number
     */
    page?: number;
    /**
     * Page size
     *
     * Page size
     */
    size?: number;
    /**
     * Filters
     *
     * Filter results by these criteria
     */
    filter_data?: CandidatesPerformanceFilters | null;
    /**
     * Sort
     *
     * Sort results by these rules
     */
    sort_rules: Array<CandidatePerformanceSort>;
};

/**
 * CandidatePerformanceResponse
 */
export type CandidatePerformanceResponse = {
    candidate: CandidateCompanyRoleResponse;
    /**
     * Advisor
     *
     * Advisor basic info
     */
    advisor: PersonBasicResponse;
    /**
     * Intro request count
     *
     * Candidate's intro requests count
     */
    introductions_requested_count: number;
    /**
     * Recommendation count
     *
     * Candidate's recommendation count
     */
    recommendation_count: number;
    /**
     * Pipeline count
     *
     * Candidate's pipeline count
     */
    pipeline_count: number;
    /**
     * Days to quarterly
     *
     * How many days until the next quarterly mark
     */
    days_to_quarterly: number;
};

/**
 * CandidatePerformanceSort
 */
export type CandidatePerformanceSort = {
    /**
     * Column
     *
     * Sort results on this column
     */
    column: CandidatePerformanceSortColumn;
    /**
     * Direction
     *
     * Sort results in this direction
     */
    direction?: SortDirection;
};

/**
 * CandidatePerformanceSortColumn
 */
export const CandidatePerformanceSortColumn = {
    DAYS_TO_QUARTERLY: 'days_to_quarterly',
    RECOMMENDATION_COUNT: 'recommendation_count',
    REQUEST_COUNT: 'request_count',
    TOTAL_FACILITATED: 'total_facilitated',
    PIPELINE_COUNT: 'pipeline_count',
    FULL_NAME: 'full_name',
    ADVISOR_NAME: 'advisor_name'
} as const;

/**
 * CandidatePerformanceSortColumn
 */
export type CandidatePerformanceSortColumn = typeof CandidatePerformanceSortColumn[keyof typeof CandidatePerformanceSortColumn];

/**
 * CandidatePipelineStatus
 *
 * Pipeline status that combines RecommendationDraftMode and RecommendationStatus.
 *
 * This enum represents the combined state of a recommendation/submission candidate:
 * - Draft modes (when status=DRAFT): APPROVED, DENIED, READY_FOR_REVIEW, SUGGESTED
 * - Published states: PUBLISHED, SCHEDULED
 * - Draft state: DRAFT (when no specific draft mode is set)
 */
export const CandidatePipelineStatus = {
    APPROVED: 'approved',
    DENIED: 'denied',
    READY_FOR_REVIEW: 'ready_for_review',
    SUGGESTED: 'suggested',
    SAVED: 'saved',
    PUBLISHED: 'published',
    SCHEDULED: 'scheduled',
    UNDEFINED: 'undefined'
} as const;

/**
 * CandidatePipelineStatus
 *
 * Pipeline status that combines RecommendationDraftMode and RecommendationStatus.
 *
 * This enum represents the combined state of a recommendation/submission candidate:
 * - Draft modes (when status=DRAFT): APPROVED, DENIED, READY_FOR_REVIEW, SUGGESTED
 * - Published states: PUBLISHED, SCHEDULED
 * - Draft state: DRAFT (when no specific draft mode is set)
 */
export type CandidatePipelineStatus = typeof CandidatePipelineStatus[keyof typeof CandidatePipelineStatus];

/**
 * CandidateRecommendationAggregations
 */
export type CandidateRecommendationAggregations = {
    /**
     * Intro Requests
     */
    intro_requests: number;
    /**
     * Intro Completed
     */
    intro_completed: number;
    /**
     * Not Interesting
     */
    not_interesting: number;
    /**
     * No Action
     */
    no_action: number;
};

/**
 * CandidateRecommendationResponse
 */
export type CandidateRecommendationResponse = {
    /**
     * Meta
     *
     * Metadata about the search result
     */
    search_metadata?: EsMeta | null;
    /**
     * Person
     *
     * Information about the person who requested an introduction with this recommendation
     */
    person?: PersonBasicEmailResponse | null;
    /**
     * Type
     *
     * Defines in which context was this recommendation published.
     */
    type: IntroductionType;
    /**
     * About
     *
     * Brief commentary about the candidate
     */
    about?: string | null;
    /**
     * Advisor
     *
     * Information about the advisor who published this recommendation
     */
    advisor?: PersonBasicEmailResponse | null;
    /**
     * Already known
     *
     * Whether this recommendation was already known to the person reviewing it
     */
    already_known?: boolean | null;
    /**
     * Candidate
     *
     * Candidate associated with this recommendation
     */
    candidate: CandidateExtendedResponse;
    /**
     * Comment author
     *
     * Information about the person who left the comment
     */
    comment_author?: PersonBasicResponse | null;
    /**
     * Comment date
     *
     * Date when the latest comment was left
     */
    comment_date?: string | null;
    /**
     * Company
     *
     * Company to which this recommendation was submitted
     */
    company: CompanyBasicTypeResponse;
    /**
     * Sent date
     *
     * Date when this recommendation was published
     */
    sent_date: string;
    /**
     * Date introduction facilitated
     *
     * Date when an introduction to this recommendation has been facilitated
     */
    date_introduction_facilitated?: string | null;
    /**
     * Date last viewed
     *
     * Date when this recommendation was last viewed
     */
    date_last_viewed?: string | null;
    /**
     * Highlights
     *
     * List of all candidate's highlights with information which ones were selected for this submission
     */
    highlights: Array<CandidateHighlightResponse>;
    /**
     * Introduction date
     *
     * Date when introduction was requested for this recommendation
     */
    introduction_date?: string | null;
    /**
     * Status
     *
     * Introduction request status
     */
    introduction_status?: IntroductionStatus | null;
    /**
     * Is liked?
     *
     * Does the user want more recommendations like this one?
     */
    is_liked?: boolean | null;
    /**
     * Comment
     *
     * Comment left about the recommendation
     */
    comment?: string | null;
    /**
     * Introduction requested
     *
     * Information whether an introduction was requested with this recommendation
     */
    make_introduction?: boolean | null;
    /**
     * Search match
     *
     * When submitted to a search, is this candidate a match?
     */
    match?: boolean | null;
    /**
     * Pipeline
     *
     * Information about pipeline where this recommendation was submitted
     */
    pipeline?: PipelineBasicHiddenResponse | null;
    /**
     * Review status
     *
     * State in which the recommendation currently is
     */
    review_status: RecommendationReviewState;
    /**
     * Team
     *
     * Team to which this recommendation was submitted
     */
    team: TeamBasicResponse;
    /**
     * Submission
     *
     * Information about the submission to which this candidate belongs
     */
    submission: SubmissionBasicResponse;
    /**
     * UUID
     *
     * UUID uniquely identifying a recommendation
     */
    uuid: string;
    /**
     * Viewed date
     *
     * Date when this recommendation was viewed
     */
    viewed_date?: string | null;
};

/**
 * CandidateRecommendationsFilterData
 */
export type CandidateRecommendationsFilterData = {
    /**
     * Company ID
     *
     * Filter by company ID
     */
    company_id: Array<number>;
    /**
     * Company Type
     *
     * Filter by company type
     */
    company_type: Array<OrgType>;
    /**
     * Response
     *
     * Filter by response
     */
    response: Array<RecommendationReviewState>;
    /**
     * Role Type
     *
     * Filter by role type
     */
    role_type: Array<PipelineRoleType>;
    /**
     * Recommendation Type
     *
     * Filter by recommendation type
     */
    type: Array<IntroductionType>;
};

/**
 * CandidateRecommendationsFilterPayload
 */
export type CandidateRecommendationsFilterPayload = {
    /**
     * Phrase
     *
     * Search phrase
     */
    phrase?: string | null;
    /**
     * Search ID
     *
     * Search session ID
     */
    search_id?: string | null;
    /**
     * Page
     *
     * Page number
     */
    page?: number;
    /**
     * Page size
     *
     * Page size
     */
    page_size?: number;
    /**
     * Date From
     *
     * Filter recommendations from this date
     */
    date_from?: string | null;
    /**
     * Date To
     *
     * Filter recommendations to this date
     */
    date_to?: string | null;
    /**
     * Filters
     *
     * Filter results by these criteria
     */
    filter_data?: CandidateRecommendationsFilterData | null;
    /**
     * Sort
     *
     * Sort results by these rules
     */
    sort_rules: Array<CandidateRecommendationsSort>;
    /**
     * Recommendation status type
     *
     * Recommendation status type to filter on
     */
    status?: RecommendationFilterStatus;
};

/**
 * CandidateRecommendationsFilterResponse
 */
export type CandidateRecommendationsFilterResponse = {
    aggregations: AppSchemasEsRecommendationCandidateSchemaElasticsearchAggregationsResponse;
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Results
     */
    results: Array<EsRecommendationCandidateIndexResult>;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCountsResponse;
};

/**
 * CandidateRecommendationsSort
 */
export type CandidateRecommendationsSort = {
    /**
     * Column
     *
     * Sort results on this column
     */
    column: RecommendationsSortColumn;
    /**
     * Direction
     *
     * Sort results in this direction
     */
    direction?: SortDirection;
};

/**
 * CandidateRecommendationsStatsRoleResponse
 */
export type CandidateRecommendationsStatsRoleResponse = {
    /**
     * Current Period
     *
     * Stats for the selected period
     */
    current_period: PeriodStatsRole;
    /**
     * Current Period Dates
     *
     * Current period dates
     */
    current_period_dates: PeriodDates;
    /**
     * Previous Period
     *
     * Stats for the previous period
     */
    previous_period: PeriodStatsRole;
    /**
     * Previous Period Dates
     *
     * Previous period dates
     */
    previous_period_dates: PeriodDates;
};

/**
 * CandidateRecommendationsStatsSubmissionResponse
 */
export type CandidateRecommendationsStatsSubmissionResponse = {
    /**
     * Current Period
     *
     * Stats for the selected period
     */
    current_period: PeriodStatsSubmission;
    /**
     * Current Period Dates
     *
     * Current period dates
     */
    current_period_dates: PeriodDates;
    /**
     * Previous Period
     *
     * Stats for the previous period
     */
    previous_period: PeriodStatsSubmission;
    /**
     * Previous Period Dates
     *
     * Previous period dates
     */
    previous_period_dates: PeriodDates;
};

/**
 * CandidateRecommendationsStatsTeamResponse
 */
export type CandidateRecommendationsStatsTeamResponse = {
    /**
     * Current Period
     *
     * Stats for the selected period
     */
    current_period: PeriodStatsTeam;
    /**
     * Current Period Dates
     *
     * Current period dates
     */
    current_period_dates: PeriodDates;
    /**
     * Previous Period
     *
     * Stats for the previous period
     */
    previous_period: PeriodStatsTeam;
    /**
     * Previous Period Dates
     *
     * Previous period dates
     */
    previous_period_dates: PeriodDates;
};

/**
 * CandidateSearchData
 */
export type CandidateSearchData = {
    /**
     * Phrase
     */
    phrase?: string | null;
    filter_data?: CandidatesFilters | null;
    /**
     * Page
     */
    page?: number;
    /**
     * Size
     */
    size?: number;
    /**
     * Search Id
     */
    search_id?: string | null;
    sort_rules: CandidatesSort;
};

/**
 * CandidateSearchResponse
 */
export type CandidateSearchResponse = {
    /**
     * Company
     *
     * Candidate's current company
     */
    company?: string | null;
    /**
     * Role
     *
     * Candidate's current role
     */
    role?: string | null;
    /**
     * Advisor
     *
     * Information about the advisor assigned to this candidate
     */
    advisor?: PersonBasicEmailResponse | null;
    /**
     * Email
     *
     * Candidate's email address
     */
    email: string;
    /**
     * First name
     *
     * Candidate's first name
     */
    first_name?: string | null;
    /**
     * Functional expertise
     *
     * List of functions candidate has experience in
     */
    function_expertise: Array<Function>;
    /**
     * Target functions
     *
     * Functional expertise candidate would like to get
     */
    function_target: Array<Function>;
    /**
     * ID
     *
     * ID uniquely identifying a candidate
     */
    id: number;
    /**
     * Image
     *
     * Path to candidate image
     */
    image?: string | null;
    /**
     * Industry expertise
     *
     * List of industries candidate has experience in
     */
    industry_expertise: Array<Industry>;
    /**
     * Target industries
     *
     * Functional expertise candidate would like to get
     */
    industry_target: Array<Industry>;
    /**
     * Board/advisory role interest
     *
     * Interest in getting board or advisory roles
     */
    interest_role_board_advisory?: InterestRole | null;
    /**
     * Operating role interest
     *
     * Interest in getting operating roles
     */
    interest_role_operating?: InterestRole | null;
    /**
     * Personal interests
     *
     * List of personal interests
     */
    interests_personal: Array<InterestPersonal>;
    /**
     * Professional interests
     *
     * List of professional interests
     */
    interests_professional: Array<InterestProfessional>;
    /**
     * Client
     *
     * Indicates whether a person is a Banff client
     */
    is_client: boolean;
    /**
     * Introduction email type
     *
     * Introduction email type (automatic or manual)
     */
    intro_email_type: IntroEmailType;
    /**
     * Keywords
     *
     * List of keywords
     */
    keywords: Array<string>;
    /**
     * Last name
     *
     * Candidate's last name
     */
    last_name?: string | null;
    /**
     * LinkedIn URL
     *
     * Link to the person's LinkedIn profile
     */
    linkedin_url?: string | null;
    /**
     * Location
     *
     * Candidate's location (from their resume)
     */
    location?: string | null;
    /**
     * Full name
     *
     * Candidate's full name
     */
    name?: string | null;
    /**
     * Tier
     *
     * Candidate tier
     */
    tier: CandidateTier;
    /**
     * Type
     *
     * Candidate type
     */
    type?: ClientType | null;
    /**
     * Pathway
     *
     * Banff service pathway for the candidate
     */
    pathway?: CandidatePathway | null;
    /**
     * UUID
     *
     * UUID uniquely identifying a candidate
     */
    uuid: string;
};

/**
 * CandidateSearchResponseWithLinkedPerson
 */
export type CandidateSearchResponseWithLinkedPerson = {
    /**
     * Company
     *
     * Candidate's current company
     */
    company?: string | null;
    /**
     * Role
     *
     * Candidate's current role
     */
    role?: string | null;
    /**
     * Advisor
     *
     * Information about the advisor assigned to this candidate
     */
    advisor?: PersonBasicEmailResponse | null;
    /**
     * Email
     *
     * Candidate's email address
     */
    email: string;
    /**
     * First name
     *
     * Candidate's first name
     */
    first_name?: string | null;
    /**
     * Functional expertise
     *
     * List of functions candidate has experience in
     */
    function_expertise: Array<Function>;
    /**
     * Target functions
     *
     * Functional expertise candidate would like to get
     */
    function_target: Array<Function>;
    /**
     * ID
     *
     * ID uniquely identifying a candidate
     */
    id: number;
    /**
     * Image
     *
     * Path to candidate image
     */
    image?: string | null;
    /**
     * Industry expertise
     *
     * List of industries candidate has experience in
     */
    industry_expertise: Array<Industry>;
    /**
     * Target industries
     *
     * Functional expertise candidate would like to get
     */
    industry_target: Array<Industry>;
    /**
     * Board/advisory role interest
     *
     * Interest in getting board or advisory roles
     */
    interest_role_board_advisory?: InterestRole | null;
    /**
     * Operating role interest
     *
     * Interest in getting operating roles
     */
    interest_role_operating?: InterestRole | null;
    /**
     * Personal interests
     *
     * List of personal interests
     */
    interests_personal: Array<InterestPersonal>;
    /**
     * Professional interests
     *
     * List of professional interests
     */
    interests_professional: Array<InterestProfessional>;
    /**
     * Client
     *
     * Indicates whether a person is a Banff client
     */
    is_client: boolean;
    /**
     * Introduction email type
     *
     * Introduction email type (automatic or manual)
     */
    intro_email_type: IntroEmailType;
    /**
     * Keywords
     *
     * List of keywords
     */
    keywords: Array<string>;
    /**
     * Last name
     *
     * Candidate's last name
     */
    last_name?: string | null;
    /**
     * LinkedIn URL
     *
     * Link to the person's LinkedIn profile
     */
    linkedin_url?: string | null;
    /**
     * Location
     *
     * Candidate's location (from their resume)
     */
    location?: string | null;
    /**
     * Full name
     *
     * Candidate's full name
     */
    name?: string | null;
    /**
     * Tier
     *
     * Candidate tier
     */
    tier: CandidateTier;
    /**
     * Type
     *
     * Candidate type
     */
    type?: ClientType | null;
    /**
     * Pathway
     *
     * Banff service pathway for the candidate
     */
    pathway?: CandidatePathway | null;
    /**
     * UUID
     *
     * UUID uniquely identifying a candidate
     */
    uuid: string;
    /**
     * Person
     *
     * Linked person record for activated candidates (only present when ExecutiveSettings exists)
     */
    person?: CandidateLinkedPersonResponse | null;
};

/**
 * CandidateShareResponse
 */
export type CandidateShareResponse = {
    /**
     * Company
     *
     * Candidate's current company
     */
    company?: string | null;
    /**
     * Role
     *
     * Candidate's current role
     */
    role?: string | null;
    /**
     * Image
     *
     * Name of the uploaded image from the response
     */
    image?: string | null;
    /**
     * Client
     *
     * Indicates whether a person is a Banff client
     */
    is_client: boolean;
    /**
     * LinkedIn URL
     *
     * Link to the person's LinkedIn profile
     */
    linkedin_url?: string | null;
    /**
     * Name
     *
     * Candidate's full name
     */
    name: string;
    /**
     * UUID
     *
     * UUID uniquely identifying a candidate
     */
    uuid: string;
};

/**
 * CandidateShowOnboardingSection
 */
export type CandidateShowOnboardingSection = {
    /**
     * Show onboarding section
     *
     * Show onboarding section
     */
    show_onboarding_section: boolean;
};

/**
 * CandidateTaskModule
 */
export type CandidateTaskModule = {
    /**
     * Created date
     */
    created: string;
    /**
     * Module ID
     */
    id: string;
    /**
     * Type
     *
     * Type of task module
     */
    type: TaskModuleType;
    /**
     * Status
     *
     * Task module status
     */
    status: TaskModuleStatus;
    readonly total_counts: TaskTotal;
};

/**
 * CandidateTaskModuleResponse
 */
export type CandidateTaskModuleResponse = {
    /**
     * Task Module
     */
    module?: CandidateTaskModule | null;
    /**
     * Tasks
     *
     * List of tasks associated with this module
     */
    tasks?: Array<TaskBasicResponse> | null;
};

/**
 * CandidateTaskModulesResponse
 */
export type CandidateTaskModulesResponse = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Results
     */
    results: Array<CandidateTaskModuleResponse>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCountsResponse;
};

/**
 * CandidateTasksFilterPayload
 */
export type CandidateTasksFilterPayload = {
    /**
     * Page
     *
     * Page number
     */
    page?: number;
    /**
     * Page size
     *
     * Page size
     */
    page_size?: number;
    /**
     * With Goals
     *
     * Get only tasks with goals
     */
    with_goals?: boolean | null;
};

/**
 * CandidateTeamResponse
 */
export type CandidateTeamResponse = {
    /**
     * UUID
     *
     * Unique identifier for the recommendation
     */
    uuid: string;
    /**
     * Company
     */
    company: CompanyExtendedResponse;
    /**
     * Pipeline
     */
    pipeline?: PipelineBasicResponse | null;
    /**
     * Team
     */
    team: TeamExtendedFlatResponse;
    /**
     * Published On
     */
    published_on?: string | null;
    /**
     * Introduction Requested On
     *
     * Introduction date
     */
    introduction_requested_on?: string | null;
    /**
     * Introduction Facilitated On
     *
     * Introduction facilitated date
     */
    introduction_facilitated_on?: string | null;
    /**
     * Not Interested On
     *
     * Not interested date
     */
    not_interested_on?: string | null;
    /**
     *  Review Status
     */
    review_status: RecommendationReviewState;
};

/**
 * CandidateTier
 */
export const CandidateTier = {
    EXECUTIVE_CLIENT: 'executive_client',
    FORMER_EXECUTIVE_CLIENT: 'former_executive_client',
    EXECUTIVE_CLIENT_1ST_DEGREE_REFERRAL: 'executive_client_1st_degree_referral',
    EXECUTIVE_CLIENT_2ND_DEGREE_REFERRAL: 'executive_client_2nd_degree_referral',
    ADVISOR_NETWORK: 'advisor_network',
    ENTERPRISE_PARTNER_1ST_DEGREE_REFERRAL: 'enterprise_partner_1st_degree_referral',
    RESEARCH: 'research',
    B4B: 'b4b',
    EXTERNAL: 'external'
} as const;

/**
 * CandidateTier
 */
export type CandidateTier = typeof CandidateTier[keyof typeof CandidateTier];

/**
 * CandidateType
 *
 * Goal program values used by candidate_goals.program.
 */
export const CandidateType = {
    BOARD_MEMBER_ASPIRING: 'board_member_aspiring',
    JOB_SEEKER_ACTIVE: 'job_seeker_active',
    JOB_SEEKER_PASSIVE: 'job_seeker_passive',
    NETWORK_BUILDER: 'network_builder',
    NEW_ROLE_LEADER: 'new_role_leader',
    UPLOADED_NETWORK: 'uploaded_network'
} as const;

/**
 * CandidateType
 *
 * Goal program values used by candidate_goals.program.
 */
export type CandidateType = typeof CandidateType[keyof typeof CandidateType];

/**
 * CandidateUpdateActive
 */
export type CandidateUpdateActive = {
    /**
     * Active
     *
     * Mark this candidate as active?
     */
    active: boolean;
};

/**
 * CandidateViewPayload
 */
export type CandidateViewPayload = {
    /**
     * Has viewed activity
     *
     * When true, stores current date in context column on a view event. This is used to determine last viewed activity event.
     */
    has_viewed_activity: boolean;
};

/**
 * CandidatesCombinedPerformanceFilterSortPayload
 */
export type CandidatesCombinedPerformanceFilterSortPayload = {
    /**
     * Page
     *
     * Page number
     */
    page?: number;
    /**
     * Page size
     *
     * Page size
     */
    size?: number;
    /**
     * Filters
     *
     * Filter results by these criteria
     */
    filter_data?: CandidatesCombinedPerformanceFilters | null;
    /**
     * Sort
     *
     * Sort results by these rules
     */
    sort_rules: Array<CandidatesCombinedPerformanceSort>;
};

/**
 * CandidatesCombinedPerformanceFilters
 */
export type CandidatesCombinedPerformanceFilters = {
    /**
     * Advisor Id
     */
    advisor_id?: Array<string> | null;
    /**
     * Candidate Uuid
     */
    candidate_uuid?: Array<string> | null;
    /**
     * Function Expertise
     */
    function_expertise?: Array<Function> | null;
    /**
     * Industry Expertise
     */
    industry_expertise?: Array<Industry> | null;
    /**
     * Type
     */
    type?: Array<ClientType> | null;
    /**
     * Pathway
     */
    pathway?: Array<CandidatePathway> | null;
};

/**
 * CandidatesCombinedPerformancePaginatedResponse
 */
export type CandidatesCombinedPerformancePaginatedResponse = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Results
     */
    results: Array<CandidatesCombinedPerformanceResponse>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCountsResponse;
};

/**
 * CandidatesCombinedPerformanceResponse
 */
export type CandidatesCombinedPerformanceResponse = {
    candidate: CandidateCompanyRoleResponse;
    /**
     * Advisor
     *
     * Advisor basic info
     */
    advisor: PersonBasicResponse;
    /**
     * Type
     *
     * Candidate's type
     */
    type?: ClientType | null;
    /**
     * Pathway
     *
     * Candidate's pathway
     */
    pathway?: CandidatePathway | null;
    /**
     * Industry expertise
     *
     * Candidate's industry expertise
     */
    industry_expertise: Array<Industry>;
    /**
     * Function expertise
     *
     * Candidate's function expertise
     */
    function_expertise: Array<Function>;
    /**
     * Intro facilitated count
     *
     * Candidate's facilitated count
     */
    introductions_facilitated_count: number;
    /**
     * Intro request count
     *
     * Candidate's intro requests count
     */
    introductions_requested_count: number;
    /**
     * Recommendation count
     *
     * Candidate's recommendation count
     */
    recommendation_count: number;
    /**
     * Pipeline count
     *
     * Candidate's pipeline count
     */
    pipeline_count: number;
    /**
     * Peer activity count
     *
     * Peer activity count
     */
    peer_activity_count: number;
    /**
     * Rank
     *
     * Candidate's trending rank
     */
    rank: number;
    /**
     * Calculated rank
     *
     * Calculated candidate's trending rank
     */
    calculated_rank: number;
};

/**
 * CandidatesCombinedPerformanceSort
 */
export type CandidatesCombinedPerformanceSort = {
    /**
     * Column
     *
     * Sort results on this column
     */
    column: CandidatesCombinedPerformanceSortColumn;
    /**
     * Direction
     *
     * Sort results in this direction
     */
    direction?: SortDirection;
};

/**
 * CandidatesCombinedPerformanceSortColumn
 */
export const CandidatesCombinedPerformanceSortColumn = {
    RANK: 'rank',
    RECOMMENDATION_COUNT: 'recommendation_count',
    REQUEST_COUNT: 'request_count',
    TOTAL_FACILITATED: 'total_facilitated',
    PIPELINE_COUNT: 'pipeline_count',
    PEER_ACTIVITY_COUNT: 'peer_activity_count',
    FULL_NAME: 'full_name',
    ADVISOR_NAME: 'advisor_name',
    TYPE: 'type'
} as const;

/**
 * CandidatesCombinedPerformanceSortColumn
 */
export type CandidatesCombinedPerformanceSortColumn = typeof CandidatesCombinedPerformanceSortColumn[keyof typeof CandidatesCombinedPerformanceSortColumn];

/**
 * CandidatesFilters
 */
export type CandidatesFilters = {
    /**
     * Advisor Id
     */
    advisor_id?: Array<string> | null;
    /**
     * Function Expertise
     */
    function_expertise?: Array<Function> | null;
    /**
     * Industry Expertise
     */
    industry_expertise?: Array<Industry> | null;
    /**
     * Interest Role Board Advisory
     */
    interest_role_board_advisory?: Array<InterestRole> | null;
    /**
     * Interest Role Operating
     */
    interest_role_operating?: Array<InterestRole> | null;
    /**
     * Tier
     */
    tier?: Array<CandidateTier> | null;
    /**
     * Type
     */
    type?: Array<ClientType> | null;
    /**
     * Pathway
     */
    pathway?: Array<CandidatePathway> | null;
    /**
     * Invoice Status
     */
    invoice_status?: Array<HubSpotInvoiceStatus> | null;
};

/**
 * CandidatesPerformanceFilters
 */
export type CandidatesPerformanceFilters = {
    /**
     * Advisor Id
     */
    advisor_id?: Array<string> | null;
};

/**
 * CandidatesPerformancePaginatedResponse
 */
export type CandidatesPerformancePaginatedResponse = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Results
     */
    results: Array<CandidatePerformanceResponse>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCountsResponse;
};

/**
 * CandidatesSort
 */
export type CandidatesSort = {
    sort_direction?: SortDirection | null;
    sort_column?: CandidatesSortColumn | null;
};

/**
 * CandidatesSortColumn
 */
export const CandidatesSortColumn = {
    ADVISOR_NAME: 'advisor_name',
    CURRENT_COMPANY: 'current_company',
    '': '',
    NAME: 'name',
    INTEREST_ROLE_BOARD_ADVISORY: 'interest_role_board_advisory',
    INTEREST_ROLE_OPERATING: 'interest_role_operating',
    INVOICE_STATUS: 'invoice_status',
    PROFILE_UPDATED: 'profile_updated',
    ROLE: 'role',
    TIER: 'tier'
} as const;

/**
 * CandidatesSortColumn
 */
export type CandidatesSortColumn = typeof CandidatesSortColumn[keyof typeof CandidatesSortColumn];

/**
 * CandidatesTableResponse
 */
export type CandidatesTableResponse = {
    /**
     * Meta
     *
     * Metadata about the search result
     */
    search_metadata?: EsMeta | null;
    /**
     * LinkedIn profile last synced
     *
     * Last time resume was checked against LinkedIn
     */
    __linkedin_profile_last_synced__DO_NOT_USE__?: string | null;
    /**
     * Manual last update
     *
     * This field is used only to calculate the profile_updated_date value
     */
    __manual_last_update__DO_NOT_USE__?: string | null;
    /**
     * Profile updated date
     *
     * Date when the candidate's profile was last updated
     */
    profile_updated_date?: string | null;
    /**
     * Resume last uploaded
     *
     * This field is used only to calculate the profile_updated_date value
     */
    __resume_last_uploaded__DO_NOT_USE__?: string | null;
    /**
     * Client
     *
     * Indicates whether a person is a Banff client
     */
    is_client?: boolean | null;
    /**
     * Company
     *
     * Candidate's current company
     */
    company?: string | null;
    /**
     * Role
     *
     * Candidate's current role
     */
    role?: string | null;
    /**
     * Image
     *
     * Path to candidate image
     */
    image?: string | null;
    /**
     * Name
     *
     * Candidate's full name
     */
    name: string;
    /**
     * UUID
     *
     * UUID uniquely identifying a person
     */
    uuid: string;
    /**
     * Active
     *
     * Is this candidate marked as active?
     */
    active: boolean;
    /**
     * Advisor
     *
     * Information about the advisor assigned to this candidate
     */
    advisor?: PersonBasicResponse | null;
    /**
     * Advisor UUID
     *
     * This field is used only to build the advisor object
     */
    advisor_advisor_id?: string | null;
    /**
     * Advisor image
     *
     * This field is used only to build the advisor object
     */
    advisor_advisor_image?: string | null;
    /**
     * Advisor name
     *
     * This field is used only to build the advisor object
     */
    advisor_advisor_name?: string | null;
    /**
     * Referred date
     *
     * Date when the candidate was referred for the first time
     */
    referred_date?: string | null;
    /**
     * Email
     *
     * Email associated with the candidate
     */
    email: string;
    /**
     * First name
     *
     * Candidate's first name
     */
    first_name?: string | null;
    /**
     * Functional expertise
     *
     * List of functions candidate has experience in
     */
    function_expertise: Array<Function>;
    /**
     * Target functions
     *
     * Functional expertise candidate would like to get
     */
    function_target: Array<Function>;
    /**
     * Industry expertise
     *
     * List of industries candidate has experience in
     */
    industry_expertise: Array<Industry>;
    /**
     * Target industries
     *
     * Functional expertise candidate would like to get
     */
    industry_target: Array<Industry>;
    /**
     * Board/advisory role interest
     *
     * Interest in getting board or advisory roles
     */
    interest_role_board_advisory?: InterestRole | null;
    /**
     * Operating role interest
     *
     * Interest in getting operating roles
     */
    interest_role_operating?: InterestRole | null;
    /**
     * Personal interests
     *
     * List of personal interests
     */
    interests_personal: Array<InterestPersonal>;
    /**
     * Professional interests
     *
     * List of professional interests
     */
    interests_professional: Array<InterestProfessional>;
    /**
     * Keywords
     *
     * List of keywords
     */
    keywords: Array<string>;
    /**
     * Last name
     *
     * Candidate's last name
     */
    last_name?: string | null;
    /**
     * LinkedIn URL
     *
     * Link to the person's LinkedIn profile
     */
    linkedin_url?: string | null;
    /**
     * Location
     *
     * Candidate's location (from their resume)
     */
    location?: string | null;
    /**
     * Tier
     *
     * Candidate tier
     */
    tier: CandidateTier;
    /**
     * Invoice Status
     *
     * HubSpot invoice status (admin-facing; tier 1/1b only)
     */
    invoice_status?: HubSpotInvoiceStatus | null;
    /**
     * Type
     *
     * Candidate type
     */
    type?: ClientType | null;
    /**
     * Pathway
     *
     * Banff service pathway for the candidate
     */
    pathway?: CandidatePathway | null;
    /**
     * Profile Image Url
     */
    readonly profile_image_url: string | null;
};

/**
 * ClerkUserPublicMetadataUpdate
 */
export type ClerkUserPublicMetadataUpdate = {
    /**
     * Onboarding Complete
     */
    onboarding_complete: boolean;
};

/**
 * ClientType
 *
 * Client types stored on contender_candidate.type.
 */
export const ClientType = {
    PRIVATE_ADVISOR: 'private_advisor',
    POSITIONER_SERVICES: 'positioner_services',
    BRAND_STRATEGIC_IDENTITY: 'brand_strategic_identity',
    PORTAGE_MAIN: 'portage_main'
} as const;

/**
 * ClientType
 *
 * Client types stored on contender_candidate.type.
 */
export type ClientType = typeof ClientType[keyof typeof ClientType];

/**
 * CommentPayload
 */
export type CommentPayload = {
    /**
     * Comment
     *
     * Comment left about a candidate
     */
    content: string;
    /**
     * Team Uuids
     *
     * Return info for specified teams only
     */
    team_uuids?: Array<string> | null;
};

/**
 * CommentResponse
 */
export type CommentResponse = {
    /**
     * Candidate
     *
     * Candidate record of the human this comment is about; null when they have no candidate record
     */
    candidate?: UuidResponse | null;
    /**
     * Person
     *
     * Comment author
     */
    person?: PersonBasicResponse | null;
    /**
     * Comment text
     *
     * The content of the recommendation comment
     */
    content: string;
    /**
     * Created
     *
     * Date when this comment was published
     */
    created: string;
    /**
     * Comment ID
     *
     * The unique identifier for comment
     */
    id: string;
    /**
     * Last modified
     *
     * Date when this comment was modified
     */
    last_modified: string;
    /**
     * Pipeline
     *
     * Pipeline in which this comment was made
     */
    pipeline?: UuidResponse | null;
    /**
     * Recommendation
     *
     * Recommendation for which this comment was made
     */
    recommendation?: UuidResponse | null;
    /**
     * Subject identity UUID
     *
     * Identity (human) this comment is about
     */
    subject_identity_uuid: string;
    /**
     * Team
     *
     * Team in which this comment was made
     */
    team: UuidResponse;
};

/**
 * CompaniesFilter
 */
export const CompaniesFilter = {
    ALL: 'all',
    CORPORATE: 'corporate',
    FREE: 'free',
    INVESTOR: 'investor',
    PREMIUM: 'premium',
    SEARCH: 'search'
} as const;

/**
 * CompaniesFilter
 */
export type CompaniesFilter = typeof CompaniesFilter[keyof typeof CompaniesFilter];

/**
 * CompaniesFilters
 */
export type CompaniesFilters = {
    /**
     * Fund type
     *
     * Filter by the kind of capital the firm deploys. Matches a company carrying ANY of the selected values, so a firm tagged both VC and PE appears under either. Team-level overrides are deliberately ignored: this answers 'which firms ARE private equity', not 'which firms have a PE team somewhere'.
     */
    fund_type?: Array<FundType> | null;
    /**
     * Payment status
     *
     * Filter by payment status
     */
    payment_status?: Array<PaymentStatus> | null;
    /**
     * Type
     *
     * Filter by organization type
     */
    type?: Array<OrgType> | null;
    /**
     * Has Members
     *
     * Filter by whether company has at least one team member (team -> person_role -> person)
     */
    has_members?: boolean | null;
    /**
     * Active
     *
     * Filter by active flag; omit to include inactive companies
     */
    active?: boolean | null;
};

/**
 * CompaniesSearchFilterSortPayload
 */
export type CompaniesSearchFilterSortPayload = {
    /**
     * Phrase
     *
     * Search phrase
     */
    phrase?: string | null;
    /**
     * Search ID
     *
     * Search session ID
     */
    search_id?: string | null;
    /**
     * Page
     *
     * Page number
     */
    page?: number;
    /**
     * Page size
     *
     * Page size
     */
    page_size?: number;
    /**
     * Filters
     *
     * Filter results by these criteria
     */
    filter_data?: CompaniesFilters | null;
    /**
     * Sort
     *
     * Sort results by these rules
     */
    sort_rules?: Array<CompaniesSort>;
};

/**
 * CompaniesSort
 */
export type CompaniesSort = {
    /**
     * Column
     *
     * Sort results on this column
     */
    column: CompaniesSortColumn;
    /**
     * Direction
     *
     * Sort results in this direction
     */
    direction?: SortDirection;
};

/**
 * CompaniesSortColumn
 */
export const CompaniesSortColumn = {
    NAME: 'name',
    PAYMENT_STATUS: 'payment_status',
    TYPE: 'type',
    USERS_COUNT: 'users_count',
    TEAMS_COUNT: 'teams_count'
} as const;

/**
 * CompaniesSortColumn
 */
export type CompaniesSortColumn = typeof CompaniesSortColumn[keyof typeof CompaniesSortColumn];

/**
 * CompaniesSorter
 */
export const CompaniesSorter = {
    '': '',
    NAME: 'name',
    STATUS: 'status',
    TEAMS: 'teams',
    TYPE: 'type',
    USERS: 'users'
} as const;

/**
 * CompaniesSorter
 */
export type CompaniesSorter = typeof CompaniesSorter[keyof typeof CompaniesSorter];

/**
 * CompanyBasicResponse
 */
export type CompanyBasicResponse = {
    /**
     * ID
     *
     * Unique identifier for a company
     */
    id: number;
    /**
     * Name
     *
     * Company name. Always null for an executive's personal (type=executive) company — that record has no name to show.
     */
    name: string | null;
    /**
     * Slug
     *
     * Slug uniquely identifying a company
     */
    slug: string;
};

/**
 * CompanyBasicTypeResponse
 */
export type CompanyBasicTypeResponse = {
    /**
     * ID
     *
     * Unique identifier for a company
     */
    id: number;
    /**
     * Name
     *
     * Company name. Always null for an executive's personal (type=executive) company — that record has no name to show.
     */
    name: string | null;
    /**
     * Slug
     *
     * Slug uniquely identifying a company
     */
    slug: string;
    /**
     * Company Type
     *
     * Type of company
     */
    type: OrgType;
};

/**
 * CompanyCreate
 */
export type CompanyCreate = {
    /**
     * Fund type
     *
     * What kind of capital this firm deploys. Multi-select: a firm that genuinely does both carries both values. Empty means no answer — it does not distinguish 'not an investment firm' from 'not yet reviewed'.
     */
    fund_type?: Array<FundType> | null;
    /**
     * Domain
     *
     * Company website domain
     */
    domain?: string | null;
    /**
     * Company status
     *
     * Status may affect permissions access
     */
    payment_status?: PaymentStatus;
    /**
     * Slug
     *
     * Company slug if we want to set it ourselves instead of using the generated value
     */
    slug?: string | null;
    /**
     * Stage
     *
     * Company stage
     */
    stage?: CompanyStage | null;
    /**
     * Company type
     *
     * Type controls permissions access in addition to being a meta data
     */
    customer_type?: OrgType | null;
    /**
     * Name
     *
     * Name of the company
     */
    organization: string;
};

/**
 * CompanyCreateV1
 *
 * Schema for POST /api/v1/companies.
 *
 * name, type, slug, payment_status and fund_type can be set.
 */
export type CompanyCreateV1 = {
    /**
     * Name
     *
     * Name of the company
     */
    organization: string;
    /**
     * Company type
     *
     * Type controls permissions access in addition to being metadata
     */
    customer_type: OrgType;
    /**
     * Slug
     *
     * Company slug if we want to set it ourselves instead of using the generated value
     */
    slug?: string | null;
    /**
     * Company status
     *
     * Status may affect permissions access
     */
    payment_status?: PaymentStatus | null;
    /**
     * Fund type
     *
     * What kind of capital this firm deploys
     */
    fund_type?: Array<FundType> | null;
};

/**
 * CompanyDetailedResponse
 *
 * Detailed company response including teams and users
 */
export type CompanyDetailedResponse = {
    /**
     * ID
     *
     * Unique identifier for a company
     */
    id: number;
    /**
     * Name
     *
     * Company name. Always null for an executive's personal (type=executive) company — that record has no name to show.
     */
    name: string | null;
    /**
     * Slug
     *
     * Slug uniquely identifying a company
     */
    slug: string;
    /**
     * Company Type
     *
     * Type of company
     */
    type: OrgType;
    /**
     * Fund type
     *
     * What kind of capital this firm deploys. Empty means no answer, which covers 'not an investment firm' and 'not yet reviewed' alike.
     */
    fund_type?: Array<FundType> | null;
    /**
     * Payment status
     *
     * Controls access to Contender features in combination with company type
     */
    payment_status: PaymentStatus;
    /**
     * Active
     *
     * Inactive companies keep their data but their teams are excluded from search results and recommendations
     */
    active?: boolean;
    /**
     * Teams count
     *
     * Total number of teams belonging to this company
     */
    teams_count: number;
    /**
     * Users count
     *
     * Total number of users belonging to this company
     */
    users_count: number;
    /**
     * Teams
     *
     * List of teams belonging to this company
     */
    teams?: Array<CompanyTeamBasicResponse>;
    /**
     * Users
     *
     * List of users belonging to this company (through their teams)
     */
    users?: Array<CompanyUserBasicResponse>;
};

/**
 * CompanyExtendedResponse
 */
export type CompanyExtendedResponse = {
    /**
     * ID
     *
     * Unique identifier for a company
     */
    id: number;
    /**
     * Name
     *
     * Company name. Always null for an executive's personal (type=executive) company — that record has no name to show.
     */
    name: string | null;
    /**
     * Slug
     *
     * Slug uniquely identifying a company
     */
    slug: string;
    /**
     * Company Type
     *
     * Type of company
     */
    type: OrgType;
    /**
     * Fund type
     *
     * What kind of capital this firm deploys. Empty means no answer, which covers 'not an investment firm' and 'not yet reviewed' alike.
     */
    fund_type?: Array<FundType> | null;
    /**
     * Payment status
     *
     * Controls access to Contender features in combination with company type
     */
    payment_status: PaymentStatus;
    /**
     * Active
     *
     * Inactive companies keep their data but their teams are excluded from search results and recommendations
     */
    active?: boolean;
};

/**
 * CompanyExtendedResponseWithFeatures
 */
export type CompanyExtendedResponseWithFeatures = {
    /**
     * ID
     *
     * Unique identifier for a company
     */
    id: number;
    /**
     * Name
     *
     * Company name. Always null for an executive's personal (type=executive) company — that record has no name to show.
     */
    name: string | null;
    /**
     * Slug
     *
     * Slug uniquely identifying a company
     */
    slug: string;
    /**
     * Company Type
     *
     * Type of company
     */
    type: OrgType;
    /**
     * Fund type
     *
     * What kind of capital this firm deploys. Empty means no answer, which covers 'not an investment firm' and 'not yet reviewed' alike.
     */
    fund_type?: Array<FundType> | null;
    /**
     * Payment status
     *
     * Controls access to Contender features in combination with company type
     */
    payment_status: PaymentStatus;
    /**
     * Active
     *
     * Inactive companies keep their data but their teams are excluded from search results and recommendations
     */
    active?: boolean;
    /**
     * Permissions
     *
     * List of permissions the company has access to
     */
    permissions: Array<CompanyPermissions>;
};

/**
 * CompanyOwnership
 */
export const CompanyOwnership = {
    NONPROFIT: 'Nonprofit',
    PRIVATE: 'Private',
    PUBLIC: 'Public',
    GOVERNMENT: 'Government'
} as const;

/**
 * CompanyOwnership
 */
export type CompanyOwnership = typeof CompanyOwnership[keyof typeof CompanyOwnership];

/**
 * CompanyPatchPayload
 */
export type CompanyPatchPayload = {
    /**
     * Fund type
     *
     * What kind of capital this firm deploys. Send an empty list or null to clear it; omit the field entirely to leave it unchanged.
     */
    fund_type?: Array<FundType> | null;
    /**
     * Payment status
     *
     * Controls access to Contender features in combination with company type
     */
    payment_status?: PaymentStatus | null;
    /**
     * Name
     *
     * Name of the company
     */
    organization?: string | null;
    /**
     * Slug
     *
     * Company slug
     */
    slug?: string | null;
    /**
     * Company type
     *
     * Type controls permissions access in addition to being a meta data
     */
    customer_type?: OrgType | null;
    /**
     * Active
     *
     * Admins only. Set false to deactivate the company: its data is kept but its teams are archived, their open pipelines closed, and the company drops out of search results and recommendations. Deactivation is permanent
     */
    active?: boolean | null;
};

/**
 * CompanyPeopleAggregations
 */
export type CompanyPeopleAggregations = {
    /**
     * Company Team Status
     */
    company_team_status?: {
        [key in TeamConnectionStatus]?: number;
    };
    /**
     * Recommendation Type
     *
     * Counts of shared candidates by pipeline/trending/both
     */
    recommendation_type?: {
        [key: string]: number;
    };
};

/**
 * CompanyPeopleCandidateSummary
 */
export type CompanyPeopleCandidateSummary = {
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Name
     */
    name: string;
    /**
     * Role
     *
     * Current role
     */
    role?: string | null;
    /**
     * Company
     *
     * Current company name
     */
    company?: string | null;
    /**
     * Image
     *
     * Profile image URL or storage key (same semantics as getCandidateV2)
     */
    image?: string | null;
    /**
     * Is Client
     *
     * Whether the person is a Banff client
     */
    is_client: boolean;
    /**
     * Email
     *
     * Optional; helps admins disambiguate duplicate names
     */
    email?: string | null;
};

/**
 * CompanyPeopleFilterPayload
 */
export type CompanyPeopleFilterPayload = {
    /**
     * Phrase
     *
     * Search phrase
     */
    phrase?: string | null;
    /**
     * Search ID
     *
     * Search session ID
     */
    search_id?: string | null;
    /**
     * Page
     *
     * Page number
     */
    page?: number;
    /**
     * Page size
     *
     * Page size
     */
    page_size?: number;
    /**
     * Which candidates to include in the result set
     */
    company_people_scope?: CompanyPeopleScope;
    /**
     * Company Team Status
     *
     * Filter by rollup connection status across company teams
     */
    company_team_status?: Array<TeamConnectionStatus> | null;
    /**
     * Has Metadata
     *
     * Filter by whether the candidate has company metadata
     */
    has_metadata?: boolean | null;
    /**
     * Is Shared
     *
     * Filter by whether the candidate has at least one published pipeline or trending recommendation on any company team
     */
    is_shared?: boolean | null;
    /**
     * Sort Rules
     *
     * Sort results by these rules
     */
    sort_rules?: Array<CompanyPeopleSort>;
};

/**
 * CompanyPeopleFilterResponse
 */
export type CompanyPeopleFilterResponse = {
    /**
     * Page
     */
    page: number;
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Size
     */
    size: number;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Results
     */
    results: Array<CompanyPeopleRow>;
    total_counts: CompanyPeopleTotalCounts;
    aggregations: CompanyPeopleAggregations;
};

/**
 * CompanyPeopleRecommendationType
 */
export const CompanyPeopleRecommendationType = { PIPELINE: 'pipeline', TRENDING: 'trending' } as const;

/**
 * CompanyPeopleRecommendationType
 */
export type CompanyPeopleRecommendationType = typeof CompanyPeopleRecommendationType[keyof typeof CompanyPeopleRecommendationType];

/**
 * CompanyPeopleRow
 */
export type CompanyPeopleRow = {
    /**
     * Candidate Uuid
     */
    candidate_uuid: string;
    /**
     * Name
     */
    name: string;
    /**
     * Role
     */
    role?: string | null;
    /**
     * Company
     */
    company?: string | null;
    /**
     * Image
     */
    image?: string | null;
    /**
     * Is Client
     */
    is_client: boolean;
    /**
     * Email
     */
    email?: string | null;
    /**
     * Has Metadata
     */
    has_metadata: boolean;
    /**
     * Is Shared
     *
     * True when the candidate has at least one published pipeline or trending recommendation on any company team
     */
    is_shared: boolean;
    company_metadata?: CandidateCompanyMetadataResponse | null;
    company_team_status: TeamConnectionStatus;
    /**
     * Recommendations Count
     *
     * Total published pipeline and trending recommendations across company teams
     */
    recommendations_count?: number;
    /**
     * Shared Teams
     */
    shared_teams?: Array<CompanyPeopleSharedTeamResponse>;
};

/**
 * CompanyPeopleScope
 */
export const CompanyPeopleScope = {
    ALL: 'all',
    SHARED: 'shared',
    METADATA_ONLY: 'metadata_only'
} as const;

/**
 * CompanyPeopleScope
 */
export type CompanyPeopleScope = typeof CompanyPeopleScope[keyof typeof CompanyPeopleScope];

/**
 * CompanyPeopleSharedTeamResponse
 */
export type CompanyPeopleSharedTeamResponse = {
    /**
     * Team Uuid
     */
    team_uuid: string;
    /**
     * Team Name
     */
    team_name: string;
    /**
     * Recommendation Types
     *
     * Recommendation kinds present on this team
     */
    recommendation_types?: Array<CompanyPeopleRecommendationType>;
    /**
     * Connection status rollup for this team
     */
    company_team_status: TeamConnectionStatus;
    /**
     * Date Recommended Last
     *
     * Most recent recommendation-added date on this team
     */
    date_recommended_last?: string | null;
};

/**
 * CompanyPeopleSort
 */
export type CompanyPeopleSort = {
    /**
     * Column
     *
     * Sort results on this column
     */
    column: CompanyPeopleSortColumn;
    /**
     * Direction
     *
     * Sort direction
     */
    direction: SortDirection;
};

/**
 * CompanyPeopleSortColumn
 */
export const CompanyPeopleSortColumn = {
    NAME: 'name',
    LAST_MODIFIED: 'last_modified',
    COMPANY_TEAM_STATUS: 'company_team_status',
    RECOMMENDATIONS_COUNT: 'recommendations_count'
} as const;

/**
 * CompanyPeopleSortColumn
 */
export type CompanyPeopleSortColumn = typeof CompanyPeopleSortColumn[keyof typeof CompanyPeopleSortColumn];

/**
 * CompanyPeopleTotalCounts
 */
export type CompanyPeopleTotalCounts = {
    /**
     * All
     *
     * Metadata ∪ shared candidates
     */
    all: number;
    /**
     * Shared
     *
     * Candidates with at least one published recommendation
     */
    shared: number;
    /**
     * Metadata Only
     *
     * Has metadata and zero recommendations on company teams
     */
    metadata_only: number;
    /**
     * Both
     *
     * Has metadata and is shared
     */
    both: number;
    /**
     * Team Status None
     *
     * Candidates whose company_team_status rollup is none
     */
    team_status_none: number;
};

/**
 * CompanyPermissions
 */
export const CompanyPermissions = {
    PEOPLE: 'people',
    PIPELINES: 'pipelines',
    TRENDING: 'trending'
} as const;

/**
 * CompanyPermissions
 */
export type CompanyPermissions = typeof CompanyPermissions[keyof typeof CompanyPermissions];

/**
 * CompanyResponse
 */
export type CompanyResponse = {
    /**
     * Dates
     *
     * Period during which person worked for the company
     */
    dates?: string | null;
    /**
     * Name
     *
     * Name of company
     */
    name: string;
    /**
     * Positions
     *
     * List of positions person held within the company
     */
    positions: Array<PositionResponse>;
};

/**
 * CompanySector
 *
 * Attributes
 * ----------
 * name : str
 * sector name
 * code : str
 * ICB Sector code
 */
export type CompanySector = {
    /**
     * Code
     */
    code: string;
    /**
     * Sector
     */
    sector: string;
};

/**
 * CompanySize
 */
export const CompanySize = {
    UNDER_10M: 'under_10m',
    FROM_10M_TO_50M: 'from_10m_to_50m',
    FROM_50M_TO_100M: 'from_50m_to_100m',
    FROM_100M_TO_500M: 'from_100m_to_500m',
    FROM_500M_TO_1B: 'from_500m_to_1b',
    FROM_1B_TO_5B: 'from_1b_to_5b',
    FROM_5B_TO_10B: 'from_5b_to_10b',
    FROM_10B_TO_50B: 'from_10b_to_50b',
    FROM_50B_TO_100B: 'from_50b_to_100b',
    ABOVE_100B: 'above_100b'
} as const;

/**
 * CompanySize
 */
export type CompanySize = typeof CompanySize[keyof typeof CompanySize];

/**
 * CompanyStage
 */
export const CompanyStage = {
    SEED: 'Seed',
    EARLY_STAGE: 'Early Stage',
    ESTABLISHED: 'Established',
    PRE_IPO_GROWTH: 'Pre-IPO/Growth'
} as const;

/**
 * CompanyStage
 */
export type CompanyStage = typeof CompanyStage[keyof typeof CompanyStage];

/**
 * CompanyTableResponse
 */
export type CompanyTableResponse = {
    /**
     * Meta
     *
     * Metadata about the search result
     */
    search_metadata?: EsMeta | null;
    /**
     * ID
     *
     * Unique identifier for a company
     */
    id: number;
    /**
     * Name
     *
     * Company name. Always null for an executive's personal (type=executive) company — that record has no name to show.
     */
    name: string | null;
    /**
     * Slug
     *
     * Slug uniquely identifying a company
     */
    slug: string;
    /**
     * Company Type
     *
     * Type of company
     */
    type: OrgType;
    /**
     * Fund type
     *
     * What kind of capital this firm deploys. Empty means no answer, which covers 'not an investment firm' and 'not yet reviewed' alike.
     */
    fund_type?: Array<FundType> | null;
    /**
     * Payment status
     *
     * Controls access to Contender features in combination with company type
     */
    payment_status: PaymentStatus;
    /**
     * Active
     *
     * Inactive companies keep their data but their teams are excluded from search results and recommendations
     */
    active?: boolean;
    /**
     * Permissions
     *
     * List of permissions the company has access to
     */
    permissions: Array<CompanyPermissions>;
    /**
     * Teams count
     *
     * Total number of teams belonging to this company
     */
    teams_count: number;
    /**
     * Users count
     *
     * Total number of users belonging to this company
     */
    users_count: number;
};

/**
 * CompanyTagAssociationCreatePayload
 *
 * Exactly one of ``tag_definition_uuid`` (existing) or ``name`` (create + link).
 */
export type CompanyTagAssociationCreatePayload = {
    /**
     * Tag Definition Uuid
     */
    tag_definition_uuid?: string | null;
    /**
     * Name
     */
    name?: string | null;
};

/**
 * CompanyTagDefinitionResponse
 */
export type CompanyTagDefinitionResponse = {
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Name
     */
    name: string;
    /**
     * Created
     */
    created: string;
    /**
     * Last Modified
     */
    last_modified: string;
    /**
     * Usage Count
     *
     * Number of team-people candidates with this tag on the company's metadata (when requested).
     */
    usage_count?: number | null;
};

/**
 * CompanyTagOnContactResponse
 *
 * After adding a tag to a company candidate.
 */
export type CompanyTagOnContactResponse = {
    /**
     * Association Uuid
     */
    association_uuid: string;
    /**
     * Tag Definition Uuid
     */
    tag_definition_uuid: string;
    /**
     * Name
     */
    name: string;
};

/**
 * CompanyTeamBasicResponse
 *
 * Basic team information for company response
 */
export type CompanyTeamBasicResponse = {
    /**
     * ID
     *
     * Unique identifier for a team
     */
    id: number;
    /**
     * UUID
     *
     * UUID uniquely identifying a team
     */
    uuid: string;
    /**
     * Slug
     *
     * Slug uniquely identifying a team
     */
    slug: string;
    /**
     * Name
     *
     * Team name
     */
    team_name: string;
    /**
     * Active
     *
     * False for an archived team
     */
    active?: boolean;
    /**
     * Additional details
     *
     * Additional details about the team
     */
    additional_details?: string | null;
    /**
     * Client size
     *
     * Typical client size
     */
    client_size?: SearchFirmType | null;
    /**
     * Members
     *
     * List of team members (person roles)
     */
    members?: Array<CompanyTeamMemberResponse>;
};

/**
 * CompanyTeamCreate
 */
export type CompanyTeamCreate = {
    /**
     * Team
     *
     * The new team to create for an existing company
     */
    team: TeamCreateV2;
    /**
     * Company
     *
     * The existing company to associate the team with
     */
    company?: CompanyUpdate | null;
};

/**
 * CompanyTeamMemberResponse
 *
 * Team member information for company response
 */
export type CompanyTeamMemberResponse = {
    /**
     * ID
     *
     * Unique identifier for the person role
     */
    id: number;
    /**
     * Role
     *
     * Role of the person in the team
     */
    role: string;
    /**
     * Person ID
     *
     * UUID of the person
     */
    person_id: string;
    /**
     * Is Admin
     *
     * Flag indicating if the member has admin privileges
     */
    is_admin?: boolean;
    /**
     * Permit Pre-registration Notifications
     *
     * Whether the member allows pre-registration notifications
     */
    permit_preregistration_notifications?: boolean;
    /**
     * Created
     *
     * Timestamp when the member record was created
     */
    created?: string | null;
};

/**
 * CompanyType
 */
export const CompanyType = {
    NONPROFIT: 'nonprofit',
    OTHER: 'other',
    PRIVATE: 'private',
    PUBLIC: 'public'
} as const;

/**
 * CompanyType
 */
export type CompanyType = typeof CompanyType[keyof typeof CompanyType];

/**
 * CompanyUpdate
 *
 * Schema for updating an existing company
 */
export type CompanyUpdate = {
    /**
     * Fund type
     *
     * What kind of capital this firm deploys. Multi-select: a firm that genuinely does both carries both values. Empty means no answer — it does not distinguish 'not an investment firm' from 'not yet reviewed'.
     */
    fund_type?: Array<FundType> | null;
    /**
     * Name
     *
     * Name of the company
     */
    organization?: string | null;
    /**
     * Company status
     *
     * Status may affect permissions access
     */
    payment_status?: PaymentStatus | null;
    /**
     * Company type
     *
     * Type controls permissions access in addition to being a meta data
     */
    customer_type?: OrgType | null;
};

/**
 * CompanyUploadResponse
 */
export type CompanyUploadResponse = {
    /**
     * Dates
     *
     * Period during which person worked for the company
     */
    dates?: string | null;
    /**
     * Name
     *
     * Name of company
     */
    name: string;
    /**
     * Positions
     *
     * List of positions person held within the company
     */
    positions: Array<PositionUploadResponse>;
};

/**
 * CompanyUserBasicResponse
 *
 * Basic user information for company response
 */
export type CompanyUserBasicResponse = {
    /**
     * ID
     *
     * Unique identifier for a user
     */
    id: string;
    /**
     * Email
     *
     * User email address
     */
    email: string;
    /**
     * First Name
     *
     * User's first name
     */
    first_name?: string | null;
    /**
     * Last Name
     *
     * User's last name
     */
    last_name?: string | null;
    /**
     * Is Admin
     *
     * Flag indicating if the user has admin privileges
     */
    is_admin?: boolean;
    /**
     * Permit Pre-registration Notifications
     *
     * Whether the user allows pre-registration notifications
     */
    permit_preregistration_notifications?: boolean;
    /**
     * Created
     *
     * Timestamp when the user record was created
     */
    created?: string | null;
};

/**
 * ConnectionStatus
 */
export const ConnectionStatus = {
    IS_FAVORITE: 'is_favorite',
    INTRODUCED: 'introduced',
    INTRODUCTION_PENDING: 'introduction_pending'
} as const;

/**
 * ConnectionStatus
 */
export type ConnectionStatus = typeof ConnectionStatus[keyof typeof ConnectionStatus];

/**
 * Contact
 */
export type Contact = {
    /**
     * Linkedin
     */
    linkedin: string;
};

/**
 * ContactEvidenceResponse
 *
 * Debug trace of why a contact appears on the people page.
 *
 * Temporary — will be removed once the people-list rollout is verified.
 */
export type ContactEvidenceResponse = {
    source: ContactSourceType;
    /**
     * Ref Uuid
     */
    ref_uuid?: string | null;
    /**
     * Url
     */
    url?: string | null;
    /**
     * Subtype
     */
    subtype?: string | null;
};

/**
 * ContactFavoriteResponse
 */
export type ContactFavoriteResponse = {
    /**
     * Contact Uuid
     *
     * The contact uuid from the path (a person or candidate uuid)
     */
    contact_uuid: string;
    /**
     * Is Favorite
     *
     * Whether the contact is favorited
     */
    is_favorite: boolean;
};

/**
 * ContactListsPushResponse
 */
export type ContactListsPushResponse = {
    /**
     * List Names
     *
     * Names of the SendGrid lists created
     */
    list_names: Array<string>;
};

/**
 * ContactSearchResult
 *
 * One human in the merged person+candidate typeahead.
 *
 * The row carries the identity only, not the login or candidate profile it
 * was found through, so a form that asks "who" can only store the identity
 * (BAN-6065).
 */
export type ContactSearchResult = {
    /**
     * Identity UUID
     *
     * The identity (human) this row stands for — the value to store when a form asks 'who'
     */
    identity_uuid: string;
    /**
     * Full Name
     */
    full_name?: string | null;
    /**
     * Email
     */
    email?: string | null;
    /**
     * Profile image
     */
    image?: string | null;
    /**
     * Company Name
     */
    company_name?: string | null;
    /**
     * Current Role
     */
    title?: string | null;
};

/**
 * ContactSourceType
 *
 * Source of a contact on the executive client people page.
 */
export const ContactSourceType = {
    B4B_INTRO: 'b4b_intro',
    EVENT: 'event',
    P2P_INTRO: 'p2p_intro',
    REFERRAL: 'referral'
} as const;

/**
 * ContactSourceType
 *
 * Source of a contact on the executive client people page.
 */
export type ContactSourceType = typeof ContactSourceType[keyof typeof ContactSourceType];

/**
 * ContactType
 *
 * Who put the role in front of the client — which is not always a firm.
 *
 * `search_firm_name` answers "who is running this search", and a third of the
 * historical values given for it are not firms at all: "Direct", "(internal
 * recruiter)", "Internal TA" all mean the company approached the client
 * itself. Left as text they never resolve, and they turn up in any ranking of
 * "which firms approach our clients" — a caveat in a retrieval skill would be
 * a request, not a guarantee, so the distinction is a column.
 *
 * Banff relaying a role an advisor heard about is deliberately NOT a contact
 * type here: that answers "how did we learn", which is `reported_by_person_id`.
 */
export const ContactType = { SEARCH_FIRM: 'search_firm', COMPANY_ITSELF: 'company_itself' } as const;

/**
 * ContactType
 *
 * Who put the role in front of the client — which is not always a firm.
 *
 * `search_firm_name` answers "who is running this search", and a third of the
 * historical values given for it are not firms at all: "Direct", "(internal
 * recruiter)", "Internal TA" all mean the company approached the client
 * itself. Left as text they never resolve, and they turn up in any ranking of
 * "which firms approach our clients" — a caveat in a retrieval skill would be
 * a request, not a guarantee, so the distinction is a column.
 *
 * Banff relaying a role an advisor heard about is deliberately NOT a contact
 * type here: that answers "how did we learn", which is `reported_by_person_id`.
 */
export type ContactType = typeof ContactType[keyof typeof ContactType];

/**
 * CountriesResponse
 */
export type CountriesResponse = {
    /**
     * Countries
     *
     * List of alphabetically sorted country names
     */
    countries: Array<string>;
};

/**
 * CreateCandidate
 */
export type CreateCandidate = {
    /**
     * First Name
     *
     * Person's first name
     */
    first_name: string;
    /**
     * Last Name
     *
     * Person's last name
     */
    last_name: string;
    /**
     * Email
     *
     * Email associated with the person
     */
    email: string;
    /**
     * LinkedIn URL
     *
     * Link to the person's LinkedIn profile
     */
    linkedin_url?: string | null;
    /**
     * Advisor UUID
     *
     * UUID uniquely identifying an advisor
     */
    advisor_uuid?: string | null;
    /**
     * Company
     *
     * Candidate's current company
     */
    last_company?: string | null;
    /**
     * Role
     *
     * Candidate's current role
     */
    last_role?: string | null;
    /**
     * Client owner uuid
     *
     * Uuid for the client owner of the candidate
     */
    client_owner_uuid?: string | null;
    /**
     * URG
     *
     * Is this candidate from an under-represented group?
     */
    is_urg?: boolean;
    /**
     * Functional expertise
     *
     * List of functions candidate has experience in
     */
    function_expertise: Array<Function>;
    /**
     * Target functions
     *
     * Functional expertise candidate would like to get
     */
    function_target: Array<Function>;
    /**
     * Industry expertise
     *
     * List of industries candidate has experience in
     */
    industry_expertise: Array<Industry>;
    /**
     * Target industries
     *
     * Functional expertise candidate would like to get
     */
    industry_target: Array<Industry>;
    /**
     * Board/advisory role interest
     *
     * Interest in getting board or advisory roles
     */
    interest_role_board_advisory?: InterestRole | null;
    /**
     * Operating role interest
     *
     * Interest in getting operating roles
     */
    interest_role_operating?: InterestRole | null;
    /**
     * Personal interests
     *
     * List of personal interests
     */
    interests_personal?: Array<InterestPersonal> | null;
    /**
     * Professional interests
     *
     * List of professional interests
     */
    interests_professional?: Array<InterestProfessional> | null;
    /**
     * Interests notes
     *
     * Notes on interests
     */
    interests_notes?: string | null;
    /**
     * Wants intros
     *
     * Whether the candidate wants introductions
     */
    wants_intros?: boolean | null;
    /**
     * Interest in relocation
     *
     * Openness to relocation
     */
    interest_relocation?: boolean | null;
    /**
     * Relocation notes
     *
     * Relocation preferences or constraints
     */
    interest_relocation_notes?: string | null;
    /**
     * Introduction email type
     *
     * Introduction email type (automatic or manual)
     */
    intro_email_type?: IntroEmailType;
    /**
     * Kickoff date
     *
     * Date candidate kicked off
     */
    kickoff_date?: string | null;
    /**
     * Not accepting start date
     *
     * Date from which the candidate is not accepting intros
     */
    not_accepting_start_date?: string | null;
    /**
     * Not accepting end date
     *
     * Date until which the candidate is not accepting intros
     */
    not_accepting_end_date?: string | null;
    /**
     * Keywords
     *
     * List of keywords
     */
    keywords: Array<string>;
    /**
     * Target network
     *
     * Areas in which this person wants to build their network
     */
    network_target?: string | null;
    /**
     * Notes
     *
     * Internal advisor notes on the candidate
     */
    notes?: string | null;
    /**
     * Operating highlights
     *
     * List of key operating experiences
     */
    operating_highlights?: Array<OperatingHighlights> | null;
    /**
     * Passions
     *
     * Hobbies, locations, niche industries, personal interests, etc.
     */
    passions?: string | null;
    /**
     * Image
     *
     * Name of the uploaded image from the response
     */
    image?: string | null;
    /**
     * Referrers
     *
     * List of person IDs
     */
    referrers: Array<string>;
    /**
     * Resume
     *
     * Response from the resume upload endpoint
     */
    resume?: LinkedInResumeUploadResponseInput | null;
    /**
     * Tier
     *
     * Candidate tier
     */
    tier: CandidateTier;
    /**
     * Type
     *
     * Candidate type
     */
    type?: ClientType | null;
    /**
     * Pathway
     *
     * Banff service pathway for the candidate
     */
    pathway?: CandidatePathway | null;
    /**
     * Source company tags
     *
     * For a candidate with a source company, an optional list of custom tags
     */
    source_company_tags?: Array<SourceCompanyTags> | null;
};

/**
 * CreateClerkUserRequest
 */
export type CreateClerkUserRequest = {
    /**
     * Password
     *
     * Password for the Clerk user account
     */
    password: string;
    /**
     * First Name
     *
     * First name for the user
     */
    first_name: string;
    /**
     * Last Name
     *
     * Last name for the user
     */
    last_name: string;
    /**
     * Onboarding Complete
     *
     * Whether the user has completed onboarding
     */
    onboarding_complete?: boolean;
    /**
     * Suppress Welcome Email
     *
     * Whether to suppress the welcome email
     */
    suppress_welcome_email?: boolean;
};

/**
 * CreatePipelinePayload
 */
export type CreatePipelinePayload = {
    /**
     * Is visible to client
     *
     * Hide this pipeline from its team members?
     */
    is_visible_to_team?: boolean | null;
    /**
     * Keywords
     *
     * List of keywords
     */
    keywords: Array<string>;
    /**
     * Placement
     *
     * Placed candidate UUID
     */
    placed_candidate_id?: string | null;
    /**
     * Is proactive
     *
     * Is this pipeline proactive?
     */
    is_proactive?: boolean;
    /**
     * Is notification enabled
     *
     * Notify the team and Banff when published?
     */
    is_notification_enabled?: boolean | null;
    /**
     * Attendees
     *
     * Number of attendees expected for an event pipeline
     */
    attendees?: PipelineAttendees | null;
    /**
     * Company
     *
     * Name or nickname of the company for which this pipeline is open
     */
    company?: string | null;
    /**
     * Company size (annual revenue)
     *
     * Size of company for which this pipeline is open
     */
    company_size?: CompanySize | null;
    /**
     * Company type
     *
     * Type of company for which this pipeline is open
     */
    company_type?: CompanyType | null;
    /**
     * Event date
     *
     * Date of the event
     */
    event_date?: string | null;
    /**
     * Event name
     *
     * Name of the event
     */
    event_name?: string | null;
    /**
     * Functional expertise
     *
     * List of functions the ideal candidate has experience in
     */
    function_expertise: Array<Function>;
    /**
     * Industry expertise
     *
     * List of industries the ideal candidate has experience in
     */
    industry_expertise: Array<Industry>;
    /**
     * Is URG
     *
     * Is the role specifically targeting under represented candidates?
     */
    is_urg?: boolean | null;
    /**
     * Location
     *
     * The location specified for the pipeline role
     */
    location?: string | null;
    /**
     * Name
     *
     * Pipeline name
     */
    name: string;
    /**
     * Notes
     *
     * Any additional details to help us calibrate recommendations? (ex: target companies, benchmark profiles, etc.)
     */
    notes?: string | null;
    /**
     * Pipeline type
     *
     * Pipeline type
     */
    pipeline_type?: PipelineType | null;
    /**
     * Point of contact
     *
     * Point of contact person ID
     */
    point_of_contact_person_id?: string | null;
    /**
     * Purpose
     *
     * Purpose of the pipeline (if type PipelineType.OTHER)
     */
    purpose?: string | null;
    /**
     * Role type
     *
     * Role type
     */
    role_type: PipelineRoleType;
};

/**
 * CurrentESIndexResponse
 */
export type CurrentEsIndexResponse = {
    /**
     * Index Name
     */
    index_name?: string | null;
    /**
     * Index Alias
     */
    index_alias: string;
    /**
     * Docs Count
     */
    docs_count: number;
    /**
     * Created At
     */
    created_at?: string | null;
};

/**
 * CustomerCompany
 *
 * Attributes
 * ----------
 * slug : str
 * used as the unique identifier for any company
 * team_count : int
 * number of associated teams, default is -1
 * user_count : int
 * number of associated persons, default is -1
 */
export type CustomerCompany = {
    /**
     * Alias
     */
    alias?: string | null;
    assets_managed?: AssetsManagedRange | null;
    budget?: AnnualBudgetRange | null;
    company_type?: CompanyOwnership | null;
    /**
     * Domain
     */
    domain?: string | null;
    employees?: FteRange | null;
    funding_round?: FundingRound | null;
    hq_location?: LocationAddress | null;
    /**
     * Industry Keywords
     */
    industry_keywords?: string | null;
    industry?: IcbIndustry | null;
    market_cap?: MarketCapRange | null;
    /**
     * Morningstar Industry
     */
    morningstar_industry?: string | null;
    /**
     * Name
     */
    name: string;
    payment_status?: PaymentStatus;
    revenue?: RevenueRange | null;
    sector?: CompanySector | null;
    stage?: CompanyStage | null;
    /**
     * Stock Symbol
     */
    stock_symbol?: string | null;
    type: OrgType;
    valuation?: ValuationRange | null;
    /**
     * Id
     */
    id: number;
    /**
     * Slug
     */
    slug: string;
    /**
     * Team Count
     */
    team_count?: number;
    /**
     * User Count
     */
    user_count?: number;
};

/**
 * DeclinedStatusFilter
 */
export const DeclinedStatusFilter = { DECLINED: 'declined', NOT_DECLINED: 'not_declined' } as const;

/**
 * DeclinedStatusFilter
 */
export type DeclinedStatusFilter = typeof DeclinedStatusFilter[keyof typeof DeclinedStatusFilter];

/**
 * DeleteHighlightResponse
 */
export type DeleteHighlightResponse = {
    /**
     * Highlight
     *
     * Text describing a candidate highlight
     */
    highlight: string;
    /**
     * Created
     *
     * Date when this highlight was created
     */
    created: string;
    /**
     * Last modified
     *
     * Date when this highlight was last modified
     */
    last_modified: string;
    /**
     * Hidden
     *
     * Is this highlight hidden?
     */
    hidden: boolean;
    /**
     * Soft delete
     *
     * [DEPRECATED] Use 'hidden' instead. Is this highlight soft deleted?
     *
     * @deprecated
     */
    soft_delete: boolean;
    /**
     * UUID
     *
     * UUID uniquely identifying a candidate highlight
     */
    uuid: string;
};

/**
 * DeleteTeamEntityResponse
 */
export type DeleteTeamEntityResponse = {
    entity_type: SubmissionType;
    /**
     * Objects Processed
     */
    objects_processed?: number;
    /**
     * Slug
     */
    slug: string;
    /**
     * Timestamp
     */
    timestamp: string;
};

/**
 * ESCandidateIndexExternalResult
 */
export type EsCandidateIndexExternalResult = {
    /**
     * Meta
     *
     * Metadata about the search result
     */
    search_metadata?: EsMeta | null;
    /**
     * Advisor
     *
     * Information about the advisor assigned to this candidate
     */
    advisor?: PersonInnerDocSchema | null;
    /**
     * Company
     *
     * Candidate's last company
     */
    company?: string | null;
    /**
     * Role
     *
     * Candidate's last role
     */
    role?: string | null;
    /**
     * Date introduction last facilitated
     *
     * When was this candidate last introduced to the team?
     */
    date_introduction_facilitated_last?: string | null;
    /**
     * Date last recommended
     *
     * When was this candidate last recommended to the team?
     */
    date_recommended_last?: string | null;
    /**
     * Email
     *
     * Email associated with the person
     */
    email: string;
    /**
     * First name
     *
     * Person's first name
     */
    first_name?: string | null;
    /**
     * Functional expertise
     *
     * List of functions candidate has experience in
     */
    function_expertise: Array<Function>;
    /**
     * Target functions
     *
     * Functional expertise candidate would like to get
     */
    function_target: Array<Function>;
    /**
     * Global introduction request person
     *
     * Person who requested the global introduction
     */
    global_introduction_request_person?: PersonInnerDocSchema | null;
    /**
     * Global introduction status
     *
     * Global introduction status between this candidate and the team
     */
    global_introduction_status?: IntroductionStatus | null;
    /**
     * ID
     *
     * UUID uniquely identifying a person
     */
    id: string;
    /**
     * Image
     *
     * Path to person image
     */
    image?: string | null;
    /**
     * Industry expertise
     *
     * List of industries candidate has experience in
     */
    industry_expertise: Array<Industry>;
    /**
     * Target industries
     *
     * Functional expertise candidate would like to get
     */
    industry_target: Array<Industry>;
    /**
     * Client
     *
     * Indicates whether a person is a Banff client
     */
    is_client: boolean;
    /**
     * Is favorite
     *
     * Whether the candidate is favorited by the current user
     */
    is_favorite: boolean;
    /**
     * Last name
     *
     * Person's last name
     */
    last_name?: string | null;
    /**
     * LinkedIn URL
     *
     * Link to the person's LinkedIn profile
     */
    linkedin_url?: string | null;
    /**
     * Location
     *
     * Candidate's location (from their resume)
     */
    location?: string | null;
    /**
     * Name
     *
     * Person's full name
     */
    name: string;
    /**
     * Operating Highlights
     *
     * List of operating highlights
     */
    operating_highlights?: Array<OperatingHighlights> | null;
    /**
     * Resume
     *
     * Response from the resume upload endpoint
     */
    resume?: LinkedInResumeResponse | null;
    /**
     * UUID
     *
     * UUID uniquely identifying a candidate
     */
    uuid: string;
};

/**
 * ESCandidateIndexResult
 */
export type EsCandidateIndexResult = {
    /**
     * Meta
     *
     * Metadata about the search result
     */
    search_metadata?: EsMeta | null;
    /**
     * Active
     *
     * Is this candidate marked as active?
     */
    active: boolean;
    /**
     * Advisor
     *
     * Information about the advisor assigned to this candidate
     */
    advisor?: PersonInnerDocSchema | null;
    /**
     * Advisor UUID
     *
     * uuid of advisor assigned to this candidate
     */
    advisor_id?: string | null;
    /**
     * Current Company
     *
     * Company of the candidate
     */
    company?: string | null;
    /**
     * Current Role
     *
     * Candidate's current role
     */
    role?: string | null;
    /**
     * Date introduction last facilitated
     *
     * When was this candidate last introduced to the team?
     */
    date_introduction_facilitated_last?: string | null;
    /**
     * Date last recommended
     *
     * When was this candidate last recommended to the team?
     */
    date_recommended_last?: string | null;
    /**
     * Functional expertise
     *
     * List of functions candidate has experience in
     */
    function_expertise: Array<Function>;
    /**
     * Target functions
     *
     * Functional expertise candidate would like to get
     */
    function_target: Array<Function>;
    /**
     * Global introduction request person
     *
     * Person who requested the global introduction
     */
    global_introduction_request_person?: PersonInnerDocSchema | null;
    /**
     * Global introduction status
     *
     * Global introduction status between this candidate and the team
     */
    global_introduction_status?: IntroductionStatus | null;
    /**
     * Industry expertise
     *
     * List of industries candidate has experience in
     */
    industry_expertise: Array<Industry>;
    /**
     * Target industries
     *
     * Functional expertise candidate would like to get
     */
    industry_target: Array<Industry>;
    /**
     * Board/advisory role interest
     *
     * Interest in getting board or advisory roles
     */
    interest_role_board_advisory?: InterestRole | null;
    /**
     * Operating role interest
     *
     * Interest in getting operating roles
     */
    interest_role_operating?: InterestRole | null;
    /**
     * Personal interests
     *
     * List of personal interests
     */
    interests_personal: Array<InterestPersonal>;
    /**
     * Professional interests
     *
     * List of professional interests
     */
    interests_professional: Array<InterestProfessional>;
    /**
     * ID
     *
     * ID uniquely identifying a candidate
     */
    id: number;
    /**
     * Client
     *
     * Indicates whether a person is a Banff client
     */
    is_client?: boolean | null;
    /**
     * Is favorite
     *
     * Whether the candidate is favorited by the current user
     */
    is_favorite: boolean;
    /**
     * Referred
     *
     * Indicates whether a person was referred
     */
    is_referred?: boolean | null;
    /**
     * Keywords
     *
     * List of keywords
     */
    keywords: Array<string>;
    /**
     * LinkedIn profile last synced
     *
     * Last time resume was checked against LinkedIn
     */
    linkedin_profile_last_synced?: string | null;
    /**
     * LinkedIn URL
     *
     * Link to the person's LinkedIn profile
     */
    linkedin_url?: string | null;
    /**
     * Location
     *
     * Candidate's location (from their resume)
     */
    location?: string | null;
    /**
     * Manual last update
     *
     * Timestamp when resume was last updated manually
     */
    manual_last_update?: string | null;
    /**
     * Notes
     *
     * Additional notes on ideal opportunities
     */
    notes?: string | null;
    /**
     * Operating Highlights
     *
     * List of operating highlights
     */
    operating_highlights?: Array<OperatingHighlights> | null;
    /**
     * Email
     *
     * Candidate's email address
     */
    email: string;
    /**
     * First Name
     *
     * Candidate's first name
     */
    first_name?: string | null;
    /**
     * Full Name
     *
     * Candidate's full name
     */
    name?: string | null;
    /**
     * Last Name
     *
     * Candidate's last name
     */
    last_name?: string | null;
    /**
     * Profile Image
     *
     * Candidate's profile image URL
     */
    image?: string | null;
    /**
     * Pipelines count
     *
     * Number of pipelines and Trending for this team where this candidate appears
     */
    pipelines_count?: number | null;
    /**
     * Referred date
     *
     * Date when the candidate was referred for the first time
     */
    referred_date?: string | null;
    /**
     * Referrers
     *
     * List of people who referred this candidate
     */
    referrers: Array<PersonInnerDocSchema>;
    /**
     * Resume
     *
     * Response from the resume upload endpoint
     */
    resume?: LinkedInResumeResponse | null;
    /**
     * Resume last uploaded
     *
     * Timestamp when resume was last updated
     */
    resume_last_uploaded?: string | null;
    /**
     * Profile updated date
     *
     * Date when the candidate's profile was last updated
     */
    profile_updated_date?: string | null;
    /**
     * Team UUID
     *
     * Team UUID
     */
    team_uuid: string;
    /**
     * Tier
     *
     * Candidate tier
     */
    tier: CandidateTier;
    /**
     * Type
     *
     * Candidate type
     */
    type?: ClientType | null;
    /**
     * Pathway
     *
     * Banff service pathway for the candidate
     */
    pathway?: CandidatePathway | null;
    /**
     * UUID
     *
     * UUID uniquely identifying a candidate
     */
    uuid: string;
    /**
     * Profile Image Url
     */
    readonly profile_image_url: string | null;
};

/**
 * ESClickEventPayload
 */
export type EsClickEventPayload = {
    /**
     * Search ID
     *
     * Search ID
     */
    search_id: string;
    /**
     * Query
     *
     * Query
     */
    query: string;
    /**
     * ES document meta
     *
     * Metadata of the ES document
     */
    document: EsMeta;
    /**
     * Click URL
     *
     * URL where the click occurred
     */
    url: string;
};

/**
 * ESMeta
 */
export type EsMeta = {
    /**
     * ID
     *
     * Elasticsearch document ID
     */
    id?: string | null;
    /**
     * Index
     *
     * Elasticsearch index name
     */
    index?: string | null;
    /**
     * Highlights
     *
     * Highlighted fields from the search
     */
    __highlights__?: {
        [key: string]: string;
    } | null;
};

/**
 * ESRecommendationCandidateIndexResult
 */
export type EsRecommendationCandidateIndexResult = {
    /**
     * Meta
     *
     * Metadata about the search result
     */
    search_metadata?: EsMeta | null;
    /**
     * Comments Count
     */
    comments_count: number;
    /**
     * Company
     */
    company: CompanyBasicTypeResponse;
    /**
     * Pipeline
     */
    pipeline?: PipelineBasicResponse | null;
    /**
     * Published On
     */
    published_on: string;
    /**
     * Introduction date
     */
    introduction_requested_on?: string | null;
    /**
     * Introduction facilitated date
     */
    introduction_facilitated_on?: string | null;
    /**
     * Not interested date
     */
    not_interested_on?: string | null;
    /**
     *  Review Status
     */
    review_status: RecommendationReviewState;
    /**
     * Team
     */
    team: TeamBasicUuidResponse;
    /**
     * UUID
     */
    uuid: string;
};

/**
 * ElasticsearchReindexResponse
 */
export type ElasticsearchReindexResponse = {
    /**
     * Is Successful
     */
    is_successful: boolean;
    /**
     * Index Name
     */
    index_name: string;
    metadata: ReindexMetadata;
};

/**
 * EmailCategory
 *
 * A group of email types, e.g. everything about events. Only events are
 * sorted so far; other email types have no category yet.
 */
export const EmailCategory = { EVENTS: 'events' } as const;

/**
 * EmailCategory
 *
 * A group of email types, e.g. everything about events. Only events are
 * sorted so far; other email types have no category yet.
 */
export type EmailCategory = typeof EmailCategory[keyof typeof EmailCategory];

/**
 * EmailEventType
 */
export const EmailEventType = {
    ADMIN_NEW_PIPELINE: 'admin_new_pipeline',
    INVITATION_CLERK: 'invitation_clerk',
    NEW_PIPELINE_SUBMISSION: 'new_pipeline_submission',
    P2P_INTRODUCTION: 'p2p_introduction',
    P2P_NOTIFICATION: 'p2p_notification',
    PERSON_WELCOME: 'person_welcome',
    REQUESTED_CANDIDATE_INTRODUCTION: 'requested_candidate_introduction',
    AUTO_INTRODUCTION: 'auto_introduction',
    AUTO_INTRODUCTION_CHECKIN: 'auto_introduction_checkin',
    RESEND_INVITATION_CLERK: 'resend_invitation_clerk',
    SHARE_RECOMMENDATIONS: 'share_recommendations',
    SUMMARY: 'summary',
    TEAM_NEW_PIPELINE: 'team_new_pipeline',
    TRENDING_STREAM: 'trending_stream',
    RECOMMENDATION_DRAFT_STATUS_CHANGE: 'recommendation_draft_status_change',
    EVENT_INVITE: 'event_invite',
    EVENT_RSVP_CONFIRMATION_ACCEPTED: 'event_rsvp_confirmation_accepted',
    EVENT_RSVP_CONFIRMATION_DECLINED: 'event_rsvp_confirmation_declined',
    EVENT_RSVP_REMINDER: 'event_rsvp_reminder',
    EVENT_ANNOUNCEMENT: 'event_announcement',
    EXECUTIVE_INVITATION_CLERK: 'executive_invitation_clerk'
} as const;

/**
 * EmailEventType
 */
export type EmailEventType = typeof EmailEventType[keyof typeof EmailEventType];

/**
 * EmailPreviewResponse
 *
 * A template variant rendered with sample values.
 */
export type EmailPreviewResponse = {
    /**
     * Subject
     */
    subject: string;
    /**
     * HTML
     */
    html: string;
};

/**
 * EmailPreviewSummary
 *
 * One template variant of an email type that Tools can preview.
 */
export type EmailPreviewSummary = {
    /**
     * Key
     *
     * Pass to GET /email-types/previews/{key}.
     */
    key: string;
    /**
     * Template
     *
     * The template file the preview renders. Variants of one template share it.
     */
    template: string;
    /**
     * Name
     *
     * The template's name, shared by its variants.
     */
    name: string;
    /**
     * Variant
     *
     * What this variant's sample data shows. Null for a template with one variant.
     */
    variant?: string | null;
};

/**
 * EmailTemplateCreatePayload
 *
 * Payload for creating an email template.
 */
export type EmailTemplateCreatePayload = {
    /**
     * Template type
     */
    template_type: EmailTemplateType;
    /**
     * Subject
     */
    subject: string;
    /**
     * Body
     */
    body: string;
    /**
     * Name
     */
    name?: string | null;
    /**
     * Description
     */
    description?: string | null;
};

/**
 * EmailTemplateDefaultPayload
 *
 * The text of a type's default template.
 */
export type EmailTemplateDefaultPayload = {
    /**
     * Subject
     */
    subject: string;
    /**
     * Body
     */
    body: string;
    /**
     * Name
     */
    name?: string | null;
    /**
     * Description
     */
    description?: string | null;
};

/**
 * EmailTemplateDefaultResponse
 *
 * The default template of one type.
 */
export type EmailTemplateDefaultResponse = {
    /**
     * Template type
     */
    template_type: EmailTemplateType;
    /**
     * Email template
     */
    email_template: EmailTemplateResponse;
};

/**
 * EmailTemplateResponse
 *
 * Email template as returned by the API. Includes allowed_placeholders for substitution.
 */
export type EmailTemplateResponse = {
    /**
     * UUID
     */
    uuid: string;
    /**
     * Template type
     */
    template_type: EmailTemplateType;
    /**
     * Subject
     */
    subject: string;
    /**
     * Body
     */
    body: string;
    /**
     * Name
     */
    name?: string | null;
    /**
     * Description
     */
    description?: string | null;
    /**
     * Event UUID
     *
     * The event this template belongs to; empty for a shared template. Which template is a type's default is set with /email-templates/defaults.
     */
    event_uuid?: string | null;
    /**
     * Hidden
     *
     * Replaced or deleted after an email was sent from it. Kept so the email history can show what was sent.
     */
    hidden?: boolean;
    /**
     * Created
     */
    created_at: string;
    /**
     * Last modified
     */
    updated_at: string;
    /**
     * Created by person
     */
    created_by_person_uuid?: string | null;
    /**
     * Allowed placeholders
     *
     * Placeholder keys that may be used in subject/body (e.g. {{event_title}}).
     */
    allowed_placeholders?: Array<string>;
    /**
     * Category
     *
     * The category of the template's type (see GET /email-types).
     */
    category?: EmailCategory | null;
};

/**
 * EmailTemplateType
 *
 * Type of email template (e.g. event invite, RSVP confirmation).
 */
export const EmailTemplateType = {
    EVENT_INVITE: 'event_invite',
    EVENT_RSVP_CONFIRMATION_ACCEPTED: 'event_rsvp_confirmation_accepted',
    EVENT_RSVP_CONFIRMATION_DECLINED: 'event_rsvp_confirmation_declined',
    EVENT_RSVP_REMINDER: 'event_rsvp_reminder',
    EVENT_ANNOUNCEMENT: 'event_announcement'
} as const;

/**
 * EmailTemplateType
 *
 * Type of email template (e.g. event invite, RSVP confirmation).
 */
export type EmailTemplateType = typeof EmailTemplateType[keyof typeof EmailTemplateType];

/**
 * EmailTemplateUpdatePayload
 *
 * Payload for updating an email template (all fields optional).
 */
export type EmailTemplateUpdatePayload = {
    /**
     * Template type
     */
    template_type?: EmailTemplateType | null;
    /**
     * Subject
     */
    subject?: string | null;
    /**
     * Body
     */
    body?: string | null;
    /**
     * Name
     */
    name?: string | null;
    /**
     * Description
     */
    description?: string | null;
};

/**
 * EmailTypeResponse
 *
 * One kind of email Contender sends.
 */
export type EmailTypeResponse = {
    /**
     * Type
     *
     * As logged in sent_email.
     */
    type: EmailEventType;
    /**
     * Category
     *
     * Empty until the type is sorted.
     */
    category?: EmailCategory | null;
    /**
     * Editable
     *
     * Whether templates of this type can be written.
     */
    editable: boolean;
    /**
     * Placeholders
     *
     * What a template of this type may use, as {{key}}.
     */
    placeholders?: Array<string>;
    /**
     * Previews
     *
     * Template variants rendered in Contender that Tools can show.
     */
    previews?: Array<EmailPreviewSummary>;
};

/**
 * EvaluationAction
 */
export const EvaluationAction = {
    COMMENTED: 'commented',
    COMPLETED: 'completed',
    FACILITATED: 'facilitated',
    FAVORITED: 'favorited',
    FAVORITED_UNDO: 'favorited_undo',
    INTRODUCTION_NOT_FACILITATED: 'introduction_not_facilitated',
    INTRODUCTION_NOT_REQUESTED: 'introduction_not_requested',
    NOT_LIKED: 'not_liked',
    NOT_LIKED_UNDO: 'not_liked_undo',
    RECOMMENDATION_ADDED: 'recommendation_added',
    RECOMMENDATION_DRAFT_APPROVED: 'recommendation_draft_approved',
    RECOMMENDATION_DRAFT_DENIED: 'recommendation_draft_denied',
    RECOMMENDATION_DRAFT_READY_FOR_REVIEW: 'recommendation_draft_ready_for_review',
    RECOMMENDATION_DRAFT_SUGGESTED: 'recommendation_draft_suggested',
    RECOMMENDATION_DRAFT_SAVED: 'recommendation_draft_saved',
    SCORED: 'scored',
    VIEWED: 'viewed'
} as const;

/**
 * EvaluationAction
 */
export type EvaluationAction = typeof EvaluationAction[keyof typeof EvaluationAction];

/**
 * EventCategory
 *
 * Event category/tag for the events feature.
 */
export const EventCategory = {
    CO_HOST: 'co_host',
    CONFERENCE: 'conference',
    DINNER: 'dinner',
    PANEL: 'panel',
    PARTNER: 'partner',
    WEBINAR: 'webinar'
} as const;

/**
 * EventCategory
 *
 * Event category/tag for the events feature.
 */
export type EventCategory = typeof EventCategory[keyof typeof EventCategory];

/**
 * EventCreatePayload
 *
 * Payload for creating an event.
 */
export type EventCreatePayload = {
    /**
     * Title
     */
    title: string;
    /**
     * Categories
     *
     * One or more event categories (new).
     */
    categories?: Array<EventCategory> | null;
    /**
     * Category
     *
     * [DEPRECATED] Single event category; use `categories` instead.
     *
     * @deprecated
     */
    category?: EventCategory | null;
    /**
     * Description
     */
    description?: string | null;
    /**
     * Event Date
     */
    event_date?: string | null;
    /**
     * Event timezone
     *
     * IANA timezone for this event's start time (e.g. Europe/London, America/New_York). Required context when event_date is naive; also used for display in API and emails.
     */
    event_timezone?: string | null;
    /**
     * Location
     */
    location?: string | null;
    /**
     * Max Participants
     */
    max_participants?: number | null;
    /**
     * Additional Link
     */
    additional_link?: string | null;
    /**
     * Draft
     */
    draft?: boolean;
    /**
     * RSVP enabled
     *
     * When false, public RSVP and invite flows that embed RSVP links are disabled.
     */
    rsvp_enabled?: boolean;
    /**
     * Notes
     *
     * Advisor-only notes; not shown on public RSVP.
     */
    notes?: string | null;
    /**
     * Invite sender email
     *
     * From address for this event's emails (invites, reminders, RSVP confirmations). Omit to use the app default sender.
     */
    invite_sender_email?: string | null;
    /**
     * Invite sender name
     *
     * Display name shown as the invite sender (optional).
     */
    invite_sender_name?: string | null;
};

/**
 * EventEmailBatchResponse
 *
 * One send run for an event: the invites sent in one go, one reminder pass,
 * one RSVP confirmation. Counts are of emails, not of SendGrid events: an
 * email opened twice is one opened email.
 */
export type EventEmailBatchResponse = {
    /**
     * Batch UUID
     */
    batch_uuid: string;
    /**
     * Email event type
     */
    email_event_type?: EmailEventType | null;
    /**
     * Sent at
     *
     * When the first email went out.
     */
    sent_at?: string | null;
    /**
     * Emails sent
     */
    email_count: number;
    /**
     * Emails delivered
     */
    delivered_count: number;
    /**
     * Emails opened at least once
     */
    opened_count: number;
    /**
     * Emails clicked at least once
     */
    clicked_count: number;
    /**
     * Emails dropped or bounced
     *
     * Never reached the inbox.
     */
    failed_count: number;
    /**
     * Email template
     *
     * The template the run was sent from, hidden or not. Empty for runs sent before October 2026.
     */
    email_template?: EmailTemplateResponse | null;
    /**
     * First emails
     *
     * The run's first emails that match the filters, newest first, at most 10 of them. Load the rest from /email-events?batch_uuid=&size=10, from page 2.
     */
    first_emails: Array<EventEmailEventItemResponse>;
};

/**
 * EventEmailEventItemResponse
 *
 * One email sent for an event, and what SendGrid has told us about it since.
 */
export type EventEmailEventItemResponse = {
    /**
     * SendGrid message ID
     */
    sendgrid_message_id: string;
    /**
     * Recipient email
     */
    sendgrid_recipient_email: string;
    /**
     * Recipient
     *
     * Who received it, when the address belongs to someone we hold. Nothing for an external address.
     */
    recipient?: IdentityBasicResponse | null;
    /**
     * Email event type
     */
    email_event_type?: EmailEventType | null;
    /**
     * Batch UUID
     *
     * The send job this email was part of: every invite or reminder sent in one go shares it.
     */
    batch_uuid?: string | null;
    /**
     * Sent at
     */
    sent_at?: string | null;
    /**
     * Delivered at
     */
    delivered_at?: string | null;
    /**
     * First opened at
     */
    first_opened_at?: string | null;
    /**
     * Last opened at
     */
    last_opened_at?: string | null;
    /**
     * Opens
     */
    open_count: number;
    /**
     * Clicks
     */
    click_count: number;
    /**
     * Failed at
     */
    failed_at?: string | null;
    /**
     * Failure
     *
     * Set when SendGrid could not deliver it.
     */
    failure?: 'dropped' | 'bounce' | null;
};

/**
 * EventEmailPreviewPayload
 *
 * Preview any event email: a saved template (the event's own or a shared
 * default), optionally with edited text, or unsaved text of a given type.
 */
export type EventEmailPreviewPayload = {
    /**
     * Email template UUID
     *
     * A saved template; subject/body, when given, replace its text.
     */
    email_template_uuid?: string | null;
    /**
     * Template type
     *
     * For unsaved text: the kind of email it is.
     */
    template_type?: EmailTemplateType | null;
    /**
     * Subject
     */
    subject?: string | null;
    /**
     * Body
     */
    body?: string | null;
    /**
     * Email
     *
     * Send the preview here. Omit to send it to yourself.
     */
    email?: string | null;
};

/**
 * EventEmailTemplateCreatePayload
 *
 * Payload for adding a template to an event.
 */
export type EventEmailTemplateCreatePayload = {
    /**
     * Template type
     */
    template_type: EmailTemplateType;
    /**
     * Subject
     */
    subject: string;
    /**
     * Body
     */
    body: string;
    /**
     * Name
     *
     * What this template is for, e.g. 'Location change'.
     */
    name?: string | null;
    /**
     * Description
     */
    description?: string | null;
};

/**
 * EventEmailTemplateUpdatePayload
 *
 * Payload for editing an event's template (all fields optional). The type
 * cannot change.
 */
export type EventEmailTemplateUpdatePayload = {
    /**
     * Subject
     */
    subject?: string | null;
    /**
     * Body
     */
    body?: string | null;
    /**
     * Name
     */
    name?: string | null;
    /**
     * Description
     */
    description?: string | null;
};

/**
 * EventGuestClientType
 *
 * Client-type classification for event guests, mirroring the users-table
 * company-type breakdown (see `OrgType`, `UserType`, and webapp's
 * `useOptionsCompanyType`).
 *
 * A guest whose person has a role at a company of a given enterprise
 * OrgType (SEARCH, INVESTOR, CORPORATE, ADVISORY) carries that value
 * (possibly several, for multi-role guests) -- this wins over candidate
 * linkage. A guest with a `candidate_uuid` and no such enterprise role is
 * EXECUTIVE. A guest with neither (standalone guests, roleless person
 * guests) is unclassified and carries none of these values.
 */
export const EventGuestClientType = {
    EXECUTIVE: 'executive',
    SEARCH: 'search',
    INVESTOR: 'investor',
    CORPORATE: 'corporate',
    ADVISORY: 'advisory'
} as const;

/**
 * EventGuestClientType
 *
 * Client-type classification for event guests, mirroring the users-table
 * company-type breakdown (see `OrgType`, `UserType`, and webapp's
 * `useOptionsCompanyType`).
 *
 * A guest whose person has a role at a company of a given enterprise
 * OrgType (SEARCH, INVESTOR, CORPORATE, ADVISORY) carries that value
 * (possibly several, for multi-role guests) -- this wins over candidate
 * linkage. A guest with a `candidate_uuid` and no such enterprise role is
 * EXECUTIVE. A guest with neither (standalone guests, roleless person
 * guests) is unclassified and carries none of these values.
 */
export type EventGuestClientType = typeof EventGuestClientType[keyof typeof EventGuestClientType];

/**
 * EventGuestItemResponse
 *
 * A guest on an event's guest list (polymorphic on source type, like potential guests).
 *
 * Always includes resolved name, role, company, email for display, plus
 * source_type, person_uuid/candidate_uuid for linking, and event-guest fields (id, uuid, status).
 */
export type EventGuestItemResponse = {
    /**
     * Source Type
     *
     * Whether this guest is a user, candidate, or standalone event guest
     */
    source_type: PotentialGuestSourceType;
    /**
     * ID
     *
     * Event guest record ID
     */
    id: number;
    /**
     * UUID
     *
     * Event guest record UUID
     */
    uuid: string;
    /**
     * Identity UUID
     *
     * The human this guest is, or null for a free-text guest. The key to group a guest by: one human is one guest however they were added
     */
    identity_uuid?: string | null;
    /**
     * Person UUID
     *
     * Person UUID when guest is linked to a B4B user
     */
    person_uuid?: string | null;
    /**
     * Candidate UUID
     *
     * Candidate UUID when guest is linked to a candidate
     */
    candidate_uuid?: string | null;
    /**
     * Invoice Status
     *
     * HubSpot invoice status when the guest is a linked candidate
     */
    invoice_status?: HubSpotInvoiceStatus | null;
    /**
     * Is Executive
     *
     * True when this guest is an activated executive user (ExecutiveSettings exists on the linked person, so person_uuid resolves to a real user page). False for plain candidates and standalone event guests.
     */
    is_executive?: boolean;
    /**
     * Name
     *
     * Display name
     */
    name?: string | null;
    /**
     * Role
     *
     * Role or title
     */
    role?: string | null;
    /**
     * Company
     *
     * Company name
     */
    company?: string | null;
    /**
     * Email
     *
     * Email address
     */
    email?: string | null;
    /**
     * Status
     *
     * Guest status for this event
     */
    status: EventGuestStatus;
    /**
     * Invite Sent At
     *
     * When the invite email was last sent
     */
    invite_sent_at?: string | null;
    /**
     * Created
     *
     * When this guest was added to the event
     */
    created: string;
    /**
     * Referrers
     *
     * Persons who referred this candidate (when source_type is candidate)
     */
    referrers?: Array<PersonBasicResponse> | null;
    /**
     * Client Types
     *
     * Server-computed client-type classification, mirroring the users-table company-type taxonomy. Enterprise roles (search, investor, corporate, advisory) win over candidate linkage; a candidate-linked guest with no enterprise role is executive; a guest with neither is unclassified (empty list).
     */
    client_types?: Array<EventGuestClientType>;
};

/**
 * EventGuestPatchPayload
 *
 * Banff Admin payload to update an event guest (e.g. RSVP / invite status).
 */
export type EventGuestPatchPayload = {
    /**
     * Status
     *
     * Guest status for this event
     */
    status: EventGuestStatus;
};

/**
 * EventGuestRsvpSummary
 *
 * Guest summary for RSVP landing: display name and current status.
 */
export type EventGuestRsvpSummary = {
    /**
     * Name
     */
    name?: string | null;
    /**
     * Status
     */
    status: EventGuestStatus;
};

/**
 * EventGuestStatus
 *
 * Status of a guest for an event.
 */
export const EventGuestStatus = {
    CANT_GO: 'cant_go',
    GOING: 'going',
    INVITED: 'invited',
    NEED_INVITE: 'need_invite'
} as const;

/**
 * EventGuestStatus
 *
 * Status of a guest for an event.
 */
export type EventGuestStatus = typeof EventGuestStatus[keyof typeof EventGuestStatus];

/**
 * EventGuestTotalCounts
 */
export type EventGuestTotalCounts = {
    /**
     * All
     */
    all: number;
    /**
     * Need Invite
     */
    need_invite: number;
    /**
     * Invited
     */
    invited: number;
    /**
     * Going
     */
    going: number;
    /**
     * Cant Go
     */
    cant_go: number;
};

/**
 * EventGuestWithEventItemResponse
 *
 * An event guest with the event it belongs to (for listing all guests across events).
 */
export type EventGuestWithEventItemResponse = {
    /**
     * Event UUID
     *
     * UUID of the event
     */
    event_uuid: string;
    /**
     * Event Title
     *
     * Title of the event
     */
    event_title: string;
    /**
     * Guest
     *
     * The guest record with resolved name, role, company, email
     */
    guest: EventGuestItemResponse;
};

/**
 * EventGuestsBulkStatusPayload
 *
 * Set one status on many guests of an event.
 */
export type EventGuestsBulkStatusPayload = {
    /**
     * Guest Uuids
     */
    guest_uuids: Array<string>;
    status: EventGuestStatus;
};

/**
 * EventGuestsPage[EventGuestItemResponse]
 */
export type EventGuestsPageEventGuestItemResponse = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Results
     */
    results: Array<EventGuestItemResponse>;
    /**
     * Size
     */
    size: number;
    total_counts: EventGuestTotalCounts;
};

/**
 * EventGuestsSelectionPayload
 *
 * Filters resolving the full matching guest set (select-all).
 */
export type EventGuestsSelectionPayload = {
    status?: EventGuestStatus | null;
    /**
     * Client Type
     */
    client_type?: Array<EventGuestClientType> | null;
    /**
     * Phrase
     */
    phrase?: string | null;
};

/**
 * EventGuestsSelectionResponse
 *
 * All guests matching the selection filters (no pagination).
 */
export type EventGuestsSelectionResponse = {
    /**
     * Results
     */
    results: Array<EventGuestItemResponse>;
    /**
     * Count
     */
    count: number;
};

/**
 * EventGuestsSortColumn
 *
 * Sort columns for GET /events/{event_uuid}/guests.
 *
 * No RELEVANCE value on purpose: best-match ranking is never a value a
 * client can request. It's the implicit default when a search `phrase` is
 * present and `sort` is omitted -- see get_event_guests / documentation.
 */
export const EventGuestsSortColumn = {
    CREATED: 'created',
    NAME: 'name',
    STATUS: 'status'
} as const;

/**
 * EventGuestsSortColumn
 *
 * Sort columns for GET /events/{event_uuid}/guests.
 *
 * No RELEVANCE value on purpose: best-match ranking is never a value a
 * client can request. It's the implicit default when a search `phrase` is
 * present and `sort` is omitted -- see get_event_guests / documentation.
 */
export type EventGuestsSortColumn = typeof EventGuestsSortColumn[keyof typeof EventGuestsSortColumn];

/**
 * EventResponse
 *
 * Event with optional guest list.
 */
export type EventResponse = {
    /**
     * ID
     */
    id: number;
    /**
     * UUID
     */
    uuid: string;
    /**
     * Title
     */
    title: string;
    /**
     * Categories
     */
    categories: Array<EventCategory>;
    /**
     * Category
     *
     * [DEPRECATED] First category from `categories`.
     *
     * @deprecated
     */
    category?: EventCategory | null;
    /**
     * Draft
     */
    draft: boolean;
    /**
     * RSVP enabled
     *
     * When false, public RSVP and invite flows that embed RSVP links are disabled.
     */
    rsvp_enabled: boolean;
    /**
     * Description
     */
    description?: string | null;
    /**
     * Notes
     *
     * Advisor-only notes; not shown on public RSVP.
     */
    notes?: string | null;
    /**
     * Event date (UTC)
     *
     * Scheduled start as stored (UTC instant). Prefer event_date_local for display.
     */
    event_date?: string | null;
    /**
     * Event timezone
     *
     * IANA timezone for displaying event_date.
     */
    event_timezone?: string | null;
    /**
     * Event date (local)
     *
     * Same instant as event_date, in event_timezone (ISO 8601 with offset).
     */
    event_date_local?: string | null;
    /**
     * Location
     */
    location?: string | null;
    /**
     * Max Participants
     */
    max_participants?: number | null;
    /**
     * Additional Link
     */
    additional_link?: string | null;
    /**
     * Invite Email Subject
     *
     * The event's own invite template's subject; None in event lists.
     */
    invite_email_subject?: string | null;
    /**
     * Invite Email Body
     *
     * The event's own invite template's body; None in event lists.
     */
    invite_email_body?: string | null;
    /**
     * Invite sender email
     *
     * Custom From email for event mail; null uses app default.
     */
    invite_sender_email?: string | null;
    /**
     * Invite sender name
     *
     * Display name for the event sender line.
     */
    invite_sender_name?: string | null;
    /**
     * Landing image
     *
     * Storage key for custom landing hero image (advisor upload).
     */
    landing_image?: string | null;
    /**
     * Created
     */
    created: string;
    /**
     * Last Modified
     */
    last_modified: string;
    /**
     * Guests Count
     */
    guests_count?: number;
    /**
     * Actual Guests Count
     *
     * Count of guests (event_guest records) for this event.
     */
    actual_guests_count?: number;
    /**
     * Guests
     *
     * Guest list when requested (polymorphic on source type)
     */
    guests?: Array<EventGuestItemResponse> | null;
    /**
     * My RSVP Status
     *
     * The viewing user's own RSVP status when listed as a guest (``GET /api/v2/users/{uuid}/events`` only).
     */
    my_status?: EventGuestStatus | null;
    /**
     * Is Past
     *
     * True when the event start is in the past (no date ⇒ upcoming). Populated on user event list responses.
     */
    is_past?: boolean | null;
};

/**
 * EventRsvpLandingResponse
 *
 * Response for GET RSVP by token: event details + guest so front can show landing and form.
 */
export type EventRsvpLandingResponse = {
    /**
     * Event
     */
    event: EventRsvpPublicResponse;
    /**
     * Guest
     */
    guest: EventGuestRsvpSummary;
    /**
     * Is preview
     *
     * True when from admin invite preview; show preview banner and disable RSVP.
     */
    is_preview?: boolean;
    /**
     * Event is draft
     *
     * True when the event is still a draft (unpublished).
     */
    event_is_draft?: boolean;
};

/**
 * EventRsvpPublicResponse
 *
 * Event details for the public RSVP landing page (no internal fields).
 */
export type EventRsvpPublicResponse = {
    /**
     * UUID
     */
    uuid: string;
    /**
     * Title
     */
    title: string;
    /**
     * Categories
     */
    categories: Array<EventCategory>;
    /**
     * Category
     *
     * [DEPRECATED] First category from `categories`.
     *
     * @deprecated
     */
    category?: EventCategory | null;
    /**
     * Description
     */
    description?: string | null;
    /**
     * Event Date
     */
    event_date?: string | null;
    /**
     * Event timezone
     *
     * IANA timezone used when displaying event_date.
     */
    event_timezone?: string | null;
    /**
     * Event date (local)
     *
     * Same instant as event_date, with offset for event_timezone (use for display; event_date remains UTC in the API).
     */
    event_date_local?: string | null;
    /**
     * Location
     */
    location?: string | null;
    /**
     * Max Participants
     */
    max_participants?: number | null;
    /**
     * Additional Link
     */
    additional_link?: string | null;
    /**
     * Draft
     *
     * True when event is unpublished; only shown in admin preview.
     */
    draft?: boolean;
    /**
     * RSVP enabled
     *
     * False when this event does not use Banff RSVP (links invalid).
     */
    rsvp_enabled?: boolean;
    /**
     * Landing image
     *
     * Storage object key for the landing hero image; fetch bytes via GET /api/v2/events/rsvp/landing-image?token=… or GET /api/v2/events/{event_uuid}/landing-image (no square crop).
     */
    landing_image?: string | null;
};

/**
 * EventTypeFilter
 */
export const EventTypeFilter = {
    GLOBAL: 'global',
    PEER: 'peer',
    PIPELINE: 'pipeline',
    TRENDING: 'trending'
} as const;

/**
 * EventTypeFilter
 */
export type EventTypeFilter = typeof EventTypeFilter[keyof typeof EventTypeFilter];

/**
 * EventUpdatePayload
 *
 * Payload for updating an event (all fields optional).
 */
export type EventUpdatePayload = {
    /**
     * Title
     */
    title?: string | null;
    /**
     * Categories
     *
     * One or more event categories (new).
     */
    categories?: Array<EventCategory> | null;
    /**
     * Category
     *
     * [DEPRECATED] Single event category; use `categories` instead.
     *
     * @deprecated
     */
    category?: EventCategory | null;
    /**
     * Description
     */
    description?: string | null;
    /**
     * Event Date
     */
    event_date?: string | null;
    /**
     * Event timezone
     *
     * IANA timezone; null clears stored timezone.
     */
    event_timezone?: string | null;
    /**
     * Location
     */
    location?: string | null;
    /**
     * Max Participants
     */
    max_participants?: number | null;
    /**
     * Additional Link
     */
    additional_link?: string | null;
    /**
     * Draft
     */
    draft?: boolean | null;
    /**
     * RSVP enabled
     *
     * When false, public RSVP and invite flows that embed RSVP links are disabled.
     */
    rsvp_enabled?: boolean | null;
    /**
     * Notes
     *
     * Advisor-only notes; not shown on public RSVP.
     */
    notes?: string | null;
    /**
     * Invite Email Subject
     *
     * Saved to the event's own invite template; null or empty changes nothing.
     */
    invite_email_subject?: string | null;
    /**
     * Invite Email Body
     *
     * Saved to the event's own invite template; null or empty changes nothing.
     */
    invite_email_body?: string | null;
    /**
     * Invite sender email
     *
     * From address for this event's emails; null clears a custom sender.
     */
    invite_sender_email?: string | null;
    /**
     * Invite sender name
     *
     * Display name for the invite sender; null clears.
     */
    invite_sender_name?: string | null;
};

/**
 * ExecutiveAccessLevel
 */
export const ExecutiveAccessLevel = { FULL: 'full', READONLY: 'readonly' } as const;

/**
 * ExecutiveAccessLevel
 */
export type ExecutiveAccessLevel = typeof ExecutiveAccessLevel[keyof typeof ExecutiveAccessLevel];

/**
 * ExecutiveActivityFilter
 */
export type ExecutiveActivityFilter = {
    /**
     * Kinds
     */
    kinds?: Array<'activity_log' | 'event' | 'peer' | 'pipeline' | 'trending' | 'global_intro' | 'referral' | 'comment'> | null;
    /**
     * Page
     */
    page?: number;
    /**
     * Size
     */
    size?: number;
};

/**
 * ExecutiveClientEventAttendee
 *
 * A fellow attendee shown to an executive client on their event view.
 *
 * Only attendees the exec already knows (in their People collection) are
 * listed for upcoming events; everyone who attended is listed for past
 * events. PII beyond name/role/company is intentionally omitted — use the
 * People page for full contact detail.
 */
export type ExecutiveClientEventAttendee = {
    /**
     * Identity UUID
     *
     * The human this attendee is; null for a free-text guest
     */
    identity_uuid?: string | null;
    /**
     * Person UUID
     */
    person_uuid?: string | null;
    /**
     * Candidate UUID
     */
    candidate_uuid?: string | null;
    /**
     * Full Name
     */
    full_name?: string | null;
    /**
     * Current Role
     */
    current_role?: string | null;
    /**
     * Current Company
     */
    current_company?: string | null;
    /**
     * Profile Image
     */
    profile_img?: string | null;
    /**
     * Profile Image Url
     */
    readonly profile_image_url: string | null;
};

/**
 * ExecutiveClientEventListItem
 *
 * One event in an executive client's event list.
 *
 * Lighter than :class:`ExecutiveClientEventResponse`: same event info and
 * the exec's own RSVP status, plus headline attendee counts — but no
 * per-attendee detail. For upcoming events, ``known_attendees_count`` and
 * ``other_attendees_count`` follow the same People-collection rules as the
 * detail endpoint.
 */
export type ExecutiveClientEventListItem = {
    /**
     * Event
     */
    event: EventRsvpPublicResponse;
    /**
     * My RSVP Status
     *
     * The requesting executive's own RSVP status for this event.
     */
    my_status: EventGuestStatus;
    /**
     * Is Past
     *
     * True when the event's date is in the past (no date ⇒ upcoming).
     */
    is_past: boolean;
    /**
     * Shows Attendees
     *
     * False for webinars, which never expose attendee counts.
     */
    shows_attendees: boolean;
    /**
     * Going Count
     *
     * Number of RSVP'd (going) attendees excluding the requesting executive. 0 for webinars.
     */
    going_count?: number;
    /**
     * Known Attendees Count
     *
     * Upcoming: RSVP'd attendees the exec already knows. Past: equals ``going_count``. 0 for webinars.
     */
    known_attendees_count?: number;
    /**
     * Other Attendees Count
     *
     * Upcoming events only: RSVP'd attendees not in the exec's People collection. 0 for past events and webinars.
     */
    other_attendees_count?: number;
};

/**
 * ExecutiveClientEventResponse
 *
 * Event detail for an external executive client who is a guest.
 *
 * Attendee visibility:
 * - Webinars never show attendees (``shows_attendees=False``).
 * - Upcoming events list only RSVP'd (going) attendees the exec already
 * knows (i.e. in their People collection independent of this event), plus
 * a count of the remaining RSVP'd attendees in ``other_attendees_count``.
 * - Past events list everyone who attended (all become contacts), and
 * ``other_attendees_count`` is 0.
 */
export type ExecutiveClientEventResponse = {
    /**
     * Event
     */
    event: EventRsvpPublicResponse;
    /**
     * My RSVP Status
     *
     * The requesting executive's own RSVP status for this event.
     */
    my_status: EventGuestStatus;
    /**
     * Is Past
     *
     * True when the event's date is in the past (no date ⇒ upcoming).
     */
    is_past: boolean;
    /**
     * Shows Attendees
     *
     * False for webinars, which never expose an attendee list.
     */
    shows_attendees: boolean;
    /**
     * Attendees
     *
     * Upcoming: RSVP'd attendees the exec already knows. Past: everyone who attended. Empty for webinars.
     */
    attendees?: Array<ExecutiveClientEventAttendee>;
    /**
     * Other Attendees Count
     *
     * Upcoming events only: number of RSVP'd attendees not shown (those the exec does not already know). 0 for past events and webinars.
     */
    other_attendees_count?: number;
};

/**
 * ExecutiveClientPeopleFilterData
 */
export type ExecutiveClientPeopleFilterData = {
    /**
     * Source Type
     */
    source_type?: Array<ContactSourceType> | null;
    /**
     * Company Type
     */
    company_type?: Array<string> | null;
    /**
     * Industry Expertise
     */
    industry_expertise?: Array<Industry> | null;
    /**
     * Function Expertise
     */
    function_expertise?: Array<Function> | null;
    /**
     * Company Id
     */
    company_id?: Array<number> | null;
    /**
     * Event Uuid
     */
    event_uuid?: Array<string> | null;
    /**
     * Connection Status
     */
    connection_status?: Array<ConnectionStatus> | null;
    /**
     * Personal Tag Uuid
     */
    personal_tag_uuid?: Array<string> | null;
};

/**
 * ExecutiveClientPeopleSearchPayload
 */
export type ExecutiveClientPeopleSearchPayload = {
    /**
     * Phrase
     *
     * Search phrase
     */
    phrase?: string | null;
    /**
     * Search ID
     *
     * Search session ID
     */
    search_id?: string | null;
    filter_data?: ExecutiveClientPeopleFilterData | null;
    /**
     * Page
     */
    page?: number;
    /**
     * Size
     */
    size?: number;
    /**
     * Sort Rules
     */
    sort_rules?: Array<SortRule>;
};

/**
 * ExecutiveClientRsvpUpdatePayload
 *
 * Payload for the exec-scoped RSVP PATCH: just the new status.
 *
 * Auth/identity come from the route (executive access + path person), so no
 * token is needed unlike the public RSVP flow.
 */
export type ExecutiveClientRsvpUpdatePayload = {
    /**
     * Status
     *
     * New RSVP status (going or cant_go).
     */
    status: 'going' | 'cant_go';
};

/**
 * ExecutiveClientSettingsResponse
 */
export type ExecutiveClientSettingsResponse = {
    /**
     * Person Uuid
     */
    person_uuid: string;
    /**
     * First Name
     */
    first_name?: string | null;
    /**
     * Last Name
     */
    last_name?: string | null;
    /**
     * Email
     */
    email: string;
    /**
     * Candidate Uuid
     */
    candidate_uuid?: string | null;
    /**
     * Receive Trending Emails
     */
    receive_trending_emails: boolean;
    /**
     * Receive Pipeline Emails
     */
    receive_pipeline_emails: boolean;
    /**
     * Access Level
     */
    access_level: string;
    /**
     * Payment Status
     */
    payment_status: string;
    /**
     * Hiring Pipeline Limit
     */
    hiring_pipeline_limit: number;
};

/**
 * ExecutiveContactDetailResponse
 *
 * Unified detail for one of the exec's contacts (BAN-5755).
 *
 * A single envelope that works whether the contact is candidate-backed or
 * person-only. The canonical relationship fields (``was_introduced``,
 * ``is_favorite``, ``source_types``, ``evidence``, ``personal_tags``) live at
 * the top level so the webapp reads them the same way regardless of type.
 *
 * ``candidate`` carries the full candidate profile when a candidate record
 * exists; ``person`` carries the linked person when one exists — both may be
 * present for a candidate who also has a ``Person`` row. The top-level
 * ``email`` prefers the candidate's (gated) email and falls back to the
 * person's; the same gated values appear on the sub-objects.
 */
export type ExecutiveContactDetailResponse = {
    /**
     * Candidate Uuid
     */
    candidate_uuid?: string | null;
    /**
     * Person Uuid
     */
    person_uuid?: string | null;
    /**
     * Identity Uuid
     */
    identity_uuid?: string | null;
    /**
     * Email
     */
    email?: string | null;
    /**
     * Source Types
     */
    source_types?: Array<ContactSourceType>;
    evidence?: ContactEvidenceResponse | null;
    /**
     * Personal Tags
     */
    personal_tags?: Array<PersonalContactTagItem>;
    /**
     * Is Favorite
     */
    is_favorite?: boolean;
    /**
     * Was Introduced
     */
    was_introduced?: boolean;
    /**
     * Is Represented
     *
     * Banff represents this contact: their candidate profile is in tiers 1 to 5. Having a profile is not enough (B4B and external profiles are not represented). Follows today's tier list; the definition is still evolving.
     */
    is_represented?: boolean;
    candidate?: CandidateExternalFullResponseWithTeamContext | null;
    person?: ExecutiveContactPerson | null;
};

/**
 * ExecutiveContactPerson
 *
 * The ``Person`` side of a unified exec contact.
 *
 * Lightweight identity for the individual behind a contact (login/profile
 * record). Present whenever the contact has a ``Person`` row — including
 * candidate-backed contacts whose candidate is linked to a person. ``email``
 * is gated identically to the envelope's top-level email.
 */
export type ExecutiveContactPerson = {
    /**
     * Person Uuid
     */
    person_uuid: string;
    /**
     * Full Name
     */
    full_name?: string | null;
    /**
     * First Name
     */
    first_name?: string | null;
    /**
     * Last Name
     */
    last_name?: string | null;
    /**
     * Email
     */
    email?: string | null;
    /**
     * Profile Img
     */
    profile_img?: string | null;
    /**
     * Notes External
     */
    notes_external?: string | null;
    /**
     * Linkedin Url
     */
    linkedin_url?: string | null;
    /**
     * Current Role
     */
    current_role?: string | null;
    /**
     * Current Company
     */
    current_company?: string | null;
    company?: CompanyBasicTypeResponse | null;
};

/**
 * ExecutiveListResponse
 */
export type ExecutiveListResponse = {
    /**
     * Items
     */
    items: Array<ExecutiveResponse>;
    /**
     * Total
     */
    total: number;
};

/**
 * ExecutivePaymentStatus
 */
export const ExecutivePaymentStatus = { FREE: 'free', PREMIUM: 'premium' } as const;

/**
 * ExecutivePaymentStatus
 */
export type ExecutivePaymentStatus = typeof ExecutivePaymentStatus[keyof typeof ExecutivePaymentStatus];

/**
 * ExecutivePeopleAggregationsResponse
 *
 * Faceted counts for the exec people page filters (BAN-5769).
 *
 * Each dimension is ``{value: count}`` over the phrase-matched contact set
 * with all *other* active filters applied (exclude-self faceting), so sibling
 * counts stay visible after a multi-select. uuid/id-keyed dimensions
 * (``company_id`` / ``event_uuid``) are resolved to display names by the
 * webapp. Follows the ``PotentialGuestsAggregationsResponse`` convention.
 */
export type ExecutivePeopleAggregationsResponse = {
    /**
     * Source Type
     */
    source_type?: {
        [key: string]: number;
    };
    /**
     * Company Type
     */
    company_type?: {
        [key: string]: number;
    };
    /**
     * Industry Expertise
     */
    industry_expertise?: {
        [key: string]: number;
    };
    /**
     * Function Expertise
     */
    function_expertise?: {
        [key: string]: number;
    };
    /**
     * Company Id
     */
    company_id?: {
        [key: string]: number;
    };
    /**
     * Event Uuid
     */
    event_uuid?: {
        [key: string]: number;
    };
    /**
     * Connection Status
     */
    connection_status?: {
        [key: string]: number;
    };
    /**
     * Personal Tag Uuid
     */
    personal_tag_uuid?: {
        [key: string]: number;
    };
    /**
     * Labels
     */
    labels?: {
        [key: string]: {
            [key: string]: string;
        };
    };
};

/**
 * ExecutivePeopleDetailResponse
 *
 * Person-only contact detail for the executive client people page.
 */
export type ExecutivePeopleDetailResponse = {
    /**
     * Person Uuid
     */
    person_uuid: string;
    /**
     * Identity Uuid
     */
    identity_uuid?: string | null;
    /**
     * Full Name
     */
    full_name?: string | null;
    /**
     * First Name
     */
    first_name?: string | null;
    /**
     * Last Name
     */
    last_name?: string | null;
    /**
     * Email
     */
    email?: string | null;
    /**
     * Profile Img
     */
    profile_img?: string | null;
    /**
     * Notes External
     */
    notes_external?: string | null;
    /**
     * Linkedin Url
     */
    linkedin_url?: string | null;
    /**
     * Current Role
     */
    current_role?: string | null;
    /**
     * Current Company
     */
    current_company?: string | null;
    company?: CompanyBasicTypeResponse | null;
    /**
     * Source Types
     */
    source_types?: Array<ContactSourceType>;
    evidence?: ContactEvidenceResponse | null;
    /**
     * Personal Tags
     */
    personal_tags?: Array<PersonalContactTagItem>;
    /**
     * Is Favorite
     */
    is_favorite?: boolean;
    /**
     * Was Introduced
     */
    was_introduced?: boolean;
    /**
     * Profile Image Url
     */
    readonly profile_image_url: string | null;
};

/**
 * ExecutivePeopleResultItem
 *
 * A single contact in the exec's people page.
 *
 * Canonical typed contact item for the people page.
 */
export type ExecutivePeopleResultItem = {
    /**
     * Object Type
     */
    object_type: 'candidate' | 'person';
    /**
     * Contact Key
     */
    contact_key: string;
    /**
     * Candidate Uuid
     */
    candidate_uuid?: string | null;
    /**
     * Person Uuid
     */
    person_uuid?: string | null;
    /**
     * Full Name
     */
    full_name?: string | null;
    /**
     * Email
     */
    email?: string | null;
    /**
     * Current Company
     */
    current_company?: string | null;
    /**
     * Current Role
     */
    current_role?: string | null;
    /**
     * Profile Img
     */
    profile_img?: string | null;
    /**
     * Source Types
     */
    source_types?: Array<ContactSourceType>;
    evidence?: ContactEvidenceResponse | null;
    /**
     * Personal Tags
     */
    personal_tags?: Array<PersonalContactTagItem>;
    /**
     * Is Favorite
     */
    is_favorite?: boolean;
    /**
     * Was Introduced
     */
    was_introduced?: boolean;
    /**
     * Profile Image Url
     */
    readonly profile_image_url: string | null;
    [key: string]: unknown;
};

/**
 * ExecutivePeopleSearchResponse
 */
export type ExecutivePeopleSearchResponse = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Results
     */
    results: Array<ExecutivePeopleResultItem>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCountsResponse;
    aggregations?: ExecutivePeopleAggregationsResponse;
    /**
     * Search Id
     */
    search_id?: string | null;
};

/**
 * ExecutiveResponse
 */
export type ExecutiveResponse = {
    /**
     * Person Uuid
     */
    person_uuid: string;
    /**
     * Email
     */
    email: string;
    /**
     * First Name
     */
    first_name?: string | null;
    /**
     * Last Name
     */
    last_name?: string | null;
    /**
     * Candidate Uuid
     */
    candidate_uuid?: string | null;
    settings: ExecutiveSettingsResponse;
};

/**
 * ExecutiveSettingsResponse
 */
export type ExecutiveSettingsResponse = {
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Access Level
     */
    access_level: string;
    /**
     * Invitation Status
     */
    invitation_status: string;
    /**
     * Payment Status
     */
    payment_status: string;
    /**
     * Hiring Pipeline Limit
     */
    hiring_pipeline_limit: number;
    /**
     * Receive Trending Emails
     */
    receive_trending_emails: boolean;
    /**
     * Receive Pipeline Emails
     */
    receive_pipeline_emails: boolean;
    /**
     * Content Access Level
     */
    content_access_level?: string | null;
};

/**
 * ExecutiveTeamResponse
 */
export type ExecutiveTeamResponse = {
    /**
     * Team Uuid
     */
    team_uuid: string;
    /**
     * Team Slug
     */
    team_slug: string;
};

/**
 * ExternalIntroductionSubject
 *
 * External person an intro is attributed to, as known after the fact.
 *
 * Unlike RecommendationExternalIntroduction (the create path, where a live
 * request needs a contactable person), corrections often know only a name —
 * email and company must not be required, or they end up fabricated.
 */
export type ExternalIntroductionSubject = {
    /**
     * Person name
     *
     * Person name
     */
    name: string;
    /**
     * Company name of the person
     *
     * Person's company name
     */
    company_name?: string | null;
    /**
     * Email address of the person
     *
     * Person's email
     */
    email?: string | null;
};

/**
 * ExternalPerson
 */
export type ExternalPerson = {
    /**
     * External person name
     *
     * External person name
     */
    name: string;
    /**
     * Company name of the external person
     *
     * External person's company name
     */
    company_name?: string | null;
    /**
     * Email address of the external person
     *
     * External person's email
     */
    email?: string | null;
};

/**
 * FTERange
 */
export const FteRange = {
    '1_100': '1-100',
    '101_1,000': '101-1,000',
    '1,001_5,000': '1,001-5,000',
    '5,001_10,000': '5,001-10,000',
    '10,001_50,000': '10,001-50,000',
    '50,001_100,000': '50,001-100,000',
    '>100,000': '>100,000'
} as const;

/**
 * FTERange
 */
export type FteRange = typeof FteRange[keyof typeof FteRange];

/**
 * FailedCandidateCompanyMetadataRow
 */
export type FailedCandidateCompanyMetadataRow = {
    /**
     * Identifier
     *
     * Candidate UUID from the row
     */
    identifier: string;
    /**
     * Reason
     *
     * Reason the row failed to import
     */
    reason: string;
};

/**
 * FailedCandidateRow
 *
 * Represents a row from the CSV that failed to import
 */
export type FailedCandidateRow = {
    /**
     * Name
     *
     * Candidate name
     */
    name?: string | null;
    /**
     * Role
     *
     * Candidate role
     */
    role?: string | null;
    /**
     * Company Name
     *
     * Company name
     */
    company_name?: string | null;
    /**
     * Linkedin Url
     *
     * LinkedIn URL
     */
    linkedin_url?: string | null;
    /**
     * Reason
     *
     * Reason for import failure
     */
    reason?: string | null;
};

/**
 * FailedCandidatesImportRequest
 *
 * Request body for the failed candidates export endpoint
 */
export type FailedCandidatesImportRequest = {
    /**
     * Failed Rows
     *
     * List of rows that failed to import
     */
    failed_rows: Array<FailedCandidateRow>;
};

/**
 * FeedbackResponse
 */
export type FeedbackResponse = {
    /**
     * Detail
     */
    detail: string;
    /**
     * Requester Name
     */
    requester_name?: string | null;
    /**
     * Requester Company
     */
    requester_company?: string | null;
};

/**
 * FeedbackSource
 */
export const FeedbackSource = {
    COMMENT: 'comment',
    EVENT: 'event',
    FEEDBACK: 'feedback'
} as const;

/**
 * FeedbackSource
 */
export type FeedbackSource = typeof FeedbackSource[keyof typeof FeedbackSource];

/**
 * FeedbackType
 */
export const FeedbackType = {
    EXTERNAL: 'external',
    GLOBAL: 'global',
    INTAKE: 'intake',
    PIPELINE: 'pipeline',
    TRENDING: 'trending'
} as const;

/**
 * FeedbackType
 */
export type FeedbackType = typeof FeedbackType[keyof typeof FeedbackType];

/**
 * FilterPresets
 */
export const FilterPresets = { DIFFERENCES: 'differences', SIMILARITIES: 'similarities' } as const;

/**
 * FilterPresets
 */
export type FilterPresets = typeof FilterPresets[keyof typeof FilterPresets];

/**
 * Function
 */
export const Function_ = {
    CEO: 'ceo',
    CLIMATE_AND_SUSTAINABILITY: 'climate_and_sustainability',
    CORPORATE_GOVERNANCE: 'corporate_governance',
    CUSTOMER_SUCCESS: 'customer_success',
    CYBERSECURITY: 'cybersecurity',
    DATA_AND_ANALYTICS: 'data_and_analytics',
    DIGITAL: 'digital',
    FINANCE: 'finance',
    FOUNDER: 'founder',
    GENERAL_MANAGEMENT: 'general_management',
    INVESTORS: 'investors',
    LEGAL: 'legal',
    MARKETING: 'marketing',
    OPERATIONS: 'operations',
    OTHER: 'other',
    PEOPLE: 'people',
    PRODUCT: 'product',
    RISK_AND_COMPLIANCE: 'risk_and_compliance',
    SALES: 'sales',
    STRATEGY: 'strategy',
    SUPPLY_CHAIN: 'supply_chain',
    TECHNOLOGY: 'technology'
} as const;

/**
 * Function
 */
export type Function = typeof Function_[keyof typeof Function_];

/**
 * FundType
 *
 * What kind of capital a firm deploys (BAN-XXXX).
 *
 * Multi-select on `company.fund_type`, overridable on `search_team.fund_type`.
 * A genuinely hybrid firm carries both values — there is deliberately no "both"
 * member, and no "other": anything that is not a PE or VC fund is left empty.
 */
export const FundType = { PRIVATE_EQUITY: 'private_equity', VENTURE_CAPITAL: 'venture_capital' } as const;

/**
 * FundType
 *
 * What kind of capital a firm deploys (BAN-XXXX).
 *
 * Multi-select on `company.fund_type`, overridable on `search_team.fund_type`.
 * A genuinely hybrid firm carries both values — there is deliberately no "both"
 * member, and no "other": anything that is not a PE or VC fund is left empty.
 */
export type FundType = typeof FundType[keyof typeof FundType];

/**
 * FundingRound
 */
export const FundingRound = {
    SERIES_A: 'Series A',
    SERIES_B: 'Series B',
    SERIES_C: 'Series C',
    SERIES_D: 'Series D',
    SERIES_E: 'Series E',
    SERIES_F_AND_ON: 'Series F and on'
} as const;

/**
 * FundingRound
 */
export type FundingRound = typeof FundingRound[keyof typeof FundingRound];

/**
 * GeneralSearchResponse
 */
export type GeneralSearchResponse = {
    /**
     * Name
     */
    name: string;
    type: GeneralSearchResponseType;
    values: GeneralSearchResponseValues;
};

/**
 * GeneralSearchResponseType
 */
export const GeneralSearchResponseType = {
    CANDIDATE: 'candidate',
    PIPELINE: 'pipeline',
    SUBMISSION: 'submission',
    TEAM: 'team'
} as const;

/**
 * GeneralSearchResponseType
 */
export type GeneralSearchResponseType = typeof GeneralSearchResponseType[keyof typeof GeneralSearchResponseType];

/**
 * GeneralSearchResponseValues
 */
export type GeneralSearchResponseValues = {
    /**
     * Meta
     *
     * Metadata about the search result
     */
    search_metadata?: EsMeta | null;
    /**
     * Candidate Uuid
     */
    candidate_uuid?: string | null;
    /**
     * Pipeline Slug
     */
    pipeline_slug?: string | null;
    /**
     * Submission Uuid
     */
    submission_uuid?: string | null;
    /**
     * Team Slug
     */
    team_slug?: string | null;
};

/**
 * GoalIntroType
 */
export const GoalIntroType = {
    CORPORATE: 'corporate',
    INVESTOR: 'investor',
    PEER: 'peer',
    SEARCH: 'search'
} as const;

/**
 * GoalIntroType
 */
export type GoalIntroType = typeof GoalIntroType[keyof typeof GoalIntroType];

/**
 * GoalPriority
 */
export const GoalPriority = {
    HIGH: 'high',
    LOW: 'low',
    MEDIUM: 'medium'
} as const;

/**
 * GoalPriority
 */
export type GoalPriority = typeof GoalPriority[keyof typeof GoalPriority];

/**
 * GoalStatus
 */
export const GoalStatus = {
    COMPLETED: 'completed',
    IN_PROGRESS: 'in_progress',
    NOT_STARTED: 'not_started'
} as const;

/**
 * GoalStatus
 */
export type GoalStatus = typeof GoalStatus[keyof typeof GoalStatus];

/**
 * GoalStatusFilter
 *
 * Filter by goal status.
 */
export const GoalStatusFilter = {
    ALL: 'all',
    COMPLETED: 'completed',
    IN_PROGRESS: 'in_progress',
    NOT_STARTED: 'not_started'
} as const;

/**
 * GoalStatusFilter
 *
 * Filter by goal status.
 */
export type GoalStatusFilter = typeof GoalStatusFilter[keyof typeof GoalStatusFilter];

/**
 * GoalType
 */
export const GoalType = { LONG_TERM: 'long_term', SHORT_TERM: 'short_term' } as const;

/**
 * GoalType
 */
export type GoalType = typeof GoalType[keyof typeof GoalType];

/**
 * GroupedActivityLogResponse
 */
export type GroupedActivityLogResponse = {
    /**
     * Log statistics
     */
    totals: CandidateActivityLogTotals;
    /**
     * Candidate
     *
     * Information about the candidate
     */
    candidate: CandidateBasicResponse;
    /**
     * Logs grouped by type
     */
    logs: CandidateActivityLogReferenceGroups;
};

/**
 * HTTPValidationError
 */
export type HttpValidationError = {
    /**
     * Detail
     */
    detail?: Array<ValidationError>;
};

/**
 * Highlight
 */
export type Highlight = {
    /**
     * Last modified
     *
     * Date when this highlight was modified
     */
    last_modified: string;
    /**
     * Highlight
     */
    highlight: string;
    /**
     * Created
     *
     * Date when this highlight was created
     */
    created: string;
    /**
     * Hidden
     *
     * Is this highlight hidden?
     */
    hidden: boolean;
    /**
     * Soft delete
     *
     * [DEPRECATED] Use 'hidden' instead. Is this highlight soft deleted?
     *
     * @deprecated
     */
    soft_delete: boolean;
    /**
     * Uuid
     */
    uuid: string;
};

/**
 * HighlightGeneratorResponse
 */
export type HighlightGeneratorResponse = {
    /**
     * Highlights
     *
     * A list of highlights for the candidate
     */
    highlights: Array<string>;
    /**
     * Candidate Name
     *
     * The name of the candidate
     */
    candidate_name: string;
};

/**
 * HighlightResponse
 */
export type HighlightResponse = {
    /**
     * Highlight
     *
     * Text describing a candidate highlight
     */
    highlight: string;
    /**
     * Created
     *
     * Date when this highlight was created
     */
    created: string;
    /**
     * Last modified
     *
     * Date when this highlight was last modified
     */
    last_modified: string;
    /**
     * Hidden
     *
     * Is this highlight hidden?
     */
    hidden: boolean;
    /**
     * Soft delete
     *
     * [DEPRECATED] Use 'hidden' instead. Is this highlight soft deleted?
     *
     * @deprecated
     */
    soft_delete: boolean;
    /**
     * UUID
     *
     * UUID uniquely identifying a candidate highlight
     */
    uuid: string;
};

/**
 * HighlightWithSelectedPayload
 */
export type HighlightWithSelectedPayload = {
    /**
     * Highlight
     *
     * Highlight text
     */
    highlight: string;
    /**
     * Is selected
     *
     * Is this highlight visible in published recommendation?
     */
    is_selected: boolean;
};

/**
 * HighlightWithSelectedResponse
 */
export type HighlightWithSelectedResponse = {
    /**
     * Highlight
     *
     * Highlight text
     */
    highlight: string;
    /**
     * Is selected
     *
     * Is this highlight visible in published recommendation?
     */
    is_selected: boolean;
};

/**
 * HomeCandidateFeedbackAction
 */
export const HomeCandidateFeedbackAction = {
    ALREADY_MET: 'already_met',
    FAVORITE: 'favorite',
    NOT_INTERESTED: 'not_interested',
    SAVED_TO_PIPELINE: 'saved_to_pipeline',
    SHARED: 'shared',
    VIEWED: 'viewed'
} as const;

/**
 * HomeCandidateFeedbackAction
 */
export type HomeCandidateFeedbackAction = typeof HomeCandidateFeedbackAction[keyof typeof HomeCandidateFeedbackAction];

/**
 * HomeCandidateFeedbackActionPayload
 */
export type HomeCandidateFeedbackActionPayload = {
    /**
     * Feedback action
     */
    action: HomeCandidateFeedbackAction;
};

/**
 * HomeCandidatesResponse
 */
export type HomeCandidatesResponse = {
    /**
     * Results
     */
    results: Array<EsCandidateIndexExternalResult>;
    /**
     * Search Id
     *
     * Search ID
     */
    search_id: string;
    total_counts: TotalCountsResponse;
};

/**
 * HomeTeamActivityCounts
 */
export type HomeTeamActivityCounts = {
    /**
     * All
     */
    all: number;
    /**
     * Completed
     */
    completed: number;
    /**
     * Pending
     */
    pending: number;
    /**
     * Unread
     */
    unread: number;
};

/**
 * HomeTeamActivityPipelineResponse
 */
export type HomeTeamActivityPipelineResponse = {
    /**
     * Introductions Pending
     */
    introductions_pending: number;
    pipeline: PipelineExternalResponse;
    /**
     * Recommendations Count
     */
    recommendations_count: number;
    /**
     * Recommendations Unread
     */
    recommendations_unread: number;
    /**
     * Recommendations
     */
    recommendations: Array<HomeTeamActivityRecommendedPerson>;
    team: TeamBasicUuidResponse;
};

/**
 * HomeTeamActivityRecommendationResponse
 */
export type HomeTeamActivityRecommendationResponse = {
    candidate: CandidateExternalFullResponse;
    /**
     * Date Introduction Facilitated
     */
    date_introduction_facilitated: string;
    /**
     * Person who was introduced to this candidate
     */
    person: PersonBasicResponse;
    pipeline: PipelineExternalResponse;
    team: TeamBasicUuidResponse;
    /**
     * Uuid
     *
     * UUID uniquely identifying a recommendation
     */
    uuid: string;
};

/**
 * HomeTeamActivityRecommendedPerson
 *
 * One recommended candidate in a pipeline's activity row.
 *
 * Name and image come from the candidate's own record, so every recommended
 * candidate appears — the counts next to these avatars include the ones with
 * no login. ``uuid`` is a login's uuid, so it is the candidate's activated
 * executive and is absent when they have none; it is never another member of
 * the identity.
 */
export type HomeTeamActivityRecommendedPerson = {
    /**
     * Name
     *
     * Person's full name
     */
    name: string;
    /**
     * First Name
     *
     * Person's first name
     */
    first_name?: string | null;
    /**
     * Last Name
     *
     * Person's last name
     */
    last_name?: string | null;
    /**
     * Image
     *
     * Path to person image
     */
    image?: string | null;
    /**
     * UUID
     *
     * UUID of the candidate's executive login, when they have one
     */
    uuid?: string | null;
    /**
     * Profile Image Url
     */
    readonly profile_image_url: string | null;
};

/**
 * HomeTeamActivityResponse
 */
export type HomeTeamActivityResponse = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Results
     */
    results: Array<HomeTeamActivityPipelineResponse | HomeTeamActivityRecommendationResponse>;
    /**
     * Size
     */
    size: number;
    total_counts: HomeTeamActivityCounts;
};

/**
 * HomeTeamActivityType
 */
export const HomeTeamActivityType = {
    COMPLETED: 'completed',
    PENDING: 'pending',
    UNREAD: 'unread'
} as const;

/**
 * HomeTeamActivityType
 */
export type HomeTeamActivityType = typeof HomeTeamActivityType[keyof typeof HomeTeamActivityType];

/**
 * HomepageActivityLogResponse
 */
export type HomepageActivityLogResponse = {
    /**
     * Actor
     *
     * Actor
     */
    actor?: PersonBasicResponse | null;
    /**
     * Candidate
     *
     * Information about the candidate
     */
    candidate?: CandidateBasicResponse | null;
    /**
     * Event date
     *
     * The date of the event
     */
    date: string;
    /**
     * Event Type
     *
     * Type of event associated with the log
     */
    event_type: ActivityLogEventType | CandidateActivityLogEventType;
    /**
     * Message
     *
     * Message associated with the event
     */
    message: ActivityLogMessage;
    /**
     * Reference ID
     *
     * UUID of the entity that triggered this event
     */
    reference_id?: string | null;
    /**
     * Reference Type
     *
     * Which entity triggered the event
     */
    reference_type?: CandidateActivityLogReferenceType | null;
    /**
     * UUID
     *
     * Event UUID or pipeline UUID
     */
    uuid: string;
};

/**
 * HubSpotFieldSyncResponse
 *
 * Response schema for the HubSpot -> Contender field sync job.
 */
export type HubSpotFieldSyncResponse = {
    /**
     * Candidates Found
     *
     * Number of tier 1/1b candidates matching sync criteria
     */
    candidates_found: number;
    /**
     * Sync Started
     *
     * Whether the reconcile job was queued (false for dry-run)
     */
    sync_started: boolean;
    /**
     * Dry Run
     *
     * Whether this was a dry-run (computes changes, writes nothing)
     */
    dry_run: boolean;
    /**
     * Would Link
     *
     * Dry-run only: candidates that would be linked to a HubSpot contact
     */
    would_link?: number | null;
    /**
     * Would Update
     *
     * Dry-run only: candidates whose invoice_status would change
     */
    would_update?: number | null;
    /**
     * Ambiguous
     *
     * Dry-run only: emails matching multiple HubSpot contacts (skipped)
     */
    ambiguous?: number | null;
    /**
     * Unmatched
     *
     * Dry-run only: candidates with no matching HubSpot contact
     */
    unmatched?: number | null;
};

/**
 * HubSpotInvoiceStatus
 *
 * HubSpot `invoice_status` contact property (enumeration / single-select).
 *
 * Members mirror the option *internal values* HubSpot returns verbatim (which,
 * for this property, equal their labels and contain spaces). Stored in the
 * plain-String `contender_candidate.invoice_status` column -- the same
 * StrEnum-over-String pattern as `CandidateTier` -- so an unknown value from
 * HubSpot (a newly added or renamed option) is logged rather than rejected.
 */
export const HubSpotInvoiceStatus = {
    '<30_DAYS_OVERDUE': '<30 Days Overdue',
    '30_DAYS_OVERDUE': '30 Days Overdue',
    '60_DAYS_OVERDUE': '60 Days Overdue',
    '90_DAYS_OVERDUE': '90 Days Overdue',
    GOOD_STANDING: 'Good Standing'
} as const;

/**
 * HubSpotInvoiceStatus
 *
 * HubSpot `invoice_status` contact property (enumeration / single-select).
 *
 * Members mirror the option *internal values* HubSpot returns verbatim (which,
 * for this property, equal their labels and contain spaces). Stored in the
 * plain-String `contender_candidate.invoice_status` column -- the same
 * StrEnum-over-String pattern as `CandidateTier` -- so an unknown value from
 * HubSpot (a newly added or renamed option) is logged rather than rejected.
 */
export type HubSpotInvoiceStatus = typeof HubSpotInvoiceStatus[keyof typeof HubSpotInvoiceStatus];

/**
 * ICBIndustry
 */
export const IcbIndustry = {
    TECHNOLOGY: 'Technology',
    TELECOMMUNICATIONS: 'Telecommunications',
    HEALTH_CARE: 'Health Care',
    FINANCIALS: 'Financials',
    REAL_ESTATE: 'Real Estate',
    CONSUMER_DISCRETIONARY: 'Consumer Discretionary',
    CONSUMER_STAPLES: 'Consumer Staples',
    INDUSTRIALS: 'Industrials',
    BASIC_MATERIALS: 'Basic Materials',
    ENERGY: 'Energy',
    UTILITIES: 'Utilities'
} as const;

/**
 * ICBIndustry
 */
export type IcbIndustry = typeof IcbIndustry[keyof typeof IcbIndustry];

/**
 * IdentityBasicResponse
 */
export type IdentityBasicResponse = {
    /**
     * UUID
     *
     * The identity: one real human.
     */
    uuid: string;
    /**
     * Name
     *
     * From the candidate record if there is one, else the executive login, else the earliest login.
     */
    name?: string | null;
    /**
     * Candidate
     *
     * Their candidate record, if we hold one.
     */
    candidate?: CandidateBasicResponse | null;
    /**
     * Executive person
     *
     * Their activated executive login, if they have one. An identity holds at most one; other logins are not exposed here.
     */
    executive_person?: PersonBasicResponse | null;
};

/**
 * IdentityCandidateItem
 */
export type IdentityCandidateItem = {
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Email
     */
    email?: string | null;
    /**
     * First Name
     */
    first_name?: string | null;
    /**
     * Last Name
     */
    last_name?: string | null;
    tier?: CandidateTier | null;
};

/**
 * IdentityCandidatePayload
 */
export type IdentityCandidatePayload = {
    /**
     * Candidate Uuid
     */
    candidate_uuid: string;
};

/**
 * IdentityEmailItemResponse
 *
 * One email sent to this human, and what SendGrid has told us about it since.
 */
export type IdentityEmailItemResponse = {
    /**
     * SendGrid message ID
     */
    sendgrid_message_id: string;
    /**
     * Recipient email
     */
    sendgrid_recipient_email: string;
    /**
     * Recipient
     *
     * Who received it, when the address belongs to someone we hold. Nothing for an external address.
     */
    recipient?: IdentityBasicResponse | null;
    /**
     * Email event type
     */
    email_event_type?: EmailEventType | null;
    /**
     * Batch UUID
     *
     * The send job this email was part of: every invite or reminder sent in one go shares it.
     */
    batch_uuid?: string | null;
    /**
     * Sent at
     */
    sent_at?: string | null;
    /**
     * Delivered at
     */
    delivered_at?: string | null;
    /**
     * First opened at
     */
    first_opened_at?: string | null;
    /**
     * Last opened at
     */
    last_opened_at?: string | null;
    /**
     * Opens
     */
    open_count: number;
    /**
     * Clicks
     */
    click_count: number;
    /**
     * Failed at
     */
    failed_at?: string | null;
    /**
     * Failure
     *
     * Set when SendGrid could not deliver it.
     */
    failure?: 'dropped' | 'bounce' | null;
};

/**
 * IdentityMergePayload
 */
export type IdentityMergePayload = {
    /**
     * From Identity Uuid
     */
    from_identity_uuid: string;
    /**
     * Dry Run
     */
    dry_run?: boolean;
};

/**
 * IdentityMergeResponse
 *
 * What a merge would do (dry run), did (applied) or why it was refused
 * (blockers). Same shape in all three cases. ``rows`` moved to the surviving
 * identity; ``dropped`` were deleted as duplicates of rows already there.
 */
export type IdentityMergeResponse = {
    /**
     * Into Identity Uuid
     */
    into_identity_uuid: string;
    /**
     * From Identity Uuid
     */
    from_identity_uuid: string;
    /**
     * Blockers
     */
    blockers: Array<string>;
    /**
     * Persons
     */
    persons: Array<string>;
    /**
     * Candidate Uuid
     */
    candidate_uuid?: string | null;
    /**
     * Rows
     */
    rows: {
        [key: string]: number;
    };
    /**
     * Dropped
     */
    dropped?: {
        [key: string]: number;
    };
    /**
     * Applied
     */
    applied: boolean;
    /**
     * Merge Uuid
     */
    merge_uuid?: string | null;
};

/**
 * IdentityPersonItem
 */
export type IdentityPersonItem = {
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Email
     */
    email: string;
    /**
     * First Name
     */
    first_name?: string | null;
    /**
     * Last Name
     */
    last_name?: string | null;
    /**
     * Name
     */
    name?: string | null;
    /**
     * Is Activated Executive
     */
    is_activated_executive: boolean;
    /**
     * Registered
     */
    registered: boolean;
    user: AdminUserDetailedResponse;
};

/**
 * IdentityPersonPayload
 */
export type IdentityPersonPayload = {
    /**
     * Person Uuid
     */
    person_uuid: string;
};

/**
 * IdentityResponse
 */
export type IdentityResponse = {
    /**
     * Identity Uuid
     */
    identity_uuid: string;
    candidate?: IdentityCandidateItem | null;
    /**
     * Executive Person Uuid
     */
    executive_person_uuid?: string | null;
    /**
     * Persons
     */
    persons: Array<IdentityPersonItem>;
    /**
     * Merged Into Uuid
     */
    merged_into_uuid?: string | null;
};

/**
 * ImageUploadResponse
 */
export type ImageUploadResponse = {
    /**
     * Name
     */
    name: string;
};

/**
 * IndexAliasUpdateResponse
 */
export type IndexAliasUpdateResponse = {
    /**
     * Index Name
     */
    index_name: string;
    /**
     * Index Alias
     */
    index_alias: string;
    /**
     * Is Updated
     */
    is_updated: boolean;
};

/**
 * Industry
 */
export const Industry = {
    ADVERTISEMENTS: 'advertisements',
    AUTOMOBILES: 'automobiles',
    BLOCKCHAIN_AND_CRYPTO: 'blockchain_and_crypto',
    CHEMICALS: 'chemicals',
    CLIMATE_AND_SUSTAINABILITY: 'climate_and_sustainability',
    CONSUMER_GOODS: 'consumer_goods',
    CONSUMER_SERVICES: 'consumer_services',
    CONSUMER_TECH: 'consumer_tech',
    EDUCATION: 'education',
    ENERGY: 'energy',
    ENTERPRISE_SOFTWARE: 'enterprise_software',
    FINANCIAL_SERVICES: 'financial_services',
    FINTECH: 'fintech',
    GOVERNMENT_AND_MILITARY: 'government_and_military',
    HEALTHCARE: 'healthcare',
    INDUSTRIALS: 'industrials',
    INSURANCE: 'insurance',
    MARKETPLACES: 'marketplaces',
    MEDIA_AND_ENTERTAINMENT: 'media_and_entertainment',
    NONPROFITS: 'nonprofits',
    OTHER: 'other',
    PHARMACEUTICALS_AND_BIOTECHNOLOGY: 'pharmaceuticals_and_biotechnology',
    PROFESSIONAL_SERVICES: 'professional_services',
    REAL_ESTATE: 'real_estate',
    RETAIL_AND_HOSPITALITY: 'retail_and_hospitality',
    SEMICONDUCTORS_AND_IT_HARDWARE: 'semiconductors_and_it_hardware',
    TELECOMMUNICATIONS: 'telecommunications',
    TRANSPORTATION: 'transportation',
    VENTURE_CAPITAL_AND_PRIVATE_EQUITY: 'venture_capital_and_private_equity'
} as const;

/**
 * Industry
 */
export type Industry = typeof Industry[keyof typeof Industry];

/**
 * InterestPersonal
 */
export const InterestPersonal = {
    ACADEMIA: 'academia',
    AEROSPACE_AND_SPACE: 'aerospace_and_space',
    AGRICULTURE: 'agriculture',
    ART: 'art',
    FILM: 'film',
    FINANCIAL_INCLUSION: 'financial_inclusion',
    FOOD_AND_RESTAURANTS: 'food_and_restaurants',
    GAMING: 'gaming',
    MEDIA: 'media',
    MUSIC: 'music',
    OUTDOOR_ACTIVITIES: 'outdoor_activities',
    PETS_AND_ANIMALS: 'pets_and_animals',
    PHILANTHROPY_AND_NON_PROFITS: 'philanthropy_and_non_profits',
    POLITICS: 'politics',
    PSYCHOLOGY: 'psychology',
    PUBLIC_SPEAKING_AND_PANELS: 'public_speaking_and_panels',
    SKIING: 'skiing',
    SPORTS: 'sports',
    TRAVEL: 'travel',
    WINE: 'wine'
} as const;

/**
 * InterestPersonal
 */
export type InterestPersonal = typeof InterestPersonal[keyof typeof InterestPersonal];

/**
 * InterestProfessional
 */
export const InterestProfessional = {
    AI_AND_ML: 'ai_and_ml',
    ANGEL_AND_VC_INVESTING: 'angel_and_vc_investing',
    BEAUTY_AND_FASHION: 'beauty_and_fashion',
    CLIMATE: 'climate',
    CRYPTO_AND_BLOCKCHAIN: 'crypto_and_blockchain',
    DEI: 'dei',
    DISRUPTIVE_TECHNOLOGY: 'disruptive_technology',
    ECOMMERCE: 'ecommerce',
    EDUCATION: 'education',
    ENTREPRENEURSHIP: 'entrepreneurship',
    GEOPOLITICS: 'geopolitics',
    GOVERNMENT_AND_PUBLIC_SECTOR: 'government_and_public_sector',
    PRIVATE_EQUITY_OPERATING: 'private_equity_operating',
    WELLNESS: 'wellness',
    WOMEN_IN_BUSINESS: 'women_in_business'
} as const;

/**
 * InterestProfessional
 */
export type InterestProfessional = typeof InterestProfessional[keyof typeof InterestProfessional];

/**
 * InterestRole
 */
export const InterestRole = {
    SEEKING_ACTIVELY: 'seeking_actively',
    SEEKING_NO: 'seeking_no',
    SEEKING_PASSIVELY: 'seeking_passively'
} as const;

/**
 * InterestRole
 */
export type InterestRole = typeof InterestRole[keyof typeof InterestRole];

/**
 * InternalCommentResponse
 */
export type InternalCommentResponse = {
    /**
     * Candidate
     *
     * Candidate for which this internal comment was made
     */
    candidate: UuidResponse;
    /**
     * Person
     *
     * Internal comment author (advisor only)
     */
    person: PersonBasicResponse;
    /**
     * Comment text
     *
     * The content of the internal recommendation comment
     */
    content: string;
    /**
     * Created
     *
     * Date when this internal comment was published
     */
    created: string;
    /**
     * Internal Comment ID
     *
     * The unique identifier for internal comment
     */
    id: string;
    /**
     * Last modified
     *
     * Date when this internal comment was modified
     */
    last_modified: string;
    /**
     * Pipeline
     *
     * Pipeline in which this internal comment was made
     */
    pipeline?: UuidResponse | null;
    /**
     * Recommendation
     *
     * Recommendation for which this internal comment was made
     */
    recommendation?: UuidResponse | null;
    /**
     * Team
     *
     * Team in which this internal comment was made
     */
    team: UuidResponse;
};

/**
 * IntroCheckType
 */
export const IntroCheckType = { UPCOMING_INTROS: 'upcoming_intros', OVERDUE_INTROS: 'overdue_intros' } as const;

/**
 * IntroCheckType
 */
export type IntroCheckType = typeof IntroCheckType[keyof typeof IntroCheckType];

/**
 * IntroEmailType
 */
export const IntroEmailType = { AUTO: 'auto', MANUAL: 'manual' } as const;

/**
 * IntroEmailType
 */
export type IntroEmailType = typeof IntroEmailType[keyof typeof IntroEmailType];

/**
 * IntroRequestItem
 *
 * One candidate a team requested an intro to (a COMPLETED evaluation_history event).
 */
export type IntroRequestItem = {
    /**
     * Candidate Uuid
     */
    candidate_uuid: string;
    /**
     * Name
     */
    name: string;
    /**
     * Role
     */
    role?: string | null;
    /**
     * Company
     */
    company?: string | null;
    /**
     * Headline
     */
    headline?: string | null;
    source: RequestSource;
    /**
     * Requested Date
     */
    requested_date: string;
    /**
     * Requester Name
     */
    requester_name?: string | null;
};

/**
 * IntroStats
 */
export type IntroStats = {
    /**
     * Intro type
     *
     * Type of intro
     */
    intro_type: GoalIntroType;
    /**
     * Intro count
     *
     * Number of intros
     */
    intro_count?: number;
    /**
     * Intro log IDs
     *
     * List of intro log IDs
     */
    intro_log_ids?: Array<string> | null;
};

/**
 * IntroTypeEnum
 */
export const IntroTypeEnum = {
    P2P: 'P2P',
    TRENDING_BATCH: 'TRENDING_BATCH',
    TRENDING_STREAM: 'TRENDING_STREAM'
} as const;

/**
 * IntroTypeEnum
 */
export type IntroTypeEnum = typeof IntroTypeEnum[keyof typeof IntroTypeEnum];

/**
 * IntroductionStatus
 */
export const IntroductionStatus = {
    FACILITATED: 'facilitated',
    NOT_FACILITATED: 'not_facilitated',
    NOT_REQUESTED: 'not_requested',
    REQUESTED: 'requested'
} as const;

/**
 * IntroductionStatus
 */
export type IntroductionStatus = typeof IntroductionStatus[keyof typeof IntroductionStatus];

/**
 * IntroductionStatusContext
 */
export const IntroductionStatusContext = { NO_INTEREST: 'no_interest', UNREACHABLE: 'unreachable' } as const;

/**
 * IntroductionStatusContext
 */
export type IntroductionStatusContext = typeof IntroductionStatusContext[keyof typeof IntroductionStatusContext];

/**
 * IntroductionStatusFilter
 */
export const IntroductionStatusFilter = { NEVER_INTRODUCED: 'never_introduced', PREVIOUSLY_INTRODUCED: 'previously_introduced' } as const;

/**
 * IntroductionStatusFilter
 */
export type IntroductionStatusFilter = typeof IntroductionStatusFilter[keyof typeof IntroductionStatusFilter];

/**
 * IntroductionStatusPayload
 */
export type IntroductionStatusPayload = {
    /**
     * Context
     *
     * Additional detail about the introduction status change
     */
    context?: string | null;
    /**
     * External introduction
     *
     * External person the introduction is for
     */
    external_introduction?: RecommendationExternalIntroduction | null;
    /**
     * Log intro
     *
     * Only log the introduction request, used for backfilling already made intros
     */
    log_only?: boolean | null;
    /**
     * Person ID
     *
     * Advisor requests an introduction on behalf of other person when this value is set
     */
    person_id?: string | null;
    /**
     * Status
     *
     * Change the introduction status to this value
     */
    status: RecommendationReviewState;
};

/**
 * IntroductionSubjectPayload
 *
 * Payload for editing who an existing intro request is for.
 *
 * An introduction has exactly one subject: either an external person
 * (not a platform user) or a platform user the request was made on
 * behalf of.
 */
export type IntroductionSubjectPayload = {
    /**
     * External introduction
     *
     * External person the introduction is for
     */
    external_introduction?: ExternalIntroductionSubject | null;
    /**
     * On behalf of user UUID
     *
     * Platform user the introduction was requested on behalf of
     */
    on_behalf_of_user_uuid?: string | null;
    /**
     * Introduction comment
     *
     * The request comment. PUT semantics: null or omitted clears any existing comment
     */
    introduction_comment?: string | null;
};

/**
 * IntroductionType
 */
export const IntroductionType = {
    PIPELINE: 'pipeline',
    SEARCH: 'search',
    TEAM: 'team'
} as const;

/**
 * IntroductionType
 */
export type IntroductionType = typeof IntroductionType[keyof typeof IntroductionType];

/**
 * InvitePreviewPayload
 *
 * Optional payload for sending invite preview. Omit email to send to the current user.
 */
export type InvitePreviewPayload = {
    /**
     * Email
     *
     * Send preview to this email. Omit to use the current user's email.
     */
    email?: string | null;
    /**
     * Subject
     *
     * Preview email subject. Omit to use the event's saved invite subject.
     */
    subject?: string | null;
    /**
     * Body
     *
     * Preview email body (HTML). Omit to use event's saved body.
     */
    body?: string | null;
};

/**
 * LastEventBucket
 *
 * When the guest last attended an event (time buckets for filter/aggregation).
 */
export const LastEventBucket = {
    '1_MONTH': '1_month',
    '3_MONTHS': '3_months',
    '6_MONTHS': '6_months',
    '9_MONTHS': '9_months',
    '12_PLUS': '12_plus'
} as const;

/**
 * LastEventBucket
 *
 * When the guest last attended an event (time buckets for filter/aggregation).
 */
export type LastEventBucket = typeof LastEventBucket[keyof typeof LastEventBucket];

/**
 * LinkCandidateResponse
 */
export type LinkCandidateResponse = {
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Search Uuid
     */
    search_uuid: string;
    /**
     * Role Title
     */
    role_title: string;
    /**
     * Target Company Name
     */
    target_company_name: string | null;
    /**
     * Client Name
     */
    client_name: string | null;
    /**
     * Search Firm Name
     */
    search_firm_name: string | null;
    /**
     * Received At
     */
    received_at: string | null;
    /**
     * Similarity
     */
    similarity: number;
    /**
     * Reason
     */
    reason: string;
};

/**
 * LinkedInBatchSyncResponse
 *
 * Response schema for LinkedIn batch sync job.
 */
export type LinkedInBatchSyncResponse = {
    /**
     * Candidates Found
     *
     * Number of candidates matching sync criteria
     */
    candidates_found: number;
    /**
     * Sync Started
     *
     * Whether the sync was actually executed (false for dry-run)
     */
    sync_started: boolean;
    /**
     * Dry Run
     *
     * Whether this was a dry-run (no actual sync performed)
     */
    dry_run: boolean;
    /**
     * Estimated Credits
     *
     * Estimated Proxycurl API credits used/required (2 per candidate)
     */
    estimated_credits: number;
    /**
     * Errors
     *
     * List of errors encountered during sync (limited to first 10)
     */
    errors?: Array<{
        [key: string]: unknown;
    }>;
};

/**
 * LinkedInPreviewRequest
 */
export type LinkedInPreviewRequest = {
    /**
     * Linkedin Url
     *
     * The profile to preview. Without it, it is looked up by name and company.
     */
    linkedin_url?: string | null;
    /**
     * First Name
     */
    first_name?: string | null;
    /**
     * Last Name
     */
    last_name?: string | null;
    /**
     * Company
     *
     * Current company, by name or domain
     */
    company?: string | null;
    /**
     * Title
     *
     * Current title; narrows a lookup by name
     */
    title?: string | null;
    /**
     * Fresh
     *
     * Read LinkedIn as it is now: no stored copy older than an hour, and the provider re-reads a profile it last saw over a month ago. Costs about one credit more when it does.
     */
    fresh?: boolean;
};

/**
 * LinkedInPreviewResponse
 */
export type LinkedInPreviewResponse = {
    /**
     * Linkedin Url
     */
    linkedin_url: string;
    /**
     * Name
     */
    name?: string | null;
    /**
     * Headline
     */
    headline?: string | null;
    /**
     * Current Role
     */
    current_role?: string | null;
    /**
     * Current Company
     */
    current_company?: string | null;
    /**
     * Location
     */
    location?: string | null;
    /**
     * Photo Url
     *
     * Signed and short-lived: show it now, don't store it
     */
    photo_url?: string | null;
    /**
     * Fetched At
     *
     * When LinkedIn was read; earlier than now on a cache hit
     */
    fetched_at: string;
    /**
     * Cache Hit
     */
    cache_hit: boolean;
    /**
     * Matches
     *
     * Whether the profile's last name and current company match the ones sent. Null when no last name and company were sent.
     */
    matches?: boolean | null;
};

/**
 * LinkedInResumeResponse
 */
export type LinkedInResumeResponse = {
    /**
     * Honors and awards
     *
     * List of honors and awards
     */
    awards: Array<string>;
    /**
     * Certifications
     *
     * List of certifications
     */
    certifications: Array<string>;
    /**
     * Experience
     *
     * List of work experiences
     */
    experience: Array<CompanyResponse>;
    /**
     * File Name
     *
     * Path to the stored file
     */
    file_name: string;
    /**
     * Headline
     *
     * Headline text
     */
    headline: string;
    /**
     * Languages
     *
     * List of languages
     */
    languages: Array<string>;
    /**
     * Last modified
     *
     * Timestamp when resume was last updated
     */
    last_modified: string;
    /**
     * LinkedIn URL
     *
     * Link to the person's LinkedIn profile
     */
    linkedin_url: string | null;
    /**
     * Location
     *
     * Location string
     */
    location: string;
    /**
     * Name
     *
     * Person's full name
     */
    name: string;
    /**
     * Patents
     *
     * List of patents
     */
    patents: Array<string>;
    /**
     * Publications
     *
     * List of publications
     */
    publications: Array<string>;
    /**
     * Education
     *
     * List of schools
     */
    education: Array<SchoolResponse>;
    /**
     * Skills
     *
     * List of skills
     */
    skills: Array<string>;
    /**
     * Summary
     *
     * Summary text about person
     */
    summary?: string | null;
    /**
     * Version
     *
     * Schema version, for compatibility
     */
    version: number;
};

/**
 * LinkedInResumeUploadResponse
 */
export type LinkedInResumeUploadResponseInput = {
    /**
     * LinkedIn URL
     *
     * Link to the person's LinkedIn profile
     */
    linkedin_url?: string | null;
    /**
     * Honors and awards
     *
     * List of honors and awards
     */
    awards: Array<string>;
    /**
     * Certifications
     *
     * List of certifications
     */
    certifications: Array<string>;
    /**
     * Experience
     *
     * List of work experiences
     */
    experience: Array<CompanyUploadResponse>;
    /**
     * File Name
     *
     * Path to the stored file
     */
    file_name: string;
    /**
     * Headline
     *
     * Headline text
     */
    headline: string;
    /**
     * Languages
     *
     * List of languages
     */
    languages: Array<string>;
    /**
     * Location
     *
     * Location string
     */
    location: string;
    /**
     * Last modified
     *
     * Timestamp when resume was last updated
     */
    last_modified?: string | null;
    /**
     * Name
     *
     * Person's full name
     */
    name: string;
    /**
     * Patents
     *
     * List of patents
     */
    patents: Array<string>;
    /**
     * Publications
     *
     * List of publications
     */
    publications: Array<string>;
    /**
     * Education
     *
     * List of schools
     */
    education: Array<SchoolUploadResponse>;
    /**
     * Skills
     *
     * List of skills
     */
    skills: Array<string>;
    /**
     * Summary
     *
     * Summary text about person
     */
    summary?: string | null;
    /**
     * Version
     *
     * Schema version, for compatibility
     */
    version: number;
};

/**
 * LinkedInResumeUploadResponse
 */
export type LinkedInResumeUploadResponseOutput = {
    /**
     * LinkedIn URL
     *
     * Link to the person's LinkedIn profile
     */
    linkedin_url?: string | null;
    /**
     * Honors and awards
     *
     * List of honors and awards
     */
    awards: Array<string>;
    /**
     * Certifications
     *
     * List of certifications
     */
    certifications: Array<string>;
    /**
     * Experience
     *
     * List of work experiences
     */
    experience: Array<CompanyUploadResponse>;
    /**
     * File Name
     *
     * Path to the stored file
     */
    file_name: string;
    /**
     * Headline
     *
     * Headline text
     */
    headline: string;
    /**
     * Languages
     *
     * List of languages
     */
    languages: Array<string>;
    /**
     * Location
     *
     * Location string
     */
    location: string;
    /**
     * Last modified
     *
     * Timestamp when resume was last updated
     */
    last_modified?: string | null;
    /**
     * Name
     *
     * Person's full name
     */
    name: string;
    /**
     * Patents
     *
     * List of patents
     */
    patents: Array<string>;
    /**
     * Publications
     *
     * List of publications
     */
    publications: Array<string>;
    /**
     * Education
     *
     * List of schools
     */
    education: Array<SchoolUploadResponse>;
    /**
     * Skills
     *
     * List of skills
     */
    skills: Array<string>;
    /**
     * Summary
     *
     * Summary text about person
     */
    summary?: string | null;
    /**
     * Version
     *
     * Schema version, for compatibility
     */
    version: number;
};

/**
 * LinkedInSyncRequest
 */
export type LinkedInSyncRequest = {
    /**
     * Replace Photo
     *
     * LinkedIn's photo replaces the candidate's current one. Without it, a photo is only filled in when missing.
     */
    replace_photo?: boolean;
    /**
     * Fresh
     *
     * Read LinkedIn as it is now: the provider re-reads a profile it last saw over a month ago instead of serving that copy. Costs about one credit more when it does.
     */
    fresh?: boolean;
};

/**
 * LocationAddress
 *
 * Attributes
 * ----------
 * address_1 : str
 * first line of mailing address (street address)
 * address_2 : str
 * optional second line of mailing address (street address)
 * city : str
 * city or township associated with this address
 * region : str
 * state or province
 * postal_code : str
 * this is the postal code for the address
 * country : str
 * this will be translated into a two-character ISO code
 */
export type LocationAddress = {
    /**
     * Address 1
     */
    address_1: string;
    /**
     * Address 2
     */
    address_2?: string | null;
    /**
     * City
     */
    city: string;
    /**
     * Region
     */
    region: string;
    /**
     * Postal Code
     */
    postal_code: string;
    /**
     * Country
     */
    country: string;
};

/**
 * LoginCreatePayload
 *
 * A new login for a human: an executive, an enterprise (B4B) user or a
 * Banff admin.
 */
export type LoginCreatePayload = {
    /**
     * User type
     *
     * executive, enterprise or admin
     */
    user_type: 'executive' | 'enterprise' | 'admin';
    /**
     * Email
     */
    email: string;
    /**
     * First name
     */
    first_name: string;
    /**
     * Last name
     */
    last_name: string;
    /**
     * Send invitation
     *
     * Send the invitation email now. An enterprise login with no team gets no invitation until it has one.
     */
    send_invitation?: boolean;
    /**
     * Team UUID
     *
     * Enterprise only: the team the login joins (and that team's company). Without it the login has no company or team yet.
     */
    team_uuid?: string | null;
    /**
     * Lead
     *
     * Enterprise only: a lead seat on the team instead of a member seat.
     */
    lead?: boolean;
    /**
     * Notifications before registering
     *
     * Enterprise only: receive emails such as Trending before accepting the invitation.
     */
    permit_preregistration_notifications?: boolean;
};

/**
 * MarketCapRange
 */
export const MarketCapRange = {
    '<_300M': '<$300M',
    $300M_2B: '$300M-$2B',
    $2B_10B: '$2B-$10B',
    $10B_200B: '$10B-$200B',
    '>_200B': '>$200B'
} as const;

/**
 * MarketCapRange
 */
export type MarketCapRange = typeof MarketCapRange[keyof typeof MarketCapRange];

/**
 * MarketSearchAttachedBy
 *
 * How a market-search observation joined its search group.
 */
export const MarketSearchAttachedBy = {
    NEW: 'new',
    RULE: 'rule',
    MODEL: 'model',
    ADVISOR: 'advisor'
} as const;

/**
 * MarketSearchAttachedBy
 *
 * How a market-search observation joined its search group.
 */
export type MarketSearchAttachedBy = typeof MarketSearchAttachedBy[keyof typeof MarketSearchAttachedBy];

/**
 * MarketSearchCandidateRef
 */
export type MarketSearchCandidateRef = {
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Full Name
     */
    full_name: string;
};

/**
 * MarketSearchClosePayload
 */
export type MarketSearchClosePayload = {
    /**
     * Closed Evidence
     *
     * Why it is closed: a pipeline reference, an announcement URL, or a note.
     */
    closed_evidence?: string | null;
    /**
     * Closed At
     */
    closed_at?: string | null;
};

/**
 * MarketSearchCompanyRef
 */
export type MarketSearchCompanyRef = {
    /**
     * Id
     */
    id: number;
    /**
     * Name
     */
    name: string | null;
    /**
     * Slug
     */
    slug: string;
    type: OrgType;
};

/**
 * MarketSearchContactOption
 *
 * A person who can be linked as a search's contact, with the teams they sit on.
 */
export type MarketSearchContactOption = {
    /**
     * Identity Uuid
     */
    identity_uuid: string;
    /**
     * Person Id
     */
    person_id: string;
    /**
     * Full Name
     */
    full_name: string | null;
    /**
     * Email
     */
    email: string | null;
    /**
     * Teams
     */
    teams: Array<MarketSearchContactTeam>;
};

/**
 * MarketSearchContactTeam
 */
export type MarketSearchContactTeam = {
    /**
     * Id
     */
    id: number;
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Name
     */
    name: string;
    company: MarketSearchCompanyRef;
};

/**
 * MarketSearchCreatePayload
 *
 * One observation of a search in the market.
 *
 * Leave a field out when the source did not state it — never send
 * placeholders like "Not specified". Classification fields are hints from
 * the caller (a model that read the note, or a person); the server fills what
 * is left blank from its title dictionary and validates every value.
 */
export type MarketSearchCreatePayload = {
    /**
     * Role Title
     */
    role_title: string;
    role_type?: PipelineRoleType | null;
    seniority?: Seniority | null;
    /**
     * Functions
     */
    functions?: Array<Function>;
    /**
     * Target Company Name
     *
     * The company the role is at.
     */
    target_company_name?: string | null;
    /**
     * Target Company Industry
     */
    target_company_industry?: string | null;
    /**
     * Search Firm Name
     *
     * Who is running the search: a search firm, an investor, or the target company itself.
     */
    search_firm_name?: string | null;
    /**
     * Who made contact: a search firm or investor, or the target company itself. Wins over the contact type read off search_firm_name; leave it out to have it read from there.
     */
    contact_type?: ContactType | null;
    /**
     * Search Contact Name
     */
    search_contact_name?: string | null;
    /**
     * Client Name
     */
    client_name?: string | null;
    /**
     * Candidate Uuid
     *
     * The exec client the search reached, when the caller already knows it.
     */
    candidate_uuid?: string | null;
    source: MarketSearchSource;
    /**
     * Source Text
     *
     * The note or transcript excerpt as heard.
     */
    source_text?: string | null;
    /**
     * Received At
     *
     * When the outreach happened; defaults to now.
     */
    received_at?: string | null;
    /**
     * Target Company Id
     *
     * The target company, if picked.
     */
    target_company_id?: number | null;
    /**
     * Search Firm Company Id
     *
     * The search firm or investor, if picked.
     */
    search_firm_company_id?: number | null;
    /**
     * Search Team Id
     *
     * The firm's team, if picked.
     */
    search_team_id?: number | null;
    /**
     * Search Contact Identity Uuid
     *
     * The person at the firm who made contact, if picked.
     */
    search_contact_identity_uuid?: string | null;
};

/**
 * MarketSearchCreateResponse
 *
 * What happened, what resolved, and what a person should confirm.
 */
export type MarketSearchCreateResponse = {
    /**
     * Outcome
     */
    outcome: 'created' | 'linked' | 'existing';
    observation: MarketSearchResponse;
    resolution: ResolutionResponse;
    /**
     * Link Candidates
     */
    link_candidates: Array<LinkCandidateResponse>;
    /**
     * Message
     */
    message: string;
};

/**
 * MarketSearchDetailResponse
 *
 * Everything one observation holds, and the other observations of its search.
 */
export type MarketSearchDetailResponse = {
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Search Uuid
     */
    search_uuid: string;
    /**
     * Same Search As Uuid
     */
    same_search_as_uuid: string | null;
    /**
     * Is Hub
     */
    is_hub: boolean;
    attached_by: MarketSearchAttachedBy;
    /**
     * Group Size
     *
     * Live observations of this search, this one included.
     */
    group_size: number;
    /**
     * Role Title
     */
    role_title: string;
    role_type: PipelineRoleType | null;
    seniority: Seniority | null;
    /**
     * Functions
     */
    functions: Array<Function>;
    /**
     * Target Company Name
     */
    target_company_name: string | null;
    /**
     * Target Company Industry
     */
    target_company_industry: string | null;
    /**
     * Target Company Id
     */
    target_company_id: number | null;
    target_company: MarketSearchCompanyRef | null;
    /**
     * Search Firm Name
     */
    search_firm_name: string | null;
    contact_type: ContactType | null;
    /**
     * Search Firm Company Id
     */
    search_firm_company_id: number | null;
    search_firm_company: MarketSearchCompanyRef | null;
    /**
     * Search Team Id
     */
    search_team_id: number | null;
    search_team: MarketSearchTeamRef | null;
    /**
     * Search Contact Name
     */
    search_contact_name: string | null;
    /**
     * Search Contact Identity Uuid
     */
    search_contact_identity_uuid: string | null;
    search_contact: MarketSearchPersonRef | null;
    /**
     * Client Name
     */
    client_name: string | null;
    /**
     * Candidate Uuid
     */
    candidate_uuid: string | null;
    candidate: MarketSearchCandidateRef | null;
    status: MarketSearchStatus;
    /**
     * Closed At
     */
    closed_at: string | null;
    /**
     * Closed Evidence
     */
    closed_evidence: string | null;
    /**
     * Pipeline Id
     */
    pipeline_id: number | null;
    pipeline: MarketSearchPipelineRef | null;
    source: MarketSearchSource;
    /**
     * Reported By Person Id
     */
    reported_by_person_id: string | null;
    reported_by: MarketSearchReporterRef | null;
    /**
     * Received At
     */
    received_at: string | null;
    /**
     * Created
     */
    created: string;
    /**
     * Last Modified
     */
    last_modified: string;
    /**
     * Source Text
     */
    source_text: string | null;
    /**
     * Notion Page Id
     */
    notion_page_id: string | null;
    /**
     * Extraction Model
     */
    extraction_model: string | null;
    /**
     * Last Resolved At
     */
    last_resolved_at: string | null;
    /**
     * Group
     *
     * The other live observations of this search, earliest first.
     */
    group: Array<MarketSearchGroupMember>;
};

/**
 * MarketSearchFilters
 *
 * Every filter is optional; values within one list are ORed, filters ANDed.
 */
export type MarketSearchFilters = {
    /**
     * Status
     */
    status?: Array<MarketSearchStatus> | null;
    /**
     * Role Type
     */
    role_type?: Array<PipelineRoleType> | null;
    /**
     * Seniority
     */
    seniority?: Array<Seniority> | null;
    /**
     * Functions
     *
     * Rows carrying ANY of these functions.
     */
    functions?: Array<Function> | null;
    /**
     * Contact Type
     */
    contact_type?: Array<ContactType> | null;
    /**
     * Source
     */
    source?: Array<MarketSearchSource> | null;
    /**
     * Received After
     */
    received_after?: string | null;
    /**
     * Received Before
     */
    received_before?: string | null;
    /**
     * Target Company Id
     */
    target_company_id?: number | null;
    /**
     * Search Firm Company Id
     */
    search_firm_company_id?: number | null;
    /**
     * Search Team Id
     */
    search_team_id?: number | null;
    /**
     * Search Contact Identity Uuid
     */
    search_contact_identity_uuid?: string | null;
    /**
     * Candidate Uuid
     */
    candidate_uuid?: string | null;
    /**
     * Search Uuid
     *
     * Only the observations of this one search.
     */
    search_uuid?: string | null;
    /**
     * Needs Attention
     *
     * Rows with ANY of these gaps.
     */
    needs_attention?: Array<MarketSearchNeedsAttention> | null;
};

/**
 * MarketSearchGroupMember
 */
export type MarketSearchGroupMember = {
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Is Hub
     */
    is_hub: boolean;
    attached_by: MarketSearchAttachedBy;
    /**
     * Received At
     */
    received_at: string | null;
    /**
     * Client Name
     */
    client_name: string | null;
    /**
     * Search Firm Name
     */
    search_firm_name: string | null;
    /**
     * Search Contact Name
     */
    search_contact_name: string | null;
};

/**
 * MarketSearchJobResponse
 *
 * Response schema for the market-search maintenance jobs.
 *
 * The routes queue work rather than doing it: a sweep can run for minutes and
 * the classification catch-up spends most of that waiting on a model, neither
 * of which belongs in a scheduler's HTTP call. The report each job produces is
 * logged and returned as the Celery task result, keyed by `task_id`.
 */
export type MarketSearchJobResponse = {
    /**
     * Job
     *
     * Name of the queued Celery task
     */
    job: string;
    /**
     * Task Id
     *
     * Celery task id, or null if the broker refused the message
     */
    task_id?: string | null;
    /**
     * Limit
     *
     * Maximum rows this run will examine
     */
    limit: number;
    /**
     * Dry Run
     *
     * Whether the run rolls back instead of committing
     */
    dry_run: boolean;
};

/**
 * MarketSearchLinkPayload
 */
export type MarketSearchLinkPayload = {
    /**
     * Hub Uuid
     *
     * The search to attach this observation to (a hub).
     */
    hub_uuid: string;
};

/**
 * MarketSearchNeedsAttention
 *
 * A name was given and the link it should have is missing.
 */
export const MarketSearchNeedsAttention = {
    TARGET_COMPANY_UNRESOLVED: 'target_company_unresolved',
    FIRM_UNRESOLVED: 'firm_unresolved',
    TEAM_UNRESOLVED: 'team_unresolved',
    CONTACT_UNRESOLVED: 'contact_unresolved',
    CLIENT_UNRESOLVED: 'client_unresolved',
    UNCLASSIFIED: 'unclassified'
} as const;

/**
 * MarketSearchNeedsAttention
 *
 * A name was given and the link it should have is missing.
 */
export type MarketSearchNeedsAttention = typeof MarketSearchNeedsAttention[keyof typeof MarketSearchNeedsAttention];

/**
 * MarketSearchPersonRef
 *
 * A human by identity, with the name and email of their earliest login.
 */
export type MarketSearchPersonRef = {
    /**
     * Identity Uuid
     */
    identity_uuid: string;
    /**
     * Person Id
     */
    person_id: string;
    /**
     * Full Name
     */
    full_name: string | null;
    /**
     * Email
     */
    email: string | null;
};

/**
 * MarketSearchPipelineOption
 */
export type MarketSearchPipelineOption = {
    /**
     * Id
     */
    id: number;
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Name
     */
    name: string;
    /**
     * Slug
     */
    slug: string;
    /**
     * Active
     */
    active: boolean;
    /**
     * Team Name
     */
    team_name: string;
    company: MarketSearchCompanyRef | null;
};

/**
 * MarketSearchPipelineRef
 */
export type MarketSearchPipelineRef = {
    /**
     * Id
     */
    id: number;
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Name
     */
    name: string;
    /**
     * Slug
     */
    slug: string;
};

/**
 * MarketSearchReporterRef
 */
export type MarketSearchReporterRef = {
    /**
     * Id
     */
    id: string;
    /**
     * Full Name
     */
    full_name: string | null;
};

/**
 * MarketSearchResponse
 */
export type MarketSearchResponse = {
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Search Uuid
     */
    search_uuid: string;
    /**
     * Same Search As Uuid
     */
    same_search_as_uuid: string | null;
    /**
     * Is Hub
     */
    is_hub: boolean;
    /**
     * Attached By
     */
    attached_by: string;
    /**
     * Role Title
     */
    role_title: string;
    /**
     * Role Type
     */
    role_type: string | null;
    /**
     * Seniority
     */
    seniority: string | null;
    /**
     * Functions
     */
    functions: Array<string>;
    /**
     * Target Company Name
     */
    target_company_name: string | null;
    /**
     * Target Company Industry
     */
    target_company_industry: string | null;
    /**
     * Target Company Id
     */
    target_company_id: number | null;
    /**
     * Search Firm Name
     */
    search_firm_name: string | null;
    /**
     * Search Contact Name
     */
    search_contact_name: string | null;
    /**
     * Search Contact Identity Uuid
     */
    search_contact_identity_uuid: string | null;
    /**
     * Search Firm Company Id
     */
    search_firm_company_id: number | null;
    /**
     * Search Team Id
     */
    search_team_id: number | null;
    /**
     * Client Name
     */
    client_name: string | null;
    /**
     * Candidate Uuid
     */
    candidate_uuid: string | null;
    /**
     * Status
     */
    status: string;
    /**
     * Closed At
     */
    closed_at: string | null;
    /**
     * Closed Evidence
     */
    closed_evidence: string | null;
    /**
     * Pipeline Id
     */
    pipeline_id: number | null;
    /**
     * Source
     */
    source: string;
    /**
     * Reported By Person Id
     */
    reported_by_person_id: string | null;
    /**
     * Received At
     */
    received_at: string | null;
    /**
     * Created
     */
    created: string;
    /**
     * Last Modified
     */
    last_modified: string;
};

/**
 * MarketSearchRowResponse
 *
 * One observation as the market-search table shows it, links included.
 */
export type MarketSearchRowResponse = {
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Search Uuid
     */
    search_uuid: string;
    /**
     * Same Search As Uuid
     */
    same_search_as_uuid: string | null;
    /**
     * Is Hub
     */
    is_hub: boolean;
    attached_by: MarketSearchAttachedBy;
    /**
     * Group Size
     *
     * Live observations of this search, this one included.
     */
    group_size: number;
    /**
     * Role Title
     */
    role_title: string;
    role_type: PipelineRoleType | null;
    seniority: Seniority | null;
    /**
     * Functions
     */
    functions: Array<Function>;
    /**
     * Target Company Name
     */
    target_company_name: string | null;
    /**
     * Target Company Industry
     */
    target_company_industry: string | null;
    /**
     * Target Company Id
     */
    target_company_id: number | null;
    target_company: MarketSearchCompanyRef | null;
    /**
     * Search Firm Name
     */
    search_firm_name: string | null;
    contact_type: ContactType | null;
    /**
     * Search Firm Company Id
     */
    search_firm_company_id: number | null;
    search_firm_company: MarketSearchCompanyRef | null;
    /**
     * Search Team Id
     */
    search_team_id: number | null;
    search_team: MarketSearchTeamRef | null;
    /**
     * Search Contact Name
     */
    search_contact_name: string | null;
    /**
     * Search Contact Identity Uuid
     */
    search_contact_identity_uuid: string | null;
    search_contact: MarketSearchPersonRef | null;
    /**
     * Client Name
     */
    client_name: string | null;
    /**
     * Candidate Uuid
     */
    candidate_uuid: string | null;
    candidate: MarketSearchCandidateRef | null;
    status: MarketSearchStatus;
    /**
     * Closed At
     */
    closed_at: string | null;
    /**
     * Closed Evidence
     */
    closed_evidence: string | null;
    /**
     * Pipeline Id
     */
    pipeline_id: number | null;
    pipeline: MarketSearchPipelineRef | null;
    source: MarketSearchSource;
    /**
     * Reported By Person Id
     */
    reported_by_person_id: string | null;
    reported_by: MarketSearchReporterRef | null;
    /**
     * Received At
     */
    received_at: string | null;
    /**
     * Created
     */
    created: string;
    /**
     * Last Modified
     */
    last_modified: string;
};

/**
 * MarketSearchSearchPayload
 */
export type MarketSearchSearchPayload = {
    /**
     * Phrase
     *
     * Search phrase
     */
    phrase?: string | null;
    /**
     * Search ID
     *
     * Search session ID
     */
    search_id?: string | null;
    /**
     * Page
     *
     * Page number
     */
    page?: number;
    /**
     * Page size
     *
     * Page size
     */
    page_size?: number;
    /**
     * Filters
     *
     * Filter results by these criteria
     */
    filter_data?: MarketSearchFilters | null;
    /**
     * Sort
     *
     * Sort results by these rules; newest heard first when empty
     */
    sort_rules?: Array<MarketSearchSort>;
};

/**
 * MarketSearchSort
 */
export type MarketSearchSort = {
    /**
     * Column
     *
     * Sort results on this column
     */
    column: MarketSearchSortColumn;
    /**
     * Direction
     *
     * Sort results in this direction
     */
    direction?: SortDirection;
};

/**
 * MarketSearchSortColumn
 */
export const MarketSearchSortColumn = {
    RECEIVED_AT: 'received_at',
    CREATED: 'created',
    LAST_MODIFIED: 'last_modified',
    ROLE_TITLE: 'role_title',
    TARGET_COMPANY_NAME: 'target_company_name',
    SEARCH_FIRM_NAME: 'search_firm_name',
    CLIENT_NAME: 'client_name',
    STATUS: 'status'
} as const;

/**
 * MarketSearchSortColumn
 */
export type MarketSearchSortColumn = typeof MarketSearchSortColumn[keyof typeof MarketSearchSortColumn];

/**
 * MarketSearchSource
 */
export const MarketSearchSource = {
    ADVISOR_NOTE: 'advisor_note',
    TRANSCRIPT: 'transcript',
    NOTION_BACKFILL: 'notion_backfill',
    MANUAL: 'manual'
} as const;

/**
 * MarketSearchSource
 */
export type MarketSearchSource = typeof MarketSearchSource[keyof typeof MarketSearchSource];

/**
 * MarketSearchStatus
 */
export const MarketSearchStatus = { ACTIVE: 'active', CLOSED: 'closed' } as const;

/**
 * MarketSearchStatus
 */
export type MarketSearchStatus = typeof MarketSearchStatus[keyof typeof MarketSearchStatus];

/**
 * MarketSearchTeamOption
 */
export type MarketSearchTeamOption = {
    /**
     * Id
     */
    id: number;
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Name
     */
    name: string;
    /**
     * Slug
     */
    slug: string;
    company: MarketSearchCompanyRef;
};

/**
 * MarketSearchTeamRef
 */
export type MarketSearchTeamRef = {
    /**
     * Id
     */
    id: number;
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Name
     */
    name: string;
    /**
     * Slug
     */
    slug: string;
};

/**
 * MarketSearchUpdatePayload
 *
 * Change what was recorded. Only the fields sent are touched.
 *
 * Classification, the target company, status and pipeline describe the
 * *search*, so they are written to every observation of it; everything else
 * belongs to this observation alone. Picking a record by id also sets its
 * name; changing a name without an id looks the name up again (exact match
 * only) and clears the link when nothing matches. Sending an id as null
 * unlinks it and keeps the name.
 */
export type MarketSearchUpdatePayload = {
    /**
     * Role Title
     */
    role_title?: string | null;
    role_type?: PipelineRoleType | null;
    seniority?: Seniority | null;
    /**
     * Functions
     */
    functions?: Array<Function> | null;
    /**
     * Target Company Name
     */
    target_company_name?: string | null;
    /**
     * Target Company Industry
     */
    target_company_industry?: string | null;
    /**
     * Target Company Id
     */
    target_company_id?: number | null;
    /**
     * Search Firm Name
     */
    search_firm_name?: string | null;
    contact_type?: ContactType | null;
    /**
     * Search Firm Company Id
     */
    search_firm_company_id?: number | null;
    /**
     * Search Team Id
     */
    search_team_id?: number | null;
    /**
     * Search Contact Name
     */
    search_contact_name?: string | null;
    /**
     * Search Contact Identity Uuid
     */
    search_contact_identity_uuid?: string | null;
    /**
     * Client Name
     */
    client_name?: string | null;
    /**
     * Candidate Uuid
     */
    candidate_uuid?: string | null;
    status?: MarketSearchStatus | null;
    /**
     * Closed At
     */
    closed_at?: string | null;
    /**
     * Closed Evidence
     */
    closed_evidence?: string | null;
    /**
     * Pipeline Id
     */
    pipeline_id?: number | null;
    /**
     * Source Text
     */
    source_text?: string | null;
    /**
     * Received At
     */
    received_at?: string | null;
};

/**
 * MarketSearchUpdateResponse
 */
export type MarketSearchUpdateResponse = {
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Search Uuid
     */
    search_uuid: string;
    /**
     * Same Search As Uuid
     */
    same_search_as_uuid: string | null;
    /**
     * Is Hub
     */
    is_hub: boolean;
    attached_by: MarketSearchAttachedBy;
    /**
     * Group Size
     *
     * Live observations of this search, this one included.
     */
    group_size: number;
    /**
     * Role Title
     */
    role_title: string;
    role_type: PipelineRoleType | null;
    seniority: Seniority | null;
    /**
     * Functions
     */
    functions: Array<Function>;
    /**
     * Target Company Name
     */
    target_company_name: string | null;
    /**
     * Target Company Industry
     */
    target_company_industry: string | null;
    /**
     * Target Company Id
     */
    target_company_id: number | null;
    target_company: MarketSearchCompanyRef | null;
    /**
     * Search Firm Name
     */
    search_firm_name: string | null;
    contact_type: ContactType | null;
    /**
     * Search Firm Company Id
     */
    search_firm_company_id: number | null;
    search_firm_company: MarketSearchCompanyRef | null;
    /**
     * Search Team Id
     */
    search_team_id: number | null;
    search_team: MarketSearchTeamRef | null;
    /**
     * Search Contact Name
     */
    search_contact_name: string | null;
    /**
     * Search Contact Identity Uuid
     */
    search_contact_identity_uuid: string | null;
    search_contact: MarketSearchPersonRef | null;
    /**
     * Client Name
     */
    client_name: string | null;
    /**
     * Candidate Uuid
     */
    candidate_uuid: string | null;
    candidate: MarketSearchCandidateRef | null;
    status: MarketSearchStatus;
    /**
     * Closed At
     */
    closed_at: string | null;
    /**
     * Closed Evidence
     */
    closed_evidence: string | null;
    /**
     * Pipeline Id
     */
    pipeline_id: number | null;
    pipeline: MarketSearchPipelineRef | null;
    source: MarketSearchSource;
    /**
     * Reported By Person Id
     */
    reported_by_person_id: string | null;
    reported_by: MarketSearchReporterRef | null;
    /**
     * Received At
     */
    received_at: string | null;
    /**
     * Created
     */
    created: string;
    /**
     * Last Modified
     */
    last_modified: string;
    /**
     * Source Text
     */
    source_text: string | null;
    /**
     * Notion Page Id
     */
    notion_page_id: string | null;
    /**
     * Extraction Model
     */
    extraction_model: string | null;
    /**
     * Last Resolved At
     */
    last_resolved_at: string | null;
    /**
     * Group
     *
     * The other live observations of this search, earliest first.
     */
    group: Array<MarketSearchGroupMember>;
    /**
     * Rows Updated
     *
     * Observations written: 1, or every observation of the search when a search-wide field changed.
     */
    rows_updated: number;
};

/**
 * MatchScoreBreakdown
 *
 * The Match score's parts: match = rubric_points + evidence_points - tier_offset, floored at 0.
 *
 * rubric is the RecSys run's 0-100 score (imputed at the run's 35th percentile when the
 * run did not score the candidate); evidence is the 0-1 cart-signal blend.
 */
export type MatchScoreBreakdown = {
    /**
     * Rubric
     */
    rubric: number;
    /**
     * Rubric Imputed
     */
    rubric_imputed: boolean;
    /**
     * Rubric Points
     */
    rubric_points: number;
    /**
     * Evidence
     */
    evidence: number;
    /**
     * Evidence Points
     */
    evidence_points: number;
    /**
     * Tier Offset
     */
    tier_offset: number;
    /**
     * Match
     */
    match: number;
};

/**
 * OffLimitsCompanyResponseSchema
 */
export type OffLimitsCompanyResponseSchema = {
    /**
     * Company ID
     *
     * The ID of the company that is off-limits to this candidate
     */
    company_id: number;
    /**
     * Company name
     *
     * Company name
     */
    company_name: string;
    /**
     * Reason
     *
     * The reason that the company is off-limits to this candidate
     */
    reason: OffLimitsReason;
};

/**
 * OffLimitsIdentityResponseSchema
 *
 * One human this candidate must not be shown to. Name, email and company
 * come from the identity's candidate record when it has one, else from one
 * of its logins; all null for an identity holding neither.
 */
export type OffLimitsIdentityResponseSchema = {
    /**
     * Identity UUID
     *
     * The human this candidate's human must not be shown to
     */
    identity_uuid: string;
    /**
     * Full name
     */
    full_name?: string | null;
    /**
     * Email
     */
    email?: string | null;
    /**
     * Company name
     */
    company_name?: string | null;
    /**
     * Profile image
     */
    image?: string | null;
    /**
     * Reason
     *
     * The reason that the human is off-limits to this candidate
     */
    reason: OffLimitsReason;
};

/**
 * OffLimitsReason
 */
export const OffLimitsReason = {
    EXISTING_RELATIONSHIP: 'EXISTING_RELATIONSHIP',
    INVESTOR_CONFLICT: 'INVESTOR_CONFLICT',
    OTHER: 'OTHER',
    SEARCH_FIRM_OFF_LIMITS: 'SEARCH_FIRM_OFF_LIMITS',
    TEAM_MEMBER_EXISTING_CANDIDATE: 'TEAM_MEMBER_EXISTING_CANDIDATE'
} as const;

/**
 * OffLimitsReason
 */
export type OffLimitsReason = typeof OffLimitsReason[keyof typeof OffLimitsReason];

/**
 * OffLimitsTeamSchema
 */
export type OffLimitsTeamSchema = {
    /**
     * Candidate UUID
     *
     * The UUID of the candidate of which the team is off-limits to
     */
    candidate_uuid: string;
    /**
     * Company Slug
     *
     * The slug of the company which originated the team's off-limitedness
     */
    company_slug?: string | null;
    /**
     * Identity UUID
     *
     * The identity (human) which originated the team's off-limitedness. For a derived own-team entry this is the candidate's own identity.
     */
    identity_uuid?: string | null;
    /**
     * Off limits reason
     *
     * The reason that this team is off limits to the candidate
     */
    reason: OffLimitsReason;
    /**
     * Team ID
     *
     * The ID of the team which is off-limits to the candidate
     */
    team_id: number;
    /**
     * Team UUID
     *
     * The UUID of the team which is off-limits to the candidate
     */
    team_uuid: string;
    /**
     * Team slug
     *
     * The UUID of the team which is off-limits to the candidate
     */
    team_slug: string;
};

/**
 * OperatingHighlights
 */
export const OperatingHighlights = {
    EARLY_STAGE: 'early_stage',
    VENTURE_CAPITAL_BACKED: 'venture_capital_backed',
    PRIVATE_EQUITY_BACKED: 'private_equity_backed',
    LED_IPO: 'led_ipo',
    PART_OF_IPO: 'part_of_ipo',
    EXIT: 'exit'
} as const;

/**
 * OperatingHighlights
 */
export type OperatingHighlights = typeof OperatingHighlights[keyof typeof OperatingHighlights];

/**
 * OrgType
 */
export const OrgType = {
    ADVISORY: 'advisory',
    CORPORATE: 'corporate',
    EXECUTIVE: 'executive',
    INVESTOR: 'investor',
    SEARCH: 'search',
    OTHER: 'other'
} as const;

/**
 * OrgType
 */
export type OrgType = typeof OrgType[keyof typeof OrgType];

/**
 * Page[AllIntrosResponse]
 */
export type PageAllIntrosResponse = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Results
     */
    results: Array<AllIntrosResponse>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCounts;
};

/**
 * Page[CandidateActivityEvent]
 */
export type PageCandidateActivityEvent = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Results
     */
    results: Array<CandidateActivityEvent>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCounts;
};

/**
 * Page[CandidateActivityLogResponse]
 */
export type PageCandidateActivityLogResponse = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Results
     */
    results: Array<CandidateActivityLogResponse>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCounts;
};

/**
 * Page[CandidateCompanyMetadataWithCandidateResponse]
 */
export type PageCandidateCompanyMetadataWithCandidateResponse = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Results
     */
    results: Array<CandidateCompanyMetadataWithCandidateResponse>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCounts;
};

/**
 * Page[CandidateExtendedResponse]
 */
export type PageCandidateExtendedResponse = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Results
     */
    results: Array<CandidateExtendedResponse>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCounts;
};

/**
 * Page[CandidateRecommendationResponse]
 */
export type PageCandidateRecommendationResponse = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Results
     */
    results: Array<CandidateRecommendationResponse>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCounts;
};

/**
 * Page[CandidateSearchResponseWithLinkedPerson]
 */
export type PageCandidateSearchResponseWithLinkedPerson = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Results
     */
    results: Array<CandidateSearchResponseWithLinkedPerson>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCounts;
};

/**
 * Page[CandidatesTableResponse]
 */
export type PageCandidatesTableResponse = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Results
     */
    results: Array<CandidatesTableResponse>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCounts;
};

/**
 * Page[CommentResponse]
 */
export type PageCommentResponse = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Results
     */
    results: Array<CommentResponse>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCounts;
};

/**
 * Page[CompanyTableResponse]
 */
export type PageCompanyTableResponse = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Results
     */
    results: Array<CompanyTableResponse>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCounts;
};

/**
 * Page[ContactSearchResult]
 */
export type PageContactSearchResult = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Results
     */
    results: Array<ContactSearchResult>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCounts;
};

/**
 * Page[ESRecommendationCandidateIndexResult]
 */
export type PageEsRecommendationCandidateIndexResult = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Results
     */
    results: Array<EsRecommendationCandidateIndexResult>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCounts;
};

/**
 * Page[EmailTemplateResponse]
 */
export type PageEmailTemplateResponse = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Results
     */
    results: Array<EmailTemplateResponse>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCounts;
};

/**
 * Page[EventEmailBatchResponse]
 */
export type PageEventEmailBatchResponse = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Results
     */
    results: Array<EventEmailBatchResponse>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCounts;
};

/**
 * Page[EventEmailEventItemResponse]
 */
export type PageEventEmailEventItemResponse = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Results
     */
    results: Array<EventEmailEventItemResponse>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCounts;
};

/**
 * Page[EventGuestWithEventItemResponse]
 */
export type PageEventGuestWithEventItemResponse = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Results
     */
    results: Array<EventGuestWithEventItemResponse>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCounts;
};

/**
 * Page[EventResponse]
 */
export type PageEventResponse = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Results
     */
    results: Array<EventResponse>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCounts;
};

/**
 * Page[ExecutiveClientEventListItem]
 */
export type PageExecutiveClientEventListItem = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Results
     */
    results: Array<ExecutiveClientEventListItem>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCounts;
};

/**
 * Page[GeneralSearchResponse]
 */
export type PageGeneralSearchResponse = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Results
     */
    results: Array<GeneralSearchResponse>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCounts;
};

/**
 * Page[HomepageActivityLogResponse]
 */
export type PageHomepageActivityLogResponse = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Results
     */
    results: Array<HomepageActivityLogResponse>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCounts;
};

/**
 * Page[IdentityEmailItemResponse]
 */
export type PageIdentityEmailItemResponse = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Results
     */
    results: Array<IdentityEmailItemResponse>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCounts;
};

/**
 * Page[InternalCommentResponse]
 */
export type PageInternalCommentResponse = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Results
     */
    results: Array<InternalCommentResponse>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCounts;
};

/**
 * Page[PeerActivityResponse]
 */
export type PagePeerActivityResponse = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Results
     */
    results: Array<PeerActivityResponse>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCounts;
};

/**
 * Page[PersonBasicEmailResponse]
 */
export type PagePersonBasicEmailResponse = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Results
     */
    results: Array<PersonBasicEmailResponse>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCounts;
};

/**
 * Page[PersonSearchResponse]
 */
export type PagePersonSearchResponse = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Results
     */
    results: Array<PersonSearchResponse>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCounts;
};

/**
 * Page[PipelineSearchResponse]
 */
export type PagePipelineSearchResponse = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Results
     */
    results: Array<PipelineSearchResponse>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCounts;
};

/**
 * Page[TeamPipelinesTableResponse]
 */
export type PageTeamPipelinesTableResponse = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Results
     */
    results: Array<TeamPipelinesTableResponse>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCounts;
};

/**
 * Page[TeamSearchResponse]
 */
export type PageTeamSearchResponse = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Results
     */
    results: Array<TeamSearchResponse>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCounts;
};

/**
 * PaginatedPipelinesTableResponse
 */
export type PaginatedPipelinesTableResponse = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Results
     */
    results: Array<PipelinesTableV2Response>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCountsResponse;
    aggregations: PipelinesAggregationsResponse;
};

/**
 * PathwayActivityDefaultRow
 */
export type PathwayActivityDefaultRow = {
    /**
     * Id
     *
     * Row id
     */
    id: number;
    /**
     * Candidate pathway
     */
    pathway: CandidatePathway;
    /**
     * Activity metric
     */
    activity_type: PathwayActivityType;
    /**
     * Target Number
     *
     * Target count for the time period
     */
    target_number: number;
    /**
     * Period for target_number
     */
    time_period: ActivityTimePeriod;
};

/**
 * PathwayActivityDefaultUpsertItem
 */
export type PathwayActivityDefaultUpsertItem = {
    pathway: CandidatePathway;
    activity_type: PathwayActivityType;
    /**
     * Target Number
     */
    target_number: number;
    time_period: ActivityTimePeriod;
};

/**
 * PathwayActivityDefaultsListResponse
 */
export type PathwayActivityDefaultsListResponse = {
    /**
     * Defaults
     *
     * All pathway × activity default rows
     */
    defaults: Array<PathwayActivityDefaultRow>;
};

/**
 * PathwayActivityDefaultsPutPayload
 */
export type PathwayActivityDefaultsPutPayload = {
    /**
     * Defaults
     *
     * Replace or upsert defaults by (pathway, activity_type)
     */
    defaults: Array<PathwayActivityDefaultUpsertItem>;
};

/**
 * PathwayActivityType
 *
 * Executive activity metrics keyed per pathway default targets.
 */
export const PathwayActivityType = {
    NARRATIVE_EDITS: 'narrative_edits',
    EVENT_INVITES: 'event_invites',
    CHECK_IN_CALLS: 'check_in_calls',
    COMP_REVIEW_OFFER_SUPPORT: 'comp_review_offer_support',
    P2P_INTROS: 'p2p_intros',
    B4B_INTROS: 'b4b_intros',
    BRAND_STRATEGIC_CALL: 'brand_strategic_call'
} as const;

/**
 * PathwayActivityType
 *
 * Executive activity metrics keyed per pathway default targets.
 */
export type PathwayActivityType = typeof PathwayActivityType[keyof typeof PathwayActivityType];

/**
 * PaymentStatus
 */
export const PaymentStatus = { FREE: 'free', PREMIUM: 'premium' } as const;

/**
 * PaymentStatus
 */
export type PaymentStatus = typeof PaymentStatus[keyof typeof PaymentStatus];

/**
 * PeerActivityResponse
 */
export type PeerActivityResponse = {
    /**
     * Activity context
     *
     * Activity context.
     */
    context?: string | null;
    /**
     * Created On
     *
     * Creation date of the activity.
     */
    created: string;
    /**
     * Creator
     *
     * Creator of the activity
     */
    creator: PersonBasicResponse;
    /**
     * Activity event
     *
     * Activity event.
     */
    event: PeerEvent;
    /**
     * Record ID
     *
     * Unique identifier for this activity
     */
    id: string;
    /**
     * Last Modified On
     *
     * Last modification date of the activity
     */
    last_modified: string;
};

/**
 * PeerAssigneesUpdatePayload
 */
export type PeerAssigneesUpdatePayload = {
    /**
     * Assignee UUIDs
     *
     * List of UUIDs of persons to assign to the P2P match.
     */
    assignee_uuids: Array<string>;
};

/**
 * PeerCommentPayload
 */
export type PeerCommentPayload = {
    /**
     * Comment Context
     *
     * The content of the comment.
     */
    context: string;
};

/**
 * PeerCommentResponse
 */
export type PeerCommentResponse = {
    /**
     * Peer Comment Context
     *
     * Context for this comment
     */
    context: string;
    /**
     * Created On
     *
     * Creation date of the p2p match
     */
    created: string;
    /**
     * Creator
     *
     * Creator of the p2p match
     */
    creator: PersonBasicResponse;
    /**
     * Peer Comment ID
     *
     * Unique identifier for this peer comment
     */
    id: string;
    /**
     * Last Modified On
     *
     * Last modification date of the p2p match
     */
    last_modified: string;
    /**
     * Comment's Peer
     *
     * Comment's peer
     */
    peer: PeerToPeerBasicResponse;
};

/**
 * PeerCompareAboutResponse
 */
export type PeerCompareAboutResponse = {
    /**
     * Executive
     *
     * Executive information
     */
    candidate: CandidateBasicResponse;
    /**
     * Candidate about
     *
     * Candidate about
     */
    introduction_templates: Array<CandidateIntroductionTemplateResponse>;
};

/**
 * PeerCompareGoalResponse
 */
export type PeerCompareGoalResponse = {
    /**
     * Goal ID
     *
     * Unique identifier for the goal
     */
    id: string;
    /**
     * Title
     *
     * Goal title
     */
    title: string;
    /**
     * Program
     *
     * Program the goal belongs to
     */
    program: CandidateType;
    /**
     * Status
     *
     * Current status of the goal
     */
    status: GoalStatus;
    /**
     * Priority
     *
     * Goal priority
     */
    priority?: GoalPriority | null;
    /**
     * Created On
     *
     * Creation date of the goal
     */
    created: string;
    /**
     * Due Date
     *
     * Date the goal is due
     */
    due_date?: string | null;
    /**
     * Completion Date
     *
     * Date the goal was completed
     */
    completion_date?: string | null;
};

/**
 * PeerCompareGoalsResponse
 */
export type PeerCompareGoalsResponse = {
    /**
     * Executive
     *
     * Executive information
     */
    candidate: CandidateBasicResponse;
    /**
     * Goals
     *
     * Goals of the candidate being compared
     */
    goals: Array<PeerCompareGoalResponse>;
};

/**
 * PeerCompareP2PResponse
 */
export type PeerCompareP2pResponse = {
    /**
     * Executive
     *
     * Executive information
     */
    candidate: CandidateBasicResponse;
    /**
     * Client candidate
     *
     * Client candidate being compared
     */
    matches: Array<PeerComparePeerToPeerResponse>;
};

/**
 * PeerComparePayload
 */
export type PeerComparePayload = {
    /**
     * Contender Candidate UUIDs
     *
     * List of contender candidate UUIDs
     */
    candidate_uuids: Array<string>;
};

/**
 * PeerComparePeerToPeerResponse
 */
export type PeerComparePeerToPeerResponse = {
    /**
     * Record ID
     *
     * Unique identifier for this peer model
     */
    id: string;
    /**
     * Created On
     *
     * Creation date of the p2p match
     */
    created: string;
    /**
     * Last Modified On
     *
     * Last modification date of the p2p match
     */
    last_modified: string;
    /**
     * Introduced On
     *
     * Date the peer was introduced
     */
    introduced_on?: string | null;
    /**
     * Candidate
     *
     * The candidate object
     */
    candidate: CandidateCompanyRoleWithAdvisorResponse;
    /**
     * Status
     *
     * Current status of a p2p match
     */
    status: PeerStatus;
};

/**
 * PeerEvent
 */
export const PeerEvent = {
    APPROVED: 'approved',
    COMMENT: 'comment',
    DECLINED_ADVISOR: 'declined_advisor',
    DECLINED_CANDIDATE: 'declined_candidate',
    INFO_NEEDED: 'info_needed',
    INTRO_MADE: 'intro_made',
    PUBLISHED: 'published',
    SCHEDULED: 'scheduled'
} as const;

/**
 * PeerEvent
 */
export type PeerEvent = typeof PeerEvent[keyof typeof PeerEvent];

/**
 * PeerIntroductionPayload
 */
export type PeerIntroductionPayload = {
    /**
     * Email body
     *
     * Customized introduction email body text
     */
    email_body?: string | null;
    /**
     * Email subject
     *
     * Customized introduction email subject line
     */
    email_subject?: string | null;
    /**
     * Email CC
     *
     * List of people to CC the email to
     */
    email_people_cc?: Array<string> | null;
    /**
     * From ID
     *
     * Person who will be shown as the email sender
     */
    from_id?: string | null;
    /**
     * Introduced on
     *
     * Date when the candidates were or will be introduced
     */
    introduced_on?: string | null;
};

/**
 * PeerStatus
 */
export const PeerStatus = {
    APPROVED: 'approved',
    DECLINED_ADVISOR: 'declined_advisor',
    DECLINED_CANDIDATE: 'declined_candidate',
    INFO_NEEDED: 'info_needed',
    INTRO_MADE: 'intro_made',
    PUBLISHED: 'published',
    SCHEDULED: 'scheduled'
} as const;

/**
 * PeerStatus
 */
export type PeerStatus = typeof PeerStatus[keyof typeof PeerStatus];

/**
 * PeerStatusUpdatePayload
 */
export type PeerStatusUpdatePayload = {
    /**
     * Context
     *
     * Context information related to the status change.
     */
    context?: string | null;
    /**
     * Peer Status
     *
     * The new status to set for the peer.
     */
    status: PeerStatus;
};

/**
 * PeerToPeerBasicESResponse
 */
export type PeerToPeerBasicEsResponse = {
    /**
     * Assignees
     *
     * Assignees responsible for the p2p match
     */
    assignees: Array<PersonBasicEmailResponse>;
    /**
     * Candidate
     *
     * The candidate object
     */
    candidate: CandidateCompanyRoleWithAdvisorResponse;
    /**
     * Target candidate
     *
     * The target candidate object (peer)
     */
    candidate_to: CandidateCompanyRoleWithAdvisorResponse;
    /**
     * Context
     *
     * Context containing reason for this peer. This is a required field for peers created in Contender, but for historical reasons, this field might be undefined because we did not require context in the original Retool app.
     */
    context?: string | null;
    /**
     * Created On
     *
     * Creation date of the p2p match
     */
    created: string;
    /**
     * Creator
     *
     * Creator of the p2p match
     */
    creator: PersonBasicEmailResponse;
    /**
     * Record ID
     *
     * Unique identifier for this peer model
     */
    id: string;
    /**
     * Introduced On
     *
     * Date the peer was introduced
     */
    introduced_on?: string | null;
    /**
     * Last Modified On
     *
     * Last modification date of the p2p match
     */
    last_modified: string;
    /**
     * Status
     *
     * Current status of a p2p match
     */
    status: PeerStatus;
    /**
     * Reviewers
     *
     * Reviewers responsible for the p2p match
     */
    reviewers: Array<PersonBasicEmailResponse>;
    /**
     * Reviewers status
     *
     * Reviewers status by person ID
     */
    reviewers_statuses: Array<PeerToPeerReviewersStatus>;
};

/**
 * PeerToPeerBasicResponse
 */
export type PeerToPeerBasicResponse = {
    /**
     * Assignees
     *
     * Assignees responsible for the p2p match
     */
    assignees: Array<PersonBasicEmailResponse>;
    /**
     * Candidate
     *
     * The candidate object
     */
    candidate: CandidateCompanyRoleWithAdvisorResponse;
    /**
     * Target candidate
     *
     * The target candidate object (peer)
     */
    candidate_to: CandidateCompanyRoleWithAdvisorResponse;
    /**
     * Context
     *
     * Context containing reason for this peer. This is a required field for peers created in Contender, but for historical reasons, this field might be undefined because we did not require context in the original Retool app.
     */
    context?: string | null;
    /**
     * Created On
     *
     * Creation date of the p2p match
     */
    created: string;
    /**
     * Creator
     *
     * Creator of the p2p match
     */
    creator: PersonBasicEmailResponse;
    /**
     * Record ID
     *
     * Unique identifier for this peer model
     */
    id: string;
    /**
     * Introduced On
     *
     * Date the peer was introduced
     */
    introduced_on?: string | null;
    /**
     * Last Modified On
     *
     * Last modification date of the p2p match
     */
    last_modified: string;
    /**
     * Status
     *
     * Current status of a p2p match
     */
    status: PeerStatus;
};

/**
 * PeerToPeerExtendedResponse
 */
export type PeerToPeerExtendedResponse = {
    /**
     * Assignees
     *
     * Assignees responsible for the p2p match
     */
    assignees: Array<PersonBasicEmailResponse>;
    /**
     * Candidate
     *
     * The candidate object
     */
    candidate: CandidateCompanyRoleWithAdvisorResponse;
    /**
     * Target candidate
     *
     * The target candidate object (peer)
     */
    candidate_to: CandidateCompanyRoleWithAdvisorResponse;
    /**
     * Context
     *
     * Context containing reason for this peer. This is a required field for peers created in Contender, but for historical reasons, this field might be undefined because we did not require context in the original Retool app.
     */
    context?: string | null;
    /**
     * Created On
     *
     * Creation date of the p2p match
     */
    created: string;
    /**
     * Creator
     *
     * Creator of the p2p match
     */
    creator: PersonBasicEmailResponse;
    /**
     * Record ID
     *
     * Unique identifier for this peer model
     */
    id: string;
    /**
     * Introduced On
     *
     * Date the peer was introduced
     */
    introduced_on?: string | null;
    /**
     * Last Modified On
     *
     * Last modification date of the p2p match
     */
    last_modified: string;
    /**
     * Status
     *
     * Current status of a p2p match
     */
    status: PeerStatus;
    /**
     * Email body
     *
     * Email body
     */
    email_body?: string | null;
    /**
     * Email sender
     *
     * Email sender
     */
    email_sender?: PersonBasicEmailResponse | null;
    /**
     * Email subject
     *
     * Email subject
     */
    email_subject?: string | null;
    /**
     * People to CC
     *
     * List of people to CC the email to
     */
    email_cc?: Array<PersonBasicEmailResponse> | null;
    /**
     * Reviewers
     *
     * Reviewers responsible for the p2p match
     */
    reviewers: Array<PersonBasicEmailResponse>;
    /**
     * Reviewers status
     *
     * Reviewers status by person ID
     */
    reviewers_status: {
        [key: string]: PeerStatus;
    };
};

/**
 * PeerToPeerLastEventResponse
 *
 * Most recent non-webinar event both this peer and the client candidate RSVP'd going.
 */
export type PeerToPeerLastEventResponse = {
    /**
     * ID
     *
     * Event primary key
     */
    id: number;
    /**
     * UUID
     *
     * Event UUID for deep links
     */
    uuid: string;
    /**
     * Title
     *
     * Event title
     */
    title: string;
    /**
     * Event date
     *
     * Scheduled start time when set
     */
    event_date?: string | null;
};

/**
 * PeerToPeerPublishCandidates
 */
export type PeerToPeerPublishCandidates = {
    /**
     * Peer candidate UUID
     *
     * The unique identifier for the peer candidate.
     */
    candidate_uuid: string;
    /**
     * Context
     *
     * The context for the match.
     */
    context: string;
    /**
     * Goal ID
     *
     * Goal associated with this p2p match
     */
    goal_id?: string | null;
    /**
     * Peer Status
     *
     * The status of the peer-to-peer candidate interaction.
     */
    status: 'approved' | 'published' | 'intro_made';
};

/**
 * PeerToPeerReviewersStatus
 */
export type PeerToPeerReviewersStatus = {
    /**
     * Status
     *
     * Review status
     */
    status: PeerStatus;
    /**
     * UUID
     *
     * Person UUID
     */
    uuid: string;
};

/**
 * PeerToPeerSelectResponse
 */
export type PeerToPeerSelectResponse = {
    /**
     * Client
     *
     * Indicates whether a person is a Banff client
     */
    is_client: boolean;
    /**
     * Company
     *
     * Candidate's current company
     */
    company?: string | null;
    /**
     * Role
     *
     * Candidate's current role
     */
    role?: string | null;
    /**
     * Image
     *
     * Path to candidate image
     */
    image?: string | null;
    /**
     * Name
     *
     * Candidate's full name
     */
    name: string;
    /**
     * UUID
     *
     * UUID uniquely identifying a person
     */
    uuid: string;
    /**
     * First name
     *
     * Candidate's first name
     */
    first_name?: string | null;
    /**
     * Functional expertise
     *
     * List of functions candidate has experience in
     */
    function_expertise: Array<Function>;
    /**
     * Target functions
     *
     * Functional expertise candidate would like to get
     */
    function_target: Array<Function>;
    /**
     * Industry expertise
     *
     * List of industries candidate has experience in
     */
    industry_expertise: Array<Industry>;
    /**
     * Target industries
     *
     * Functional expertise candidate would like to get
     */
    industry_target: Array<Industry>;
    /**
     * Board/advisory role interest
     *
     * Interest in getting board or advisory roles
     */
    interest_role_board_advisory?: InterestRole | null;
    /**
     * Operating role interest
     *
     * Interest in getting operating roles
     */
    interest_role_operating?: InterestRole | null;
    /**
     * Personal interests
     *
     * List of personal interests
     */
    interests_personal: Array<InterestPersonal>;
    /**
     * Professional interests
     *
     * List of professional interests
     */
    interests_professional: Array<InterestProfessional>;
    /**
     * Keywords
     *
     * List of keywords
     */
    keywords: Array<string>;
    /**
     * Last name
     *
     * Candidate's last name
     */
    last_name?: string | null;
    /**
     * LinkedIn URL
     *
     * Link to the person's LinkedIn profile
     */
    linkedin_url?: string | null;
    /**
     * Tier
     *
     * Candidate tier
     */
    tier: CandidateTier;
    /**
     * Type
     *
     * Candidate type
     */
    type?: ClientType | null;
    /**
     * Pathway
     *
     * Banff service pathway for the candidate
     */
    pathway?: CandidatePathway | null;
    /**
     * Advisor
     *
     * Information about the advisor assigned to this candidate
     */
    advisor?: PersonBasicResponse | null;
    /**
     * P2P introductions count
     *
     * Overall count of P2P introductions a candidate ever had either as a client or as a peer.
     */
    intros_count?: number;
    /**
     * Last P2P match introduced on
     *
     * Last time a candidate had an introduction in any P2P match as a client or as a peer.
     */
    last_peer_introduced_on?: string | null;
    /**
     * Previous P2P match status
     *
     * Last status of a previous P2P match with client executive candidate
     */
    last_peer_status?: PeerStatus | null;
    /**
     * Last event date
     *
     * Start time of the most recent non-webinar event where this peer candidate and the client executive both RSVP'd going.
     */
    last_event_date?: string | null;
    /**
     * Last event
     *
     * The event for last_event_date (non-webinar; both candidates going).
     */
    last_event?: PeerToPeerLastEventResponse | null;
    /**
     * Client has peer as referrer
     *
     * True if the client executive's candidate profile lists the peer executive (their linked Person) as a referrer.
     */
    client_has_peer_as_referrer?: boolean;
    /**
     * Peer has client as referrer
     *
     * True if the peer executive's candidate profile lists the client executive (their linked Person) as a referrer.
     */
    peer_has_client_as_referrer?: boolean;
    /**
     * Location
     *
     * Candidate's location (from their resume)
     */
    location?: string | null;
    /**
     * Invoice Status
     *
     * HubSpot invoice status (admin-facing; tier 1/1b only)
     */
    invoice_status?: HubSpotInvoiceStatus | null;
    /**
     * Profile Image Url
     */
    readonly profile_image_url: string | null;
};

/**
 * PeerToPeerTableExecutiveAggregationsResponse
 */
export type PeerToPeerTableExecutiveAggregationsResponse = {
    /**
     * Introduced
     */
    introduced: number;
    /**
     * Pending
     */
    pending: number;
    /**
     * Total
     */
    total: number;
};

/**
 * PeerToPeerTableExecutivePayload
 */
export type PeerToPeerTableExecutivePayload = {
    /**
     * Phrase
     *
     * Search phrase
     */
    phrase?: string | null;
    /**
     * Search ID
     *
     * Search session ID
     */
    search_id?: string | null;
    /**
     * Page
     *
     * Page number
     */
    page?: number;
    /**
     * Page size
     *
     * Page size
     */
    page_size?: number;
    /**
     * Sort
     *
     * Sort results by these rules
     */
    sort_rules: Array<PeerToPeerTableSort>;
};

/**
 * PeerToPeerTableExecutiveResponse
 */
export type PeerToPeerTableExecutiveResponse = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Results
     */
    results: Array<PeerToPeerBasicResponse>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCountsResponse;
    aggregations: PeerToPeerTableExecutiveAggregationsResponse;
};

/**
 * PeerToPeerTableFilterAggregationsResponse
 */
export type PeerToPeerTableFilterAggregationsResponse = {
    /**
     * Advisor Uuid
     */
    advisor_uuid: {
        [key: string]: number;
    };
    /**
     * Assignee Uuid
     */
    assignee_uuid: {
        [key: string]: number;
    };
    /**
     * Recommender Uuid
     */
    recommender_uuid: {
        [key: string]: number;
    };
    /**
     * Status
     */
    status: {
        [key: string]: number;
    };
};

/**
 * PeerToPeerTableFilterData
 */
export type PeerToPeerTableFilterData = {
    /**
     * Advisor UUID
     *
     * Peer to Peer match advisor UUID filter
     */
    advisor_uuid?: Array<string>;
    /**
     * Assignee UUID
     *
     * Peer to Peer match assignee UUID filter
     */
    assignee_uuid?: Array<string>;
    /**
     * Candidate UUID
     *
     * Candidate UUID filter (supports only one item)
     */
    candidate_uuid?: Array<string>;
    /**
     * Recommender UUID
     *
     * Peer to Peer match creator UUID filter
     */
    recommender_uuid?: Array<string>;
    /**
     * Client exec status type
     *
     * Client exec status type to filter on
     */
    status?: Array<PeerStatus>;
};

/**
 * PeerToPeerTableFilterPayload
 */
export type PeerToPeerTableFilterPayload = {
    /**
     * Phrase
     *
     * Search phrase
     */
    phrase?: string | null;
    /**
     * Search ID
     *
     * Search session ID
     */
    search_id?: string | null;
    /**
     * Page
     *
     * Page number
     */
    page?: number;
    /**
     * Page size
     *
     * Page size
     */
    page_size?: number;
    /**
     * Filters
     *
     * Filter results by these criteria
     */
    filter_data?: PeerToPeerTableFilterData | null;
    /**
     * Sort
     *
     * Sort results by these rules
     */
    sort_rules: Array<PeerToPeerTableSort>;
};

/**
 * PeerToPeerTableResponse
 */
export type PeerToPeerTableResponse = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Results
     */
    results: Array<PeerToPeerBasicEsResponse>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCountsResponse;
    aggregations: PeerToPeerTableFilterAggregationsResponse;
};

/**
 * PeerToPeerTableSelectAggregationsResponse
 */
export type PeerToPeerTableSelectAggregationsResponse = {
    /**
     * Industry Expertise
     */
    industry_expertise: {
        [key: string]: number;
    };
    /**
     * Function Expertise
     */
    function_expertise: {
        [key: string]: number;
    };
    /**
     * Industry Target
     */
    industry_target: {
        [key: string]: number;
    };
    /**
     * Function Target
     */
    function_target: {
        [key: string]: number;
    };
    /**
     * Tier
     */
    tier: {
        [key: string]: number;
    };
    /**
     * Interests Personal
     */
    interests_personal: {
        [key: string]: number;
    };
    /**
     * Interests Professional
     */
    interests_professional: {
        [key: string]: number;
    };
    /**
     * Type
     */
    type: {
        [key: string]: number;
    };
    /**
     * Pathway
     */
    pathway: {
        [key: string]: number;
    };
};

/**
 * PeerToPeerTableSelectFilterData
 */
export type PeerToPeerTableSelectFilterData = {
    /**
     * Function expertise
     *
     * Filter by function expertise
     */
    function_expertise?: Array<Function> | null;
    /**
     * Industry expertise
     *
     * Filter by industry expertise
     */
    industry_expertise?: Array<Industry> | null;
    /**
     * Personal interests
     *
     * List of personal interests
     */
    interests_personal?: Array<InterestPersonal> | null;
    /**
     * Professional interests
     *
     * List of professional interests
     */
    interests_professional?: Array<InterestProfessional> | null;
    /**
     * Target functions
     *
     * Functional expertise candidate would like to get
     */
    function_target?: Array<Function> | null;
    /**
     * Target industries
     *
     * Functional expertise candidate would like to get
     */
    industry_target?: Array<Industry> | null;
    /**
     * Tier
     *
     * Candidate tier
     */
    tier?: Array<CandidateTier> | null;
    /**
     * Type
     *
     * Candidate type
     */
    type?: Array<ClientType> | null;
    /**
     * Pathway
     *
     * Candidate pathway
     */
    pathway?: Array<CandidatePathway> | null;
};

/**
 * PeerToPeerTableSelectFilterPayload
 */
export type PeerToPeerTableSelectFilterPayload = {
    /**
     * Phrase
     *
     * Search phrase
     */
    phrase?: string | null;
    /**
     * Search ID
     *
     * Search session ID
     */
    search_id?: string | null;
    /**
     * Page
     *
     * Page number
     */
    page?: number;
    /**
     * Page size
     *
     * Page size
     */
    page_size?: number;
    /**
     * Filters
     *
     * Filter results by these criteria
     */
    filter_data?: PeerToPeerTableSelectFilterData | null;
    /**
     * Include inactive
     *
     * Also return inactive candidates. The peer picker leaves this off. Merged candidates are never returned.
     */
    include_inactive?: boolean;
    /**
     * Sort
     *
     * Sort results by these rules
     */
    sort_rules: Array<PeerToPeerTableSelectSort>;
};

/**
 * PeerToPeerTableSelectResponse
 */
export type PeerToPeerTableSelectResponse = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Results
     */
    results: Array<PeerToPeerSelectResponse>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCountsResponse;
    aggregations: PeerToPeerTableSelectAggregationsResponse;
};

/**
 * PeerToPeerTableSelectSort
 */
export type PeerToPeerTableSelectSort = {
    /**
     * Column
     *
     * Sort results on this column
     */
    column: PeerToPeerTableSelectSortColumn;
    /**
     * Direction
     *
     * Sort results in this direction
     */
    direction?: SortDirection;
};

/**
 * PeerToPeerTableSelectSortColumn
 */
export const PeerToPeerTableSelectSortColumn = {
    NAME: 'name',
    LAST_PEER_STATUS: 'last_peer_status',
    LAST_PEER_INTRODUCED_ON: 'last_peer_introduced_on',
    LAST_EVENT_DATE: 'last_event_date',
    INDUSTRY_EXPERTISE: 'industry_expertise',
    FUNCTION_EXPERTISE: 'function_expertise',
    INTERESTS_PROFESSIONAL: 'interests_professional',
    INTROS_COUNT: 'intros_count',
    ADVISOR_NAME: 'advisor_name',
    TYPE: 'type'
} as const;

/**
 * PeerToPeerTableSelectSortColumn
 */
export type PeerToPeerTableSelectSortColumn = typeof PeerToPeerTableSelectSortColumn[keyof typeof PeerToPeerTableSelectSortColumn];

/**
 * PeerToPeerTableSort
 */
export type PeerToPeerTableSort = {
    /**
     * Column
     *
     * Sort results on this column
     */
    column: PeerToPeerTableSortColumn;
    /**
     * Direction
     *
     * Sort results in this direction
     */
    direction?: SortDirection;
};

/**
 * PeerToPeerTableSortColumn
 */
export const PeerToPeerTableSortColumn = {
    ASSIGNEE: 'assignee',
    CANDIDATE_NAME: 'candidate_name',
    CANDIDATE_TO_NAME: 'candidate_to_name',
    CREATOR: 'creator',
    SCHEDULED: 'scheduled',
    STATUS: 'status',
    UPDATED: 'updated'
} as const;

/**
 * PeerToPeerTableSortColumn
 */
export type PeerToPeerTableSortColumn = typeof PeerToPeerTableSortColumn[keyof typeof PeerToPeerTableSortColumn];

/**
 * PeoplePost
 */
export type PeoplePost = {
    /**
     * First Name
     *
     * Person's first name
     */
    first_name?: string | null;
    /**
     * Last Name
     *
     * Person's last name
     */
    last_name?: string | null;
    /**
     * Email
     *
     * Email associated with the person
     */
    email: string;
    /**
     * Person name
     *
     * Name of the invited person
     *
     * @deprecated
     */
    person_name?: string | null;
    /**
     * Permit Preregistration Notifications
     *
     * Whether to allow sending preregistration emails to this user. Defaults to False for pending users, set to True to enable marketing emails before signup.
     */
    permit_preregistration_notifications?: boolean;
    /**
     * Send Invite Email
     *
     * Whether to send invitation email to this user.Set to False to create invite without emailing
     */
    send_invite_email?: boolean;
    /**
     * Additional details
     *
     * Additional details about the team
     */
    additional_details?: string | null;
    /**
     * Client size
     *
     * Typical client size
     */
    company_type?: SearchFirmType | null;
    /**
     * Fund type
     *
     * What kind of capital this firm deploys. Multi-select: a firm that genuinely does both carries both values. Empty means no answer — it does not distinguish 'not an investment firm' from 'not yet reviewed'.
     */
    fund_type?: Array<FundType> | null;
    /**
     * Functional focus
     *
     * The roles and responsibilities the team typically searches for
     */
    functional_focus?: Array<Function> | null;
    /**
     * Industry focus
     *
     * Sectors the team typically focuses on
     */
    industry_focus?: Array<Industry> | null;
    /**
     * Name
     *
     * Team name
     */
    team_name?: string | null;
    /**
     * Team type
     *
     * Whether this team is the firm itself or a company the firm invested in.
     */
    team_type?: TeamType | null;
    /**
     * Team slug
     *
     * When inviting a person to an existing team, we will pass the team slug
     */
    team_slug?: string | null;
    /**
     * Name
     *
     * Name of the company
     */
    organization?: string | null;
    /**
     * Company status
     *
     * Status may affect permissions access
     */
    payment_status?: PaymentStatus | null;
    /**
     * Company type
     *
     * Type controls permissions access in addition to being a meta data
     */
    customer_type?: OrgType | null;
    /**
     * Company slug
     *
     * When inviting a person to an existing company, we will pass the company slug
     */
    company_slug?: string | null;
    /**
     * User role to assign to the invited person
     */
    role: Role;
    /**
     * User type
     *
     * What kind of login this is: `enterprise` for a team role, `admin` for the Banff admin role. Stated by the caller and checked against `role`, never derived from it. Executive logins are made by activating an executive, not here.
     */
    user_type: UserType;
};

/**
 * PeriodDates
 */
export type PeriodDates = {
    /**
     * Filter stats from this date
     */
    date_from?: string | null;
    /**
     * Filter stats to this date
     */
    date_to: string;
};

/**
 * PeriodStats
 */
export type PeriodStats = {
    /**
     * Intro Completed
     *
     * Number of introductions completed
     */
    intro_completed: number;
    /**
     * Intro Requests
     *
     * Number of introductions requested
     */
    intro_requests: number;
    /**
     * Pipeline Created
     *
     * Number of pipelines created
     */
    pipeline_created: number;
};

/**
 * PeriodStatsRole
 */
export type PeriodStatsRole = {
    /**
     * First Recommendation
     *
     * Date when the first recommendation was published within the selected time range
     */
    first_recommendation?: string | null;
    all: StatSplitRole;
    intro_completed: StatSplitRole;
    intro_requests: StatSplitRole;
    not_interesting: StatSplitRole;
};

/**
 * PeriodStatsSubmission
 */
export type PeriodStatsSubmission = {
    /**
     * First Recommendation
     *
     * Date when the first recommendation was published within the selected time range
     */
    first_recommendation?: string | null;
    all: StatSplitSubmission;
    intro_completed: StatSplitSubmission;
    intro_requests: StatSplitSubmission;
    not_interesting: StatSplitSubmission;
};

/**
 * PeriodStatsTeam
 */
export type PeriodStatsTeam = {
    /**
     * First Recommendation
     *
     * Date when the first recommendation was published within the selected time range
     */
    first_recommendation?: string | null;
    all: StatSplitTeam;
    intro_completed: StatSplitTeam;
    intro_requests: StatSplitTeam;
    not_interesting: StatSplitTeam;
};

/**
 * PersonBasicEmailResponse
 */
export type PersonBasicEmailResponse = {
    /**
     * Email
     *
     * Email associated with the person
     */
    email: string;
    /**
     * Name
     *
     * Person's full name
     */
    name: string;
    /**
     * First Name
     *
     * Person's first name
     */
    first_name?: string | null;
    /**
     * Last Name
     *
     * Person's last name
     */
    last_name?: string | null;
    /**
     * Image
     *
     * Path to person image
     */
    image?: string | null;
    /**
     * UUID
     *
     * UUID uniquely identifying a person
     */
    uuid: string;
    /**
     * Profile Image Url
     */
    readonly profile_image_url: string | null;
};

/**
 * PersonBasicResponse
 */
export type PersonBasicResponse = {
    /**
     * Name
     *
     * Person's full name
     */
    name: string;
    /**
     * First Name
     *
     * Person's first name
     */
    first_name?: string | null;
    /**
     * Last Name
     *
     * Person's last name
     */
    last_name?: string | null;
    /**
     * Image
     *
     * Path to person image
     */
    image?: string | null;
    /**
     * UUID
     *
     * UUID uniquely identifying a person
     */
    uuid: string;
    /**
     * Profile Image Url
     */
    readonly profile_image_url: string | null;
};

/**
 * PersonClerkPasswordPut
 *
 * Set a user's Clerk password (admin on v1, self-service on v2 ``/current``).
 */
export type PersonClerkPasswordPut = {
    /**
     * New Password
     *
     * New password meeting strength requirements
     */
    new_password: string;
};

/**
 * PersonCurrentExternalPatch
 *
 * Schema for self-service updates of external person fields.
 */
export type PersonCurrentExternalPatch = {
    /**
     * Email
     *
     * Email associated with the person
     */
    email?: string | null;
    /**
     * First Name
     *
     * Person's first name
     */
    first_name?: string | null;
    /**
     * Last Name
     *
     * Person's last name
     */
    last_name?: string | null;
    /**
     * Notes External
     *
     * External notes
     */
    notes_external?: string | null;
    /**
     * LinkedIn URL
     *
     * LinkedIn URL
     */
    linkedin_url?: string | null;
};

/**
 * PersonCurrentExternalResponse
 */
export type PersonCurrentExternalResponse = {
    /**
     * UUID
     *
     * UUID uniquely identifying a person
     */
    uuid: string;
    /**
     * Email
     *
     * Email associated with the person
     */
    email: string;
    /**
     * First Name
     *
     * Person's first name
     */
    first_name?: string | null;
    /**
     * Last Name
     *
     * Person's last name
     */
    last_name?: string | null;
    /**
     * Notes External
     *
     * External notes
     */
    notes_external?: string | null;
    /**
     * LinkedIn URL
     *
     * LinkedIn URL
     */
    linkedin_url?: string | null;
};

/**
 * PersonEmailNotificationsPatch
 *
 * Schema for updating a person's email notification preferences.
 */
export type PersonEmailNotificationsPatch = {
    /**
     * Permit Preregistration Notifications
     *
     * Whether to send preregistration marketing emails to this user
     */
    permit_preregistration_notifications: boolean;
};

/**
 * PersonInnerDocSchema
 */
export type PersonInnerDocSchema = {
    /**
     * Email
     *
     * Email associated with the person
     */
    email: string;
    /**
     * First name
     *
     * Person's first name
     */
    first_name?: string | null;
    /**
     * Name
     *
     * Person's full name
     */
    name: string;
    /**
     * Last name
     *
     * Person's last name
     */
    last_name?: string | null;
    /**
     * Image
     *
     * Path to person image
     */
    image?: string | null;
    /**
     * ID
     *
     * UUID uniquely identifying a person
     */
    uuid: string;
    /**
     * Profile Image Url
     */
    readonly profile_image_url: string | null;
};

/**
 * PersonMemberResponse
 */
export type PersonMemberResponse = {
    /**
     * First Name
     *
     * Person's first name
     */
    first_name?: string | null;
    /**
     * Last Name
     *
     * Person's last name
     */
    last_name?: string | null;
    /**
     * Email
     *
     * Email associated with the person
     */
    email: string;
    /**
     * Pending
     *
     * Is the person's invite still pending, or has it been accepted?
     */
    pending: boolean;
    /**
     * Terms accepted
     *
     * Time when a person accepted terms and conditions on first login
     *
     * @deprecated
     */
    first_login?: string | null;
    /**
     * Name
     *
     * Person's full name
     */
    name: string;
    /**
     * ID
     *
     * ID uniquely identifying a person
     */
    id: string;
    /**
     * Image
     *
     * Path to person image
     */
    image?: string | null;
    /**
     * Roles
     *
     * List of person's roles
     */
    roles: Array<PersonMemberRole>;
    /**
     * Permit Preregistration Notifications
     *
     * Whether the person can receivepreregistration notifications
     */
    permit_preregistration_notifications: boolean;
    /**
     * Terms accepted
     *
     * Time when a person accepted terms and conditions on first login
     */
    terms_accepted?: string | null;
    /**
     * Profile Image Url
     */
    readonly profile_image_url: string | null;
};

/**
 * PersonMemberRole
 */
export type PersonMemberRole = {
    /**
     * Company
     *
     * Company with which this role is associated
     */
    company?: CompanyBasicTypeResponse | null;
    /**
     * Person's role
     */
    role: Role;
    /**
     * Team
     *
     * Team with which this role is associated
     */
    team_?: TeamBasicResponse | null;
};

/**
 * PersonPasswordUpdatedResponse
 *
 * Returned after a successful Clerk password update.
 */
export type PersonPasswordUpdatedResponse = {
    /**
     * Email
     *
     * Email of the user whose Clerk password was updated
     */
    email: string;
};

/**
 * PersonPatch
 *
 * Schema for partially updating a person's profile.
 */
export type PersonPatch = {
    /**
     * Email
     *
     * Email associated with the person
     */
    email?: string | null;
    /**
     * First Name
     *
     * Person's first name
     */
    first_name?: string | null;
    /**
     * Last Name
     *
     * Person's last name
     */
    last_name?: string | null;
    /**
     * Notes External
     *
     * External notes
     */
    notes_external?: string | null;
    /**
     * Notes Internal
     *
     * Internal notes
     */
    notes_internal?: string | null;
    /**
     * LinkedIn URL
     *
     * LinkedIn URL
     */
    linkedin_url?: string | null;
    /**
     * Wants Intros
     *
     * Preference for whether the person wants intros
     */
    wants_intros?: boolean | null;
    /**
     * Wants Intro Followups
     *
     * Whether the person wants intro followups
     */
    wants_intro_followups?: boolean | null;
    /**
     * Handle Intros As Exec
     *
     * Whether intro handling should be done as executive
     */
    handle_intros_as_exec?: boolean | null;
};

/**
 * PersonRole
 */
export type PersonRole = {
    company?: CustomerCompany | null;
    /**
     * Team Slug
     */
    team_slug?: string | null;
    /**
     * Team Active
     */
    team_active?: boolean | null;
    title: Role;
};

/**
 * PersonSearchResponse
 */
export type PersonSearchResponse = {
    /**
     * Meta
     *
     * Metadata about the search result
     */
    search_metadata?: EsMeta | null;
    /**
     * First Name
     *
     * Person's first name
     */
    first_name?: string | null;
    /**
     * Last Name
     *
     * Person's last name
     */
    last_name?: string | null;
    /**
     * Email
     *
     * Email associated with the person
     */
    email: string;
    /**
     * Company
     */
    company?: CompanyBasicTypeResponse | null;
    /**
     * Full Name
     *
     * Person's full name
     */
    full_name?: string | null;
    /**
     * ID
     *
     * ID uniquely identifying a person
     */
    id: string;
    /**
     * UUID
     *
     * UUID uniquely identifying a person
     */
    uuid: string;
};

/**
 * PersonV1CurrentResponse
 *
 * GET ``/api/v1/people/current`` — legacy person payload with a signed profile image URL.
 */
export type PersonV1CurrentResponse = {
    /**
     * Country
     */
    country?: string | null;
    /**
     * Email
     */
    email: string;
    /**
     * First Login
     */
    first_login?: string | null;
    /**
     * First Name
     */
    first_name?: string | null;
    /**
     * Has Set Password
     */
    has_set_password?: boolean;
    /**
     * Id
     */
    id?: string | null;
    /**
     * Last Login
     */
    last_login?: string | null;
    /**
     * Last Name
     */
    last_name?: string | null;
    /**
     * Last Team Visit Slug
     */
    last_team_visit_slug?: string | null;
    /**
     * Name
     */
    name?: string | null;
    /**
     * Newly Invited
     */
    newly_invited?: boolean;
    /**
     * Notes External
     */
    notes_external?: string | null;
    /**
     * Linkedin Url
     */
    linkedin_url?: string | null;
    /**
     * Person Image
     */
    person_image?: string | null;
    /**
     * Person Name
     */
    person_name?: string | null;
    /**
     * Is Executive
     */
    is_executive?: boolean;
    /**
     * Roles
     */
    roles?: Array<PersonRole>;
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Profile Image Url
     */
    readonly profile_image_url: string | null;
};

/**
 * PersonalContactTagItem
 *
 * Tag label on a contact (executive people list, profiles, etc.).
 */
export type PersonalContactTagItem = {
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Name
     */
    name: string;
};

/**
 * PersonalTagAssociationCreatePayload
 *
 * Exactly one of ``tag_definition_uuid`` (existing) or ``name`` (create + link).
 */
export type PersonalTagAssociationCreatePayload = {
    /**
     * Tag Definition Uuid
     */
    tag_definition_uuid?: string | null;
    /**
     * Name
     */
    name?: string | null;
};

/**
 * PersonalTagDefinitionCreatePayload
 */
export type PersonalTagDefinitionCreatePayload = {
    /**
     * Name
     */
    name: string;
};

/**
 * PersonalTagDefinitionPatchPayload
 */
export type PersonalTagDefinitionPatchPayload = {
    /**
     * Name
     */
    name: string;
};

/**
 * PersonalTagDefinitionResponse
 */
export type PersonalTagDefinitionResponse = {
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Name
     */
    name: string;
    /**
     * Created
     */
    created: string;
    /**
     * Last Modified
     */
    last_modified: string;
    /**
     * Usage Count
     *
     * Number of contact associations for this tag (when requested).
     */
    usage_count?: number | null;
};

/**
 * PersonalTagOnContactResponse
 *
 * After adding a tag to a contact.
 */
export type PersonalTagOnContactResponse = {
    /**
     * Association Uuid
     */
    association_uuid: string;
    /**
     * Tag Definition Uuid
     */
    tag_definition_uuid: string;
    /**
     * Name
     */
    name: string;
};

/**
 * PipelineActive
 */
export type PipelineActive = {
    /**
     * Active
     *
     * Is this pipeline marked as active?
     */
    active: boolean;
};

/**
 * PipelineAttendees
 */
export const PipelineAttendees = {
    '1_10': '1-10',
    '11_20': '11-20',
    '21_50': '21-50'
} as const;

/**
 * PipelineAttendees
 */
export type PipelineAttendees = typeof PipelineAttendees[keyof typeof PipelineAttendees];

/**
 * PipelineBasicHiddenResponse
 */
export type PipelineBasicHiddenResponse = {
    /**
     * Name
     *
     * Pipeline name
     */
    name: string;
    /**
     * Pipeline type
     *
     * Pipeline type
     */
    pipeline_type?: PipelineType;
    /**
     * Slug
     *
     * Slug uniquely identifying a pipeline
     */
    slug: string;
    /**
     * Is visible to team
     *
     * Is this pipeline visible to the team?
     */
    is_visible_to_team: boolean;
    /**
     * Hidden
     *
     * Is this pipeline hidden? Inverse of is_visible_to_team
     */
    hidden?: boolean;
};

/**
 * PipelineBasicResponse
 */
export type PipelineBasicResponse = {
    /**
     * Name
     *
     * Pipeline name
     */
    name: string;
    /**
     * Pipeline type
     *
     * Pipeline type
     */
    pipeline_type?: PipelineType;
    /**
     * Slug
     *
     * Slug uniquely identifying a pipeline
     */
    slug: string;
};

/**
 * PipelineBasicV2Response
 */
export type PipelineBasicV2Response = {
    /**
     * Company
     *
     * Name or nickname of the company for which this pipeline is open
     */
    company?: string | null;
    /**
     * Pipeline type
     *
     * Pipeline type
     */
    pipeline_type?: PipelineType;
    /**
     * Role
     *
     * Pipeline name
     */
    role: string;
    /**
     * Slug
     *
     * Slug uniquely identifying a pipeline
     */
    slug: string;
    /**
     * UUID
     *
     * UUID uniquely identifying a pipeline
     */
    uuid: string;
};

/**
 * PipelineCandidateFilterPayload
 */
export type PipelineCandidateFilterPayload = {
    /**
     * Phrase
     *
     * Search phrase
     */
    phrase?: string | null;
    /**
     * Search ID
     *
     * Search session ID
     */
    search_id?: string | null;
    /**
     * Page
     *
     * Page number
     */
    page?: number;
    /**
     * Page size
     *
     * Page size
     */
    page_size?: number;
    /**
     * Filters
     *
     * Filter results by these criteria
     */
    filter_data?: PipelineCandidatesFilters | null;
    /**
     * Sort
     *
     * Sort results by these rules
     */
    sort_rules: Array<PipelineCandidatesSort>;
};

/**
 * PipelineCandidateSearchFilterSortPayload
 */
export type PipelineCandidateSearchFilterSortPayload = {
    /**
     * Phrase
     *
     * Search phrase
     */
    phrase?: string | null;
    /**
     * Search ID
     *
     * Search session ID
     */
    search_id?: string | null;
    /**
     * Page
     *
     * Page number
     */
    page?: number;
    /**
     * Page size
     *
     * Page size
     */
    page_size?: number;
    /**
     * Filters
     *
     * Filter results by these criteria
     */
    filter_data?: PipelineCandidatesFilters | null;
    /**
     * Sort
     *
     * Sort results by these rules
     */
    sort_rules: Array<PipelineCandidatesSort>;
    /**
     * Aggregate Fields
     *
     * Which aggregation buckets to compute and return. If omitted, all aggregations are computed (backwards-compatible). Pass an empty list to skip all aggregations.
     */
    aggregate_fields?: Array<string> | null;
    /**
     * Include Results
     *
     * Whether to fetch and return candidate result rows. Set to False when only aggregation totals are needed (e.g. filter dialog) to skip ES scoring and DB result fetch.
     */
    include_results?: boolean;
    /**
     * Include inactive
     *
     * Also offer inactive candidates that are not on the pipeline yet. Merged candidates are never returned.
     */
    include_inactive?: boolean;
    /**
     * Switched-off picks
     *
     * Only with the Suggested filter on: candidates already on the pipeline that the advisor switched off as signals (BAN-6235). They stay out of the list but stop steering it. With every pick switched off, the list is the RecSys run alone.
     */
    suggestion_excluded_picks?: Array<string> | null;
};

/**
 * PipelineCandidateSearchFilterSortResponse
 */
export type PipelineCandidateSearchFilterSortResponse = {
    /**
     * Meta
     *
     * Metadata about the search result
     */
    search_metadata?: EsMeta | null;
    /**
     * Advisor
     *
     * Information about the advisor assigned to this candidate
     */
    advisor?: PersonBasicResponse | null;
    /**
     * Client Owner
     *
     * Information about the client owner of this candidate
     */
    client_owner?: PersonBasicResponse | null;
    /**
     * Created
     *
     * Date when this candidate profile was created
     */
    created?: string | null;
    /**
     * Company
     *
     * Candidate's current company
     */
    current_company?: string | null;
    /**
     * Role
     *
     * Candidate's current role
     */
    current_role?: string | null;
    /**
     * Current Role
     *
     * Candidate's current role
     */
    role?: string | null;
    /**
     * Location
     *
     * Candidate's location (from their resume)
     */
    location?: string | null;
    /**
     * Board/advisory role interest
     *
     * Interest in getting board or advisory roles
     */
    interest_role_board_advisory?: InterestRole | null;
    /**
     * Operating role interest
     *
     * Interest in getting operating roles
     */
    interest_role_operating?: InterestRole | null;
    /**
     * Keywords
     */
    keywords?: Array<string> | null;
    /**
     * Notes
     */
    notes?: string | null;
    /**
     * Functional expertise
     *
     * List of functions candidate has experience in
     */
    function_expertise?: Array<Function>;
    /**
     * Industry expertise
     *
     * List of industries candidate has experience in
     */
    industry_expertise?: Array<Industry>;
    /**
     * URG
     *
     * Is this candidate from an under-represented group?
     */
    is_urg?: boolean | null;
    /**
     * Operating Highlights
     *
     * List of operating highlights
     */
    operating_highlights?: Array<OperatingHighlights> | null;
    /**
     * Email
     *
     * Candidate's email address
     */
    email: string;
    /**
     * Name
     *
     * Candidate's full name
     */
    name: string;
    /**
     * Image
     *
     * Candidate's profile image URL
     */
    image?: string | null;
    /**
     * Referrers
     *
     * List of people who referred this candidate
     */
    referrers?: Array<PersonBasicResponse> | null;
    /**
     * Tier
     *
     * Candidate tier
     */
    tier: CandidateTier;
    /**
     * Type
     *
     * Banff client type; serialized only for Banff advisors
     */
    type?: ClientType | null;
    /**
     * Pathway
     *
     * Banff pathway; serialized only for Banff advisors
     */
    pathway?: CandidatePathway | null;
    /**
     * Invoice Status
     *
     * HubSpot invoice status; serialized only for Banff advisors
     */
    invoice_status?: HubSpotInvoiceStatus | null;
    /**
     * Warm
     *
     * This value represents whether the candidate has been warm with Banff
     */
    warm?: boolean | null;
    /**
     * RecSys Score
     *
     * Score calculated by the recommendation system
     */
    recsys_score?: number | null;
    /**
     * Suggestion sources
     *
     * Present only when the Suggested filter is on: which rankers surfaced this candidate -- 'recsys' (the pipeline run), 'cart' (live cart signals), or both.
     */
    suggestion_sources?: Array<string> | null;
    /**
     * Suggestion reasons
     *
     * Up to two short advisor-facing reasons from the cart signals.
     */
    suggestion_reasons?: Array<string> | null;
    /**
     * Match Score
     *
     * RecSys's single 0-100 Match score for this pipeline (run rubric blended with cart evidence); the Suggested list is sorted by it. Only set on that list.
     */
    match_score?: number | null;
    /**
     * Match Score Breakdown
     *
     * How the Match score was made, for display on hover.
     */
    match_breakdown?: MatchScoreBreakdown | null;
    /**
     * Team Status
     *
     * Team status for the candidate
     */
    team_status?: PipelineCandidateTeamStatus | null;
    /**
     * Pipeline Status
     *
     * Pipeline status for the candidate
     */
    pipeline_status?: CandidatePipelineStatus | null;
    /**
     * Recommendation UUID
     *
     * use recommendation object instead
     *
     * @deprecated
     */
    recommendation_uuid?: string | null;
    /**
     * Recommendation
     *
     * Minimal recommendation object to hydrate form values
     */
    recommendation?: RecommendationDraftBasicResponse | null;
    /**
     * Introduction email type
     *
     * Type of introduction email
     */
    intro_email_type: IntroEmailType;
    /**
     * Draft assignee
     *
     * Person assigned to this draft recommendation
     */
    draft_assignee?: PersonBasicResponse | null;
    /**
     * Recommender
     *
     * Person who created this recommendation
     */
    recommender?: PersonBasicResponse | null;
    /**
     * Recommended on
     *
     * When the candidate was put on this pipeline (the recommendation's creation time). Null when the candidate has no recommendation here. Distinct from `created`, which is when the candidate's profile was created.
     */
    recommended_on?: string | null;
    /**
     * Autosuggested
     *
     * True when autosuggestion (the RecSys scheduler) created this candidate's recommendation rather than a person. The robot is not a person, so such rows have no meaningful `recommender`; this flag is what identifies their author.
     */
    autosuggested?: boolean;
    /**
     * Internal comments count
     *
     * Count of internal comments on this recommendation
     */
    internal_comments_count?: number | null;
    /**
     * Is accepting intros
     *
     * Whether the candidate is currently accepting introductions (not in a blackout period)
     */
    is_accepting_intros?: boolean | null;
    /**
     * Not accepting start date
     *
     * Start date of the current blackout period when candidate is not accepting introductions
     */
    not_accepting_start_date?: string | null;
    /**
     * Not accepting end date
     *
     * End date of the current blackout period when candidate is not accepting introductions
     */
    not_accepting_end_date?: string | null;
    /**
     * Off-limits for this pipeline team
     *
     * When this pipeline's search team is off-limits for the candidate (company or team member), the same payload shape as submission errors
     */
    pipeline_off_limits?: OffLimitsTeamSchema | null;
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Profile Image Url
     */
    readonly profile_image_url: string | null;
};

/**
 * PipelineCandidateSortColumn
 */
export const PipelineCandidateSortColumn = {
    ASSIGNEE_NAME: 'assignee_name',
    CREATED: 'created',
    INTEREST_ROLE_BOARD_ADVISORY: 'interest_role_board_advisory',
    INTEREST_ROLE_OPERATING: 'interest_role_operating',
    NAME: 'name',
    NOT_ACCEPTING_END_DATE: 'not_accepting_end_date',
    OPERATING_HIGHLIGHTS: 'operating_highlights',
    PIPELINE_STATUS: 'pipeline_status',
    RECOMMENDED_ON: 'recommended_on',
    TIER: 'tier',
    TIER_RANK: 'tier_rank',
    UNDER_REPRESENTED: 'under_represented',
    WARM: 'warm'
} as const;

/**
 * PipelineCandidateSortColumn
 */
export type PipelineCandidateSortColumn = typeof PipelineCandidateSortColumn[keyof typeof PipelineCandidateSortColumn];

/**
 * PipelineCandidateTeamStatus
 */
export type PipelineCandidateTeamStatus = {
    /**
     * Action
     *
     * action taken on the candidate
     */
    action: EvaluationAction;
    /**
     * Created
     *
     * date when the action was taken
     */
    created?: string | null;
};

/**
 * PipelineCandidatesFilterPaginatedResponse
 */
export type PipelineCandidatesFilterPaginatedResponse = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Results
     */
    results: Array<PipelineCandidatesFilterResponse>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCountsResponse;
};

/**
 * PipelineCandidatesFilterResponse
 */
export type PipelineCandidatesFilterResponse = {
    /**
     * Meta
     *
     * Metadata about the search result
     */
    search_metadata?: EsMeta | null;
    /**
     * Email
     *
     * Candidate's email address
     */
    email: string;
    /**
     * Name
     *
     * Candidate's full name
     */
    name?: string | null;
    /**
     * Image
     *
     * Candidate's profile image URL
     */
    image?: string | null;
    /**
     * Candidate UUID
     *
     * UUID of the candidate
     */
    uuid?: string;
    /**
     * Current Company
     *
     * Company of the candidate
     */
    current_company?: string | null;
    /**
     * Current Role
     *
     * Candidate's current role
     */
    current_role?: string | null;
    /**
     * Function Expertise
     *
     * Function expertise of the candidate
     */
    function_expertise?: Array<Function>;
    /**
     * Industry Expertise
     *
     * Industry expertise of the candidate
     */
    industry_expertise?: Array<Industry>;
    /**
     * Operating role interest
     *
     * Interest in getting operating roles
     */
    interest_role_operating?: InterestRole | null;
    /**
     * Operating highlights
     *
     * Operating highlights
     */
    operating_highlights?: Array<OperatingHighlights>;
    /**
     * Board/advisory role interest
     *
     * Interest in getting board or advisory roles
     */
    interest_role_board_advisory?: InterestRole | null;
    /**
     * Tier
     *
     * Candidate tier
     */
    tier?: CandidateTier | null;
    /**
     * Advisor
     *
     * The candidate's advisor, if present
     */
    advisor?: PersonBasicResponse | null;
    /**
     * Recommendation status in relation to this company across all its teams
     */
    connection_status?: RecommendationStatusOutput | null;
    /**
     * Recommendation status on this pipeline
     */
    pipeline_status?: RecommendationStatusOutput | null;
    /**
     * Warm
     *
     * Whether this candidate is a warm connection
     */
    warm?: boolean;
    /**
     * Created
     *
     * Created at
     */
    created?: string | null;
    /**
     * Keywords
     *
     * Keywords for the candidate profile
     */
    keywords?: Array<string>;
    /**
     * Notes
     */
    notes?: string | null;
    /**
     * Underrepresented
     *
     * Is part of an underrepresented demographic
     */
    is_urg?: boolean;
    /**
     * Referrers
     *
     * Who referred this candidate
     */
    referrers?: Array<PersonBasicResponse>;
    /**
     * Profile Image Url
     */
    readonly profile_image_url: string | null;
};

/**
 * PipelineCandidatesFilters
 */
export type PipelineCandidatesFilters = {
    /**
     * Advisor Id
     */
    advisor_id?: Array<string> | null;
    /**
     * Function Expertise
     */
    function_expertise?: Array<Function> | null;
    /**
     * Industry Expertise
     */
    industry_expertise?: Array<Industry> | null;
    /**
     * Declined Status
     *
     * Filter by whether the team has declined this candidate. OR within the category, AND with other filter categories.
     */
    declined_status?: Array<DeclinedStatusFilter> | null;
    /**
     * Introduction Status
     *
     * Filter by whether the candidate has been introduced to this pipeline's team. For exec teams, 'introduced' covers all paths: B4B intros, EvaluationHistory.FACILITATED, P2P (INTRO_MADE), and event co-attendance. OR within the category, AND with other filter categories.
     */
    introduction_status?: Array<IntroductionStatusFilter> | null;
    /**
     * Is Accepting Intros
     */
    is_accepting_intros?: Array<boolean> | null;
    /**
     * Recommendation Status
     *
     * Filter by whether the candidate has a published recommendation for this pipeline. OR within the category, AND with other filter categories.
     */
    recommendation_status?: Array<RecommendationStatusFilter> | null;
    /**
     * Pipeline Status
     */
    pipeline_status?: Array<CandidatePipelineStatus> | null;
    /**
     * Recsys Suggested
     *
     * [true] restricts results to the pipeline's latest RecSys run, resolved server-side against the RecSys service (default order: RecSys rank; explicit sort_rules and phrase relevance still win). [false] or omitted: no restriction. 502/503 when the filter is on and RecSys is unavailable.
     */
    recsys_suggested?: Array<boolean> | null;
    /**
     * Warm
     */
    warm?: Array<boolean> | null;
    /**
     * Tier
     */
    tier?: Array<CandidateTier> | null;
    /**
     * Type
     */
    type?: Array<ClientType> | null;
    /**
     * Pathway
     */
    pathway?: Array<CandidatePathway> | null;
    /**
     * Underrepresented
     */
    underrepresented?: Array<boolean> | null;
    /**
     * Operating Highlights
     */
    operating_highlights?: Array<OperatingHighlights> | null;
};

/**
 * PipelineCandidatesSort
 */
export type PipelineCandidatesSort = {
    /**
     * Column
     *
     * Sort results on this column
     */
    column: PipelineCandidateSortColumn;
    /**
     * Direction
     *
     * Sort results in this direction
     */
    direction?: SortDirection;
};

/**
 * PipelineExternalResponse
 */
export type PipelineExternalResponse = {
    /**
     * Created
     *
     * Date when this pipeline was created
     */
    created: string;
    /**
     * Company
     *
     * Name or nickname of the company for which this pipeline is open
     */
    company?: string | null;
    /**
     * Formatted name
     *
     * Readable pipeline name combining multiple properties into a more unique name
     */
    formatted_name: string;
    /**
     * Last modified
     *
     * Date when this pipeline was last modified
     */
    last_modified: string;
    /**
     * Pipeline type
     *
     * Pipeline type
     */
    type?: PipelineType;
    /**
     * Role
     *
     * Pipeline name
     */
    role: string;
    /**
     * Slug
     *
     * Slug uniquely identifying a pipeline
     */
    slug: string;
    /**
     * UUID
     *
     * UUID uniquely identifying a pipeline
     */
    uuid: string;
};

/**
 * PipelineFullResponse
 */
export type PipelineFullResponse = {
    /**
     * Is visible to client
     *
     * Hide this pipeline from its team members?
     */
    is_visible_to_team?: boolean | null;
    /**
     * Keywords
     *
     * List of keywords
     */
    keywords?: Array<string> | null;
    /**
     * Placement
     *
     * Candidate that got placed for this pipeline
     */
    placed_candidate?: CandidateBasicResponse | null;
    /**
     * Pipeline type
     *
     * Pipeline type
     */
    pipeline_type?: PipelineType;
    /**
     * Active
     *
     * Is this pipeline marked as active?
     */
    active: boolean;
    /**
     * Attendees
     *
     * Number of attendees
     */
    attendees?: PipelineAttendees | null;
    /**
     * Company
     *
     * Name or nickname of the company for which this pipeline is open
     */
    company?: string | null;
    /**
     * Company size (annual revenue)
     *
     * Size of company for which this pipeline is open
     */
    company_size?: CompanySize | null;
    /**
     * Company type
     *
     * Type of company for which this pipeline is open
     */
    company_type?: CompanyType | null;
    /**
     * Created
     *
     * Date when this pipeline was created
     */
    created: string;
    /**
     * Creator
     *
     * Person who created this pipeline
     */
    creator?: PersonBasicResponse | null;
    /**
     * Event date
     *
     * Date of the event
     */
    event_date?: string | null;
    /**
     * Event name
     *
     * Name of the event
     */
    event_name?: string | null;
    /**
     * Formatted name
     *
     * Readable pipeline name combining multiple properties into a more unique name
     */
    formatted_name: string;
    /**
     * Functional expertise
     *
     * List of functions the ideal candidate has experience in
     */
    function_expertise: Array<Function>;
    /**
     * Industry expertise
     *
     * List of industries the ideal candidate has experience in
     */
    industry_expertise: Array<Industry>;
    /**
     * Is URG
     *
     * Is the role specifically targeting under represented candidates?
     */
    is_urg: boolean;
    /**
     * Location
     *
     * The location specified for the pipeline role
     */
    location?: string | null;
    /**
     * Name
     *
     * Pipeline name
     */
    name: string;
    /**
     * Notes
     *
     * Any additional details to help us calibrate recommendations? (ex: target companies, benchmark profiles, etc.)
     */
    notes?: string | null;
    /**
     * Point of contact
     *
     * Point of contact person
     */
    point_of_contact?: PersonBasicResponse | null;
    /**
     * Purpose
     *
     * For 'other' type pipelines, the purpose of the pipeline
     */
    purpose?: string | null;
    /**
     * Is proactive
     *
     * Is this pipeline proactive?
     */
    is_proactive: boolean;
    /**
     * Role type
     *
     * Role type
     */
    role_type: PipelineRoleType;
    /**
     * Role
     *
     * Pipeline name
     */
    role: string;
    /**
     * Team
     *
     * Team for which this pipeline was opened
     */
    team: TeamCompanyResponse;
    /**
     * Slug
     *
     * Slug uniquely identifying a pipeline
     */
    slug: string;
    /**
     * UUID
     *
     * UUID uniquely identifying a pipeline
     */
    uuid: string;
};

/**
 * PipelineObjective
 */
export const PipelineObjective = {
    BOARD_ADVISORY: 'Board/Advisory',
    BACKABLE_EXECUTIVE: 'Backable Executive',
    GENERAL: 'General',
    OPERATING: 'Operating',
    OTHER: 'Other'
} as const;

/**
 * PipelineObjective
 */
export type PipelineObjective = typeof PipelineObjective[keyof typeof PipelineObjective];

/**
 * PipelineRoleType
 */
export const PipelineRoleType = {
    BOARD: 'BOARD',
    EXECUTIVE: 'EXECUTIVE',
    GENERAL: 'GENERAL',
    OPERATING: 'OPERATING',
    OTHER: 'OTHER'
} as const;

/**
 * PipelineRoleType
 */
export type PipelineRoleType = typeof PipelineRoleType[keyof typeof PipelineRoleType];

/**
 * PipelineRoleTypeWithNull
 */
export const PipelineRoleTypeWithNull = {
    BOARD: 'BOARD',
    EXECUTIVE: 'EXECUTIVE',
    GENERAL: 'GENERAL',
    OPERATING: 'OPERATING',
    OTHER: 'OTHER',
    NULL: 'null'
} as const;

/**
 * PipelineRoleTypeWithNull
 */
export type PipelineRoleTypeWithNull = typeof PipelineRoleTypeWithNull[keyof typeof PipelineRoleTypeWithNull];

/**
 * PipelineSearchResponse
 */
export type PipelineSearchResponse = {
    /**
     * Active
     */
    active: boolean;
    /**
     * Company
     */
    company?: string | null;
    /**
     * Function Expertise
     */
    function_expertise?: Array<string> | null;
    /**
     * ID
     */
    id: number;
    /**
     * Industry Expertise
     */
    industry_expertise?: Array<string> | null;
    /**
     * Keywords
     */
    keywords?: Array<string> | null;
    /**
     * Name
     */
    name: string;
    /**
     * Candidates
     *
     * This is just to store the `candidates` relationship under the `recommendations` key
     */
    candidates: Array<RecommendationCandidateBasicResponse>;
    /**
     * Slug
     */
    slug: string;
    /**
     * Team
     *
     * This is just to store the `search_team` relationship under the `team` key
     */
    search_team: TeamWithMembersBasicResponse;
    team_type: OrgType;
    /**
     * UUID
     */
    uuid: string;
};

/**
 * PipelineStatus
 */
export const PipelineStatus = { ACTIVE: 'active', CLOSED: 'closed' } as const;

/**
 * PipelineStatus
 */
export type PipelineStatus = typeof PipelineStatus[keyof typeof PipelineStatus];

/**
 * PipelineTransferPayload
 *
 * Payload for transferring pipelines to another team.
 */
export type PipelineTransferPayload = {
    /**
     * Target Team Uuid
     *
     * UUID of the target team to transfer pipelines to
     */
    target_team_uuid: string;
    /**
     * Pipeline Uuids
     *
     * List of pipeline UUIDs to transfer
     */
    pipeline_uuids: Array<string>;
};

/**
 * PipelineTransferResponse
 *
 * Response for pipeline transfer operation.
 */
export type PipelineTransferResponse = {
    /**
     * Transferred Count
     *
     * Number of pipelines successfully transferred
     */
    transferred_count: number;
    /**
     * Pipeline Uuids
     *
     * List of transferred pipeline UUIDs
     */
    pipeline_uuids: Array<string>;
    /**
     * Target Team Uuid
     *
     * UUID of the target team
     */
    target_team_uuid: string;
    /**
     * Warnings
     *
     * Warnings about entities that need manual attention
     */
    warnings?: Array<string>;
};

/**
 * PipelineType
 */
export const PipelineType = {
    HIRING: 'HIRING',
    NETWORKING: 'NETWORKING',
    EVENT: 'EVENT',
    RESEARCH: 'RESEARCH',
    OTHER: 'OTHER'
} as const;

/**
 * PipelineType
 */
export type PipelineType = typeof PipelineType[keyof typeof PipelineType];

/**
 * PipelineTypeFilter
 */
export const PipelineTypeFilter = {
    HIRING: 'hiring',
    NETWORKING: 'networking',
    OTHER: 'other',
    RESEARCH: 'research',
    EVENT: 'event'
} as const;

/**
 * PipelineTypeFilter
 */
export type PipelineTypeFilter = typeof PipelineTypeFilter[keyof typeof PipelineTypeFilter];

/**
 * PipelinesAdvancedSearch
 */
export type PipelinesAdvancedSearch = {
    /**
     * Title
     */
    title?: Array<string> | null;
    /**
     * Location
     */
    location?: Array<string> | null;
    /**
     * Notes
     */
    notes?: Array<string> | null;
};

/**
 * PipelinesAggregationsResponse
 */
export type PipelinesAggregationsResponse = {
    /**
     * Status
     */
    status: {
        [key in PipelineStatus]?: number;
    };
    /**
     * Industry Expertise
     */
    industry_expertise: {
        [key in Industry]?: number;
    };
    /**
     * Function Expertise
     */
    function_expertise: {
        [key in Function]?: number;
    };
    /**
     * Is Proactive
     */
    is_proactive: {
        [key: string]: number;
    };
    /**
     * Company Size
     */
    company_size: {
        [key in CompanySize]?: number;
    };
    /**
     * Has Completed Intro
     */
    has_completed_intro: {
        [key: string]: number;
    };
    /**
     * Team Type
     */
    team_type: {
        [key in OrgType]?: number;
    };
    /**
     * Pipeline Type
     */
    pipeline_type: {
        [key in PipelineType]?: number;
    };
    /**
     * Team Uuid
     */
    team_uuid: {
        [key: string]: number;
    };
    /**
     * Team Company Slug
     */
    team_company_slug: {
        [key: string]: number;
    };
};

/**
 * PipelinesFilters
 */
export type PipelinesFilters = {
    /**
     * Status
     */
    status?: Array<PipelineStatus> | null;
    /**
     * Industry Expertise
     */
    industry_expertise?: Array<Industry> | null;
    /**
     * Function Expertise
     */
    function_expertise?: Array<Function> | null;
    /**
     * Is Proactive
     */
    is_proactive?: Array<boolean> | null;
    /**
     * Company Size
     */
    company_size?: Array<CompanySize> | null;
    /**
     * Has Completed Intro
     */
    has_completed_intro?: Array<boolean> | null;
    /**
     * Team Type
     */
    team_type?: Array<OrgType> | null;
    /**
     * Pipeline Type
     */
    pipeline_type?: Array<PipelineType> | null;
    /**
     * Team Uuid
     */
    team_uuid?: Array<string> | null;
    /**
     * Company
     */
    company?: Array<string> | null;
};

/**
 * PipelinesSearchData
 */
export type PipelinesSearchData = {
    /**
     * Phrase
     */
    phrase?: string | null;
    advanced_search?: PipelinesAdvancedSearch | null;
    filter_data?: PipelinesFilters | null;
    /**
     * Page
     */
    page?: number;
    /**
     * Size
     */
    size?: number;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Sort
     *
     * Sort results by these rules
     */
    sort_rules: Array<PipelinesSort>;
};

/**
 * PipelinesSort
 */
export type PipelinesSort = {
    /**
     * Column
     *
     * Sort results on this column
     */
    column: PipelinesSortColumn;
    /**
     * Direction
     *
     * Sort results in this direction
     */
    direction?: SortDirection;
};

/**
 * PipelinesSortColumn
 */
export const PipelinesSortColumn = {
    '': '',
    ROLE: 'role',
    ROLE_TYPE: 'role_type',
    CREATED: 'created',
    LAST_MODIFIED: 'last_modified',
    INDUSTRY_EXPERTISE: 'industry_expertise',
    FUNCTION_EXPERTISE: 'function_expertise',
    IS_PROACTIVE: 'is_proactive',
    COMPANY: 'company',
    HAS_COMPLETED_INTRO: 'has_completed_intro',
    RECOMMENDATIONS_COUNT: 'recommendations_count',
    INTRODUCTIONS_COUNT: 'introductions_count',
    LAST_SUBMISSION: 'last_submission',
    RECOMMENDATIONS: 'recommendations',
    ACTIVE: 'active',
    TEAM_TYPE: 'team_type'
} as const;

/**
 * PipelinesSortColumn
 */
export type PipelinesSortColumn = typeof PipelinesSortColumn[keyof typeof PipelinesSortColumn];

/**
 * PipelinesTableV2Response
 */
export type PipelinesTableV2Response = {
    /**
     * Meta
     *
     * Metadata about the search result
     */
    search_metadata?: EsMeta | null;
    /**
     * Active
     *
     * Is this pipeline marked as active?
     */
    active: boolean;
    /**
     * Company
     *
     * Name or nickname of the company for which this pipeline is open
     */
    company?: string | null;
    /**
     * Created
     *
     * Date when this pipeline was created
     */
    created: string;
    /**
     * Last modified
     *
     * Date when this pipeline was updated
     */
    last_modified: string;
    /**
     * Functional expertise
     *
     * List of functions the ideal candidate has experience in
     */
    function_expertise: Array<Function>;
    /**
     * Industry expertise
     *
     * List of industries the ideal candidate has experience in
     */
    industry_expertise: Array<Industry>;
    /**
     * Introductions count
     *
     * Total number of introductions requested for this pipeline
     */
    introductions_count: number;
    /**
     * Is proactive
     *
     * Is this pipeline proactive?
     */
    is_proactive: boolean;
    /**
     * Is visible to client
     *
     * Hide this pipeline from its team members?
     */
    visible_to_team: boolean;
    /**
     * Last submission
     *
     * Date of the last submission to this pipeline
     */
    last_submission?: string | null;
    /**
     * Pipeline type
     *
     * Pipeline type
     */
    pipeline_type?: PipelineType;
    /**
     * Recommendations count
     *
     * Total number of recommendations made for this pipeline
     */
    recommendations_count: number;
    /**
     * Role
     *
     * Pipeline name
     */
    role: string;
    /**
     * Role type
     *
     * Role type
     */
    role_type: PipelineRoleType;
    /**
     * Slug
     *
     * Slug uniquely identifying a pipeline
     */
    slug: string;
    /**
     * Search team
     *
     * Search team for which this pipeline was opened
     */
    team: TeamBasicUuidResponse;
    /**
     * Team company
     *
     * Type of company this team belongs to
     */
    team_company: CompanyBasicTypeResponse;
    /**
     * Team type
     *
     * Type of company this team belongs to
     */
    team_type?: OrgType;
    /**
     * UUID
     *
     * UUID uniquely identifying a pipeline
     */
    uuid: string;
};

/**
 * PositionResponse
 */
export type PositionResponse = {
    /**
     * Dates
     *
     * Period during which person worked in the position
     */
    dates?: string | null;
    /**
     * Duration
     *
     * String describing length of dates person worked in the position
     */
    duration?: string | null;
    /**
     * Location
     *
     * Location string
     */
    location?: string | null;
    /**
     * Summary
     *
     * Summary text about the position
     */
    summary?: string | null;
    /**
     * Name
     *
     * Name of position
     */
    name: string;
};

/**
 * PositionUploadResponse
 */
export type PositionUploadResponse = {
    /**
     * Dates
     *
     * Period during which person worked in the position
     */
    dates?: string | null;
    /**
     * Duration
     *
     * String describing length of dates person worked in the position
     */
    duration?: string | null;
    /**
     * Location
     *
     * Location string
     */
    location?: string | null;
    /**
     * Summary
     *
     * Summary text about the position
     */
    summary?: string | null;
    /**
     * Name
     *
     * Name of position
     */
    name: string;
};

/**
 * PostPage[ActivityFeedItem]
 */
export type PostPageActivityFeedItem = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Results
     */
    results: Array<ActivityFeedItem>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCounts;
};

/**
 * PostPage[CompanyDetailedResponse]
 */
export type PostPageCompanyDetailedResponse = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Results
     */
    results: Array<CompanyDetailedResponse>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCounts;
};

/**
 * PostPage[MarketSearchRowResponse]
 */
export type PostPageMarketSearchRowResponse = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Results
     */
    results: Array<MarketSearchRowResponse>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCounts;
};

/**
 * PostPersonClerkUserResponse
 */
export type PostPersonClerkUserResponse = {
    /**
     * Person
     *
     * Person with the Clerk user
     */
    person: PersonBasicResponse;
    /**
     * Clerk user ID
     *
     * Clerk user ID
     */
    clerk_user_id: string;
};

/**
 * PotentialGuestEmailItemResponse
 *
 * Stripped-down potential guest row for email selection/export flows.
 */
export type PotentialGuestEmailItemResponse = {
    /**
     * Source Type
     *
     * Whether this guest is a user, candidate, or standalone event guest
     */
    source_type: PotentialGuestSourceType;
    /**
     * Person UUID
     *
     * Person UUID when row is linked to a person
     */
    person_uuid?: string | null;
    /**
     * Candidate UUID
     *
     * Candidate UUID when row is linked to a candidate
     */
    candidate_uuid?: string | null;
    /**
     * Email
     *
     * Email address used for copy/export
     */
    email?: string | null;
};

/**
 * PotentialGuestItem
 *
 * A single potential guest for event guest lists.
 *
 * Drawn from B4B users (non-advisory), candidates, or standalone event
 * guests from previous events. When a person is both a user and a candidate,
 * they appear once (as user) with optional candidate_uuid set.
 */
export type PotentialGuestItem = {
    /**
     * Source Type
     *
     * Whether this guest is from users, candidates, or past event guests
     */
    source_type: PotentialGuestSourceType;
    /**
     * Identity UUID
     *
     * The human this row is about, or null for a standalone guest from a past event. One human is one row; this is the key to select and de-duplicate by
     */
    identity_uuid?: string | null;
    /**
     * Person UUID
     *
     * Person UUID when source is user (or when candidate is also a user)
     */
    person_uuid?: string | null;
    /**
     * Candidate UUID
     *
     * Candidate UUID when source is candidate
     */
    candidate_uuid?: string | null;
    /**
     * Is Executive
     *
     * True when this candidate is an activated executive user (ExecutiveSettings exists on the linked person, so person_uuid resolves to a real user page). False for plain candidates and standalone event guests.
     */
    is_executive?: boolean;
    /**
     * Name
     *
     * Display name
     */
    name?: string | null;
    /**
     * Role
     *
     * Role or title
     */
    role?: string | null;
    /**
     * Company
     *
     * Company name
     */
    company?: string | null;
    /**
     * Email
     *
     * Email address
     */
    email?: string | null;
    /**
     * Created
     *
     * When the user, candidate, or event guest record was created
     */
    created?: string | null;
    /**
     * Last Event Date
     *
     * Date of the most recent past event this guest attended (status=going)
     */
    last_event_date?: string | null;
    /**
     * Events Attended Count
     *
     * Number of past events this guest attended
     */
    events_attended_count?: number;
    /**
     * Events Invited Count
     *
     * Number of events this guest was invited to (on guest list)
     */
    events_invited_count?: number;
    /**
     * Tier
     *
     * Candidate tier (when source is user with candidate or candidate)
     */
    tier?: CandidateTier | null;
    /**
     * Candidate Type
     *
     * Candidate's Banff client type (tier 1 only)
     */
    candidate_type?: ClientType | null;
    /**
     * Pathway
     *
     * Candidate's Banff pathway (tier 1 only)
     */
    pathway?: CandidatePathway | null;
    /**
     * Industry
     *
     * Industry expertise (when source is user with candidate or candidate)
     */
    industry?: Array<Industry> | null;
    /**
     * Function
     *
     * Function expertise (when source is user with candidate or candidate)
     */
    function?: Array<Function> | null;
    /**
     * Operating Highlights
     *
     * Operating highlights (when source is user with candidate or candidate)
     */
    operating_highlights?: Array<OperatingHighlights> | null;
    /**
     * Interest Role Operating
     *
     * Interest role operating (when source is user with candidate or candidate)
     */
    interest_role_operating?: InterestRole | null;
    /**
     * Interest Role Board
     *
     * Interest role board/advisory (user with candidate or candidate)
     */
    interest_role_board?: InterestRole | null;
    /**
     * Keywords
     *
     * Keywords (when source is user with candidate or candidate)
     */
    keywords?: Array<string> | null;
    /**
     * Underrepresented
     *
     * Whether from underrepresented demographic (candidate diverse flag)
     */
    underrepresented?: boolean | null;
    /**
     * Warm
     *
     * Whether this candidate is a warm connection (tier-based; candidates only)
     */
    warm?: boolean | null;
    /**
     * Invoice Status
     *
     * HubSpot invoice status when the guest is a linked candidate
     */
    invoice_status?: HubSpotInvoiceStatus | null;
    /**
     * Referrers
     *
     * Persons who referred this candidate (when source_type is candidate)
     */
    referrers?: Array<PersonBasicResponse> | null;
    /**
     * Advisor
     *
     * Advisor for this guest (candidates only; user/event_guest have no advisor)
     */
    advisor?: PersonBasicResponse | null;
    /**
     * Client Types
     *
     * Server-computed client-type classification, mirroring the users-table company-type taxonomy. Enterprise roles (search, investor, corporate, advisory) win over candidate linkage; a candidate-linked guest with no enterprise role is executive; a guest with neither is unclassified (empty list).
     */
    client_types?: Array<EventGuestClientType>;
};

/**
 * PotentialGuestSelectionKey
 *
 * Identity key for selecting a potential guest row.
 */
export type PotentialGuestSelectionKey = {
    /**
     * Source Type
     *
     * Source of the potential guest row
     */
    source_type: PotentialGuestSourceType;
    /**
     * Person UUID
     *
     * Person UUID for user rows (or candidate rows with linked person)
     */
    person_uuid?: string | null;
    /**
     * Candidate UUID
     *
     * Candidate UUID for candidate rows (or user rows with linked candidate)
     */
    candidate_uuid?: string | null;
    /**
     * Email
     *
     * Email identity for standalone event_guest rows
     */
    email?: string | null;
};

/**
 * PotentialGuestSortColumn
 *
 * Sort columns for potential guests search (event guest list).
 */
export const PotentialGuestSortColumn = {
    ADVISOR_NAME: 'advisor_name',
    CREATED: 'created',
    EVENTS_ATTENDED_COUNT: 'events_attended_count',
    EVENTS_INVITED_COUNT: 'events_invited_count',
    LAST_EVENT_DATE: 'last_event_date',
    NAME: 'name',
    EMAIL: 'email',
    SOURCE_TYPE: 'source_type',
    TIER: 'tier',
    UNDERREPRESENTED: 'underrepresented',
    WARM: 'warm'
} as const;

/**
 * PotentialGuestSortColumn
 *
 * Sort columns for potential guests search (event guest list).
 */
export type PotentialGuestSortColumn = typeof PotentialGuestSortColumn[keyof typeof PotentialGuestSortColumn];

/**
 * PotentialGuestSourceType
 *
 * Source of a potential guest in the pool.
 */
export const PotentialGuestSourceType = {
    CANDIDATE: 'candidate',
    EVENT_GUEST: 'event_guest',
    USER: 'user'
} as const;

/**
 * PotentialGuestSourceType
 *
 * Source of a potential guest in the pool.
 */
export type PotentialGuestSourceType = typeof PotentialGuestSourceType[keyof typeof PotentialGuestSourceType];

/**
 * PotentialGuestsAggregationsResponse
 *
 * Aggregation totals for potential guests filters.
 */
export type PotentialGuestsAggregationsResponse = {
    /**
     * Source Type
     *
     * Count per client/source type (user, candidate, event_guest)
     */
    source_type?: {
        [key: string]: number;
    };
    /**
     * Client Type
     *
     * Count per client type (executive, or enterprise org types: search/investor/corporate/advisory)
     */
    client_type?: {
        [key: string]: number;
    };
    /**
     * Tier
     *
     * Count per candidate tier (only rows with a tier)
     */
    tier?: {
        [key: string]: number;
    };
    /**
     * Candidate Type
     *
     * Count per candidate client type (tier 1 rows only)
     */
    candidate_type?: {
        [key: string]: number;
    };
    /**
     * Pathway
     *
     * Count per candidate pathway (tier 1 rows only)
     */
    pathway?: {
        [key: string]: number;
    };
    /**
     * Advisor
     *
     * Count per advisor UUID (only rows with an advisor, e.g. candidates)
     */
    advisor_id?: {
        [key: string]: number;
    };
    /**
     * Event UUIDs
     *
     * Count per event UUID for matching guests (a guest can contribute to multiple events)
     */
    event_uuids?: {
        [key: string]: number;
    };
    /**
     * Company IDs
     *
     * Count per company ID (team-based person_role company for matched guests)
     */
    company_ids?: {
        [key: string]: number;
    };
    /**
     * Has Attended Events
     *
     * Count by whether guest has attended at least one past event (true/false)
     */
    has_attended_events?: {
        [key: string]: number;
    };
    /**
     * Has Invited Events
     *
     * Count by whether guest has been invited to at least one event (true/false)
     */
    has_invited_events?: {
        [key: string]: number;
    };
    /**
     * Industry Expertise
     *
     * Count per industry expertise value (unnested; candidates only)
     */
    industry_expertise?: {
        [key: string]: number;
    };
    /**
     * Function Expertise
     *
     * Count per function expertise value (unnested; candidates only)
     */
    function_expertise?: {
        [key: string]: number;
    };
    /**
     * Operating Highlights
     *
     * Count per operating highlight value (unnested; candidates only)
     */
    operating_highlights?: {
        [key: string]: number;
    };
    /**
     * Interest Role Operating
     *
     * Count per interest role operating value (candidates only)
     */
    interest_role_operating?: {
        [key: string]: number;
    };
    /**
     * Interest Role Board
     *
     * Count per interest role board/advisory value (candidates only)
     */
    interest_role_board?: {
        [key: string]: number;
    };
    /**
     * Last Event
     *
     * Count per last-event bucket (1_month, 3_months, 6_months, 9_months, 12_plus)
     */
    last_event?: {
        [key: string]: number;
    };
};

/**
 * PotentialGuestsEmailsPayload
 *
 * Payload for resolving potential guest emails from search filters + selection mode.
 */
export type PotentialGuestsEmailsPayload = {
    /**
     * Phrase
     *
     * Search phrase (same behavior as potential guests search)
     */
    phrase?: string | null;
    /**
     * Filters
     *
     * Same filters as potential guests search endpoint
     */
    filter_data?: PotentialGuestsFilters | null;
    /**
     * Include inactive
     *
     * Same as potential guests search endpoint
     */
    include_inactive?: boolean;
    /**
     * Selection Mode
     *
     * 'explicit' selects included_items only; 'all_matching' selects all filtered rows except excluded_items
     */
    selection_mode?: 'explicit' | 'all_matching';
    /**
     * Included Items
     *
     * Selected rows when selection_mode is explicit
     */
    included_items?: Array<PotentialGuestSelectionKey>;
    /**
     * Excluded Items
     *
     * Excluded rows when selection_mode is all_matching
     */
    excluded_items?: Array<PotentialGuestSelectionKey>;
};

/**
 * PotentialGuestsEmailsResponse
 *
 * Selected potential guest rows for client-side email list assembly.
 */
export type PotentialGuestsEmailsResponse = {
    /**
     * Results
     *
     * Selected potential guest rows with minimal identity fields
     */
    results?: Array<PotentialGuestEmailItemResponse>;
    /**
     * Count
     *
     * Number of selected rows returned
     */
    count?: number;
};

/**
 * PotentialGuestsFilters
 *
 * Filters for potential guests search.
 */
export type PotentialGuestsFilters = {
    /**
     * Source Type
     *
     * Filter by client/source type (user, candidate, event_guest)
     */
    source_type?: Array<PotentialGuestSourceType> | null;
    /**
     * Client Type
     *
     * Filter by client type (executive, or enterprise org types: search/investor/corporate/advisory)
     */
    client_type?: Array<EventGuestClientType> | null;
    /**
     * Tier
     *
     * Filter by candidate tier (only applies to rows with a tier)
     */
    tier?: Array<CandidateTier> | null;
    /**
     * Candidate Type
     *
     * Filter by the candidate's Banff client type (tier 1 only). Not to be confused with client_type, the executive/enterprise split.
     */
    candidate_type?: Array<ClientType> | null;
    /**
     * Pathway
     *
     * Filter by the candidate's Banff pathway (tier 1 only)
     */
    pathway?: Array<CandidatePathway> | null;
    /**
     * Advisor
     *
     * Filter by advisor UUID (candidates only)
     */
    advisor_id?: Array<string> | null;
    /**
     * Event UUIDs
     *
     * Filter to guests who appear on any of the provided event guest lists (by event UUIDs)
     */
    event_uuids?: Array<string> | null;
    /**
     * Company IDs
     *
     * Filter by company IDs via person's team-based person_role.company_id
     */
    company_ids?: Array<number> | null;
    /**
     * Has Attended Events
     *
     * Filter by whether guest has attended at least one past event (true/false)
     */
    has_attended_events?: boolean | null;
    /**
     * Has Invited Events
     *
     * Filter by whether guest has been invited to at least one event (any status; true/false)
     */
    has_invited_events?: boolean | null;
    /**
     * Events Attended Count Min
     *
     * Minimum number of past events attended
     */
    events_attended_count_min?: number | null;
    /**
     * Events Attended Count Max
     *
     * Maximum number of past events attended
     */
    events_attended_count_max?: number | null;
    /**
     * Events Invited Count Min
     *
     * Minimum number of events invited to
     */
    events_invited_count_min?: number | null;
    /**
     * Events Invited Count Max
     *
     * Maximum number of events invited to
     */
    events_invited_count_max?: number | null;
    /**
     * Last Event Date After
     *
     * Filter guests whose last attended event was after this date
     */
    last_event_date_after?: string | null;
    /**
     * Last Event Date Before
     *
     * Filter guests whose last attended event was before this date
     */
    last_event_date_before?: string | null;
    /**
     * Industry Expertise
     *
     * Filter by industry expertise (array overlap; candidates only)
     */
    industry_expertise?: Array<Industry> | null;
    /**
     * Function Expertise
     *
     * Filter by function expertise (array overlap; candidates only)
     */
    function_expertise?: Array<Function> | null;
    /**
     * Operating Highlights
     *
     * Filter by operating highlights (array overlap; candidates only)
     */
    operating_highlights?: Array<OperatingHighlights> | null;
    /**
     * Interest Role Operating
     *
     * Filter by interest in operating roles (candidates only)
     */
    interest_role_operating?: Array<InterestRole> | null;
    /**
     * Interest Role Board
     *
     * Filter by interest in board/advisory roles (candidates only)
     */
    interest_role_board?: Array<InterestRole> | null;
    /**
     * Last Event
     *
     * Filter by when guest last attended (1 month, 3/6/9 months, 12+ ago)
     */
    last_event?: Array<LastEventBucket> | null;
};

/**
 * PotentialGuestsSearchPayload
 *
 * Payload for searching potential guests (users, candidates, event guests).
 */
export type PotentialGuestsSearchPayload = {
    /**
     * Phrase
     *
     * Search phrase
     */
    phrase?: string | null;
    /**
     * Search ID
     *
     * Search session ID
     */
    search_id?: string | null;
    /**
     * Page
     *
     * Page number
     */
    page?: number;
    /**
     * Page size
     *
     * Page size
     */
    page_size?: number;
    /**
     * Filters
     *
     * Filter by source_type, tier, advisor, event_uuids, company_ids, attendance, invitations, industry, function, operating_highlights, interest roles
     */
    filter_data?: PotentialGuestsFilters | null;
    /**
     * Sort
     *
     * Sort rules (default: name asc, email asc)
     */
    sort_rules?: Array<PotentialGuestsSortRule> | null;
    /**
     * Include inactive
     *
     * Also offer deactivated logins and inactive candidates. Merged candidates are never returned.
     */
    include_inactive?: boolean;
    /**
     * Include Total
     *
     * Include total count. Set false with include_aggregations=false for fastest response (results only); total_counts['all'] will be -1.
     */
    include_total?: boolean;
    /**
     * Include Aggregations
     *
     * Include aggregation totals (source_type, tier, advisor, has_attended_events, has_invited_events)
     */
    include_aggregations?: boolean;
    /**
     * Include Results
     *
     * Include the page of results. Set false to fetch only total and/or aggregations (e.g. in a separate parallel request).
     */
    include_results?: boolean;
};

/**
 * PotentialGuestsSearchResponse
 *
 * Paginated response for potential guests search with optional aggregations.
 */
export type PotentialGuestsSearchResponse = {
    /**
     * Results
     *
     * List of potential guests
     */
    results: Array<PotentialGuestItem>;
    /**
     * Page
     *
     * Current page number
     */
    page: number;
    /**
     * Page Count
     *
     * Total number of pages
     */
    page_count: number;
    /**
     * Size
     *
     * Page size
     */
    size: number;
    /**
     * Total Counts
     *
     * Total counts (e.g. all=total matching). all=-1 when include_total=false.
     */
    total_counts: {
        [key: string]: number;
    };
    /**
     * Aggregations
     *
     * Aggregation totals (only when include_aggregations=true)
     */
    aggregations?: PotentialGuestsAggregationsResponse | null;
};

/**
 * PotentialGuestsSortRule
 *
 * Single sort rule for potential guests.
 */
export type PotentialGuestsSortRule = {
    /**
     * Column
     *
     * Column to sort by
     */
    column: PotentialGuestSortColumn;
    /**
     * Direction
     *
     * Sort direction
     */
    direction?: SortDirection;
};

/**
 * PriorCompany
 *
 * Attributes
 * ----------
 * selected : bool
 * indicates whether or not this company should be presented to teams in the
 * user experience as part of the candidate's work history
 * company : str
 * the name of the company
 */
export type PriorCompany = {
    /**
     * Selected
     */
    selected?: boolean;
    /**
     * Company
     */
    company: string;
};

/**
 * PutHighlightResponse
 */
export type PutHighlightResponse = {
    /**
     * Highlight
     *
     * Text describing a candidate highlight
     */
    highlight: string;
    /**
     * Created
     *
     * Date when this highlight was created
     */
    created: string;
    /**
     * Last modified
     *
     * Date when this highlight was last modified
     */
    last_modified: string;
    /**
     * Hidden
     *
     * Is this highlight hidden?
     */
    hidden: boolean;
    /**
     * Soft delete
     *
     * [DEPRECATED] Use 'hidden' instead. Is this highlight soft deleted?
     *
     * @deprecated
     */
    soft_delete: boolean;
    /**
     * UUID
     *
     * UUID uniquely identifying a candidate highlight
     */
    uuid: string;
};

/**
 * PutIntroductionTemplatePayload
 */
export type PutIntroductionTemplatePayload = {
    /**
     * Body
     *
     * The body text associated with the introduction template
     */
    body: string;
    /**
     * Title
     *
     * The name of the introduction template, for reference
     */
    title: string;
};

/**
 * QueuedMessageResponse
 *
 * Response for endpoints that queue an email task (202 Accepted).
 */
export type QueuedMessageResponse = {
    /**
     * Message
     *
     * Human-readable status
     */
    message: string;
};

/**
 * RecommendationBioResponse
 */
export type RecommendationBioResponse = {
    /**
     * About
     *
     * Brief commentary about the candidate
     */
    about?: string | null;
    /**
     * Candidate
     *
     * Information about the candidate
     */
    candidate: CandidateShareResponse;
    /**
     * Highlights
     *
     * Selected candidate highlights
     */
    highlights: Array<string>;
    /**
     * Advisor
     *
     * Information about the advisor who shows up as the presenter
     */
    presenter?: PersonBasicResponse | null;
};

/**
 * RecommendationCandidateBasicResponse
 */
export type RecommendationCandidateBasicResponse = {
    /**
     * Candidate
     *
     * Information about the candidate
     */
    candidate: CandidateBasicResponse;
};

/**
 * RecommendationCandidatePipelinesResponse
 */
export type RecommendationCandidatePipelinesResponse = {
    /**
     * Candidate
     *
     * Information about the candidate
     */
    candidate: CandidateBasicResponse;
    /**
     * Pipeline
     *
     * Pipeline to which this recommendation has been published (null means it's Trending)
     */
    pipeline?: PipelineBasicV2Response | null;
    /**
     * UUID
     *
     * UUID uniquely identifying a recommendation
     */
    uuid: string;
};

/**
 * RecommendationCommentaryPayload
 */
export type RecommendationCommentaryPayload = {
    /**
     * About
     *
     * Brief commentary about the candidate
     */
    about?: string | null;
    /**
     * Highlights
     *
     * List of highlights published in the recommendation
     */
    highlights: Array<string>;
};

/**
 * RecommendationCommentsPayload
 */
export type RecommendationCommentsPayload = {
    /**
     * Comment
     *
     * Comment left about the recommendation
     */
    team_comments?: string | null;
};

/**
 * RecommendationDraftBasicResponse
 */
export type RecommendationDraftBasicResponse = {
    /**
     * UUID
     *
     * UUID uniquely identifying a recommendation
     */
    uuid: string;
    /**
     * Highlights
     *
     * Selected candidate highlights
     */
    highlight_uuids?: Array<string>;
    /**
     * Tailored highlights
     *
     * AI-drafted highlights written for this pipeline, as the advisor left them
     */
    tailored_highlights?: Array<TailoredHighlightResponse>;
    /**
     * Introduction template UUID
     *
     * UUID of the introduction template
     */
    introduction_template_uuid?: string | null;
    /**
     * Source
     *
     * Where the recommendation originated (e.g. `recsys` for RecSys/Brief Match suggestions, `manual` for advisor-created)
     */
    source?: RecommendationSource | null;
};

/**
 * RecommendationDraftMode
 */
export const RecommendationDraftMode = {
    APPROVED: 'approved',
    DENIED: 'denied',
    READY_FOR_REVIEW: 'ready_for_review',
    SUGGESTED: 'suggested',
    SAVED: 'saved'
} as const;

/**
 * RecommendationDraftMode
 */
export type RecommendationDraftMode = typeof RecommendationDraftMode[keyof typeof RecommendationDraftMode];

/**
 * RecommendationExternalFullResponse
 */
export type RecommendationExternalFullResponse = {
    /**
     * Status
     *
     * Introduction request status
     */
    introduction_status?: IntroductionStatus | null;
    /**
     * About
     *
     * Brief commentary about the candidate
     */
    about?: string | null;
    /**
     * Candidate
     *
     * Information about the candidate
     */
    candidate: CandidateExternalFullResponse;
    /**
     * Company
     *
     * Recommendation belongs to this company
     */
    company: CompanyBasicResponse;
    /**
     * Highlights
     *
     * Selected candidate highlights
     */
    highlights: Array<string>;
    /**
     * Introduction request person
     *
     * Person who requested the introduction
     */
    introduction_request_person?: PersonBasicResponse | null;
    /**
     * External person introduction info
     *
     * External person introduction info
     */
    external_introduction_request_person?: ExternalPerson | null;
    /**
     * Introduction email type
     *
     * Type of introduction email
     */
    intro_email_type: IntroEmailType;
    /**
     * Is liked?
     *
     * Does the user want more recommendations like this one?
     */
    is_liked?: boolean | null;
    /**
     * Pipeline
     *
     * Pipeline to which this recommendation has been published (null means it's Trending)
     */
    pipeline: PipelineBasicResponse | null;
    /**
     * Team
     *
     * Recommendation belongs to this team
     */
    team: TeamBasicResponse;
    /**
     * UUID
     *
     * UUID uniquely identifying a recommendation
     */
    uuid: string;
    /**
     * Draft mode
     *
     * Whether this recommendation is in draft mode
     */
    draft_mode?: RecommendationDraftMode | null;
    /**
     * Draft assignee
     *
     * Person assigned to this draft recommendation
     */
    draft_assignee?: PersonBasicResponse | null;
};

/**
 * RecommendationExternalIntroduction
 */
export type RecommendationExternalIntroduction = {
    /**
     * Person name
     *
     * Person name
     */
    name: string;
    /**
     * Company name of the person
     *
     * Person's company name
     */
    company_name: string;
    /**
     * Email address of the person
     *
     * Person's email
     */
    email: string;
};

/**
 * RecommendationFilterResult
 */
export type RecommendationFilterResult = {
    /**
     * Comments Count
     *
     * Comments count for the recommendation
     */
    comments_count: number;
    /**
     * Company to which this team belongs
     */
    company: CompanyExtendedResponse;
    /**
     * State in which the recommendation currently is
     */
    review_status: RecommendationReviewState;
    /**
     * Team information
     */
    team: RecommendationFilterTeam;
    /**
     * Recommendation status
     */
    status: RecommendationStatusOutput;
    /**
     * Published On
     *
     * Date when this recommendation was, or will be, published to the team
     */
    published_on: string | null;
    /**
     * Uuid
     *
     * Recommendation UUID
     */
    uuid: string;
};

/**
 * RecommendationFilterStatus
 */
export const RecommendationFilterStatus = {
    ALL: 'all',
    INTRO_REQUESTS: 'intro_requests',
    INTRO_COMPLETED: 'intro_completed',
    NOT_INTERESTING: 'not_interesting'
} as const;

/**
 * RecommendationFilterStatus
 */
export type RecommendationFilterStatus = typeof RecommendationFilterStatus[keyof typeof RecommendationFilterStatus];

/**
 * RecommendationFilterTeam
 */
export type RecommendationFilterTeam = {
    /**
     * Active
     *
     * Inactive (archived) teams keep their data but are hidden from search and recommendations and closed to their members
     */
    active?: boolean;
    /**
     * Slug
     *
     * Slug uniquely identifying a team
     */
    slug: string;
    /**
     * Name
     *
     * Team name
     */
    name: string;
    /**
     * UUID
     *
     * UUID uniquely identifying a team
     */
    uuid: string;
    /**
     * Additional details
     *
     * Additional details about the team
     */
    additional_details?: string | null;
    /**
     * Client size
     *
     * Typical client size
     */
    client_size?: SearchFirmType | null;
    /**
     * Company to which this team belongs
     */
    company: CompanyExtendedResponse;
    /**
     * Functional focus
     *
     * The roles and responsibilities the team typically searches for
     */
    functional_focus?: Array<Function> | null;
    /**
     * Has Preregistered Users
     *
     * True if any team members receive preregistration emails
     */
    has_preregistered_users?: boolean | null;
    /**
     * Industry focus
     *
     * Sectors the team typically focuses on
     */
    industry_focus?: Array<Industry> | null;
};

/**
 * RecommendationIntroEmailType
 */
export type RecommendationIntroEmailType = {
    /**
     * Introduction email type
     *
     * Introduction email type (automatic or manual)
     */
    intro_email_type: IntroEmailType;
};

/**
 * RecommendationIntroduction
 */
export type RecommendationIntroduction = {
    /**
     * Requested on behalf UUID
     *
     * Only advisors can request an introduction on behalf of team members
     */
    on_behalf_of_user_uuid?: string | null;
    /**
     * Already known
     *
     * Whether this recommendation was already known to the person reviewing it
     */
    already_known?: boolean | null;
    /**
     * Introduce
     *
     * Does the user want an introduction with the recommendation?
     */
    introduce: boolean;
    /**
     * External introduction
     *
     * External introduction object
     */
    external_introduction?: RecommendationExternalIntroduction | null;
    /**
     * List of email addresses to CC the introduction
     */
    intro_email_cc_list?: Array<string>;
    /**
     * Introduction email type (automatic or manual)
     *
     * Introduction email type (automatic or manual)
     */
    intro_email_type?: IntroEmailType;
    /**
     * Log intro
     *
     * Only log the introduction request, used for backfilling already made intros
     */
    log_only?: boolean | null;
    /**
     * Introduction comment
     *
     * Instructions or context provided for the introduction request
     */
    introduction_comment?: string | null;
};

/**
 * RecommendationIntroductionResponse
 */
export type RecommendationIntroductionResponse = {
    /**
     * Advisor
     *
     * Information about the advisor who responded to this introduction request
     */
    advisor?: PersonBasicResponse | null;
    /**
     * Date
     *
     * Date when an introduction was requested or rejected
     */
    date?: string | null;
    /**
     * Status context
     *
     * Additional context about the introduction status
     */
    status_context?: IntroductionStatusContext | null;
    /**
     * Resolved date
     *
     * Date when an advisor updated the introduction request status
     */
    resolved_date?: string | null;
    /**
     * Person
     *
     * Information about the person who requested an introduction with this recommendation
     */
    person?: PersonBasicResponse | null;
    /**
     * Status
     *
     * Introduction request status
     */
    introduction_status?: IntroductionStatus | null;
    /**
     * Already known
     *
     * Whether this recommendation was already known to the person reviewing it
     */
    already_known?: boolean | null;
    /**
     * Is liked?
     *
     * Does the user want more recommendations like this one?
     */
    is_liked?: boolean | null;
    /**
     * UUID
     *
     * UUID uniquely identifying a recommendation
     */
    uuid: string;
};

/**
 * RecommendationIsLiked
 */
export type RecommendationIsLiked = {
    /**
     * Is liked?
     *
     * Does the user want more recommendations like this one?
     */
    is_liked?: boolean | null;
};

/**
 * RecommendationOffLimits
 */
export type RecommendationOffLimits = {
    /**
     * Teams
     *
     * List of team slugs to check for off-limit restrictions
     */
    team_slugs: Array<string>;
};

/**
 * RecommendationPipelinesResponse
 */
export type RecommendationPipelinesResponse = {
    /**
     * Created
     *
     * Date when this recommendation was published to the team
     */
    published_on: string;
    /**
     * Is liked?
     *
     * Does the user want more recommendations like this one?
     */
    is_liked?: boolean | null;
    /**
     * Pipeline
     *
     * Pipeline to which this recommendation has been published (null means it's Trending)
     */
    pipeline?: PipelineBasicV2Response | null;
    /**
     * Review status
     *
     * State in which the recommendation currently is
     */
    review_status: RecommendationReviewState;
    /**
     * UUID
     *
     * UUID uniquely identifying a recommendation
     */
    uuid: string;
    /**
     * Search team
     *
     * Search team
     */
    team?: TeamBasicUuidResponse | null;
};

/**
 * RecommendationPreviewResponse
 *
 * Preview/guest version of a recommendation with limited fields for unauthenticated access.
 */
export type RecommendationPreviewResponse = {
    /**
     * Candidate
     *
     * Basic candidate information
     */
    candidate: CandidateBasicResponse;
    /**
     * UUID
     *
     * UUID uniquely identifying a recommendation
     */
    uuid: string;
};

/**
 * RecommendationPublishedNotificationType
 */
export const RecommendationPublishedNotificationType = {
    BATCH: 'batch',
    STREAM: 'stream',
    STREAM_PLAIN: 'stream_plain',
    STREAM_PLAIN_V2: 'stream_plain_v2'
} as const;

/**
 * RecommendationPublishedNotificationType
 */
export type RecommendationPublishedNotificationType = typeof RecommendationPublishedNotificationType[keyof typeof RecommendationPublishedNotificationType];

/**
 * RecommendationReviewState
 */
export const RecommendationReviewState = {
    INTERESTING: 'interesting',
    INTRO_MADE: 'intro_made',
    INTRO_REQUESTED: 'intro_requested',
    INTRO_UNAVAILABLE: 'intro_unavailable',
    NEEDS_REVIEW: 'needs_review',
    NOT_INTERESTING: 'not_interesting'
} as const;

/**
 * RecommendationReviewState
 */
export type RecommendationReviewState = typeof RecommendationReviewState[keyof typeof RecommendationReviewState];

/**
 * RecommendationSource
 *
 * Where a recommendation originated.
 *
 * MANUAL - created by an advisor in Contender (default for all existing recs)
 * CLAUDE - created by an AI-assisted flow (reserved)
 * RECSYS - pushed in from the RecSys (Brief Match) app
 */
export const RecommendationSource = {
    MANUAL: 'manual',
    CLAUDE: 'claude',
    RECSYS: 'recsys'
} as const;

/**
 * RecommendationSource
 *
 * Where a recommendation originated.
 *
 * MANUAL - created by an advisor in Contender (default for all existing recs)
 * CLAUDE - created by an AI-assisted flow (reserved)
 * RECSYS - pushed in from the RecSys (Brief Match) app
 */
export type RecommendationSource = typeof RecommendationSource[keyof typeof RecommendationSource];

/**
 * RecommendationStatus
 */
export type RecommendationStatusInput = {
    /**
     * Skip notifications
     *
     * If true, this action will not generate any notification events
     */
    skip_notifications?: boolean;
    /**
     * Introduced
     *
     * Did the advisor introduce the candidate to the team?
     */
    introduced: boolean;
    /**
     * Reason
     *
     * Reason why an advisor wasn't able to facilitate an introduction
     */
    reason?: RejectionReasonEnum | null;
};

/**
 * RecommendationStatus
 */
export const RecommendationStatusOutput = {
    DRAFT: 'draft',
    PUBLISHED: 'published',
    SCHEDULED: 'scheduled'
} as const;

/**
 * RecommendationStatus
 */
export type RecommendationStatusOutput = typeof RecommendationStatusOutput[keyof typeof RecommendationStatusOutput];

/**
 * RecommendationStatusFilter
 */
export const RecommendationStatusFilter = { NEVER_RECOMMENDED: 'never_recommended', PREVIOUSLY_RECOMMENDED: 'previously_recommended' } as const;

/**
 * RecommendationStatusFilter
 */
export type RecommendationStatusFilter = typeof RecommendationStatusFilter[keyof typeof RecommendationStatusFilter];

/**
 * RecommendationType
 */
export const RecommendationType = { TRENDINGS: 'trendings', PIPELINES: 'pipelines' } as const;

/**
 * RecommendationType
 */
export type RecommendationType = typeof RecommendationType[keyof typeof RecommendationType];

/**
 * RecommendationsFilter
 */
export const RecommendationsFilter = {
    ALL: 'all',
    INTRO: 'intro',
    INTRODUCTION: 'introduction',
    NO_INTRO: 'noIntro',
    PIPELINE: 'pipeline',
    SEARCH: 'search',
    VIEWED: 'viewed'
} as const;

/**
 * RecommendationsFilter
 */
export type RecommendationsFilter = typeof RecommendationsFilter[keyof typeof RecommendationsFilter];

/**
 * RecommendationsHistoryRecommendation
 */
export type RecommendationsHistoryRecommendation = {
    /**
     * Advisor
     *
     * Information about the advisor who responded to this introduction request
     */
    advisor?: PersonBasicResponse | null;
    /**
     * Date
     *
     * Date when an introduction was requested
     */
    date?: string | null;
    /**
     * Status context
     *
     * Additional context about the introduction status
     */
    status_context?: IntroductionStatusContext | null;
    /**
     * Resolved date
     *
     * Date when an advisor updated the introduction request status
     */
    resolved_date?: string | null;
    /**
     * Person
     *
     * Information about the person who requested an introduction with this recommendation
     */
    person?: PersonBasicResponse | null;
    /**
     * Status
     *
     * Introduction request status
     */
    status?: IntroductionStatus | null;
    /**
     * Type
     *
     * Defines in which context was this recommendation published.
     */
    type: IntroductionType;
    /**
     * Created
     *
     * Date when this recommendation was published to the team
     */
    created: string;
    /**
     * Pipeline
     *
     * Information about pipeline where this candidate was submitted
     */
    pipeline?: PipelineBasicResponse | null;
    /**
     * Team
     *
     * Team which received the recommendation
     */
    team: TeamBasicResponse;
};

/**
 * RecommendationsHistoryResponse
 */
export type RecommendationsHistoryResponse = {
    /**
     * Candidate
     *
     * Information about the candidate for which we're viewing recommendations history
     */
    candidate: CandidateBasicResponse;
    /**
     * Recommendations
     *
     * List of recommendation objects for the recommendations history
     */
    recommendations: Array<RecommendationsHistoryRecommendation>;
};

/**
 * RecommendationsSortColumn
 */
export const RecommendationsSortColumn = {
    INTRO_MADE_ON: 'intro_made_on',
    INTRO_REQUESTED_ON: 'intro_requested_on',
    NOT_INTERESTED_ON: 'not_interested_on',
    PUBLISHED_ON: 'published_on'
} as const;

/**
 * RecommendationsSortColumn
 */
export type RecommendationsSortColumn = typeof RecommendationsSortColumn[keyof typeof RecommendationsSortColumn];

/**
 * RecommendationsSorter
 */
export const RecommendationsSorter = {
    ADVISOR: 'advisor',
    ALREADY_KNOWN: 'already_known',
    COMMENTS: 'comments',
    COMPANY_NAME: 'company_name',
    COMPANY_TYPE: 'company_type',
    '': '',
    NAME: 'name',
    INTRODUCTION_DATE: 'introduction_date',
    INTRODUCTION_FACILITATED: 'introduction_facilitated',
    INTRODUCTION_REQUESTED: 'introduction_requested',
    INTRODUCTION_STATUS: 'introduction_status',
    REVIEW_STATUS: 'review_status',
    SENT_DATE: 'sent_date',
    TEAM: 'team',
    TEAM_MEMBER: 'team_member',
    TYPE: 'type',
    VIEWED_DATE: 'viewed_date'
} as const;

/**
 * RecommendationsSorter
 */
export type RecommendationsSorter = typeof RecommendationsSorter[keyof typeof RecommendationsSorter];

/**
 * RecsysAssignee
 */
export type RecsysAssignee = {
    /**
     * Person Uuid
     *
     * Advisor's Contender Person id
     */
    person_uuid: string;
    /**
     * Name
     *
     * Advisor's display name
     */
    name: string;
};

/**
 * RecsysAssigneesResponse
 */
export type RecsysAssigneesResponse = {
    /**
     * Assignees
     *
     * Advisors (APPADMIN) in this environment a suggestion can be assigned to
     */
    assignees?: Array<RecsysAssignee>;
};

/**
 * RecsysCandidateContextPair
 */
export type RecsysCandidateContextPair = {
    /**
     * Candidate UUID
     *
     * Candidate to fetch context for
     */
    candidate_uuid: string;
    /**
     * Pipeline UUID
     *
     * Pipeline whose search team the context is scoped to
     */
    pipeline_uuid: string;
};

/**
 * RecsysCandidateContextRequest
 */
export type RecsysCandidateContextRequest = {
    /**
     * Pairs
     *
     * (candidate, pipeline) pairs to fetch team-relationship context for. Warmth and last-action are scoped to each pipeline's search team; a candidate may appear against many pipelines (e.g. an opportunity queue) and vice versa (a pipeline's candidate slate) in one request.
     */
    pairs: Array<RecsysCandidateContextPair>;
};

/**
 * RecsysCandidateContextResponse
 */
export type RecsysCandidateContextResponse = {
    /**
     * Results
     *
     * Team-relationship context, one entry per resolved (candidate, pipeline) pair
     */
    results?: Array<RecsysCandidateTeamContext>;
    /**
     * Not Found Pipelines
     *
     * Requested pipeline UUIDs that do not resolve to a pipeline
     */
    not_found_pipelines?: Array<string>;
    /**
     * Not Found Candidates
     *
     * Requested candidate UUIDs that do not resolve to a candidate
     */
    not_found_candidates?: Array<string>;
};

/**
 * RecsysCandidateOpportunitiesResponse
 */
export type RecsysCandidateOpportunitiesResponse = {
    /**
     * Total
     *
     * Opportunities in the exec's whole queue
     */
    total?: number;
    /**
     * Already Recommended Count
     *
     * How many of the queue's pipelines the exec is already on (across the whole queue, not just this page)
     */
    already_recommended_count?: number;
    /**
     * Items
     *
     * Hydrated opportunities for this page
     */
    items?: Array<RecsysCandidateOpportunityItem>;
};

/**
 * RecsysCandidateOpportunityItem
 */
export type RecsysCandidateOpportunityItem = {
    /**
     * Hydrated Contender pipeline
     */
    pipeline: RecsysOpportunityPipeline;
    /**
     * Rank
     *
     * Position in the exec's whole ranked queue (1 = best fit), absolute across pages
     */
    rank: number;
    /**
     * Fit Score
     *
     * RecSys rubric fit score (0–100). Null when the pair hasn't been scored yet — those rank last
     */
    fit_score?: number | null;
    /**
     * Which RecSys feed(s) surfaced this opportunity
     */
    provenance?: RecsysOpportunityProvenance;
    /**
     * Recommendation Id
     *
     * RecSys recommendation id — pass to the feedback endpoint to record Good/Bad. Null for opportunities that came from the candidate-side sweep rather than a pipeline's own run
     */
    recommendation_id?: string | null;
    /**
     * Rationale
     *
     * Why this exec fits this search: {headline, bullets, caution, sources} (object), or a raw string. Present when RecSys has already written one; null otherwise — fetch it from the rationale endpoint, which generates on demand
     */
    rationale?: unknown | null;
    /**
     * Has Rationale
     *
     * RecSys already has a written 'why' for this pair — so `rationale` above is populated. When false, generating one costs a model call
     */
    has_rationale?: boolean;
    /**
     * Reviewed
     *
     * An advisor has already acted on this pair in RecSys
     */
    reviewed?: boolean;
    /**
     * Assessment
     *
     * The advisor's verdict on this pair, e.g. 'bad_match'; null when they have only marked it reviewed
     */
    assessment?: string | null;
    /**
     * Warm
     *
     * This exec is warm to the pipeline's search team — an exec/former-exec client, a recent advisor-network profile, or a facilitated intro with this team
     */
    warm?: boolean;
    /**
     * Most significant thing that has happened between this exec and the pipeline's team, with its date. Null means no team activity
     */
    last_action?: PipelineCandidateTeamStatus | null;
    /**
     * Already Recommended
     *
     * The exec is already on this pipeline in Contender (disable quick add)
     */
    already_recommended?: boolean;
    /**
     * Recommendation Uuid
     *
     * Contender recommendation uuid when the exec is already on this pipeline; null otherwise
     */
    recommendation_uuid?: string | null;
    /**
     * Current status of the existing recommendation; null when the exec is not on the pipeline
     */
    pipeline_status?: CandidatePipelineStatus | null;
    /**
     * Person assigned to the existing draft recommendation
     */
    draft_assignee?: PersonBasicResponse | null;
};

/**
 * RecsysCandidateSuggestion
 */
export type RecsysCandidateSuggestion = {
    /**
     * Candidate UUID
     *
     * Candidate to add as a `suggested` draft recommendation
     */
    candidate_uuid: string;
    /**
     * Assignee Person UUID
     *
     * Contender Person to assign the draft recommendation to. Must be an advisor. Defaults to the author when omitted.
     */
    assignee_person_uuid?: string | null;
};

/**
 * RecsysCandidateTeamContext
 */
export type RecsysCandidateTeamContext = {
    /**
     * Candidate Uuid
     *
     * Candidate this context is for
     */
    candidate_uuid: string;
    /**
     * Pipeline Uuid
     *
     * Pipeline the pair was requested against
     */
    pipeline_uuid: string;
    /**
     * Team Uuid
     *
     * Search team the warmth / last-action are scoped to (the pipeline's team)
     */
    team_uuid: string;
    /**
     * Warm
     *
     * Whether the candidate is warm to the pipeline's search team (exec/former-exec client, recent advisor-network profile, or a facilitated intro with no 'not facilitated' outcome)
     */
    warm: boolean;
    /**
     * Most significant action between the candidate and the team (a facilitated introduction, else being added to a recommendation), with its date. Null when there is no recorded action.
     */
    last_action?: PipelineCandidateTeamStatus | null;
    /**
     * Referrers
     *
     * People or candidates who referred this candidate to Banff. Candidate-level (who vouches for them), not team-scoped.
     */
    referrers?: Array<ReferrerBasicResponse>;
};

/**
 * RecsysFeedbackPayload
 */
export type RecsysFeedbackPayload = {
    /**
     * Verdict
     *
     * Good (thumbs-up) or Bad (thumbs-down) on a RecSys recommendation
     */
    verdict: 'good' | 'bad';
    /**
     * Note
     *
     * Optional free-text note accompanying the feedback
     */
    note?: string | null;
};

/**
 * RecsysOpportunityBadMatchPayload
 */
export type RecsysOpportunityBadMatchPayload = {
    /**
     * Comment
     *
     * Why this pipeline is a bad fit for the exec. Feeds the engine's training signal, so it is worth writing
     */
    comment?: string | null;
};

/**
 * RecsysOpportunityPipeline
 */
export type RecsysOpportunityPipeline = {
    /**
     * Uuid
     *
     * Contender pipeline id
     */
    uuid: string;
    /**
     * Role
     *
     * Role the pipeline is searching for
     */
    role: string;
    /**
     * Slug
     *
     * Slug uniquely identifying a pipeline
     */
    slug: string;
    /**
     * Company
     *
     * Company the pipeline is open for
     */
    company?: string | null;
    /**
     * Pipeline type
     */
    pipeline_type?: PipelineType | null;
    /**
     * Created
     *
     * When the pipeline was opened
     */
    created?: string | null;
    /**
     * What kind of org the search team belongs to (corporate, investor, search firm) — shown as a badge so an advisor can read the pipeline's world at a glance
     */
    company_type?: OrgType | null;
    /**
     * Notes
     *
     * The search's additional details — target companies, benchmark profiles, and anything else the team wrote to calibrate recommendations. The part of a pipeline that says what the role title cannot
     */
    notes?: string | null;
    /**
     * Function Expertise
     *
     * Functions the ideal candidate has experience in. Always present; empty when the search carries no tags
     */
    function_expertise: Array<Function>;
    /**
     * Industry Expertise
     *
     * Industries the ideal candidate has experience in. Always present; empty when the search carries no tags
     */
    industry_expertise: Array<Industry>;
    /**
     * Search team running the pipeline
     */
    team: RecsysOpportunityTeam;
};

/**
 * RecsysOpportunityProvenance
 */
export type RecsysOpportunityProvenance = {
    /**
     * Inbound
     *
     * A pipeline's own RecSys run ranked this exec highly
     */
    inbound?: boolean;
    /**
     * Outbound
     *
     * RecSys's candidate-side sweep scored this pipeline a good fit for the exec
     */
    outbound?: boolean;
    /**
     * Manual
     *
     * Surfaced by an ad-hoc search someone ran
     */
    manual?: boolean;
    /**
     * Mutual
     *
     * Both directions agree — the strongest signal
     */
    mutual?: boolean;
};

/**
 * RecsysOpportunityRationaleResponse
 */
export type RecsysOpportunityRationaleResponse = {
    /**
     * Rationale
     *
     * Why this exec fits this search: {headline, bullets, caution, sources} (object), or a raw string / null when RecSys couldn't produce one
     */
    rationale?: unknown | null;
};

/**
 * RecsysOpportunityReviewPayload
 */
export type RecsysOpportunityReviewPayload = {
    /**
     * Reviewed
     *
     * True marks the pair reviewed; False clears it, which on the RecSys side fully resets the pair — the bad-match verdict and comment go with it
     */
    reviewed?: boolean;
};

/**
 * RecsysOpportunityTeam
 */
export type RecsysOpportunityTeam = {
    /**
     * Uuid
     *
     * Search team the pipeline belongs to
     */
    uuid: string;
    /**
     * Name
     *
     * Search team display name
     */
    name: string;
    /**
     * Slug
     *
     * Search team slug
     */
    slug: string;
    /**
     * Company
     *
     * Name of the company the search team belongs to — shown alongside the team name in the pipeline identity cell, like the Pipelines table
     */
    company?: string | null;
};

/**
 * RecsysPipelineRecommendationItem
 */
export type RecsysPipelineRecommendationItem = {
    /**
     * Hydrated Contender candidate
     */
    candidate: RecsysRecommendationCandidate;
    /**
     * Recommendation Id
     *
     * RecSys recommendation id — pass to the feedback endpoint to record Good/Bad on this recommendation
     */
    recommendation_id?: string | null;
    /**
     * Rank
     *
     * RecSys rank within the run (1 = best; global across tiers)
     */
    rank?: number | null;
    /**
     * Tier
     *
     * RecSys tier group: 'tier1' | 'tier3'
     */
    tier?: string | null;
    /**
     * Score
     *
     * RecSys fit score (0–100)
     */
    score?: number | null;
    /**
     * Reasons
     *
     * Engine provenance notes (RRF / rerank)
     */
    reasons?: Array<string>;
    /**
     * Cautions
     *
     * Engine caution notes
     */
    cautions?: Array<string>;
    /**
     * Rationale
     *
     * Advisor-facing rationale: {headline, bullets, caution, sources} (object), or a raw string / null when absent
     */
    rationale?: unknown | null;
    /**
     * Already On Pipeline
     *
     * Candidate is already recommended on this pipeline (disable 'Add as draft')
     */
    already_on_pipeline?: boolean;
    /**
     * Recommendation Uuid
     *
     * Contender recommendation uuid when the candidate is already on this pipeline (drives the status/assignee dropdowns); null otherwise
     */
    recommendation_uuid?: string | null;
    /**
     * Current pipeline status of the existing recommendation; null when the candidate is not on the pipeline
     */
    pipeline_status?: CandidatePipelineStatus | null;
    /**
     * Person assigned to the existing draft recommendation
     */
    draft_assignee?: PersonBasicResponse | null;
};

/**
 * RecsysPipelineRecommendationsResponse
 */
export type RecsysPipelineRecommendationsResponse = {
    /**
     * Total
     *
     * Total candidate recommendations in the RecSys run
     */
    total?: number;
    /**
     * Computed At
     *
     * When the RecSys run was computed (ISO 8601)
     */
    computed_at?: string | null;
    /**
     * Algorithm Version
     *
     * RecSys algorithm version that produced the run
     */
    algorithm_version?: string | null;
    /**
     * Run Id
     *
     * RecSys run id
     */
    run_id?: string | null;
    /**
     * Already On Pipeline Count
     *
     * How many of the run's candidates are already recommended on this pipeline (across the whole run, not just this page)
     */
    already_on_pipeline_count?: number;
    /**
     * Items
     *
     * Hydrated recommendations for this page
     */
    items?: Array<RecsysPipelineRecommendationItem>;
};

/**
 * RecsysRecommendationCandidate
 */
export type RecsysRecommendationCandidate = {
    /**
     * Uuid
     *
     * Contender candidate id
     */
    uuid: string;
    /**
     * Name
     *
     * Candidate display name
     */
    name?: string | null;
    /**
     * Formatted Role
     *
     * Current role + company, e.g. 'CRO, AppFolio'
     */
    formatted_role?: string | null;
    /**
     * Current Role
     *
     * Candidate's current role
     */
    current_role?: string | null;
    /**
     * Current Company
     *
     * Candidate's current company
     */
    current_company?: string | null;
    /**
     * Candidate's Contender tier (CandidateTier value, e.g. `executive_client`)
     */
    tier?: CandidateTier | null;
    /**
     * Profile Image Url
     *
     * Signed profile image URL
     */
    profile_image_url?: string | null;
};

/**
 * RecsysSuggestedRecommendations
 */
export type RecsysSuggestedRecommendations = {
    /**
     * Pipeline UUID
     *
     * Contender pipeline the candidates are suggested for
     */
    pipeline_uuid: string;
    /**
     * Candidates
     *
     * Candidates to add as `suggested` draft recommendations, each with an optional per-candidate assignee
     */
    candidates: Array<RecsysCandidateSuggestion>;
    /**
     * Author Person UUID
     *
     * Contender Person of the advisor sending from RecSys; recorded as the actor for the created suggestions
     */
    author_person_uuid: string;
    /**
     * Comment
     *
     * Optional rationale stored as an internal comment per suggestion
     */
    comment?: string | null;
    /**
     * Draft mode
     *
     * Draft mode for the created recommendations. Defaults to `ready_for_review` when omitted (the interactive add-to-Contender behaviour); the scheduled auto-seed of new pipelines passes `suggested`.
     */
    draft_mode?: RecommendationDraftMode | null;
    /**
     * Autosuggested
     *
     * True when autosuggestion produced these recommendations, false when an advisor picked them. The endpoint cannot tell the two apart — both arrive on the same service key — so the caller must say which it is. Recorded per recommendation alongside `source = recsys`.
     */
    autosuggested?: boolean;
};

/**
 * RecsysSuggestedRecommendationsResponse
 */
export type RecsysSuggestedRecommendationsResponse = {
    /**
     * Created
     *
     * Candidates added as new `suggested` draft recommendations
     */
    created?: Array<string>;
    /**
     * Skipped
     *
     * Candidates already present on the pipeline (no-op)
     */
    skipped?: Array<string>;
    /**
     * Not Found
     *
     * Candidate UUIDs that do not resolve to a candidate
     */
    not_found?: Array<string>;
    /**
     * Off Limits
     *
     * Candidates off-limits to the pipeline's search team; no draft was created for them
     */
    off_limits?: Array<string>;
};

/**
 * ReferrerBasicResponse
 */
export type ReferrerBasicResponse = {
    /**
     * Image
     *
     * Path to person image
     */
    image?: string | null;
    /**
     * Name
     *
     * Referrer's full name
     */
    name: string;
    /**
     * UUID
     *
     * The referrer's identity uuid; post it back as-is to keep the referrer
     */
    uuid: string;
};

/**
 * RefreshTeamInsightsResponse
 *
 * Response schema for the refresh_team_insights job.
 *
 * The route queues work rather than doing it: the sweep makes LLM calls per stale
 * team, which does not belong in a scheduler's HTTP call. The refreshed/failed
 * counts are logged and returned as the Celery task result, keyed by `task_id`.
 */
export type RefreshTeamInsightsResponse = {
    /**
     * Job
     *
     * Name of the queued Celery task
     */
    job: string;
    /**
     * Task Id
     *
     * Celery task id, or null if the broker refused the message
     */
    task_id?: string | null;
    /**
     * Limit
     *
     * Max stale teams this run will recompute (null = no cap)
     */
    limit?: number | null;
};

/**
 * ReindexAsyncResponse
 *
 * Response for async reindex operations.
 */
export type ReindexAsyncResponse = {
    /**
     * Task Id
     */
    task_id: string;
    /**
     * Status
     */
    status: string;
    /**
     * Message
     */
    message: string;
    /**
     * Status Url
     */
    status_url: string;
};

/**
 * ReindexMetadata
 */
export type ReindexMetadata = {
    /**
     * Success
     */
    success: number;
    /**
     * Failed
     */
    failed: number;
    /**
     * Time Taken
     */
    time_taken: string;
    /**
     * Error
     */
    error?: string | null;
};

/**
 * ReindexStatusResponse
 *
 * Response for reindex status checks.
 *
 * Fields are populated based on task state:
 * - PENDING: Only task_id, state, message
 * - PROGRESS: task_id, state, phase, index, index_alias, message
 * - SUCCESS: task_id, state, message, result
 * - FAILURE: task_id, state, message, error
 */
export type ReindexStatusResponse = {
    /**
     * Task Id
     */
    task_id: string;
    /**
     * State
     */
    state: string;
    /**
     * Phase
     */
    phase?: string | null;
    /**
     * Index
     */
    index?: string | null;
    /**
     * Index Alias
     */
    index_alias?: string | null;
    /**
     * Message
     */
    message?: string | null;
    result?: ElasticsearchReindexResponse | null;
    /**
     * Error
     */
    error?: string | null;
};

/**
 * RejectionReasonEnum
 */
export const RejectionReasonEnum = { NO_INTEREST: 'noInterest', UNREACHABLE: 'unreachable' } as const;

/**
 * RejectionReasonEnum
 */
export type RejectionReasonEnum = typeof RejectionReasonEnum[keyof typeof RejectionReasonEnum];

/**
 * ReminderPreviewPayload
 *
 * Optional payload for sending reminder (nudge) preview. Omit email to use current user.
 */
export type ReminderPreviewPayload = {
    /**
     * Email
     *
     * Send preview to this email. Omit to use the current user's email.
     */
    email?: string | null;
};

/**
 * RequestSource
 *
 * Where an intro request originated, from the evaluation_history event linkage.
 */
export const RequestSource = {
    PIPELINE: 'pipeline',
    TRENDING: 'trending',
    GLOBAL: 'global'
} as const;

/**
 * RequestSource
 *
 * Where an intro request originated, from the evaluation_history event linkage.
 */
export type RequestSource = typeof RequestSource[keyof typeof RequestSource];

/**
 * ResolutionResponse
 */
export type ResolutionResponse = {
    target_company: ResolvedReferenceResponse;
    search_firm: ResolvedReferenceResponse;
    search_team: ResolvedReferenceResponse;
    client: ResolvedReferenceResponse;
    search_contact: ResolvedReferenceResponse;
};

/**
 * ResolvePersonCandidateByEmailRequest
 */
export type ResolvePersonCandidateByEmailRequest = {
    /**
     * Emails
     *
     * Emails to resolve to person_uuid and/or candidate_uuid.
     */
    emails: Array<string>;
};

/**
 * ResolvePersonCandidateByEmailResponse
 */
export type ResolvePersonCandidateByEmailResponse = {
    /**
     * Results
     */
    results: Array<ResolvePersonCandidateByEmailResult>;
};

/**
 * ResolvePersonCandidateByEmailResult
 */
export type ResolvePersonCandidateByEmailResult = {
    /**
     * Email
     *
     * Resolved input email (lowercased).
     */
    email: string;
    /**
     * Person Uuid
     *
     * UUID of an existing Person for this email (if present).
     */
    person_uuid?: string | null;
    /**
     * Candidate Uuid
     *
     * UUID of an existing ContenderCandidate for this email (if present).
     */
    candidate_uuid?: string | null;
    /**
     * Basic person fields for UI when a person resolves.
     */
    person?: PersonBasicResponse | null;
    /**
     * Basic candidate fields for UI when a candidate resolves.
     */
    candidate?: CandidateBasicResponse | null;
    /**
     * Person Active
     *
     * Whether the resolved person's login is active (false: archived).
     */
    person_active?: boolean | null;
    /**
     * Identity Uuid
     *
     * The human the email belongs to: the resolved person's identity, else the candidate's.
     */
    identity_uuid?: string | null;
};

/**
 * ResolvedReferenceResponse
 */
export type ResolvedReferenceResponse = {
    /**
     * Given
     */
    given: string | null;
    /**
     * Resolved
     */
    resolved: boolean;
    /**
     * Resolved Id
     */
    resolved_id: number | string | null;
    /**
     * Resolved Name
     */
    resolved_name: string | null;
};

/**
 * ResponseType
 */
export const ResponseType = {
    INTRO_REQUESTS: 'intro_requests',
    INTRO_COMPLETED: 'intro_completed',
    NOT_INTERESTING: 'not_interesting',
    NO_ACTION: 'no_action'
} as const;

/**
 * ResponseType
 */
export type ResponseType = typeof ResponseType[keyof typeof ResponseType];

/**
 * ResumeHeader
 */
export type ResumeHeader = {
    /**
     * Candidate Name
     */
    candidate_name?: string | null;
    /**
     * First Name
     */
    first_name?: string | null;
    /**
     * Last Name
     */
    last_name?: string | null;
    /**
     * Location
     */
    location: string;
    /**
     * Title
     */
    title: string;
};

/**
 * RevenueRange
 */
export const RevenueRange = {
    '<_10M': '<$10M',
    $10M_100M: '$10M-$100M',
    $100M_500M: '$100M-$500M',
    '>_500M': '>$500M'
} as const;

/**
 * RevenueRange
 */
export type RevenueRange = typeof RevenueRange[keyof typeof RevenueRange];

/**
 * Role
 */
export const Role = {
    USED_FOR_NOTIFICATIONS_APPLICABLE_TO_ALL_PEOPLE: 'Used For Notifications Applicable to All People',
    UNASSIGNED_TEAM_MEMBER: 'Unassigned Team Member',
    BANFF_ADMIN: 'Banff Admin',
    BANFF_CANDIDATE: 'Banff Candidate',
    BANFF_CLIENT: 'Banff Client',
    EXECUTIVE_CLIENT: 'Executive Client',
    EXECUTIVE_TEAM_LEAD: 'Executive Team Lead',
    INVESTOR_TEAM_LEAD: 'Investor Team Lead',
    INVESTOR_TEAM_MEMBER: 'Investor Team Member',
    BANFF_APPLICANT: 'Banff Applicant',
    SEARCH_TEAM_LEAD: 'Search Team Lead',
    SEARCH_TEAM_MEMBER: 'Search Team Member',
    CORPORATE_TEAM_LEAD: 'Corporate Team Lead',
    CORPORATE_TEAM_MEMBER: 'Corporate Team Member'
} as const;

/**
 * Role
 */
export type Role = typeof Role[keyof typeof Role];

/**
 * RsvpPreviewPayload
 *
 * Payload for POST RSVP preview: send confirmation email preview to the current user.
 */
export type RsvpPreviewPayload = {
    /**
     * Status
     *
     * Preview email for this status (going or cant_go).
     */
    status: 'going' | 'cant_go';
};

/**
 * RsvpUpdatePayload
 *
 * Payload for PATCH RSVP: token and new status.
 */
export type RsvpUpdatePayload = {
    /**
     * Token
     *
     * Event RSVP JWT from the invite link
     */
    token: string;
    /**
     * Status
     *
     * New RSVP status (going or cant_go)
     */
    status: 'going' | 'cant_go';
};

/**
 * SchoolResponse
 */
export type SchoolResponse = {
    /**
     * Degree
     *
     * Name of degree
     */
    degree?: string | null;
    /**
     * Name
     *
     * Name of school
     */
    name?: string | null;
    /**
     * Dates
     *
     * Period during which person attended school
     */
    dates?: string | null;
};

/**
 * SchoolUploadResponse
 */
export type SchoolUploadResponse = {
    /**
     * Degree
     *
     * Name of degree
     */
    degree?: string | null;
    /**
     * Name
     *
     * Name of school
     */
    name?: string | null;
    /**
     * Dates
     *
     * Period during which person attended school
     */
    dates?: string | null;
};

/**
 * SearchFirmType
 */
export const SearchFirmType = {
    BOTH_EARLY_STAGE_AND_LARGE_ESTABLISHED: 'Both Early Stage and Large/Established',
    MOSTLY_EARLY_STAGE: 'Mostly Early Stage',
    MOSTLY_LARGE_ESTABLISHED: 'Mostly Large/Established'
} as const;

/**
 * SearchFirmType
 */
export type SearchFirmType = typeof SearchFirmType[keyof typeof SearchFirmType];

/**
 * SearchIndex
 */
export const SearchIndex = {
    CANDIDATES: 'candidates',
    COMPANIES: 'companies',
    PEERS: 'peers',
    PEOPLE: 'people',
    PIPELINES: 'pipelines',
    RECOMMENDATION_CANDIDATES: 'recommendation-candidates',
    RECOMMENDATIONS: 'recommendations',
    SUBMISSIONS: 'submissions',
    TASKS: 'tasks',
    TEAM_CANDIDATES: 'team-candidates',
    TEAMS: 'teams'
} as const;

/**
 * SearchIndex
 */
export type SearchIndex = typeof SearchIndex[keyof typeof SearchIndex];

/**
 * SendAnnouncementPayload
 *
 * Payload for POST .../announcement/send: which template, to which guests.
 */
export type SendAnnouncementPayload = {
    /**
     * Email template UUID
     *
     * An event_announcement template: the event's own or a shared one.
     */
    email_template_uuid: string;
    /**
     * Guest UUIDs
     *
     * The guests to send to. Each must have an email address; RSVP status and whether an invite was sent do not matter.
     */
    guest_uuids: Array<string>;
};

/**
 * SendGridEmailEvent
 *
 * `email_event.sendgrid_event`: the SendGrid events the webhook stores. Our
 * own record of a send is its `sent_email` row.
 */
export const SendGridEmailEvent = {
    BOUNCE: 'bounce',
    CLICK: 'click',
    DELIVERED: 'delivered',
    DROPPED: 'dropped',
    OPEN: 'open'
} as const;

/**
 * SendGridEmailEvent
 *
 * `email_event.sendgrid_event`: the SendGrid events the webhook stores. Our
 * own record of a send is its `sent_email` row.
 */
export type SendGridEmailEvent = typeof SendGridEmailEvent[keyof typeof SendGridEmailEvent];

/**
 * SendInvitePayload
 *
 * Payload for POST .../invite/send. See GuestSendTargetsPayload for targeting.
 */
export type SendInvitePayload = {
    /**
     * Guest UUID
     *
     * Send to this guest only (legacy). Prefer guest_uuids for new clients.
     */
    guest_uuid?: string | null;
    /**
     * Guest UUIDs
     *
     * Send only to these guests. Omit with guest_uuid unset for all eligible.
     */
    guest_uuids?: Array<string> | null;
};

/**
 * SendReminderPayload
 *
 * Payload for POST .../reminder/send. See GuestSendTargetsPayload for targeting.
 */
export type SendReminderPayload = {
    /**
     * Guest UUID
     *
     * Send to this guest only (legacy). Prefer guest_uuids for new clients.
     */
    guest_uuid?: string | null;
    /**
     * Guest UUIDs
     *
     * Send only to these guests. Omit with guest_uuid unset for all eligible.
     */
    guest_uuids?: Array<string> | null;
};

/**
 * Seniority
 */
export const Seniority = {
    C_SUITE: 'c_suite',
    PRESIDENT: 'president',
    EVP_SVP: 'evp_svp',
    VP: 'vp',
    HEAD_OF: 'head_of',
    BOARD_MEMBER: 'board_member',
    PARTNER: 'partner',
    OTHER: 'other'
} as const;

/**
 * Seniority
 */
export type Seniority = typeof Seniority[keyof typeof Seniority];

/**
 * ShareRecommendations
 */
export type ShareRecommendations = {
    /**
     * Candidates
     *
     * List of candidate UUIDs to share with the recipient(s)
     */
    candidates: Array<string>;
    /**
     * CC
     *
     * List of email addresses to cc on the share recommendations email
     */
    cc?: Array<string>;
    /**
     * BCC
     *
     * List of email addresses to bcc on the share recommendations email
     */
    bcc?: Array<string>;
    /**
     * Footer
     *
     * Content of the footer section
     */
    footer: string;
    /**
     * Message
     *
     * Content of the email body
     */
    message: string;
    /**
     * Subject
     *
     * Subject for the share recommendations email
     */
    subject: string;
    /**
     * To
     *
     * List of email addresses to receive the share recommendations email
     */
    to: Array<string>;
};

/**
 * ShareRecommendationsResponse
 */
export type ShareRecommendationsResponse = {
    /**
     * Candidates
     *
     * List of candidate UUIDs shared with the recipient(s)
     */
    candidates: Array<string>;
    /**
     * CC
     *
     * List of email addresses cc'ed on the share recommendations email
     */
    cc?: Array<string>;
    /**
     * BCC
     *
     * List of email addresses bcc'ed on the share recommendations email
     */
    bcc?: Array<string>;
    /**
     * Footer
     *
     * Content of the footer section
     */
    footer: string;
    /**
     * Message
     *
     * Content of the email body
     */
    message: string;
    /**
     * Subject
     *
     * Subject of the share recommendations email
     */
    subject: string;
    /**
     * To
     *
     * List of email addresses that received the share recommendations email
     */
    to: Array<string>;
};

/**
 * SortDirection
 */
export const SortDirection = { ASC: 'asc', DESC: 'desc' } as const;

/**
 * SortDirection
 */
export type SortDirection = typeof SortDirection[keyof typeof SortDirection];

/**
 * SortRule
 */
export type SortRule = {
    /**
     * Column
     *
     * Sort results on this column
     */
    column: string;
    /**
     * Direction
     *
     * Sort results in this direction
     */
    direction?: SortDirection;
};

/**
 * SourceCompanyTags
 */
export const SourceCompanyTags = {
    ACREW_TIER_1: 'acrew_tier_1',
    ACREW_TIER_2: 'acrew_tier_2',
    ACREW_TIER_3: 'acrew_tier_3'
} as const;

/**
 * SourceCompanyTags
 */
export type SourceCompanyTags = typeof SourceCompanyTags[keyof typeof SourceCompanyTags];

/**
 * StatSplitRole
 */
export type StatSplitRole = {
    /**
     * Board
     */
    board: number;
    /**
     * Operating
     */
    operating: number;
    /**
     * Other
     */
    other: number;
};

/**
 * StatSplitSubmission
 */
export type StatSplitSubmission = {
    /**
     * Global
     */
    global?: number | null;
    /**
     * Pipeline
     */
    pipeline: number;
    /**
     * Trending
     */
    trending: number;
};

/**
 * StatSplitTeam
 */
export type StatSplitTeam = {
    /**
     * Corporate
     */
    corporate: number;
    /**
     * Investor
     */
    investor: number;
    /**
     * Other
     */
    other: number;
    /**
     * Search
     */
    search: number;
};

/**
 * SubmissionBasicResponse
 */
export type SubmissionBasicResponse = {
    /**
     * Name
     *
     * Submission name
     */
    name?: string | null;
    /**
     * UUID
     *
     * UUID uniquely identifying a submission
     */
    uuid: string;
};

/**
 * SubmissionCandidateDraftV2Payload
 */
export type SubmissionCandidateDraftV2Payload = {
    /**
     * Candidate UUID
     *
     * UUID of the candidate to recommend
     */
    candidate_uuid: string;
    /**
     * Highlight selections
     *
     * Map of highlight UUID -> selected (True to show, False to hide/remove)
     */
    highlight_selections?: {
        [key: string]: boolean;
    } | null;
    /**
     * Tailored highlights
     *
     * Highlights written for this pipeline (see tailored-highlights), shown before the selected profile highlights. Written when the recommendation is created, like the profile selection.
     */
    tailored_highlights?: Array<TailoredHighlightPayload> | null;
    /**
     * Introduction template UUID
     *
     * UUID of the introduction template
     */
    introduction_template_uuid?: string | null;
    /**
     * Introduction email type
     *
     * Recommendation-level override
     */
    intro_email_type?: IntroEmailType | null;
    /**
     * Recommendation UUID
     *
     * UUID of existing draft recommendation to convert to published status. If provided, the existing recommendation will be updated instead of creating a new one.
     */
    recommendation_uuid?: string | null;
};

/**
 * SubmissionCandidatePipelinesResponse
 */
export type SubmissionCandidatePipelinesResponse = {
    /**
     * Recommendations
     *
     * List of recommendations in this submission
     */
    recommendations: Array<RecommendationCandidatePipelinesResponse>;
    /**
     * UUID
     *
     * UUID uniquely identifying a submission
     */
    uuid: string;
};

/**
 * SubmissionCheckSharedResponse
 */
export type SubmissionCheckSharedResponse = {
    /**
     * Candidate
     *
     * Information about the candidate for which we ran the shared check
     */
    candidate: CandidateExtendedResponse;
    /**
     * UUID
     *
     * UUID uniquely identifying a submission candidate
     */
    uuid: string;
};

/**
 * SubmissionDraftV2
 */
export type SubmissionDraftV2 = {
    /**
     * Skip notifications
     *
     * If true, this action will not generate any notification events
     */
    skip_notifications?: boolean;
    /**
     * Presenter UUID
     *
     * UUID uniquely identifying an advisor
     */
    presenter_uuid?: string | null;
    /**
     * Advisor UUID
     *
     * UUID uniquely identifying an advisor
     */
    advisor_uuid?: string | null;
    /**
     * Candidates
     *
     * List of candidates to recommend with their highlights
     */
    candidates: Array<SubmissionCandidateDraftV2Payload>;
    /**
     * Draft Mode
     *
     * Draft mode for the submission candidates
     */
    draft_mode?: RecommendationDraftMode | null;
    /**
     * Source
     *
     * Origin of the created recommendations (e.g. `recsys` when added from the RecSys panel). Defaults to `manual` when omitted.
     */
    source?: RecommendationSource | null;
    /**
     * Intro email manual override
     *
     * Introduction email manual override, disabled by default. If set to `True` it overrides all submission candidates `intro_email_type` setting.
     */
    intro_email_type_manual_override?: boolean;
};

/**
 * SubmissionFilterCountsResponse
 */
export type SubmissionFilterCountsResponse = {
    /**
     * All
     */
    all: number;
    /**
     * Interesting
     */
    interesting: number;
    /**
     * Intro Made
     */
    intro_made: number;
    /**
     * Intro Requested
     */
    intro_requested: number;
    /**
     * Intro Unavailable
     */
    intro_unavailable: number;
    /**
     * Needs Review
     */
    needs_review: number;
    /**
     * Not Interesting
     */
    not_interesting: number;
};

/**
 * SubmissionFilterFilterData
 */
export type SubmissionFilterFilterData = {
    /**
     * Review Status
     */
    review_status?: Array<RecommendationReviewState> | null;
};

/**
 * SubmissionFilterPayload
 */
export type SubmissionFilterPayload = {
    /**
     * Page
     *
     * Page number
     */
    page?: number;
    /**
     * Page size
     *
     * Page size
     */
    page_size?: number;
    /**
     * Filters
     *
     * Filter results by these criteria
     */
    filter_data?: SubmissionFilterFilterData | null;
    /**
     * Sort
     *
     * Sort results by these rules
     */
    sort_rules: Array<SubmissionFilterSortData>;
};

/**
 * SubmissionFilterResponse
 */
export type SubmissionFilterResponse = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Results
     */
    results: Array<RecommendationFilterResult>;
    /**
     * Size
     */
    size: number;
    total_counts: SubmissionFilterCountsResponse;
};

/**
 * SubmissionFilterSortColumn
 */
export const SubmissionFilterSortColumn = {
    COMMENTS: 'comments',
    '': '',
    REVIEW_STATUS: 'review_status',
    TEAM: 'team'
} as const;

/**
 * SubmissionFilterSortColumn
 */
export type SubmissionFilterSortColumn = typeof SubmissionFilterSortColumn[keyof typeof SubmissionFilterSortColumn];

/**
 * SubmissionFilterSortData
 */
export type SubmissionFilterSortData = {
    /**
     * Sort results on this column
     */
    column: SubmissionFilterSortColumn;
    /**
     * Direction
     *
     * Sort results in this direction
     */
    direction?: SortDirection;
};

/**
 * SubmissionSearchFilterSortPayload
 */
export type SubmissionSearchFilterSortPayload = {
    /**
     * Phrase
     *
     * Search phrase
     */
    phrase?: string | null;
    /**
     * Search ID
     *
     * Search session ID
     */
    search_id?: string | null;
    /**
     * Page
     *
     * Page number
     */
    page?: number;
    /**
     * Page size
     *
     * Page size
     */
    page_size?: number;
    /**
     * Filters
     *
     * Filter results by these criteria
     */
    filter_data?: SubmissionsFilters | null;
    /**
     * Sort
     *
     * Sort results by these rules
     */
    sort_rules: Array<SubmissionsSort>;
};

/**
 * SubmissionSearchFilterSortResponse
 */
export type SubmissionSearchFilterSortResponse = {
    /**
     * Emails delivered
     *
     * Number of emails delivered in this submission
     */
    emails_delivered: number;
    /**
     * Emails opened
     *
     * Number of emails opened in this submission
     */
    emails_opened: number;
    /**
     * Advisor
     *
     * Advisor data
     */
    advisor?: PersonBasicEmailResponse | null;
    /**
     * Candidate
     *
     * When defined, that means we're working with a Trending submission
     */
    candidate?: PersonBasicEmailResponse | null;
    /**
     * Intro requests
     *
     * Number of introduction requests in this submission
     */
    introduction_request_count: number;
    /**
     * Last executed in CRON summary job
     *
     * Date when this biweekly submission was last executed
     */
    last_executed_in_summary_cron?: string | null;
    /**
     * Last modified
     *
     * Date when this submission was last modified
     */
    last_modified: string;
    /**
     * Name
     *
     * Submission name
     */
    name?: string | null;
    /**
     * Notification type
     *
     * Submission's notification type
     */
    notification_type?: RecommendationPublishedNotificationType | null;
    /**
     * Published on
     *
     * Date when this submission was published
     */
    published_on?: string | null;
    /**
     * Recommendations
     *
     * Number of recommendations in this submission
     */
    recommendations_count: number;
    /**
     * Status
     *
     * Submission status
     */
    status: SubmissionStatus;
    /**
     * Type
     *
     * Submission type
     */
    type?: SubmissionType | null;
    /**
     * UUID
     *
     * Submission UUID
     */
    uuid: string;
};

/**
 * SubmissionStatus
 */
export const SubmissionStatus = {
    DRAFT: 'draft',
    PUBLISHED: 'published',
    SCHEDULED: 'scheduled'
} as const;

/**
 * SubmissionStatus
 */
export type SubmissionStatus = typeof SubmissionStatus[keyof typeof SubmissionStatus];

/**
 * SubmissionStatusPayload
 */
export type SubmissionStatusPayload = {
    /**
     * Status
     *
     * Status we want this submission to have
     */
    status: SubmissionStatus;
};

/**
 * SubmissionType
 */
export const SubmissionType = {
    PIPELINE: 'pipeline',
    SCHEDULED: 'scheduled',
    SEARCH: 'search',
    INTRODUCTION: 'introduction'
} as const;

/**
 * SubmissionType
 */
export type SubmissionType = typeof SubmissionType[keyof typeof SubmissionType];

/**
 * SubmissionV3InternalEmailStatsResponse
 */
export type SubmissionV3InternalEmailStatsResponse = {
    /**
     * Emails delivered
     *
     * Number of emails delivered in this submission
     */
    emails_delivered: number;
    /**
     * Emails opened
     *
     * Number of emails opened in this submission
     */
    emails_opened: number;
    /**
     * About
     *
     * Brief commentary about the candidate
     */
    about?: string | null;
    /**
     * Advisor
     *
     * Advisor whose identity individual (stream) trending emails are sent from
     */
    advisor?: PersonBasicEmailResponse | null;
    /**
     * Candidate
     *
     * When defined, that means we're working with a Trending submission
     */
    candidate?: CandidateSearchResponse | null;
    /**
     * Recommendations
     *
     * List of recommendations in this submission
     */
    recommendations: Array<SubmissionV3RecommendationResponse>;
    /**
     * Highlights
     *
     * List of highlights to show for the candidate
     */
    highlights_with_selected: Array<HighlightWithSelectedResponse>;
    /**
     * Last executed in CRON summary job
     *
     * Date when this biweekly submission was last executed
     */
    last_executed_in_summary_cron?: string | null;
    /**
     * Notification type
     *
     * Strategy used to notify teams they have new candidates to review
     */
    notification_type: RecommendationPublishedNotificationType;
    /**
     * Publish email body
     *
     * Customized publish email body text
     */
    publish_email_body?: string | null;
    /**
     * Publish email subject
     *
     * Customized publish email subject line
     */
    publish_email_subject?: string | null;
    /**
     * Published on
     *
     * Date when this submission was or will be published
     */
    published_on?: string | null;
    /**
     * Status
     *
     * Submission status
     */
    status: SubmissionStatus;
    /**
     * Type
     *
     * Submission type
     */
    type?: SubmissionType | null;
    /**
     * UUID
     *
     * UUID uniquely identifying a submission
     */
    uuid: string;
};

/**
 * SubmissionV3InternalResponse
 */
export type SubmissionV3InternalResponse = {
    /**
     * About
     *
     * Brief commentary about the candidate
     */
    about?: string | null;
    /**
     * Advisor
     *
     * Advisor whose identity individual (stream) trending emails are sent from
     */
    advisor?: PersonBasicEmailResponse | null;
    /**
     * Candidate
     *
     * When defined, that means we're working with a Trending submission
     */
    candidate?: CandidateSearchResponse | null;
    /**
     * Recommendations
     *
     * List of recommendations in this submission
     */
    recommendations: Array<SubmissionV3RecommendationResponse>;
    /**
     * Highlights
     *
     * List of highlights to show for the candidate
     */
    highlights_with_selected: Array<HighlightWithSelectedResponse>;
    /**
     * Last executed in CRON summary job
     *
     * Date when this biweekly submission was last executed
     */
    last_executed_in_summary_cron?: string | null;
    /**
     * Notification type
     *
     * Strategy used to notify teams they have new candidates to review
     */
    notification_type: RecommendationPublishedNotificationType;
    /**
     * Publish email body
     *
     * Customized publish email body text
     */
    publish_email_body?: string | null;
    /**
     * Publish email subject
     *
     * Customized publish email subject line
     */
    publish_email_subject?: string | null;
    /**
     * Published on
     *
     * Date when this submission was or will be published
     */
    published_on?: string | null;
    /**
     * Status
     *
     * Submission status
     */
    status: SubmissionStatus;
    /**
     * Type
     *
     * Submission type
     */
    type?: SubmissionType | null;
    /**
     * UUID
     *
     * UUID uniquely identifying a submission
     */
    uuid: string;
};

/**
 * SubmissionV3Payload
 */
export type SubmissionV3Payload = {
    /**
     * About
     *
     * Brief commentary about the candidate
     */
    about?: string | null;
    /**
     * Advisor ID
     *
     * Advisor whose identity individual (stream) trending emails are sent from
     */
    advisor_id?: string | null;
    /**
     * Candidate UUID
     *
     * When defined, that means we're working with a Trending submission
     */
    candidate_uuid?: string | null;
    /**
     * Goal ID
     *
     * Goal associated with this trending submission
     */
    goal_id?: string | null;
    /**
     * Highlights
     *
     * List of highlights to show for the candidate
     */
    highlights_with_selected?: Array<HighlightWithSelectedPayload> | null;
    /**
     * Notification type
     *
     * Strategy used to notify teams they have new candidates to review; null sends no email at all
     */
    notification_type?: RecommendationPublishedNotificationType | null;
    /**
     * Publish email body
     *
     * Customized publish email body text
     */
    publish_email_body?: string | null;
    /**
     * Publish email subject
     *
     * Customized publish email subject line
     */
    publish_email_subject?: string | null;
    /**
     * Published on
     *
     * Date when this submission was or will be published
     */
    published_on?: string | null;
    /**
     * Recommendations
     *
     * List of recommendations
     */
    recommendations?: Array<SubmissionV3RecommendationPayload> | null;
};

/**
 * SubmissionV3RecommendationPayload
 */
export type SubmissionV3RecommendationPayload = {
    /**
     * Candidate UUID
     *
     * Candidate for which this recommendation is made
     */
    candidate_uuid: string;
    /**
     * Team UUID
     *
     * Team for which this recommendation is made
     */
    team_uuid: string;
    /**
     * UUID
     *
     * Recommendation UUID
     */
    uuid?: string | null;
    /**
     * Published On
     *
     * Recommendation publish date
     */
    published_on?: string | null;
};

/**
 * SubmissionV3RecommendationResponse
 */
export type SubmissionV3RecommendationResponse = {
    /**
     * Candidate
     *
     * Candidate information
     */
    candidate: CandidateSearchResponse;
    /**
     * Team
     *
     * Team information
     */
    team: TeamsSearchTeamResponse;
    /**
     * UUID
     *
     * Recommendation UUID
     */
    uuid: string;
    /**
     * Recommendation published on
     */
    published_on?: string | null;
    /**
     * Recommendation status
     *
     * Recommendation status
     */
    status: RecommendationStatusOutput;
};

/**
 * SubmissionsFilters
 */
export type SubmissionsFilters = {
    /**
     * Advisor Id
     */
    advisor_id?: Array<string> | null;
    /**
     * Executive Id
     */
    executive_id?: Array<string> | null;
    /**
     * Function Expertise
     */
    function_expertise?: Array<Function> | null;
    /**
     * Industry Expertise
     */
    industry_expertise?: Array<Industry> | null;
    /**
     * Notification Type
     */
    notification_type?: Array<RecommendationPublishedNotificationType> | null;
    /**
     * Status
     */
    status?: Array<SubmissionStatus> | null;
    /**
     * Type
     */
    type?: Array<SubmissionType> | null;
};

/**
 * SubmissionsSearchFilterSortPaginatedResponse
 */
export type SubmissionsSearchFilterSortPaginatedResponse = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Results
     */
    results: Array<SubmissionSearchFilterSortResponse>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCountsResponse;
    /**
     * Search Id
     */
    search_id?: string | null;
};

/**
 * SubmissionsSort
 */
export type SubmissionsSort = {
    /**
     * Column
     *
     * Sort results on this column
     */
    column: SubmissionsSortColumn;
    /**
     * Direction
     *
     * Sort results in this direction
     */
    direction?: SortDirection;
};

/**
 * SubmissionsSortColumn
 */
export const SubmissionsSortColumn = {
    NAME: 'name',
    PUBLISHED_ON: 'published_on',
    ADVISOR: 'advisor',
    NOTIFICATION_TYPE: 'notification_type',
    INTRODUCTION_REQUEST_COUNT: 'introduction_request_count',
    EMAILS_OPENED: 'emails_opened',
    EMAILS_DELIVERED: 'emails_delivered',
    STATUS: 'status'
} as const;

/**
 * SubmissionsSortColumn
 */
export type SubmissionsSortColumn = typeof SubmissionsSortColumn[keyof typeof SubmissionsSortColumn];

/**
 * SuggestionPick
 */
export type SuggestionPick = {
    /**
     * Candidate UUID
     */
    candidate_uuid: string;
    /**
     * Name
     */
    name: string;
    /**
     * Steering
     *
     * False when the advisor switched this pick off as a signal.
     */
    steering: boolean;
};

/**
 * SuggestionWeights
 *
 * Dial positions, 0-1 each. Omitted dials use the backtested defaults.
 *
 * Only ratios matter — every signal is min-max normalized across the pool
 * before weighting. The two cart dials have no effect while
 * `selected_team_uuids` is empty.
 */
export type SuggestionWeights = {
    /**
     * Trended For Similar
     */
    trended_for_similar?: number | null;
    /**
     * Trended Alongside Cart
     */
    trended_alongside_cart?: number | null;
    /**
     * Profile Overlaps Cart
     */
    profile_overlaps_cart?: number | null;
    /**
     * Profile Match
     */
    profile_match?: number | null;
    /**
     * Interacted With Similar
     */
    interacted_with_similar?: number | null;
};

/**
 * Summary
 */
export type Summary = {
    /**
     * Summary
     */
    summary: string;
};

/**
 * SummaryStatus
 */
export const SummaryStatus = {
    READY: 'ready',
    PENDING: 'pending',
    NONE: 'none'
} as const;

/**
 * SummaryStatus
 */
export type SummaryStatus = typeof SummaryStatus[keyof typeof SummaryStatus];

/**
 * TailoredHighlightDraft
 */
export type TailoredHighlightDraft = {
    /**
     * Text
     *
     * The highlight as the model wrote it
     */
    text: string;
    /**
     * Basis
     *
     * Resume roles the highlight draws on. Advisor-only.
     */
    basis: string;
};

/**
 * TailoredHighlightPayload
 */
export type TailoredHighlightPayload = {
    /**
     * Highlight
     *
     * Highlight text as the advisor left it
     */
    highlight: string;
    /**
     * Is selected
     *
     * Shown to the search team when True; stored either way
     */
    is_selected?: boolean;
    /**
     * Generated
     *
     * The model's original wording, before any advisor edits. Kept for measuring how often drafts are edited; never shown to the search team.
     */
    generated?: string | null;
};

/**
 * TailoredHighlightResponse
 */
export type TailoredHighlightResponse = {
    /**
     * Highlight
     *
     * Highlight text
     */
    highlight: string;
    /**
     * Is selected
     *
     * Shown to the search team
     */
    is_selected?: boolean;
    /**
     * Generated
     *
     * The model's original wording, before any advisor edits
     */
    generated?: string | null;
};

/**
 * TailoredHighlightsQueued
 */
export type TailoredHighlightsQueued = {
    /**
     * Task Id
     */
    task_id: string;
    /**
     * Status
     */
    status?: string;
    /**
     * Status Url
     */
    status_url: string;
};

/**
 * TailoredHighlightsRequest
 */
export type TailoredHighlightsRequest = {
    /**
     * Candidate UUID
     *
     * Candidate to draft highlights for
     */
    candidate_uuid: string;
};

/**
 * TailoredHighlightsStatus
 */
export type TailoredHighlightsStatus = {
    /**
     * Task Id
     */
    task_id: string;
    /**
     * State
     *
     * PENDING, SUCCESS or FAILURE
     */
    state: string;
    /**
     * Highlights
     */
    highlights?: Array<TailoredHighlightDraft> | null;
    /**
     * Model Version
     */
    model_version?: string | null;
    /**
     * Error
     */
    error?: string | null;
};

/**
 * TaskBasicResponse
 */
export type TaskBasicResponse = {
    /**
     * Candidate
     */
    assignee: PersonBasicEmailResponse;
    /**
     * Candidate
     */
    candidate: CandidateBasicResponse;
    /**
     * Created date
     */
    created: string;
    /**
     * Candidate
     */
    creator: PersonBasicEmailResponse;
    /**
     * Completion date
     */
    completion_date?: string | null;
    /**
     * Description
     */
    description?: string | null;
    /**
     * Due date
     */
    due_date?: string | null;
    /**
     * Task ID
     */
    id: string;
    /**
     * Is completed?
     */
    is_completed: boolean;
    /**
     * Last modified
     */
    last_modified: string;
    /**
     * Module
     */
    task_module: TaskModuleBasicResponse | null;
    /**
     * Title
     */
    title: string;
    /**
     * Overdue
     */
    readonly overdue: boolean;
};

/**
 * TaskCreateUpdateManyPayload
 */
export type TaskCreateUpdateManyPayload = {
    /**
     * Tasks
     *
     * List of tasks to create/update
     */
    tasks: Array<TaskCreateUpdatePayload>;
};

/**
 * TaskCreateUpdatePayload
 */
export type TaskCreateUpdatePayload = {
    /**
     * Assignee UUID
     *
     * Person assigned to this task
     */
    assignee_id: string;
    /**
     * Candidate UUID
     *
     * Candidate for which this task is made
     */
    candidate_uuid: string;
    /**
     * Description
     */
    description?: string | null;
    /**
     * Due Date
     *
     * When is this task due?
     */
    due_date?: string | null;
    /**
     * Is completed?
     *
     * Is this task completed?
     */
    is_completed?: boolean | null;
    /**
     * Task Module ID
     *
     * Task module ID
     */
    task_module_id?: string | null;
    /**
     * Title
     */
    title: string;
};

/**
 * TaskCreated
 */
export const TaskCreated = {
    OLDER_THAN_ONE_MONTH: 'older_than_one_month',
    ONE_MONTH_AGO: 'one_month_ago',
    ONE_WEEK_AGO: 'one_week_ago',
    THIS_WEEK: 'this_week',
    TODAY: 'today',
    TWO_WEEKS_AGO: 'two_weeks_ago',
    YESTERDAY: 'yesterday'
} as const;

/**
 * TaskCreated
 */
export type TaskCreated = typeof TaskCreated[keyof typeof TaskCreated];

/**
 * TaskDueDate
 */
export const TaskDueDate = { NEXT_WEEK: 'next_week', THIS_WEEK: 'this_week' } as const;

/**
 * TaskDueDate
 */
export type TaskDueDate = typeof TaskDueDate[keyof typeof TaskDueDate];

/**
 * TaskModuleBasicResponse
 */
export type TaskModuleBasicResponse = {
    /**
     * Created date
     */
    created: string;
    /**
     * Module ID
     */
    id: string;
    /**
     * Type
     *
     * Type of task module
     */
    type: TaskModuleType;
};

/**
 * TaskModuleCreateUpdatePayload
 */
export type TaskModuleCreateUpdatePayload = {
    /**
     * Assignee UUID
     *
     * Person assigned to this task module
     */
    assignee_id: string;
    /**
     * Candidate UUID
     *
     * Candidate for which this task module is made
     */
    candidate_uuid: string;
    /**
     * Description
     */
    description?: string | null;
    /**
     * Due Date
     *
     * When is this task module due?
     */
    due_date: string;
    /**
     * Is completed?
     *
     * Is this task module completed?
     */
    is_completed: boolean;
    /**
     * Start Date
     *
     * When does this task module start?
     */
    start_date: string;
    /**
     * Tasks
     *
     * Tasks in this module
     */
    tasks?: Array<TaskCreateUpdatePayload> | null;
    /**
     * Type
     *
     * Type of task module
     */
    type: TaskModuleType;
};

/**
 * TaskModuleResponse
 */
export type TaskModuleResponse = {
    /**
     * Candidate
     */
    assignee: PersonBasicEmailResponse;
    /**
     * Candidate
     */
    candidate: CandidateBasicResponse;
    /**
     * Created date
     */
    created: string;
    /**
     * Candidate
     */
    creator: PersonBasicEmailResponse;
    /**
     * Description
     */
    description?: string | null;
    /**
     * End date
     *
     * When does this task module end?
     */
    due_date: string;
    /**
     * Module ID
     */
    id: string;
    /**
     * Last modified
     */
    last_modified: string;
    /**
     * Start date
     *
     * When does this task module start?
     */
    start_date: string;
    /**
     * Status
     *
     * Task module status
     */
    status: TaskModuleStatus;
    /**
     * Tasks
     *
     * List of tasks associated with this module
     */
    tasks?: Array<TaskBasicResponse> | null;
    /**
     * Type
     *
     * Type of task module
     */
    type: TaskModuleType;
    readonly total_counts: TaskTotal;
};

/**
 * TaskModuleStatus
 */
export const TaskModuleStatus = {
    AT_RISK: 'at_risk',
    COMPLETED: 'completed',
    IN_PROGRESS: 'in_progress',
    NO_TASKS: 'no_tasks',
    NOT_STARTED: 'not_started',
    OVERDUE: 'overdue'
} as const;

/**
 * TaskModuleStatus
 */
export type TaskModuleStatus = typeof TaskModuleStatus[keyof typeof TaskModuleStatus];

/**
 * TaskModuleStatusPayload
 */
export type TaskModuleStatusPayload = {
    /**
     * Is completed?
     *
     * Is this task module completed?
     */
    is_completed: boolean;
    /**
     * Mark all tasks as completed
     *
     * Mark all tasks in module as completed
     */
    mark_tasks_as_completed?: boolean | null;
};

/**
 * TaskModuleType
 */
export const TaskModuleType = {
    KICKOFF_ONBOARDING: 'kickoff_onboarding',
    NARRATIVE_REFRESH: 'narrative_refresh',
    ON_HOLD: 'on_hold',
    OTHER: 'other',
    PEER_TO_PEER: 'peer_to_peer',
    TRENDING: 'trending'
} as const;

/**
 * TaskModuleType
 */
export type TaskModuleType = typeof TaskModuleType[keyof typeof TaskModuleType];

/**
 * TaskStatus
 */
export const TaskStatus = { COMPLETED: 'completed', INCOMPLETE: 'incomplete' } as const;

/**
 * TaskStatus
 */
export type TaskStatus = typeof TaskStatus[keyof typeof TaskStatus];

/**
 * TaskStatusPayload
 */
export type TaskStatusPayload = {
    /**
     * Is completed?
     *
     * Mark this task as completed/incomplete
     */
    is_completed: boolean;
};

/**
 * TaskTotal
 */
export type TaskTotal = {
    /**
     * All
     */
    all: number;
    /**
     * Incomplete
     */
    incomplete: number;
};

/**
 * TasksTableFilterAggregationsResponse
 */
export type TasksTableFilterAggregationsResponse = {
    /**
     * Assignee Id
     */
    assignee_id: {
        [key: string]: number;
    };
    /**
     * Candidate Uuid
     */
    candidate_uuid: {
        [key: string]: number;
    };
    /**
     * Created
     */
    created: {
        [key: string]: number;
    };
    /**
     * Due Date
     */
    due_date: {
        [key: string]: number;
    };
    /**
     * Overdue
     */
    overdue: {
        [key: string]: number;
    };
    /**
     * Status
     */
    status: {
        [key: string]: number;
    };
};

/**
 * TasksTableFilterData
 */
export type TasksTableFilterData = {
    /**
     * Assignee UUID
     *
     * Filter tasks assigned to these people
     */
    assignee_uuid?: Array<string>;
    /**
     * Candidate UUID
     *
     * Filter tasks for these candidates
     */
    candidate_uuid?: Array<string>;
    /**
     * Task Created
     *
     * Filter tasks created within these dates
     */
    created?: Array<TaskCreated>;
    /**
     * Task Due
     *
     * Filter tasks due within these dates
     */
    due_date?: Array<TaskDueDate>;
    /**
     * Overdue
     *
     * Filter tasks that are overdue
     */
    overdue?: Array<boolean>;
    /**
     * Task Status
     *
     * Filter tasks with this status
     */
    status?: Array<TaskStatus>;
};

/**
 * TasksTableFilterPayload
 */
export type TasksTableFilterPayload = {
    /**
     * Phrase
     *
     * Search phrase
     */
    phrase?: string | null;
    /**
     * Search ID
     *
     * Search session ID
     */
    search_id?: string | null;
    /**
     * Page
     *
     * Page number
     */
    page?: number;
    /**
     * Page size
     *
     * Page size
     */
    page_size?: number;
    /**
     * Filters
     *
     * Filter results by these criteria
     */
    filter_data?: TasksTableFilterData | null;
    /**
     * Sort
     *
     * Sort results by these rules
     */
    sort_rules: Array<TasksTableSort>;
};

/**
 * TasksTableResponse
 */
export type TasksTableResponse = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Results
     */
    results: Array<TaskBasicResponse>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCountsResponse;
    aggregations: TasksTableFilterAggregationsResponse;
};

/**
 * TasksTableSort
 */
export type TasksTableSort = {
    /**
     * Column
     *
     * Sort results on this column
     */
    column: TasksTableSortColumn;
    /**
     * Direction
     *
     * Sort results in this direction
     */
    direction?: SortDirection;
};

/**
 * TasksTableSortColumn
 */
export const TasksTableSortColumn = {
    ASSIGNEE_NAME: 'assignee_name',
    CANDIDATE_NAME: 'candidate_name',
    CREATED: 'created',
    COMPLETION_DATE: 'completion_date',
    DUE_DATE: 'due_date'
} as const;

/**
 * TasksTableSortColumn
 */
export type TasksTableSortColumn = typeof TasksTableSortColumn[keyof typeof TasksTableSortColumn];

/**
 * TeamAndCompanyCreate
 */
export type TeamAndCompanyCreate = {
    /**
     * Team
     *
     * The new team to create
     */
    team: TeamCreateV2;
    /**
     * Company
     *
     * The new company to associate the team with
     */
    company: CompanyCreate;
};

/**
 * TeamBasicResponse
 */
export type TeamBasicResponse = {
    /**
     * Active
     *
     * Inactive (archived) teams keep their data but are hidden from search and recommendations and closed to their members
     */
    active?: boolean;
    /**
     * Slug
     *
     * Slug uniquely identifying a team
     */
    slug: string;
    /**
     * Name
     *
     * Team name
     */
    name: string;
};

/**
 * TeamBasicUuidResponse
 */
export type TeamBasicUuidResponse = {
    /**
     * Active
     *
     * Inactive (archived) teams keep their data but are hidden from search and recommendations and closed to their members
     */
    active?: boolean;
    /**
     * Slug
     *
     * Slug uniquely identifying a team
     */
    slug: string;
    /**
     * Name
     *
     * Team name
     */
    name: string;
    /**
     * UUID
     *
     * UUID uniquely identifying a team
     */
    uuid: string;
};

/**
 * TeamCandidatePipelinesPayload
 */
export type TeamCandidatePipelinesPayload = {
    /**
     * Pipelines
     *
     * List of pipeline UUIDs to publish candidate to
     */
    pipelines: Array<string>;
    /**
     * Team Uuids
     *
     * Return info for specified teams only
     */
    team_uuids?: Array<string> | null;
};

/**
 * TeamCandidatesFilterPayloadV3
 */
export type TeamCandidatesFilterPayloadV3 = {
    /**
     * Phrase
     *
     * Search phrase
     */
    phrase?: string | null;
    /**
     * Search ID
     *
     * Search session ID
     */
    search_id?: string | null;
    /**
     * Page
     *
     * Page number
     */
    page?: number;
    /**
     * Page size
     *
     * Page size
     */
    page_size?: number;
    /**
     * Filters
     *
     * Filter results by these criteria
     */
    filter_data?: TeamCandidatesFiltersV3;
    /**
     * Sort
     *
     * Sort results by these rules
     */
    sort_rules: Array<TeamCandidatesSort>;
    /**
     * Team Uuids
     *
     * Return info for specified teams only
     */
    team_uuids?: Array<string> | null;
    /**
     * Custom keywords filter
     *
     * Exact-match multi-select filter on per-company `custom_keywords`, scoped to the path team's company.
     */
    custom_keywords_filter?: Array<string> | null;
};

/**
 * TeamCandidatesFilterResponse
 */
export type TeamCandidatesFilterResponse = {
    aggregations: AppSchemasEsCandidateSchemaElasticsearchAggregationsResponse;
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Results
     */
    results: Array<EsCandidateIndexResult>;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCountsResponse;
};

/**
 * TeamCandidatesFiltersV3
 */
export type TeamCandidatesFiltersV3 = {
    /**
     * Connection status
     *
     * Filter by connection status
     */
    connection_status?: Array<ConnectionStatus> | null;
    /**
     * Function expertise
     *
     * Filter by function expertise
     */
    function_expertise?: Array<Function> | null;
    /**
     * Industry expertise
     *
     * Filter by industry expertise
     */
    industry_expertise?: Array<Industry> | null;
    /**
     * Interest in board/advisory roles
     *
     * Filter by interest in board or advisory roles
     */
    interest_role_board_advisory?: Array<InterestRole> | null;
    /**
     * Interest in operating roles
     *
     * Filter by interest in operating roles
     */
    interest_role_operating?: Array<InterestRole> | null;
    /**
     * Key operating experiences
     *
     * Filter by key operating experiences
     */
    operating_highlights?: Array<OperatingHighlights> | null;
    /**
     * Source company tags
     *
     * Filter by source company tags
     */
    source_company_tags?: Array<SourceCompanyTags> | null;
};

/**
 * TeamCandidatesSort
 */
export type TeamCandidatesSort = {
    /**
     * Column
     *
     * Sort results on this column
     */
    column: TeamCandidatesSortColumn;
    /**
     * Direction
     *
     * Sort results in this direction
     */
    direction?: SortDirection;
};

/**
 * TeamCandidatesSortColumn
 */
export const TeamCandidatesSortColumn = { NAME: 'name' } as const;

/**
 * TeamCandidatesSortColumn
 */
export type TeamCandidatesSortColumn = typeof TeamCandidatesSortColumn[keyof typeof TeamCandidatesSortColumn];

/**
 * TeamCompanyResponse
 */
export type TeamCompanyResponse = {
    /**
     * Active
     *
     * Inactive (archived) teams keep their data but are hidden from search and recommendations and closed to their members
     */
    active?: boolean;
    /**
     * Slug
     *
     * Slug uniquely identifying a team
     */
    slug: string;
    /**
     * Name
     *
     * Team name
     */
    name: string;
    /**
     * UUID
     *
     * UUID uniquely identifying a team
     */
    uuid: string;
    /**
     * Company
     *
     * Company to which this team belongs
     */
    company: CompanyBasicResponse;
};

/**
 * TeamConnectionStatus
 */
export const TeamConnectionStatus = {
    RECOMMENDATION_ADDED: 'recommendation_added',
    FACILITATED: 'facilitated',
    NONE: 'none'
} as const;

/**
 * TeamConnectionStatus
 */
export type TeamConnectionStatus = typeof TeamConnectionStatus[keyof typeof TeamConnectionStatus];

/**
 * TeamCreate
 */
export type TeamCreate = {
    /**
     * Client size
     *
     * Typical client size
     */
    company_type: SearchFirmType;
    /**
     * Slug
     *
     * Team slug if we want to set it ourselves instead of using the generated value
     */
    slug?: string | null;
    /**
     * Name
     *
     * Team name
     */
    team_name: string;
};

/**
 * TeamCreateV2
 */
export type TeamCreateV2 = {
    /**
     * Additional details
     *
     * Additional details about the team
     */
    additional_details?: string | null;
    /**
     * Client size
     *
     * Typical client size
     */
    client_size: SearchFirmType;
    /**
     * Functional focus
     *
     * The roles and responsibilities the team typically searches for
     */
    functional_focus?: Array<Function> | null;
    /**
     * Fund type
     *
     * This team's own fund type. Null inherits the company's value (the normal case); an empty list means this team explicitly has no fund type even if its company does; a non-empty list overrides the company, e.g. a PE deal team inside a VC firm.
     */
    fund_type?: Array<FundType> | null;
    /**
     * Industry focus
     *
     * Sectors the team typically focuses on
     */
    industry_focus?: Array<Industry> | null;
    /**
     * Name
     *
     * Team name
     */
    team_name: string;
    /**
     * Team type
     *
     * Whether this team is the firm itself or a company the firm invested in.
     */
    team_type?: TeamType | null;
};

/**
 * TeamExclusionCriteria
 */
export const TeamExclusionCriteria = {
    BASIC_INVESTOR_TEAMS: 'basic_investor_teams',
    HAS_NO_MEMBERS: 'has_no_members',
    INACTIVE: 'inactive',
    PORTFOLIO_COMPANY_TEAMS: 'portfolio_company_teams'
} as const;

/**
 * TeamExclusionCriteria
 */
export type TeamExclusionCriteria = typeof TeamExclusionCriteria[keyof typeof TeamExclusionCriteria];

/**
 * TeamExtendedFlatResponse
 */
export type TeamExtendedFlatResponse = {
    /**
     * Active
     *
     * Inactive (archived) teams keep their data but are hidden from search and recommendations and closed to their members
     */
    active?: boolean;
    /**
     * Slug
     *
     * Slug uniquely identifying a team
     */
    slug: string;
    /**
     * Name
     *
     * Team name
     */
    name: string;
    /**
     * UUID
     *
     * UUID uniquely identifying a team
     */
    uuid: string;
    /**
     * Additional details
     *
     * Additional details about the team
     */
    additional_details?: string | null;
    /**
     * Client size
     *
     * Typical client size
     */
    client_size?: SearchFirmType | null;
};

/**
 * TeamExtendedResponse
 */
export type TeamExtendedResponse = {
    /**
     * Active
     *
     * Inactive (archived) teams keep their data but are hidden from search and recommendations and closed to their members
     */
    active?: boolean;
    /**
     * Slug
     *
     * Slug uniquely identifying a team
     */
    slug: string;
    /**
     * Name
     *
     * Team name
     */
    name: string;
    /**
     * UUID
     *
     * UUID uniquely identifying a team
     */
    uuid: string;
    /**
     * Additional details
     *
     * Additional details about the team
     */
    additional_details?: string | null;
    /**
     * Company
     *
     * Company to which this team belongs
     */
    company: CompanyExtendedResponseWithFeatures;
    /**
     * Client size
     *
     * Typical client size
     */
    client_size?: SearchFirmType | null;
    /**
     * Effective fund type
     *
     * The fund type that actually applies to this team: its own override if set, otherwise its company's. This is what fund-type filters match on.
     */
    effective_fund_type?: Array<FundType> | null;
    /**
     * Fund type
     *
     * Team-level override of the company's fund type. Empty means the team inherits its company's value, which is the normal case.
     */
    fund_type?: Array<FundType> | null;
    /**
     * Functional focus
     *
     * The roles and responsibilities the team typically searches for
     */
    functional_focus?: Array<Function> | null;
    /**
     * Industry focus
     *
     * Sectors the team typically focuses on
     */
    industry_focus?: Array<Industry> | null;
    /**
     * Team type
     *
     * Whether this team is the firm itself or a company it invested in
     */
    team_type?: TeamType | null;
};

/**
 * TeamIntroInsight
 *
 * API response: the live candidate list + the stored AI summary (when fresh).
 */
export type TeamIntroInsight = {
    /**
     * Team Slug
     */
    team_slug: string;
    /**
     * Window Label
     */
    window_label: string;
    /**
     * Candidates
     */
    candidates: Array<IntroRequestItem>;
    summary_status: SummaryStatus;
    /**
     * Takeaway
     */
    takeaway?: string | null;
    /**
     * Points
     */
    points?: Array<string>;
    /**
     * Computed At
     */
    computed_at?: string | null;
    /**
     * Model Version
     */
    model_version?: string | null;
};

/**
 * TeamMembersBulkInvitePayload
 */
export type TeamMembersBulkInvitePayload = {
    /**
     * Emails
     *
     * Emails to create new people
     */
    emails: Array<string>;
    /**
     * Message
     *
     * Message to include in the invitation email
     */
    message?: string | null;
};

/**
 * TeamMembersPost
 */
export type TeamMembersPost = {
    /**
     * First Name
     *
     * Person's first name
     */
    first_name?: string | null;
    /**
     * Last Name
     *
     * Person's last name
     */
    last_name?: string | null;
    /**
     * Email
     *
     * Email associated with the person
     */
    email: string;
    /**
     * Person name
     *
     * Name of the invited person
     *
     * @deprecated
     */
    person_name?: string | null;
    /**
     * Permit Preregistration Notifications
     *
     * Whether to allow sending preregistration emails to this user. Defaults to False for pending users, set to True to enable marketing emails before signup.
     */
    permit_preregistration_notifications?: boolean;
    /**
     * Send Invite Email
     *
     * Whether to send invitation email to this user.Set to False to create invite without emailing
     */
    send_invite_email?: boolean;
};

/**
 * TeamMergePayload
 *
 * Payload for merging a source team into the target (path) team.
 *
 * The team in the URL is the target/base team that survives; the source
 * team named here is folded into it and then deactivated.
 */
export type TeamMergePayload = {
    /**
     * Source Team Uuid
     *
     * UUID of the source team to merge into (and deactivate)
     */
    source_team_uuid: string;
    /**
     * New Team Name
     *
     * Optional new name for the surviving target team (slug is regenerated from it)
     */
    new_team_name?: string | null;
};

/**
 * TeamPipelineCandidatesResponse
 */
export type TeamPipelineCandidatesResponse = {
    /**
     * Search ID
     *
     * Search session ID
     */
    search_id?: string | null;
    /**
     * Pipeline
     *
     * Information about the pipeline
     */
    pipeline: PipelineFullResponse;
    /**
     * Recommendations
     *
     * List of recommendations for this pipeline sorted by review status
     */
    recommendations: Array<CandidateItem>;
};

/**
 * TeamPipelineCandidatesSearchAggregations
 */
export type TeamPipelineCandidatesSearchAggregations = {
    /**
     * Advisor Id
     */
    advisor_id?: {
        [key: string]: number;
    };
    /**
     * Industry Expertise
     */
    industry_expertise?: {
        [key in Industry]?: number;
    };
    /**
     * Function Expertise
     */
    function_expertise?: {
        [key in Function]?: number;
    };
    /**
     * Is Accepting Intros
     */
    is_accepting_intros?: {
        [key: string]: number;
    };
    /**
     * Tier
     */
    tier?: {
        [key in CandidateTier]?: number;
    };
    /**
     * Type
     */
    type?: {
        [key in ClientType]?: number;
    };
    /**
     * Pathway
     */
    pathway?: {
        [key in CandidatePathway]?: number;
    };
    /**
     * Underrepresented
     */
    underrepresented?: {
        [key: string]: number;
    };
    /**
     * Warm
     */
    warm?: {
        [key: string]: number;
    };
    /**
     * Recsys Suggested
     */
    recsys_suggested?: {
        [key: string]: number;
    };
    /**
     * Pipeline Status
     */
    pipeline_status?: {
        [key in CandidatePipelineStatus]?: number;
    };
    /**
     * Operating Highlights
     */
    operating_highlights?: {
        [key in OperatingHighlights]?: number;
    };
    /**
     * Recommendation Status
     */
    recommendation_status?: {
        [key in RecommendationStatusFilter]?: number;
    };
    /**
     * Introduction Status
     */
    introduction_status?: {
        [key in IntroductionStatusFilter]?: number;
    };
    /**
     * Declined Status
     */
    declined_status?: {
        [key in DeclinedStatusFilter]?: number;
    };
};

/**
 * TeamPipelineCandidatesSearchFilterSortResponse
 */
export type TeamPipelineCandidatesSearchFilterSortResponse = {
    /**
     * Search ID
     *
     * Search session ID
     */
    search_id?: string | null;
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    aggregations: TeamPipelineCandidatesSearchAggregations;
    /**
     * Results
     */
    results: Array<PipelineCandidateSearchFilterSortResponse>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCountsResponse;
    /**
     * Suggestion Basis
     *
     * Only when the Suggested filter is on: what the list is sorted by. 'ai_evaluation_and_contender_signals' when the pipeline has a RecSys run, 'contender_signals' when it does not, 'ai_evaluation' when every pick is switched off (the RecSys run alone).
     */
    suggestion_basis?: string | null;
    /**
     * Suggestion Picks
     *
     * Only when the Suggested filter is on: the candidates on the pipeline that can steer the list (published, scheduled, ready for review or approved), by name, each marked with whether it is steering.
     */
    suggestion_picks?: Array<SuggestionPick> | null;
};

/**
 * TeamPipelinesFilter
 */
export const TeamPipelinesFilter = {
    ACTIVE: 'active',
    ALL: 'all',
    CLOSED: 'closed'
} as const;

/**
 * TeamPipelinesFilter
 */
export type TeamPipelinesFilter = typeof TeamPipelinesFilter[keyof typeof TeamPipelinesFilter];

/**
 * TeamPipelinesSorter
 */
export const TeamPipelinesSorter = {
    COMPANY: 'company',
    CREATED: 'created',
    '': '',
    NAME: 'name',
    INTRODUCTIONS: 'introductions',
    INTRODUCTIONS_COMPLETED: 'introductions_completed',
    LAST_SUBMISSION: 'last_submission',
    LAST_MODIFIED: 'last_modified',
    RECOMMENDATIONS: 'recommendations',
    ROLE_TYPE: 'role_type',
    STATUS: 'status',
    UNREVIEWED: 'unreviewed',
    TEAM_TYPE: 'team_type'
} as const;

/**
 * TeamPipelinesSorter
 */
export type TeamPipelinesSorter = typeof TeamPipelinesSorter[keyof typeof TeamPipelinesSorter];

/**
 * TeamPipelinesTablePersonBasicResponse
 */
export type TeamPipelinesTablePersonBasicResponse = {
    /**
     * Name
     *
     * Person's full name
     */
    name: string;
    /**
     * First Name
     *
     * Person's first name
     */
    first_name?: string | null;
    /**
     * Last Name
     *
     * Person's last name
     */
    last_name?: string | null;
    /**
     * Image
     *
     * Path to person image
     */
    image?: string | null;
    /**
     * UUID
     *
     * UUID uniquely identifying a person
     */
    uuid: string;
};

/**
 * TeamPipelinesTableResponse
 */
export type TeamPipelinesTableResponse = {
    /**
     * Meta
     *
     * Metadata about the search result
     */
    search_metadata?: EsMeta | null;
    /**
     * Name
     *
     * Pipeline name
     */
    name: string;
    /**
     * Pipeline type
     *
     * Pipeline type
     */
    pipeline_type?: PipelineType;
    /**
     * Slug
     *
     * Slug uniquely identifying a pipeline
     */
    slug: string;
    /**
     * Is visible to team
     *
     * Is this pipeline visible to the team?
     */
    is_visible_to_team: boolean;
    /**
     * Hidden
     *
     * Is this pipeline hidden? Inverse of is_visible_to_team
     */
    hidden?: boolean;
    /**
     * Active
     *
     * Is this pipeline marked as active?
     */
    active: boolean;
    /**
     * Owner UUID
     *
     * UUID uniquely identifying a person
     */
    assoc_member_uuid?: string | null;
    /**
     * Company
     *
     * Name or nickname of the company for which this pipeline is open
     */
    company?: string | null;
    /**
     * Created
     *
     * Date when this pipeline was created
     */
    created: string;
    /**
     * Creator
     *
     * Person who created this pipeline
     */
    creator?: TeamPipelinesTablePersonBasicResponse | null;
    /**
     * Functional expertise
     *
     * List of functions the ideal candidate has experience in
     */
    function_expertise: Array<Function>;
    /**
     * ID
     *
     * ID uniquely identifying a pipeline
     */
    id: number;
    /**
     * Industry expertise
     *
     * List of industries the ideal candidate has experience in
     */
    industry_expertise: Array<Industry>;
    /**
     * Introductions count
     *
     * Total number of introductions requested for this pipeline
     */
    introductions: number;
    /**
     * Introductions completed count
     *
     * Total number of introductions completed for this pipeline
     */
    introductions_completed_count: number;
    /**
     * Introductions completed unviewed count
     *
     * Number of introductions completed since this pipeline was last viewed
     */
    introductions_completed_unviewed_count: number;
    /**
     * Diverse
     *
     * Is connecting with talent from under-represented groups a specific initiative of this pipeline?
     */
    is_urg: boolean;
    /**
     * Keywords
     *
     * Keywords added by advisors
     */
    keywords?: string | null;
    /**
     * Last modified
     *
     * Date when this pipeline was last modified
     */
    last_modified: string;
    /**
     * Last submission
     *
     * Date of the last submission to this pipeline
     */
    last_submission?: string | null;
    /**
     * Location focus
     *
     * Pipeline location focus
     */
    location?: string | null;
    /**
     * Additional info
     *
     * Brief description of any other relevant parameters including industry keywords, investment themes, position titles, priority companies, etc.
     */
    notes?: string | null;
    /**
     * Pipeline last viewed on
     *
     * Date when this pipeline was last viewed by the user
     */
    pipeline_last_viewed_on?: string | null;
    /**
     * Placement name
     *
     * The placed candidate's name
     */
    placement_name?: string | null;
    /**
     * Placement UUID
     *
     * The placed candidate's executive login, if any
     */
    placement_uuid?: string | null;
    /**
     * Point of contact
     *
     * Point of contact name
     */
    point_of_contact_name?: string | null;
    /**
     * Point of contact UUID
     *
     * UUID uniquely identifying a person
     */
    point_of_contact_uuid?: string | null;
    /**
     * Recommendations count
     *
     * Total number of recommendations made for this pipeline
     */
    recommendations_count: number;
    /**
     * Recommendations unviewed count
     *
     * Number of recommendations published since this pipeline was last viewed
     */
    recommendations_unviewed_count: number;
    /**
     * Recommendations unviewed activity count
     *
     * Number of recommendations with unviewed activity in this pipeline
     */
    recommendations_unviewed_activity_count: number;
    /**
     * Role
     *
     * Pipeline name
     */
    role: string;
    /**
     * Role type
     *
     * Role type
     */
    role_type: PipelineObjective;
    /**
     * Team name
     *
     * Name of the team for which this pipeline was opened
     */
    team_name: string;
    /**
     * Team type
     *
     * Type of company this team belongs to
     */
    team_type?: OrgType;
    /**
     * Unreviewed count
     *
     * Total number of unreviewed recommendations for this pipeline
     */
    unreviewed_count_new: number;
    /**
     * UUID
     *
     * UUID uniquely identifying a pipeline
     */
    uuid: string;
};

/**
 * TeamSearchResponse
 */
export type TeamSearchResponse = {
    /**
     * Meta
     *
     * Metadata about the search result
     */
    search_metadata?: EsMeta | null;
    /**
     * Active
     *
     * Inactive (archived) teams keep their data but are hidden from search and recommendations and closed to their members
     */
    active?: boolean;
    /**
     * Slug
     *
     * Slug uniquely identifying a team
     */
    slug: string;
    /**
     * Name
     *
     * Team name
     */
    name: string;
    /**
     * UUID
     *
     * UUID uniquely identifying a team
     */
    uuid: string;
    /**
     * Additional details
     *
     * Additional details about the team
     */
    additional_details?: string | null;
    /**
     * Company
     *
     * Company to which this team belongs
     */
    company: CompanyBasicResponse;
    /**
     * Functional focus
     *
     * The roles and responsibilities the team typically searches for
     */
    functional_focus?: Array<Function> | null;
    /**
     * Industry focus
     *
     * Sectors the team typically focuses on
     */
    industry_focus?: Array<Industry> | null;
};

/**
 * TeamStatsResponse
 */
export type TeamStatsResponse = {
    /**
     * Current Period
     *
     * Stats for the selected period
     */
    current_period: PeriodStats;
    /**
     * Current Period Dates
     *
     * Current period dates
     */
    current_period_dates: PeriodDates;
};

/**
 * TeamSuggestionResponse
 */
export type TeamSuggestionResponse = {
    /**
     * Team Uuid
     */
    team_uuid: string;
    /**
     * Score
     */
    score: number;
    /**
     * Signals
     */
    signals: {
        [key: string]: number;
    };
    /**
     * Reasons
     */
    reasons: Array<string>;
};

/**
 * TeamTransferPayload
 *
 * Payload for transferring a team to a different company.
 */
export type TeamTransferPayload = {
    /**
     * Target Company Id
     *
     * ID of the company to transfer the team to
     */
    target_company_id: number;
    /**
     * New Team Name
     *
     * Optional new team name (required if name conflicts with existing team in target company)
     */
    new_team_name?: string | null;
    /**
     * New Slug
     *
     * Optional new slug (auto-generated from new_team_name if not provided)
     */
    new_slug?: string | null;
};

/**
 * TeamTrendingCandidatesResponse
 */
export type TeamTrendingCandidatesResponse = {
    /**
     * Search ID
     *
     * Search session ID
     */
    search_id?: string | null;
    /**
     * Recommendations
     *
     * List of trending recommendations for this team
     */
    recommendations: Array<CandidateItem>;
};

/**
 * TeamType
 *
 * What kind of group a team is (BAN-XXXX).
 *
 * Single-select on `search_team.team_type`. Deliberately binary: it is the one
 * cut that needs no judgement, so it backfills reliably. A fund's talent team
 * is INTERNAL — the talent partner is often exactly who trending wants.
 */
export const TeamType = { INTERNAL: 'internal', PORTFOLIO_COMPANY: 'portfolio_company' } as const;

/**
 * TeamType
 *
 * What kind of group a team is (BAN-XXXX).
 *
 * Single-select on `search_team.team_type`. Deliberately binary: it is the one
 * cut that needs no judgement, so it backfills reliably. A fund's talent team
 * is INTERNAL — the talent partner is often exactly who trending wants.
 */
export type TeamType = typeof TeamType[keyof typeof TeamType];

/**
 * TeamUpdate
 *
 * PATCH payload: every field optional, only the ones sent are applied.
 */
export type TeamUpdate = {
    /**
     * Additional details
     *
     * Additional details about the team
     */
    additional_details?: string | null;
    /**
     * Client size
     *
     * Typical client size
     */
    company_type?: SearchFirmType | null;
    /**
     * Fund type
     *
     * This team's own fund type. Null inherits the company's value (the normal case); an empty list means this team explicitly has no fund type even if its company does; a non-empty list overrides the company, e.g. a PE deal team inside a VC firm.
     */
    fund_type?: Array<FundType> | null;
    /**
     * Functional focus
     *
     * The roles and responsibilities the team typically searches for
     */
    functional_focus?: Array<Function> | null;
    /**
     * Industry focus
     *
     * Sectors the team typically focuses on
     */
    industry_focus?: Array<Industry> | null;
    /**
     * Name
     *
     * Team name
     */
    team_name?: string | null;
    /**
     * Team type
     *
     * Whether this team is the firm itself or a company the firm invested in.
     */
    team_type?: TeamType | null;
    /**
     * Slug
     *
     * Team slug if we want to set it ourselves instead of using the generated value
     */
    slug?: string | null;
    /**
     * Active
     *
     * Admins only. Set false to archive the team: it keeps its data but drops out of search results, recommendations and emails, its open pipelines are closed and its members lose access. Archiving is permanent
     */
    active?: boolean | null;
};

/**
 * TeamUpdateV2
 */
export type TeamUpdateV2 = {
    /**
     * Additional details
     *
     * Additional details about the team
     */
    additional_details?: string | null;
    /**
     * Client size
     *
     * Typical client size
     */
    client_size: SearchFirmType;
    /**
     * Functional focus
     *
     * The roles and responsibilities the team typically searches for
     */
    functional_focus?: Array<Function> | null;
    /**
     * Fund type
     *
     * This team's own fund type. Null inherits the company's value (the normal case); an empty list means this team explicitly has no fund type even if its company does; a non-empty list overrides the company, e.g. a PE deal team inside a VC firm.
     */
    fund_type?: Array<FundType> | null;
    /**
     * Industry focus
     *
     * Sectors the team typically focuses on
     */
    industry_focus?: Array<Industry> | null;
    /**
     * Name
     *
     * Team name
     */
    team_name: string;
    /**
     * Team type
     *
     * Whether this team is the firm itself or a company the firm invested in.
     */
    team_type?: TeamType | null;
};

/**
 * TeamWithMembersBasicResponse
 */
export type TeamWithMembersBasicResponse = {
    /**
     * Members
     */
    members: Array<PersonBasicEmailResponse>;
    /**
     * Active
     *
     * Inactive (archived) teams keep their data but are hidden from search and recommendations and closed to their members
     */
    active?: boolean;
    /**
     * Slug
     *
     * Slug uniquely identifying a team
     */
    slug: string;
    /**
     * Name
     *
     * Team name
     */
    name: string;
};

/**
 * TeamsFilters
 */
export type TeamsFilters = {
    /**
     * Declined Status
     *
     * Filter by whether the team has declined this candidate. OR within the category, AND with other filter categories.
     */
    declined_status?: Array<DeclinedStatusFilter> | null;
    /**
     * Exclude
     */
    exclude?: Array<TeamExclusionCriteria> | null;
    /**
     * Fund Type
     *
     * Filter by the team's EFFECTIVE fund type — its own override if it has one, otherwise its company's. Matches a team carrying any of the selected values, so a firm tagged both VC and PE appears under either.
     */
    fund_type?: Array<FundType> | null;
    /**
     * Functional Focus
     */
    functional_focus?: Array<Function> | null;
    /**
     * Has Preregistered Users
     *
     * Filter by teams that have at least one preregistered member (True = has preregistered users, False = no preregistered users)
     */
    has_preregistered_users?: boolean | null;
    /**
     * Industry Focus
     */
    industry_focus?: Array<Industry> | null;
    /**
     * Introduction Status
     *
     * Filter by whether the team has been introduced to this candidate. For exec teams, 'introduced' covers all paths: B4B intros, EvaluationHistory.FACILITATED, P2P (INTRO_MADE), and event co-attendance. For regular teams, 'introduced' means EvaluationHistory.FACILITATED on that team. OR within the category, AND with other filter categories.
     */
    introduction_status?: Array<IntroductionStatusFilter> | null;
    /**
     * Recommendation Status
     *
     * Filter by whether the team has received a published recommendation for this candidate. OR within the category, AND with other filter categories.
     */
    recommendation_status?: Array<RecommendationStatusFilter> | null;
    /**
     * Team Type
     */
    team_type?: Array<OrgType> | null;
};

/**
 * TeamsSearchFilterPaginatedResponse
 */
export type TeamsSearchFilterPaginatedResponse = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Results Hidden
     */
    results_hidden: Array<OffLimitsTeamSchema>;
    /**
     * Results
     */
    results: Array<TeamsSearchTeamResponse>;
    /**
     * Search Id
     */
    search_id: string;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCountsResponse;
};

/**
 * TeamsSearchFilterPayload
 */
export type TeamsSearchFilterPayload = {
    /**
     * Phrase
     *
     * Search phrase
     */
    phrase?: string | null;
    /**
     * Search ID
     *
     * Search session ID
     */
    search_id?: string | null;
    /**
     * Page
     *
     * Page number
     */
    page?: number;
    /**
     * Page size
     *
     * Page size
     */
    page_size?: number;
    /**
     * Candidate Uuid
     *
     * When defined, off-limits, last/next shared, and intro columns won't be null
     */
    candidate_uuid: string;
    /**
     * Filters
     *
     * Filter results by these criteria
     */
    filter_data?: TeamsFilters | null;
    /**
     * Published On
     *
     * When defined, specifies recommendation datetime threshold for concurrent trending filter
     */
    published_on?: string | null;
    /**
     * Team Uuids
     *
     * Get specified teams only, return nothing when empty
     */
    team_uuids?: Array<string> | null;
    /**
     * Suggested restriction
     *
     * Restrict to the candidate's trending suggestion pool (the full ranking chosen by `ranking` after the trending hygiene exclusions). Filters, phrase, paging and totals compose with the pool; without a phrase, results come back in rank order and each hit carries `suggestion_score` / `suggestion_reasons`. Mutually exclusive with `team_uuids`.
     */
    suggested?: TeamsSuggestedRestriction | null;
};

/**
 * TeamsSearchTeamResponse
 */
export type TeamsSearchTeamResponse = {
    /**
     * Meta
     *
     * Metadata about the search result
     */
    search_metadata?: EsMeta | null;
    /**
     * Active
     *
     * Inactive (archived) teams keep their data but are hidden from search and recommendations and closed to their members
     */
    active?: boolean;
    /**
     * Slug
     *
     * Slug uniquely identifying a team
     */
    slug: string;
    /**
     * Name
     *
     * Team name
     */
    name: string;
    /**
     * UUID
     *
     * UUID uniquely identifying a team
     */
    uuid: string;
    /**
     * Additional details
     *
     * Additional details about the team
     */
    additional_details?: string | null;
    /**
     * Client size
     *
     * Typical client size
     */
    client_size?: SearchFirmType | null;
    /**
     * Company
     *
     * Company to which this team belongs
     */
    company: CompanyExtendedResponse;
    /**
     * Last/next date recommended
     *
     * The next scheduled/last published recommendation for provided candidate to this team
     */
    date_recommended?: string | null;
    /**
     * Effective fund type
     *
     * The fund type that actually applies to this team: its own override if set, otherwise its company's. This is what fund-type filters match on.
     */
    effective_fund_type?: Array<FundType> | null;
    /**
     * Fund type
     *
     * Team-level override of the company's fund type. Empty means the team inherits its company's value, which is the normal case.
     */
    fund_type?: Array<FundType> | null;
    /**
     * Functional focus
     *
     * The roles and responsibilities the team typically searches for
     */
    functional_focus?: Array<Function> | null;
    /**
     * ID
     *
     * ID uniquely identifying a team
     */
    id: number;
    /**
     * Industry focus
     *
     * The sectors the team typically focuses on
     */
    industry_focus?: Array<Industry> | null;
    /**
     * Is introduction requested?
     *
     * Has this team requested an introduction with the provided candidate in the past?
     */
    is_introduction_requested?: boolean | null;
    /**
     * Is off-limits?
     *
     * Is this team off-limits for provided candidate?
     */
    is_off_limits?: boolean | null;
    /**
     * Team type
     *
     * Whether this team is the firm itself or a company it invested in
     */
    team_type?: TeamType | null;
    /**
     * Declined Candidate
     *
     * Has this team marked the candidate as not interesting
     */
    declined_candidate?: boolean | null;
    /**
     * Has No Members
     *
     * Does this team have members?
     */
    has_no_members?: boolean | null;
    /**
     * Inactive
     *
     * A team that has either has all inactive users or users who are active but none have logged in in the last 6 months
     */
    inactive?: boolean | null;
    /**
     * Previously Introduced
     *
     * Has an introduction with the provided candidate been facilitated the past?
     */
    previously_introduced?: boolean | null;
    /**
     * Previously Introduced On
     *
     * Date of the most recent introduction between the candidate and this team (across all introduction paths), if introduced.
     */
    previously_introduced_on?: string | null;
    /**
     * Previously Shared
     *
     * Last time (if ever) the candidate has been shared with the team
     */
    previously_shared?: string | null;
    /**
     * Has Preregistered Users
     *
     * True if any team members receive preregistration emails
     */
    has_preregistered_users?: boolean | null;
    /**
     * Executive's current role
     *
     * For executive teams only: the executive's current role, so pickers can show real content instead of ghost-team fields
     */
    exec_current_role?: string | null;
    /**
     * Executive's current company
     *
     * For executive teams only: the executive's current company
     */
    exec_current_company?: string | null;
    /**
     * Suggestion score
     *
     * Trending suggestion score for the candidate (weighted sum of min-max normalized signals). Set only when the search was restricted to the suggestion pool via `suggested`.
     */
    suggestion_score?: number | null;
    /**
     * Suggestion reasons
     *
     * Why the team was suggested for the candidate, in display order. Set only when the search was restricted via `suggested`.
     */
    suggestion_reasons?: Array<string> | null;
    /**
     * Suggestion match
     *
     * Trending suggestion match, 0-100: the suggestion score as a share of the best possible score in this pool (the summed dial weights of the signals any team earns). 100 = best in the pool on every earnable signal; 0 = no evidence on any signal. Set only when the search was restricted via `suggested`.
     */
    suggestion_match?: number | null;
    /**
     * Team added
     *
     * When the team was added. Set only for `suggested` searches with ranking `explore`.
     */
    suggestion_team_created?: string | null;
    /**
     * Trendings
     *
     * Trending campaigns the team was ever in. A campaign is a published trending with at least 4 teams; smaller ones are usually logged intros and don't count. Set only for `suggested` searches with ranking `explore`.
     */
    suggestion_trendings_total?: number | null;
    /**
     * Last trended
     *
     * Most recent such campaign. Set only for `suggested` searches with ranking `explore`.
     */
    suggestion_last_trended_on?: string | null;
};

/**
 * TeamsSuggestedPayload
 */
export type TeamsSuggestedPayload = {
    /**
     * Candidate Uuid
     */
    candidate_uuid: string;
    /**
     * Limit
     */
    limit?: number;
    /**
     * Selected Team Uuids
     *
     * Teams already added to the campaign. When non-empty, the two cart dials activate (co-occurrence and profile overlap with the cart), and the selected teams are excluded from the results.
     */
    selected_team_uuids?: Array<string>;
    /**
     * User dial positions; omit for the recommended formula.
     */
    weights?: SuggestionWeights | null;
};

/**
 * TeamsSuggestedResponse
 */
export type TeamsSuggestedResponse = {
    /**
     * Candidate Uuid
     */
    candidate_uuid: string;
    /**
     * Results
     */
    results: Array<TeamSuggestionResponse>;
};

/**
 * TeamsSuggestedRestriction
 *
 * Restrict a team search to the candidate's trending suggestion pool.
 *
 * The pool is the full ranking chosen by `ranking` (see
 * app/helpers/team_suggestions.py) after the trending hygiene exclusions;
 * every other filter, the search phrase, paging and totals compose with it
 * server-side. Without a phrase the results come back in rank order.
 */
export type TeamsSuggestedRestriction = {
    /**
     * Selected Team Uuids
     *
     * Teams already added to the campaign. When non-empty, the two cart dials activate (co-occurrence and profile overlap with the cart), and the selected teams are excluded from the results.
     */
    selected_team_uuids?: Array<string>;
    /**
     * User dial positions; omit for the recommended formula. Applies to the `top` ranking only.
     */
    weights?: SuggestionWeights | null;
    /**
     * `top`: the main ranking. `explore`: new and rarely trended teams (at most 2 campaigns in the last year) that fit the candidate, ranked without trending history; results carry `suggestion_team_created`, `suggestion_trendings_total` and `suggestion_last_trended_on`, and no `suggestion_match`.
     */
    ranking?: TrendingSuggestionRanking;
};

/**
 * TotalCounts
 */
export type TotalCounts = {
    /**
     * All
     */
    all: number;
};

/**
 * TotalCountsCandidateCompanyMetadataImportResponse
 */
export type TotalCountsCandidateCompanyMetadataImportResponse = {
    /**
     * All
     *
     * Total rows processed
     */
    all?: number;
    /**
     * Succeeded
     *
     * Rows successfully imported
     */
    succeeded?: number;
    /**
     * Failed
     *
     * Rows that failed to import
     */
    failed?: number;
};

/**
 * TotalCountsResponse
 */
export type TotalCountsResponse = {
    /**
     * All
     */
    all: number;
};

/**
 * TrendingCandidateFilterSortPayload
 */
export type TrendingCandidateFilterSortPayload = {
    /**
     * Page
     *
     * Page number
     */
    page?: number;
    /**
     * Page size
     *
     * Page size
     */
    size?: number;
    /**
     * Filters
     *
     * Filter results by these criteria
     */
    filter_data?: TrendingCandidatesFilters | null;
    /**
     * Sort
     *
     * Sort results by these rules
     */
    sort_rules: Array<TrendingCandidateSort>;
};

/**
 * TrendingCandidateResponse
 */
export type TrendingCandidateResponse = {
    candidate: CandidateCompanyRoleResponse;
    /**
     * Advisor
     *
     * Advisor basic info
     */
    advisor: PersonBasicResponse;
    /**
     * Favorite count
     *
     * Number of users who favorite this candidate
     */
    favorite_count: number;
    /**
     * Intro facilitated count
     *
     * Candidate's facilitated count
     */
    introductions_facilitated_count: number;
    /**
     * Intro request count
     *
     * Candidate's intro requests count
     */
    introductions_requested_count: number;
    /**
     * Recommendation count
     *
     * Candidate's recommendation count
     */
    recommendation_count: number;
    /**
     * Rank
     *
     * Candidate's trending rank
     */
    rank: number;
};

/**
 * TrendingCandidateSort
 */
export type TrendingCandidateSort = {
    /**
     * Column
     *
     * Sort results on this column
     */
    column: TrendingCandidateSortColumn;
    /**
     * Direction
     *
     * Sort results in this direction
     */
    direction?: SortDirection;
};

/**
 * TrendingCandidateSortColumn
 */
export const TrendingCandidateSortColumn = { RANK: 'rank' } as const;

/**
 * TrendingCandidateSortColumn
 */
export type TrendingCandidateSortColumn = typeof TrendingCandidateSortColumn[keyof typeof TrendingCandidateSortColumn];

/**
 * TrendingCandidatesFilters
 */
export type TrendingCandidatesFilters = {
    /**
     * Advisor Id
     */
    advisor_id?: Array<string> | null;
    /**
     * Function Expertise
     */
    function_expertise?: Array<Function> | null;
    /**
     * Industry Expertise
     */
    industry_expertise?: Array<Industry> | null;
    /**
     * Type
     */
    type?: Array<ClientType> | null;
    /**
     * Pathway
     */
    pathway?: Array<CandidatePathway> | null;
};

/**
 * TrendingCandidatesPaginatedResponse
 */
export type TrendingCandidatesPaginatedResponse = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Results
     */
    results: Array<TrendingCandidateResponse>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCountsResponse;
};

/**
 * TrendingMessageKeysResponse
 *
 * What a Plain trending email's subject and message can say.
 */
export type TrendingMessageKeysResponse = {
    /**
     * Default message
     *
     * Message a new campaign starts from
     */
    default_message: string;
    /**
     * Default subject
     *
     * Subject a new campaign starts from
     */
    default_subject: string;
    /**
     * Link keys
     *
     * Message keys that become an address, written as [words]({{key}}) or on their own
     */
    link_keys: Array<string>;
    /**
     * Message keys
     *
     * Keys the message can use, as {{key}}
     */
    message_keys: Array<string>;
    /**
     * Subject keys
     *
     * Keys the subject can use, as {{key}}
     */
    subject_keys: Array<string>;
};

/**
 * TrendingMessageResponse
 *
 * A message an advisor wrote for one candidate's Trending campaigns.
 * Campaigns that sent the same body text count as one message.
 */
export type TrendingMessageResponse = {
    /**
     * Advisor
     *
     * Advisor who sent the message the last time it was used
     */
    advisor?: PersonBasicResponse | null;
    /**
     * Body
     *
     * Email body text, as the advisor wrote it
     */
    body: string;
    /**
     * Last used on
     *
     * When a campaign last sent, or is scheduled to send, this message
     */
    last_used_on: string;
    /**
     * Subject
     *
     * Email subject line the last time the message was used
     */
    subject?: string | null;
    /**
     * Submission UUID
     *
     * Trending submission that last used this message
     */
    submission_uuid: string;
    /**
     * Times used
     *
     * Number of Trending campaigns that sent this message
     */
    times_used: number;
};

/**
 * TrendingScheduleCampaignSpreads
 */
export const TrendingScheduleCampaignSpreads = { FIRST_AVAILABLE: 'first_available', CUSTOM: 'custom' } as const;

/**
 * TrendingScheduleCampaignSpreads
 */
export type TrendingScheduleCampaignSpreads = typeof TrendingScheduleCampaignSpreads[keyof typeof TrendingScheduleCampaignSpreads];

/**
 * TrendingSchedulePayload
 */
export type TrendingSchedulePayload = {
    /**
     * Campaign Spread
     *
     * How we want to spread the campaign
     */
    campaign_spread?: TrendingScheduleCampaignSpreads;
    /**
     * Campaign Start Date
     *
     * Date to start the campaign.
     */
    campaign_start_date?: string;
    /**
     * Campaign Weeks
     *
     * Number of weeks we want the custom campaign to be spread out over.
     */
    campaign_weeks?: number | null;
    /**
     * Team UUIDs
     *
     * List of team uuids
     */
    team_uuids?: Array<string>;
};

/**
 * TrendingScheduleResponse
 */
export type TrendingScheduleResponse = {
    /**
     * Campaign Spread
     *
     * How we want to spread the campaign
     */
    campaign_spread: TrendingScheduleCampaignSpreads;
    /**
     * Optimized Submissions
     *
     * List of optimized submissions
     */
    optimized_submissions: Array<TrendingScheduleTeamSubmission>;
    /**
     * Campaign Start Date
     *
     * Date of UTC datetime requested by client for when campaign starts
     */
    campaign_start_date: string;
    /**
     * Campaign Weeks
     *
     * Client requested number of weeks to schedule custom campaign for. 0 for first available campaign spreads
     */
    campaign_weeks: number;
    /**
     * Display Weeks
     *
     * Number of weeks we need to display all returned scheduled dates
     */
    display_weeks: number;
    /**
     * Team UUIDs
     *
     * List of team uuids we return scheduled dates for
     */
    team_uuids: Array<string>;
    /**
     * Starts Today
     *
     * Whether the Campaign start date is today
     */
    starts_today: boolean;
};

/**
 * TrendingScheduleTeamSubmission
 */
export type TrendingScheduleTeamSubmission = {
    /**
     * Team UUID
     *
     * UUID uniquely identifying a team
     */
    team_uuid: string;
    /**
     * Scheduling Method
     *
     * How we actually scheduled the team
     */
    scheduling_method: TrendingSchedulingMethod;
    /**
     * Scheduled Date
     *
     * Date when this submission will be published
     */
    scheduled_date: string;
    /**
     * Blackout Dates
     *
     * Dates when this submission cannot be scheduled
     */
    blackout_dates: Array<string>;
};

/**
 * TrendingSchedulingMethod
 */
export const TrendingSchedulingMethod = { FIRST_AVAILABLE: 'first_available', OPTIMIZED: 'optimized' } as const;

/**
 * TrendingSchedulingMethod
 */
export type TrendingSchedulingMethod = typeof TrendingSchedulingMethod[keyof typeof TrendingSchedulingMethod];

/**
 * TrendingSuggestionRanking
 *
 * Which trending team ranking a `suggested` team search uses.
 */
export const TrendingSuggestionRanking = { TOP: 'top', EXPLORE: 'explore' } as const;

/**
 * TrendingSuggestionRanking
 *
 * Which trending team ranking a `suggested` team search uses.
 */
export type TrendingSuggestionRanking = typeof TrendingSuggestionRanking[keyof typeof TrendingSuggestionRanking];

/**
 * TypeFilter
 */
export const TypeFilter = {
    ROLE: 'role',
    SUBMISSION: 'submission',
    TEAM: 'team'
} as const;

/**
 * TypeFilter
 */
export type TypeFilter = typeof TypeFilter[keyof typeof TypeFilter];

/**
 * UUIDResponse
 */
export type UuidResponse = {
    /**
     * UUID
     *
     * UUID uniquely identifying an object
     */
    uuid: string;
};

/**
 * UndoExecutiveActivationBlocker
 *
 * Why an executive activation can no longer be undone.
 */
export const UndoExecutiveActivationBlocker = { INVITATION_SENT: 'invitation_sent', TEAM_HAS_CONTENT: 'team_has_content' } as const;

/**
 * UndoExecutiveActivationBlocker
 *
 * Why an executive activation can no longer be undone.
 */
export type UndoExecutiveActivationBlocker = typeof UndoExecutiveActivationBlocker[keyof typeof UndoExecutiveActivationBlocker];

/**
 * UpdateCandidate
 *
 * Body of ``PATCH /api/v2/candidates/{candidate_uuid}``: a JSON merge patch
 * (RFC 7396). A field that is absent is left alone; ``null`` clears it. Fields
 * ``CreateCandidate`` requires cannot be cleared. No cross-field rules: any
 * combination of tier, advisor, client owner, type and pathway is accepted.
 *
 * The fields are ``CreateCandidate``'s, all optional, except the ones written
 * as rows elsewhere: ``referrers`` (their own endpoints), the blackout dates
 * and ``resume``.
 */
export type UpdateCandidate = {
    /**
     * First Name
     *
     * Person's first name
     */
    first_name?: string | null;
    /**
     * Last Name
     *
     * Person's last name
     */
    last_name?: string | null;
    /**
     * LinkedIn URL
     *
     * Link to the person's LinkedIn profile
     */
    linkedin_url?: string | null;
    /**
     * Advisor UUID
     *
     * UUID uniquely identifying an advisor
     */
    advisor_uuid?: string | null;
    /**
     * Email
     *
     * Email associated with the person
     */
    email?: string | null;
    /**
     * Company
     *
     * Candidate's current company
     */
    company?: string | null;
    /**
     * Role
     *
     * Candidate's current role
     */
    role?: string | null;
    /**
     * Client owner uuid
     *
     * Uuid for the client owner of the candidate
     */
    client_owner_uuid?: string | null;
    /**
     * URG
     *
     * Is this candidate from an under-represented group?
     */
    is_urg?: boolean | null;
    /**
     * Functional expertise
     *
     * List of functions candidate has experience in
     */
    function_expertise?: Array<Function> | null;
    /**
     * Target functions
     *
     * Functional expertise candidate would like to get
     */
    function_target?: Array<Function> | null;
    /**
     * Industry expertise
     *
     * List of industries candidate has experience in
     */
    industry_expertise?: Array<Industry> | null;
    /**
     * Target industries
     *
     * Functional expertise candidate would like to get
     */
    industry_target?: Array<Industry> | null;
    /**
     * Board/advisory role interest
     *
     * Interest in getting board or advisory roles
     */
    interest_role_board_advisory?: InterestRole | null;
    /**
     * Operating role interest
     *
     * Interest in getting operating roles
     */
    interest_role_operating?: InterestRole | null;
    /**
     * Personal interests
     *
     * List of personal interests
     */
    interests_personal?: Array<InterestPersonal> | null;
    /**
     * Professional interests
     *
     * List of professional interests
     */
    interests_professional?: Array<InterestProfessional> | null;
    /**
     * Interests notes
     *
     * Notes on interests
     */
    interests_notes?: string | null;
    /**
     * Wants intros
     *
     * Whether the candidate wants introductions
     */
    wants_intros?: boolean | null;
    /**
     * Interest in relocation
     *
     * Openness to relocation
     */
    interest_relocation?: boolean | null;
    /**
     * Relocation notes
     *
     * Relocation preferences or constraints
     */
    interest_relocation_notes?: string | null;
    /**
     * Introduction email type
     *
     * Introduction email type (automatic or manual)
     */
    intro_email_type?: IntroEmailType | null;
    /**
     * Kickoff date
     *
     * Date candidate kicked off
     */
    kickoff_date?: string | null;
    /**
     * Keywords
     *
     * List of keywords
     */
    keywords?: Array<string> | null;
    /**
     * Target network
     *
     * Areas in which this person wants to build their network
     */
    network_target?: string | null;
    /**
     * Notes
     *
     * Internal advisor notes on the candidate
     */
    notes?: string | null;
    /**
     * Operating highlights
     *
     * List of key operating experiences
     */
    operating_highlights?: Array<OperatingHighlights> | null;
    /**
     * Passions
     *
     * Hobbies, locations, niche industries, personal interests, etc.
     */
    passions?: string | null;
    /**
     * Image
     *
     * Name of the uploaded image from the response
     */
    image?: string | null;
    /**
     * Tier
     *
     * Candidate tier
     */
    tier?: CandidateTier | null;
    /**
     * Type
     *
     * Candidate type
     */
    type?: ClientType | null;
    /**
     * Pathway
     *
     * Banff service pathway for the candidate
     */
    pathway?: CandidatePathway | null;
    /**
     * Source company tags
     *
     * For a candidate with a source company, an optional list of custom tags
     */
    source_company_tags?: Array<SourceCompanyTags> | null;
};

/**
 * UpdateExecutiveClientSettingsPayload
 */
export type UpdateExecutiveClientSettingsPayload = {
    /**
     * First Name
     */
    first_name?: string | null;
    /**
     * Last Name
     */
    last_name?: string | null;
    /**
     * Email
     */
    email?: string | null;
    /**
     * Receive Trending Emails
     */
    receive_trending_emails?: boolean | null;
    /**
     * Receive Pipeline Emails
     */
    receive_pipeline_emails?: boolean | null;
};

/**
 * UpdateExecutiveSettingsPayload
 */
export type UpdateExecutiveSettingsPayload = {
    access_level?: ExecutiveAccessLevel | null;
    payment_status?: ExecutivePaymentStatus | null;
    /**
     * Hiring Pipeline Limit
     */
    hiring_pipeline_limit?: number | null;
    /**
     * Receive Trending Emails
     */
    receive_trending_emails?: boolean | null;
    /**
     * Receive Pipeline Emails
     */
    receive_pipeline_emails?: boolean | null;
    /**
     * Content Access Level
     */
    content_access_level?: string | null;
    /**
     * Email
     *
     * Update login email (Person.email)
     */
    email?: string | null;
};

/**
 * UpdateMultipleRecommendationDraftPayload
 */
export type UpdateMultipleRecommendationDraftPayload = {
    /**
     * Recommendation UUIDs
     *
     * List of recommendation UUIDs to update
     */
    recommendation_uuids: Array<string>;
    /**
     * Draft mode
     *
     * Draft mode status for the recommendations
     */
    draft_mode?: RecommendationDraftMode | null;
    /**
     * Draft assignee ID
     *
     * UUID of the person assigned to review the drafts
     */
    draft_assignee_id?: string | null;
};

/**
 * UpdatePipelinePayload
 */
export type UpdatePipelinePayload = {
    /**
     * Is visible to client
     *
     * Hide this pipeline from its team members?
     */
    is_visible_to_team?: boolean | null;
    /**
     * Keywords
     *
     * List of keywords
     */
    keywords: Array<string>;
    /**
     * Placement
     *
     * Placed candidate UUID
     */
    placed_candidate_id?: string | null;
    /**
     * Is proactive
     *
     * Is this pipeline proactive?
     */
    is_proactive?: boolean;
    /**
     * Attendees
     *
     * Number of attendees expected for an event pipeline
     */
    attendees?: PipelineAttendees | null;
    /**
     * Company
     *
     * Name or nickname of the company for which this pipeline is open
     */
    company?: string | null;
    /**
     * Company size (annual revenue)
     *
     * Size of company for which this pipeline is open
     */
    company_size?: CompanySize | null;
    /**
     * Company type
     *
     * Type of company for which this pipeline is open
     */
    company_type?: CompanyType | null;
    /**
     * Event date
     *
     * Date of the event
     */
    event_date?: string | null;
    /**
     * Event name
     *
     * Name of the event
     */
    event_name?: string | null;
    /**
     * Functional expertise
     *
     * List of functions the ideal candidate has experience in
     */
    function_expertise: Array<Function>;
    /**
     * Industry expertise
     *
     * List of industries the ideal candidate has experience in
     */
    industry_expertise: Array<Industry>;
    /**
     * Is URG
     *
     * Is the role specifically targeting under represented candidates?
     */
    is_urg?: boolean | null;
    /**
     * Location
     *
     * The location specified for the pipeline role
     */
    location?: string | null;
    /**
     * Name
     *
     * Pipeline name
     */
    name: string;
    /**
     * Notes
     *
     * Any additional details to help us calibrate recommendations? (ex: target companies, benchmark profiles, etc.)
     */
    notes?: string | null;
    /**
     * Pipeline type
     *
     * Pipeline type
     */
    pipeline_type?: PipelineType | null;
    /**
     * Point of contact
     *
     * Point of contact person ID
     */
    point_of_contact_person_id?: string | null;
    /**
     * Purpose
     *
     * Purpose of the pipeline (if type PipelineType.OTHER)
     */
    purpose?: string | null;
    /**
     * Role type
     *
     * Role type
     */
    role_type: PipelineRoleType;
};

/**
 * UserBulkDeleteResult
 *
 * One requested uuid. ``clerk_error`` is set when the row was deleted but
 * the Clerk user could not be: the login still exists in Clerk and has to be
 * removed there by hand.
 */
export type UserBulkDeleteResult = {
    /**
     * User Uuid
     */
    user_uuid: string;
    /**
     * Status
     */
    status: 'deleted' | 'not_found' | 'error';
    /**
     * Error
     */
    error?: string | null;
    /**
     * Clerk Error
     */
    clerk_error?: string | null;
};

/**
 * UserCompanyPayload
 */
export type UserCompanyPayload = {
    /**
     * Company ID
     *
     * The company the login joins
     */
    company_id: number;
};

/**
 * UserDetailedResponse
 *
 * Detailed user response including company and role information
 */
export type UserDetailedResponse = {
    /**
     * ID
     *
     * Same as uuid
     */
    id: string;
    /**
     * UUID
     *
     * UUID uniquely identifying a user
     */
    uuid: string;
    /**
     * Email
     *
     * User email address
     */
    email: string;
    /**
     * First Name
     *
     * User's first name
     */
    first_name?: string | null;
    /**
     * Last Name
     *
     * User's last name
     */
    last_name?: string | null;
    /**
     * Full Name
     *
     * User's full name
     */
    full_name?: string | null;
    /**
     * Company
     *
     * Company the user belongs to (includes payment status)
     */
    company?: CompanyExtendedResponse | null;
    /**
     * Created
     *
     * Timestamp when the user record was created
     */
    created: string;
    /**
     * First Login
     *
     * Timestamp when the user record was first logged in
     */
    first_login?: string | null;
    /**
     * Last Login
     *
     * Timestamp of user's last login
     */
    last_login?: string | null;
    /**
     * Active
     *
     * Whether the user is active
     */
    active: boolean;
    /**
     * Pending
     *
     * Whether the user's invite is still pending (no clerk_id)
     */
    pending: boolean;
    /**
     * Permit Preregistration Notifications
     *
     * Whether user can receive marketing emails before completing signup
     */
    permit_preregistration_notifications: boolean;
    /**
     * Notes External
     *
     * External notes for this user profile
     */
    notes_external?: string | null;
    /**
     * Notes Internal
     *
     * Internal notes for this user profile
     */
    notes_internal?: string | null;
    /**
     * LinkedIn URL
     *
     * LinkedIn URL for this user profile
     */
    linkedin_url?: string | null;
    /**
     * Wants Intros
     *
     * Whether this user currently wants intros
     */
    wants_intros?: boolean | null;
    /**
     * Wants Intro Followups
     *
     * Whether follow-up on intro requests is wanted internally
     */
    wants_intro_followups?: boolean | null;
    /**
     * Handle Intros As Exec
     *
     * Whether intro handling should be done as executive
     */
    handle_intros_as_exec?: boolean | null;
    /**
     * Teams Count
     *
     * Total number of teams the user belongs to
     */
    teams_count: number;
    /**
     * Teams
     *
     * List of teams the user belongs to
     */
    teams?: Array<UserTeamBasicResponse>;
    /**
     * Roles
     *
     * List of role objects the user has
     */
    roles?: Array<UserRoleResponse>;
    /**
     * Identity UUID
     *
     * UUID of the identity this login belongs to: the one row per real human that also holds the candidate, if there is one.
     */
    identity_uuid?: string | null;
    /**
     * Candidate UUID
     *
     * UUID of the candidate that shares this person's identity (executives and similar).
     */
    candidate_uuid?: string | null;
    /**
     * Candidate
     *
     * Basic profile of the candidate that shares this person's identity (executives and similar; same as candidate_uuid).
     */
    candidate?: CandidateBasicResponse | null;
    /**
     * User type
     *
     * executive/admin/enterprise
     */
    user_type: UserType;
    /**
     * Search Metadata
     *
     * Search result metadata, including highlighted fields
     */
    search_metadata?: EsMeta | null;
};

/**
 * UserInvitationPersonBasicResponse
 *
 * Basic person fields for invitation sender/recipient.
 */
export type UserInvitationPersonBasicResponse = {
    /**
     * UUID
     *
     * UUID uniquely identifying a person
     */
    uuid: string;
    /**
     * Email
     *
     * Person email address
     */
    email: string;
    /**
     * First Name
     *
     * Person first name
     */
    first_name?: string | null;
    /**
     * Last Name
     *
     * Person last name
     */
    last_name?: string | null;
    /**
     * Full Name
     *
     * Person full name
     */
    full_name?: string | null;
};

/**
 * UserInvitationResponse
 *
 * Invitation history record for a user.
 */
export type UserInvitationResponse = {
    /**
     * ID
     *
     * Unique identifier for the invitation
     */
    id: number;
    /**
     * Created
     *
     * Timestamp when the invitation record was created
     */
    created: string;
    /**
     * Last Modified
     *
     * Timestamp when the invitation record was last modified
     */
    last_modified: string;
    /**
     * Reminder Sent
     *
     * Timestamp when reminder email was sent
     */
    reminder_sent?: string | null;
    /**
     * Clerk Invitation ID
     *
     * Clerk invitation ID associated with this invitation
     */
    clerk_invitation_id?: string | null;
    /**
     * From ID
     *
     * UUID of person who sent the invitation
     */
    from_id?: string | null;
    /**
     * To ID
     *
     * UUID of person who received the invitation
     */
    to_id?: string | null;
    /**
     * Team ID
     *
     * Team ID associated with this invitation
     */
    team_id?: number | null;
    /**
     * Sender
     *
     * Person who sent the invitation
     */
    sender?: UserInvitationPersonBasicResponse | null;
    /**
     * Recipient
     *
     * Person who received the invitation
     */
    recipient?: UserInvitationPersonBasicResponse | null;
    /**
     * Team
     *
     * Team associated with this invitation
     */
    team?: UserTeamBasicResponse | null;
};

/**
 * UserInviteResponse
 */
export type UserInviteResponse = {
    /**
     * Company
     *
     * Company with which this person is associated
     */
    company: CompanyBasicTypeResponse;
    /**
     * Email
     *
     * Email associated with the person
     */
    email: string;
    /**
     * Name
     *
     * Person's full name
     */
    full_name: string;
    /**
     * Teams
     *
     * Teams associated with this person
     */
    teams: Array<TeamBasicResponse>;
    /**
     * UUID
     *
     * UUID uniquely identifying a person
     */
    uuid: string;
};

/**
 * UserRoleResponse
 *
 * Role information for user response
 */
export type UserRoleResponse = {
    /**
     * ID
     *
     * Unique identifier for the role
     */
    id: number;
    /**
     * Role enum value
     */
    role: Role;
    /**
     * Company ID
     *
     * Company ID associated with this role
     */
    company_id?: number | null;
    /**
     * Company
     *
     * Company associated with this role
     */
    company?: CompanyBasicTypeResponse | null;
    /**
     * Team ID
     *
     * Team ID associated with this role
     */
    team_id?: number | null;
    /**
     * Team
     *
     * Team associated with this role
     */
    team?: UserTeamBasicResponse | null;
};

/**
 * UserTeamBasicResponse
 *
 * Basic team information for user response
 */
export type UserTeamBasicResponse = {
    /**
     * ID
     *
     * Unique identifier for a team
     */
    id: number;
    /**
     * UUID
     *
     * UUID uniquely identifying a team
     */
    uuid: string;
    /**
     * Slug
     *
     * Slug uniquely identifying a team
     */
    slug: string;
    /**
     * Name
     *
     * Team name
     */
    team_name: string;
};

/**
 * UserTeamPayload
 */
export type UserTeamPayload = {
    /**
     * Team UUID
     *
     * The team the login joins
     */
    team_uuid: string;
    /**
     * Lead
     *
     * A lead seat instead of a member seat
     */
    lead?: boolean;
};

/**
 * UserType
 *
 * Classification for users list / detail
 */
export const UserType = {
    EXECUTIVE: 'executive',
    ENTERPRISE: 'enterprise',
    ADMIN: 'admin'
} as const;

/**
 * UserType
 *
 * Classification for users list / detail
 */
export type UserType = typeof UserType[keyof typeof UserType];

/**
 * UserUpdatePayload
 *
 * Payload for updating user/person fields.
 */
export type UserUpdatePayload = {
    /**
     * Active
     *
     * Whether the user should be active
     */
    active?: boolean | null;
    /**
     * Permit Preregistration Notifications
     *
     * Whether user can receive marketing emails before completing signup
     */
    permit_preregistration_notifications?: boolean | null;
};

/**
 * UsersAggregationsResponse
 *
 * Aggregation totals for users search filters
 */
export type UsersAggregationsResponse = {
    /**
     * Companies
     *
     * Count of users per company ID
     */
    companies?: {
        [key: string]: number;
    };
    /**
     * Roles
     *
     * Count of users per role
     */
    roles?: {
        [key: string]: number;
    };
    /**
     * Company Types
     *
     * Count of users per company type
     */
    company_types?: {
        [key: string]: number;
    };
    /**
     * Active
     *
     * Count of users by active status
     */
    active?: {
        [key: string]: number;
    };
    /**
     * Is Admin
     *
     * Count of users by admin status
     */
    is_admin?: {
        [key: string]: number;
    };
    /**
     * Registered
     *
     * Count of users by registration status
     */
    registered?: {
        [key: string]: number;
    };
    /**
     * Preregistered
     *
     * Count of users by preregistered status
     */
    preregistered?: {
        [key: string]: number;
    };
    /**
     * User type
     *
     * Count of users per user_type (executive, enterprise, admin)
     */
    user_type?: {
        [key: string]: number;
    };
};

/**
 * UsersBulkDeletePayload
 *
 * At most 500 per call, so one call is one checked batch.
 */
export type UsersBulkDeletePayload = {
    /**
     * User Uuids
     */
    user_uuids: Array<string>;
};

/**
 * UsersBulkDeleteResponse
 */
export type UsersBulkDeleteResponse = {
    /**
     * Results
     */
    results: Array<UserBulkDeleteResult>;
    /**
     * Deleted Count
     */
    deleted_count: number;
    /**
     * Not Found Count
     */
    not_found_count: number;
    /**
     * Error Count
     */
    error_count: number;
};

/**
 * UsersFilters
 */
export type UsersFilters = {
    /**
     * Role
     *
     * Filter by user role
     */
    role?: Array<Role> | null;
    /**
     * Company ID
     *
     * Filter by company ID
     */
    company_id?: Array<number> | null;
    /**
     * Company Type
     *
     * Filter by company type
     */
    company_type?: Array<OrgType> | null;
    /**
     * Team ID
     *
     * Filter by team ID (members of any of the given teams)
     */
    team_id?: Array<number> | null;
    /**
     * Company Payment Status
     *
     * Filter by the payment status of the user's (newest-role) company (e.g. premium, free)
     */
    company_payment_status?: Array<PaymentStatus> | null;
    /**
     * Active
     *
     * Filter by active status
     */
    active?: boolean | null;
    /**
     * Is Admin
     *
     * Filter by admin status (users with APPADMIN role)
     */
    is_admin?: boolean | null;
    /**
     * Registered
     *
     * Filter by registration status (True = registered/accepted invite, False = pending/invite not accepted)
     */
    registered?: boolean | null;
    /**
     * Preregistered
     *
     * Filter by preregistered status (True = active users with no clerk_id and permit_preregistration_notifications=True, False = not preregistered)
     */
    preregistered?: boolean | null;
    /**
     * User type
     *
     * Filter by user type
     */
    user_type?: Array<UserType> | null;
    /**
     * Wants Intros
     *
     * Filter by whether the user currently wants intros. Only meaningful for enterprise (B4B) users; executives/admins are never matched.
     */
    wants_intros?: boolean | null;
};

/**
 * UsersPostPage[UserDetailedResponse]
 */
export type UsersPostPageUserDetailedResponse = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Results
     */
    results: Array<UserDetailedResponse>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCounts;
    aggregations?: UsersAggregationsResponse | null;
};

/**
 * UsersSearchFilterSortPayload
 */
export type UsersSearchFilterSortPayload = {
    /**
     * Phrase
     *
     * Search phrase
     */
    phrase?: string | null;
    /**
     * Search ID
     *
     * Search session ID
     */
    search_id?: string | null;
    /**
     * Page
     *
     * Page number
     */
    page?: number;
    /**
     * Page size
     *
     * Page size
     */
    page_size?: number;
    /**
     * Filters
     *
     * Filter results by these criteria
     */
    filter_data?: UsersFilters | null;
    /**
     * Sort
     *
     * Sort results by these rules
     */
    sort_rules?: Array<UsersSort>;
    /**
     * Include Aggregations
     *
     * Include aggregation totals for filters (companies, roles, company_types, active, is_admin, registered, preregistered, user_type)
     */
    include_aggregations?: boolean;
};

/**
 * UsersSort
 */
export type UsersSort = {
    /**
     * Column
     *
     * Sort results on this column
     */
    column: UsersSortColumn;
    /**
     * Direction
     *
     * Sort results in this direction
     */
    direction?: SortDirection;
};

/**
 * UsersSortColumn
 */
export const UsersSortColumn = {
    EMAIL: 'email',
    FIRST_NAME: 'first_name',
    LAST_NAME: 'last_name',
    FULL_NAME: 'full_name',
    CREATED: 'created',
    LAST_LOGIN: 'last_login',
    TEAMS_COUNT: 'teams_count',
    ACTIVE: 'active',
    COMPANY_NAME: 'company_name',
    COMPANY_TYPE: 'company_type',
    USER_TYPE: 'user_type'
} as const;

/**
 * UsersSortColumn
 */
export type UsersSortColumn = typeof UsersSortColumn[keyof typeof UsersSortColumn];

/**
 * ValidationError
 */
export type ValidationError = {
    /**
     * Location
     */
    loc: Array<string | number>;
    /**
     * Message
     */
    msg: string;
    /**
     * Error Type
     */
    type: string;
    /**
     * Input
     */
    input?: unknown;
    /**
     * Context
     */
    ctx?: {
        [key: string]: unknown;
    };
};

/**
 * ValuationRange
 */
export const ValuationRange = {
    '<_1M': '<$1M',
    $1M_10M: '$1M-$10M',
    $10M_100M: '$10M-$100M',
    $100M_1B: '$100M-$1B',
    '>_1B': '>$1B'
} as const;

/**
 * ValuationRange
 */
export type ValuationRange = typeof ValuationRange[keyof typeof ValuationRange];

/**
 * VerificationResponse
 */
export type VerificationResponse = {
    /**
     * Detail
     */
    detail: string;
};

/**
 * ElasticsearchAggregationsResponse
 */
export type AppSchemasEsCandidateSchemaElasticsearchAggregationsResponse = {
    /**
     * Connection Status
     */
    connection_status?: {
        [key in ConnectionStatus]?: number;
    } | null;
    /**
     * Function Expertise
     */
    function_expertise?: {
        [key in Function]?: number;
    } | null;
    /**
     * Industry Expertise
     */
    industry_expertise?: {
        [key in Industry]?: number;
    } | null;
    /**
     * Interest Role Board Advisory
     */
    interest_role_board_advisory?: {
        [key in InterestRole]?: number;
    } | null;
    /**
     * Interest Role Operating
     */
    interest_role_operating?: {
        [key in InterestRole]?: number;
    } | null;
    /**
     * Operating Highlights
     */
    operating_highlights?: {
        [key in OperatingHighlights]?: number;
    } | null;
    /**
     * Source Company Tags
     */
    source_company_tags?: {
        [key in SourceCompanyTags]?: number;
    } | null;
    /**
     * Custom Keywords
     */
    custom_keywords?: {
        [key: string]: number;
    } | null;
};

/**
 * ElasticsearchAggregationsResponse
 */
export type AppSchemasEsRecommendationCandidateSchemaElasticsearchAggregationsResponse = {
    /**
     * Company Id
     */
    company_id: {
        [key: string]: number;
    };
    /**
     * Company Type
     */
    company_type: {
        [key in OrgType]?: number;
    };
    /**
     * Review Status
     */
    review_status: {
        [key in RecommendationReviewState]?: number;
    };
    /**
     * Role Type
     */
    role_type: {
        [key in PipelineRoleTypeWithNull]?: number;
    };
    /**
     * Type
     */
    type: {
        [key in IntroductionType]?: number;
    };
};

/**
 * ActivityEventResponse
 */
export type ActivityEventResponseWritable = {
    /**
     * Action
     *
     * Which activity event was performed?
     */
    action: EvaluationAction;
    /**
     * Advisor
     *
     * If this activity event was made on behalf of person, advisor is who made it
     */
    advisor?: PersonBasicResponseWritable | null;
    /**
     * Candidate
     *
     * Candidate for which this activity event was made
     */
    candidate: UuidResponse;
    /**
     * Created
     *
     * Date when this activity event was created
     */
    created: string;
    /**
     * Activity event ID
     *
     * The unique identifier for this activity event
     */
    id: number;
    /**
     * Last modified
     *
     * Date when this activity event was modified
     */
    last_modified: string;
    /**
     * Person
     *
     * Person who performed this activity event
     */
    person?: PersonBasicResponseWritable | null;
    /**
     * Pipeline
     *
     * Pipeline in which this activity event was made
     */
    pipeline?: UuidResponse | null;
    /**
     * Recommendation
     *
     * Recommendation for which this activity event was made
     */
    recommendation?: UuidResponse | null;
    /**
     * Team
     *
     * Team in which this activity event was made
     */
    team: UuidResponse;
};

/**
 * ActivityFeedItem
 *
 * A single row in the unified exec activity feed.
 *
 * Mixes `CandidateActivityLog` rows (kind="activity_log") with synthetic
 * connection-source rows derived from Event / Peer / SubmissionCandidate /
 * Referrer joins. Activity-log items carry the rich fields (actor,
 * candidate, message, event_type); connection-source items carry
 * structured ``contact`` / ``company`` / ``event_ref`` refs.
 *
 * Message composition is the webapp's responsibility — backend exposes
 * only the components (ids, names, slugs) and the webapp builds the
 * localized string with linkable participants from i18n keys.
 */
export type ActivityFeedItemWritable = {
    /**
     * Kind
     */
    kind: 'activity_log' | 'event' | 'peer' | 'pipeline' | 'trending' | 'global_intro' | 'referral' | 'comment';
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Date
     */
    date: string;
    /**
     * Event Type
     */
    event_type?: string | null;
    actor?: PersonBasicResponseWritable | null;
    candidate?: CandidateBasicResponseWritable | null;
    message?: ActivityLogMessage | null;
    /**
     * Reference Type
     */
    reference_type?: string | null;
    /**
     * Reference Id
     */
    reference_id?: string | null;
    contact?: ActivityFeedParticipant | null;
    company?: ActivityFeedCompanyRef | null;
    event_ref?: ActivityFeedEventRef | null;
    referrer?: ActivityFeedParticipant | null;
    referred?: ActivityFeedParticipant | null;
    /**
     * Content
     */
    content?: string | null;
};

/**
 * AddGuestsResponse
 *
 * Response after adding guests to an event.
 */
export type AddGuestsResponseWritable = {
    /**
     * Added
     *
     * The guests that were added (polymorphic on source type)
     */
    added: Array<EventGuestItemResponseWritable>;
};

/**
 * AdminUserDetailedResponse
 *
 * Admin-only detailed user response including invitation history.
 */
export type AdminUserDetailedResponseWritable = {
    /**
     * ID
     *
     * Same as uuid
     */
    id: string;
    /**
     * UUID
     *
     * UUID uniquely identifying a user
     */
    uuid: string;
    /**
     * Email
     *
     * User email address
     */
    email: string;
    /**
     * First Name
     *
     * User's first name
     */
    first_name?: string | null;
    /**
     * Last Name
     *
     * User's last name
     */
    last_name?: string | null;
    /**
     * Full Name
     *
     * User's full name
     */
    full_name?: string | null;
    /**
     * Company
     *
     * Company the user belongs to (includes payment status)
     */
    company?: CompanyExtendedResponse | null;
    /**
     * Created
     *
     * Timestamp when the user record was created
     */
    created: string;
    /**
     * First Login
     *
     * Timestamp when the user record was first logged in
     */
    first_login?: string | null;
    /**
     * Last Login
     *
     * Timestamp of user's last login
     */
    last_login?: string | null;
    /**
     * Active
     *
     * Whether the user is active
     */
    active: boolean;
    /**
     * Pending
     *
     * Whether the user's invite is still pending (no clerk_id)
     */
    pending: boolean;
    /**
     * Permit Preregistration Notifications
     *
     * Whether user can receive marketing emails before completing signup
     */
    permit_preregistration_notifications: boolean;
    /**
     * Notes External
     *
     * External notes for this user profile
     */
    notes_external?: string | null;
    /**
     * Notes Internal
     *
     * Internal notes for this user profile
     */
    notes_internal?: string | null;
    /**
     * LinkedIn URL
     *
     * LinkedIn URL for this user profile
     */
    linkedin_url?: string | null;
    /**
     * Wants Intros
     *
     * Whether this user currently wants intros
     */
    wants_intros?: boolean | null;
    /**
     * Wants Intro Followups
     *
     * Whether follow-up on intro requests is wanted internally
     */
    wants_intro_followups?: boolean | null;
    /**
     * Handle Intros As Exec
     *
     * Whether intro handling should be done as executive
     */
    handle_intros_as_exec?: boolean | null;
    /**
     * Teams Count
     *
     * Total number of teams the user belongs to
     */
    teams_count: number;
    /**
     * Teams
     *
     * List of teams the user belongs to
     */
    teams?: Array<UserTeamBasicResponse>;
    /**
     * Roles
     *
     * List of role objects the user has
     */
    roles?: Array<UserRoleResponse>;
    /**
     * Identity UUID
     *
     * UUID of the identity this login belongs to: the one row per real human that also holds the candidate, if there is one.
     */
    identity_uuid?: string | null;
    /**
     * Candidate UUID
     *
     * UUID of the candidate that shares this person's identity (executives and similar).
     */
    candidate_uuid?: string | null;
    /**
     * Candidate
     *
     * Basic profile of the candidate that shares this person's identity (executives and similar; same as candidate_uuid).
     */
    candidate?: CandidateBasicResponseWritable | null;
    /**
     * User type
     *
     * executive/admin/enterprise
     */
    user_type: UserType;
    /**
     * Search Metadata
     *
     * Search result metadata, including highlighted fields
     */
    search_metadata?: EsMeta | null;
    /**
     * Invitations
     *
     * Invitation history records for this user
     */
    invitations?: Array<UserInvitationResponse>;
    /**
     * Undo executive activation blocker
     *
     * For an activated executive, why DELETE /api/v2/admin/executives/{uuid}/activation would be refused; null when it would succeed, and for anyone else
     */
    undo_executive_activation_blocker?: UndoExecutiveActivationBlocker | null;
};

/**
 * AllIntrosResponse
 */
export type AllIntrosResponseWritable = {
    /**
     * Intro UUID
     *
     * UUID of the intro
     */
    uuid: string;
    /**
     * Intro type
     *
     * Type of the intro
     */
    intro_type: IntroTypeEnum;
    /**
     * Intro time
     *
     * Time intro is scheduled
     */
    time: string;
    /**
     * Candidate
     *
     * Information about the candidate
     */
    candidate: CandidateBasicResponseWritable;
    /**
     * Target Candidate
     *
     * Information about the target candidate
     */
    candidate_to?: CandidateBasicResponseWritable | null;
    /**
     * Team
     *
     * Information about the team
     */
    team?: TeamBasicUuidResponse | null;
};

/**
 * CandidateActivityEvent
 */
export type CandidateActivityEventWritable = {
    /**
     * Comment UUID
     *
     * UUID of the comment if present
     */
    comment_uuid?: string | null;
    /**
     * Event context
     *
     * Context of the event
     */
    context?: string | null;
    /**
     * External person
     *
     * External person related to this action
     */
    external_introduction_request_person?: ExternalPerson | null;
    /**
     * Event date
     *
     * The date of the event
     */
    date: string;
    /**
     * Event type
     *
     * Type of the event
     */
    event_type: CandidateActivityEventType;
    /**
     * Pipeline
     *
     * The related pipeline if present
     */
    pipeline?: PipelineBasicV2Response | null;
    /**
     * Recommendation uuid
     *
     * The related recommendation's uuid
     */
    recommendation_uuid?: string | null;
    /**
     * User
     *
     * Information about the person who made activity
     */
    user: PersonBasicResponseWritable;
    /**
     * UUID
     *
     * Event UUID
     */
    uuid: string;
    /**
     * Team UUID
     *
     * Team UUID responsible for action
     */
    team_uuid?: string | null;
};

/**
 * CandidateActivityLogGroupMember
 */
export type CandidateActivityLogGroupMemberWritable = {
    /**
     * Actor
     *
     * Actor
     */
    actor?: PersonBasicResponseWritable | null;
    /**
     * Event date
     *
     * The date of the event
     */
    date: string;
    /**
     * Event Type
     *
     * Type of event associated with the log
     */
    event_type: CandidateActivityLogEventType | CandidateActivityLogEventTypeHidden;
    /**
     * UUID
     *
     * Event UUID
     */
    uuid: string;
    /**
     * Message
     *
     * Message associated with the event
     */
    message: ActivityLogMessage;
    /**
     * Reference ID
     *
     * UUID of the entity that triggered this event
     */
    reference_id?: string | null;
    /**
     * Reference Type
     *
     * Which entity triggered the event
     */
    reference_type?: CandidateActivityLogReferenceType | null;
    /**
     * Latest recommendation
     *
     * Latest recommendation activity log
     */
    latest_recommendation?: CandidateActivityLatestRecommendation | null;
};

/**
 * CandidateActivityLogReferenceGroups
 */
export type CandidateActivityLogReferenceGroupsWritable = {
    /**
     * Global
     *
     * global activity logs
     */
    global?: Array<CandidateActivityLogGroupMemberWritable> | null;
    /**
     * Peer
     *
     * p2p related activity logs
     */
    peer?: Array<CandidateActivityLogGroupMemberWritable> | null;
    /**
     * Pipeline
     *
     * pipeline related activity logs
     */
    pipeline?: Array<CandidateActivityLogGroupMemberWritable> | null;
    /**
     * Trending
     *
     * trending activity logs
     */
    trending?: Array<CandidateActivityLogGroupMemberWritable> | null;
};

/**
 * CandidateActivityLogResponse
 */
export type CandidateActivityLogResponseWritable = {
    /**
     * Actor
     *
     * Actor
     */
    actor?: PersonBasicResponseWritable | null;
    /**
     * Candidate
     *
     * Information about the candidate
     */
    candidate: CandidateBasicResponseWritable;
    /**
     * Event date
     *
     * The date of the event
     */
    date: string;
    /**
     * Event Type
     *
     * Type of event associated with the log
     */
    event_type: CandidateActivityLogEventType;
    /**
     * UUID
     *
     * Event UUID
     */
    uuid: string;
    /**
     * Message
     *
     * Message associated with the event
     */
    message: ActivityLogMessage;
    /**
     * Reference ID
     *
     * UUID of the entity that triggered this event
     */
    reference_id?: string | null;
    /**
     * Reference Type
     *
     * Which entity triggered the event
     */
    reference_type?: CandidateActivityLogReferenceType | null;
    /**
     * Recommendation UUID
     */
    recommendation_uuid?: string | null;
};

/**
 * CandidateActivityLogResponseWithHidden
 */
export type CandidateActivityLogResponseWithHiddenWritable = {
    /**
     * Actor
     *
     * Actor
     */
    actor?: PersonBasicResponseWritable | null;
    /**
     * Candidate
     *
     * Information about the candidate
     */
    candidate: CandidateBasicResponseWritable;
    /**
     * Event date
     *
     * The date of the event
     */
    date: string;
    /**
     * Event type
     *
     * Event type
     */
    event_type: CandidateActivityLogEventType | CandidateActivityLogEventTypeHidden;
    /**
     * UUID
     *
     * Event UUID
     */
    uuid: string;
    /**
     * Message
     *
     * Message associated with the event
     */
    message: ActivityLogMessage;
    /**
     * Reference ID
     *
     * UUID of the entity that triggered this event
     */
    reference_id?: string | null;
    /**
     * Reference Type
     *
     * Which entity triggered the event
     */
    reference_type?: CandidateActivityLogReferenceType | null;
    /**
     * Recommendation UUID
     */
    recommendation_uuid?: string | null;
    /**
     * Latest recommendation
     *
     * Latest recommendation activity log
     */
    latest_recommendation?: CandidateActivityLatestRecommendation | null;
};

/**
 * CandidateBasicResponse
 */
export type CandidateBasicResponseWritable = {
    /**
     * Image
     *
     * Path to candidate image
     */
    image?: string | null;
    /**
     * Name
     *
     * Candidate's full name
     */
    name: string;
    /**
     * UUID
     *
     * UUID uniquely identifying a person
     */
    uuid: string;
};

/**
 * CandidateCompanyRoleResponse
 */
export type CandidateCompanyRoleResponseWritable = {
    /**
     * Client
     *
     * Indicates whether a person is a Banff client
     */
    is_client: boolean;
    /**
     * Company
     *
     * Candidate's current company
     */
    company?: string | null;
    /**
     * Role
     *
     * Candidate's current role
     */
    role?: string | null;
    /**
     * Image
     *
     * Path to candidate image
     */
    image?: string | null;
    /**
     * Name
     *
     * Candidate's full name
     */
    name: string;
    /**
     * UUID
     *
     * UUID uniquely identifying a person
     */
    uuid: string;
    /**
     * Tier
     *
     * Candidate tier
     */
    tier: CandidateTier;
};

/**
 * CandidateCompanyRoleWithAdvisorResponse
 */
export type CandidateCompanyRoleWithAdvisorResponseWritable = {
    /**
     * Company
     *
     * Candidate's current company
     */
    company?: string | null;
    /**
     * Role
     *
     * Candidate's current role
     */
    role?: string | null;
    /**
     * Image
     *
     * Path to candidate image
     */
    image?: string | null;
    /**
     * Name
     *
     * Candidate's full name
     */
    name: string;
    /**
     * UUID
     *
     * UUID uniquely identifying a person
     */
    uuid: string;
    /**
     * Advisor
     *
     * Information about the advisor assigned to this candidate
     */
    advisor?: PersonBasicResponseWritable | null;
    /**
     * First name
     *
     * Candidate's first name
     */
    first_name?: string | null;
    /**
     * Client
     *
     * Indicates whether a person is a Banff client
     */
    is_client: boolean;
    /**
     * Last name
     *
     * Candidate's last name
     */
    last_name?: string | null;
    /**
     * LinkedIn URL
     *
     * Link to the person's LinkedIn profile
     */
    linkedin_url?: string | null;
};

/**
 * CandidateExtendedResponse
 */
export type CandidateExtendedResponseWritable = {
    /**
     * Client
     *
     * Indicates whether a person is a Banff client
     */
    is_client: boolean;
    /**
     * Company
     *
     * Candidate's current company
     */
    company?: string | null;
    /**
     * Role
     *
     * Candidate's current role
     */
    role?: string | null;
    /**
     * Image
     *
     * Path to candidate image
     */
    image?: string | null;
    /**
     * Name
     *
     * Candidate's full name
     */
    name: string;
    /**
     * UUID
     *
     * UUID uniquely identifying a person
     */
    uuid: string;
    /**
     * First name
     *
     * Candidate's first name
     */
    first_name?: string | null;
    /**
     * Functional expertise
     *
     * List of functions candidate has experience in
     */
    function_expertise: Array<Function>;
    /**
     * Target functions
     *
     * Functional expertise candidate would like to get
     */
    function_target: Array<Function>;
    /**
     * Industry expertise
     *
     * List of industries candidate has experience in
     */
    industry_expertise: Array<Industry>;
    /**
     * Target industries
     *
     * Functional expertise candidate would like to get
     */
    industry_target: Array<Industry>;
    /**
     * Board/advisory role interest
     *
     * Interest in getting board or advisory roles
     */
    interest_role_board_advisory?: InterestRole | null;
    /**
     * Operating role interest
     *
     * Interest in getting operating roles
     */
    interest_role_operating?: InterestRole | null;
    /**
     * Personal interests
     *
     * List of personal interests
     */
    interests_personal: Array<InterestPersonal>;
    /**
     * Professional interests
     *
     * List of professional interests
     */
    interests_professional: Array<InterestProfessional>;
    /**
     * Keywords
     *
     * List of keywords
     */
    keywords: Array<string>;
    /**
     * Last name
     *
     * Candidate's last name
     */
    last_name?: string | null;
    /**
     * LinkedIn URL
     *
     * Link to the person's LinkedIn profile
     */
    linkedin_url?: string | null;
    /**
     * Tier
     *
     * Candidate tier
     */
    tier: CandidateTier;
    /**
     * Type
     *
     * Candidate type
     */
    type?: ClientType | null;
    /**
     * Pathway
     *
     * Banff service pathway for the candidate
     */
    pathway?: CandidatePathway | null;
};

/**
 * CandidateExternalFullResponseWithTeamContext
 *
 * Candidate response with team-specific context fields.
 *
 * This extends CandidateExternalFullResponse with fields that are only
 * meaningful in the context of a specific team. Use this schema for
 * endpoints that return a candidate in a team context.
 */
export type CandidateExternalFullResponseWithTeamContextWritable = {
    /**
     * LinkedIn profile last synced
     *
     * Last time resume was checked against LinkedIn
     */
    __linkedin_profile_last_synced__DO_NOT_USE__?: string | null;
    /**
     * Manual last update
     *
     * This field is used only to calculate the profile_updated_date value
     */
    __manual_last_update__DO_NOT_USE__?: string | null;
    /**
     * Profile updated date
     *
     * Date when the candidate's profile was last updated
     */
    profile_updated_date?: string | null;
    /**
     * Resume last uploaded
     *
     * This field is used only to calculate the profile_updated_date value
     */
    __resume_last_uploaded__DO_NOT_USE__?: string | null;
    /**
     * Company
     *
     * Candidate's current company
     */
    company?: string | null;
    /**
     * Role
     *
     * Candidate's current role
     */
    role?: string | null;
    /**
     * Image
     *
     * Name of the uploaded image from the response
     */
    image?: string | null;
    /**
     * Client
     *
     * Indicates whether a person is a Banff client
     */
    is_client: boolean;
    /**
     * LinkedIn URL
     *
     * Link to the person's LinkedIn profile
     */
    linkedin_url?: string | null;
    /**
     * Name
     *
     * Candidate's full name
     */
    name: string;
    /**
     * UUID
     *
     * UUID uniquely identifying a candidate
     */
    uuid: string;
    /**
     * Formatted role
     *
     * Readable role combining current company and role
     */
    formatted_role?: string | null;
    /**
     * Location
     *
     * Candidate's location (from their resume)
     */
    location?: string | null;
    /**
     * Resume
     *
     * Response from the resume upload endpoint
     */
    resume?: LinkedInResumeResponse | null;
    /**
     * Identity UUID
     *
     * UUID of the identity this candidate belongs to: the key for the person page and for the team's contact comments
     */
    identity_uuid?: string | null;
    /**
     * Board/advisory role interest
     *
     * Interest in getting board or advisory roles
     */
    interest_role_board_advisory?: InterestRole | null;
    /**
     * Operating role interest
     *
     * Interest in getting operating roles
     */
    interest_role_operating?: InterestRole | null;
    /**
     * Functional expertise
     *
     * List of functions candidate has experience in
     */
    function_expertise: Array<Function>;
    /**
     * Industry expertise
     *
     * List of industries candidate has experience in
     */
    industry_expertise: Array<Industry>;
    /**
     * Operating Highlights
     *
     * List of operating highlights
     */
    operating_highlights: Array<OperatingHighlights>;
    /**
     * Source company tags
     *
     * List of custom B4B tags. Currently only available for Acrew
     */
    source_company_tags?: Array<SourceCompanyTags> | null;
    /**
     * Personal interests
     *
     * List of personal interests
     */
    interests_personal: Array<InterestPersonal>;
    /**
     * Professional interests
     *
     * List of professional interests
     */
    interests_professional: Array<InterestProfessional>;
    /**
     * Interests notes
     *
     * Notes on interests
     */
    interests_notes?: string | null;
    /**
     * Wants intros
     *
     * Whether the candidate wants introductions
     */
    wants_intros?: boolean | null;
    /**
     * Interest in relocation
     *
     * Openness to relocation
     */
    interest_relocation?: boolean | null;
    /**
     * Relocation notes
     *
     * Relocation preferences or constraints
     */
    interest_relocation_notes?: string | null;
    /**
     * Email
     *
     * Candidate's email address
     */
    email?: string | null;
    /**
     * Team UUID
     *
     * UUID of the team this candidate is viewed in context of
     */
    team_uuid: string;
    /**
     * Global introduction status
     *
     * Global introduction status between this candidate and the team
     */
    global_introduction_status?: IntroductionStatus | null;
    /**
     * Global introduction request person
     *
     * Person who requested the global introduction
     */
    global_introduction_request_person?: PersonBasicResponseWritable | null;
    /**
     * Pipelines count
     *
     * Number of pipelines and Trending for this team where this candidate appears
     */
    pipelines_count?: number;
    /**
     * Date last recommended
     *
     * When was this candidate last recommended to the team?
     */
    date_recommended_last?: string | null;
    /**
     * Date introduction last facilitated
     *
     * When was this candidate last introduced to the team?
     */
    date_introduction_facilitated_last?: string | null;
    /**
     * Is favorite
     *
     * Whether the candidate is favorited by the current user
     */
    is_favorite?: boolean;
    /**
     * Company metadata
     *
     * Per-company custom metadata for this candidate, filtered to the team's company. Either 0 or 1 entry.
     */
    company_metadata?: Array<CandidateCompanyMetadataResponse>;
    /**
     * Personal tags
     *
     * Personal tags on this contact for the viewing context owner
     */
    personal_tags?: Array<PersonalContactTagItem>;
};

/**
 * CandidateFeedbackFilterResponse
 */
export type CandidateFeedbackFilterResponseWritable = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Results
     */
    results: Array<CandidateFeedbackResultWritable>;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCountsResponse;
};

/**
 * CandidateFeedbackPersonResponse
 *
 * The commenter as displayed, filled from the from-identity: the candidate's
 * name when the identity has a candidate, else the earliest login's. ``uuid``
 * and ``email`` are null for a candidate without a login.
 */
export type CandidateFeedbackPersonResponseWritable = {
    /**
     * Email
     */
    email?: string | null;
    /**
     * Name
     *
     * Person's full name
     */
    name: string;
    /**
     * First Name
     *
     * Person's first name
     */
    first_name?: string | null;
    /**
     * Last Name
     *
     * Person's last name
     */
    last_name?: string | null;
    /**
     * Image
     *
     * Path to person image
     */
    image?: string | null;
    /**
     * UUID
     */
    uuid?: string | null;
    /**
     * Company
     */
    company?: CompanyBasicTypeResponse | null;
};

/**
 * CandidateFeedbackResult
 */
export type CandidateFeedbackResultWritable = {
    /**
     * Advisor
     *
     * Advisor who added this feedback
     */
    advisor?: PersonBasicEmailResponseWritable | null;
    /**
     * Candidate
     */
    candidate?: CandidateBasicResponseWritable;
    /**
     * Comment
     */
    comment: string;
    /**
     * Company
     */
    company?: CompanyBasicTypeResponse | null;
    /**
     * Published On
     */
    published_on: string;
    /**
     * Person
     */
    person?: CandidateFeedbackPersonResponseWritable | null;
    /**
     * Pipeline
     */
    pipeline?: PipelineBasicResponse | null;
    /**
     * Team
     */
    team?: TeamBasicUuidResponse | null;
    /**
     * Source
     */
    source: FeedbackSource;
    /**
     * Recommendation
     */
    recommendation?: EsRecommendationCandidateIndexResult | null;
    /**
     * Type
     */
    type: FeedbackType;
    /**
     * UUID
     */
    uuid: string;
    /**
     * From Identity UUID
     *
     * Identity of the person whose feedback this is (null for anonymous feedback)
     */
    from_identity_uuid?: string | null;
    /**
     * From Candidate UUID
     *
     * The commenter's candidate record, when their identity has one
     */
    from_candidate_uuid?: string | null;
    /**
     * Subject Identity UUID
     *
     * Identity of the person this feedback is about
     */
    subject_identity_uuid?: string | null;
};

/**
 * CandidateGoal
 */
export type CandidateGoalWritable = {
    /**
     * Activities
     *
     * List of activities associated with the goal
     */
    activities?: Array<CandidateActivityLogResponseWithHiddenWritable> | null;
    /**
     * Completion date
     */
    completion_date?: string | null;
    /**
     * Created date
     */
    created: string;
    /**
     * Due date
     */
    due_date?: string | null;
    /**
     * Goal ID
     *
     * UUID uniquely identifying a goal
     */
    id: string;
    /**
     * Intro Stats
     */
    intro_stats?: Array<IntroStats> | null;
    /**
     * Priority
     *
     * Goal priority
     */
    priority?: GoalPriority | null;
    /**
     * Program
     *
     * Program type
     */
    program: CandidateType;
    /**
     * Notes
     *
     * Goal notes
     */
    notes?: Array<CandidateGoalNotes> | null;
    /**
     * Latest Note
     *
     * Latest note
     */
    latest_note: string | null;
    /**
     * Status
     *
     * Goal status
     */
    status: GoalStatus;
    /**
     * Tasks
     */
    tasks?: Array<CandidateGoalTaskWritable> | null;
    /**
     * Title
     *
     * Goal title
     */
    title: string;
    /**
     * Last modified
     *
     * Date when the goal was last modified
     */
    last_modified: string;
};

/**
 * CandidateGoalTask
 */
export type CandidateGoalTaskWritable = {
    /**
     * Candidate
     */
    assignee: PersonBasicEmailResponseWritable;
    /**
     * Created date
     */
    created: string;
    /**
     * Candidate
     */
    creator: PersonBasicEmailResponseWritable;
    /**
     * Completion date
     */
    completion_date?: string | null;
    /**
     * Description
     */
    description?: string | null;
    /**
     * Due date
     */
    due_date?: string | null;
    /**
     * Task ID
     */
    id: string;
    /**
     * Is completed?
     */
    is_completed: boolean;
    /**
     * Last modified
     */
    last_modified: string;
    /**
     * Title
     */
    title: string;
};

/**
 * CandidateGoalsResponse
 */
export type CandidateGoalsResponseWritable = {
    /**
     * Candidate UUID
     *
     * UUID uniquely identifying a candidate
     */
    candidate_uuid: string;
    /**
     * Goals
     *
     * List of goals
     */
    goals: Array<CandidateGoalWritable>;
};

/**
 * CandidateInternalFullResponse
 */
export type CandidateInternalFullResponseWritable = {
    /**
     * First Name
     *
     * Person's first name
     */
    first_name?: string | null;
    /**
     * Last Name
     *
     * Person's last name
     */
    last_name?: string | null;
    /**
     * Company
     *
     * Candidate's current company
     */
    company?: string | null;
    /**
     * Role
     *
     * Candidate's current role
     */
    role?: string | null;
    /**
     * Image
     *
     * Name of the uploaded image from the response
     */
    image?: string | null;
    /**
     * Client
     *
     * Indicates whether a person is a Banff client
     */
    is_client: boolean;
    /**
     * LinkedIn URL
     *
     * Link to the person's LinkedIn profile
     */
    linkedin_url?: string | null;
    /**
     * Name
     *
     * Candidate's full name
     */
    name: string;
    /**
     * UUID
     *
     * UUID uniquely identifying a candidate
     */
    uuid: string;
    /**
     * Active
     *
     * Is this candidate marked as active?
     */
    active: boolean;
    /**
     * Merged into
     *
     * Set when this record was merged into another: its rows live on that candidate now, and this one is only a signpost. The webapp redirects an old profile URL to it.
     */
    merged_into_uuid?: string | null;
    /**
     * Advisor
     *
     * Information about the advisor assigned to this candidate
     */
    advisor?: PersonBasicResponseWritable | null;
    /**
     * Client owner
     *
     * Information about the client owner of the candidate
     */
    client_owner?: PersonBasicResponseWritable | null;
    /**
     * URG
     *
     * Is this candidate from an under-represented group?
     */
    is_urg?: boolean | null;
    /**
     * Email
     *
     * Email associated with the person
     */
    email: string;
    /**
     * Functional expertise
     *
     * List of functions candidate has experience in
     */
    function_expertise: Array<Function>;
    /**
     * Target functions
     *
     * Functional expertise candidate would like to get
     */
    function_target: Array<Function>;
    /**
     * Long-term goals
     *
     * Long-term goals
     */
    long_term_goals: Array<string>;
    /**
     * ID
     *
     * ID uniquely identifying a candidate
     */
    id: number;
    /**
     * Person ID
     *
     * UUID of the candidate's executive login, if any (derived from the identity, not from the frozen person_id column)
     */
    person_id?: string | null;
    /**
     * Identity UUID
     *
     * UUID of the identity this candidate belongs to: the one row per real human that also holds the candidate's logins
     */
    identity_uuid?: string | null;
    /**
     * Industry expertise
     *
     * List of industries candidate has experience in
     */
    industry_expertise: Array<Industry>;
    /**
     * Target industries
     *
     * Functional expertise candidate would like to get
     */
    industry_target: Array<Industry>;
    /**
     * Board/advisory role interest
     *
     * Interest in getting board or advisory roles
     */
    interest_role_board_advisory?: InterestRole | null;
    /**
     * Operating role interest
     *
     * Interest in getting operating roles
     */
    interest_role_operating?: InterestRole | null;
    /**
     * Personal interests
     *
     * List of personal interests
     */
    interests_personal: Array<InterestPersonal>;
    /**
     * Professional interests
     *
     * List of professional interests
     */
    interests_professional: Array<InterestProfessional>;
    /**
     * Interests notes
     *
     * Notes on interests
     */
    interests_notes?: string | null;
    /**
     * Invoice Status
     *
     * HubSpot invoice status (admin-facing; tier 1/1b only)
     */
    invoice_status?: HubSpotInvoiceStatus | null;
    /**
     * HubSpot ID
     *
     * HubSpot contact id linked by the reconcile job (debugging)
     */
    hubspot_id?: string | null;
    /**
     * Wants intros
     *
     * Whether the candidate wants introductions
     */
    wants_intros?: boolean | null;
    /**
     * Interest in relocation
     *
     * Self-reported openness to relocation (external profile)
     */
    interest_relocation?: boolean | null;
    /**
     * Relocation notes
     *
     * Self-reported relocation preferences or constraints (external profile)
     */
    interest_relocation_notes?: string | null;
    /**
     * Introduction email type
     *
     * Introduction email type (automatic or manual)
     */
    intro_email_type: IntroEmailType;
    /**
     * Accepting Intros
     *
     * Whether candidate is accepting intros during current period
     */
    is_accepting_intros: boolean;
    /**
     * Keywords
     *
     * List of keywords
     */
    keywords: Array<string>;
    /**
     * Kickoff Date
     *
     * Kickoff date
     */
    kickoff_date?: string | null;
    /**
     * Location
     *
     * Candidate's location (from their resume)
     */
    location?: string | null;
    /**
     * Manual last update
     *
     * Timestamp when resume was last updated manually
     */
    manual_last_update?: string | null;
    /**
     * Resume manual last update person UUID
     *
     * Person UUID when resume was last updated manually
     */
    manual_update_person_id?: string | null;
    /**
     * Target network
     *
     * Areas in which this person wants to build their network
     */
    network_target?: string | null;
    /**
     * Not accepting start date
     *
     * Date from which the candidate is not accepting intros
     */
    not_accepting_start_date?: string | null;
    /**
     * Not accepting end date
     *
     * Date until which the candidate is not accepting intros
     */
    not_accepting_end_date?: string | null;
    /**
     * Notes
     *
     * Internal advisor notes on the candidate
     */
    notes?: string | null;
    /**
     * Operating Highlights
     *
     * List of operating highlights
     */
    operating_highlights: Array<OperatingHighlights>;
    /**
     * Passions
     *
     * Hobbies, locations, niche industries, personal interests, etc.
     */
    passions?: string | null;
    /**
     * Referrers
     *
     * List of people who referred this candidate
     */
    referrers: Array<ReferrerBasicResponse>;
    /**
     * Resume
     *
     * Response from the resume upload endpoint
     */
    resume?: LinkedInResumeResponse | null;
    /**
     * Profile updated date
     *
     * Date when the candidate's profile was last updated
     */
    profile_updated_date?: string | null;
    /**
     * Resume last uploaded
     *
     * Timestamp when resume was last updated
     */
    resume_last_uploaded?: string | null;
    /**
     * Short-term goals
     *
     * Short-term goals
     */
    short_term_goals: Array<string>;
    /**
     * Source company tags
     *
     * List of custom B4B tags, if and only if the candidate has a source company
     */
    source_company_tags?: Array<SourceCompanyTags> | null;
    /**
     * Tier
     *
     * Candidate tier
     */
    tier: CandidateTier;
    /**
     * Type
     *
     * Candidate type
     */
    type?: ClientType | null;
    /**
     * Pathway
     *
     * Banff service pathway for the candidate
     */
    pathway?: CandidatePathway | null;
};

/**
 * CandidateInternalFullResponseWithLinkedPerson
 */
export type CandidateInternalFullResponseWithLinkedPersonWritable = {
    /**
     * First Name
     *
     * Person's first name
     */
    first_name?: string | null;
    /**
     * Last Name
     *
     * Person's last name
     */
    last_name?: string | null;
    /**
     * Company
     *
     * Candidate's current company
     */
    company?: string | null;
    /**
     * Role
     *
     * Candidate's current role
     */
    role?: string | null;
    /**
     * Image
     *
     * Name of the uploaded image from the response
     */
    image?: string | null;
    /**
     * Client
     *
     * Indicates whether a person is a Banff client
     */
    is_client: boolean;
    /**
     * LinkedIn URL
     *
     * Link to the person's LinkedIn profile
     */
    linkedin_url?: string | null;
    /**
     * Name
     *
     * Candidate's full name
     */
    name: string;
    /**
     * UUID
     *
     * UUID uniquely identifying a candidate
     */
    uuid: string;
    /**
     * Active
     *
     * Is this candidate marked as active?
     */
    active: boolean;
    /**
     * Merged into
     *
     * Set when this record was merged into another: its rows live on that candidate now, and this one is only a signpost. The webapp redirects an old profile URL to it.
     */
    merged_into_uuid?: string | null;
    /**
     * Advisor
     *
     * Information about the advisor assigned to this candidate
     */
    advisor?: PersonBasicResponseWritable | null;
    /**
     * Client owner
     *
     * Information about the client owner of the candidate
     */
    client_owner?: PersonBasicResponseWritable | null;
    /**
     * URG
     *
     * Is this candidate from an under-represented group?
     */
    is_urg?: boolean | null;
    /**
     * Email
     *
     * Email associated with the person
     */
    email: string;
    /**
     * Functional expertise
     *
     * List of functions candidate has experience in
     */
    function_expertise: Array<Function>;
    /**
     * Target functions
     *
     * Functional expertise candidate would like to get
     */
    function_target: Array<Function>;
    /**
     * Long-term goals
     *
     * Long-term goals
     */
    long_term_goals: Array<string>;
    /**
     * ID
     *
     * ID uniquely identifying a candidate
     */
    id: number;
    /**
     * Person ID
     *
     * UUID of the candidate's executive login, if any (derived from the identity, not from the frozen person_id column)
     */
    person_id?: string | null;
    /**
     * Identity UUID
     *
     * UUID of the identity this candidate belongs to: the one row per real human that also holds the candidate's logins
     */
    identity_uuid?: string | null;
    /**
     * Industry expertise
     *
     * List of industries candidate has experience in
     */
    industry_expertise: Array<Industry>;
    /**
     * Target industries
     *
     * Functional expertise candidate would like to get
     */
    industry_target: Array<Industry>;
    /**
     * Board/advisory role interest
     *
     * Interest in getting board or advisory roles
     */
    interest_role_board_advisory?: InterestRole | null;
    /**
     * Operating role interest
     *
     * Interest in getting operating roles
     */
    interest_role_operating?: InterestRole | null;
    /**
     * Personal interests
     *
     * List of personal interests
     */
    interests_personal: Array<InterestPersonal>;
    /**
     * Professional interests
     *
     * List of professional interests
     */
    interests_professional: Array<InterestProfessional>;
    /**
     * Interests notes
     *
     * Notes on interests
     */
    interests_notes?: string | null;
    /**
     * Invoice Status
     *
     * HubSpot invoice status (admin-facing; tier 1/1b only)
     */
    invoice_status?: HubSpotInvoiceStatus | null;
    /**
     * HubSpot ID
     *
     * HubSpot contact id linked by the reconcile job (debugging)
     */
    hubspot_id?: string | null;
    /**
     * Wants intros
     *
     * Whether the candidate wants introductions
     */
    wants_intros?: boolean | null;
    /**
     * Interest in relocation
     *
     * Self-reported openness to relocation (external profile)
     */
    interest_relocation?: boolean | null;
    /**
     * Relocation notes
     *
     * Self-reported relocation preferences or constraints (external profile)
     */
    interest_relocation_notes?: string | null;
    /**
     * Introduction email type
     *
     * Introduction email type (automatic or manual)
     */
    intro_email_type: IntroEmailType;
    /**
     * Accepting Intros
     *
     * Whether candidate is accepting intros during current period
     */
    is_accepting_intros: boolean;
    /**
     * Keywords
     *
     * List of keywords
     */
    keywords: Array<string>;
    /**
     * Kickoff Date
     *
     * Kickoff date
     */
    kickoff_date?: string | null;
    /**
     * Location
     *
     * Candidate's location (from their resume)
     */
    location?: string | null;
    /**
     * Manual last update
     *
     * Timestamp when resume was last updated manually
     */
    manual_last_update?: string | null;
    /**
     * Resume manual last update person UUID
     *
     * Person UUID when resume was last updated manually
     */
    manual_update_person_id?: string | null;
    /**
     * Target network
     *
     * Areas in which this person wants to build their network
     */
    network_target?: string | null;
    /**
     * Not accepting start date
     *
     * Date from which the candidate is not accepting intros
     */
    not_accepting_start_date?: string | null;
    /**
     * Not accepting end date
     *
     * Date until which the candidate is not accepting intros
     */
    not_accepting_end_date?: string | null;
    /**
     * Notes
     *
     * Internal advisor notes on the candidate
     */
    notes?: string | null;
    /**
     * Operating Highlights
     *
     * List of operating highlights
     */
    operating_highlights: Array<OperatingHighlights>;
    /**
     * Passions
     *
     * Hobbies, locations, niche industries, personal interests, etc.
     */
    passions?: string | null;
    /**
     * Referrers
     *
     * List of people who referred this candidate
     */
    referrers: Array<ReferrerBasicResponse>;
    /**
     * Resume
     *
     * Response from the resume upload endpoint
     */
    resume?: LinkedInResumeResponse | null;
    /**
     * Profile updated date
     *
     * Date when the candidate's profile was last updated
     */
    profile_updated_date?: string | null;
    /**
     * Resume last uploaded
     *
     * Timestamp when resume was last updated
     */
    resume_last_uploaded?: string | null;
    /**
     * Short-term goals
     *
     * Short-term goals
     */
    short_term_goals: Array<string>;
    /**
     * Source company tags
     *
     * List of custom B4B tags, if and only if the candidate has a source company
     */
    source_company_tags?: Array<SourceCompanyTags> | null;
    /**
     * Tier
     *
     * Candidate tier
     */
    tier: CandidateTier;
    /**
     * Type
     *
     * Candidate type
     */
    type?: ClientType | null;
    /**
     * Pathway
     *
     * Banff service pathway for the candidate
     */
    pathway?: CandidatePathway | null;
    /**
     * Person
     *
     * Linked person record for activated candidates (only present when ExecutiveSettings exists)
     */
    person?: CandidateLinkedPersonResponse | null;
    /**
     * Company metadata
     *
     * Per-company custom metadata for this candidate. Advisors see all active rows across companies.
     */
    company_metadata?: Array<CandidateCompanyMetadataResponse>;
};

/**
 * CandidateItem
 */
export type CandidateItemWritable = {
    /**
     * Meta
     *
     * Metadata about the search result
     */
    search_metadata?: EsMeta | null;
    /**
     * Is favorite
     *
     * Is the candidate favorited by the current user?
     */
    is_favorite: boolean;
    /**
     * Activity count
     *
     * Activity count for the recommendation
     */
    activity_count: number;
    /**
     * Activity last on
     *
     * Date of the last activity event
     */
    activity_last_on?: string | null;
    /**
     * Activity last viewed on
     *
     * Date when the user last viewed an activity on this candidate
     */
    activity_last_viewed_on?: string | null;
    /**
     * Candidate
     *
     * Candidate submitted in this recommendation
     */
    candidate: CandidateExtendedResponseWritable;
    /**
     * Expires on
     *
     * Ephemeral recommendations may expire at some date
     */
    expires_on?: string | null;
    /**
     * Is viewed?
     *
     * Has this recommendation been viewed by the user loading it?
     */
    is_viewed: boolean;
    /**
     * Created
     *
     * Date when this recommendation was published to the team
     */
    published_on: string;
    /**
     * Review status
     *
     * This recommendation's review status
     */
    status: RecommendationReviewState;
    /**
     * Recommendation UUID
     *
     * Recommendation UUID
     */
    uuid: string;
    /**
     * Draft mode
     *
     * Whether this recommendation is in draft mode
     */
    draft_mode?: RecommendationDraftMode | null;
    /**
     * Draft assignee
     *
     * Person assigned to this draft recommendation
     */
    draft_assignee?: PersonBasicResponseWritable | null;
};

/**
 * CandidateOnboardingStatus
 */
export type CandidateOnboardingStatusWritable = {
    /**
     * Show Onboarding Section
     *
     * Show onboarding section
     */
    show_onboarding_section?: boolean | null;
    /**
     * Candidate
     */
    candidate: CandidateBasicResponseWritable;
    /**
     * Tasks
     *
     * List of onboarding tasks
     */
    tasks?: Array<TaskBasicResponseWritable> | null;
};

/**
 * CandidatePerformanceResponse
 */
export type CandidatePerformanceResponseWritable = {
    candidate: CandidateCompanyRoleResponseWritable;
    /**
     * Advisor
     *
     * Advisor basic info
     */
    advisor: PersonBasicResponseWritable;
    /**
     * Intro request count
     *
     * Candidate's intro requests count
     */
    introductions_requested_count: number;
    /**
     * Recommendation count
     *
     * Candidate's recommendation count
     */
    recommendation_count: number;
    /**
     * Pipeline count
     *
     * Candidate's pipeline count
     */
    pipeline_count: number;
    /**
     * Days to quarterly
     *
     * How many days until the next quarterly mark
     */
    days_to_quarterly: number;
};

/**
 * CandidateRecommendationResponse
 */
export type CandidateRecommendationResponseWritable = {
    /**
     * Meta
     *
     * Metadata about the search result
     */
    search_metadata?: EsMeta | null;
    /**
     * Person
     *
     * Information about the person who requested an introduction with this recommendation
     */
    person?: PersonBasicEmailResponseWritable | null;
    /**
     * Type
     *
     * Defines in which context was this recommendation published.
     */
    type: IntroductionType;
    /**
     * About
     *
     * Brief commentary about the candidate
     */
    about?: string | null;
    /**
     * Advisor
     *
     * Information about the advisor who published this recommendation
     */
    advisor?: PersonBasicEmailResponseWritable | null;
    /**
     * Already known
     *
     * Whether this recommendation was already known to the person reviewing it
     */
    already_known?: boolean | null;
    /**
     * Candidate
     *
     * Candidate associated with this recommendation
     */
    candidate: CandidateExtendedResponseWritable;
    /**
     * Comment author
     *
     * Information about the person who left the comment
     */
    comment_author?: PersonBasicResponseWritable | null;
    /**
     * Comment date
     *
     * Date when the latest comment was left
     */
    comment_date?: string | null;
    /**
     * Company
     *
     * Company to which this recommendation was submitted
     */
    company: CompanyBasicTypeResponse;
    /**
     * Sent date
     *
     * Date when this recommendation was published
     */
    sent_date: string;
    /**
     * Date introduction facilitated
     *
     * Date when an introduction to this recommendation has been facilitated
     */
    date_introduction_facilitated?: string | null;
    /**
     * Date last viewed
     *
     * Date when this recommendation was last viewed
     */
    date_last_viewed?: string | null;
    /**
     * Highlights
     *
     * List of all candidate's highlights with information which ones were selected for this submission
     */
    highlights: Array<CandidateHighlightResponse>;
    /**
     * Introduction date
     *
     * Date when introduction was requested for this recommendation
     */
    introduction_date?: string | null;
    /**
     * Status
     *
     * Introduction request status
     */
    introduction_status?: IntroductionStatus | null;
    /**
     * Is liked?
     *
     * Does the user want more recommendations like this one?
     */
    is_liked?: boolean | null;
    /**
     * Comment
     *
     * Comment left about the recommendation
     */
    comment?: string | null;
    /**
     * Introduction requested
     *
     * Information whether an introduction was requested with this recommendation
     */
    make_introduction?: boolean | null;
    /**
     * Search match
     *
     * When submitted to a search, is this candidate a match?
     */
    match?: boolean | null;
    /**
     * Pipeline
     *
     * Information about pipeline where this recommendation was submitted
     */
    pipeline?: PipelineBasicHiddenResponse | null;
    /**
     * Review status
     *
     * State in which the recommendation currently is
     */
    review_status: RecommendationReviewState;
    /**
     * Team
     *
     * Team to which this recommendation was submitted
     */
    team: TeamBasicResponse;
    /**
     * Submission
     *
     * Information about the submission to which this candidate belongs
     */
    submission: SubmissionBasicResponse;
    /**
     * UUID
     *
     * UUID uniquely identifying a recommendation
     */
    uuid: string;
    /**
     * Viewed date
     *
     * Date when this recommendation was viewed
     */
    viewed_date?: string | null;
};

/**
 * CandidateSearchResponse
 */
export type CandidateSearchResponseWritable = {
    /**
     * Company
     *
     * Candidate's current company
     */
    company?: string | null;
    /**
     * Role
     *
     * Candidate's current role
     */
    role?: string | null;
    /**
     * Advisor
     *
     * Information about the advisor assigned to this candidate
     */
    advisor?: PersonBasicEmailResponseWritable | null;
    /**
     * Email
     *
     * Candidate's email address
     */
    email: string;
    /**
     * First name
     *
     * Candidate's first name
     */
    first_name?: string | null;
    /**
     * Functional expertise
     *
     * List of functions candidate has experience in
     */
    function_expertise: Array<Function>;
    /**
     * Target functions
     *
     * Functional expertise candidate would like to get
     */
    function_target: Array<Function>;
    /**
     * ID
     *
     * ID uniquely identifying a candidate
     */
    id: number;
    /**
     * Image
     *
     * Path to candidate image
     */
    image?: string | null;
    /**
     * Industry expertise
     *
     * List of industries candidate has experience in
     */
    industry_expertise: Array<Industry>;
    /**
     * Target industries
     *
     * Functional expertise candidate would like to get
     */
    industry_target: Array<Industry>;
    /**
     * Board/advisory role interest
     *
     * Interest in getting board or advisory roles
     */
    interest_role_board_advisory?: InterestRole | null;
    /**
     * Operating role interest
     *
     * Interest in getting operating roles
     */
    interest_role_operating?: InterestRole | null;
    /**
     * Personal interests
     *
     * List of personal interests
     */
    interests_personal: Array<InterestPersonal>;
    /**
     * Professional interests
     *
     * List of professional interests
     */
    interests_professional: Array<InterestProfessional>;
    /**
     * Client
     *
     * Indicates whether a person is a Banff client
     */
    is_client: boolean;
    /**
     * Introduction email type
     *
     * Introduction email type (automatic or manual)
     */
    intro_email_type: IntroEmailType;
    /**
     * Keywords
     *
     * List of keywords
     */
    keywords: Array<string>;
    /**
     * Last name
     *
     * Candidate's last name
     */
    last_name?: string | null;
    /**
     * LinkedIn URL
     *
     * Link to the person's LinkedIn profile
     */
    linkedin_url?: string | null;
    /**
     * Location
     *
     * Candidate's location (from their resume)
     */
    location?: string | null;
    /**
     * Full name
     *
     * Candidate's full name
     */
    name?: string | null;
    /**
     * Tier
     *
     * Candidate tier
     */
    tier: CandidateTier;
    /**
     * Type
     *
     * Candidate type
     */
    type?: ClientType | null;
    /**
     * Pathway
     *
     * Banff service pathway for the candidate
     */
    pathway?: CandidatePathway | null;
    /**
     * UUID
     *
     * UUID uniquely identifying a candidate
     */
    uuid: string;
};

/**
 * CandidateSearchResponseWithLinkedPerson
 */
export type CandidateSearchResponseWithLinkedPersonWritable = {
    /**
     * Company
     *
     * Candidate's current company
     */
    company?: string | null;
    /**
     * Role
     *
     * Candidate's current role
     */
    role?: string | null;
    /**
     * Advisor
     *
     * Information about the advisor assigned to this candidate
     */
    advisor?: PersonBasicEmailResponseWritable | null;
    /**
     * Email
     *
     * Candidate's email address
     */
    email: string;
    /**
     * First name
     *
     * Candidate's first name
     */
    first_name?: string | null;
    /**
     * Functional expertise
     *
     * List of functions candidate has experience in
     */
    function_expertise: Array<Function>;
    /**
     * Target functions
     *
     * Functional expertise candidate would like to get
     */
    function_target: Array<Function>;
    /**
     * ID
     *
     * ID uniquely identifying a candidate
     */
    id: number;
    /**
     * Image
     *
     * Path to candidate image
     */
    image?: string | null;
    /**
     * Industry expertise
     *
     * List of industries candidate has experience in
     */
    industry_expertise: Array<Industry>;
    /**
     * Target industries
     *
     * Functional expertise candidate would like to get
     */
    industry_target: Array<Industry>;
    /**
     * Board/advisory role interest
     *
     * Interest in getting board or advisory roles
     */
    interest_role_board_advisory?: InterestRole | null;
    /**
     * Operating role interest
     *
     * Interest in getting operating roles
     */
    interest_role_operating?: InterestRole | null;
    /**
     * Personal interests
     *
     * List of personal interests
     */
    interests_personal: Array<InterestPersonal>;
    /**
     * Professional interests
     *
     * List of professional interests
     */
    interests_professional: Array<InterestProfessional>;
    /**
     * Client
     *
     * Indicates whether a person is a Banff client
     */
    is_client: boolean;
    /**
     * Introduction email type
     *
     * Introduction email type (automatic or manual)
     */
    intro_email_type: IntroEmailType;
    /**
     * Keywords
     *
     * List of keywords
     */
    keywords: Array<string>;
    /**
     * Last name
     *
     * Candidate's last name
     */
    last_name?: string | null;
    /**
     * LinkedIn URL
     *
     * Link to the person's LinkedIn profile
     */
    linkedin_url?: string | null;
    /**
     * Location
     *
     * Candidate's location (from their resume)
     */
    location?: string | null;
    /**
     * Full name
     *
     * Candidate's full name
     */
    name?: string | null;
    /**
     * Tier
     *
     * Candidate tier
     */
    tier: CandidateTier;
    /**
     * Type
     *
     * Candidate type
     */
    type?: ClientType | null;
    /**
     * Pathway
     *
     * Banff service pathway for the candidate
     */
    pathway?: CandidatePathway | null;
    /**
     * UUID
     *
     * UUID uniquely identifying a candidate
     */
    uuid: string;
    /**
     * Person
     *
     * Linked person record for activated candidates (only present when ExecutiveSettings exists)
     */
    person?: CandidateLinkedPersonResponse | null;
};

/**
 * CandidateTaskModule
 */
export type CandidateTaskModuleWritable = {
    /**
     * Created date
     */
    created: string;
    /**
     * Module ID
     */
    id: string;
    /**
     * Type
     *
     * Type of task module
     */
    type: TaskModuleType;
    /**
     * Status
     *
     * Task module status
     */
    status: TaskModuleStatus;
};

/**
 * CandidateTaskModuleResponse
 */
export type CandidateTaskModuleResponseWritable = {
    /**
     * Task Module
     */
    module?: CandidateTaskModuleWritable | null;
    /**
     * Tasks
     *
     * List of tasks associated with this module
     */
    tasks?: Array<TaskBasicResponseWritable> | null;
};

/**
 * CandidateTaskModulesResponse
 */
export type CandidateTaskModulesResponseWritable = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Results
     */
    results: Array<CandidateTaskModuleResponseWritable>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCountsResponse;
};

/**
 * CandidatesCombinedPerformancePaginatedResponse
 */
export type CandidatesCombinedPerformancePaginatedResponseWritable = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Results
     */
    results: Array<CandidatesCombinedPerformanceResponseWritable>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCountsResponse;
};

/**
 * CandidatesCombinedPerformanceResponse
 */
export type CandidatesCombinedPerformanceResponseWritable = {
    candidate: CandidateCompanyRoleResponseWritable;
    /**
     * Advisor
     *
     * Advisor basic info
     */
    advisor: PersonBasicResponseWritable;
    /**
     * Type
     *
     * Candidate's type
     */
    type?: ClientType | null;
    /**
     * Pathway
     *
     * Candidate's pathway
     */
    pathway?: CandidatePathway | null;
    /**
     * Industry expertise
     *
     * Candidate's industry expertise
     */
    industry_expertise: Array<Industry>;
    /**
     * Function expertise
     *
     * Candidate's function expertise
     */
    function_expertise: Array<Function>;
    /**
     * Intro facilitated count
     *
     * Candidate's facilitated count
     */
    introductions_facilitated_count: number;
    /**
     * Intro request count
     *
     * Candidate's intro requests count
     */
    introductions_requested_count: number;
    /**
     * Recommendation count
     *
     * Candidate's recommendation count
     */
    recommendation_count: number;
    /**
     * Pipeline count
     *
     * Candidate's pipeline count
     */
    pipeline_count: number;
    /**
     * Peer activity count
     *
     * Peer activity count
     */
    peer_activity_count: number;
    /**
     * Rank
     *
     * Candidate's trending rank
     */
    rank: number;
    /**
     * Calculated rank
     *
     * Calculated candidate's trending rank
     */
    calculated_rank: number;
};

/**
 * CandidatesPerformancePaginatedResponse
 */
export type CandidatesPerformancePaginatedResponseWritable = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Results
     */
    results: Array<CandidatePerformanceResponseWritable>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCountsResponse;
};

/**
 * CandidatesTableResponse
 */
export type CandidatesTableResponseWritable = {
    /**
     * Meta
     *
     * Metadata about the search result
     */
    search_metadata?: EsMeta | null;
    /**
     * LinkedIn profile last synced
     *
     * Last time resume was checked against LinkedIn
     */
    __linkedin_profile_last_synced__DO_NOT_USE__?: string | null;
    /**
     * Manual last update
     *
     * This field is used only to calculate the profile_updated_date value
     */
    __manual_last_update__DO_NOT_USE__?: string | null;
    /**
     * Profile updated date
     *
     * Date when the candidate's profile was last updated
     */
    profile_updated_date?: string | null;
    /**
     * Resume last uploaded
     *
     * This field is used only to calculate the profile_updated_date value
     */
    __resume_last_uploaded__DO_NOT_USE__?: string | null;
    /**
     * Client
     *
     * Indicates whether a person is a Banff client
     */
    is_client?: boolean | null;
    /**
     * Company
     *
     * Candidate's current company
     */
    company?: string | null;
    /**
     * Role
     *
     * Candidate's current role
     */
    role?: string | null;
    /**
     * Image
     *
     * Path to candidate image
     */
    image?: string | null;
    /**
     * Name
     *
     * Candidate's full name
     */
    name: string;
    /**
     * UUID
     *
     * UUID uniquely identifying a person
     */
    uuid: string;
    /**
     * Active
     *
     * Is this candidate marked as active?
     */
    active: boolean;
    /**
     * Advisor
     *
     * Information about the advisor assigned to this candidate
     */
    advisor?: PersonBasicResponseWritable | null;
    /**
     * Advisor UUID
     *
     * This field is used only to build the advisor object
     */
    advisor_advisor_id?: string | null;
    /**
     * Advisor image
     *
     * This field is used only to build the advisor object
     */
    advisor_advisor_image?: string | null;
    /**
     * Advisor name
     *
     * This field is used only to build the advisor object
     */
    advisor_advisor_name?: string | null;
    /**
     * Referred date
     *
     * Date when the candidate was referred for the first time
     */
    referred_date?: string | null;
    /**
     * Email
     *
     * Email associated with the candidate
     */
    email: string;
    /**
     * First name
     *
     * Candidate's first name
     */
    first_name?: string | null;
    /**
     * Functional expertise
     *
     * List of functions candidate has experience in
     */
    function_expertise: Array<Function>;
    /**
     * Target functions
     *
     * Functional expertise candidate would like to get
     */
    function_target: Array<Function>;
    /**
     * Industry expertise
     *
     * List of industries candidate has experience in
     */
    industry_expertise: Array<Industry>;
    /**
     * Target industries
     *
     * Functional expertise candidate would like to get
     */
    industry_target: Array<Industry>;
    /**
     * Board/advisory role interest
     *
     * Interest in getting board or advisory roles
     */
    interest_role_board_advisory?: InterestRole | null;
    /**
     * Operating role interest
     *
     * Interest in getting operating roles
     */
    interest_role_operating?: InterestRole | null;
    /**
     * Personal interests
     *
     * List of personal interests
     */
    interests_personal: Array<InterestPersonal>;
    /**
     * Professional interests
     *
     * List of professional interests
     */
    interests_professional: Array<InterestProfessional>;
    /**
     * Keywords
     *
     * List of keywords
     */
    keywords: Array<string>;
    /**
     * Last name
     *
     * Candidate's last name
     */
    last_name?: string | null;
    /**
     * LinkedIn URL
     *
     * Link to the person's LinkedIn profile
     */
    linkedin_url?: string | null;
    /**
     * Location
     *
     * Candidate's location (from their resume)
     */
    location?: string | null;
    /**
     * Tier
     *
     * Candidate tier
     */
    tier: CandidateTier;
    /**
     * Invoice Status
     *
     * HubSpot invoice status (admin-facing; tier 1/1b only)
     */
    invoice_status?: HubSpotInvoiceStatus | null;
    /**
     * Type
     *
     * Candidate type
     */
    type?: ClientType | null;
    /**
     * Pathway
     *
     * Banff service pathway for the candidate
     */
    pathway?: CandidatePathway | null;
};

/**
 * CommentResponse
 */
export type CommentResponseWritable = {
    /**
     * Candidate
     *
     * Candidate record of the human this comment is about; null when they have no candidate record
     */
    candidate?: UuidResponse | null;
    /**
     * Person
     *
     * Comment author
     */
    person?: PersonBasicResponseWritable | null;
    /**
     * Comment text
     *
     * The content of the recommendation comment
     */
    content: string;
    /**
     * Created
     *
     * Date when this comment was published
     */
    created: string;
    /**
     * Comment ID
     *
     * The unique identifier for comment
     */
    id: string;
    /**
     * Last modified
     *
     * Date when this comment was modified
     */
    last_modified: string;
    /**
     * Pipeline
     *
     * Pipeline in which this comment was made
     */
    pipeline?: UuidResponse | null;
    /**
     * Recommendation
     *
     * Recommendation for which this comment was made
     */
    recommendation?: UuidResponse | null;
    /**
     * Subject identity UUID
     *
     * Identity (human) this comment is about
     */
    subject_identity_uuid: string;
    /**
     * Team
     *
     * Team in which this comment was made
     */
    team: UuidResponse;
};

/**
 * ESCandidateIndexExternalResult
 */
export type EsCandidateIndexExternalResultWritable = {
    /**
     * Meta
     *
     * Metadata about the search result
     */
    search_metadata?: EsMeta | null;
    /**
     * Advisor
     *
     * Information about the advisor assigned to this candidate
     */
    advisor?: PersonInnerDocSchemaWritable | null;
    /**
     * Company
     *
     * Candidate's last company
     */
    company?: string | null;
    /**
     * Role
     *
     * Candidate's last role
     */
    role?: string | null;
    /**
     * Date introduction last facilitated
     *
     * When was this candidate last introduced to the team?
     */
    date_introduction_facilitated_last?: string | null;
    /**
     * Date last recommended
     *
     * When was this candidate last recommended to the team?
     */
    date_recommended_last?: string | null;
    /**
     * Email
     *
     * Email associated with the person
     */
    email: string;
    /**
     * First name
     *
     * Person's first name
     */
    first_name?: string | null;
    /**
     * Functional expertise
     *
     * List of functions candidate has experience in
     */
    function_expertise: Array<Function>;
    /**
     * Target functions
     *
     * Functional expertise candidate would like to get
     */
    function_target: Array<Function>;
    /**
     * Global introduction request person
     *
     * Person who requested the global introduction
     */
    global_introduction_request_person?: PersonInnerDocSchemaWritable | null;
    /**
     * Global introduction status
     *
     * Global introduction status between this candidate and the team
     */
    global_introduction_status?: IntroductionStatus | null;
    /**
     * ID
     *
     * UUID uniquely identifying a person
     */
    id: string;
    /**
     * Image
     *
     * Path to person image
     */
    image?: string | null;
    /**
     * Industry expertise
     *
     * List of industries candidate has experience in
     */
    industry_expertise: Array<Industry>;
    /**
     * Target industries
     *
     * Functional expertise candidate would like to get
     */
    industry_target: Array<Industry>;
    /**
     * Client
     *
     * Indicates whether a person is a Banff client
     */
    is_client: boolean;
    /**
     * Is favorite
     *
     * Whether the candidate is favorited by the current user
     */
    is_favorite: boolean;
    /**
     * Last name
     *
     * Person's last name
     */
    last_name?: string | null;
    /**
     * LinkedIn URL
     *
     * Link to the person's LinkedIn profile
     */
    linkedin_url?: string | null;
    /**
     * Location
     *
     * Candidate's location (from their resume)
     */
    location?: string | null;
    /**
     * Name
     *
     * Person's full name
     */
    name: string;
    /**
     * Operating Highlights
     *
     * List of operating highlights
     */
    operating_highlights?: Array<OperatingHighlights> | null;
    /**
     * Resume
     *
     * Response from the resume upload endpoint
     */
    resume?: LinkedInResumeResponse | null;
    /**
     * UUID
     *
     * UUID uniquely identifying a candidate
     */
    uuid: string;
};

/**
 * ESCandidateIndexResult
 */
export type EsCandidateIndexResultWritable = {
    /**
     * Meta
     *
     * Metadata about the search result
     */
    search_metadata?: EsMeta | null;
    /**
     * Active
     *
     * Is this candidate marked as active?
     */
    active: boolean;
    /**
     * Advisor
     *
     * Information about the advisor assigned to this candidate
     */
    advisor?: PersonInnerDocSchemaWritable | null;
    /**
     * Advisor UUID
     *
     * uuid of advisor assigned to this candidate
     */
    advisor_id?: string | null;
    /**
     * Current Company
     *
     * Company of the candidate
     */
    company?: string | null;
    /**
     * Current Role
     *
     * Candidate's current role
     */
    role?: string | null;
    /**
     * Date introduction last facilitated
     *
     * When was this candidate last introduced to the team?
     */
    date_introduction_facilitated_last?: string | null;
    /**
     * Date last recommended
     *
     * When was this candidate last recommended to the team?
     */
    date_recommended_last?: string | null;
    /**
     * Functional expertise
     *
     * List of functions candidate has experience in
     */
    function_expertise: Array<Function>;
    /**
     * Target functions
     *
     * Functional expertise candidate would like to get
     */
    function_target: Array<Function>;
    /**
     * Global introduction request person
     *
     * Person who requested the global introduction
     */
    global_introduction_request_person?: PersonInnerDocSchemaWritable | null;
    /**
     * Global introduction status
     *
     * Global introduction status between this candidate and the team
     */
    global_introduction_status?: IntroductionStatus | null;
    /**
     * Industry expertise
     *
     * List of industries candidate has experience in
     */
    industry_expertise: Array<Industry>;
    /**
     * Target industries
     *
     * Functional expertise candidate would like to get
     */
    industry_target: Array<Industry>;
    /**
     * Board/advisory role interest
     *
     * Interest in getting board or advisory roles
     */
    interest_role_board_advisory?: InterestRole | null;
    /**
     * Operating role interest
     *
     * Interest in getting operating roles
     */
    interest_role_operating?: InterestRole | null;
    /**
     * Personal interests
     *
     * List of personal interests
     */
    interests_personal: Array<InterestPersonal>;
    /**
     * Professional interests
     *
     * List of professional interests
     */
    interests_professional: Array<InterestProfessional>;
    /**
     * ID
     *
     * ID uniquely identifying a candidate
     */
    id: number;
    /**
     * Client
     *
     * Indicates whether a person is a Banff client
     */
    is_client?: boolean | null;
    /**
     * Is favorite
     *
     * Whether the candidate is favorited by the current user
     */
    is_favorite: boolean;
    /**
     * Referred
     *
     * Indicates whether a person was referred
     */
    is_referred?: boolean | null;
    /**
     * Keywords
     *
     * List of keywords
     */
    keywords: Array<string>;
    /**
     * LinkedIn profile last synced
     *
     * Last time resume was checked against LinkedIn
     */
    linkedin_profile_last_synced?: string | null;
    /**
     * LinkedIn URL
     *
     * Link to the person's LinkedIn profile
     */
    linkedin_url?: string | null;
    /**
     * Location
     *
     * Candidate's location (from their resume)
     */
    location?: string | null;
    /**
     * Manual last update
     *
     * Timestamp when resume was last updated manually
     */
    manual_last_update?: string | null;
    /**
     * Notes
     *
     * Additional notes on ideal opportunities
     */
    notes?: string | null;
    /**
     * Operating Highlights
     *
     * List of operating highlights
     */
    operating_highlights?: Array<OperatingHighlights> | null;
    /**
     * Email
     *
     * Candidate's email address
     */
    email: string;
    /**
     * First Name
     *
     * Candidate's first name
     */
    first_name?: string | null;
    /**
     * Full Name
     *
     * Candidate's full name
     */
    name?: string | null;
    /**
     * Last Name
     *
     * Candidate's last name
     */
    last_name?: string | null;
    /**
     * Profile Image
     *
     * Candidate's profile image URL
     */
    image?: string | null;
    /**
     * Pipelines count
     *
     * Number of pipelines and Trending for this team where this candidate appears
     */
    pipelines_count?: number | null;
    /**
     * Referred date
     *
     * Date when the candidate was referred for the first time
     */
    referred_date?: string | null;
    /**
     * Referrers
     *
     * List of people who referred this candidate
     */
    referrers: Array<PersonInnerDocSchemaWritable>;
    /**
     * Resume
     *
     * Response from the resume upload endpoint
     */
    resume?: LinkedInResumeResponse | null;
    /**
     * Resume last uploaded
     *
     * Timestamp when resume was last updated
     */
    resume_last_uploaded?: string | null;
    /**
     * Profile updated date
     *
     * Date when the candidate's profile was last updated
     */
    profile_updated_date?: string | null;
    /**
     * Team UUID
     *
     * Team UUID
     */
    team_uuid: string;
    /**
     * Tier
     *
     * Candidate tier
     */
    tier: CandidateTier;
    /**
     * Type
     *
     * Candidate type
     */
    type?: ClientType | null;
    /**
     * Pathway
     *
     * Banff service pathway for the candidate
     */
    pathway?: CandidatePathway | null;
    /**
     * UUID
     *
     * UUID uniquely identifying a candidate
     */
    uuid: string;
};

/**
 * EventEmailBatchResponse
 *
 * One send run for an event: the invites sent in one go, one reminder pass,
 * one RSVP confirmation. Counts are of emails, not of SendGrid events: an
 * email opened twice is one opened email.
 */
export type EventEmailBatchResponseWritable = {
    /**
     * Batch UUID
     */
    batch_uuid: string;
    /**
     * Email event type
     */
    email_event_type?: EmailEventType | null;
    /**
     * Sent at
     *
     * When the first email went out.
     */
    sent_at?: string | null;
    /**
     * Emails sent
     */
    email_count: number;
    /**
     * Emails delivered
     */
    delivered_count: number;
    /**
     * Emails opened at least once
     */
    opened_count: number;
    /**
     * Emails clicked at least once
     */
    clicked_count: number;
    /**
     * Emails dropped or bounced
     *
     * Never reached the inbox.
     */
    failed_count: number;
    /**
     * Email template
     *
     * The template the run was sent from, hidden or not. Empty for runs sent before October 2026.
     */
    email_template?: EmailTemplateResponse | null;
    /**
     * First emails
     *
     * The run's first emails that match the filters, newest first, at most 10 of them. Load the rest from /email-events?batch_uuid=&size=10, from page 2.
     */
    first_emails: Array<EventEmailEventItemResponseWritable>;
};

/**
 * EventEmailEventItemResponse
 *
 * One email sent for an event, and what SendGrid has told us about it since.
 */
export type EventEmailEventItemResponseWritable = {
    /**
     * SendGrid message ID
     */
    sendgrid_message_id: string;
    /**
     * Recipient email
     */
    sendgrid_recipient_email: string;
    /**
     * Recipient
     *
     * Who received it, when the address belongs to someone we hold. Nothing for an external address.
     */
    recipient?: IdentityBasicResponseWritable | null;
    /**
     * Email event type
     */
    email_event_type?: EmailEventType | null;
    /**
     * Batch UUID
     *
     * The send job this email was part of: every invite or reminder sent in one go shares it.
     */
    batch_uuid?: string | null;
    /**
     * Sent at
     */
    sent_at?: string | null;
    /**
     * Delivered at
     */
    delivered_at?: string | null;
    /**
     * First opened at
     */
    first_opened_at?: string | null;
    /**
     * Last opened at
     */
    last_opened_at?: string | null;
    /**
     * Opens
     */
    open_count: number;
    /**
     * Clicks
     */
    click_count: number;
    /**
     * Failed at
     */
    failed_at?: string | null;
    /**
     * Failure
     *
     * Set when SendGrid could not deliver it.
     */
    failure?: 'dropped' | 'bounce' | null;
};

/**
 * EventGuestItemResponse
 *
 * A guest on an event's guest list (polymorphic on source type, like potential guests).
 *
 * Always includes resolved name, role, company, email for display, plus
 * source_type, person_uuid/candidate_uuid for linking, and event-guest fields (id, uuid, status).
 */
export type EventGuestItemResponseWritable = {
    /**
     * Source Type
     *
     * Whether this guest is a user, candidate, or standalone event guest
     */
    source_type: PotentialGuestSourceType;
    /**
     * ID
     *
     * Event guest record ID
     */
    id: number;
    /**
     * UUID
     *
     * Event guest record UUID
     */
    uuid: string;
    /**
     * Identity UUID
     *
     * The human this guest is, or null for a free-text guest. The key to group a guest by: one human is one guest however they were added
     */
    identity_uuid?: string | null;
    /**
     * Person UUID
     *
     * Person UUID when guest is linked to a B4B user
     */
    person_uuid?: string | null;
    /**
     * Candidate UUID
     *
     * Candidate UUID when guest is linked to a candidate
     */
    candidate_uuid?: string | null;
    /**
     * Invoice Status
     *
     * HubSpot invoice status when the guest is a linked candidate
     */
    invoice_status?: HubSpotInvoiceStatus | null;
    /**
     * Is Executive
     *
     * True when this guest is an activated executive user (ExecutiveSettings exists on the linked person, so person_uuid resolves to a real user page). False for plain candidates and standalone event guests.
     */
    is_executive?: boolean;
    /**
     * Name
     *
     * Display name
     */
    name?: string | null;
    /**
     * Role
     *
     * Role or title
     */
    role?: string | null;
    /**
     * Company
     *
     * Company name
     */
    company?: string | null;
    /**
     * Email
     *
     * Email address
     */
    email?: string | null;
    /**
     * Status
     *
     * Guest status for this event
     */
    status: EventGuestStatus;
    /**
     * Invite Sent At
     *
     * When the invite email was last sent
     */
    invite_sent_at?: string | null;
    /**
     * Created
     *
     * When this guest was added to the event
     */
    created: string;
    /**
     * Referrers
     *
     * Persons who referred this candidate (when source_type is candidate)
     */
    referrers?: Array<PersonBasicResponseWritable> | null;
    /**
     * Client Types
     *
     * Server-computed client-type classification, mirroring the users-table company-type taxonomy. Enterprise roles (search, investor, corporate, advisory) win over candidate linkage; a candidate-linked guest with no enterprise role is executive; a guest with neither is unclassified (empty list).
     */
    client_types?: Array<EventGuestClientType>;
};

/**
 * EventGuestWithEventItemResponse
 *
 * An event guest with the event it belongs to (for listing all guests across events).
 */
export type EventGuestWithEventItemResponseWritable = {
    /**
     * Event UUID
     *
     * UUID of the event
     */
    event_uuid: string;
    /**
     * Event Title
     *
     * Title of the event
     */
    event_title: string;
    /**
     * Guest
     *
     * The guest record with resolved name, role, company, email
     */
    guest: EventGuestItemResponseWritable;
};

/**
 * EventGuestsPage[EventGuestItemResponse]
 */
export type EventGuestsPageEventGuestItemResponseWritable = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Results
     */
    results: Array<EventGuestItemResponseWritable>;
    /**
     * Size
     */
    size: number;
    total_counts: EventGuestTotalCounts;
};

/**
 * EventGuestsSelectionResponse
 *
 * All guests matching the selection filters (no pagination).
 */
export type EventGuestsSelectionResponseWritable = {
    /**
     * Results
     */
    results: Array<EventGuestItemResponseWritable>;
    /**
     * Count
     */
    count: number;
};

/**
 * EventResponse
 *
 * Event with optional guest list.
 */
export type EventResponseWritable = {
    /**
     * ID
     */
    id: number;
    /**
     * UUID
     */
    uuid: string;
    /**
     * Title
     */
    title: string;
    /**
     * Categories
     */
    categories: Array<EventCategory>;
    /**
     * Category
     *
     * [DEPRECATED] First category from `categories`.
     *
     * @deprecated
     */
    category?: EventCategory | null;
    /**
     * Draft
     */
    draft: boolean;
    /**
     * RSVP enabled
     *
     * When false, public RSVP and invite flows that embed RSVP links are disabled.
     */
    rsvp_enabled: boolean;
    /**
     * Description
     */
    description?: string | null;
    /**
     * Notes
     *
     * Advisor-only notes; not shown on public RSVP.
     */
    notes?: string | null;
    /**
     * Event date (UTC)
     *
     * Scheduled start as stored (UTC instant). Prefer event_date_local for display.
     */
    event_date?: string | null;
    /**
     * Event timezone
     *
     * IANA timezone for displaying event_date.
     */
    event_timezone?: string | null;
    /**
     * Event date (local)
     *
     * Same instant as event_date, in event_timezone (ISO 8601 with offset).
     */
    event_date_local?: string | null;
    /**
     * Location
     */
    location?: string | null;
    /**
     * Max Participants
     */
    max_participants?: number | null;
    /**
     * Additional Link
     */
    additional_link?: string | null;
    /**
     * Invite Email Subject
     *
     * The event's own invite template's subject; None in event lists.
     */
    invite_email_subject?: string | null;
    /**
     * Invite Email Body
     *
     * The event's own invite template's body; None in event lists.
     */
    invite_email_body?: string | null;
    /**
     * Invite sender email
     *
     * Custom From email for event mail; null uses app default.
     */
    invite_sender_email?: string | null;
    /**
     * Invite sender name
     *
     * Display name for the event sender line.
     */
    invite_sender_name?: string | null;
    /**
     * Landing image
     *
     * Storage key for custom landing hero image (advisor upload).
     */
    landing_image?: string | null;
    /**
     * Created
     */
    created: string;
    /**
     * Last Modified
     */
    last_modified: string;
    /**
     * Guests Count
     */
    guests_count?: number;
    /**
     * Actual Guests Count
     *
     * Count of guests (event_guest records) for this event.
     */
    actual_guests_count?: number;
    /**
     * Guests
     *
     * Guest list when requested (polymorphic on source type)
     */
    guests?: Array<EventGuestItemResponseWritable> | null;
    /**
     * My RSVP Status
     *
     * The viewing user's own RSVP status when listed as a guest (``GET /api/v2/users/{uuid}/events`` only).
     */
    my_status?: EventGuestStatus | null;
    /**
     * Is Past
     *
     * True when the event start is in the past (no date ⇒ upcoming). Populated on user event list responses.
     */
    is_past?: boolean | null;
};

/**
 * ExecutiveClientEventAttendee
 *
 * A fellow attendee shown to an executive client on their event view.
 *
 * Only attendees the exec already knows (in their People collection) are
 * listed for upcoming events; everyone who attended is listed for past
 * events. PII beyond name/role/company is intentionally omitted — use the
 * People page for full contact detail.
 */
export type ExecutiveClientEventAttendeeWritable = {
    /**
     * Identity UUID
     *
     * The human this attendee is; null for a free-text guest
     */
    identity_uuid?: string | null;
    /**
     * Person UUID
     */
    person_uuid?: string | null;
    /**
     * Candidate UUID
     */
    candidate_uuid?: string | null;
    /**
     * Full Name
     */
    full_name?: string | null;
    /**
     * Current Role
     */
    current_role?: string | null;
    /**
     * Current Company
     */
    current_company?: string | null;
    /**
     * Profile Image
     */
    profile_img?: string | null;
};

/**
 * ExecutiveClientEventResponse
 *
 * Event detail for an external executive client who is a guest.
 *
 * Attendee visibility:
 * - Webinars never show attendees (``shows_attendees=False``).
 * - Upcoming events list only RSVP'd (going) attendees the exec already
 * knows (i.e. in their People collection independent of this event), plus
 * a count of the remaining RSVP'd attendees in ``other_attendees_count``.
 * - Past events list everyone who attended (all become contacts), and
 * ``other_attendees_count`` is 0.
 */
export type ExecutiveClientEventResponseWritable = {
    /**
     * Event
     */
    event: EventRsvpPublicResponse;
    /**
     * My RSVP Status
     *
     * The requesting executive's own RSVP status for this event.
     */
    my_status: EventGuestStatus;
    /**
     * Is Past
     *
     * True when the event's date is in the past (no date ⇒ upcoming).
     */
    is_past: boolean;
    /**
     * Shows Attendees
     *
     * False for webinars, which never expose an attendee list.
     */
    shows_attendees: boolean;
    /**
     * Attendees
     *
     * Upcoming: RSVP'd attendees the exec already knows. Past: everyone who attended. Empty for webinars.
     */
    attendees?: Array<ExecutiveClientEventAttendeeWritable>;
    /**
     * Other Attendees Count
     *
     * Upcoming events only: number of RSVP'd attendees not shown (those the exec does not already know). 0 for past events and webinars.
     */
    other_attendees_count?: number;
};

/**
 * ExecutiveContactDetailResponse
 *
 * Unified detail for one of the exec's contacts (BAN-5755).
 *
 * A single envelope that works whether the contact is candidate-backed or
 * person-only. The canonical relationship fields (``was_introduced``,
 * ``is_favorite``, ``source_types``, ``evidence``, ``personal_tags``) live at
 * the top level so the webapp reads them the same way regardless of type.
 *
 * ``candidate`` carries the full candidate profile when a candidate record
 * exists; ``person`` carries the linked person when one exists — both may be
 * present for a candidate who also has a ``Person`` row. The top-level
 * ``email`` prefers the candidate's (gated) email and falls back to the
 * person's; the same gated values appear on the sub-objects.
 */
export type ExecutiveContactDetailResponseWritable = {
    /**
     * Candidate Uuid
     */
    candidate_uuid?: string | null;
    /**
     * Person Uuid
     */
    person_uuid?: string | null;
    /**
     * Identity Uuid
     */
    identity_uuid?: string | null;
    /**
     * Email
     */
    email?: string | null;
    /**
     * Source Types
     */
    source_types?: Array<ContactSourceType>;
    evidence?: ContactEvidenceResponse | null;
    /**
     * Personal Tags
     */
    personal_tags?: Array<PersonalContactTagItem>;
    /**
     * Is Favorite
     */
    is_favorite?: boolean;
    /**
     * Was Introduced
     */
    was_introduced?: boolean;
    /**
     * Is Represented
     *
     * Banff represents this contact: their candidate profile is in tiers 1 to 5. Having a profile is not enough (B4B and external profiles are not represented). Follows today's tier list; the definition is still evolving.
     */
    is_represented?: boolean;
    candidate?: CandidateExternalFullResponseWithTeamContextWritable | null;
    person?: ExecutiveContactPerson | null;
};

/**
 * ExecutivePeopleDetailResponse
 *
 * Person-only contact detail for the executive client people page.
 */
export type ExecutivePeopleDetailResponseWritable = {
    /**
     * Person Uuid
     */
    person_uuid: string;
    /**
     * Identity Uuid
     */
    identity_uuid?: string | null;
    /**
     * Full Name
     */
    full_name?: string | null;
    /**
     * First Name
     */
    first_name?: string | null;
    /**
     * Last Name
     */
    last_name?: string | null;
    /**
     * Email
     */
    email?: string | null;
    /**
     * Profile Img
     */
    profile_img?: string | null;
    /**
     * Notes External
     */
    notes_external?: string | null;
    /**
     * Linkedin Url
     */
    linkedin_url?: string | null;
    /**
     * Current Role
     */
    current_role?: string | null;
    /**
     * Current Company
     */
    current_company?: string | null;
    company?: CompanyBasicTypeResponse | null;
    /**
     * Source Types
     */
    source_types?: Array<ContactSourceType>;
    evidence?: ContactEvidenceResponse | null;
    /**
     * Personal Tags
     */
    personal_tags?: Array<PersonalContactTagItem>;
    /**
     * Is Favorite
     */
    is_favorite?: boolean;
    /**
     * Was Introduced
     */
    was_introduced?: boolean;
};

/**
 * ExecutivePeopleResultItem
 *
 * A single contact in the exec's people page.
 *
 * Canonical typed contact item for the people page.
 */
export type ExecutivePeopleResultItemWritable = {
    /**
     * Object Type
     */
    object_type: 'candidate' | 'person';
    /**
     * Contact Key
     */
    contact_key: string;
    /**
     * Candidate Uuid
     */
    candidate_uuid?: string | null;
    /**
     * Person Uuid
     */
    person_uuid?: string | null;
    /**
     * Full Name
     */
    full_name?: string | null;
    /**
     * Email
     */
    email?: string | null;
    /**
     * Current Company
     */
    current_company?: string | null;
    /**
     * Current Role
     */
    current_role?: string | null;
    /**
     * Profile Img
     */
    profile_img?: string | null;
    /**
     * Source Types
     */
    source_types?: Array<ContactSourceType>;
    evidence?: ContactEvidenceResponse | null;
    /**
     * Personal Tags
     */
    personal_tags?: Array<PersonalContactTagItem>;
    /**
     * Is Favorite
     */
    is_favorite?: boolean;
    /**
     * Was Introduced
     */
    was_introduced?: boolean;
    [key: string]: unknown;
};

/**
 * ExecutivePeopleSearchResponse
 */
export type ExecutivePeopleSearchResponseWritable = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Results
     */
    results: Array<ExecutivePeopleResultItemWritable>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCountsResponse;
    aggregations?: ExecutivePeopleAggregationsResponse;
    /**
     * Search Id
     */
    search_id?: string | null;
};

/**
 * GroupedActivityLogResponse
 */
export type GroupedActivityLogResponseWritable = {
    /**
     * Log statistics
     */
    totals: CandidateActivityLogTotals;
    /**
     * Candidate
     *
     * Information about the candidate
     */
    candidate: CandidateBasicResponseWritable;
    /**
     * Logs grouped by type
     */
    logs: CandidateActivityLogReferenceGroupsWritable;
};

/**
 * HomeCandidatesResponse
 */
export type HomeCandidatesResponseWritable = {
    /**
     * Results
     */
    results: Array<EsCandidateIndexExternalResultWritable>;
    /**
     * Search Id
     *
     * Search ID
     */
    search_id: string;
    total_counts: TotalCountsResponse;
};

/**
 * HomeTeamActivityPipelineResponse
 */
export type HomeTeamActivityPipelineResponseWritable = {
    /**
     * Introductions Pending
     */
    introductions_pending: number;
    pipeline: PipelineExternalResponse;
    /**
     * Recommendations Count
     */
    recommendations_count: number;
    /**
     * Recommendations Unread
     */
    recommendations_unread: number;
    /**
     * Recommendations
     */
    recommendations: Array<HomeTeamActivityRecommendedPersonWritable>;
    team: TeamBasicUuidResponse;
};

/**
 * HomeTeamActivityRecommendationResponse
 */
export type HomeTeamActivityRecommendationResponseWritable = {
    candidate: CandidateExternalFullResponse;
    /**
     * Date Introduction Facilitated
     */
    date_introduction_facilitated: string;
    /**
     * Person who was introduced to this candidate
     */
    person: PersonBasicResponseWritable;
    pipeline: PipelineExternalResponse;
    team: TeamBasicUuidResponse;
    /**
     * Uuid
     *
     * UUID uniquely identifying a recommendation
     */
    uuid: string;
};

/**
 * HomeTeamActivityRecommendedPerson
 *
 * One recommended candidate in a pipeline's activity row.
 *
 * Name and image come from the candidate's own record, so every recommended
 * candidate appears — the counts next to these avatars include the ones with
 * no login. ``uuid`` is a login's uuid, so it is the candidate's activated
 * executive and is absent when they have none; it is never another member of
 * the identity.
 */
export type HomeTeamActivityRecommendedPersonWritable = {
    /**
     * Name
     *
     * Person's full name
     */
    name: string;
    /**
     * First Name
     *
     * Person's first name
     */
    first_name?: string | null;
    /**
     * Last Name
     *
     * Person's last name
     */
    last_name?: string | null;
    /**
     * Image
     *
     * Path to person image
     */
    image?: string | null;
    /**
     * UUID
     *
     * UUID of the candidate's executive login, when they have one
     */
    uuid?: string | null;
};

/**
 * HomeTeamActivityResponse
 */
export type HomeTeamActivityResponseWritable = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Results
     */
    results: Array<HomeTeamActivityPipelineResponseWritable | HomeTeamActivityRecommendationResponseWritable>;
    /**
     * Size
     */
    size: number;
    total_counts: HomeTeamActivityCounts;
};

/**
 * HomepageActivityLogResponse
 */
export type HomepageActivityLogResponseWritable = {
    /**
     * Actor
     *
     * Actor
     */
    actor?: PersonBasicResponseWritable | null;
    /**
     * Candidate
     *
     * Information about the candidate
     */
    candidate?: CandidateBasicResponseWritable | null;
    /**
     * Event date
     *
     * The date of the event
     */
    date: string;
    /**
     * Event Type
     *
     * Type of event associated with the log
     */
    event_type: ActivityLogEventType | CandidateActivityLogEventType;
    /**
     * Message
     *
     * Message associated with the event
     */
    message: ActivityLogMessage;
    /**
     * Reference ID
     *
     * UUID of the entity that triggered this event
     */
    reference_id?: string | null;
    /**
     * Reference Type
     *
     * Which entity triggered the event
     */
    reference_type?: CandidateActivityLogReferenceType | null;
    /**
     * UUID
     *
     * Event UUID or pipeline UUID
     */
    uuid: string;
};

/**
 * IdentityBasicResponse
 */
export type IdentityBasicResponseWritable = {
    /**
     * UUID
     *
     * The identity: one real human.
     */
    uuid: string;
    /**
     * Name
     *
     * From the candidate record if there is one, else the executive login, else the earliest login.
     */
    name?: string | null;
    /**
     * Candidate
     *
     * Their candidate record, if we hold one.
     */
    candidate?: CandidateBasicResponseWritable | null;
    /**
     * Executive person
     *
     * Their activated executive login, if they have one. An identity holds at most one; other logins are not exposed here.
     */
    executive_person?: PersonBasicResponseWritable | null;
};

/**
 * IdentityEmailItemResponse
 *
 * One email sent to this human, and what SendGrid has told us about it since.
 */
export type IdentityEmailItemResponseWritable = {
    /**
     * SendGrid message ID
     */
    sendgrid_message_id: string;
    /**
     * Recipient email
     */
    sendgrid_recipient_email: string;
    /**
     * Recipient
     *
     * Who received it, when the address belongs to someone we hold. Nothing for an external address.
     */
    recipient?: IdentityBasicResponseWritable | null;
    /**
     * Email event type
     */
    email_event_type?: EmailEventType | null;
    /**
     * Batch UUID
     *
     * The send job this email was part of: every invite or reminder sent in one go shares it.
     */
    batch_uuid?: string | null;
    /**
     * Sent at
     */
    sent_at?: string | null;
    /**
     * Delivered at
     */
    delivered_at?: string | null;
    /**
     * First opened at
     */
    first_opened_at?: string | null;
    /**
     * Last opened at
     */
    last_opened_at?: string | null;
    /**
     * Opens
     */
    open_count: number;
    /**
     * Clicks
     */
    click_count: number;
    /**
     * Failed at
     */
    failed_at?: string | null;
    /**
     * Failure
     *
     * Set when SendGrid could not deliver it.
     */
    failure?: 'dropped' | 'bounce' | null;
};

/**
 * IdentityPersonItem
 */
export type IdentityPersonItemWritable = {
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Email
     */
    email: string;
    /**
     * First Name
     */
    first_name?: string | null;
    /**
     * Last Name
     */
    last_name?: string | null;
    /**
     * Name
     */
    name?: string | null;
    /**
     * Is Activated Executive
     */
    is_activated_executive: boolean;
    /**
     * Registered
     */
    registered: boolean;
    user: AdminUserDetailedResponseWritable;
};

/**
 * IdentityResponse
 */
export type IdentityResponseWritable = {
    /**
     * Identity Uuid
     */
    identity_uuid: string;
    candidate?: IdentityCandidateItem | null;
    /**
     * Executive Person Uuid
     */
    executive_person_uuid?: string | null;
    /**
     * Persons
     */
    persons: Array<IdentityPersonItemWritable>;
    /**
     * Merged Into Uuid
     */
    merged_into_uuid?: string | null;
};

/**
 * InternalCommentResponse
 */
export type InternalCommentResponseWritable = {
    /**
     * Candidate
     *
     * Candidate for which this internal comment was made
     */
    candidate: UuidResponse;
    /**
     * Person
     *
     * Internal comment author (advisor only)
     */
    person: PersonBasicResponseWritable;
    /**
     * Comment text
     *
     * The content of the internal recommendation comment
     */
    content: string;
    /**
     * Created
     *
     * Date when this internal comment was published
     */
    created: string;
    /**
     * Internal Comment ID
     *
     * The unique identifier for internal comment
     */
    id: string;
    /**
     * Last modified
     *
     * Date when this internal comment was modified
     */
    last_modified: string;
    /**
     * Pipeline
     *
     * Pipeline in which this internal comment was made
     */
    pipeline?: UuidResponse | null;
    /**
     * Recommendation
     *
     * Recommendation for which this internal comment was made
     */
    recommendation?: UuidResponse | null;
    /**
     * Team
     *
     * Team in which this internal comment was made
     */
    team: UuidResponse;
};

/**
 * Page[AllIntrosResponse]
 */
export type PageAllIntrosResponseWritable = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Results
     */
    results: Array<AllIntrosResponseWritable>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCounts;
};

/**
 * Page[CandidateActivityEvent]
 */
export type PageCandidateActivityEventWritable = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Results
     */
    results: Array<CandidateActivityEventWritable>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCounts;
};

/**
 * Page[CandidateActivityLogResponse]
 */
export type PageCandidateActivityLogResponseWritable = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Results
     */
    results: Array<CandidateActivityLogResponseWritable>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCounts;
};

/**
 * Page[CandidateExtendedResponse]
 */
export type PageCandidateExtendedResponseWritable = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Results
     */
    results: Array<CandidateExtendedResponseWritable>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCounts;
};

/**
 * Page[CandidateRecommendationResponse]
 */
export type PageCandidateRecommendationResponseWritable = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Results
     */
    results: Array<CandidateRecommendationResponseWritable>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCounts;
};

/**
 * Page[CandidateSearchResponseWithLinkedPerson]
 */
export type PageCandidateSearchResponseWithLinkedPersonWritable = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Results
     */
    results: Array<CandidateSearchResponseWithLinkedPersonWritable>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCounts;
};

/**
 * Page[CandidatesTableResponse]
 */
export type PageCandidatesTableResponseWritable = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Results
     */
    results: Array<CandidatesTableResponseWritable>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCounts;
};

/**
 * Page[CommentResponse]
 */
export type PageCommentResponseWritable = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Results
     */
    results: Array<CommentResponseWritable>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCounts;
};

/**
 * Page[EventEmailBatchResponse]
 */
export type PageEventEmailBatchResponseWritable = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Results
     */
    results: Array<EventEmailBatchResponseWritable>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCounts;
};

/**
 * Page[EventEmailEventItemResponse]
 */
export type PageEventEmailEventItemResponseWritable = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Results
     */
    results: Array<EventEmailEventItemResponseWritable>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCounts;
};

/**
 * Page[EventGuestWithEventItemResponse]
 */
export type PageEventGuestWithEventItemResponseWritable = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Results
     */
    results: Array<EventGuestWithEventItemResponseWritable>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCounts;
};

/**
 * Page[EventResponse]
 */
export type PageEventResponseWritable = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Results
     */
    results: Array<EventResponseWritable>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCounts;
};

/**
 * Page[HomepageActivityLogResponse]
 */
export type PageHomepageActivityLogResponseWritable = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Results
     */
    results: Array<HomepageActivityLogResponseWritable>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCounts;
};

/**
 * Page[IdentityEmailItemResponse]
 */
export type PageIdentityEmailItemResponseWritable = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Results
     */
    results: Array<IdentityEmailItemResponseWritable>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCounts;
};

/**
 * Page[InternalCommentResponse]
 */
export type PageInternalCommentResponseWritable = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Results
     */
    results: Array<InternalCommentResponseWritable>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCounts;
};

/**
 * Page[PeerActivityResponse]
 */
export type PagePeerActivityResponseWritable = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Results
     */
    results: Array<PeerActivityResponseWritable>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCounts;
};

/**
 * Page[PersonBasicEmailResponse]
 */
export type PagePersonBasicEmailResponseWritable = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Results
     */
    results: Array<PersonBasicEmailResponseWritable>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCounts;
};

/**
 * Page[PipelineSearchResponse]
 */
export type PagePipelineSearchResponseWritable = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Results
     */
    results: Array<PipelineSearchResponseWritable>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCounts;
};

/**
 * PeerActivityResponse
 */
export type PeerActivityResponseWritable = {
    /**
     * Activity context
     *
     * Activity context.
     */
    context?: string | null;
    /**
     * Created On
     *
     * Creation date of the activity.
     */
    created: string;
    /**
     * Creator
     *
     * Creator of the activity
     */
    creator: PersonBasicResponseWritable;
    /**
     * Activity event
     *
     * Activity event.
     */
    event: PeerEvent;
    /**
     * Record ID
     *
     * Unique identifier for this activity
     */
    id: string;
    /**
     * Last Modified On
     *
     * Last modification date of the activity
     */
    last_modified: string;
};

/**
 * PeerCommentResponse
 */
export type PeerCommentResponseWritable = {
    /**
     * Peer Comment Context
     *
     * Context for this comment
     */
    context: string;
    /**
     * Created On
     *
     * Creation date of the p2p match
     */
    created: string;
    /**
     * Creator
     *
     * Creator of the p2p match
     */
    creator: PersonBasicResponseWritable;
    /**
     * Peer Comment ID
     *
     * Unique identifier for this peer comment
     */
    id: string;
    /**
     * Last Modified On
     *
     * Last modification date of the p2p match
     */
    last_modified: string;
    /**
     * Comment's Peer
     *
     * Comment's peer
     */
    peer: PeerToPeerBasicResponseWritable;
};

/**
 * PeerCompareAboutResponse
 */
export type PeerCompareAboutResponseWritable = {
    /**
     * Executive
     *
     * Executive information
     */
    candidate: CandidateBasicResponseWritable;
    /**
     * Candidate about
     *
     * Candidate about
     */
    introduction_templates: Array<CandidateIntroductionTemplateResponse>;
};

/**
 * PeerCompareGoalsResponse
 */
export type PeerCompareGoalsResponseWritable = {
    /**
     * Executive
     *
     * Executive information
     */
    candidate: CandidateBasicResponseWritable;
    /**
     * Goals
     *
     * Goals of the candidate being compared
     */
    goals: Array<PeerCompareGoalResponse>;
};

/**
 * PeerCompareP2PResponse
 */
export type PeerCompareP2pResponseWritable = {
    /**
     * Executive
     *
     * Executive information
     */
    candidate: CandidateBasicResponseWritable;
    /**
     * Client candidate
     *
     * Client candidate being compared
     */
    matches: Array<PeerComparePeerToPeerResponseWritable>;
};

/**
 * PeerComparePeerToPeerResponse
 */
export type PeerComparePeerToPeerResponseWritable = {
    /**
     * Record ID
     *
     * Unique identifier for this peer model
     */
    id: string;
    /**
     * Created On
     *
     * Creation date of the p2p match
     */
    created: string;
    /**
     * Last Modified On
     *
     * Last modification date of the p2p match
     */
    last_modified: string;
    /**
     * Introduced On
     *
     * Date the peer was introduced
     */
    introduced_on?: string | null;
    /**
     * Candidate
     *
     * The candidate object
     */
    candidate: CandidateCompanyRoleWithAdvisorResponseWritable;
    /**
     * Status
     *
     * Current status of a p2p match
     */
    status: PeerStatus;
};

/**
 * PeerToPeerBasicESResponse
 */
export type PeerToPeerBasicEsResponseWritable = {
    /**
     * Assignees
     *
     * Assignees responsible for the p2p match
     */
    assignees: Array<PersonBasicEmailResponseWritable>;
    /**
     * Candidate
     *
     * The candidate object
     */
    candidate: CandidateCompanyRoleWithAdvisorResponseWritable;
    /**
     * Target candidate
     *
     * The target candidate object (peer)
     */
    candidate_to: CandidateCompanyRoleWithAdvisorResponseWritable;
    /**
     * Context
     *
     * Context containing reason for this peer. This is a required field for peers created in Contender, but for historical reasons, this field might be undefined because we did not require context in the original Retool app.
     */
    context?: string | null;
    /**
     * Created On
     *
     * Creation date of the p2p match
     */
    created: string;
    /**
     * Creator
     *
     * Creator of the p2p match
     */
    creator: PersonBasicEmailResponseWritable;
    /**
     * Record ID
     *
     * Unique identifier for this peer model
     */
    id: string;
    /**
     * Introduced On
     *
     * Date the peer was introduced
     */
    introduced_on?: string | null;
    /**
     * Last Modified On
     *
     * Last modification date of the p2p match
     */
    last_modified: string;
    /**
     * Status
     *
     * Current status of a p2p match
     */
    status: PeerStatus;
    /**
     * Reviewers
     *
     * Reviewers responsible for the p2p match
     */
    reviewers: Array<PersonBasicEmailResponseWritable>;
    /**
     * Reviewers status
     *
     * Reviewers status by person ID
     */
    reviewers_statuses: Array<PeerToPeerReviewersStatus>;
};

/**
 * PeerToPeerBasicResponse
 */
export type PeerToPeerBasicResponseWritable = {
    /**
     * Assignees
     *
     * Assignees responsible for the p2p match
     */
    assignees: Array<PersonBasicEmailResponseWritable>;
    /**
     * Candidate
     *
     * The candidate object
     */
    candidate: CandidateCompanyRoleWithAdvisorResponseWritable;
    /**
     * Target candidate
     *
     * The target candidate object (peer)
     */
    candidate_to: CandidateCompanyRoleWithAdvisorResponseWritable;
    /**
     * Context
     *
     * Context containing reason for this peer. This is a required field for peers created in Contender, but for historical reasons, this field might be undefined because we did not require context in the original Retool app.
     */
    context?: string | null;
    /**
     * Created On
     *
     * Creation date of the p2p match
     */
    created: string;
    /**
     * Creator
     *
     * Creator of the p2p match
     */
    creator: PersonBasicEmailResponseWritable;
    /**
     * Record ID
     *
     * Unique identifier for this peer model
     */
    id: string;
    /**
     * Introduced On
     *
     * Date the peer was introduced
     */
    introduced_on?: string | null;
    /**
     * Last Modified On
     *
     * Last modification date of the p2p match
     */
    last_modified: string;
    /**
     * Status
     *
     * Current status of a p2p match
     */
    status: PeerStatus;
};

/**
 * PeerToPeerExtendedResponse
 */
export type PeerToPeerExtendedResponseWritable = {
    /**
     * Assignees
     *
     * Assignees responsible for the p2p match
     */
    assignees: Array<PersonBasicEmailResponseWritable>;
    /**
     * Candidate
     *
     * The candidate object
     */
    candidate: CandidateCompanyRoleWithAdvisorResponseWritable;
    /**
     * Target candidate
     *
     * The target candidate object (peer)
     */
    candidate_to: CandidateCompanyRoleWithAdvisorResponseWritable;
    /**
     * Context
     *
     * Context containing reason for this peer. This is a required field for peers created in Contender, but for historical reasons, this field might be undefined because we did not require context in the original Retool app.
     */
    context?: string | null;
    /**
     * Created On
     *
     * Creation date of the p2p match
     */
    created: string;
    /**
     * Creator
     *
     * Creator of the p2p match
     */
    creator: PersonBasicEmailResponseWritable;
    /**
     * Record ID
     *
     * Unique identifier for this peer model
     */
    id: string;
    /**
     * Introduced On
     *
     * Date the peer was introduced
     */
    introduced_on?: string | null;
    /**
     * Last Modified On
     *
     * Last modification date of the p2p match
     */
    last_modified: string;
    /**
     * Status
     *
     * Current status of a p2p match
     */
    status: PeerStatus;
    /**
     * Email body
     *
     * Email body
     */
    email_body?: string | null;
    /**
     * Email sender
     *
     * Email sender
     */
    email_sender?: PersonBasicEmailResponseWritable | null;
    /**
     * Email subject
     *
     * Email subject
     */
    email_subject?: string | null;
    /**
     * People to CC
     *
     * List of people to CC the email to
     */
    email_cc?: Array<PersonBasicEmailResponseWritable> | null;
    /**
     * Reviewers
     *
     * Reviewers responsible for the p2p match
     */
    reviewers: Array<PersonBasicEmailResponseWritable>;
    /**
     * Reviewers status
     *
     * Reviewers status by person ID
     */
    reviewers_status: {
        [key: string]: PeerStatus;
    };
};

/**
 * PeerToPeerSelectResponse
 */
export type PeerToPeerSelectResponseWritable = {
    /**
     * Client
     *
     * Indicates whether a person is a Banff client
     */
    is_client: boolean;
    /**
     * Company
     *
     * Candidate's current company
     */
    company?: string | null;
    /**
     * Role
     *
     * Candidate's current role
     */
    role?: string | null;
    /**
     * Image
     *
     * Path to candidate image
     */
    image?: string | null;
    /**
     * Name
     *
     * Candidate's full name
     */
    name: string;
    /**
     * UUID
     *
     * UUID uniquely identifying a person
     */
    uuid: string;
    /**
     * First name
     *
     * Candidate's first name
     */
    first_name?: string | null;
    /**
     * Functional expertise
     *
     * List of functions candidate has experience in
     */
    function_expertise: Array<Function>;
    /**
     * Target functions
     *
     * Functional expertise candidate would like to get
     */
    function_target: Array<Function>;
    /**
     * Industry expertise
     *
     * List of industries candidate has experience in
     */
    industry_expertise: Array<Industry>;
    /**
     * Target industries
     *
     * Functional expertise candidate would like to get
     */
    industry_target: Array<Industry>;
    /**
     * Board/advisory role interest
     *
     * Interest in getting board or advisory roles
     */
    interest_role_board_advisory?: InterestRole | null;
    /**
     * Operating role interest
     *
     * Interest in getting operating roles
     */
    interest_role_operating?: InterestRole | null;
    /**
     * Personal interests
     *
     * List of personal interests
     */
    interests_personal: Array<InterestPersonal>;
    /**
     * Professional interests
     *
     * List of professional interests
     */
    interests_professional: Array<InterestProfessional>;
    /**
     * Keywords
     *
     * List of keywords
     */
    keywords: Array<string>;
    /**
     * Last name
     *
     * Candidate's last name
     */
    last_name?: string | null;
    /**
     * LinkedIn URL
     *
     * Link to the person's LinkedIn profile
     */
    linkedin_url?: string | null;
    /**
     * Tier
     *
     * Candidate tier
     */
    tier: CandidateTier;
    /**
     * Type
     *
     * Candidate type
     */
    type?: ClientType | null;
    /**
     * Pathway
     *
     * Banff service pathway for the candidate
     */
    pathway?: CandidatePathway | null;
    /**
     * Advisor
     *
     * Information about the advisor assigned to this candidate
     */
    advisor?: PersonBasicResponseWritable | null;
    /**
     * P2P introductions count
     *
     * Overall count of P2P introductions a candidate ever had either as a client or as a peer.
     */
    intros_count?: number;
    /**
     * Last P2P match introduced on
     *
     * Last time a candidate had an introduction in any P2P match as a client or as a peer.
     */
    last_peer_introduced_on?: string | null;
    /**
     * Previous P2P match status
     *
     * Last status of a previous P2P match with client executive candidate
     */
    last_peer_status?: PeerStatus | null;
    /**
     * Last event date
     *
     * Start time of the most recent non-webinar event where this peer candidate and the client executive both RSVP'd going.
     */
    last_event_date?: string | null;
    /**
     * Last event
     *
     * The event for last_event_date (non-webinar; both candidates going).
     */
    last_event?: PeerToPeerLastEventResponse | null;
    /**
     * Client has peer as referrer
     *
     * True if the client executive's candidate profile lists the peer executive (their linked Person) as a referrer.
     */
    client_has_peer_as_referrer?: boolean;
    /**
     * Peer has client as referrer
     *
     * True if the peer executive's candidate profile lists the client executive (their linked Person) as a referrer.
     */
    peer_has_client_as_referrer?: boolean;
    /**
     * Location
     *
     * Candidate's location (from their resume)
     */
    location?: string | null;
    /**
     * Invoice Status
     *
     * HubSpot invoice status (admin-facing; tier 1/1b only)
     */
    invoice_status?: HubSpotInvoiceStatus | null;
};

/**
 * PeerToPeerTableExecutiveResponse
 */
export type PeerToPeerTableExecutiveResponseWritable = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Results
     */
    results: Array<PeerToPeerBasicResponseWritable>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCountsResponse;
    aggregations: PeerToPeerTableExecutiveAggregationsResponse;
};

/**
 * PeerToPeerTableResponse
 */
export type PeerToPeerTableResponseWritable = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Results
     */
    results: Array<PeerToPeerBasicEsResponseWritable>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCountsResponse;
    aggregations: PeerToPeerTableFilterAggregationsResponse;
};

/**
 * PeerToPeerTableSelectResponse
 */
export type PeerToPeerTableSelectResponseWritable = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Results
     */
    results: Array<PeerToPeerSelectResponseWritable>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCountsResponse;
    aggregations: PeerToPeerTableSelectAggregationsResponse;
};

/**
 * PersonBasicEmailResponse
 */
export type PersonBasicEmailResponseWritable = {
    /**
     * Email
     *
     * Email associated with the person
     */
    email: string;
    /**
     * Name
     *
     * Person's full name
     */
    name: string;
    /**
     * First Name
     *
     * Person's first name
     */
    first_name?: string | null;
    /**
     * Last Name
     *
     * Person's last name
     */
    last_name?: string | null;
    /**
     * Image
     *
     * Path to person image
     */
    image?: string | null;
    /**
     * UUID
     *
     * UUID uniquely identifying a person
     */
    uuid: string;
};

/**
 * PersonBasicResponse
 */
export type PersonBasicResponseWritable = {
    /**
     * Name
     *
     * Person's full name
     */
    name: string;
    /**
     * First Name
     *
     * Person's first name
     */
    first_name?: string | null;
    /**
     * Last Name
     *
     * Person's last name
     */
    last_name?: string | null;
    /**
     * Image
     *
     * Path to person image
     */
    image?: string | null;
    /**
     * UUID
     *
     * UUID uniquely identifying a person
     */
    uuid: string;
};

/**
 * PersonInnerDocSchema
 */
export type PersonInnerDocSchemaWritable = {
    /**
     * Email
     *
     * Email associated with the person
     */
    email: string;
    /**
     * First name
     *
     * Person's first name
     */
    first_name?: string | null;
    /**
     * Name
     *
     * Person's full name
     */
    name: string;
    /**
     * Last name
     *
     * Person's last name
     */
    last_name?: string | null;
    /**
     * Image
     *
     * Path to person image
     */
    image?: string | null;
    /**
     * ID
     *
     * UUID uniquely identifying a person
     */
    uuid: string;
};

/**
 * PersonMemberResponse
 */
export type PersonMemberResponseWritable = {
    /**
     * First Name
     *
     * Person's first name
     */
    first_name?: string | null;
    /**
     * Last Name
     *
     * Person's last name
     */
    last_name?: string | null;
    /**
     * Email
     *
     * Email associated with the person
     */
    email: string;
    /**
     * Pending
     *
     * Is the person's invite still pending, or has it been accepted?
     */
    pending: boolean;
    /**
     * Terms accepted
     *
     * Time when a person accepted terms and conditions on first login
     *
     * @deprecated
     */
    first_login?: string | null;
    /**
     * Name
     *
     * Person's full name
     */
    name: string;
    /**
     * ID
     *
     * ID uniquely identifying a person
     */
    id: string;
    /**
     * Image
     *
     * Path to person image
     */
    image?: string | null;
    /**
     * Roles
     *
     * List of person's roles
     */
    roles: Array<PersonMemberRole>;
    /**
     * Permit Preregistration Notifications
     *
     * Whether the person can receivepreregistration notifications
     */
    permit_preregistration_notifications: boolean;
    /**
     * Terms accepted
     *
     * Time when a person accepted terms and conditions on first login
     */
    terms_accepted?: string | null;
};

/**
 * PersonV1CurrentResponse
 *
 * GET ``/api/v1/people/current`` — legacy person payload with a signed profile image URL.
 */
export type PersonV1CurrentResponseWritable = {
    /**
     * Country
     */
    country?: string | null;
    /**
     * Email
     */
    email: string;
    /**
     * First Login
     */
    first_login?: string | null;
    /**
     * First Name
     */
    first_name?: string | null;
    /**
     * Has Set Password
     */
    has_set_password?: boolean;
    /**
     * Id
     */
    id?: string | null;
    /**
     * Last Login
     */
    last_login?: string | null;
    /**
     * Last Name
     */
    last_name?: string | null;
    /**
     * Last Team Visit Slug
     */
    last_team_visit_slug?: string | null;
    /**
     * Name
     */
    name?: string | null;
    /**
     * Newly Invited
     */
    newly_invited?: boolean;
    /**
     * Notes External
     */
    notes_external?: string | null;
    /**
     * Linkedin Url
     */
    linkedin_url?: string | null;
    /**
     * Person Image
     */
    person_image?: string | null;
    /**
     * Person Name
     */
    person_name?: string | null;
    /**
     * Is Executive
     */
    is_executive?: boolean;
    /**
     * Roles
     */
    roles?: Array<PersonRole>;
    /**
     * Uuid
     */
    uuid: string;
};

/**
 * PipelineCandidateSearchFilterSortResponse
 */
export type PipelineCandidateSearchFilterSortResponseWritable = {
    /**
     * Meta
     *
     * Metadata about the search result
     */
    search_metadata?: EsMeta | null;
    /**
     * Advisor
     *
     * Information about the advisor assigned to this candidate
     */
    advisor?: PersonBasicResponseWritable | null;
    /**
     * Client Owner
     *
     * Information about the client owner of this candidate
     */
    client_owner?: PersonBasicResponseWritable | null;
    /**
     * Created
     *
     * Date when this candidate profile was created
     */
    created?: string | null;
    /**
     * Company
     *
     * Candidate's current company
     */
    current_company?: string | null;
    /**
     * Role
     *
     * Candidate's current role
     */
    current_role?: string | null;
    /**
     * Current Role
     *
     * Candidate's current role
     */
    role?: string | null;
    /**
     * Location
     *
     * Candidate's location (from their resume)
     */
    location?: string | null;
    /**
     * Board/advisory role interest
     *
     * Interest in getting board or advisory roles
     */
    interest_role_board_advisory?: InterestRole | null;
    /**
     * Operating role interest
     *
     * Interest in getting operating roles
     */
    interest_role_operating?: InterestRole | null;
    /**
     * Keywords
     */
    keywords?: Array<string> | null;
    /**
     * Notes
     */
    notes?: string | null;
    /**
     * Functional expertise
     *
     * List of functions candidate has experience in
     */
    function_expertise?: Array<Function>;
    /**
     * Industry expertise
     *
     * List of industries candidate has experience in
     */
    industry_expertise?: Array<Industry>;
    /**
     * URG
     *
     * Is this candidate from an under-represented group?
     */
    is_urg?: boolean | null;
    /**
     * Operating Highlights
     *
     * List of operating highlights
     */
    operating_highlights?: Array<OperatingHighlights> | null;
    /**
     * Email
     *
     * Candidate's email address
     */
    email: string;
    /**
     * Name
     *
     * Candidate's full name
     */
    name: string;
    /**
     * Image
     *
     * Candidate's profile image URL
     */
    image?: string | null;
    /**
     * Referrers
     *
     * List of people who referred this candidate
     */
    referrers?: Array<PersonBasicResponseWritable> | null;
    /**
     * Tier
     *
     * Candidate tier
     */
    tier: CandidateTier;
    /**
     * Type
     *
     * Banff client type; serialized only for Banff advisors
     */
    type?: ClientType | null;
    /**
     * Pathway
     *
     * Banff pathway; serialized only for Banff advisors
     */
    pathway?: CandidatePathway | null;
    /**
     * Invoice Status
     *
     * HubSpot invoice status; serialized only for Banff advisors
     */
    invoice_status?: HubSpotInvoiceStatus | null;
    /**
     * Warm
     *
     * This value represents whether the candidate has been warm with Banff
     */
    warm?: boolean | null;
    /**
     * RecSys Score
     *
     * Score calculated by the recommendation system
     */
    recsys_score?: number | null;
    /**
     * Suggestion sources
     *
     * Present only when the Suggested filter is on: which rankers surfaced this candidate -- 'recsys' (the pipeline run), 'cart' (live cart signals), or both.
     */
    suggestion_sources?: Array<string> | null;
    /**
     * Suggestion reasons
     *
     * Up to two short advisor-facing reasons from the cart signals.
     */
    suggestion_reasons?: Array<string> | null;
    /**
     * Match Score
     *
     * RecSys's single 0-100 Match score for this pipeline (run rubric blended with cart evidence); the Suggested list is sorted by it. Only set on that list.
     */
    match_score?: number | null;
    /**
     * Match Score Breakdown
     *
     * How the Match score was made, for display on hover.
     */
    match_breakdown?: MatchScoreBreakdown | null;
    /**
     * Team Status
     *
     * Team status for the candidate
     */
    team_status?: PipelineCandidateTeamStatus | null;
    /**
     * Pipeline Status
     *
     * Pipeline status for the candidate
     */
    pipeline_status?: CandidatePipelineStatus | null;
    /**
     * Recommendation UUID
     *
     * use recommendation object instead
     *
     * @deprecated
     */
    recommendation_uuid?: string | null;
    /**
     * Recommendation
     *
     * Minimal recommendation object to hydrate form values
     */
    recommendation?: RecommendationDraftBasicResponse | null;
    /**
     * Introduction email type
     *
     * Type of introduction email
     */
    intro_email_type: IntroEmailType;
    /**
     * Draft assignee
     *
     * Person assigned to this draft recommendation
     */
    draft_assignee?: PersonBasicResponseWritable | null;
    /**
     * Recommender
     *
     * Person who created this recommendation
     */
    recommender?: PersonBasicResponseWritable | null;
    /**
     * Recommended on
     *
     * When the candidate was put on this pipeline (the recommendation's creation time). Null when the candidate has no recommendation here. Distinct from `created`, which is when the candidate's profile was created.
     */
    recommended_on?: string | null;
    /**
     * Autosuggested
     *
     * True when autosuggestion (the RecSys scheduler) created this candidate's recommendation rather than a person. The robot is not a person, so such rows have no meaningful `recommender`; this flag is what identifies their author.
     */
    autosuggested?: boolean;
    /**
     * Internal comments count
     *
     * Count of internal comments on this recommendation
     */
    internal_comments_count?: number | null;
    /**
     * Is accepting intros
     *
     * Whether the candidate is currently accepting introductions (not in a blackout period)
     */
    is_accepting_intros?: boolean | null;
    /**
     * Not accepting start date
     *
     * Start date of the current blackout period when candidate is not accepting introductions
     */
    not_accepting_start_date?: string | null;
    /**
     * Not accepting end date
     *
     * End date of the current blackout period when candidate is not accepting introductions
     */
    not_accepting_end_date?: string | null;
    /**
     * Off-limits for this pipeline team
     *
     * When this pipeline's search team is off-limits for the candidate (company or team member), the same payload shape as submission errors
     */
    pipeline_off_limits?: OffLimitsTeamSchema | null;
    /**
     * Uuid
     */
    uuid: string;
};

/**
 * PipelineCandidatesFilterPaginatedResponse
 */
export type PipelineCandidatesFilterPaginatedResponseWritable = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Results
     */
    results: Array<PipelineCandidatesFilterResponseWritable>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCountsResponse;
};

/**
 * PipelineCandidatesFilterResponse
 */
export type PipelineCandidatesFilterResponseWritable = {
    /**
     * Meta
     *
     * Metadata about the search result
     */
    search_metadata?: EsMeta | null;
    /**
     * Email
     *
     * Candidate's email address
     */
    email: string;
    /**
     * Name
     *
     * Candidate's full name
     */
    name?: string | null;
    /**
     * Image
     *
     * Candidate's profile image URL
     */
    image?: string | null;
    /**
     * Candidate UUID
     *
     * UUID of the candidate
     */
    uuid?: string;
    /**
     * Current Company
     *
     * Company of the candidate
     */
    current_company?: string | null;
    /**
     * Current Role
     *
     * Candidate's current role
     */
    current_role?: string | null;
    /**
     * Function Expertise
     *
     * Function expertise of the candidate
     */
    function_expertise?: Array<Function>;
    /**
     * Industry Expertise
     *
     * Industry expertise of the candidate
     */
    industry_expertise?: Array<Industry>;
    /**
     * Operating role interest
     *
     * Interest in getting operating roles
     */
    interest_role_operating?: InterestRole | null;
    /**
     * Operating highlights
     *
     * Operating highlights
     */
    operating_highlights?: Array<OperatingHighlights>;
    /**
     * Board/advisory role interest
     *
     * Interest in getting board or advisory roles
     */
    interest_role_board_advisory?: InterestRole | null;
    /**
     * Tier
     *
     * Candidate tier
     */
    tier?: CandidateTier | null;
    /**
     * Advisor
     *
     * The candidate's advisor, if present
     */
    advisor?: PersonBasicResponseWritable | null;
    /**
     * Recommendation status in relation to this company across all its teams
     */
    connection_status?: RecommendationStatusOutput | null;
    /**
     * Recommendation status on this pipeline
     */
    pipeline_status?: RecommendationStatusOutput | null;
    /**
     * Warm
     *
     * Whether this candidate is a warm connection
     */
    warm?: boolean;
    /**
     * Created
     *
     * Created at
     */
    created?: string | null;
    /**
     * Keywords
     *
     * Keywords for the candidate profile
     */
    keywords?: Array<string>;
    /**
     * Notes
     */
    notes?: string | null;
    /**
     * Underrepresented
     *
     * Is part of an underrepresented demographic
     */
    is_urg?: boolean;
    /**
     * Referrers
     *
     * Who referred this candidate
     */
    referrers?: Array<PersonBasicResponseWritable>;
};

/**
 * PipelineFullResponse
 */
export type PipelineFullResponseWritable = {
    /**
     * Is visible to client
     *
     * Hide this pipeline from its team members?
     */
    is_visible_to_team?: boolean | null;
    /**
     * Keywords
     *
     * List of keywords
     */
    keywords?: Array<string> | null;
    /**
     * Placement
     *
     * Candidate that got placed for this pipeline
     */
    placed_candidate?: CandidateBasicResponseWritable | null;
    /**
     * Pipeline type
     *
     * Pipeline type
     */
    pipeline_type?: PipelineType;
    /**
     * Active
     *
     * Is this pipeline marked as active?
     */
    active: boolean;
    /**
     * Attendees
     *
     * Number of attendees
     */
    attendees?: PipelineAttendees | null;
    /**
     * Company
     *
     * Name or nickname of the company for which this pipeline is open
     */
    company?: string | null;
    /**
     * Company size (annual revenue)
     *
     * Size of company for which this pipeline is open
     */
    company_size?: CompanySize | null;
    /**
     * Company type
     *
     * Type of company for which this pipeline is open
     */
    company_type?: CompanyType | null;
    /**
     * Created
     *
     * Date when this pipeline was created
     */
    created: string;
    /**
     * Creator
     *
     * Person who created this pipeline
     */
    creator?: PersonBasicResponseWritable | null;
    /**
     * Event date
     *
     * Date of the event
     */
    event_date?: string | null;
    /**
     * Event name
     *
     * Name of the event
     */
    event_name?: string | null;
    /**
     * Formatted name
     *
     * Readable pipeline name combining multiple properties into a more unique name
     */
    formatted_name: string;
    /**
     * Functional expertise
     *
     * List of functions the ideal candidate has experience in
     */
    function_expertise: Array<Function>;
    /**
     * Industry expertise
     *
     * List of industries the ideal candidate has experience in
     */
    industry_expertise: Array<Industry>;
    /**
     * Is URG
     *
     * Is the role specifically targeting under represented candidates?
     */
    is_urg: boolean;
    /**
     * Location
     *
     * The location specified for the pipeline role
     */
    location?: string | null;
    /**
     * Name
     *
     * Pipeline name
     */
    name: string;
    /**
     * Notes
     *
     * Any additional details to help us calibrate recommendations? (ex: target companies, benchmark profiles, etc.)
     */
    notes?: string | null;
    /**
     * Point of contact
     *
     * Point of contact person
     */
    point_of_contact?: PersonBasicResponseWritable | null;
    /**
     * Purpose
     *
     * For 'other' type pipelines, the purpose of the pipeline
     */
    purpose?: string | null;
    /**
     * Is proactive
     *
     * Is this pipeline proactive?
     */
    is_proactive: boolean;
    /**
     * Role type
     *
     * Role type
     */
    role_type: PipelineRoleType;
    /**
     * Role
     *
     * Pipeline name
     */
    role: string;
    /**
     * Team
     *
     * Team for which this pipeline was opened
     */
    team: TeamCompanyResponse;
    /**
     * Slug
     *
     * Slug uniquely identifying a pipeline
     */
    slug: string;
    /**
     * UUID
     *
     * UUID uniquely identifying a pipeline
     */
    uuid: string;
};

/**
 * PipelineSearchResponse
 */
export type PipelineSearchResponseWritable = {
    /**
     * Active
     */
    active: boolean;
    /**
     * Company
     */
    company?: string | null;
    /**
     * Function Expertise
     */
    function_expertise?: Array<string> | null;
    /**
     * ID
     */
    id: number;
    /**
     * Industry Expertise
     */
    industry_expertise?: Array<string> | null;
    /**
     * Keywords
     */
    keywords?: Array<string> | null;
    /**
     * Name
     */
    name: string;
    /**
     * Candidates
     *
     * This is just to store the `candidates` relationship under the `recommendations` key
     */
    candidates: Array<RecommendationCandidateBasicResponseWritable>;
    /**
     * Slug
     */
    slug: string;
    /**
     * Team
     *
     * This is just to store the `search_team` relationship under the `team` key
     */
    search_team: TeamWithMembersBasicResponseWritable;
    team_type: OrgType;
    /**
     * UUID
     */
    uuid: string;
};

/**
 * PostPage[ActivityFeedItem]
 */
export type PostPageActivityFeedItemWritable = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Results
     */
    results: Array<ActivityFeedItemWritable>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCounts;
};

/**
 * PostPersonClerkUserResponse
 */
export type PostPersonClerkUserResponseWritable = {
    /**
     * Person
     *
     * Person with the Clerk user
     */
    person: PersonBasicResponseWritable;
    /**
     * Clerk user ID
     *
     * Clerk user ID
     */
    clerk_user_id: string;
};

/**
 * PotentialGuestItem
 *
 * A single potential guest for event guest lists.
 *
 * Drawn from B4B users (non-advisory), candidates, or standalone event
 * guests from previous events. When a person is both a user and a candidate,
 * they appear once (as user) with optional candidate_uuid set.
 */
export type PotentialGuestItemWritable = {
    /**
     * Source Type
     *
     * Whether this guest is from users, candidates, or past event guests
     */
    source_type: PotentialGuestSourceType;
    /**
     * Identity UUID
     *
     * The human this row is about, or null for a standalone guest from a past event. One human is one row; this is the key to select and de-duplicate by
     */
    identity_uuid?: string | null;
    /**
     * Person UUID
     *
     * Person UUID when source is user (or when candidate is also a user)
     */
    person_uuid?: string | null;
    /**
     * Candidate UUID
     *
     * Candidate UUID when source is candidate
     */
    candidate_uuid?: string | null;
    /**
     * Is Executive
     *
     * True when this candidate is an activated executive user (ExecutiveSettings exists on the linked person, so person_uuid resolves to a real user page). False for plain candidates and standalone event guests.
     */
    is_executive?: boolean;
    /**
     * Name
     *
     * Display name
     */
    name?: string | null;
    /**
     * Role
     *
     * Role or title
     */
    role?: string | null;
    /**
     * Company
     *
     * Company name
     */
    company?: string | null;
    /**
     * Email
     *
     * Email address
     */
    email?: string | null;
    /**
     * Created
     *
     * When the user, candidate, or event guest record was created
     */
    created?: string | null;
    /**
     * Last Event Date
     *
     * Date of the most recent past event this guest attended (status=going)
     */
    last_event_date?: string | null;
    /**
     * Events Attended Count
     *
     * Number of past events this guest attended
     */
    events_attended_count?: number;
    /**
     * Events Invited Count
     *
     * Number of events this guest was invited to (on guest list)
     */
    events_invited_count?: number;
    /**
     * Tier
     *
     * Candidate tier (when source is user with candidate or candidate)
     */
    tier?: CandidateTier | null;
    /**
     * Candidate Type
     *
     * Candidate's Banff client type (tier 1 only)
     */
    candidate_type?: ClientType | null;
    /**
     * Pathway
     *
     * Candidate's Banff pathway (tier 1 only)
     */
    pathway?: CandidatePathway | null;
    /**
     * Industry
     *
     * Industry expertise (when source is user with candidate or candidate)
     */
    industry?: Array<Industry> | null;
    /**
     * Function
     *
     * Function expertise (when source is user with candidate or candidate)
     */
    function?: Array<Function> | null;
    /**
     * Operating Highlights
     *
     * Operating highlights (when source is user with candidate or candidate)
     */
    operating_highlights?: Array<OperatingHighlights> | null;
    /**
     * Interest Role Operating
     *
     * Interest role operating (when source is user with candidate or candidate)
     */
    interest_role_operating?: InterestRole | null;
    /**
     * Interest Role Board
     *
     * Interest role board/advisory (user with candidate or candidate)
     */
    interest_role_board?: InterestRole | null;
    /**
     * Keywords
     *
     * Keywords (when source is user with candidate or candidate)
     */
    keywords?: Array<string> | null;
    /**
     * Underrepresented
     *
     * Whether from underrepresented demographic (candidate diverse flag)
     */
    underrepresented?: boolean | null;
    /**
     * Warm
     *
     * Whether this candidate is a warm connection (tier-based; candidates only)
     */
    warm?: boolean | null;
    /**
     * Invoice Status
     *
     * HubSpot invoice status when the guest is a linked candidate
     */
    invoice_status?: HubSpotInvoiceStatus | null;
    /**
     * Referrers
     *
     * Persons who referred this candidate (when source_type is candidate)
     */
    referrers?: Array<PersonBasicResponseWritable> | null;
    /**
     * Advisor
     *
     * Advisor for this guest (candidates only; user/event_guest have no advisor)
     */
    advisor?: PersonBasicResponseWritable | null;
    /**
     * Client Types
     *
     * Server-computed client-type classification, mirroring the users-table company-type taxonomy. Enterprise roles (search, investor, corporate, advisory) win over candidate linkage; a candidate-linked guest with no enterprise role is executive; a guest with neither is unclassified (empty list).
     */
    client_types?: Array<EventGuestClientType>;
};

/**
 * PotentialGuestsSearchResponse
 *
 * Paginated response for potential guests search with optional aggregations.
 */
export type PotentialGuestsSearchResponseWritable = {
    /**
     * Results
     *
     * List of potential guests
     */
    results: Array<PotentialGuestItemWritable>;
    /**
     * Page
     *
     * Current page number
     */
    page: number;
    /**
     * Page Count
     *
     * Total number of pages
     */
    page_count: number;
    /**
     * Size
     *
     * Page size
     */
    size: number;
    /**
     * Total Counts
     *
     * Total counts (e.g. all=total matching). all=-1 when include_total=false.
     */
    total_counts: {
        [key: string]: number;
    };
    /**
     * Aggregations
     *
     * Aggregation totals (only when include_aggregations=true)
     */
    aggregations?: PotentialGuestsAggregationsResponse | null;
};

/**
 * RecommendationBioResponse
 */
export type RecommendationBioResponseWritable = {
    /**
     * About
     *
     * Brief commentary about the candidate
     */
    about?: string | null;
    /**
     * Candidate
     *
     * Information about the candidate
     */
    candidate: CandidateShareResponse;
    /**
     * Highlights
     *
     * Selected candidate highlights
     */
    highlights: Array<string>;
    /**
     * Advisor
     *
     * Information about the advisor who shows up as the presenter
     */
    presenter?: PersonBasicResponseWritable | null;
};

/**
 * RecommendationCandidateBasicResponse
 */
export type RecommendationCandidateBasicResponseWritable = {
    /**
     * Candidate
     *
     * Information about the candidate
     */
    candidate: CandidateBasicResponseWritable;
};

/**
 * RecommendationCandidatePipelinesResponse
 */
export type RecommendationCandidatePipelinesResponseWritable = {
    /**
     * Candidate
     *
     * Information about the candidate
     */
    candidate: CandidateBasicResponseWritable;
    /**
     * Pipeline
     *
     * Pipeline to which this recommendation has been published (null means it's Trending)
     */
    pipeline?: PipelineBasicV2Response | null;
    /**
     * UUID
     *
     * UUID uniquely identifying a recommendation
     */
    uuid: string;
};

/**
 * RecommendationExternalFullResponse
 */
export type RecommendationExternalFullResponseWritable = {
    /**
     * Status
     *
     * Introduction request status
     */
    introduction_status?: IntroductionStatus | null;
    /**
     * About
     *
     * Brief commentary about the candidate
     */
    about?: string | null;
    /**
     * Candidate
     *
     * Information about the candidate
     */
    candidate: CandidateExternalFullResponse;
    /**
     * Company
     *
     * Recommendation belongs to this company
     */
    company: CompanyBasicResponse;
    /**
     * Highlights
     *
     * Selected candidate highlights
     */
    highlights: Array<string>;
    /**
     * Introduction request person
     *
     * Person who requested the introduction
     */
    introduction_request_person?: PersonBasicResponseWritable | null;
    /**
     * External person introduction info
     *
     * External person introduction info
     */
    external_introduction_request_person?: ExternalPerson | null;
    /**
     * Introduction email type
     *
     * Type of introduction email
     */
    intro_email_type: IntroEmailType;
    /**
     * Is liked?
     *
     * Does the user want more recommendations like this one?
     */
    is_liked?: boolean | null;
    /**
     * Pipeline
     *
     * Pipeline to which this recommendation has been published (null means it's Trending)
     */
    pipeline: PipelineBasicResponse | null;
    /**
     * Team
     *
     * Recommendation belongs to this team
     */
    team: TeamBasicResponse;
    /**
     * UUID
     *
     * UUID uniquely identifying a recommendation
     */
    uuid: string;
    /**
     * Draft mode
     *
     * Whether this recommendation is in draft mode
     */
    draft_mode?: RecommendationDraftMode | null;
    /**
     * Draft assignee
     *
     * Person assigned to this draft recommendation
     */
    draft_assignee?: PersonBasicResponseWritable | null;
};

/**
 * RecommendationIntroductionResponse
 */
export type RecommendationIntroductionResponseWritable = {
    /**
     * Advisor
     *
     * Information about the advisor who responded to this introduction request
     */
    advisor?: PersonBasicResponseWritable | null;
    /**
     * Date
     *
     * Date when an introduction was requested or rejected
     */
    date?: string | null;
    /**
     * Status context
     *
     * Additional context about the introduction status
     */
    status_context?: IntroductionStatusContext | null;
    /**
     * Resolved date
     *
     * Date when an advisor updated the introduction request status
     */
    resolved_date?: string | null;
    /**
     * Person
     *
     * Information about the person who requested an introduction with this recommendation
     */
    person?: PersonBasicResponseWritable | null;
    /**
     * Status
     *
     * Introduction request status
     */
    introduction_status?: IntroductionStatus | null;
    /**
     * Already known
     *
     * Whether this recommendation was already known to the person reviewing it
     */
    already_known?: boolean | null;
    /**
     * Is liked?
     *
     * Does the user want more recommendations like this one?
     */
    is_liked?: boolean | null;
    /**
     * UUID
     *
     * UUID uniquely identifying a recommendation
     */
    uuid: string;
};

/**
 * RecommendationPreviewResponse
 *
 * Preview/guest version of a recommendation with limited fields for unauthenticated access.
 */
export type RecommendationPreviewResponseWritable = {
    /**
     * Candidate
     *
     * Basic candidate information
     */
    candidate: CandidateBasicResponseWritable;
    /**
     * UUID
     *
     * UUID uniquely identifying a recommendation
     */
    uuid: string;
};

/**
 * RecommendationsHistoryRecommendation
 */
export type RecommendationsHistoryRecommendationWritable = {
    /**
     * Advisor
     *
     * Information about the advisor who responded to this introduction request
     */
    advisor?: PersonBasicResponseWritable | null;
    /**
     * Date
     *
     * Date when an introduction was requested
     */
    date?: string | null;
    /**
     * Status context
     *
     * Additional context about the introduction status
     */
    status_context?: IntroductionStatusContext | null;
    /**
     * Resolved date
     *
     * Date when an advisor updated the introduction request status
     */
    resolved_date?: string | null;
    /**
     * Person
     *
     * Information about the person who requested an introduction with this recommendation
     */
    person?: PersonBasicResponseWritable | null;
    /**
     * Status
     *
     * Introduction request status
     */
    status?: IntroductionStatus | null;
    /**
     * Type
     *
     * Defines in which context was this recommendation published.
     */
    type: IntroductionType;
    /**
     * Created
     *
     * Date when this recommendation was published to the team
     */
    created: string;
    /**
     * Pipeline
     *
     * Information about pipeline where this candidate was submitted
     */
    pipeline?: PipelineBasicResponse | null;
    /**
     * Team
     *
     * Team which received the recommendation
     */
    team: TeamBasicResponse;
};

/**
 * RecommendationsHistoryResponse
 */
export type RecommendationsHistoryResponseWritable = {
    /**
     * Candidate
     *
     * Information about the candidate for which we're viewing recommendations history
     */
    candidate: CandidateBasicResponseWritable;
    /**
     * Recommendations
     *
     * List of recommendation objects for the recommendations history
     */
    recommendations: Array<RecommendationsHistoryRecommendationWritable>;
};

/**
 * RecsysCandidateOpportunitiesResponse
 */
export type RecsysCandidateOpportunitiesResponseWritable = {
    /**
     * Total
     *
     * Opportunities in the exec's whole queue
     */
    total?: number;
    /**
     * Already Recommended Count
     *
     * How many of the queue's pipelines the exec is already on (across the whole queue, not just this page)
     */
    already_recommended_count?: number;
    /**
     * Items
     *
     * Hydrated opportunities for this page
     */
    items?: Array<RecsysCandidateOpportunityItemWritable>;
};

/**
 * RecsysCandidateOpportunityItem
 */
export type RecsysCandidateOpportunityItemWritable = {
    /**
     * Hydrated Contender pipeline
     */
    pipeline: RecsysOpportunityPipeline;
    /**
     * Rank
     *
     * Position in the exec's whole ranked queue (1 = best fit), absolute across pages
     */
    rank: number;
    /**
     * Fit Score
     *
     * RecSys rubric fit score (0–100). Null when the pair hasn't been scored yet — those rank last
     */
    fit_score?: number | null;
    /**
     * Which RecSys feed(s) surfaced this opportunity
     */
    provenance?: RecsysOpportunityProvenance;
    /**
     * Recommendation Id
     *
     * RecSys recommendation id — pass to the feedback endpoint to record Good/Bad. Null for opportunities that came from the candidate-side sweep rather than a pipeline's own run
     */
    recommendation_id?: string | null;
    /**
     * Rationale
     *
     * Why this exec fits this search: {headline, bullets, caution, sources} (object), or a raw string. Present when RecSys has already written one; null otherwise — fetch it from the rationale endpoint, which generates on demand
     */
    rationale?: unknown | null;
    /**
     * Has Rationale
     *
     * RecSys already has a written 'why' for this pair — so `rationale` above is populated. When false, generating one costs a model call
     */
    has_rationale?: boolean;
    /**
     * Reviewed
     *
     * An advisor has already acted on this pair in RecSys
     */
    reviewed?: boolean;
    /**
     * Assessment
     *
     * The advisor's verdict on this pair, e.g. 'bad_match'; null when they have only marked it reviewed
     */
    assessment?: string | null;
    /**
     * Warm
     *
     * This exec is warm to the pipeline's search team — an exec/former-exec client, a recent advisor-network profile, or a facilitated intro with this team
     */
    warm?: boolean;
    /**
     * Most significant thing that has happened between this exec and the pipeline's team, with its date. Null means no team activity
     */
    last_action?: PipelineCandidateTeamStatus | null;
    /**
     * Already Recommended
     *
     * The exec is already on this pipeline in Contender (disable quick add)
     */
    already_recommended?: boolean;
    /**
     * Recommendation Uuid
     *
     * Contender recommendation uuid when the exec is already on this pipeline; null otherwise
     */
    recommendation_uuid?: string | null;
    /**
     * Current status of the existing recommendation; null when the exec is not on the pipeline
     */
    pipeline_status?: CandidatePipelineStatus | null;
    /**
     * Person assigned to the existing draft recommendation
     */
    draft_assignee?: PersonBasicResponseWritable | null;
};

/**
 * RecsysPipelineRecommendationItem
 */
export type RecsysPipelineRecommendationItemWritable = {
    /**
     * Hydrated Contender candidate
     */
    candidate: RecsysRecommendationCandidate;
    /**
     * Recommendation Id
     *
     * RecSys recommendation id — pass to the feedback endpoint to record Good/Bad on this recommendation
     */
    recommendation_id?: string | null;
    /**
     * Rank
     *
     * RecSys rank within the run (1 = best; global across tiers)
     */
    rank?: number | null;
    /**
     * Tier
     *
     * RecSys tier group: 'tier1' | 'tier3'
     */
    tier?: string | null;
    /**
     * Score
     *
     * RecSys fit score (0–100)
     */
    score?: number | null;
    /**
     * Reasons
     *
     * Engine provenance notes (RRF / rerank)
     */
    reasons?: Array<string>;
    /**
     * Cautions
     *
     * Engine caution notes
     */
    cautions?: Array<string>;
    /**
     * Rationale
     *
     * Advisor-facing rationale: {headline, bullets, caution, sources} (object), or a raw string / null when absent
     */
    rationale?: unknown | null;
    /**
     * Already On Pipeline
     *
     * Candidate is already recommended on this pipeline (disable 'Add as draft')
     */
    already_on_pipeline?: boolean;
    /**
     * Recommendation Uuid
     *
     * Contender recommendation uuid when the candidate is already on this pipeline (drives the status/assignee dropdowns); null otherwise
     */
    recommendation_uuid?: string | null;
    /**
     * Current pipeline status of the existing recommendation; null when the candidate is not on the pipeline
     */
    pipeline_status?: CandidatePipelineStatus | null;
    /**
     * Person assigned to the existing draft recommendation
     */
    draft_assignee?: PersonBasicResponseWritable | null;
};

/**
 * RecsysPipelineRecommendationsResponse
 */
export type RecsysPipelineRecommendationsResponseWritable = {
    /**
     * Total
     *
     * Total candidate recommendations in the RecSys run
     */
    total?: number;
    /**
     * Computed At
     *
     * When the RecSys run was computed (ISO 8601)
     */
    computed_at?: string | null;
    /**
     * Algorithm Version
     *
     * RecSys algorithm version that produced the run
     */
    algorithm_version?: string | null;
    /**
     * Run Id
     *
     * RecSys run id
     */
    run_id?: string | null;
    /**
     * Already On Pipeline Count
     *
     * How many of the run's candidates are already recommended on this pipeline (across the whole run, not just this page)
     */
    already_on_pipeline_count?: number;
    /**
     * Items
     *
     * Hydrated recommendations for this page
     */
    items?: Array<RecsysPipelineRecommendationItemWritable>;
};

/**
 * ResolvePersonCandidateByEmailResponse
 */
export type ResolvePersonCandidateByEmailResponseWritable = {
    /**
     * Results
     */
    results: Array<ResolvePersonCandidateByEmailResultWritable>;
};

/**
 * ResolvePersonCandidateByEmailResult
 */
export type ResolvePersonCandidateByEmailResultWritable = {
    /**
     * Email
     *
     * Resolved input email (lowercased).
     */
    email: string;
    /**
     * Person Uuid
     *
     * UUID of an existing Person for this email (if present).
     */
    person_uuid?: string | null;
    /**
     * Candidate Uuid
     *
     * UUID of an existing ContenderCandidate for this email (if present).
     */
    candidate_uuid?: string | null;
    /**
     * Basic person fields for UI when a person resolves.
     */
    person?: PersonBasicResponseWritable | null;
    /**
     * Basic candidate fields for UI when a candidate resolves.
     */
    candidate?: CandidateBasicResponseWritable | null;
    /**
     * Person Active
     *
     * Whether the resolved person's login is active (false: archived).
     */
    person_active?: boolean | null;
    /**
     * Identity Uuid
     *
     * The human the email belongs to: the resolved person's identity, else the candidate's.
     */
    identity_uuid?: string | null;
};

/**
 * SubmissionCandidatePipelinesResponse
 */
export type SubmissionCandidatePipelinesResponseWritable = {
    /**
     * Recommendations
     *
     * List of recommendations in this submission
     */
    recommendations: Array<RecommendationCandidatePipelinesResponseWritable>;
    /**
     * UUID
     *
     * UUID uniquely identifying a submission
     */
    uuid: string;
};

/**
 * SubmissionCheckSharedResponse
 */
export type SubmissionCheckSharedResponseWritable = {
    /**
     * Candidate
     *
     * Information about the candidate for which we ran the shared check
     */
    candidate: CandidateExtendedResponseWritable;
    /**
     * UUID
     *
     * UUID uniquely identifying a submission candidate
     */
    uuid: string;
};

/**
 * SubmissionSearchFilterSortResponse
 */
export type SubmissionSearchFilterSortResponseWritable = {
    /**
     * Emails delivered
     *
     * Number of emails delivered in this submission
     */
    emails_delivered: number;
    /**
     * Emails opened
     *
     * Number of emails opened in this submission
     */
    emails_opened: number;
    /**
     * Advisor
     *
     * Advisor data
     */
    advisor?: PersonBasicEmailResponseWritable | null;
    /**
     * Candidate
     *
     * When defined, that means we're working with a Trending submission
     */
    candidate?: PersonBasicEmailResponseWritable | null;
    /**
     * Intro requests
     *
     * Number of introduction requests in this submission
     */
    introduction_request_count: number;
    /**
     * Last executed in CRON summary job
     *
     * Date when this biweekly submission was last executed
     */
    last_executed_in_summary_cron?: string | null;
    /**
     * Last modified
     *
     * Date when this submission was last modified
     */
    last_modified: string;
    /**
     * Name
     *
     * Submission name
     */
    name?: string | null;
    /**
     * Notification type
     *
     * Submission's notification type
     */
    notification_type?: RecommendationPublishedNotificationType | null;
    /**
     * Published on
     *
     * Date when this submission was published
     */
    published_on?: string | null;
    /**
     * Recommendations
     *
     * Number of recommendations in this submission
     */
    recommendations_count: number;
    /**
     * Status
     *
     * Submission status
     */
    status: SubmissionStatus;
    /**
     * Type
     *
     * Submission type
     */
    type?: SubmissionType | null;
    /**
     * UUID
     *
     * Submission UUID
     */
    uuid: string;
};

/**
 * SubmissionV3InternalEmailStatsResponse
 */
export type SubmissionV3InternalEmailStatsResponseWritable = {
    /**
     * Emails delivered
     *
     * Number of emails delivered in this submission
     */
    emails_delivered: number;
    /**
     * Emails opened
     *
     * Number of emails opened in this submission
     */
    emails_opened: number;
    /**
     * About
     *
     * Brief commentary about the candidate
     */
    about?: string | null;
    /**
     * Advisor
     *
     * Advisor whose identity individual (stream) trending emails are sent from
     */
    advisor?: PersonBasicEmailResponseWritable | null;
    /**
     * Candidate
     *
     * When defined, that means we're working with a Trending submission
     */
    candidate?: CandidateSearchResponseWritable | null;
    /**
     * Recommendations
     *
     * List of recommendations in this submission
     */
    recommendations: Array<SubmissionV3RecommendationResponseWritable>;
    /**
     * Highlights
     *
     * List of highlights to show for the candidate
     */
    highlights_with_selected: Array<HighlightWithSelectedResponse>;
    /**
     * Last executed in CRON summary job
     *
     * Date when this biweekly submission was last executed
     */
    last_executed_in_summary_cron?: string | null;
    /**
     * Notification type
     *
     * Strategy used to notify teams they have new candidates to review
     */
    notification_type: RecommendationPublishedNotificationType;
    /**
     * Publish email body
     *
     * Customized publish email body text
     */
    publish_email_body?: string | null;
    /**
     * Publish email subject
     *
     * Customized publish email subject line
     */
    publish_email_subject?: string | null;
    /**
     * Published on
     *
     * Date when this submission was or will be published
     */
    published_on?: string | null;
    /**
     * Status
     *
     * Submission status
     */
    status: SubmissionStatus;
    /**
     * Type
     *
     * Submission type
     */
    type?: SubmissionType | null;
    /**
     * UUID
     *
     * UUID uniquely identifying a submission
     */
    uuid: string;
};

/**
 * SubmissionV3InternalResponse
 */
export type SubmissionV3InternalResponseWritable = {
    /**
     * About
     *
     * Brief commentary about the candidate
     */
    about?: string | null;
    /**
     * Advisor
     *
     * Advisor whose identity individual (stream) trending emails are sent from
     */
    advisor?: PersonBasicEmailResponseWritable | null;
    /**
     * Candidate
     *
     * When defined, that means we're working with a Trending submission
     */
    candidate?: CandidateSearchResponseWritable | null;
    /**
     * Recommendations
     *
     * List of recommendations in this submission
     */
    recommendations: Array<SubmissionV3RecommendationResponseWritable>;
    /**
     * Highlights
     *
     * List of highlights to show for the candidate
     */
    highlights_with_selected: Array<HighlightWithSelectedResponse>;
    /**
     * Last executed in CRON summary job
     *
     * Date when this biweekly submission was last executed
     */
    last_executed_in_summary_cron?: string | null;
    /**
     * Notification type
     *
     * Strategy used to notify teams they have new candidates to review
     */
    notification_type: RecommendationPublishedNotificationType;
    /**
     * Publish email body
     *
     * Customized publish email body text
     */
    publish_email_body?: string | null;
    /**
     * Publish email subject
     *
     * Customized publish email subject line
     */
    publish_email_subject?: string | null;
    /**
     * Published on
     *
     * Date when this submission was or will be published
     */
    published_on?: string | null;
    /**
     * Status
     *
     * Submission status
     */
    status: SubmissionStatus;
    /**
     * Type
     *
     * Submission type
     */
    type?: SubmissionType | null;
    /**
     * UUID
     *
     * UUID uniquely identifying a submission
     */
    uuid: string;
};

/**
 * SubmissionV3RecommendationResponse
 */
export type SubmissionV3RecommendationResponseWritable = {
    /**
     * Candidate
     *
     * Candidate information
     */
    candidate: CandidateSearchResponseWritable;
    /**
     * Team
     *
     * Team information
     */
    team: TeamsSearchTeamResponse;
    /**
     * UUID
     *
     * Recommendation UUID
     */
    uuid: string;
    /**
     * Recommendation published on
     */
    published_on?: string | null;
    /**
     * Recommendation status
     *
     * Recommendation status
     */
    status: RecommendationStatusOutput;
};

/**
 * SubmissionsSearchFilterSortPaginatedResponse
 */
export type SubmissionsSearchFilterSortPaginatedResponseWritable = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Results
     */
    results: Array<SubmissionSearchFilterSortResponseWritable>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCountsResponse;
    /**
     * Search Id
     */
    search_id?: string | null;
};

/**
 * TaskBasicResponse
 */
export type TaskBasicResponseWritable = {
    /**
     * Candidate
     */
    assignee: PersonBasicEmailResponseWritable;
    /**
     * Candidate
     */
    candidate: CandidateBasicResponseWritable;
    /**
     * Created date
     */
    created: string;
    /**
     * Candidate
     */
    creator: PersonBasicEmailResponseWritable;
    /**
     * Completion date
     */
    completion_date?: string | null;
    /**
     * Description
     */
    description?: string | null;
    /**
     * Due date
     */
    due_date?: string | null;
    /**
     * Task ID
     */
    id: string;
    /**
     * Is completed?
     */
    is_completed: boolean;
    /**
     * Last modified
     */
    last_modified: string;
    /**
     * Module
     */
    task_module: TaskModuleBasicResponse | null;
    /**
     * Title
     */
    title: string;
};

/**
 * TaskModuleResponse
 */
export type TaskModuleResponseWritable = {
    /**
     * Candidate
     */
    assignee: PersonBasicEmailResponseWritable;
    /**
     * Candidate
     */
    candidate: CandidateBasicResponseWritable;
    /**
     * Created date
     */
    created: string;
    /**
     * Candidate
     */
    creator: PersonBasicEmailResponseWritable;
    /**
     * Description
     */
    description?: string | null;
    /**
     * End date
     *
     * When does this task module end?
     */
    due_date: string;
    /**
     * Module ID
     */
    id: string;
    /**
     * Last modified
     */
    last_modified: string;
    /**
     * Start date
     *
     * When does this task module start?
     */
    start_date: string;
    /**
     * Status
     *
     * Task module status
     */
    status: TaskModuleStatus;
    /**
     * Tasks
     *
     * List of tasks associated with this module
     */
    tasks?: Array<TaskBasicResponseWritable> | null;
    /**
     * Type
     *
     * Type of task module
     */
    type: TaskModuleType;
};

/**
 * TasksTableResponse
 */
export type TasksTableResponseWritable = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Results
     */
    results: Array<TaskBasicResponseWritable>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCountsResponse;
    aggregations: TasksTableFilterAggregationsResponse;
};

/**
 * TeamCandidatesFilterResponse
 */
export type TeamCandidatesFilterResponseWritable = {
    aggregations: AppSchemasEsCandidateSchemaElasticsearchAggregationsResponse;
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Results
     */
    results: Array<EsCandidateIndexResultWritable>;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCountsResponse;
};

/**
 * TeamPipelineCandidatesResponse
 */
export type TeamPipelineCandidatesResponseWritable = {
    /**
     * Search ID
     *
     * Search session ID
     */
    search_id?: string | null;
    /**
     * Pipeline
     *
     * Information about the pipeline
     */
    pipeline: PipelineFullResponseWritable;
    /**
     * Recommendations
     *
     * List of recommendations for this pipeline sorted by review status
     */
    recommendations: Array<CandidateItemWritable>;
};

/**
 * TeamPipelineCandidatesSearchFilterSortResponse
 */
export type TeamPipelineCandidatesSearchFilterSortResponseWritable = {
    /**
     * Search ID
     *
     * Search session ID
     */
    search_id?: string | null;
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    aggregations: TeamPipelineCandidatesSearchAggregations;
    /**
     * Results
     */
    results: Array<PipelineCandidateSearchFilterSortResponseWritable>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCountsResponse;
    /**
     * Suggestion Basis
     *
     * Only when the Suggested filter is on: what the list is sorted by. 'ai_evaluation_and_contender_signals' when the pipeline has a RecSys run, 'contender_signals' when it does not, 'ai_evaluation' when every pick is switched off (the RecSys run alone).
     */
    suggestion_basis?: string | null;
    /**
     * Suggestion Picks
     *
     * Only when the Suggested filter is on: the candidates on the pipeline that can steer the list (published, scheduled, ready for review or approved), by name, each marked with whether it is steering.
     */
    suggestion_picks?: Array<SuggestionPick> | null;
};

/**
 * TeamTrendingCandidatesResponse
 */
export type TeamTrendingCandidatesResponseWritable = {
    /**
     * Search ID
     *
     * Search session ID
     */
    search_id?: string | null;
    /**
     * Recommendations
     *
     * List of trending recommendations for this team
     */
    recommendations: Array<CandidateItemWritable>;
};

/**
 * TeamWithMembersBasicResponse
 */
export type TeamWithMembersBasicResponseWritable = {
    /**
     * Members
     */
    members: Array<PersonBasicEmailResponseWritable>;
    /**
     * Active
     *
     * Inactive (archived) teams keep their data but are hidden from search and recommendations and closed to their members
     */
    active?: boolean;
    /**
     * Slug
     *
     * Slug uniquely identifying a team
     */
    slug: string;
    /**
     * Name
     *
     * Team name
     */
    name: string;
};

/**
 * TrendingCandidateResponse
 */
export type TrendingCandidateResponseWritable = {
    candidate: CandidateCompanyRoleResponseWritable;
    /**
     * Advisor
     *
     * Advisor basic info
     */
    advisor: PersonBasicResponseWritable;
    /**
     * Favorite count
     *
     * Number of users who favorite this candidate
     */
    favorite_count: number;
    /**
     * Intro facilitated count
     *
     * Candidate's facilitated count
     */
    introductions_facilitated_count: number;
    /**
     * Intro request count
     *
     * Candidate's intro requests count
     */
    introductions_requested_count: number;
    /**
     * Recommendation count
     *
     * Candidate's recommendation count
     */
    recommendation_count: number;
    /**
     * Rank
     *
     * Candidate's trending rank
     */
    rank: number;
};

/**
 * TrendingCandidatesPaginatedResponse
 */
export type TrendingCandidatesPaginatedResponseWritable = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Results
     */
    results: Array<TrendingCandidateResponseWritable>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCountsResponse;
};

/**
 * TrendingMessageResponse
 *
 * A message an advisor wrote for one candidate's Trending campaigns.
 * Campaigns that sent the same body text count as one message.
 */
export type TrendingMessageResponseWritable = {
    /**
     * Advisor
     *
     * Advisor who sent the message the last time it was used
     */
    advisor?: PersonBasicResponseWritable | null;
    /**
     * Body
     *
     * Email body text, as the advisor wrote it
     */
    body: string;
    /**
     * Last used on
     *
     * When a campaign last sent, or is scheduled to send, this message
     */
    last_used_on: string;
    /**
     * Subject
     *
     * Email subject line the last time the message was used
     */
    subject?: string | null;
    /**
     * Submission UUID
     *
     * Trending submission that last used this message
     */
    submission_uuid: string;
    /**
     * Times used
     *
     * Number of Trending campaigns that sent this message
     */
    times_used: number;
};

/**
 * UserDetailedResponse
 *
 * Detailed user response including company and role information
 */
export type UserDetailedResponseWritable = {
    /**
     * ID
     *
     * Same as uuid
     */
    id: string;
    /**
     * UUID
     *
     * UUID uniquely identifying a user
     */
    uuid: string;
    /**
     * Email
     *
     * User email address
     */
    email: string;
    /**
     * First Name
     *
     * User's first name
     */
    first_name?: string | null;
    /**
     * Last Name
     *
     * User's last name
     */
    last_name?: string | null;
    /**
     * Full Name
     *
     * User's full name
     */
    full_name?: string | null;
    /**
     * Company
     *
     * Company the user belongs to (includes payment status)
     */
    company?: CompanyExtendedResponse | null;
    /**
     * Created
     *
     * Timestamp when the user record was created
     */
    created: string;
    /**
     * First Login
     *
     * Timestamp when the user record was first logged in
     */
    first_login?: string | null;
    /**
     * Last Login
     *
     * Timestamp of user's last login
     */
    last_login?: string | null;
    /**
     * Active
     *
     * Whether the user is active
     */
    active: boolean;
    /**
     * Pending
     *
     * Whether the user's invite is still pending (no clerk_id)
     */
    pending: boolean;
    /**
     * Permit Preregistration Notifications
     *
     * Whether user can receive marketing emails before completing signup
     */
    permit_preregistration_notifications: boolean;
    /**
     * Notes External
     *
     * External notes for this user profile
     */
    notes_external?: string | null;
    /**
     * Notes Internal
     *
     * Internal notes for this user profile
     */
    notes_internal?: string | null;
    /**
     * LinkedIn URL
     *
     * LinkedIn URL for this user profile
     */
    linkedin_url?: string | null;
    /**
     * Wants Intros
     *
     * Whether this user currently wants intros
     */
    wants_intros?: boolean | null;
    /**
     * Wants Intro Followups
     *
     * Whether follow-up on intro requests is wanted internally
     */
    wants_intro_followups?: boolean | null;
    /**
     * Handle Intros As Exec
     *
     * Whether intro handling should be done as executive
     */
    handle_intros_as_exec?: boolean | null;
    /**
     * Teams Count
     *
     * Total number of teams the user belongs to
     */
    teams_count: number;
    /**
     * Teams
     *
     * List of teams the user belongs to
     */
    teams?: Array<UserTeamBasicResponse>;
    /**
     * Roles
     *
     * List of role objects the user has
     */
    roles?: Array<UserRoleResponse>;
    /**
     * Identity UUID
     *
     * UUID of the identity this login belongs to: the one row per real human that also holds the candidate, if there is one.
     */
    identity_uuid?: string | null;
    /**
     * Candidate UUID
     *
     * UUID of the candidate that shares this person's identity (executives and similar).
     */
    candidate_uuid?: string | null;
    /**
     * Candidate
     *
     * Basic profile of the candidate that shares this person's identity (executives and similar; same as candidate_uuid).
     */
    candidate?: CandidateBasicResponseWritable | null;
    /**
     * User type
     *
     * executive/admin/enterprise
     */
    user_type: UserType;
    /**
     * Search Metadata
     *
     * Search result metadata, including highlighted fields
     */
    search_metadata?: EsMeta | null;
};

/**
 * UsersPostPage[UserDetailedResponse]
 */
export type UsersPostPageUserDetailedResponseWritable = {
    /**
     * Page Count
     */
    page_count: number;
    /**
     * Page
     */
    page: number;
    /**
     * Search Id
     */
    search_id?: string | null;
    /**
     * Results
     */
    results: Array<UserDetailedResponseWritable>;
    /**
     * Size
     */
    size: number;
    total_counts: TotalCounts;
    aggregations?: UsersAggregationsResponse | null;
};

export type AppVersionData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/';
};

export type AppVersionResponses = {
    /**
     * Successful Response
     */
    200: unknown;
};

export type HealthCheckData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/health';
};

export type HealthCheckResponses = {
    /**
     * Successful Response
     */
    200: unknown;
};

export type GetVerifyEmailOneTimeCodeData = {
    body?: never;
    path?: never;
    query: {
        /**
         * Code
         */
        code: string;
    };
    url: '/email-response';
};

export type GetVerifyEmailOneTimeCodeErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetVerifyEmailOneTimeCodeError = GetVerifyEmailOneTimeCodeErrors[keyof GetVerifyEmailOneTimeCodeErrors];

export type GetVerifyEmailOneTimeCodeResponses = {
    /**
     * Successful Response
     */
    200: VerificationResponse;
};

export type GetVerifyEmailOneTimeCodeResponse = GetVerifyEmailOneTimeCodeResponses[keyof GetVerifyEmailOneTimeCodeResponses];

export type PostEmailResponseFeedbackData = {
    body?: never;
    path?: never;
    query: {
        /**
         * Code
         */
        code: string;
    };
    url: '/email-response/feedback';
};

export type PostEmailResponseFeedbackErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostEmailResponseFeedbackError = PostEmailResponseFeedbackErrors[keyof PostEmailResponseFeedbackErrors];

export type PostEmailResponseFeedbackResponses = {
    /**
     * Successful Response
     */
    200: FeedbackResponse;
};

export type PostEmailResponseFeedbackResponse = PostEmailResponseFeedbackResponses[keyof PostEmailResponseFeedbackResponses];

export type GetCountriesData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/countries';
};

export type GetCountriesResponses = {
    /**
     * Successful Response
     */
    200: CountriesResponse;
};

export type GetCountriesResponse = GetCountriesResponses[keyof GetCountriesResponses];

export type PostTokenData = {
    body: BodyPostToken;
    path?: never;
    query?: never;
    url: '/api/v1/token';
};

export type PostTokenErrors = {
    /**
     * Bad Request - Invalid Token
     */
    400: unknown;
    /**
     * Unauthorized - Expired Token
     */
    401: unknown;
    /**
     * Not Found - Deactivated User
     */
    404: unknown;
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostTokenError = PostTokenErrors[keyof PostTokenErrors];

export type PostTokenResponses = {
    /**
     * Successful Response
     */
    200: AccessToken;
};

export type PostTokenResponse = PostTokenResponses[keyof PostTokenResponses];

export type PosthogProxyData = {
    body?: never;
    path: {
        /**
         * Posthog Path
         */
        posthog_path: string;
    };
    query?: never;
    url: '/api/v1/ingest/{posthog_path}/';
};

export type PosthogProxyErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PosthogProxyError = PosthogProxyErrors[keyof PosthogProxyErrors];

export type PosthogProxyResponses = {
    /**
     * Successful Response
     */
    200: unknown;
};

export type PosthogJsData = {
    body?: never;
    path: {
        /**
         * Filename
         */
        filename: string;
    };
    query?: never;
    url: '/api/v1/ingest/static/{filename}';
};

export type PosthogJsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PosthogJsError = PosthogJsErrors[keyof PosthogJsErrors];

export type PosthogJsResponses = {
    /**
     * Successful Response
     */
    200: unknown;
};

export type PostCandidatesFilterData = {
    body: CandidateSearchData;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Page number
         */
        page?: number;
        /**
         * Size
         *
         * Page size
         */
        size?: number;
    };
    url: '/api/v1/candidates/filter';
};

export type PostCandidatesFilterErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostCandidatesFilterError = PostCandidatesFilterErrors[keyof PostCandidatesFilterErrors];

export type PostCandidatesFilterResponses = {
    /**
     * Successful Response
     */
    200: PageCandidatesTableResponse;
};

export type PostCandidatesFilterResponse = PostCandidatesFilterResponses[keyof PostCandidatesFilterResponses];

export type GetCandidatesFindData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Client
         *
         * Return only Banff clients?
         */
        is_client?: boolean | null;
        /**
         * Referred
         *
         * Return only referred clients?
         */
        is_referred?: boolean | null;
        /**
         * Include inactive
         *
         * Also return inactive candidates. Pickers leave this off; a filter over existing records turns it on. Merged candidates are never returned.
         */
        include_inactive?: boolean;
        /**
         * Page
         */
        page?: number;
        /**
         * Size
         */
        size?: number;
        /**
         * Order
         */
        order?: 'asc' | 'desc';
        /**
         * Phrase
         */
        phrase?: string | null;
        /**
         * Row
         */
        row?: string | null;
        /**
         * Search Id
         */
        search_id?: string | null;
        /**
         * Search By Name Only
         */
        search_by_name_only?: boolean;
    };
    url: '/api/v1/candidates/find';
};

export type GetCandidatesFindErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetCandidatesFindError = GetCandidatesFindErrors[keyof GetCandidatesFindErrors];

export type GetCandidatesFindResponses = {
    /**
     * Successful Response
     */
    200: PageCandidateSearchResponseWithLinkedPerson;
};

export type GetCandidatesFindResponse = GetCandidatesFindResponses[keyof GetCandidatesFindResponses];

export type PostCandidatesPerformanceInRedData = {
    body: CandidatePerformanceFilterSortPayload;
    path?: never;
    query: {
        /**
         * Intro check type
         *
         * Type of intros to check
         */
        intro_check_type: IntroCheckType;
    };
    url: '/api/v1/candidates/performance/in-red';
};

export type PostCandidatesPerformanceInRedErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostCandidatesPerformanceInRedError = PostCandidatesPerformanceInRedErrors[keyof PostCandidatesPerformanceInRedErrors];

export type PostCandidatesPerformanceInRedResponses = {
    /**
     * Successful Response
     */
    200: CandidatesPerformancePaginatedResponse;
};

export type PostCandidatesPerformanceInRedResponse = PostCandidatesPerformanceInRedResponses[keyof PostCandidatesPerformanceInRedResponses];

export type PostCandidatesPerformanceFilterData = {
    body: CandidatesCombinedPerformanceFilterSortPayload;
    path?: never;
    query?: {
        /**
         * Date From
         *
         * Filter stats from this date
         */
        date_from?: string | null;
        /**
         * Date To
         *
         * Filter stats to this date
         */
        date_to?: string | null;
    };
    url: '/api/v1/candidates/performance/filter';
};

export type PostCandidatesPerformanceFilterErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostCandidatesPerformanceFilterError = PostCandidatesPerformanceFilterErrors[keyof PostCandidatesPerformanceFilterErrors];

export type PostCandidatesPerformanceFilterResponses = {
    /**
     * Successful Response
     */
    200: CandidatesCombinedPerformancePaginatedResponse;
};

export type PostCandidatesPerformanceFilterResponse = PostCandidatesPerformanceFilterResponses[keyof PostCandidatesPerformanceFilterResponses];

export type GetCandidateCommentaryData = {
    body?: never;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
    };
    query?: never;
    url: '/api/v1/candidates/{candidate_uuid}/commentary';
};

export type GetCandidateCommentaryErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetCandidateCommentaryError = GetCandidateCommentaryErrors[keyof GetCandidateCommentaryErrors];

export type GetCandidateCommentaryResponses = {
    /**
     * Successful Response
     */
    200: CandidateCommentaryResponseSchema;
};

export type GetCandidateCommentaryResponse = GetCandidateCommentaryResponses[keyof GetCandidateCommentaryResponses];

export type PutCandidateCommentaryData = {
    body: CandidateCommentary;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
    };
    query?: never;
    url: '/api/v1/candidates/{candidate_uuid}/commentary';
};

export type PutCandidateCommentaryErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PutCandidateCommentaryError = PutCandidateCommentaryErrors[keyof PutCandidateCommentaryErrors];

export type PutCandidateCommentaryResponses = {
    /**
     * Successful Response
     */
    200: CandidateCommentaryResponseSchema;
};

export type PutCandidateCommentaryResponse = PutCandidateCommentaryResponses[keyof PutCandidateCommentaryResponses];

export type PostCandidateHighlightsData = {
    body?: never;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
    };
    query?: never;
    url: '/api/v1/candidates/{candidate_uuid}/highlights';
};

export type PostCandidateHighlightsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostCandidateHighlightsError = PostCandidateHighlightsErrors[keyof PostCandidateHighlightsErrors];

export type PostCandidateHighlightsResponses = {
    /**
     * Successful Response
     */
    200: HighlightGeneratorResponse;
};

export type PostCandidateHighlightsResponse = PostCandidateHighlightsResponses[keyof PostCandidateHighlightsResponses];

export type GetCandidateImageData = {
    body?: never;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
    };
    query?: {
        /**
         * Size
         */
        size?: number | null;
    };
    url: '/api/v1/candidates/{candidate_uuid}/image';
};

export type GetCandidateImageErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetCandidateImageError = GetCandidateImageErrors[keyof GetCandidateImageErrors];

export type GetCandidateImageResponses = {
    /**
     * Return a profile image.
     */
    200: unknown;
};

export type GetCandidateIntroductionsTeamHistoryData = {
    body?: never;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
        /**
         * Team Slug
         */
        team_slug: string;
    };
    query?: never;
    url: '/api/v1/candidates/{candidate_uuid}/introductions/{team_slug}/history';
};

export type GetCandidateIntroductionsTeamHistoryErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetCandidateIntroductionsTeamHistoryError = GetCandidateIntroductionsTeamHistoryErrors[keyof GetCandidateIntroductionsTeamHistoryErrors];

export type GetCandidateIntroductionsTeamHistoryResponses = {
    /**
     * Successful Response
     */
    200: RecommendationsHistoryResponse;
};

export type GetCandidateIntroductionsTeamHistoryResponse = GetCandidateIntroductionsTeamHistoryResponses[keyof GetCandidateIntroductionsTeamHistoryResponses];

export type GetCandidateIntroductionTemplatesData = {
    body?: never;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
    };
    query?: never;
    url: '/api/v1/candidates/{candidate_uuid}/introduction_templates';
};

export type GetCandidateIntroductionTemplatesErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetCandidateIntroductionTemplatesError = GetCandidateIntroductionTemplatesErrors[keyof GetCandidateIntroductionTemplatesErrors];

export type GetCandidateIntroductionTemplatesResponses = {
    /**
     * Response Get Candidate Introduction Templates
     *
     * Successful Response
     */
    200: Array<CandidateIntroductionTemplateResponse>;
};

export type GetCandidateIntroductionTemplatesResponse = GetCandidateIntroductionTemplatesResponses[keyof GetCandidateIntroductionTemplatesResponses];

export type GetCandidateOffLimitsCompaniesData = {
    body?: never;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
    };
    query?: never;
    url: '/api/v1/candidates/{candidate_uuid}/off_limits/companies';
};

export type GetCandidateOffLimitsCompaniesErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetCandidateOffLimitsCompaniesError = GetCandidateOffLimitsCompaniesErrors[keyof GetCandidateOffLimitsCompaniesErrors];

export type GetCandidateOffLimitsCompaniesResponses = {
    /**
     * Response Get Candidate Off Limits Companies
     *
     * Successful Response
     */
    200: Array<OffLimitsCompanyResponseSchema>;
};

export type GetCandidateOffLimitsCompaniesResponse = GetCandidateOffLimitsCompaniesResponses[keyof GetCandidateOffLimitsCompaniesResponses];

export type PutCandidateOffLimitsCompaniesData = {
    /**
     * Payload
     */
    body: Array<CandidateOffLimitsCompany>;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
    };
    query?: never;
    url: '/api/v1/candidates/{candidate_uuid}/off_limits/companies';
};

export type PutCandidateOffLimitsCompaniesErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PutCandidateOffLimitsCompaniesError = PutCandidateOffLimitsCompaniesErrors[keyof PutCandidateOffLimitsCompaniesErrors];

export type PutCandidateOffLimitsCompaniesResponses = {
    /**
     * Response Put Candidate Off Limits Companies
     *
     * Successful Response
     */
    200: Array<OffLimitsCompanyResponseSchema>;
};

export type PutCandidateOffLimitsCompaniesResponse = PutCandidateOffLimitsCompaniesResponses[keyof PutCandidateOffLimitsCompaniesResponses];

export type GetCandidateOffLimitsIdentitiesData = {
    body?: never;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
    };
    query?: never;
    url: '/api/v1/candidates/{candidate_uuid}/off_limits/identities';
};

export type GetCandidateOffLimitsIdentitiesErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetCandidateOffLimitsIdentitiesError = GetCandidateOffLimitsIdentitiesErrors[keyof GetCandidateOffLimitsIdentitiesErrors];

export type GetCandidateOffLimitsIdentitiesResponses = {
    /**
     * Response Get Candidate Off Limits Identities
     *
     * Successful Response
     */
    200: Array<OffLimitsIdentityResponseSchema>;
};

export type GetCandidateOffLimitsIdentitiesResponse = GetCandidateOffLimitsIdentitiesResponses[keyof GetCandidateOffLimitsIdentitiesResponses];

export type PutCandidateOffLimitsIdentitiesData = {
    /**
     * Payload
     */
    body: Array<CandidateOffLimitsIdentity>;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
    };
    query?: never;
    url: '/api/v1/candidates/{candidate_uuid}/off_limits/identities';
};

export type PutCandidateOffLimitsIdentitiesErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PutCandidateOffLimitsIdentitiesError = PutCandidateOffLimitsIdentitiesErrors[keyof PutCandidateOffLimitsIdentitiesErrors];

export type PutCandidateOffLimitsIdentitiesResponses = {
    /**
     * Response Put Candidate Off Limits Identities
     *
     * Successful Response
     */
    200: Array<OffLimitsIdentityResponseSchema>;
};

export type PutCandidateOffLimitsIdentitiesResponse = PutCandidateOffLimitsIdentitiesResponses[keyof PutCandidateOffLimitsIdentitiesResponses];

export type GetCandidateRecommendationsData = {
    body?: never;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
    };
    query?: {
        /**
         * Category
         *
         * Apply category. This should be merged into filter query param.
         *
         * @deprecated
         */
        category?: RecommendationsFilter | null;
        /**
         * Filter
         *
         * Apply filter
         */
        filter?: RecommendationsFilter | null;
        /**
         * Sort
         *
         * Sort by column
         */
        sort?: RecommendationsSorter | null;
        /**
         * Page
         */
        page?: number;
        /**
         * Size
         */
        size?: number;
        /**
         * Order
         */
        order?: 'asc' | 'desc';
        /**
         * Phrase
         */
        phrase?: string | null;
        /**
         * Row
         */
        row?: string | null;
        /**
         * Search Id
         */
        search_id?: string | null;
    };
    url: '/api/v1/candidates/{candidate_uuid}/recommendations';
};

export type GetCandidateRecommendationsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetCandidateRecommendationsError = GetCandidateRecommendationsErrors[keyof GetCandidateRecommendationsErrors];

export type GetCandidateRecommendationsResponses = {
    /**
     * Successful Response
     */
    200: PageCandidateRecommendationResponse;
};

export type GetCandidateRecommendationsResponse = GetCandidateRecommendationsResponses[keyof GetCandidateRecommendationsResponses];

export type GetCandidateRecommendationsCompareBackgroundData = {
    body?: never;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
    };
    query: {
        /**
         * Peer Uuid
         */
        peer_uuid: string;
    };
    url: '/api/v1/candidates/{candidate_uuid}/recommendations/compare/background';
};

export type GetCandidateRecommendationsCompareBackgroundErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetCandidateRecommendationsCompareBackgroundError = GetCandidateRecommendationsCompareBackgroundErrors[keyof GetCandidateRecommendationsCompareBackgroundErrors];

export type GetCandidateRecommendationsCompareBackgroundResponses = {
    /**
     * Successful Response
     */
    200: CandidatePeerBackgroundComparisonResponse;
};

export type GetCandidateRecommendationsCompareBackgroundResponse = GetCandidateRecommendationsCompareBackgroundResponses[keyof GetCandidateRecommendationsCompareBackgroundResponses];

export type GetCandidateRecommendationsComparePerformanceData = {
    body?: never;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
    };
    query: {
        /**
         * Date From
         *
         * Filter stats from this date
         */
        date_from?: string | null;
        /**
         * Date To
         *
         * Filter stats to this date
         */
        date_to?: string | null;
        /**
         * Peer Uuid
         */
        peer_uuid: string;
    };
    url: '/api/v1/candidates/{candidate_uuid}/recommendations/compare/performance';
};

export type GetCandidateRecommendationsComparePerformanceErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetCandidateRecommendationsComparePerformanceError = GetCandidateRecommendationsComparePerformanceErrors[keyof GetCandidateRecommendationsComparePerformanceErrors];

export type GetCandidateRecommendationsComparePerformanceResponses = {
    /**
     * Successful Response
     */
    200: CandidatePeerPerformanceComparisonResponse;
};

export type GetCandidateRecommendationsComparePerformanceResponse = GetCandidateRecommendationsComparePerformanceResponses[keyof GetCandidateRecommendationsComparePerformanceResponses];

export type GetCandidateRecommendationsCompareRecommendationsData = {
    body?: never;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
    };
    query: {
        /**
         * Company Type
         *
         * Filter by company types
         */
        company_type?: Array<OrgType> | null;
        /**
         * Filter Presets
         *
         * Show similarities or differences
         */
        filter_presets?: FilterPresets | null;
        /**
         * Recommendation Type
         *
         * Filter by recommendation type
         */
        recommendation_type?: RecommendationType | null;
        /**
         * Response Type
         *
         * Filter by response types
         */
        response_type?: Array<ResponseType> | null;
        /**
         * Date From
         *
         * Filter stats from this date
         */
        date_from?: string | null;
        /**
         * Date To
         *
         * Filter stats to this date
         */
        date_to?: string | null;
        /**
         * Peer Uuid
         */
        peer_uuid: string;
    };
    url: '/api/v1/candidates/{candidate_uuid}/recommendations/compare/recommendations';
};

export type GetCandidateRecommendationsCompareRecommendationsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetCandidateRecommendationsCompareRecommendationsError = GetCandidateRecommendationsCompareRecommendationsErrors[keyof GetCandidateRecommendationsCompareRecommendationsErrors];

export type GetCandidateRecommendationsCompareRecommendationsResponses = {
    /**
     * Successful Response
     */
    200: CandidatePeerRecommendationComparisonResponse;
};

export type GetCandidateRecommendationsCompareRecommendationsResponse = GetCandidateRecommendationsCompareRecommendationsResponses[keyof GetCandidateRecommendationsCompareRecommendationsResponses];

export type GetCandidateRecommendationsComparePipelinesData = {
    body?: never;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
    };
    query: {
        /**
         * Company Type
         *
         * Filter by company types
         */
        company_type?: Array<OrgType> | null;
        /**
         * Filter Presets
         *
         * Show similarities or differences
         */
        filter_presets?: FilterPresets | null;
        /**
         * Response Type
         *
         * Filter by response types
         */
        response_type?: Array<ResponseType> | null;
        /**
         * Date From
         *
         * Filter stats from this date
         */
        date_from?: string | null;
        /**
         * Date To
         *
         * Filter stats to this date
         */
        date_to?: string | null;
        /**
         * Peer Uuid
         */
        peer_uuid: string;
    };
    url: '/api/v1/candidates/{candidate_uuid}/recommendations/compare/pipelines';
};

export type GetCandidateRecommendationsComparePipelinesErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetCandidateRecommendationsComparePipelinesError = GetCandidateRecommendationsComparePipelinesErrors[keyof GetCandidateRecommendationsComparePipelinesErrors];

export type GetCandidateRecommendationsComparePipelinesResponses = {
    /**
     * Successful Response
     */
    200: CandidatePeerRecommendationComparisonResponse;
};

export type GetCandidateRecommendationsComparePipelinesResponse = GetCandidateRecommendationsComparePipelinesResponses[keyof GetCandidateRecommendationsComparePipelinesResponses];

export type GetCandidateResumeData = {
    body?: never;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
    };
    query?: never;
    url: '/api/v1/candidates/{candidate_uuid}/resume';
};

export type GetCandidateResumeErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetCandidateResumeError = GetCandidateResumeErrors[keyof GetCandidateResumeErrors];

export type GetCandidateResumeResponses = {
    /**
     * Successful Response
     */
    200: AdminResumeView;
};

export type GetCandidateResumeResponse = GetCandidateResumeResponses[keyof GetCandidateResumeResponses];

export type PatchCandidateData = {
    body: CandidateUpdateActive;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
    };
    query?: never;
    url: '/api/v1/candidates/{candidate_uuid}';
};

export type PatchCandidateErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PatchCandidateError = PatchCandidateErrors[keyof PatchCandidateErrors];

export type PatchCandidateResponses = {
    /**
     * Successful Response
     */
    200: unknown;
};

export type GetCompaniesData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Filter
         *
         * Apply filter
         */
        filter?: CompaniesFilter | null;
        /**
         * Type
         */
        type?: Array<OrgType> | null;
        /**
         * Sort
         *
         * Sort by column
         */
        sort?: CompaniesSorter | null;
        /**
         * Page
         */
        page?: number;
        /**
         * Size
         */
        size?: number;
        /**
         * Order
         */
        order?: 'asc' | 'desc';
        /**
         * Phrase
         */
        phrase?: string | null;
        /**
         * Row
         */
        row?: string | null;
        /**
         * Search Id
         */
        search_id?: string | null;
    };
    url: '/api/v1/companies';
};

export type GetCompaniesErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetCompaniesError = GetCompaniesErrors[keyof GetCompaniesErrors];

export type GetCompaniesResponses = {
    /**
     * Successful Response
     */
    200: PageCompanyTableResponse;
};

export type GetCompaniesResponse = GetCompaniesResponses[keyof GetCompaniesResponses];

export type PostCompaniesData = {
    body: CompanyCreateV1;
    path?: never;
    query?: never;
    url: '/api/v1/companies';
};

export type PostCompaniesErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostCompaniesError = PostCompaniesErrors[keyof PostCompaniesErrors];

export type PostCompaniesResponses = {
    /**
     * Successful Response
     */
    200: CustomerCompany;
};

export type PostCompaniesResponse = PostCompaniesResponses[keyof PostCompaniesResponses];

export type PutCompaniesData = {
    body: CustomerCompany;
    path?: never;
    query?: never;
    url: '/api/v1/companies';
};

export type PutCompaniesErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PutCompaniesError = PutCompaniesErrors[keyof PutCompaniesErrors];

export type PutCompaniesResponses = {
    /**
     * Successful Response
     */
    200: CustomerCompany;
};

export type PutCompaniesResponse = PutCompaniesResponses[keyof PutCompaniesResponses];

export type GetCompanyByNameData = {
    body?: never;
    path?: never;
    query: {
        /**
         * Name
         *
         * Company name to search for
         */
        name: string;
        /**
         * Case Insensitive
         *
         * Whether to perform case-insensitive search
         */
        case_insensitive?: boolean;
    };
    url: '/api/v1/companies/by-name';
};

export type GetCompanyByNameErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetCompanyByNameError = GetCompanyByNameErrors[keyof GetCompanyByNameErrors];

export type GetCompanyByNameResponses = {
    /**
     * Successful Response
     */
    200: CompanyExtendedResponseWithFeatures;
};

export type GetCompanyByNameResponse = GetCompanyByNameResponses[keyof GetCompanyByNameResponses];

export type GetCompaniesFindData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * ID
         *
         * List of IDs to filter results by
         */
        id?: Array<number> | null;
        /**
         * Page
         */
        page?: number;
        /**
         * Size
         */
        size?: number;
        /**
         * Order
         */
        order?: 'asc' | 'desc';
        /**
         * Phrase
         */
        phrase?: string | null;
        /**
         * Row
         */
        row?: string | null;
        /**
         * Search Id
         */
        search_id?: string | null;
    };
    url: '/api/v1/companies/find';
};

export type GetCompaniesFindErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetCompaniesFindError = GetCompaniesFindErrors[keyof GetCompaniesFindErrors];

export type GetCompaniesFindResponses = {
    /**
     * Successful Response
     */
    200: PageCompanyTableResponse;
};

export type GetCompaniesFindResponse = GetCompaniesFindResponses[keyof GetCompaniesFindResponses];

export type GetCompaniesSectorsData = {
    body?: never;
    path?: never;
    query: {
        industry: IcbIndustry;
    };
    url: '/api/v1/companies/sectors';
};

export type GetCompaniesSectorsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetCompaniesSectorsError = GetCompaniesSectorsErrors[keyof GetCompaniesSectorsErrors];

export type GetCompaniesSectorsResponses = {
    /**
     * Response Get Companies Sectors
     *
     * Successful Response
     */
    200: Array<CompanySector>;
};

export type GetCompaniesSectorsResponse = GetCompaniesSectorsResponses[keyof GetCompaniesSectorsResponses];

export type DeleteCompanyData = {
    body?: never;
    path: {
        /**
         * Company Slug
         */
        company_slug: string;
    };
    query?: never;
    url: '/api/v1/companies/{company_slug}';
};

export type DeleteCompanyErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type DeleteCompanyError = DeleteCompanyErrors[keyof DeleteCompanyErrors];

export type DeleteCompanyResponses = {
    /**
     * Successful Response
     */
    200: CompanyExtendedResponseWithFeatures;
};

export type DeleteCompanyResponse = DeleteCompanyResponses[keyof DeleteCompanyResponses];

export type GetCompanyData = {
    body?: never;
    path: {
        /**
         * Company Slug
         */
        company_slug: string;
    };
    query?: never;
    url: '/api/v1/companies/{company_slug}';
};

export type GetCompanyErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetCompanyError = GetCompanyErrors[keyof GetCompanyErrors];

export type GetCompanyResponses = {
    /**
     * Successful Response
     */
    200: CompanyExtendedResponseWithFeatures;
};

export type GetCompanyResponse = GetCompanyResponses[keyof GetCompanyResponses];

export type PatchCompanyData = {
    body: CompanyPatchPayload;
    path: {
        /**
         * Company Slug
         */
        company_slug: string;
    };
    query?: never;
    url: '/api/v1/companies/{company_slug}';
};

export type PatchCompanyErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PatchCompanyError = PatchCompanyErrors[keyof PatchCompanyErrors];

export type PatchCompanyResponses = {
    /**
     * Successful Response
     */
    200: CompanyExtendedResponseWithFeatures;
};

export type PatchCompanyResponse = PatchCompanyResponses[keyof PatchCompanyResponses];

export type GetCompanyMembersData = {
    body?: never;
    path: {
        /**
         * Company Slug
         */
        company_slug: string;
    };
    query?: never;
    url: '/api/v1/companies/{company_slug}/members';
};

export type GetCompanyMembersErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetCompanyMembersError = GetCompanyMembersErrors[keyof GetCompanyMembersErrors];

export type GetCompanyMembersResponses = {
    /**
     * Successful Response
     */
    200: CompanyDetailedResponse;
};

export type GetCompanyMembersResponse = GetCompanyMembersResponses[keyof GetCompanyMembersResponses];

export type GetContactsFindData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         */
        page?: number;
        /**
         * Size
         */
        size?: number;
        /**
         * Order
         */
        order?: 'asc' | 'desc';
        /**
         * Phrase
         */
        phrase?: string | null;
        /**
         * Row
         */
        row?: string | null;
        /**
         * Search Id
         */
        search_id?: string | null;
    };
    url: '/api/v1/contacts/find';
};

export type GetContactsFindErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetContactsFindError = GetContactsFindErrors[keyof GetContactsFindErrors];

export type GetContactsFindResponses = {
    /**
     * Successful Response
     */
    200: PageContactSearchResult;
};

export type GetContactsFindResponse = GetContactsFindResponses[keyof GetContactsFindResponses];

export type GetFilesImageData = {
    body?: never;
    path?: never;
    query: {
        /**
         * Name
         *
         * Image name
         */
        name: string;
        /**
         * Size
         *
         * Image size
         */
        size: number;
    };
    url: '/api/v1/files/image';
};

export type GetFilesImageErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetFilesImageError = GetFilesImageErrors[keyof GetFilesImageErrors];

export type GetFilesImageResponses = {
    /**
     * Returned image.
     */
    200: unknown;
};

export type PostFilesImageData = {
    body: BodyPostFilesImage;
    path?: never;
    query?: never;
    url: '/api/v1/files/image';
};

export type PostFilesImageErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostFilesImageError = PostFilesImageErrors[keyof PostFilesImageErrors];

export type PostFilesImageResponses = {
    /**
     * Successful Response
     */
    200: ImageUploadResponse;
};

export type PostFilesImageResponse = PostFilesImageResponses[keyof PostFilesImageResponses];

export type PostFilesLinkedinResumeData = {
    body: BodyPostFilesLinkedinResume;
    path?: never;
    query?: never;
    url: '/api/v1/files/linkedin_resume';
};

export type PostFilesLinkedinResumeErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostFilesLinkedinResumeError = PostFilesLinkedinResumeErrors[keyof PostFilesLinkedinResumeErrors];

export type PostFilesLinkedinResumeResponses = {
    /**
     * Successful Response
     */
    200: LinkedInResumeUploadResponseOutput;
};

export type PostFilesLinkedinResumeResponse = PostFilesLinkedinResumeResponses[keyof PostFilesLinkedinResumeResponses];

export type GetFindData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         */
        page?: number;
        /**
         * Size
         */
        size?: number;
        /**
         * Order
         */
        order?: 'asc' | 'desc';
        /**
         * Phrase
         */
        phrase?: string | null;
        /**
         * Row
         */
        row?: string | null;
        /**
         * Search Id
         */
        search_id?: string | null;
    };
    url: '/api/v1/find';
};

export type GetFindErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetFindError = GetFindErrors[keyof GetFindErrors];

export type GetFindResponses = {
    /**
     * Successful Response
     */
    200: PageGeneralSearchResponse;
};

export type GetFindResponse = GetFindResponses[keyof GetFindResponses];

export type GetHomeTeamActivityData = {
    body?: never;
    path: {
        /**
         * Team Uuid
         */
        team_uuid: string;
    };
    query: {
        /**
         * Which update type should this endpoint return?
         */
        type: HomeTeamActivityType;
        /**
         * Page
         */
        page?: number;
        /**
         * Size
         */
        size?: number;
    };
    url: '/api/v1/home/teams/{team_uuid}/activity';
};

export type GetHomeTeamActivityErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetHomeTeamActivityError = GetHomeTeamActivityErrors[keyof GetHomeTeamActivityErrors];

export type GetHomeTeamActivityResponses = {
    /**
     * Successful Response
     */
    200: HomeTeamActivityResponse;
};

export type GetHomeTeamActivityResponse = GetHomeTeamActivityResponses[keyof GetHomeTeamActivityResponses];

export type GetHomeTeamCandidatesData = {
    body?: never;
    path: {
        /**
         * Team Uuid
         */
        team_uuid: string;
    };
    query?: never;
    url: '/api/v1/home/teams/{team_uuid}/candidates';
};

export type GetHomeTeamCandidatesErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetHomeTeamCandidatesError = GetHomeTeamCandidatesErrors[keyof GetHomeTeamCandidatesErrors];

export type GetHomeTeamCandidatesResponses = {
    /**
     * Successful Response
     */
    200: HomeCandidatesResponse;
};

export type GetHomeTeamCandidatesResponse = GetHomeTeamCandidatesResponses[keyof GetHomeTeamCandidatesResponses];

export type PostHomeCandidatesData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/home/candidates';
};

export type PostHomeCandidatesResponses = {
    /**
     * Successful Response
     */
    200: unknown;
};

export type GetHomeCandidatesDownvotesData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/home/candidates/downvotes';
};

export type GetHomeCandidatesDownvotesResponses = {
    /**
     * Successful Response
     */
    200: unknown;
};

export type PostHomeTeamCandidateFeedbackData = {
    body: HomeCandidateFeedbackActionPayload;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
        /**
         * Team Uuid
         */
        team_uuid: string;
    };
    query?: {
        /**
         * Search Id
         *
         * Search ID
         */
        search_id?: string | null;
    };
    url: '/api/v1/home/teams/{team_uuid}/candidates/{candidate_uuid}/feedback';
};

export type PostHomeTeamCandidateFeedbackErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostHomeTeamCandidateFeedbackError = PostHomeTeamCandidateFeedbackErrors[keyof PostHomeTeamCandidateFeedbackErrors];

export type PostHomeTeamCandidateFeedbackResponses = {
    /**
     * Successful Response
     */
    200: unknown;
};

export type GetHomeTeamStatsData = {
    body?: never;
    path: {
        /**
         * Team Uuid
         */
        team_uuid: string;
    };
    query?: {
        /**
         * Date From
         *
         * Filter stats from this date
         */
        date_from?: string | null;
        /**
         * Date To
         *
         * Filter stats to this date
         */
        date_to?: string | null;
    };
    url: '/api/v1/home/teams/{team_uuid}/stats';
};

export type GetHomeTeamStatsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetHomeTeamStatsError = GetHomeTeamStatsErrors[keyof GetHomeTeamStatsErrors];

export type GetHomeTeamStatsResponses = {
    /**
     * Successful Response
     */
    200: TeamStatsResponse;
};

export type GetHomeTeamStatsResponse = GetHomeTeamStatsResponses[keyof GetHomeTeamStatsResponses];

export type PostHomeTeamViewsData = {
    body?: never;
    path: {
        /**
         * Team Uuid
         */
        team_uuid: string;
    };
    query: {
        /**
         * Which update type should this endpoint mark as read?
         */
        type: HomeTeamActivityType;
    };
    url: '/api/v1/home/teams/{team_uuid}/views';
};

export type PostHomeTeamViewsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostHomeTeamViewsError = PostHomeTeamViewsErrors[keyof PostHomeTeamViewsErrors];

export type PostHomeTeamViewsResponses = {
    /**
     * Successful Response
     */
    200: unknown;
};

export type GetJobsFacilitatedIntroReminderData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Test Mode
         */
        test_mode?: boolean;
    };
    url: '/api/v1/jobs/facilitated_intro_reminder';
};

export type GetJobsFacilitatedIntroReminderErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetJobsFacilitatedIntroReminderError = GetJobsFacilitatedIntroReminderErrors[keyof GetJobsFacilitatedIntroReminderErrors];

export type GetJobsFacilitatedIntroReminderResponses = {
    /**
     * Successful Response
     */
    200: unknown;
};

export type GetJobsResendinviteData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Older than days
         *
         * Resend invite emails to all invitations older than X days
         */
        older_than_days?: number | null;
    };
    url: '/api/v1/jobs/resendinvite';
};

export type GetJobsResendinviteErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetJobsResendinviteError = GetJobsResendinviteErrors[keyof GetJobsResendinviteErrors];

export type GetJobsResendinviteResponses = {
    /**
     * Successful Response
     */
    200: unknown;
};

export type GetJobsScheduledIntrosData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Date
         */
        date?: string | null;
    };
    url: '/api/v1/jobs/scheduled_intros';
};

export type GetJobsScheduledIntrosErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetJobsScheduledIntrosError = GetJobsScheduledIntrosErrors[keyof GetJobsScheduledIntrosErrors];

export type GetJobsScheduledIntrosResponses = {
    /**
     * Response Get Jobs Scheduled Intros
     *
     * Successful Response
     */
    200: boolean;
};

export type GetJobsScheduledIntrosResponse = GetJobsScheduledIntrosResponses[keyof GetJobsScheduledIntrosResponses];

export type PostHomepageCandidatesData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/jobs/homepage-candidates';
};

export type PostHomepageCandidatesResponses = {
    /**
     * Successful Response
     */
    200: unknown;
};

export type GetJobsScheduledP2pIntrosData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Date
         */
        date?: string | null;
    };
    url: '/api/v1/jobs/scheduled_p2p_intros';
};

export type GetJobsScheduledP2pIntrosErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetJobsScheduledP2pIntrosError = GetJobsScheduledP2pIntrosErrors[keyof GetJobsScheduledP2pIntrosErrors];

export type GetJobsScheduledP2pIntrosResponses = {
    /**
     * Response Get Jobs Scheduled P2P Intros
     *
     * Successful Response
     */
    200: boolean;
};

export type GetJobsScheduledP2pIntrosResponse = GetJobsScheduledP2pIntrosResponses[keyof GetJobsScheduledP2pIntrosResponses];

export type GetJobsAutoIntroductionsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/jobs/auto_introductions';
};

export type GetJobsAutoIntroductionsResponses = {
    /**
     * Response Get Jobs Auto Introductions
     *
     * Successful Response
     */
    200: {
        [key: string]: string;
    };
};

export type GetJobsAutoIntroductionsResponse = GetJobsAutoIntroductionsResponses[keyof GetJobsAutoIntroductionsResponses];

export type GetJobsDraftStatusChangeEmailsData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Minutes Lookback
         *
         * Minutes to look back for events
         */
        minutes_lookback?: number;
    };
    url: '/api/v1/jobs/draft_status_change_emails';
};

export type GetJobsDraftStatusChangeEmailsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetJobsDraftStatusChangeEmailsError = GetJobsDraftStatusChangeEmailsErrors[keyof GetJobsDraftStatusChangeEmailsErrors];

export type GetJobsDraftStatusChangeEmailsResponses = {
    /**
     * Response Get Jobs Draft Status Change Emails
     *
     * Successful Response
     */
    200: {
        [key: string]: unknown;
    };
};

export type GetJobsDraftStatusChangeEmailsResponse = GetJobsDraftStatusChangeEmailsResponses[keyof GetJobsDraftStatusChangeEmailsResponses];

export type GetJobsLinkedinBatchSyncData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Dry Run
         *
         * If true, returns count without executing sync
         */
        dry_run?: boolean;
        /**
         * Batch Size
         *
         * Number of candidates to batch sync (default: all)
         */
        batch_size?: number | null;
    };
    url: '/api/v1/jobs/linkedin_batch_sync';
};

export type GetJobsLinkedinBatchSyncErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetJobsLinkedinBatchSyncError = GetJobsLinkedinBatchSyncErrors[keyof GetJobsLinkedinBatchSyncErrors];

export type GetJobsLinkedinBatchSyncResponses = {
    /**
     * Successful Response
     */
    200: LinkedInBatchSyncResponse;
};

export type GetJobsLinkedinBatchSyncResponse = GetJobsLinkedinBatchSyncResponses[keyof GetJobsLinkedinBatchSyncResponses];

export type GetJobsHubspotFieldSyncData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Dry Run
         *
         * If true, computes what would link/update without writing
         */
        dry_run?: boolean;
        /**
         * Batch Size
         *
         * Limit the number of candidates reconciled (default: all)
         */
        batch_size?: number | null;
    };
    url: '/api/v1/jobs/hubspot_field_sync';
};

export type GetJobsHubspotFieldSyncErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetJobsHubspotFieldSyncError = GetJobsHubspotFieldSyncErrors[keyof GetJobsHubspotFieldSyncErrors];

export type GetJobsHubspotFieldSyncResponses = {
    /**
     * Successful Response
     */
    200: HubSpotFieldSyncResponse;
};

export type GetJobsHubspotFieldSyncResponse = GetJobsHubspotFieldSyncResponses[keyof GetJobsHubspotFieldSyncResponses];

export type GetJobsMarketSearchResolveSweepData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Limit
         */
        limit?: number;
        /**
         * Dry Run
         *
         * If true, computes the changes and rolls back
         */
        dry_run?: boolean;
    };
    url: '/api/v1/jobs/market_search_resolve_sweep';
};

export type GetJobsMarketSearchResolveSweepErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetJobsMarketSearchResolveSweepError = GetJobsMarketSearchResolveSweepErrors[keyof GetJobsMarketSearchResolveSweepErrors];

export type GetJobsMarketSearchResolveSweepResponses = {
    /**
     * Successful Response
     */
    200: MarketSearchJobResponse;
};

export type GetJobsMarketSearchResolveSweepResponse = GetJobsMarketSearchResolveSweepResponses[keyof GetJobsMarketSearchResolveSweepResponses];

export type GetJobsMarketSearchClosureVerificationData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Limit
         */
        limit?: number;
        /**
         * Dry Run
         *
         * If true, computes the changes and rolls back
         */
        dry_run?: boolean;
    };
    url: '/api/v1/jobs/market_search_closure_verification';
};

export type GetJobsMarketSearchClosureVerificationErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetJobsMarketSearchClosureVerificationError = GetJobsMarketSearchClosureVerificationErrors[keyof GetJobsMarketSearchClosureVerificationErrors];

export type GetJobsMarketSearchClosureVerificationResponses = {
    /**
     * Successful Response
     */
    200: MarketSearchJobResponse;
};

export type GetJobsMarketSearchClosureVerificationResponse = GetJobsMarketSearchClosureVerificationResponses[keyof GetJobsMarketSearchClosureVerificationResponses];

export type GetJobsMarketSearchClassificationCatchupData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Limit
         */
        limit?: number;
        /**
         * Dry Run
         *
         * If true, computes the changes and rolls back
         */
        dry_run?: boolean;
    };
    url: '/api/v1/jobs/market_search_classification_catchup';
};

export type GetJobsMarketSearchClassificationCatchupErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetJobsMarketSearchClassificationCatchupError = GetJobsMarketSearchClassificationCatchupErrors[keyof GetJobsMarketSearchClassificationCatchupErrors];

export type GetJobsMarketSearchClassificationCatchupResponses = {
    /**
     * Successful Response
     */
    200: MarketSearchJobResponse;
};

export type GetJobsMarketSearchClassificationCatchupResponse = GetJobsMarketSearchClassificationCatchupResponses[keyof GetJobsMarketSearchClassificationCatchupResponses];

export type GetJobsRefreshTeamInsightsData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Limit
         *
         * Cap how many stale teams to recompute this run (default: all).
         */
        limit?: number | null;
    };
    url: '/api/v1/jobs/refresh_team_insights';
};

export type GetJobsRefreshTeamInsightsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetJobsRefreshTeamInsightsError = GetJobsRefreshTeamInsightsErrors[keyof GetJobsRefreshTeamInsightsErrors];

export type GetJobsRefreshTeamInsightsResponses = {
    /**
     * Successful Response
     */
    200: RefreshTeamInsightsResponse;
};

export type GetJobsRefreshTeamInsightsResponse = GetJobsRefreshTeamInsightsResponses[keyof GetJobsRefreshTeamInsightsResponses];

export type PostUtilsMailSendData = {
    body: BodyPostUtilsMailSend;
    path?: never;
    query?: never;
    url: '/api/v1/utils/mail_send';
};

export type PostUtilsMailSendErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostUtilsMailSendError = PostUtilsMailSendErrors[keyof PostUtilsMailSendErrors];

export type PostUtilsMailSendResponses = {
    /**
     * Response Post Utils Mail Send
     *
     * Successful Response
     */
    200: {
        [key: string]: unknown;
    };
};

export type PostUtilsMailSendResponse = PostUtilsMailSendResponses[keyof PostUtilsMailSendResponses];

export type GetPeopleAdvisorsData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Page number
         */
        page?: number;
        /**
         * Size
         *
         * Page size
         */
        size?: number;
    };
    url: '/api/v1/people/advisors';
};

export type GetPeopleAdvisorsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetPeopleAdvisorsError = GetPeopleAdvisorsErrors[keyof GetPeopleAdvisorsErrors];

export type GetPeopleAdvisorsResponses = {
    /**
     * Successful Response
     */
    200: PagePersonBasicEmailResponse;
};

export type GetPeopleAdvisorsResponse = GetPeopleAdvisorsResponses[keyof GetPeopleAdvisorsResponses];

export type GetPeopleCurrentData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/people/current';
};

export type GetPeopleCurrentResponses = {
    /**
     * Successful Response
     */
    200: PersonV1CurrentResponse;
};

export type GetPeopleCurrentResponse = GetPeopleCurrentResponses[keyof GetPeopleCurrentResponses];

export type GetPeopleCurrentTeamsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/people/current/teams';
};

export type GetPeopleCurrentTeamsResponses = {
    /**
     * Response Get People Current Teams
     *
     * Successful Response
     */
    200: Array<TeamExtendedResponse>;
};

export type GetPeopleCurrentTeamsResponse = GetPeopleCurrentTeamsResponses[keyof GetPeopleCurrentTeamsResponses];

export type GetPeopleFindData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Users Only
         */
        users_only?: boolean | null;
        /**
         * Page
         */
        page?: number;
        /**
         * Size
         */
        size?: number;
        /**
         * Order
         */
        order?: 'asc' | 'desc';
        /**
         * Phrase
         */
        phrase?: string | null;
        /**
         * Row
         */
        row?: string | null;
        /**
         * Search Id
         */
        search_id?: string | null;
    };
    url: '/api/v1/people/find';
};

export type GetPeopleFindErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetPeopleFindError = GetPeopleFindErrors[keyof GetPeopleFindErrors];

export type GetPeopleFindResponses = {
    /**
     * Successful Response
     */
    200: PagePersonSearchResponse;
};

export type GetPeopleFindResponse = GetPeopleFindResponses[keyof GetPeopleFindResponses];

export type DeletePersonImageData = {
    body?: never;
    path: {
        /**
         * Person Uuid
         */
        person_uuid: string;
    };
    query?: never;
    url: '/api/v1/people/{person_uuid}/image';
};

export type DeletePersonImageErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type DeletePersonImageError = DeletePersonImageErrors[keyof DeletePersonImageErrors];

export type DeletePersonImageResponses = {
    /**
     * Successful Response
     */
    200: unknown;
};

export type GetPersonImageData = {
    body?: never;
    path: {
        /**
         * Person Uuid
         */
        person_uuid: string;
    };
    query?: {
        /**
         * Size
         */
        size?: number | null;
    };
    url: '/api/v1/people/{person_uuid}/image';
};

export type GetPersonImageErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetPersonImageError = GetPersonImageErrors[keyof GetPersonImageErrors];

export type GetPersonImageResponses = {
    /**
     * Return a profile image.
     */
    200: unknown;
};

export type PostPersonImageData = {
    body: BodyPostPersonImage;
    path: {
        /**
         * Person Uuid
         */
        person_uuid: string;
    };
    query?: never;
    url: '/api/v1/people/{person_uuid}/image';
};

export type PostPersonImageErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostPersonImageError = PostPersonImageErrors[keyof PostPersonImageErrors];

export type PostPersonImageResponses = {
    /**
     * Successful Response
     */
    200: ImageUploadResponse;
};

export type PostPersonImageResponse = PostPersonImageResponses[keyof PostPersonImageResponses];

export type PutPersonPasswordData = {
    body: PersonClerkPasswordPut;
    path: {
        /**
         * Person Uuid
         */
        person_uuid: string;
    };
    query?: never;
    url: '/api/v1/people/{person_uuid}/password';
};

export type PutPersonPasswordErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PutPersonPasswordError = PutPersonPasswordErrors[keyof PutPersonPasswordErrors];

export type PutPersonPasswordResponses = {
    /**
     * Successful Response
     */
    200: PersonPasswordUpdatedResponse;
};

export type PutPersonPasswordResponse = PutPersonPasswordResponses[keyof PutPersonPasswordResponses];

export type GetPipelinesFindData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Company Id
         */
        company_id?: number | null;
        /**
         * Page
         */
        page?: number;
        /**
         * Size
         */
        size?: number;
        /**
         * Order
         */
        order?: 'asc' | 'desc';
        /**
         * Phrase
         */
        phrase?: string | null;
        /**
         * Row
         */
        row?: string | null;
        /**
         * Search Id
         */
        search_id?: string | null;
    };
    url: '/api/v1/pipelines/find';
};

export type GetPipelinesFindErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetPipelinesFindError = GetPipelinesFindErrors[keyof GetPipelinesFindErrors];

export type GetPipelinesFindResponses = {
    /**
     * Successful Response
     */
    200: PagePipelineSearchResponse;
};

export type GetPipelinesFindResponse = GetPipelinesFindResponses[keyof GetPipelinesFindResponses];

export type DeleteAPipelineData = {
    body?: never;
    path: {
        /**
         * Pipeline Slug
         */
        pipeline_slug: string;
    };
    query?: never;
    url: '/api/v1/pipelines/{pipeline_slug}';
};

export type DeleteAPipelineErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type DeleteAPipelineError = DeleteAPipelineErrors[keyof DeleteAPipelineErrors];

export type DeleteAPipelineResponses = {
    /**
     * Successful Response
     */
    200: DeleteTeamEntityResponse;
};

export type DeleteAPipelineResponse = DeleteAPipelineResponses[keyof DeleteAPipelineResponses];

export type GetRecommendationsFindData = {
    body?: never;
    path?: never;
    query: {
        /**
         * Company ID
         *
         * Return only recommendations made to this company
         */
        company_id?: number | null;
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
        /**
         * Person ID
         *
         * Return only recommendations made to teams this person belongs to
         */
        person_id?: string | null;
        /**
         * Type
         */
        type?: IntroductionType | null;
        /**
         * Page
         */
        page?: number;
        /**
         * Size
         */
        size?: number;
        /**
         * Order
         */
        order?: 'asc' | 'desc';
        /**
         * Phrase
         */
        phrase?: string | null;
        /**
         * Row
         */
        row?: string | null;
        /**
         * Search Id
         */
        search_id?: string | null;
    };
    url: '/api/v1/recommendations/find';
};

export type GetRecommendationsFindErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetRecommendationsFindError = GetRecommendationsFindErrors[keyof GetRecommendationsFindErrors];

export type GetRecommendationsFindResponses = {
    /**
     * Successful Response
     */
    200: PageEsRecommendationCandidateIndexResult;
};

export type GetRecommendationsFindResponse = GetRecommendationsFindResponses[keyof GetRecommendationsFindResponses];

export type GetV1RecommendationData = {
    body?: never;
    path: {
        /**
         * Recommendation Uuid
         */
        recommendation_uuid: string;
    };
    query?: never;
    url: '/api/v1/recommendations/{recommendation_uuid}';
};

export type GetV1RecommendationErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetV1RecommendationError = GetV1RecommendationErrors[keyof GetV1RecommendationErrors];

export type GetV1RecommendationResponses = {
    /**
     * Successful Response
     */
    200: RecommendationExternalFullResponse;
};

export type GetV1RecommendationResponse = GetV1RecommendationResponses[keyof GetV1RecommendationResponses];

export type GetV1RecommendationPreviewData = {
    body?: never;
    path: {
        /**
         * Recommendation Uuid
         */
        recommendation_uuid: string;
    };
    query: {
        /**
         * Token
         *
         * Signed preview token for accessing the recommendation
         */
        token: string;
    };
    url: '/api/v1/recommendations/{recommendation_uuid}/preview';
};

export type GetV1RecommendationPreviewErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetV1RecommendationPreviewError = GetV1RecommendationPreviewErrors[keyof GetV1RecommendationPreviewErrors];

export type GetV1RecommendationPreviewResponses = {
    /**
     * Successful Response
     */
    200: RecommendationPreviewResponse;
};

export type GetV1RecommendationPreviewResponse = GetV1RecommendationPreviewResponses[keyof GetV1RecommendationPreviewResponses];

export type PostRecommendationOffLimitsData = {
    body: RecommendationOffLimits;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
    };
    query?: never;
    url: '/api/v1/recommendations/{candidate_uuid}/off_limits';
};

export type PostRecommendationOffLimitsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostRecommendationOffLimitsError = PostRecommendationOffLimitsErrors[keyof PostRecommendationOffLimitsErrors];

export type PostRecommendationOffLimitsResponses = {
    /**
     * Response Post Recommendation Off Limits
     *
     * Successful Response
     */
    200: Array<OffLimitsTeamSchema>;
};

export type PostRecommendationOffLimitsResponse = PostRecommendationOffLimitsResponses[keyof PostRecommendationOffLimitsResponses];

export type PutRecommendationStatusData = {
    body: RecommendationStatusInput;
    path: {
        /**
         * Submission Candidate Uuid
         */
        submission_candidate_uuid: string;
    };
    query?: never;
    url: '/api/v1/recommendations/{submission_candidate_uuid}/status';
};

export type PutRecommendationStatusErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PutRecommendationStatusError = PutRecommendationStatusErrors[keyof PutRecommendationStatusErrors];

export type PutRecommendationStatusResponses = {
    /**
     * Successful Response
     */
    200: unknown;
};

export type PostShareRecommendationsData = {
    body: ShareRecommendations;
    path?: never;
    query?: never;
    url: '/api/v1/share/recommendations';
};

export type PostShareRecommendationsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostShareRecommendationsError = PostShareRecommendationsErrors[keyof PostShareRecommendationsErrors];

export type PostShareRecommendationsResponses = {
    /**
     * Successful Response
     */
    200: ShareRecommendationsResponse;
};

export type PostShareRecommendationsResponse = PostShareRecommendationsResponses[keyof PostShareRecommendationsResponses];

export type PostTeamsData = {
    body: TeamCreate;
    path?: never;
    query?: never;
    url: '/api/v1/teams';
};

export type PostTeamsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostTeamsError = PostTeamsErrors[keyof PostTeamsErrors];

export type PostTeamsResponses = {
    /**
     * Successful Response
     */
    200: TeamExtendedResponse;
};

export type PostTeamsResponse = PostTeamsResponses[keyof PostTeamsResponses];

export type GetTeamsFindData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Company slug
         *
         * Return only teams belonging to this company
         */
        company_slug?: string | null;
        /**
         * Person ID
         *
         * Return only teams to which this person belongs
         */
        person_id?: string | null;
        /**
         * Page
         */
        page?: number;
        /**
         * Size
         */
        size?: number;
        /**
         * Order
         */
        order?: 'asc' | 'desc';
        /**
         * Phrase
         */
        phrase?: string | null;
        /**
         * Row
         */
        row?: string | null;
        /**
         * Search Id
         */
        search_id?: string | null;
    };
    url: '/api/v1/teams/find';
};

export type GetTeamsFindErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetTeamsFindError = GetTeamsFindErrors[keyof GetTeamsFindErrors];

export type GetTeamsFindResponses = {
    /**
     * Successful Response
     */
    200: PageTeamSearchResponse;
};

export type GetTeamsFindResponse = GetTeamsFindResponses[keyof GetTeamsFindResponses];

export type DeleteTeamData = {
    body?: never;
    path: {
        /**
         * Team Slug
         */
        team_slug: string;
    };
    query?: never;
    url: '/api/v1/teams/{team_slug}';
};

export type DeleteTeamErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type DeleteTeamError = DeleteTeamErrors[keyof DeleteTeamErrors];

export type DeleteTeamResponses = {
    /**
     * Successful Response
     */
    200: unknown;
};

export type GetTeamData = {
    body?: never;
    path: {
        /**
         * Team Slug
         */
        team_slug: string;
    };
    query?: never;
    url: '/api/v1/teams/{team_slug}';
};

export type GetTeamErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetTeamError = GetTeamErrors[keyof GetTeamErrors];

export type GetTeamResponses = {
    /**
     * Successful Response
     */
    200: TeamExtendedResponse;
};

export type GetTeamResponse = GetTeamResponses[keyof GetTeamResponses];

export type PatchTeamData = {
    body: TeamUpdate;
    path: {
        /**
         * Team Slug
         */
        team_slug: string;
    };
    query?: never;
    url: '/api/v1/teams/{team_slug}';
};

export type PatchTeamErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PatchTeamError = PatchTeamErrors[keyof PatchTeamErrors];

export type PatchTeamResponses = {
    /**
     * Successful Response
     */
    200: TeamSearchResponse;
};

export type PatchTeamResponse = PatchTeamResponses[keyof PatchTeamResponses];

export type GetTeamMembersData = {
    body?: never;
    path: {
        /**
         * Team Slug
         */
        team_slug: string;
    };
    query?: {
        /**
         * Team Uuids
         *
         * Return info for specified teams only
         */
        team_uuids?: Array<string> | null;
    };
    url: '/api/v1/teams/{team_slug}/members';
};

export type GetTeamMembersErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetTeamMembersError = GetTeamMembersErrors[keyof GetTeamMembersErrors];

export type GetTeamMembersResponses = {
    /**
     * Response Get Team Members
     *
     * Successful Response
     */
    200: Array<PersonMemberResponse>;
};

export type GetTeamMembersResponse = GetTeamMembersResponses[keyof GetTeamMembersResponses];

export type DeleteTeamMemberData = {
    body?: never;
    path: {
        /**
         * Team Slug
         */
        team_slug: string;
        /**
         * Person Uuid
         */
        person_uuid: string;
    };
    query?: never;
    url: '/api/v1/teams/{team_slug}/members/{person_uuid}';
};

export type DeleteTeamMemberErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type DeleteTeamMemberError = DeleteTeamMemberErrors[keyof DeleteTeamMemberErrors];

export type DeleteTeamMemberResponses = {
    /**
     * Successful Response
     */
    200: PersonBasicResponse;
};

export type DeleteTeamMemberResponse = DeleteTeamMemberResponses[keyof DeleteTeamMemberResponses];

export type GetTeamPipelinesData = {
    body?: never;
    path: {
        /**
         * Team Slug
         */
        team_slug: string;
    };
    query?: {
        /**
         * Filter
         *
         * Apply filter
         */
        filter?: Array<TeamPipelinesFilter>;
        /**
         * Pipeline Type
         *
         * Apply filter by pipeline type
         */
        pipeline_type?: Array<PipelineTypeFilter>;
        /**
         * Team Uuids
         *
         * Return info for specified teams only
         */
        team_uuids?: Array<string> | null;
        /**
         * Sort
         *
         * Sort by column
         */
        sort?: TeamPipelinesSorter | null;
        /**
         * Page
         */
        page?: number;
        /**
         * Size
         */
        size?: number;
        /**
         * Order
         */
        order?: 'asc' | 'desc';
        /**
         * Phrase
         */
        phrase?: string | null;
        /**
         * Row
         */
        row?: string | null;
        /**
         * Search Id
         */
        search_id?: string | null;
    };
    url: '/api/v1/teams/{team_slug}/pipelines';
};

export type GetTeamPipelinesErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetTeamPipelinesError = GetTeamPipelinesErrors[keyof GetTeamPipelinesErrors];

export type GetTeamPipelinesResponses = {
    /**
     * Successful Response
     */
    200: PageTeamPipelinesTableResponse;
};

export type GetTeamPipelinesResponse = GetTeamPipelinesResponses[keyof GetTeamPipelinesResponses];

export type PatchTeamPipelineData = {
    body: PipelineActive;
    path: {
        /**
         * Team Slug
         */
        team_slug: string;
        /**
         * Pipeline Slug
         */
        pipeline_slug: string;
    };
    query?: never;
    url: '/api/v1/teams/{team_slug}/pipelines/{pipeline_slug}';
};

export type PatchTeamPipelineErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PatchTeamPipelineError = PatchTeamPipelineErrors[keyof PatchTeamPipelineErrors];

export type PatchTeamPipelineResponses = {
    /**
     * Successful Response
     */
    200: PipelineBasicResponse;
};

export type PatchTeamPipelineResponse = PatchTeamPipelineResponses[keyof PatchTeamPipelineResponses];

export type GetTeamPipelineRecommendationBioData = {
    body?: never;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
        /**
         * Pipeline Slug
         */
        pipeline_slug: string;
        /**
         * Team Slug
         */
        team_slug: string;
    };
    query?: never;
    url: '/api/v1/teams/{team_slug}/pipelines/{pipeline_slug}/recommendations/{candidate_uuid}/bio';
};

export type GetTeamPipelineRecommendationBioErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetTeamPipelineRecommendationBioError = GetTeamPipelineRecommendationBioErrors[keyof GetTeamPipelineRecommendationBioErrors];

export type GetTeamPipelineRecommendationBioResponses = {
    /**
     * Successful Response
     */
    200: RecommendationBioResponse;
};

export type GetTeamPipelineRecommendationBioResponse = GetTeamPipelineRecommendationBioResponses[keyof GetTeamPipelineRecommendationBioResponses];

export type GetTeamPipelineRecommendationChecksSharedData = {
    body?: never;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
        /**
         * Team Slug
         */
        team_slug: string;
        /**
         * Pipeline Slug
         */
        pipeline_slug: string;
    };
    query?: never;
    url: '/api/v1/teams/{team_slug}/pipelines/{pipeline_slug}/recommendations/{candidate_uuid}/checks/shared';
};

export type GetTeamPipelineRecommendationChecksSharedErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetTeamPipelineRecommendationChecksSharedError = GetTeamPipelineRecommendationChecksSharedErrors[keyof GetTeamPipelineRecommendationChecksSharedErrors];

export type GetTeamPipelineRecommendationChecksSharedResponses = {
    /**
     * Response Get Team Pipeline Recommendation Checks Shared
     *
     * Successful Response
     */
    200: Array<SubmissionCheckSharedResponse>;
};

export type GetTeamPipelineRecommendationChecksSharedResponse = GetTeamPipelineRecommendationChecksSharedResponses[keyof GetTeamPipelineRecommendationChecksSharedResponses];

export type GetTeamPipelineRecommendationIntroductionData = {
    body?: never;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
        /**
         * Pipeline Slug
         */
        pipeline_slug: string;
        /**
         * Team Slug
         */
        team_slug: string;
    };
    query?: never;
    url: '/api/v1/teams/{team_slug}/pipelines/{pipeline_slug}/recommendations/{candidate_uuid}/introduction';
};

export type GetTeamPipelineRecommendationIntroductionErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetTeamPipelineRecommendationIntroductionError = GetTeamPipelineRecommendationIntroductionErrors[keyof GetTeamPipelineRecommendationIntroductionErrors];

export type GetTeamPipelineRecommendationIntroductionResponses = {
    /**
     * Successful Response
     */
    200: RecommendationIntroductionResponse;
};

export type GetTeamPipelineRecommendationIntroductionResponse = GetTeamPipelineRecommendationIntroductionResponses[keyof GetTeamPipelineRecommendationIntroductionResponses];

export type PostTeamPipelineRecommendationIntroductionData = {
    body: RecommendationIntroduction;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
        /**
         * Pipeline Slug
         */
        pipeline_slug: string;
        /**
         * Team Slug
         */
        team_slug: string;
    };
    query?: never;
    url: '/api/v1/teams/{team_slug}/pipelines/{pipeline_slug}/recommendations/{candidate_uuid}/introduction';
};

export type PostTeamPipelineRecommendationIntroductionErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostTeamPipelineRecommendationIntroductionError = PostTeamPipelineRecommendationIntroductionErrors[keyof PostTeamPipelineRecommendationIntroductionErrors];

export type PostTeamPipelineRecommendationIntroductionResponses = {
    /**
     * Successful Response
     */
    200: unknown;
};

export type GetTeamRecommendationBioData = {
    body?: never;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
        /**
         * Team Slug
         */
        team_slug: string;
    };
    query?: never;
    url: '/api/v1/teams/{team_slug}/recommendations/{candidate_uuid}/bio';
};

export type GetTeamRecommendationBioErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetTeamRecommendationBioError = GetTeamRecommendationBioErrors[keyof GetTeamRecommendationBioErrors];

export type GetTeamRecommendationBioResponses = {
    /**
     * Successful Response
     */
    200: RecommendationBioResponse;
};

export type GetTeamRecommendationBioResponse = GetTeamRecommendationBioResponses[keyof GetTeamRecommendationBioResponses];

export type GetTeamRecommendationIntroductionData = {
    body?: never;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
        /**
         * Team Slug
         */
        team_slug: string;
    };
    query?: never;
    url: '/api/v1/teams/{team_slug}/recommendations/{candidate_uuid}/introduction';
};

export type GetTeamRecommendationIntroductionErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetTeamRecommendationIntroductionError = GetTeamRecommendationIntroductionErrors[keyof GetTeamRecommendationIntroductionErrors];

export type GetTeamRecommendationIntroductionResponses = {
    /**
     * Successful Response
     */
    200: RecommendationIntroductionResponse;
};

export type GetTeamRecommendationIntroductionResponse = GetTeamRecommendationIntroductionResponses[keyof GetTeamRecommendationIntroductionResponses];

export type PostTeamRecommendationIntroductionData = {
    body: RecommendationIntroduction;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
        /**
         * Team Slug
         */
        team_slug: string;
    };
    query?: never;
    url: '/api/v1/teams/{team_slug}/recommendations/{candidate_uuid}/introduction';
};

export type PostTeamRecommendationIntroductionErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostTeamRecommendationIntroductionError = PostTeamRecommendationIntroductionErrors[keyof PostTeamRecommendationIntroductionErrors];

export type PostTeamRecommendationIntroductionResponses = {
    /**
     * Successful Response
     */
    200: unknown;
};

export type PostTrendingScheduleData = {
    body: TrendingSchedulePayload;
    path?: never;
    query?: never;
    url: '/api/v1/trending_schedule';
};

export type PostTrendingScheduleErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostTrendingScheduleError = PostTrendingScheduleErrors[keyof PostTrendingScheduleErrors];

export type PostTrendingScheduleResponses = {
    /**
     * Successful Response
     */
    200: TrendingScheduleResponse;
};

export type PostTrendingScheduleResponse = PostTrendingScheduleResponses[keyof PostTrendingScheduleResponses];

export type PostClerkWebhookData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/webhooks/clerk';
};

export type PostClerkWebhookResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type PostClerkWebhookResponse = PostClerkWebhookResponses[keyof PostClerkWebhookResponses];

export type PostWebhooksEsEventsData = {
    body: EsClickEventPayload;
    path?: never;
    query?: never;
    url: '/api/v1/webhooks/es_events';
};

export type PostWebhooksEsEventsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostWebhooksEsEventsError = PostWebhooksEsEventsErrors[keyof PostWebhooksEsEventsErrors];

export type PostWebhooksEsEventsResponses = {
    /**
     * Successful Response
     */
    200: unknown;
};

export type PostCandidatesImportFromCsvData = {
    body?: BodyPostCandidatesImportFromCsv;
    path?: never;
    query?: {
        /**
         * Skip Linkedin Sync
         *
         * Skip Proxycurl LinkedIn sync after import. Use for local/staging when sync is unavailable; imported candidates are kept even with LinkedIn URLs.
         */
        skip_linkedin_sync?: boolean;
    };
    url: '/api/v1/candidates/import';
};

export type PostCandidatesImportFromCsvErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostCandidatesImportFromCsvError = PostCandidatesImportFromCsvErrors[keyof PostCandidatesImportFromCsvErrors];

export type PostCandidatesImportFromCsvResponses = {
    /**
     * Successful Response
     */
    200: CandidateAsyncImportResponse;
};

export type PostCandidatesImportFromCsvResponse = PostCandidatesImportFromCsvResponses[keyof PostCandidatesImportFromCsvResponses];

export type PostCandidatesExportFailedData = {
    body: FailedCandidatesImportRequest;
    path?: never;
    query?: never;
    url: '/api/v1/candidates/export-failed';
};

export type PostCandidatesExportFailedErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostCandidatesExportFailedError = PostCandidatesExportFailedErrors[keyof PostCandidatesExportFailedErrors];

export type PostCandidatesExportFailedResponses = {
    /**
     * Successful Response
     */
    200: unknown;
};

export type PostEsReindexData = {
    body?: never;
    path?: never;
    query: {
        /**
         * Index
         *
         * Reindex a single index
         */
        index: SearchIndex;
    };
    url: '/api/v2/es-reindex';
};

export type PostEsReindexErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostEsReindexError = PostEsReindexErrors[keyof PostEsReindexErrors];

export type PostEsReindexResponses = {
    /**
     * Successful Response
     */
    202: ReindexAsyncResponse;
};

export type PostEsReindexResponse = PostEsReindexResponses[keyof PostEsReindexResponses];

export type GetReindexStatusData = {
    body?: never;
    path: {
        /**
         * Task Id
         */
        task_id: string;
    };
    query?: never;
    url: '/api/v2/es-reindex/status/{task_id}';
};

export type GetReindexStatusErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetReindexStatusError = GetReindexStatusErrors[keyof GetReindexStatusErrors];

export type GetReindexStatusResponses = {
    /**
     * Successful Response
     */
    200: ReindexStatusResponse;
};

export type GetReindexStatusResponse = GetReindexStatusResponses[keyof GetReindexStatusResponses];

export type GetEsIndexData = {
    body?: never;
    path?: never;
    query: {
        /**
         * Index
         *
         * Get details of current index
         */
        index: SearchIndex;
    };
    url: '/api/v2/current-es-index';
};

export type GetEsIndexErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetEsIndexError = GetEsIndexErrors[keyof GetEsIndexErrors];

export type GetEsIndexResponses = {
    /**
     * Successful Response
     */
    200: CurrentEsIndexResponse;
};

export type GetEsIndexResponse = GetEsIndexResponses[keyof GetEsIndexResponses];

export type PatchAliasToEsIndexData = {
    body?: never;
    path?: never;
    query: {
        /**
         * Index Name
         */
        index_name: string;
        /**
         * Index
         *
         * Search index to update. Refers to model name and not actual index name
         */
        index: SearchIndex;
    };
    url: '/api/v2/current-es-index';
};

export type PatchAliasToEsIndexErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PatchAliasToEsIndexError = PatchAliasToEsIndexErrors[keyof PatchAliasToEsIndexErrors];

export type PatchAliasToEsIndexResponses = {
    /**
     * Successful Response
     */
    200: IndexAliasUpdateResponse;
};

export type PatchAliasToEsIndexResponse = PatchAliasToEsIndexResponses[keyof PatchAliasToEsIndexResponses];

export type CreateMarketSearchData = {
    body: MarketSearchCreatePayload;
    path?: never;
    query?: never;
    url: '/api/v2/market-searches';
};

export type CreateMarketSearchErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type CreateMarketSearchError = CreateMarketSearchErrors[keyof CreateMarketSearchErrors];

export type CreateMarketSearchResponses = {
    /**
     * Successful Response
     */
    201: MarketSearchCreateResponse;
};

export type CreateMarketSearchResponse = CreateMarketSearchResponses[keyof CreateMarketSearchResponses];

export type LinkMarketSearchData = {
    body: MarketSearchLinkPayload;
    path: {
        /**
         * Market Search Uuid
         */
        market_search_uuid: string;
    };
    query?: never;
    url: '/api/v2/market-searches/{market_search_uuid}/link';
};

export type LinkMarketSearchErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type LinkMarketSearchError = LinkMarketSearchErrors[keyof LinkMarketSearchErrors];

export type LinkMarketSearchResponses = {
    /**
     * Successful Response
     */
    200: MarketSearchResponse;
};

export type LinkMarketSearchResponse = LinkMarketSearchResponses[keyof LinkMarketSearchResponses];

export type CloseMarketSearchData = {
    body: MarketSearchClosePayload;
    path: {
        /**
         * Market Search Uuid
         */
        market_search_uuid: string;
    };
    query?: never;
    url: '/api/v2/market-searches/{market_search_uuid}/close';
};

export type CloseMarketSearchErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type CloseMarketSearchError = CloseMarketSearchErrors[keyof CloseMarketSearchErrors];

export type CloseMarketSearchResponses = {
    /**
     * Successful Response
     */
    200: MarketSearchResponse;
};

export type CloseMarketSearchResponse = CloseMarketSearchResponses[keyof CloseMarketSearchResponses];

export type SearchMarketSearchesData = {
    body: MarketSearchSearchPayload;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Page number
         */
        page?: number;
        /**
         * Size
         *
         * Page size
         */
        size?: number;
    };
    url: '/api/v2/market-searches/search';
};

export type SearchMarketSearchesErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type SearchMarketSearchesError = SearchMarketSearchesErrors[keyof SearchMarketSearchesErrors];

export type SearchMarketSearchesResponses = {
    /**
     * Successful Response
     */
    200: PostPageMarketSearchRowResponse;
};

export type SearchMarketSearchesResponse = SearchMarketSearchesResponses[keyof SearchMarketSearchesResponses];

export type GetMarketSearchContactOptionsData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Phrase
         *
         * Words of the name or email
         */
        phrase?: string | null;
        /**
         * Company Id
         *
         * Only people at this firm
         */
        company_id?: number | null;
        /**
         * Search Team Id
         *
         * Only people on this team
         */
        search_team_id?: number | null;
        /**
         * Limit
         */
        limit?: number;
    };
    url: '/api/v2/market-searches/contact-options';
};

export type GetMarketSearchContactOptionsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetMarketSearchContactOptionsError = GetMarketSearchContactOptionsErrors[keyof GetMarketSearchContactOptionsErrors];

export type GetMarketSearchContactOptionsResponses = {
    /**
     * Response Get Market Search Contact Options
     *
     * Successful Response
     */
    200: Array<MarketSearchContactOption>;
};

export type GetMarketSearchContactOptionsResponse = GetMarketSearchContactOptionsResponses[keyof GetMarketSearchContactOptionsResponses];

export type GetMarketSearchTeamOptionsData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Phrase
         *
         * Words of the team or firm name
         */
        phrase?: string | null;
        /**
         * Company Id
         *
         * Only this firm's teams
         */
        company_id?: number | null;
        /**
         * Limit
         */
        limit?: number;
    };
    url: '/api/v2/market-searches/team-options';
};

export type GetMarketSearchTeamOptionsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetMarketSearchTeamOptionsError = GetMarketSearchTeamOptionsErrors[keyof GetMarketSearchTeamOptionsErrors];

export type GetMarketSearchTeamOptionsResponses = {
    /**
     * Response Get Market Search Team Options
     *
     * Successful Response
     */
    200: Array<MarketSearchTeamOption>;
};

export type GetMarketSearchTeamOptionsResponse = GetMarketSearchTeamOptionsResponses[keyof GetMarketSearchTeamOptionsResponses];

export type GetMarketSearchPipelineOptionsData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Phrase
         *
         * Words of the role, team or company name
         */
        phrase?: string | null;
        /**
         * Company Id
         *
         * Only pipelines at this company
         */
        company_id?: number | null;
        /**
         * Limit
         */
        limit?: number;
    };
    url: '/api/v2/market-searches/pipeline-options';
};

export type GetMarketSearchPipelineOptionsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetMarketSearchPipelineOptionsError = GetMarketSearchPipelineOptionsErrors[keyof GetMarketSearchPipelineOptionsErrors];

export type GetMarketSearchPipelineOptionsResponses = {
    /**
     * Response Get Market Search Pipeline Options
     *
     * Successful Response
     */
    200: Array<MarketSearchPipelineOption>;
};

export type GetMarketSearchPipelineOptionsResponse = GetMarketSearchPipelineOptionsResponses[keyof GetMarketSearchPipelineOptionsResponses];

export type DeleteMarketSearchData = {
    body?: never;
    path: {
        /**
         * Market Search Uuid
         */
        market_search_uuid: string;
    };
    query?: never;
    url: '/api/v2/market-searches/{market_search_uuid}';
};

export type DeleteMarketSearchErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type DeleteMarketSearchError = DeleteMarketSearchErrors[keyof DeleteMarketSearchErrors];

export type DeleteMarketSearchResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteMarketSearchResponse = DeleteMarketSearchResponses[keyof DeleteMarketSearchResponses];

export type GetMarketSearchData = {
    body?: never;
    path: {
        /**
         * Market Search Uuid
         */
        market_search_uuid: string;
    };
    query?: never;
    url: '/api/v2/market-searches/{market_search_uuid}';
};

export type GetMarketSearchErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetMarketSearchError = GetMarketSearchErrors[keyof GetMarketSearchErrors];

export type GetMarketSearchResponses = {
    /**
     * Successful Response
     */
    200: MarketSearchDetailResponse;
};

export type GetMarketSearchResponse = GetMarketSearchResponses[keyof GetMarketSearchResponses];

export type UpdateMarketSearchData = {
    body: MarketSearchUpdatePayload;
    path: {
        /**
         * Market Search Uuid
         */
        market_search_uuid: string;
    };
    query?: never;
    url: '/api/v2/market-searches/{market_search_uuid}';
};

export type UpdateMarketSearchErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type UpdateMarketSearchError = UpdateMarketSearchErrors[keyof UpdateMarketSearchErrors];

export type UpdateMarketSearchResponses = {
    /**
     * Successful Response
     */
    200: MarketSearchUpdateResponse;
};

export type UpdateMarketSearchResponse = UpdateMarketSearchResponses[keyof UpdateMarketSearchResponses];

export type PostCandidatesData = {
    body: CreateCandidate;
    path?: never;
    query?: never;
    url: '/api/v2/candidates';
};

export type PostCandidatesErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostCandidatesError = PostCandidatesErrors[keyof PostCandidatesErrors];

export type PostCandidatesResponses = {
    /**
     * Successful Response
     */
    200: CandidateInternalFullResponse;
};

export type PostCandidatesResponse = PostCandidatesResponses[keyof PostCandidatesResponses];

export type GetCandidateV2Data = {
    body?: never;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
    };
    query?: never;
    url: '/api/v2/candidates/{candidate_uuid}';
};

export type GetCandidateV2Errors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetCandidateV2Error = GetCandidateV2Errors[keyof GetCandidateV2Errors];

export type GetCandidateV2Responses = {
    /**
     * Successful Response
     */
    200: CandidateInternalFullResponseWithLinkedPerson;
};

export type GetCandidateV2Response = GetCandidateV2Responses[keyof GetCandidateV2Responses];

export type PatchCandidateV2Data = {
    body: UpdateCandidate;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
    };
    query?: never;
    url: '/api/v2/candidates/{candidate_uuid}';
};

export type PatchCandidateV2Errors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PatchCandidateV2Error = PatchCandidateV2Errors[keyof PatchCandidateV2Errors];

export type PatchCandidateV2Responses = {
    /**
     * Successful Response
     */
    200: CandidateInternalFullResponse;
};

export type PatchCandidateV2Response = PatchCandidateV2Responses[keyof PatchCandidateV2Responses];

export type PutCandidateData = {
    body: CreateCandidate;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
    };
    query?: never;
    url: '/api/v2/candidates/{candidate_uuid}';
};

export type PutCandidateErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PutCandidateError = PutCandidateErrors[keyof PutCandidateErrors];

export type PutCandidateResponses = {
    /**
     * Successful Response
     */
    200: CandidateInternalFullResponse;
};

export type PutCandidateResponse = PutCandidateResponses[keyof PutCandidateResponses];

export type DeleteCandidateReferrerData = {
    body?: never;
    path: {
        /**
         * Identity Uuid
         */
        identity_uuid: string;
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
    };
    query?: never;
    url: '/api/v2/candidates/{candidate_uuid}/referrers/{identity_uuid}';
};

export type DeleteCandidateReferrerErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type DeleteCandidateReferrerError = DeleteCandidateReferrerErrors[keyof DeleteCandidateReferrerErrors];

export type DeleteCandidateReferrerResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteCandidateReferrerResponse = DeleteCandidateReferrerResponses[keyof DeleteCandidateReferrerResponses];

export type PutCandidateReferrerData = {
    body?: never;
    path: {
        /**
         * Identity Uuid
         */
        identity_uuid: string;
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
    };
    query?: never;
    url: '/api/v2/candidates/{candidate_uuid}/referrers/{identity_uuid}';
};

export type PutCandidateReferrerErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PutCandidateReferrerError = PutCandidateReferrerErrors[keyof PutCandidateReferrerErrors];

export type PutCandidateReferrerResponses = {
    /**
     * Successful Response
     */
    200: ReferrerBasicResponse;
    /**
     * Created
     */
    201: ReferrerBasicResponse;
};

export type PutCandidateReferrerResponse = PutCandidateReferrerResponses[keyof PutCandidateReferrerResponses];

export type DeleteCandidateFavoriteData = {
    body?: never;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
    };
    query?: never;
    url: '/api/v2/candidates/{candidate_uuid}/favorite';
};

export type DeleteCandidateFavoriteErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type DeleteCandidateFavoriteError = DeleteCandidateFavoriteErrors[keyof DeleteCandidateFavoriteErrors];

export type DeleteCandidateFavoriteResponses = {
    /**
     * Successful Response
     */
    200: CandidateFavoriteResponse;
};

export type DeleteCandidateFavoriteResponse = DeleteCandidateFavoriteResponses[keyof DeleteCandidateFavoriteResponses];

export type PostCandidateFavoriteData = {
    body?: never;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
    };
    query?: never;
    url: '/api/v2/candidates/{candidate_uuid}/favorite';
};

export type PostCandidateFavoriteErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostCandidateFavoriteError = PostCandidateFavoriteErrors[keyof PostCandidateFavoriteErrors];

export type PostCandidateFavoriteResponses = {
    /**
     * Successful Response
     */
    200: CandidateFavoriteResponse;
};

export type PostCandidateFavoriteResponse = PostCandidateFavoriteResponses[keyof PostCandidateFavoriteResponses];

export type GetCandidateGoalsData = {
    body?: never;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
    };
    query?: never;
    url: '/api/v2/candidates/{candidate_uuid}/goals';
};

export type GetCandidateGoalsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetCandidateGoalsError = GetCandidateGoalsErrors[keyof GetCandidateGoalsErrors];

export type GetCandidateGoalsResponses = {
    /**
     * Successful Response
     */
    200: CandidateGoalResponse;
};

export type GetCandidateGoalsResponse = GetCandidateGoalsResponses[keyof GetCandidateGoalsResponses];

export type PatchCandidateGoalsData = {
    body: CandidateGoalsLegacyPayload;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
    };
    query?: never;
    url: '/api/v2/candidates/{candidate_uuid}/goals';
};

export type PatchCandidateGoalsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PatchCandidateGoalsError = PatchCandidateGoalsErrors[keyof PatchCandidateGoalsErrors];

export type PatchCandidateGoalsResponses = {
    /**
     * Successful Response
     */
    200: CandidateInternalFullResponse;
};

export type PatchCandidateGoalsResponse = PatchCandidateGoalsResponses[keyof PatchCandidateGoalsResponses];

export type GetCandidateGoalsWithActivitiesData = {
    body?: never;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
    };
    query?: {
        /**
         * Goal Status
         *
         * Filter goals by status
         */
        goal_status?: Array<GoalStatusFilter>;
    };
    url: '/api/v2/candidates/{candidate_uuid}/goals-activities';
};

export type GetCandidateGoalsWithActivitiesErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetCandidateGoalsWithActivitiesError = GetCandidateGoalsWithActivitiesErrors[keyof GetCandidateGoalsWithActivitiesErrors];

export type GetCandidateGoalsWithActivitiesResponses = {
    /**
     * Successful Response
     */
    200: CandidateGoalsResponse;
};

export type GetCandidateGoalsWithActivitiesResponse = GetCandidateGoalsWithActivitiesResponses[keyof GetCandidateGoalsWithActivitiesResponses];

export type PostCandidatesGoalsV2Data = {
    body: CandidateGoalsPayload;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
    };
    query?: never;
    url: '/api/v2/candidates/{candidate_uuid}/goals-activities';
};

export type PostCandidatesGoalsV2Errors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostCandidatesGoalsV2Error = PostCandidatesGoalsV2Errors[keyof PostCandidatesGoalsV2Errors];

export type PostCandidatesGoalsV2Responses = {
    /**
     * Successful Response
     */
    200: CandidateGoalsResponse;
};

export type PostCandidatesGoalsV2Response = PostCandidatesGoalsV2Responses[keyof PostCandidatesGoalsV2Responses];

export type PostCandidateShowOnboardingSectionData = {
    body: CandidateShowOnboardingSection;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
    };
    query?: never;
    url: '/api/v2/candidates/{candidate_uuid}/show-onboarding-section';
};

export type PostCandidateShowOnboardingSectionErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostCandidateShowOnboardingSectionError = PostCandidateShowOnboardingSectionErrors[keyof PostCandidateShowOnboardingSectionErrors];

export type PostCandidateShowOnboardingSectionResponses = {
    /**
     * Successful Response
     */
    200: unknown;
};

export type PostCandidateFeedbackV2Data = {
    body: CandidateFeedbackPayload;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
    };
    query?: never;
    url: '/api/v2/candidates/{candidate_uuid}/feedback';
};

export type PostCandidateFeedbackV2Errors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostCandidateFeedbackV2Error = PostCandidateFeedbackV2Errors[keyof PostCandidateFeedbackV2Errors];

export type PostCandidateFeedbackV2Responses = {
    /**
     * Successful Response
     */
    200: CandidateFeedbackResult;
};

export type PostCandidateFeedbackV2Response = PostCandidateFeedbackV2Responses[keyof PostCandidateFeedbackV2Responses];

export type PostCandidateFeedbackFilterData = {
    body: CandidateFeedbackFilterPayload;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
    };
    query?: never;
    url: '/api/v2/candidates/{candidate_uuid}/feedback/filter';
};

export type PostCandidateFeedbackFilterErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostCandidateFeedbackFilterError = PostCandidateFeedbackFilterErrors[keyof PostCandidateFeedbackFilterErrors];

export type PostCandidateFeedbackFilterResponses = {
    /**
     * Successful Response
     */
    200: CandidateFeedbackFilterResponse;
};

export type PostCandidateFeedbackFilterResponse = PostCandidateFeedbackFilterResponses[keyof PostCandidateFeedbackFilterResponses];

export type DeleteCandidateFeedbackData = {
    body?: never;
    path: {
        /**
         * Feedback Uuid
         */
        feedback_uuid: string;
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
    };
    query?: never;
    url: '/api/v2/candidates/{candidate_uuid}/feedback/{feedback_uuid}';
};

export type DeleteCandidateFeedbackErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type DeleteCandidateFeedbackError = DeleteCandidateFeedbackErrors[keyof DeleteCandidateFeedbackErrors];

export type DeleteCandidateFeedbackResponses = {
    /**
     * Successful Response
     */
    200: CandidateFeedbackResult;
};

export type DeleteCandidateFeedbackResponse = DeleteCandidateFeedbackResponses[keyof DeleteCandidateFeedbackResponses];

export type PutCandidateFeedbackData = {
    body: CandidateFeedbackPayload;
    path: {
        /**
         * Feedback Uuid
         */
        feedback_uuid: string;
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
    };
    query?: never;
    url: '/api/v2/candidates/{candidate_uuid}/feedback/{feedback_uuid}';
};

export type PutCandidateFeedbackErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PutCandidateFeedbackError = PutCandidateFeedbackErrors[keyof PutCandidateFeedbackErrors];

export type PutCandidateFeedbackResponses = {
    /**
     * Successful Response
     */
    200: CandidateFeedbackResult;
};

export type PutCandidateFeedbackResponse = PutCandidateFeedbackResponses[keyof PutCandidateFeedbackResponses];

export type DeleteCandidateGoalData = {
    body?: never;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
        /**
         * Goal Id
         */
        goal_id: string;
    };
    query?: never;
    url: '/api/v2/candidates/{candidate_uuid}/goals/{goal_id}';
};

export type DeleteCandidateGoalErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type DeleteCandidateGoalError = DeleteCandidateGoalErrors[keyof DeleteCandidateGoalErrors];

export type DeleteCandidateGoalResponses = {
    /**
     * Successful Response
     */
    200: CandidateGoal;
};

export type DeleteCandidateGoalResponse = DeleteCandidateGoalResponses[keyof DeleteCandidateGoalResponses];

export type GetCandidateGoalV2Data = {
    body?: never;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
        /**
         * Goal Id
         */
        goal_id: string;
    };
    query?: never;
    url: '/api/v2/candidates/{candidate_uuid}/goals/{goal_id}';
};

export type GetCandidateGoalV2Errors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetCandidateGoalV2Error = GetCandidateGoalV2Errors[keyof GetCandidateGoalV2Errors];

export type GetCandidateGoalV2Responses = {
    /**
     * Successful Response
     */
    200: CandidateGoal;
};

export type GetCandidateGoalV2Response = GetCandidateGoalV2Responses[keyof GetCandidateGoalV2Responses];

export type PatchCandidateGoalV2Data = {
    body: CandidateGoalsPatchPayload;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
        /**
         * Goal Id
         */
        goal_id: string;
    };
    query?: never;
    url: '/api/v2/candidates/{candidate_uuid}/goals/{goal_id}';
};

export type PatchCandidateGoalV2Errors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PatchCandidateGoalV2Error = PatchCandidateGoalV2Errors[keyof PatchCandidateGoalV2Errors];

export type PatchCandidateGoalV2Responses = {
    /**
     * Successful Response
     */
    200: CandidateGoal;
};

export type PatchCandidateGoalV2Response = PatchCandidateGoalV2Responses[keyof PatchCandidateGoalV2Responses];

export type DeleteCandidateGoalActivityData = {
    body: CandidateGoalActivitiesPayload;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
        /**
         * Goal Id
         */
        goal_id: string;
    };
    query?: never;
    url: '/api/v2/candidates/{candidate_uuid}/goals/{goal_id}/activity';
};

export type DeleteCandidateGoalActivityErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type DeleteCandidateGoalActivityError = DeleteCandidateGoalActivityErrors[keyof DeleteCandidateGoalActivityErrors];

export type DeleteCandidateGoalActivityResponses = {
    /**
     * Successful Response
     */
    200: CandidateGoal;
};

export type DeleteCandidateGoalActivityResponse = DeleteCandidateGoalActivityResponses[keyof DeleteCandidateGoalActivityResponses];

export type PostCandidateGoalActivityData = {
    body: CandidateGoalActivitiesPayload;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
        /**
         * Goal Id
         */
        goal_id: string;
    };
    query?: never;
    url: '/api/v2/candidates/{candidate_uuid}/goals/{goal_id}/activity';
};

export type PostCandidateGoalActivityErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostCandidateGoalActivityError = PostCandidateGoalActivityErrors[keyof PostCandidateGoalActivityErrors];

export type PostCandidateGoalActivityResponses = {
    /**
     * Successful Response
     */
    200: CandidateGoal;
};

export type PostCandidateGoalActivityResponse = PostCandidateGoalActivityResponses[keyof PostCandidateGoalActivityResponses];

export type PostCandidateGoalTasksData = {
    body: TaskCreateUpdateManyPayload;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
        /**
         * Goal Id
         */
        goal_id: string;
    };
    query?: never;
    url: '/api/v2/candidates/{candidate_uuid}/goals/{goal_id}/tasks';
};

export type PostCandidateGoalTasksErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostCandidateGoalTasksError = PostCandidateGoalTasksErrors[keyof PostCandidateGoalTasksErrors];

export type PostCandidateGoalTasksResponses = {
    /**
     * Successful Response
     */
    200: CandidateGoal;
};

export type PostCandidateGoalTasksResponse = PostCandidateGoalTasksResponses[keyof PostCandidateGoalTasksResponses];

export type PostCandidateRecommendationsFilterData = {
    body: CandidateRecommendationsFilterPayload;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
    };
    query?: never;
    url: '/api/v2/candidates/{candidate_uuid}/recommendations/filter';
};

export type PostCandidateRecommendationsFilterErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostCandidateRecommendationsFilterError = PostCandidateRecommendationsFilterErrors[keyof PostCandidateRecommendationsFilterErrors];

export type PostCandidateRecommendationsFilterResponses = {
    /**
     * Successful Response
     */
    200: CandidateRecommendationsFilterResponse;
};

export type PostCandidateRecommendationsFilterResponse = PostCandidateRecommendationsFilterResponses[keyof PostCandidateRecommendationsFilterResponses];

export type PostCandidateIntroTemplatesData = {
    body: CandidateIntroductionTemplateCreate;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
    };
    query?: never;
    url: '/api/v2/candidates/{candidate_uuid}/introduction-templates';
};

export type PostCandidateIntroTemplatesErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostCandidateIntroTemplatesError = PostCandidateIntroTemplatesErrors[keyof PostCandidateIntroTemplatesErrors];

export type PostCandidateIntroTemplatesResponses = {
    /**
     * Introduction template with uuid if successfully created
     */
    200: CandidateIntroductionTemplateResponse;
};

export type PostCandidateIntroTemplatesResponse = PostCandidateIntroTemplatesResponses[keyof PostCandidateIntroTemplatesResponses];

export type PostCandidateHighlightsV2Data = {
    body: CandidateHighlightCreate;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
    };
    query?: never;
    url: '/api/v2/candidates/{candidate_uuid}/highlights';
};

export type PostCandidateHighlightsV2Errors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostCandidateHighlightsV2Error = PostCandidateHighlightsV2Errors[keyof PostCandidateHighlightsV2Errors];

export type PostCandidateHighlightsV2Responses = {
    /**
     * Highlight with uuid if successfully created
     */
    200: HighlightResponse;
};

export type PostCandidateHighlightsV2Response = PostCandidateHighlightsV2Responses[keyof PostCandidateHighlightsV2Responses];

export type GetCandidateStatsData = {
    body?: never;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
    };
    query: {
        /**
         * Date From
         *
         * Filter stats from this date
         */
        date_from?: string | null;
        /**
         * Date To
         *
         * Filter stats to this date
         */
        date_to?: string | null;
        /**
         * Filter stats by entity type
         */
        type: TypeFilter;
    };
    url: '/api/v2/candidates/{candidate_uuid}/stats';
};

export type GetCandidateStatsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetCandidateStatsError = GetCandidateStatsErrors[keyof GetCandidateStatsErrors];

export type GetCandidateStatsResponses = {
    /**
     * Response Get Candidate Stats
     *
     * Successful Response
     */
    200: CandidateRecommendationsStatsRoleResponse | CandidateRecommendationsStatsSubmissionResponse | CandidateRecommendationsStatsTeamResponse;
};

export type GetCandidateStatsResponse = GetCandidateStatsResponses[keyof GetCandidateStatsResponses];

export type GetRecsysCandidateOpportunitiesData = {
    body?: never;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
    };
    query?: {
        /**
         * Limit
         */
        limit?: number;
        /**
         * Offset
         */
        offset?: number;
    };
    url: '/api/v2/candidates/{candidate_uuid}/recsys-opportunities';
};

export type GetRecsysCandidateOpportunitiesErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetRecsysCandidateOpportunitiesError = GetRecsysCandidateOpportunitiesErrors[keyof GetRecsysCandidateOpportunitiesErrors];

export type GetRecsysCandidateOpportunitiesResponses = {
    /**
     * Successful Response
     */
    200: RecsysCandidateOpportunitiesResponse;
};

export type GetRecsysCandidateOpportunitiesResponse = GetRecsysCandidateOpportunitiesResponses[keyof GetRecsysCandidateOpportunitiesResponses];

export type GetRecsysOpportunityRationaleData = {
    body?: never;
    path: {
        /**
         * Pipeline Uuid
         */
        pipeline_uuid: string;
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
    };
    query?: never;
    url: '/api/v2/candidates/{candidate_uuid}/recsys-opportunities/{pipeline_uuid}/rationale';
};

export type GetRecsysOpportunityRationaleErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetRecsysOpportunityRationaleError = GetRecsysOpportunityRationaleErrors[keyof GetRecsysOpportunityRationaleErrors];

export type GetRecsysOpportunityRationaleResponses = {
    /**
     * Successful Response
     */
    200: RecsysOpportunityRationaleResponse;
};

export type GetRecsysOpportunityRationaleResponse = GetRecsysOpportunityRationaleResponses[keyof GetRecsysOpportunityRationaleResponses];

export type PostRecsysOpportunityReviewedData = {
    body: RecsysOpportunityReviewPayload;
    path: {
        /**
         * Pipeline Uuid
         */
        pipeline_uuid: string;
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
    };
    query?: never;
    url: '/api/v2/candidates/{candidate_uuid}/recsys-opportunities/{pipeline_uuid}/reviewed';
};

export type PostRecsysOpportunityReviewedErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostRecsysOpportunityReviewedError = PostRecsysOpportunityReviewedErrors[keyof PostRecsysOpportunityReviewedErrors];

export type PostRecsysOpportunityReviewedResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type PostRecsysOpportunityReviewedResponse = PostRecsysOpportunityReviewedResponses[keyof PostRecsysOpportunityReviewedResponses];

export type PostRecsysOpportunityBadMatchData = {
    body: RecsysOpportunityBadMatchPayload;
    path: {
        /**
         * Pipeline Uuid
         */
        pipeline_uuid: string;
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
    };
    query?: never;
    url: '/api/v2/candidates/{candidate_uuid}/recsys-opportunities/{pipeline_uuid}/bad';
};

export type PostRecsysOpportunityBadMatchErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostRecsysOpportunityBadMatchError = PostRecsysOpportunityBadMatchErrors[keyof PostRecsysOpportunityBadMatchErrors];

export type PostRecsysOpportunityBadMatchResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type PostRecsysOpportunityBadMatchResponse = PostRecsysOpportunityBadMatchResponses[keyof PostRecsysOpportunityBadMatchResponses];

export type PostCandidatesTrendingFilterData = {
    body: TrendingCandidateFilterSortPayload;
    path?: never;
    query?: never;
    url: '/api/v2/candidates/trending/filter';
};

export type PostCandidatesTrendingFilterErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostCandidatesTrendingFilterError = PostCandidatesTrendingFilterErrors[keyof PostCandidatesTrendingFilterErrors];

export type PostCandidatesTrendingFilterResponses = {
    /**
     * Successful Response
     */
    200: TrendingCandidatesPaginatedResponse;
};

export type PostCandidatesTrendingFilterResponse = PostCandidatesTrendingFilterResponses[keyof PostCandidatesTrendingFilterResponses];

export type PostCompaniesSearchV2Data = {
    body?: CompaniesSearchFilterSortPayload;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Page number
         */
        page?: number;
        /**
         * Size
         *
         * Page size
         */
        size?: number;
    };
    url: '/api/v2/companies/search';
};

export type PostCompaniesSearchV2Errors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostCompaniesSearchV2Error = PostCompaniesSearchV2Errors[keyof PostCompaniesSearchV2Errors];

export type PostCompaniesSearchV2Responses = {
    /**
     * Successful Response
     */
    200: PostPageCompanyDetailedResponse;
};

export type PostCompaniesSearchV2Response = PostCompaniesSearchV2Responses[keyof PostCompaniesSearchV2Responses];

export type PostCompanyTeamsData = {
    body: CompanyTeamCreate;
    path: {
        /**
         * Company Slug
         */
        company_slug: string;
    };
    query?: never;
    url: '/api/v2/companies/{company_slug}/teams';
};

export type PostCompanyTeamsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostCompanyTeamsError = PostCompanyTeamsErrors[keyof PostCompanyTeamsErrors];

export type PostCompanyTeamsResponses = {
    /**
     * Successful Response
     */
    200: TeamExtendedResponse;
};

export type PostCompanyTeamsResponse = PostCompanyTeamsResponses[keyof PostCompanyTeamsResponses];

export type DeleteCommentData = {
    body?: never;
    path: {
        /**
         * Comment Uuid
         */
        comment_uuid: string;
    };
    query?: never;
    url: '/api/v2/comments/{comment_uuid}';
};

export type DeleteCommentErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type DeleteCommentError = DeleteCommentErrors[keyof DeleteCommentErrors];

export type DeleteCommentResponses = {
    /**
     * Successful Response
     */
    200: CommentResponse;
};

export type DeleteCommentResponse = DeleteCommentResponses[keyof DeleteCommentResponses];

export type PutCommentData = {
    body: CommentPayload;
    path: {
        /**
         * Comment Uuid
         */
        comment_uuid: string;
    };
    query?: never;
    url: '/api/v2/comments/{comment_uuid}';
};

export type PutCommentErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PutCommentError = PutCommentErrors[keyof PutCommentErrors];

export type PutCommentResponses = {
    /**
     * Successful Response
     */
    200: CommentResponse;
};

export type PutCommentResponse = PutCommentResponses[keyof PutCommentResponses];

export type DeleteInternalCommentData = {
    body?: never;
    path: {
        /**
         * Comment Uuid
         */
        comment_uuid: string;
    };
    query?: never;
    url: '/api/v2/internal-comments/{comment_uuid}';
};

export type DeleteInternalCommentErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type DeleteInternalCommentError = DeleteInternalCommentErrors[keyof DeleteInternalCommentErrors];

export type DeleteInternalCommentResponses = {
    /**
     * Successful Response
     */
    200: InternalCommentResponse;
};

export type DeleteInternalCommentResponse = DeleteInternalCommentResponses[keyof DeleteInternalCommentResponses];

export type PutInternalCommentData = {
    body: CommentPayload;
    path: {
        /**
         * Comment Uuid
         */
        comment_uuid: string;
    };
    query?: never;
    url: '/api/v2/internal-comments/{comment_uuid}';
};

export type PutInternalCommentErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PutInternalCommentError = PutInternalCommentErrors[keyof PutInternalCommentErrors];

export type PutInternalCommentResponses = {
    /**
     * Successful Response
     */
    200: InternalCommentResponse;
};

export type PutInternalCommentResponse = PutInternalCommentResponses[keyof PutInternalCommentResponses];

export type PutEvaluationHistoryIntroductionData = {
    body: IntroductionSubjectPayload;
    path: {
        /**
         * Evaluation History Uuid
         */
        evaluation_history_uuid: string;
    };
    query?: never;
    url: '/api/v2/evaluation-history/{evaluation_history_uuid}/introduction';
};

export type PutEvaluationHistoryIntroductionErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PutEvaluationHistoryIntroductionError = PutEvaluationHistoryIntroductionErrors[keyof PutEvaluationHistoryIntroductionErrors];

export type PutEvaluationHistoryIntroductionResponses = {
    /**
     * Response Put Evaluation History Introduction
     *
     * Successful Response
     */
    200: {
        [key: string]: unknown;
    };
};

export type PutEvaluationHistoryIntroductionResponse = PutEvaluationHistoryIntroductionResponses[keyof PutEvaluationHistoryIntroductionResponses];

export type RevertEvaluationHistoryData = {
    body?: never;
    path: {
        /**
         * Evaluation History Uuid
         */
        evaluation_history_uuid: string;
    };
    query?: never;
    url: '/api/v2/evaluation-history/{evaluation_history_uuid}/revert';
};

export type RevertEvaluationHistoryErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type RevertEvaluationHistoryError = RevertEvaluationHistoryErrors[keyof RevertEvaluationHistoryErrors];

export type RevertEvaluationHistoryResponses = {
    /**
     * Response Revert Evaluation History
     *
     * Successful Response
     */
    200: {
        [key: string]: unknown;
    };
};

export type RevertEvaluationHistoryResponse = RevertEvaluationHistoryResponses[keyof RevertEvaluationHistoryResponses];

export type DeleteHighlightData = {
    body?: never;
    path: {
        /**
         * Highlight Uuid
         */
        highlight_uuid: string;
    };
    query?: never;
    url: '/api/v2/highlights/{highlight_uuid}';
};

export type DeleteHighlightErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type DeleteHighlightError = DeleteHighlightErrors[keyof DeleteHighlightErrors];

export type DeleteHighlightResponses = {
    /**
     * Successful Response
     */
    200: DeleteHighlightResponse;
};

export type DeleteHighlightResponse2 = DeleteHighlightResponses[keyof DeleteHighlightResponses];

export type PutHighlightData = {
    body: CandidateHighlightCreate;
    path: {
        /**
         * Highlight Uuid
         */
        highlight_uuid: string;
    };
    query?: never;
    url: '/api/v2/highlights/{highlight_uuid}';
};

export type PutHighlightErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PutHighlightError = PutHighlightErrors[keyof PutHighlightErrors];

export type PutHighlightResponses = {
    /**
     * Successful Response
     */
    200: PutHighlightResponse;
};

export type PutHighlightResponse2 = PutHighlightResponses[keyof PutHighlightResponses];

export type DeleteIntroductionTemplateData = {
    body?: never;
    path: {
        /**
         * Template Uuid
         */
        template_uuid: string;
    };
    query?: never;
    url: '/api/v2/introduction-templates/{template_uuid}';
};

export type DeleteIntroductionTemplateErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type DeleteIntroductionTemplateError = DeleteIntroductionTemplateErrors[keyof DeleteIntroductionTemplateErrors];

export type DeleteIntroductionTemplateResponses = {
    /**
     * Successful Response
     */
    200: CandidateIntroductionTemplateResponse;
};

export type DeleteIntroductionTemplateResponse = DeleteIntroductionTemplateResponses[keyof DeleteIntroductionTemplateResponses];

export type PutIntroductionTemplateData = {
    body: PutIntroductionTemplatePayload;
    path: {
        /**
         * Template Uuid
         */
        template_uuid: string;
    };
    query?: never;
    url: '/api/v2/introduction-templates/{template_uuid}';
};

export type PutIntroductionTemplateErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PutIntroductionTemplateError = PutIntroductionTemplateErrors[keyof PutIntroductionTemplateErrors];

export type PutIntroductionTemplateResponses = {
    /**
     * Successful Response
     */
    200: CandidateIntroductionTemplateResponse;
};

export type PutIntroductionTemplateResponse = PutIntroductionTemplateResponses[keyof PutIntroductionTemplateResponses];

export type PostLinkedinSyncCandidateData = {
    /**
     * Body
     */
    body?: LinkedInSyncRequest | null;
    path: {
        /**
         * Candidate Id
         */
        candidate_id: number | string;
    };
    query?: never;
    url: '/api/v2/linkedin_sync/candidates/{candidate_id}';
};

export type PostLinkedinSyncCandidateErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostLinkedinSyncCandidateError = PostLinkedinSyncCandidateErrors[keyof PostLinkedinSyncCandidateErrors];

export type PostLinkedinSyncCandidateResponses = {
    /**
     * Successful Response
     */
    200: LinkedInResumeUploadResponseOutput;
    /**
     * No Content
     */
    204: void;
};

export type PostLinkedinSyncCandidateResponse = PostLinkedinSyncCandidateResponses[keyof PostLinkedinSyncCandidateResponses];

export type PostLinkedinPreviewData = {
    body: LinkedInPreviewRequest;
    path?: never;
    query?: never;
    url: '/api/v2/linkedin_sync/preview';
};

export type PostLinkedinPreviewErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostLinkedinPreviewError = PostLinkedinPreviewErrors[keyof PostLinkedinPreviewErrors];

export type PostLinkedinPreviewResponses = {
    /**
     * Successful Response
     */
    200: LinkedInPreviewResponse;
};

export type PostLinkedinPreviewResponse = PostLinkedinPreviewResponses[keyof PostLinkedinPreviewResponses];

export type PostAutotagPreviewData = {
    body: AutotagPreviewRequest;
    path?: never;
    query?: never;
    url: '/api/v2/candidates/autotag/preview';
};

export type PostAutotagPreviewErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostAutotagPreviewError = PostAutotagPreviewErrors[keyof PostAutotagPreviewErrors];

export type PostAutotagPreviewResponses = {
    /**
     * Successful Response
     */
    202: AutotagPreviewQueued;
};

export type PostAutotagPreviewResponse = PostAutotagPreviewResponses[keyof PostAutotagPreviewResponses];

export type GetAutotagPreviewStatusData = {
    body?: never;
    path: {
        /**
         * Task Id
         */
        task_id: string;
    };
    query?: never;
    url: '/api/v2/candidates/autotag/preview/status/{task_id}';
};

export type GetAutotagPreviewStatusErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetAutotagPreviewStatusError = GetAutotagPreviewStatusErrors[keyof GetAutotagPreviewStatusErrors];

export type GetAutotagPreviewStatusResponses = {
    /**
     * Successful Response
     */
    200: AutotagPreviewStatus;
};

export type GetAutotagPreviewStatusResponse = GetAutotagPreviewStatusResponses[keyof GetAutotagPreviewStatusResponses];

export type PostPipelinesFilterData = {
    body: PipelinesSearchData;
    path?: never;
    query?: never;
    url: '/api/v2/pipelines/filter';
};

export type PostPipelinesFilterErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostPipelinesFilterError = PostPipelinesFilterErrors[keyof PostPipelinesFilterErrors];

export type PostPipelinesFilterResponses = {
    /**
     * Successful Response
     */
    200: PaginatedPipelinesTableResponse;
};

export type PostPipelinesFilterResponse = PostPipelinesFilterResponses[keyof PostPipelinesFilterResponses];

export type PutMultipleRecommendationDraftsData = {
    body: UpdateMultipleRecommendationDraftPayload;
    path?: never;
    query?: never;
    url: '/api/v2/recommendations/drafts';
};

export type PutMultipleRecommendationDraftsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PutMultipleRecommendationDraftsError = PutMultipleRecommendationDraftsErrors[keyof PutMultipleRecommendationDraftsErrors];

export type PutMultipleRecommendationDraftsResponses = {
    /**
     * Response Put Multiple Recommendation Drafts
     *
     * Successful Response
     */
    200: Array<RecommendationExternalFullResponse>;
};

export type PutMultipleRecommendationDraftsResponse = PutMultipleRecommendationDraftsResponses[keyof PutMultipleRecommendationDraftsResponses];

export type DeleteRecommendationData = {
    body?: never;
    path: {
        /**
         * Recommendation Uuid
         */
        recommendation_uuid: string;
    };
    query?: never;
    url: '/api/v2/recommendations/{recommendation_uuid}';
};

export type DeleteRecommendationErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type DeleteRecommendationError = DeleteRecommendationErrors[keyof DeleteRecommendationErrors];

export type DeleteRecommendationResponses = {
    /**
     * Successful Response
     */
    200: RecommendationExternalFullResponse;
};

export type DeleteRecommendationResponse = DeleteRecommendationResponses[keyof DeleteRecommendationResponses];

export type GetRecommendationData = {
    body?: never;
    path: {
        /**
         * Recommendation Uuid
         */
        recommendation_uuid: string;
    };
    query?: never;
    url: '/api/v2/recommendations/{recommendation_uuid}';
};

export type GetRecommendationErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetRecommendationError = GetRecommendationErrors[keyof GetRecommendationErrors];

export type GetRecommendationResponses = {
    /**
     * Successful Response
     */
    200: RecommendationExternalFullResponse;
};

export type GetRecommendationResponse = GetRecommendationResponses[keyof GetRecommendationResponses];

export type PutRecommendationCommentaryData = {
    body: RecommendationCommentaryPayload;
    path: {
        /**
         * Recommendation Uuid
         */
        recommendation_uuid: string;
    };
    query?: never;
    url: '/api/v2/recommendations/{recommendation_uuid}/commentary';
};

export type PutRecommendationCommentaryErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PutRecommendationCommentaryError = PutRecommendationCommentaryErrors[keyof PutRecommendationCommentaryErrors];

export type PutRecommendationCommentaryResponses = {
    /**
     * Successful Response
     */
    200: RecommendationExternalFullResponse;
};

export type PutRecommendationCommentaryResponse = PutRecommendationCommentaryResponses[keyof PutRecommendationCommentaryResponses];

export type PutRecommendationStatusV2Data = {
    body: IntroductionStatusPayload;
    path: {
        /**
         * Recommendation Uuid
         */
        recommendation_uuid: string;
    };
    query?: never;
    url: '/api/v2/recommendations/{recommendation_uuid}/status';
};

export type PutRecommendationStatusV2Errors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PutRecommendationStatusV2Error = PutRecommendationStatusV2Errors[keyof PutRecommendationStatusV2Errors];

export type PutRecommendationStatusV2Responses = {
    /**
     * Successful Response
     */
    200: RecommendationExternalFullResponse;
};

export type PutRecommendationStatusV2Response = PutRecommendationStatusV2Responses[keyof PutRecommendationStatusV2Responses];

export type DeleteRecommendationAssigneeData = {
    body?: never;
    path: {
        /**
         * Recommendation Uuid
         */
        recommendation_uuid: string;
    };
    query?: never;
    url: '/api/v2/recommendations/{recommendation_uuid}/assignee';
};

export type DeleteRecommendationAssigneeErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type DeleteRecommendationAssigneeError = DeleteRecommendationAssigneeErrors[keyof DeleteRecommendationAssigneeErrors];

export type DeleteRecommendationAssigneeResponses = {
    /**
     * Successful Response
     */
    200: RecommendationExternalFullResponse;
};

export type DeleteRecommendationAssigneeResponse = DeleteRecommendationAssigneeResponses[keyof DeleteRecommendationAssigneeResponses];

export type PostRecsysRecommendationsData = {
    body: RecsysSuggestedRecommendations;
    path?: never;
    query?: never;
    url: '/api/v2/integrations/recsys/recommendations';
};

export type PostRecsysRecommendationsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostRecsysRecommendationsError = PostRecsysRecommendationsErrors[keyof PostRecsysRecommendationsErrors];

export type PostRecsysRecommendationsResponses = {
    /**
     * Successful Response
     */
    200: RecsysSuggestedRecommendationsResponse;
};

export type PostRecsysRecommendationsResponse = PostRecsysRecommendationsResponses[keyof PostRecsysRecommendationsResponses];

export type PostRecsysCandidateContextData = {
    body: RecsysCandidateContextRequest;
    path?: never;
    query?: never;
    url: '/api/v2/integrations/recsys/candidate-team-context';
};

export type PostRecsysCandidateContextErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostRecsysCandidateContextError = PostRecsysCandidateContextErrors[keyof PostRecsysCandidateContextErrors];

export type PostRecsysCandidateContextResponses = {
    /**
     * Successful Response
     */
    200: RecsysCandidateContextResponse;
};

export type PostRecsysCandidateContextResponse = PostRecsysCandidateContextResponses[keyof PostRecsysCandidateContextResponses];

export type GetRecsysAssigneesData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v2/integrations/recsys/assignees';
};

export type GetRecsysAssigneesResponses = {
    /**
     * Successful Response
     */
    200: RecsysAssigneesResponse;
};

export type GetRecsysAssigneesResponse = GetRecsysAssigneesResponses[keyof GetRecsysAssigneesResponses];

export type PostSubmissionsFilterData = {
    body: SubmissionSearchFilterSortPayload;
    path?: never;
    query?: never;
    url: '/api/v2/submissions/filter';
};

export type PostSubmissionsFilterErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostSubmissionsFilterError = PostSubmissionsFilterErrors[keyof PostSubmissionsFilterErrors];

export type PostSubmissionsFilterResponses = {
    /**
     * Successful Response
     */
    200: SubmissionsSearchFilterSortPaginatedResponse;
};

export type PostSubmissionsFilterResponse = PostSubmissionsFilterResponses[keyof PostSubmissionsFilterResponses];

export type PostSubmissionFilterData = {
    body: SubmissionFilterPayload;
    path: {
        /**
         * Submission Uuid
         */
        submission_uuid: string;
    };
    query?: never;
    url: '/api/v2/submissions/{submission_uuid}/filter';
};

export type PostSubmissionFilterErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostSubmissionFilterError = PostSubmissionFilterErrors[keyof PostSubmissionFilterErrors];

export type PostSubmissionFilterResponses = {
    /**
     * Successful Response
     */
    200: SubmissionFilterResponse;
};

export type PostSubmissionFilterResponse = PostSubmissionFilterResponses[keyof PostSubmissionFilterResponses];

export type PostTeamsV2Data = {
    body: TeamAndCompanyCreate;
    path?: never;
    query?: never;
    url: '/api/v2/teams';
};

export type PostTeamsV2Errors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostTeamsV2Error = PostTeamsV2Errors[keyof PostTeamsV2Errors];

export type PostTeamsV2Responses = {
    /**
     * Successful Response
     */
    200: TeamExtendedResponse;
};

export type PostTeamsV2Response = PostTeamsV2Responses[keyof PostTeamsV2Responses];

export type PutTeamData = {
    body: TeamUpdateV2;
    path: {
        /**
         * Team Uuid
         */
        team_uuid: string;
    };
    query?: never;
    url: '/api/v2/teams/{team_uuid}';
};

export type PutTeamErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PutTeamError = PutTeamErrors[keyof PutTeamErrors];

export type PutTeamResponses = {
    /**
     * Successful Response
     */
    200: TeamExtendedResponse;
};

export type PutTeamResponse = PutTeamResponses[keyof PutTeamResponses];

export type PostTeamsFilterData = {
    body: TeamsSearchFilterPayload;
    path?: never;
    query?: never;
    url: '/api/v2/teams/filter';
};

export type PostTeamsFilterErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostTeamsFilterError = PostTeamsFilterErrors[keyof PostTeamsFilterErrors];

export type PostTeamsFilterResponses = {
    /**
     * Successful Response
     */
    200: TeamsSearchFilterPaginatedResponse;
};

export type PostTeamsFilterResponse = PostTeamsFilterResponses[keyof PostTeamsFilterResponses];

export type PostTeamsSuggestedData = {
    body: TeamsSuggestedPayload;
    path?: never;
    query?: never;
    url: '/api/v2/teams/suggested';
};

export type PostTeamsSuggestedErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostTeamsSuggestedError = PostTeamsSuggestedErrors[keyof PostTeamsSuggestedErrors];

export type PostTeamsSuggestedResponses = {
    /**
     * Successful Response
     */
    200: TeamsSuggestedResponse;
};

export type PostTeamsSuggestedResponse = PostTeamsSuggestedResponses[keyof PostTeamsSuggestedResponses];

export type PostTeamTeamsData = {
    body: TeamCreateV2;
    path: {
        /**
         * Team Uuid
         */
        team_uuid: string;
    };
    query?: never;
    url: '/api/v2/teams/{team_uuid}/teams';
};

export type PostTeamTeamsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostTeamTeamsError = PostTeamTeamsErrors[keyof PostTeamTeamsErrors];

export type PostTeamTeamsResponses = {
    /**
     * Successful Response
     */
    200: TeamExtendedResponse;
};

export type PostTeamTeamsResponse = PostTeamTeamsResponses[keyof PostTeamTeamsResponses];

export type GetTeamCandidateData = {
    body?: never;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
        /**
         * Team Uuid
         */
        team_uuid: string;
    };
    query?: never;
    url: '/api/v2/teams/{team_uuid}/candidates/{candidate_uuid}';
};

export type GetTeamCandidateErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetTeamCandidateError = GetTeamCandidateErrors[keyof GetTeamCandidateErrors];

export type GetTeamCandidateResponses = {
    /**
     * Successful Response
     */
    200: CandidateExternalFullResponseWithTeamContext;
};

export type GetTeamCandidateResponse = GetTeamCandidateResponses[keyof GetTeamCandidateResponses];

export type GetTeamCandidateActivityData = {
    body?: never;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
        /**
         * Team Uuid
         */
        team_uuid: string;
    };
    query?: {
        /**
         * Team Uuids
         *
         * Return info for specified teams only
         */
        team_uuids?: Array<string> | null;
        /**
         * Page
         *
         * Page number
         */
        page?: number;
        /**
         * Size
         *
         * Page size
         */
        size?: number;
    };
    url: '/api/v2/teams/{team_uuid}/candidates/{candidate_uuid}/activity';
};

export type GetTeamCandidateActivityErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetTeamCandidateActivityError = GetTeamCandidateActivityErrors[keyof GetTeamCandidateActivityErrors];

export type GetTeamCandidateActivityResponses = {
    /**
     * Successful Response
     */
    200: PageCandidateActivityEvent;
};

export type GetTeamCandidateActivityResponse = GetTeamCandidateActivityResponses[keyof GetTeamCandidateActivityResponses];

export type PostTeamCandidateFavoriteData = {
    body: CandidateFavoritePayload;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
        /**
         * Team Uuid
         */
        team_uuid: string;
    };
    query?: never;
    url: '/api/v2/teams/{team_uuid}/candidates/{candidate_uuid}/favorite';
};

export type PostTeamCandidateFavoriteErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostTeamCandidateFavoriteError = PostTeamCandidateFavoriteErrors[keyof PostTeamCandidateFavoriteErrors];

export type PostTeamCandidateFavoriteResponses = {
    /**
     * Successful Response
     */
    200: CandidateExternalResponse;
};

export type PostTeamCandidateFavoriteResponse = PostTeamCandidateFavoriteResponses[keyof PostTeamCandidateFavoriteResponses];

export type PostTeamCandidatePipelinesData = {
    body: TeamCandidatePipelinesPayload;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
        /**
         * Team Uuid
         */
        team_uuid: string;
    };
    query?: never;
    url: '/api/v2/teams/{team_uuid}/candidates/{candidate_uuid}/pipelines';
};

export type PostTeamCandidatePipelinesErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostTeamCandidatePipelinesError = PostTeamCandidatePipelinesErrors[keyof PostTeamCandidatePipelinesErrors];

export type PostTeamCandidatePipelinesResponses = {
    /**
     * Successful Response
     */
    200: SubmissionCandidatePipelinesResponse;
};

export type PostTeamCandidatePipelinesResponse = PostTeamCandidatePipelinesResponses[keyof PostTeamCandidatePipelinesResponses];

export type GetTeamCandidateRecommendationsData = {
    body?: never;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
        /**
         * Team Uuid
         */
        team_uuid: string;
    };
    query?: {
        /**
         * Team Uuids
         *
         * Return info for specified teams only
         */
        team_uuids?: Array<string> | null;
    };
    url: '/api/v2/teams/{team_uuid}/candidates/{candidate_uuid}/recommendations';
};

export type GetTeamCandidateRecommendationsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetTeamCandidateRecommendationsError = GetTeamCandidateRecommendationsErrors[keyof GetTeamCandidateRecommendationsErrors];

export type GetTeamCandidateRecommendationsResponses = {
    /**
     * Response Get Team Candidate Recommendations
     *
     * Successful Response
     */
    200: Array<RecommendationPipelinesResponse>;
};

export type GetTeamCandidateRecommendationsResponse = GetTeamCandidateRecommendationsResponses[keyof GetTeamCandidateRecommendationsResponses];

export type PostTeamCandidateStatusData = {
    body: IntroductionStatusPayload;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
        /**
         * Team Uuid
         */
        team_uuid: string;
    };
    query?: never;
    url: '/api/v2/teams/{team_uuid}/candidates/{candidate_uuid}/status';
};

export type PostTeamCandidateStatusErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostTeamCandidateStatusError = PostTeamCandidateStatusErrors[keyof PostTeamCandidateStatusErrors];

export type PostTeamCandidateStatusResponses = {
    /**
     * Successful Response
     */
    200: ActivityEventResponse;
};

export type PostTeamCandidateStatusResponse = PostTeamCandidateStatusResponses[keyof PostTeamCandidateStatusResponses];

export type PutTeamCandidateStatusData = {
    body: IntroductionStatusPayload;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
        /**
         * Team Uuid
         */
        team_uuid: string;
    };
    query?: never;
    url: '/api/v2/teams/{team_uuid}/candidates/{candidate_uuid}/status';
};

export type PutTeamCandidateStatusErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PutTeamCandidateStatusError = PutTeamCandidateStatusErrors[keyof PutTeamCandidateStatusErrors];

export type PutTeamCandidateStatusResponses = {
    /**
     * Successful Response
     */
    200: ActivityEventResponse;
};

export type PutTeamCandidateStatusResponse = PutTeamCandidateStatusResponses[keyof PutTeamCandidateStatusResponses];

export type PostTeamCandidateViewsData = {
    /**
     * Payload
     */
    body?: CandidateViewPayload | null;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
        /**
         * Team Uuid
         */
        team_uuid: string;
    };
    query?: never;
    url: '/api/v2/teams/{team_uuid}/candidates/{candidate_uuid}/views';
};

export type PostTeamCandidateViewsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostTeamCandidateViewsError = PostTeamCandidateViewsErrors[keyof PostTeamCandidateViewsErrors];

export type PostTeamCandidateViewsResponses = {
    /**
     * Successful Response
     */
    200: unknown;
};

export type GetTeamContactCommentsData = {
    body?: never;
    path: {
        /**
         * Identity Uuid
         */
        identity_uuid: string;
        /**
         * Team Uuid
         */
        team_uuid: string;
    };
    query?: never;
    url: '/api/v2/teams/{team_uuid}/contacts/{identity_uuid}/comments';
};

export type GetTeamContactCommentsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetTeamContactCommentsError = GetTeamContactCommentsErrors[keyof GetTeamContactCommentsErrors];

export type GetTeamContactCommentsResponses = {
    /**
     * Response Get Team Contact Comments
     *
     * Successful Response
     */
    200: Array<CommentResponse>;
};

export type GetTeamContactCommentsResponse = GetTeamContactCommentsResponses[keyof GetTeamContactCommentsResponses];

export type PostTeamContactCommentData = {
    body: CommentPayload;
    path: {
        /**
         * Identity Uuid
         */
        identity_uuid: string;
        /**
         * Team Uuid
         */
        team_uuid: string;
    };
    query?: never;
    url: '/api/v2/teams/{team_uuid}/contacts/{identity_uuid}/comments';
};

export type PostTeamContactCommentErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostTeamContactCommentError = PostTeamContactCommentErrors[keyof PostTeamContactCommentErrors];

export type PostTeamContactCommentResponses = {
    /**
     * Successful Response
     */
    200: CommentResponse;
};

export type PostTeamContactCommentResponse = PostTeamContactCommentResponses[keyof PostTeamContactCommentResponses];

export type DeleteTeamContactCommentData = {
    body?: never;
    path: {
        /**
         * Comment Uuid
         */
        comment_uuid: string;
        /**
         * Identity Uuid
         */
        identity_uuid: string;
        /**
         * Team Uuid
         */
        team_uuid: string;
    };
    query?: never;
    url: '/api/v2/teams/{team_uuid}/contacts/{identity_uuid}/comments/{comment_uuid}';
};

export type DeleteTeamContactCommentErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type DeleteTeamContactCommentError = DeleteTeamContactCommentErrors[keyof DeleteTeamContactCommentErrors];

export type DeleteTeamContactCommentResponses = {
    /**
     * Successful Response
     */
    200: CommentResponse;
};

export type DeleteTeamContactCommentResponse = DeleteTeamContactCommentResponses[keyof DeleteTeamContactCommentResponses];

export type PutTeamContactCommentData = {
    body: CommentPayload;
    path: {
        /**
         * Comment Uuid
         */
        comment_uuid: string;
        /**
         * Identity Uuid
         */
        identity_uuid: string;
        /**
         * Team Uuid
         */
        team_uuid: string;
    };
    query?: never;
    url: '/api/v2/teams/{team_uuid}/contacts/{identity_uuid}/comments/{comment_uuid}';
};

export type PutTeamContactCommentErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PutTeamContactCommentError = PutTeamContactCommentErrors[keyof PutTeamContactCommentErrors];

export type PutTeamContactCommentResponses = {
    /**
     * Successful Response
     */
    200: CommentResponse;
};

export type PutTeamContactCommentResponse = PutTeamContactCommentResponses[keyof PutTeamContactCommentResponses];

export type ListCompanyContactTagsData = {
    body?: never;
    path: {
        /**
         * Company Id
         */
        company_id: number;
    };
    query?: {
        /**
         * Include Deleted
         */
        include_deleted?: boolean;
        /**
         * Include Usage Count
         */
        include_usage_count?: boolean;
    };
    url: '/api/v2/companies/{company_id}/contacts/tags';
};

export type ListCompanyContactTagsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type ListCompanyContactTagsError = ListCompanyContactTagsErrors[keyof ListCompanyContactTagsErrors];

export type ListCompanyContactTagsResponses = {
    /**
     * Response List Company Contact Tags
     *
     * Successful Response
     */
    200: Array<CompanyTagDefinitionResponse>;
};

export type ListCompanyContactTagsResponse = ListCompanyContactTagsResponses[keyof ListCompanyContactTagsResponses];

export type AddCompanyContactTagData = {
    body: CompanyTagAssociationCreatePayload;
    path: {
        /**
         * Company Id
         */
        company_id: number;
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
    };
    query?: never;
    url: '/api/v2/companies/{company_id}/contacts/{candidate_uuid}/tags';
};

export type AddCompanyContactTagErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type AddCompanyContactTagError = AddCompanyContactTagErrors[keyof AddCompanyContactTagErrors];

export type AddCompanyContactTagResponses = {
    /**
     * Successful Response
     */
    201: CompanyTagOnContactResponse;
};

export type AddCompanyContactTagResponse = AddCompanyContactTagResponses[keyof AddCompanyContactTagResponses];

export type RemoveCompanyContactTagData = {
    body?: never;
    path: {
        /**
         * Company Id
         */
        company_id: number;
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
        /**
         * Tag Definition Uuid
         */
        tag_definition_uuid: string;
    };
    query?: never;
    url: '/api/v2/companies/{company_id}/contacts/{candidate_uuid}/tags/{tag_definition_uuid}';
};

export type RemoveCompanyContactTagErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type RemoveCompanyContactTagError = RemoveCompanyContactTagErrors[keyof RemoveCompanyContactTagErrors];

export type RemoveCompanyContactTagResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type RemoveCompanyContactTagResponse = RemoveCompanyContactTagResponses[keyof RemoveCompanyContactTagResponses];

export type PostTeamPipelinesData = {
    body: CreatePipelinePayload;
    path: {
        /**
         * Team Uuid
         */
        team_uuid: string;
    };
    query?: never;
    url: '/api/v2/teams/{team_uuid}/pipelines';
};

export type PostTeamPipelinesErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostTeamPipelinesError = PostTeamPipelinesErrors[keyof PostTeamPipelinesErrors];

export type PostTeamPipelinesResponses = {
    /**
     * Successful Response
     */
    200: PipelineFullResponse;
};

export type PostTeamPipelinesResponse = PostTeamPipelinesResponses[keyof PostTeamPipelinesResponses];

export type GetTeamPipelineData = {
    body?: never;
    path: {
        /**
         * Pipeline Uuid
         */
        pipeline_uuid: string;
        /**
         * Team Uuid
         */
        team_uuid: string;
    };
    query?: never;
    url: '/api/v2/teams/{team_uuid}/pipelines/{pipeline_uuid}';
};

export type GetTeamPipelineErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetTeamPipelineError = GetTeamPipelineErrors[keyof GetTeamPipelineErrors];

export type GetTeamPipelineResponses = {
    /**
     * Successful Response
     */
    200: PipelineFullResponse;
};

export type GetTeamPipelineResponse = GetTeamPipelineResponses[keyof GetTeamPipelineResponses];

export type PutTeamPipelineData = {
    body: UpdatePipelinePayload;
    path: {
        /**
         * Pipeline Uuid
         */
        pipeline_uuid: string;
        /**
         * Team Uuid
         */
        team_uuid: string;
    };
    query?: never;
    url: '/api/v2/teams/{team_uuid}/pipelines/{pipeline_uuid}';
};

export type PutTeamPipelineErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PutTeamPipelineError = PutTeamPipelineErrors[keyof PutTeamPipelineErrors];

export type PutTeamPipelineResponses = {
    /**
     * Successful Response
     */
    200: PipelineFullResponse;
};

export type PutTeamPipelineResponse = PutTeamPipelineResponses[keyof PutTeamPipelineResponses];

export type PostTeamPipelineViewsData = {
    body?: never;
    path: {
        /**
         * Pipeline Uuid
         */
        pipeline_uuid: string;
        /**
         * Team Uuid
         */
        team_uuid: string;
    };
    query?: never;
    url: '/api/v2/teams/{team_uuid}/pipelines/{pipeline_uuid}/views';
};

export type PostTeamPipelineViewsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostTeamPipelineViewsError = PostTeamPipelineViewsErrors[keyof PostTeamPipelineViewsErrors];

export type PostTeamPipelineViewsResponses = {
    /**
     * Successful Response
     */
    200: unknown;
};

export type GetTeamPipelineCandidatesData = {
    body?: never;
    path: {
        /**
         * Pipeline Uuid
         */
        pipeline_uuid: string;
        /**
         * Team Uuid
         */
        team_uuid: string;
    };
    query?: {
        /**
         * Search Id
         */
        search_id?: string | null;
    };
    url: '/api/v2/teams/{team_uuid}/pipelines/{pipeline_uuid}/candidates';
};

export type GetTeamPipelineCandidatesErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetTeamPipelineCandidatesError = GetTeamPipelineCandidatesErrors[keyof GetTeamPipelineCandidatesErrors];

export type GetTeamPipelineCandidatesResponses = {
    /**
     * Successful Response
     */
    200: TeamPipelineCandidatesResponse;
};

export type GetTeamPipelineCandidatesResponse = GetTeamPipelineCandidatesResponses[keyof GetTeamPipelineCandidatesResponses];

export type PostPipelineCandidatesData = {
    body: PipelineCandidateSearchFilterSortPayload;
    path: {
        /**
         * Pipeline Uuid
         */
        pipeline_uuid: string;
        /**
         * Team Uuid
         */
        team_uuid: string;
    };
    query?: never;
    url: '/api/v2/teams/{team_uuid}/pipelines/{pipeline_uuid}/candidates';
};

export type PostPipelineCandidatesErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostPipelineCandidatesError = PostPipelineCandidatesErrors[keyof PostPipelineCandidatesErrors];

export type PostPipelineCandidatesResponses = {
    /**
     * Successful Response
     */
    200: TeamPipelineCandidatesSearchFilterSortResponse;
};

export type PostPipelineCandidatesResponse = PostPipelineCandidatesResponses[keyof PostPipelineCandidatesResponses];

export type GetTeamPipelineCandidatesOffLimitsData = {
    body?: never;
    path: {
        /**
         * Pipeline Uuid
         */
        pipeline_uuid: string;
        /**
         * Team Uuid
         */
        team_uuid: string;
    };
    query?: {
        /**
         * Candidate Uuids
         */
        candidate_uuids?: Array<string> | null;
    };
    url: '/api/v2/teams/{team_uuid}/pipelines/{pipeline_uuid}/candidates/off_limits';
};

export type GetTeamPipelineCandidatesOffLimitsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetTeamPipelineCandidatesOffLimitsError = GetTeamPipelineCandidatesOffLimitsErrors[keyof GetTeamPipelineCandidatesOffLimitsErrors];

export type GetTeamPipelineCandidatesOffLimitsResponses = {
    /**
     * Response Get Team Pipeline Candidates Off Limits
     *
     * Successful Response
     */
    200: Array<OffLimitsTeamSchema>;
};

export type GetTeamPipelineCandidatesOffLimitsResponse = GetTeamPipelineCandidatesOffLimitsResponses[keyof GetTeamPipelineCandidatesOffLimitsResponses];

export type PostTeamPipelineSubmissionData = {
    body: SubmissionDraftV2;
    path: {
        /**
         * Pipeline Uuid
         */
        pipeline_uuid: string;
        /**
         * Team Uuid
         */
        team_uuid: string;
    };
    query?: never;
    url: '/api/v2/teams/{team_uuid}/pipelines/{pipeline_uuid}/submissions';
};

export type PostTeamPipelineSubmissionErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostTeamPipelineSubmissionError = PostTeamPipelineSubmissionErrors[keyof PostTeamPipelineSubmissionErrors];

export type PostTeamPipelineSubmissionResponses = {
    /**
     * Response Post Team Pipeline Submission
     *
     * Successful Response
     */
    200: Array<SubmissionV3InternalResponse>;
};

export type PostTeamPipelineSubmissionResponse = PostTeamPipelineSubmissionResponses[keyof PostTeamPipelineSubmissionResponses];

export type PostTailoredHighlightsData = {
    body: TailoredHighlightsRequest;
    path: {
        /**
         * Pipeline Uuid
         */
        pipeline_uuid: string;
        /**
         * Team Uuid
         */
        team_uuid: string;
    };
    query?: never;
    url: '/api/v2/teams/{team_uuid}/pipelines/{pipeline_uuid}/tailored-highlights';
};

export type PostTailoredHighlightsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostTailoredHighlightsError = PostTailoredHighlightsErrors[keyof PostTailoredHighlightsErrors];

export type PostTailoredHighlightsResponses = {
    /**
     * Successful Response
     */
    202: TailoredHighlightsQueued;
};

export type PostTailoredHighlightsResponse = PostTailoredHighlightsResponses[keyof PostTailoredHighlightsResponses];

export type GetTailoredHighlightsStatusData = {
    body?: never;
    path: {
        /**
         * Task Id
         */
        task_id: string;
        /**
         * Pipeline Uuid
         */
        pipeline_uuid: string;
        /**
         * Team Uuid
         */
        team_uuid: string;
    };
    query?: never;
    url: '/api/v2/teams/{team_uuid}/pipelines/{pipeline_uuid}/tailored-highlights/status/{task_id}';
};

export type GetTailoredHighlightsStatusErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetTailoredHighlightsStatusError = GetTailoredHighlightsStatusErrors[keyof GetTailoredHighlightsStatusErrors];

export type GetTailoredHighlightsStatusResponses = {
    /**
     * Successful Response
     */
    200: TailoredHighlightsStatus;
};

export type GetTailoredHighlightsStatusResponse = GetTailoredHighlightsStatusResponses[keyof GetTailoredHighlightsStatusResponses];

export type GetTeamPipelineRecommendationData = {
    body?: never;
    path: {
        /**
         * Pipeline Uuid
         */
        pipeline_uuid: string;
        /**
         * Recommendation Uuid
         */
        recommendation_uuid: string;
        /**
         * Team Uuid
         */
        team_uuid: string;
    };
    query?: never;
    url: '/api/v2/teams/{team_uuid}/pipelines/{pipeline_uuid}/recommendations/{recommendation_uuid}';
};

export type GetTeamPipelineRecommendationErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetTeamPipelineRecommendationError = GetTeamPipelineRecommendationErrors[keyof GetTeamPipelineRecommendationErrors];

export type GetTeamPipelineRecommendationResponses = {
    /**
     * Successful Response
     */
    200: RecommendationExternalFullResponse;
};

export type GetTeamPipelineRecommendationResponse = GetTeamPipelineRecommendationResponses[keyof GetTeamPipelineRecommendationResponses];

export type PutTeamPipelineRecommendationIntroEmailTypeData = {
    body: RecommendationIntroEmailType;
    path: {
        /**
         * Pipeline Uuid
         */
        pipeline_uuid: string;
        /**
         * Recommendation Uuid
         */
        recommendation_uuid: string;
        /**
         * Team Uuid
         */
        team_uuid: string;
    };
    query?: never;
    url: '/api/v2/teams/{team_uuid}/pipelines/{pipeline_uuid}/recommendations/{recommendation_uuid}';
};

export type PutTeamPipelineRecommendationIntroEmailTypeErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PutTeamPipelineRecommendationIntroEmailTypeError = PutTeamPipelineRecommendationIntroEmailTypeErrors[keyof PutTeamPipelineRecommendationIntroEmailTypeErrors];

export type PutTeamPipelineRecommendationIntroEmailTypeResponses = {
    /**
     * Successful Response
     */
    200: RecommendationExternalFullResponse;
};

export type PutTeamPipelineRecommendationIntroEmailTypeResponse = PutTeamPipelineRecommendationIntroEmailTypeResponses[keyof PutTeamPipelineRecommendationIntroEmailTypeResponses];

export type PostTeamPipelineRecommendationCommentsData = {
    body: RecommendationCommentsPayload;
    path: {
        /**
         * Pipeline Uuid
         */
        pipeline_uuid: string;
        /**
         * Recommendation Uuid
         */
        recommendation_uuid: string;
        /**
         * Team Uuid
         */
        team_uuid: string;
    };
    query?: never;
    url: '/api/v2/teams/{team_uuid}/pipelines/{pipeline_uuid}/recommendations/{recommendation_uuid}/comments';
};

export type PostTeamPipelineRecommendationCommentsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostTeamPipelineRecommendationCommentsError = PostTeamPipelineRecommendationCommentsErrors[keyof PostTeamPipelineRecommendationCommentsErrors];

export type PostTeamPipelineRecommendationCommentsResponses = {
    /**
     * Successful Response
     */
    200: CommentResponse;
};

export type PostTeamPipelineRecommendationCommentsResponse = PostTeamPipelineRecommendationCommentsResponses[keyof PostTeamPipelineRecommendationCommentsResponses];

export type PostTeamPipelineRecommendationDislikeData = {
    body: RecommendationIsLiked;
    path: {
        /**
         * Pipeline Uuid
         */
        pipeline_uuid: string;
        /**
         * Recommendation Uuid
         */
        recommendation_uuid: string;
        /**
         * Team Uuid
         */
        team_uuid: string;
    };
    query?: never;
    url: '/api/v2/teams/{team_uuid}/pipelines/{pipeline_uuid}/recommendations/{recommendation_uuid}/dislike';
};

export type PostTeamPipelineRecommendationDislikeErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostTeamPipelineRecommendationDislikeError = PostTeamPipelineRecommendationDislikeErrors[keyof PostTeamPipelineRecommendationDislikeErrors];

export type PostTeamPipelineRecommendationDislikeResponses = {
    /**
     * Successful Response
     */
    200: RecommendationExternalFullResponse;
};

export type PostTeamPipelineRecommendationDislikeResponse = PostTeamPipelineRecommendationDislikeResponses[keyof PostTeamPipelineRecommendationDislikeResponses];

export type PostTeamPipelineRecommendationFavoriteData = {
    body: CandidateFavoritePayload;
    path: {
        /**
         * Pipeline Uuid
         */
        pipeline_uuid: string;
        /**
         * Recommendation Uuid
         */
        recommendation_uuid: string;
        /**
         * Team Uuid
         */
        team_uuid: string;
    };
    query?: never;
    url: '/api/v2/teams/{team_uuid}/pipelines/{pipeline_uuid}/recommendations/{recommendation_uuid}/favorite';
};

export type PostTeamPipelineRecommendationFavoriteErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostTeamPipelineRecommendationFavoriteError = PostTeamPipelineRecommendationFavoriteErrors[keyof PostTeamPipelineRecommendationFavoriteErrors];

export type PostTeamPipelineRecommendationFavoriteResponses = {
    /**
     * Successful Response
     */
    200: RecommendationExternalFullResponse;
};

export type PostTeamPipelineRecommendationFavoriteResponse = PostTeamPipelineRecommendationFavoriteResponses[keyof PostTeamPipelineRecommendationFavoriteResponses];

export type PostTeamPipelineRecommendationViewsData = {
    /**
     * Payload
     */
    body?: CandidateViewPayload | null;
    path: {
        /**
         * Pipeline Uuid
         */
        pipeline_uuid: string;
        /**
         * Recommendation Uuid
         */
        recommendation_uuid: string;
        /**
         * Team Uuid
         */
        team_uuid: string;
    };
    query?: never;
    url: '/api/v2/teams/{team_uuid}/pipelines/{pipeline_uuid}/recommendations/{recommendation_uuid}/views';
};

export type PostTeamPipelineRecommendationViewsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostTeamPipelineRecommendationViewsError = PostTeamPipelineRecommendationViewsErrors[keyof PostTeamPipelineRecommendationViewsErrors];

export type PostTeamPipelineRecommendationViewsResponses = {
    /**
     * Successful Response
     */
    200: unknown;
};

export type GetTeamPipelineRecommendationInternalCommentsData = {
    body?: never;
    path: {
        /**
         * Pipeline Uuid
         */
        pipeline_uuid: string;
        /**
         * Recommendation Uuid
         */
        recommendation_uuid: string;
        /**
         * Team Uuid
         */
        team_uuid: string;
    };
    query?: {
        /**
         * Page
         *
         * Page number
         */
        page?: number;
        /**
         * Size
         *
         * Page size
         */
        size?: number;
    };
    url: '/api/v2/teams/{team_uuid}/pipelines/{pipeline_uuid}/recommendations/{recommendation_uuid}/internal-comments';
};

export type GetTeamPipelineRecommendationInternalCommentsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetTeamPipelineRecommendationInternalCommentsError = GetTeamPipelineRecommendationInternalCommentsErrors[keyof GetTeamPipelineRecommendationInternalCommentsErrors];

export type GetTeamPipelineRecommendationInternalCommentsResponses = {
    /**
     * Successful Response
     */
    200: PageInternalCommentResponse;
};

export type GetTeamPipelineRecommendationInternalCommentsResponse = GetTeamPipelineRecommendationInternalCommentsResponses[keyof GetTeamPipelineRecommendationInternalCommentsResponses];

export type PostTeamPipelineRecommendationInternalCommentsData = {
    body: CommentPayload;
    path: {
        /**
         * Pipeline Uuid
         */
        pipeline_uuid: string;
        /**
         * Recommendation Uuid
         */
        recommendation_uuid: string;
        /**
         * Team Uuid
         */
        team_uuid: string;
    };
    query?: never;
    url: '/api/v2/teams/{team_uuid}/pipelines/{pipeline_uuid}/recommendations/{recommendation_uuid}/internal-comments';
};

export type PostTeamPipelineRecommendationInternalCommentsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostTeamPipelineRecommendationInternalCommentsError = PostTeamPipelineRecommendationInternalCommentsErrors[keyof PostTeamPipelineRecommendationInternalCommentsErrors];

export type PostTeamPipelineRecommendationInternalCommentsResponses = {
    /**
     * Successful Response
     */
    200: InternalCommentResponse;
};

export type PostTeamPipelineRecommendationInternalCommentsResponse = PostTeamPipelineRecommendationInternalCommentsResponses[keyof PostTeamPipelineRecommendationInternalCommentsResponses];

export type GetRecsysPipelineRecommendationsData = {
    body?: never;
    path: {
        /**
         * Pipeline Uuid
         */
        pipeline_uuid: string;
        /**
         * Team Uuid
         */
        team_uuid: string;
    };
    query?: {
        /**
         * Limit
         */
        limit?: number;
        /**
         * Offset
         */
        offset?: number;
    };
    url: '/api/v2/teams/{team_uuid}/pipelines/{pipeline_uuid}/recsys-recommendations';
};

export type GetRecsysPipelineRecommendationsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetRecsysPipelineRecommendationsError = GetRecsysPipelineRecommendationsErrors[keyof GetRecsysPipelineRecommendationsErrors];

export type GetRecsysPipelineRecommendationsResponses = {
    /**
     * Successful Response
     */
    200: RecsysPipelineRecommendationsResponse;
};

export type GetRecsysPipelineRecommendationsResponse = GetRecsysPipelineRecommendationsResponses[keyof GetRecsysPipelineRecommendationsResponses];

export type PostRecsysRecommendationFeedbackData = {
    body: RecsysFeedbackPayload;
    path: {
        /**
         * Recommendation Id
         */
        recommendation_id: string;
        /**
         * Pipeline Uuid
         */
        pipeline_uuid: string;
        /**
         * Team Uuid
         */
        team_uuid: string;
    };
    query?: never;
    url: '/api/v2/teams/{team_uuid}/pipelines/{pipeline_uuid}/recsys-recommendations/{recommendation_id}/feedback';
};

export type PostRecsysRecommendationFeedbackErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostRecsysRecommendationFeedbackError = PostRecsysRecommendationFeedbackErrors[keyof PostRecsysRecommendationFeedbackErrors];

export type PostRecsysRecommendationFeedbackResponses = {
    /**
     * Successful Response
     */
    201: unknown;
};

export type GetTeamTrendingData = {
    body?: never;
    path: {
        /**
         * Team Uuid
         */
        team_uuid: string;
    };
    query?: {
        /**
         * Team Uuids
         *
         * Return info for specified teams only
         */
        team_uuids?: Array<string> | null;
        /**
         * Search Id
         */
        search_id?: string | null;
    };
    url: '/api/v2/teams/{team_uuid}/trending';
};

export type GetTeamTrendingErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetTeamTrendingError = GetTeamTrendingErrors[keyof GetTeamTrendingErrors];

export type GetTeamTrendingResponses = {
    /**
     * Successful Response
     */
    200: TeamTrendingCandidatesResponse;
};

export type GetTeamTrendingResponse = GetTeamTrendingResponses[keyof GetTeamTrendingResponses];

export type GetTeamTrendingRecommendationData = {
    body?: never;
    path: {
        /**
         * Recommendation Uuid
         */
        recommendation_uuid: string;
        /**
         * Team Uuid
         */
        team_uuid: string;
    };
    query?: never;
    url: '/api/v2/teams/{team_uuid}/trending/{recommendation_uuid}';
};

export type GetTeamTrendingRecommendationErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetTeamTrendingRecommendationError = GetTeamTrendingRecommendationErrors[keyof GetTeamTrendingRecommendationErrors];

export type GetTeamTrendingRecommendationResponses = {
    /**
     * Successful Response
     */
    200: RecommendationExternalFullResponse;
};

export type GetTeamTrendingRecommendationResponse = GetTeamTrendingRecommendationResponses[keyof GetTeamTrendingRecommendationResponses];

export type GetTeamTrendingRecommendationActivityData = {
    body?: never;
    path: {
        /**
         * Recommendation Uuid
         */
        recommendation_uuid: string;
        /**
         * Team Uuid
         */
        team_uuid: string;
    };
    query?: {
        /**
         * Team Uuids
         *
         * Return info for specified teams only
         */
        team_uuids?: Array<string> | null;
        /**
         * Page
         *
         * Page number
         */
        page?: number;
        /**
         * Size
         *
         * Page size
         */
        size?: number;
    };
    url: '/api/v2/teams/{team_uuid}/trending/{recommendation_uuid}/activity';
};

export type GetTeamTrendingRecommendationActivityErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetTeamTrendingRecommendationActivityError = GetTeamTrendingRecommendationActivityErrors[keyof GetTeamTrendingRecommendationActivityErrors];

export type GetTeamTrendingRecommendationActivityResponses = {
    /**
     * Successful Response
     */
    200: PageCandidateActivityEvent;
};

export type GetTeamTrendingRecommendationActivityResponse = GetTeamTrendingRecommendationActivityResponses[keyof GetTeamTrendingRecommendationActivityResponses];

export type GetTeamTrendingRecommendationCommentsData = {
    body?: never;
    path: {
        /**
         * Team Uuid
         */
        team_uuid: string;
        /**
         * Recommendation Uuid
         */
        recommendation_uuid: string;
    };
    query?: {
        /**
         * Team Uuids
         *
         * Return info for specified teams only
         */
        team_uuids?: Array<string> | null;
        /**
         * Page
         *
         * Page number
         */
        page?: number;
        /**
         * Size
         *
         * Page size
         */
        size?: number;
    };
    url: '/api/v2/teams/{team_uuid}/trending/{recommendation_uuid}/comments';
};

export type GetTeamTrendingRecommendationCommentsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetTeamTrendingRecommendationCommentsError = GetTeamTrendingRecommendationCommentsErrors[keyof GetTeamTrendingRecommendationCommentsErrors];

export type GetTeamTrendingRecommendationCommentsResponses = {
    /**
     * Successful Response
     */
    200: PageCommentResponse;
};

export type GetTeamTrendingRecommendationCommentsResponse = GetTeamTrendingRecommendationCommentsResponses[keyof GetTeamTrendingRecommendationCommentsResponses];

export type PostTeamTrendingRecommendationCommentsData = {
    body: CommentPayload;
    path: {
        /**
         * Recommendation Uuid
         */
        recommendation_uuid: string;
        /**
         * Team Uuid
         */
        team_uuid: string;
    };
    query?: never;
    url: '/api/v2/teams/{team_uuid}/trending/{recommendation_uuid}/comments';
};

export type PostTeamTrendingRecommendationCommentsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostTeamTrendingRecommendationCommentsError = PostTeamTrendingRecommendationCommentsErrors[keyof PostTeamTrendingRecommendationCommentsErrors];

export type PostTeamTrendingRecommendationCommentsResponses = {
    /**
     * Successful Response
     */
    200: CommentResponse;
};

export type PostTeamTrendingRecommendationCommentsResponse = PostTeamTrendingRecommendationCommentsResponses[keyof PostTeamTrendingRecommendationCommentsResponses];

export type PostTeamTrendingRecommendationDislikeData = {
    body: RecommendationIsLiked;
    path: {
        /**
         * Recommendation Uuid
         */
        recommendation_uuid: string;
        /**
         * Team Uuid
         */
        team_uuid: string;
    };
    query?: never;
    url: '/api/v2/teams/{team_uuid}/trending/{recommendation_uuid}/dislike';
};

export type PostTeamTrendingRecommendationDislikeErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostTeamTrendingRecommendationDislikeError = PostTeamTrendingRecommendationDislikeErrors[keyof PostTeamTrendingRecommendationDislikeErrors];

export type PostTeamTrendingRecommendationDislikeResponses = {
    /**
     * Successful Response
     */
    200: RecommendationExternalFullResponse;
};

export type PostTeamTrendingRecommendationDislikeResponse = PostTeamTrendingRecommendationDislikeResponses[keyof PostTeamTrendingRecommendationDislikeResponses];

export type PostTeamTrendingRecommendationFavoriteData = {
    body: CandidateFavoritePayload;
    path: {
        /**
         * Recommendation Uuid
         */
        recommendation_uuid: string;
        /**
         * Team Uuid
         */
        team_uuid: string;
    };
    query?: never;
    url: '/api/v2/teams/{team_uuid}/trending/{recommendation_uuid}/favorite';
};

export type PostTeamTrendingRecommendationFavoriteErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostTeamTrendingRecommendationFavoriteError = PostTeamTrendingRecommendationFavoriteErrors[keyof PostTeamTrendingRecommendationFavoriteErrors];

export type PostTeamTrendingRecommendationFavoriteResponses = {
    /**
     * Successful Response
     */
    200: RecommendationExternalFullResponse;
};

export type PostTeamTrendingRecommendationFavoriteResponse = PostTeamTrendingRecommendationFavoriteResponses[keyof PostTeamTrendingRecommendationFavoriteResponses];

export type PostTeamTrendingRecommendationViewsData = {
    /**
     * Payload
     */
    body?: CandidateViewPayload | null;
    path: {
        /**
         * Recommendation Uuid
         */
        recommendation_uuid: string;
        /**
         * Team Uuid
         */
        team_uuid: string;
    };
    query?: never;
    url: '/api/v2/teams/{team_uuid}/trending/{recommendation_uuid}/views';
};

export type PostTeamTrendingRecommendationViewsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostTeamTrendingRecommendationViewsError = PostTeamTrendingRecommendationViewsErrors[keyof PostTeamTrendingRecommendationViewsErrors];

export type PostTeamTrendingRecommendationViewsResponses = {
    /**
     * Successful Response
     */
    200: unknown;
};

export type PostCandidateMergeData = {
    body: CandidateMergePayload;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
    };
    query?: never;
    url: '/api/v2/admin/candidates/{candidate_uuid}/merge';
};

export type PostCandidateMergeErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostCandidateMergeError = PostCandidateMergeErrors[keyof PostCandidateMergeErrors];

export type PostCandidateMergeResponses = {
    /**
     * Successful Response
     */
    200: CandidateMergeResponse;
};

export type PostCandidateMergeResponse = PostCandidateMergeResponses[keyof PostCandidateMergeResponses];

export type GetNewCandidatesData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Page number
         */
        page?: number;
        /**
         * Size
         *
         * Page size
         */
        size?: number;
    };
    url: '/api/v2/admin/candidates/new';
};

export type GetNewCandidatesErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetNewCandidatesError = GetNewCandidatesErrors[keyof GetNewCandidatesErrors];

export type GetNewCandidatesResponses = {
    /**
     * Successful Response
     */
    200: PageCandidateExtendedResponse;
};

export type GetNewCandidatesResponse = GetNewCandidatesResponses[keyof GetNewCandidatesResponses];

export type FindIdentityData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Person Uuid
         */
        person_uuid?: string | null;
        /**
         * Candidate Uuid
         */
        candidate_uuid?: string | null;
    };
    url: '/api/v2/admin/identities';
};

export type FindIdentityErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type FindIdentityError = FindIdentityErrors[keyof FindIdentityErrors];

export type FindIdentityResponses = {
    /**
     * Successful Response
     */
    200: IdentityResponse;
};

export type FindIdentityResponse = FindIdentityResponses[keyof FindIdentityResponses];

export type PostIdentityData = {
    body: LoginCreatePayload;
    path?: never;
    query?: never;
    url: '/api/v2/admin/identities';
};

export type PostIdentityErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostIdentityError = PostIdentityErrors[keyof PostIdentityErrors];

export type PostIdentityResponses = {
    /**
     * Successful Response
     */
    200: IdentityResponse;
};

export type PostIdentityResponse = PostIdentityResponses[keyof PostIdentityResponses];

export type GetIdentityData = {
    body?: never;
    path: {
        /**
         * Identity Uuid
         */
        identity_uuid: string;
    };
    query?: never;
    url: '/api/v2/admin/identities/{identity_uuid}';
};

export type GetIdentityErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetIdentityError = GetIdentityErrors[keyof GetIdentityErrors];

export type GetIdentityResponses = {
    /**
     * Successful Response
     */
    200: IdentityResponse;
};

export type GetIdentityResponse = GetIdentityResponses[keyof GetIdentityResponses];

export type GetIdentityEmailEventsData = {
    body?: never;
    path: {
        /**
         * Identity Uuid
         */
        identity_uuid: string;
    };
    query?: {
        /**
         * Page
         *
         * Page number
         */
        page?: number;
        /**
         * Size
         *
         * Page size
         */
        size?: number;
        /**
         * Event Uuid
         *
         * Only the emails about this event.
         */
        event_uuid?: string | null;
        /**
         * Batch Uuid
         *
         * Only the emails of this send run.
         */
        batch_uuid?: string | null;
        /**
         * Email Event Type
         *
         * Only the emails of these types (invite, reminder, summary and so on). Several values match an email of any of them.
         */
        email_event_type?: Array<EmailEventType> | null;
        /**
         * Sendgrid Event
         *
         * Only the emails SendGrid reported at least one of these events for. Several values match an email with any of them.
         */
        sendgrid_event?: Array<SendGridEmailEvent> | null;
    };
    url: '/api/v2/admin/identities/{identity_uuid}/email-events';
};

export type GetIdentityEmailEventsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetIdentityEmailEventsError = GetIdentityEmailEventsErrors[keyof GetIdentityEmailEventsErrors];

export type GetIdentityEmailEventsResponses = {
    /**
     * Successful Response
     */
    200: PageIdentityEmailItemResponse;
};

export type GetIdentityEmailEventsResponse = GetIdentityEmailEventsResponses[keyof GetIdentityEmailEventsResponses];

export type PostIdentityPersonData = {
    body: IdentityPersonPayload;
    path: {
        /**
         * Identity Uuid
         */
        identity_uuid: string;
    };
    query?: never;
    url: '/api/v2/admin/identities/{identity_uuid}/persons';
};

export type PostIdentityPersonErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostIdentityPersonError = PostIdentityPersonErrors[keyof PostIdentityPersonErrors];

export type PostIdentityPersonResponses = {
    /**
     * Successful Response
     */
    200: IdentityResponse;
};

export type PostIdentityPersonResponse = PostIdentityPersonResponses[keyof PostIdentityPersonResponses];

export type DeleteIdentityPersonData = {
    body?: never;
    path: {
        /**
         * Identity Uuid
         */
        identity_uuid: string;
        /**
         * Person Uuid
         */
        person_uuid: string;
    };
    query?: never;
    url: '/api/v2/admin/identities/{identity_uuid}/persons/{person_uuid}';
};

export type DeleteIdentityPersonErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type DeleteIdentityPersonError = DeleteIdentityPersonErrors[keyof DeleteIdentityPersonErrors];

export type DeleteIdentityPersonResponses = {
    /**
     * Successful Response
     */
    200: IdentityResponse;
};

export type DeleteIdentityPersonResponse = DeleteIdentityPersonResponses[keyof DeleteIdentityPersonResponses];

export type DeleteIdentityCandidateData = {
    body?: never;
    path: {
        /**
         * Identity Uuid
         */
        identity_uuid: string;
    };
    query?: never;
    url: '/api/v2/admin/identities/{identity_uuid}/candidate';
};

export type DeleteIdentityCandidateErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type DeleteIdentityCandidateError = DeleteIdentityCandidateErrors[keyof DeleteIdentityCandidateErrors];

export type DeleteIdentityCandidateResponses = {
    /**
     * Successful Response
     */
    200: IdentityResponse;
};

export type DeleteIdentityCandidateResponse = DeleteIdentityCandidateResponses[keyof DeleteIdentityCandidateResponses];

export type PutIdentityCandidateData = {
    body: IdentityCandidatePayload;
    path: {
        /**
         * Identity Uuid
         */
        identity_uuid: string;
    };
    query?: never;
    url: '/api/v2/admin/identities/{identity_uuid}/candidate';
};

export type PutIdentityCandidateErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PutIdentityCandidateError = PutIdentityCandidateErrors[keyof PutIdentityCandidateErrors];

export type PutIdentityCandidateResponses = {
    /**
     * Successful Response
     */
    200: IdentityResponse;
};

export type PutIdentityCandidateResponse = PutIdentityCandidateResponses[keyof PutIdentityCandidateResponses];

export type PostIdentityMergeData = {
    body: IdentityMergePayload;
    path: {
        /**
         * Identity Uuid
         */
        identity_uuid: string;
    };
    query?: never;
    url: '/api/v2/admin/identities/{identity_uuid}/merge';
};

export type PostIdentityMergeErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostIdentityMergeError = PostIdentityMergeErrors[keyof PostIdentityMergeErrors];

export type PostIdentityMergeResponses = {
    /**
     * Successful Response
     */
    200: IdentityMergeResponse;
};

export type PostIdentityMergeResponse = PostIdentityMergeResponses[keyof PostIdentityMergeResponses];

export type PostIdentityLoginData = {
    body: LoginCreatePayload;
    path: {
        /**
         * Identity Uuid
         */
        identity_uuid: string;
    };
    query?: never;
    url: '/api/v2/admin/identities/{identity_uuid}/logins';
};

export type PostIdentityLoginErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostIdentityLoginError = PostIdentityLoginErrors[keyof PostIdentityLoginErrors];

export type PostIdentityLoginResponses = {
    /**
     * Successful Response
     */
    200: IdentityResponse;
};

export type PostIdentityLoginResponse = PostIdentityLoginResponses[keyof PostIdentityLoginResponses];

export type PostContactListsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v2/admin/contact-lists';
};

export type PostContactListsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostContactListsError = PostContactListsErrors[keyof PostContactListsErrors];

export type PostContactListsResponses = {
    /**
     * Successful Response
     */
    200: ContactListsPushResponse;
};

export type PostContactListsResponse = PostContactListsResponses[keyof PostContactListsResponses];

export type GetPathwayActivityDefaultsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v2/admin/pathway-activity-defaults';
};

export type GetPathwayActivityDefaultsResponses = {
    /**
     * Successful Response
     */
    200: PathwayActivityDefaultsListResponse;
};

export type GetPathwayActivityDefaultsResponse = GetPathwayActivityDefaultsResponses[keyof GetPathwayActivityDefaultsResponses];

export type PutPathwayActivityDefaultsData = {
    body: PathwayActivityDefaultsPutPayload;
    path?: never;
    query?: never;
    url: '/api/v2/admin/pathway-activity-defaults';
};

export type PutPathwayActivityDefaultsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PutPathwayActivityDefaultsError = PutPathwayActivityDefaultsErrors[keyof PutPathwayActivityDefaultsErrors];

export type PutPathwayActivityDefaultsResponses = {
    /**
     * Successful Response
     */
    200: PathwayActivityDefaultsListResponse;
};

export type PutPathwayActivityDefaultsResponse = PutPathwayActivityDefaultsResponses[keyof PutPathwayActivityDefaultsResponses];

export type GetCandidateActivityLogsData = {
    body?: never;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
    };
    query?: {
        /**
         * Phrase
         *
         * Search by candidate name or team name
         */
        phrase?: string | null;
        /**
         * Page
         *
         * Page number
         */
        page?: number;
        /**
         * Size
         *
         * Page size
         */
        size?: number;
    };
    url: '/api/v2/admin/candidates/{candidate_uuid}/logs';
};

export type GetCandidateActivityLogsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetCandidateActivityLogsError = GetCandidateActivityLogsErrors[keyof GetCandidateActivityLogsErrors];

export type GetCandidateActivityLogsResponses = {
    /**
     * Successful Response
     */
    200: PageCandidateActivityLogResponse;
};

export type GetCandidateActivityLogsResponse = GetCandidateActivityLogsResponses[keyof GetCandidateActivityLogsResponses];

export type PostCandidateActivityLogData = {
    body: CandidateActivityLogPayload;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
    };
    query?: never;
    url: '/api/v2/admin/candidates/{candidate_uuid}/logs';
};

export type PostCandidateActivityLogErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostCandidateActivityLogError = PostCandidateActivityLogErrors[keyof PostCandidateActivityLogErrors];

export type PostCandidateActivityLogResponses = {
    /**
     * Successful Response
     */
    200: CandidateActivityLogResponse;
};

export type PostCandidateActivityLogResponse = PostCandidateActivityLogResponses[keyof PostCandidateActivityLogResponses];

export type GetCandidateActivityLogsSearchData = {
    body?: never;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
    };
    query?: {
        /**
         * Phrase
         *
         * Search by candidate name or team name
         */
        phrase?: string | null;
        /**
         * Get only activities of this type
         */
        activity_type?: EventTypeFilter | null;
        /**
         * Has Goal
         *
         * Filter by whether activity is linked to a goal
         */
        has_goal?: boolean | null;
    };
    url: '/api/v2/admin/candidates/{candidate_uuid}/logs/search';
};

export type GetCandidateActivityLogsSearchErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetCandidateActivityLogsSearchError = GetCandidateActivityLogsSearchErrors[keyof GetCandidateActivityLogsSearchErrors];

export type GetCandidateActivityLogsSearchResponses = {
    /**
     * Successful Response
     */
    200: GroupedActivityLogResponse;
};

export type GetCandidateActivityLogsSearchResponse = GetCandidateActivityLogsSearchResponses[keyof GetCandidateActivityLogsSearchResponses];

export type PostCandidateTasksFilterData = {
    body: CandidateTasksFilterPayload;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
    };
    query?: never;
    url: '/api/v2/admin/candidates/{candidate_uuid}/tasks/filter';
};

export type PostCandidateTasksFilterErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostCandidateTasksFilterError = PostCandidateTasksFilterErrors[keyof PostCandidateTasksFilterErrors];

export type PostCandidateTasksFilterResponses = {
    /**
     * Successful Response
     */
    200: CandidateTaskModulesResponse;
};

export type PostCandidateTasksFilterResponse = PostCandidateTasksFilterResponses[keyof PostCandidateTasksFilterResponses];

export type GetCandidateTasksOnboardingData = {
    body?: never;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
    };
    query?: never;
    url: '/api/v2/admin/candidates/{candidate_uuid}/tasks/onboarding-status';
};

export type GetCandidateTasksOnboardingErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetCandidateTasksOnboardingError = GetCandidateTasksOnboardingErrors[keyof GetCandidateTasksOnboardingErrors];

export type GetCandidateTasksOnboardingResponses = {
    /**
     * Successful Response
     */
    200: CandidateOnboardingStatus;
};

export type GetCandidateTasksOnboardingResponse = GetCandidateTasksOnboardingResponses[keyof GetCandidateTasksOnboardingResponses];

export type DeleteAllCandidatePathwayActivityTargetsData = {
    body?: never;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
    };
    query?: never;
    url: '/api/v2/admin/candidates/{candidate_uuid}/pathway-activity-targets';
};

export type DeleteAllCandidatePathwayActivityTargetsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type DeleteAllCandidatePathwayActivityTargetsError = DeleteAllCandidatePathwayActivityTargetsErrors[keyof DeleteAllCandidatePathwayActivityTargetsErrors];

export type DeleteAllCandidatePathwayActivityTargetsResponses = {
    /**
     * Successful Response
     */
    200: CandidatePathwayActivityTargetsResponse;
};

export type DeleteAllCandidatePathwayActivityTargetsResponse = DeleteAllCandidatePathwayActivityTargetsResponses[keyof DeleteAllCandidatePathwayActivityTargetsResponses];

export type GetCandidatePathwayActivityTargetsData = {
    body?: never;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
    };
    query?: never;
    url: '/api/v2/admin/candidates/{candidate_uuid}/pathway-activity-targets';
};

export type GetCandidatePathwayActivityTargetsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetCandidatePathwayActivityTargetsError = GetCandidatePathwayActivityTargetsErrors[keyof GetCandidatePathwayActivityTargetsErrors];

export type GetCandidatePathwayActivityTargetsResponses = {
    /**
     * Successful Response
     */
    200: CandidatePathwayActivityTargetsResponse;
};

export type GetCandidatePathwayActivityTargetsResponse = GetCandidatePathwayActivityTargetsResponses[keyof GetCandidatePathwayActivityTargetsResponses];

export type PutCandidatePathwayActivityTargetsData = {
    body: CandidatePathwayActivityTargetsPutPayload;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
    };
    query?: never;
    url: '/api/v2/admin/candidates/{candidate_uuid}/pathway-activity-targets';
};

export type PutCandidatePathwayActivityTargetsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PutCandidatePathwayActivityTargetsError = PutCandidatePathwayActivityTargetsErrors[keyof PutCandidatePathwayActivityTargetsErrors];

export type PutCandidatePathwayActivityTargetsResponses = {
    /**
     * Successful Response
     */
    200: CandidatePathwayActivityTargetsResponse;
};

export type PutCandidatePathwayActivityTargetsResponse = PutCandidatePathwayActivityTargetsResponses[keyof PutCandidatePathwayActivityTargetsResponses];

export type DeleteCandidatePathwayActivityTargetData = {
    body?: never;
    path: {
        activity_type: PathwayActivityType;
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
    };
    query?: never;
    url: '/api/v2/admin/candidates/{candidate_uuid}/pathway-activity-targets/{activity_type}';
};

export type DeleteCandidatePathwayActivityTargetErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type DeleteCandidatePathwayActivityTargetError = DeleteCandidatePathwayActivityTargetErrors[keyof DeleteCandidatePathwayActivityTargetErrors];

export type DeleteCandidatePathwayActivityTargetResponses = {
    /**
     * Successful Response
     */
    200: CandidatePathwayActivityTargetsResponse;
};

export type DeleteCandidatePathwayActivityTargetResponse = DeleteCandidatePathwayActivityTargetResponses[keyof DeleteCandidatePathwayActivityTargetResponses];

export type GetHomeActivityLogsData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Page number
         */
        page?: number;
        /**
         * Size
         *
         * Page size
         */
        size?: number;
    };
    url: '/api/v2/admin/home/logs';
};

export type GetHomeActivityLogsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetHomeActivityLogsError = GetHomeActivityLogsErrors[keyof GetHomeActivityLogsErrors];

export type GetHomeActivityLogsResponses = {
    /**
     * Successful Response
     */
    200: PageHomepageActivityLogResponse;
};

export type GetHomeActivityLogsResponse = GetHomeActivityLogsResponses[keyof GetHomeActivityLogsResponses];

export type GetHomeAllIntrosData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Page number
         */
        page?: number;
        /**
         * Size
         *
         * Page size
         */
        size?: number;
    };
    url: '/api/v2/admin/home/intros';
};

export type GetHomeAllIntrosErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetHomeAllIntrosError = GetHomeAllIntrosErrors[keyof GetHomeAllIntrosErrors];

export type GetHomeAllIntrosResponses = {
    /**
     * Successful Response
     */
    200: PageAllIntrosResponse;
};

export type GetHomeAllIntrosResponse = GetHomeAllIntrosResponses[keyof GetHomeAllIntrosResponses];

export type PostAdminPeersFilterData = {
    body: PeerToPeerTableFilterPayload;
    path?: never;
    query?: never;
    url: '/api/v2/admin/peers/filter';
};

export type PostAdminPeersFilterErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostAdminPeersFilterError = PostAdminPeersFilterErrors[keyof PostAdminPeersFilterErrors];

export type PostAdminPeersFilterResponses = {
    /**
     * Successful Response
     */
    200: PeerToPeerTableResponse;
};

export type PostAdminPeersFilterResponse = PostAdminPeersFilterResponses[keyof PostAdminPeersFilterResponses];

export type PostCandidateCompareAboutData = {
    body: PeerComparePayload;
    path?: never;
    query?: never;
    url: '/api/v2/admin/peers/compare/about';
};

export type PostCandidateCompareAboutErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostCandidateCompareAboutError = PostCandidateCompareAboutErrors[keyof PostCandidateCompareAboutErrors];

export type PostCandidateCompareAboutResponses = {
    /**
     * Response Post Candidate Compare About
     *
     * Successful Response
     */
    200: Array<PeerCompareAboutResponse>;
};

export type PostCandidateCompareAboutResponse = PostCandidateCompareAboutResponses[keyof PostCandidateCompareAboutResponses];

export type PostCandidateCompareGoalsData = {
    body: PeerComparePayload;
    path?: never;
    query?: never;
    url: '/api/v2/admin/peers/compare/goals';
};

export type PostCandidateCompareGoalsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostCandidateCompareGoalsError = PostCandidateCompareGoalsErrors[keyof PostCandidateCompareGoalsErrors];

export type PostCandidateCompareGoalsResponses = {
    /**
     * Response Post Candidate Compare Goals
     *
     * Successful Response
     */
    200: Array<PeerCompareGoalsResponse>;
};

export type PostCandidateCompareGoalsResponse = PostCandidateCompareGoalsResponses[keyof PostCandidateCompareGoalsResponses];

export type PostCandidateCompareP2pData = {
    body: PeerComparePayload;
    path?: never;
    query?: never;
    url: '/api/v2/admin/peers/compare/p2p';
};

export type PostCandidateCompareP2pErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostCandidateCompareP2pError = PostCandidateCompareP2pErrors[keyof PostCandidateCompareP2pErrors];

export type PostCandidateCompareP2pResponses = {
    /**
     * Response Post Candidate Compare P2P
     *
     * Successful Response
     */
    200: Array<PeerCompareP2pResponse>;
};

export type PostCandidateCompareP2pResponse = PostCandidateCompareP2pResponses[keyof PostCandidateCompareP2pResponses];

export type PostAdminPeersMatchesData = {
    body: PeerToPeerTableExecutivePayload;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
    };
    query?: never;
    url: '/api/v2/admin/peers/{candidate_uuid}/matches';
};

export type PostAdminPeersMatchesErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostAdminPeersMatchesError = PostAdminPeersMatchesErrors[keyof PostAdminPeersMatchesErrors];

export type PostAdminPeersMatchesResponses = {
    /**
     * Successful Response
     */
    200: PeerToPeerTableExecutiveResponse;
};

export type PostAdminPeersMatchesResponse = PostAdminPeersMatchesResponses[keyof PostAdminPeersMatchesResponses];

export type PostAdminPeersCandidatesFilterData = {
    body: PeerToPeerTableSelectFilterPayload;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
    };
    query?: never;
    url: '/api/v2/admin/peers/{candidate_uuid}/candidates/filter';
};

export type PostAdminPeersCandidatesFilterErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostAdminPeersCandidatesFilterError = PostAdminPeersCandidatesFilterErrors[keyof PostAdminPeersCandidatesFilterErrors];

export type PostAdminPeersCandidatesFilterResponses = {
    /**
     * Successful Response
     */
    200: PeerToPeerTableSelectResponse;
};

export type PostAdminPeersCandidatesFilterResponse = PostAdminPeersCandidatesFilterResponses[keyof PostAdminPeersCandidatesFilterResponses];

export type PostAdminPeersPublishData = {
    /**
     * Payload
     */
    body: Array<PeerToPeerPublishCandidates>;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
    };
    query?: never;
    url: '/api/v2/admin/peers/{candidate_uuid}';
};

export type PostAdminPeersPublishErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostAdminPeersPublishError = PostAdminPeersPublishErrors[keyof PostAdminPeersPublishErrors];

export type PostAdminPeersPublishResponses = {
    /**
     * Response Post Admin Peers Publish
     *
     * Successful Response
     */
    200: Array<PeerToPeerExtendedResponse>;
};

export type PostAdminPeersPublishResponse = PostAdminPeersPublishResponses[keyof PostAdminPeersPublishResponses];

export type DeletePeerData = {
    body?: never;
    path: {
        /**
         * Peer Id
         */
        peer_id: string;
    };
    query?: never;
    url: '/api/v2/admin/peers/{peer_id}';
};

export type DeletePeerErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type DeletePeerError = DeletePeerErrors[keyof DeletePeerErrors];

export type DeletePeerResponses = {
    /**
     * Successful Response
     */
    200: PeerToPeerExtendedResponse;
};

export type DeletePeerResponse = DeletePeerResponses[keyof DeletePeerResponses];

export type GetPeerData = {
    body?: never;
    path: {
        /**
         * Peer Id
         */
        peer_id: string;
    };
    query?: never;
    url: '/api/v2/admin/peers/{peer_id}';
};

export type GetPeerErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetPeerError = GetPeerErrors[keyof GetPeerErrors];

export type GetPeerResponses = {
    /**
     * Successful Response
     */
    200: PeerToPeerExtendedResponse;
};

export type GetPeerResponse = GetPeerResponses[keyof GetPeerResponses];

export type PutPeerStatusData = {
    body: PeerStatusUpdatePayload;
    path: {
        /**
         * Peer Id
         */
        peer_id: string;
    };
    query?: never;
    url: '/api/v2/admin/peers/{peer_id}/status';
};

export type PutPeerStatusErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PutPeerStatusError = PutPeerStatusErrors[keyof PutPeerStatusErrors];

export type PutPeerStatusResponses = {
    /**
     * Successful Response
     */
    200: PeerToPeerExtendedResponse;
};

export type PutPeerStatusResponse = PutPeerStatusResponses[keyof PutPeerStatusResponses];

export type PutPeerAssigneesData = {
    body: PeerAssigneesUpdatePayload;
    path: {
        /**
         * Peer Id
         */
        peer_id: string;
    };
    query?: never;
    url: '/api/v2/admin/peers/{peer_id}/assignees';
};

export type PutPeerAssigneesErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PutPeerAssigneesError = PutPeerAssigneesErrors[keyof PutPeerAssigneesErrors];

export type PutPeerAssigneesResponses = {
    /**
     * Successful Response
     */
    200: PeerToPeerExtendedResponse;
};

export type PutPeerAssigneesResponse = PutPeerAssigneesResponses[keyof PutPeerAssigneesResponses];

export type PutPeerIntroductionData = {
    body: PeerIntroductionPayload;
    path: {
        /**
         * Peer Id
         */
        peer_id: string;
    };
    query?: never;
    url: '/api/v2/admin/peers/{peer_id}/introduction';
};

export type PutPeerIntroductionErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PutPeerIntroductionError = PutPeerIntroductionErrors[keyof PutPeerIntroductionErrors];

export type PutPeerIntroductionResponses = {
    /**
     * Successful Response
     */
    200: PeerToPeerExtendedResponse;
};

export type PutPeerIntroductionResponse = PutPeerIntroductionResponses[keyof PutPeerIntroductionResponses];

export type GetPeerActivityData = {
    body?: never;
    path: {
        /**
         * Peer Id
         */
        peer_id: string;
    };
    query?: {
        /**
         * Page
         *
         * Page number
         */
        page?: number;
        /**
         * Size
         *
         * Page size
         */
        size?: number;
    };
    url: '/api/v2/admin/peers/{peer_id}/activity';
};

export type GetPeerActivityErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetPeerActivityError = GetPeerActivityErrors[keyof GetPeerActivityErrors];

export type GetPeerActivityResponses = {
    /**
     * Successful Response
     */
    200: PagePeerActivityResponse;
};

export type GetPeerActivityResponse = GetPeerActivityResponses[keyof GetPeerActivityResponses];

export type PostPeerCommentData = {
    body: PeerCommentPayload;
    path: {
        /**
         * Peer Id
         */
        peer_id: string;
    };
    query?: never;
    url: '/api/v2/admin/peers/{peer_id}/comments';
};

export type PostPeerCommentErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostPeerCommentError = PostPeerCommentErrors[keyof PostPeerCommentErrors];

export type PostPeerCommentResponses = {
    /**
     * Successful Response
     */
    200: PeerCommentResponse;
};

export type PostPeerCommentResponse = PostPeerCommentResponses[keyof PostPeerCommentResponses];

export type DeletePeerCommentData = {
    body?: never;
    path: {
        /**
         * Peer Id
         */
        peer_id: string;
        /**
         * Peer Comment Id
         */
        peer_comment_id: string;
    };
    query?: never;
    url: '/api/v2/admin/peers/{peer_id}/comments/{peer_comment_id}';
};

export type DeletePeerCommentErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type DeletePeerCommentError = DeletePeerCommentErrors[keyof DeletePeerCommentErrors];

export type DeletePeerCommentResponses = {
    /**
     * Successful Response
     */
    200: PeerCommentResponse;
};

export type DeletePeerCommentResponse = DeletePeerCommentResponses[keyof DeletePeerCommentResponses];

export type PostTasksData = {
    body: TaskCreateUpdatePayload;
    path?: never;
    query?: never;
    url: '/api/v2/admin/tasks';
};

export type PostTasksErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostTasksError = PostTasksErrors[keyof PostTasksErrors];

export type PostTasksResponses = {
    /**
     * Successful Response
     */
    200: TaskBasicResponse;
};

export type PostTasksResponse = PostTasksResponses[keyof PostTasksResponses];

export type PostTasksFilterData = {
    body: TasksTableFilterPayload;
    path?: never;
    query?: never;
    url: '/api/v2/admin/tasks/filter';
};

export type PostTasksFilterErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostTasksFilterError = PostTasksFilterErrors[keyof PostTasksFilterErrors];

export type PostTasksFilterResponses = {
    /**
     * Successful Response
     */
    200: TasksTableResponse;
};

export type PostTasksFilterResponse = PostTasksFilterResponses[keyof PostTasksFilterResponses];

export type DeleteTaskData = {
    body?: never;
    path: {
        /**
         * Task Id
         */
        task_id: string;
    };
    query?: never;
    url: '/api/v2/admin/tasks/{task_id}';
};

export type DeleteTaskErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type DeleteTaskError = DeleteTaskErrors[keyof DeleteTaskErrors];

export type DeleteTaskResponses = {
    /**
     * Successful Response
     */
    200: TaskBasicResponse;
};

export type DeleteTaskResponse = DeleteTaskResponses[keyof DeleteTaskResponses];

export type GetTaskData = {
    body?: never;
    path: {
        /**
         * Task Id
         */
        task_id: string;
    };
    query?: never;
    url: '/api/v2/admin/tasks/{task_id}';
};

export type GetTaskErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetTaskError = GetTaskErrors[keyof GetTaskErrors];

export type GetTaskResponses = {
    /**
     * Successful Response
     */
    200: TaskBasicResponse;
};

export type GetTaskResponse = GetTaskResponses[keyof GetTaskResponses];

export type PutTaskData = {
    body: TaskCreateUpdatePayload;
    path: {
        /**
         * Task Id
         */
        task_id: string;
    };
    query?: never;
    url: '/api/v2/admin/tasks/{task_id}';
};

export type PutTaskErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PutTaskError = PutTaskErrors[keyof PutTaskErrors];

export type PutTaskResponses = {
    /**
     * Successful Response
     */
    200: TaskBasicResponse;
};

export type PutTaskResponse = PutTaskResponses[keyof PutTaskResponses];

export type PutTaskStatusData = {
    body: TaskStatusPayload;
    path: {
        /**
         * Task Id
         */
        task_id: string;
    };
    query?: never;
    url: '/api/v2/admin/tasks/{task_id}/status';
};

export type PutTaskStatusErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PutTaskStatusError = PutTaskStatusErrors[keyof PutTaskStatusErrors];

export type PutTaskStatusResponses = {
    /**
     * Successful Response
     */
    200: TaskBasicResponse;
};

export type PutTaskStatusResponse = PutTaskStatusResponses[keyof PutTaskStatusResponses];

export type PostTeamTransferToCompanyData = {
    body: TeamTransferPayload;
    path: {
        /**
         * Team Uuid
         */
        team_uuid: string;
    };
    query?: never;
    url: '/api/v2/admin/teams/{team_uuid}/transfer-to-company';
};

export type PostTeamTransferToCompanyErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostTeamTransferToCompanyError = PostTeamTransferToCompanyErrors[keyof PostTeamTransferToCompanyErrors];

export type PostTeamTransferToCompanyResponses = {
    /**
     * Successful Response
     */
    200: TeamExtendedResponse;
};

export type PostTeamTransferToCompanyResponse = PostTeamTransferToCompanyResponses[keyof PostTeamTransferToCompanyResponses];

export type PostTeamMergeData = {
    body: TeamMergePayload;
    path: {
        /**
         * Team Uuid
         */
        team_uuid: string;
    };
    query?: never;
    url: '/api/v2/admin/teams/{team_uuid}/merge';
};

export type PostTeamMergeErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostTeamMergeError = PostTeamMergeErrors[keyof PostTeamMergeErrors];

export type PostTeamMergeResponses = {
    /**
     * Successful Response
     */
    200: TeamExtendedResponse;
};

export type PostTeamMergeResponse = PostTeamMergeResponses[keyof PostTeamMergeResponses];

export type PostTaskModulesData = {
    body: TaskModuleCreateUpdatePayload;
    path?: never;
    query?: never;
    url: '/api/v2/admin/task-modules';
};

export type PostTaskModulesErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostTaskModulesError = PostTaskModulesErrors[keyof PostTaskModulesErrors];

export type PostTaskModulesResponses = {
    /**
     * Successful Response
     */
    200: TaskModuleResponse;
};

export type PostTaskModulesResponse = PostTaskModulesResponses[keyof PostTaskModulesResponses];

export type DeleteTaskModuleData = {
    body?: never;
    path: {
        /**
         * Task Module Id
         */
        task_module_id: string;
    };
    query?: never;
    url: '/api/v2/admin/task-modules/{task_module_id}';
};

export type DeleteTaskModuleErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type DeleteTaskModuleError = DeleteTaskModuleErrors[keyof DeleteTaskModuleErrors];

export type DeleteTaskModuleResponses = {
    /**
     * Successful Response
     */
    200: TaskModuleResponse;
};

export type DeleteTaskModuleResponse = DeleteTaskModuleResponses[keyof DeleteTaskModuleResponses];

export type GetTaskModuleData = {
    body?: never;
    path: {
        /**
         * Task Module Id
         */
        task_module_id: string;
    };
    query?: never;
    url: '/api/v2/admin/task-modules/{task_module_id}';
};

export type GetTaskModuleErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetTaskModuleError = GetTaskModuleErrors[keyof GetTaskModuleErrors];

export type GetTaskModuleResponses = {
    /**
     * Successful Response
     */
    200: TaskModuleResponse;
};

export type GetTaskModuleResponse = GetTaskModuleResponses[keyof GetTaskModuleResponses];

export type PutTaskModuleData = {
    body: TaskModuleCreateUpdatePayload;
    path: {
        /**
         * Task Module Id
         */
        task_module_id: string;
    };
    query?: never;
    url: '/api/v2/admin/task-modules/{task_module_id}';
};

export type PutTaskModuleErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PutTaskModuleError = PutTaskModuleErrors[keyof PutTaskModuleErrors];

export type PutTaskModuleResponses = {
    /**
     * Successful Response
     */
    200: TaskModuleResponse;
};

export type PutTaskModuleResponse = PutTaskModuleResponses[keyof PutTaskModuleResponses];

export type PutTaskModuleStatusData = {
    body: TaskModuleStatusPayload;
    path: {
        /**
         * Task Module Id
         */
        task_module_id: string;
    };
    query?: never;
    url: '/api/v2/admin/task-modules/{task_module_id}/status';
};

export type PutTaskModuleStatusErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PutTaskModuleStatusError = PutTaskModuleStatusErrors[keyof PutTaskModuleStatusErrors];

export type PutTaskModuleStatusResponses = {
    /**
     * Successful Response
     */
    200: TaskModuleResponse;
};

export type PutTaskModuleStatusResponse = PutTaskModuleStatusResponses[keyof PutTaskModuleStatusResponses];

export type PostAdminPipelineCandidatesFilterData = {
    body: PipelineCandidateFilterPayload;
    path: {
        /**
         * Pipeline Uuid
         */
        pipeline_uuid: string;
    };
    query?: never;
    url: '/api/v2/admin/pipelines/{pipeline_uuid}/candidates/filter';
};

export type PostAdminPipelineCandidatesFilterErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostAdminPipelineCandidatesFilterError = PostAdminPipelineCandidatesFilterErrors[keyof PostAdminPipelineCandidatesFilterErrors];

export type PostAdminPipelineCandidatesFilterResponses = {
    /**
     * Successful Response
     */
    200: PipelineCandidatesFilterPaginatedResponse;
};

export type PostAdminPipelineCandidatesFilterResponse = PostAdminPipelineCandidatesFilterResponses[keyof PostAdminPipelineCandidatesFilterResponses];

export type PostPipelineTransferToTeamData = {
    body: PipelineTransferPayload;
    path?: never;
    query?: never;
    url: '/api/v2/admin/pipelines/transfer-to-team';
};

export type PostPipelineTransferToTeamErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostPipelineTransferToTeamError = PostPipelineTransferToTeamErrors[keyof PostPipelineTransferToTeamErrors];

export type PostPipelineTransferToTeamResponses = {
    /**
     * Successful Response
     */
    200: PipelineTransferResponse;
};

export type PostPipelineTransferToTeamResponse = PostPipelineTransferToTeamResponses[keyof PostPipelineTransferToTeamResponses];

export type PatchUserRoleRemoveCompanyData = {
    body?: never;
    path: {
        /**
         * Role Id
         */
        role_id: number;
        /**
         * Person Uuid
         */
        person_uuid: string;
    };
    query?: never;
    url: '/api/v2/admin/users/{person_uuid}/roles/{role_id}/remove-company';
};

export type PatchUserRoleRemoveCompanyErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PatchUserRoleRemoveCompanyError = PatchUserRoleRemoveCompanyErrors[keyof PatchUserRoleRemoveCompanyErrors];

export type PatchUserRoleRemoveCompanyResponses = {
    /**
     * Successful Response
     */
    200: PersonBasicResponse;
};

export type PatchUserRoleRemoveCompanyResponse = PatchUserRoleRemoveCompanyResponses[keyof PatchUserRoleRemoveCompanyResponses];

export type PatchUserData = {
    body: UserUpdatePayload;
    path: {
        /**
         * Person Uuid
         */
        person_uuid: string;
    };
    query?: never;
    url: '/api/v2/admin/users/{person_uuid}';
};

export type PatchUserErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PatchUserError = PatchUserErrors[keyof PatchUserErrors];

export type PatchUserResponses = {
    /**
     * Successful Response
     */
    200: PersonBasicResponse;
};

export type PatchUserResponse = PatchUserResponses[keyof PatchUserResponses];

export type PostUserCompanyData = {
    body: UserCompanyPayload;
    path: {
        /**
         * Person Uuid
         */
        person_uuid: string;
    };
    query?: never;
    url: '/api/v2/admin/users/{person_uuid}/company';
};

export type PostUserCompanyErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostUserCompanyError = PostUserCompanyErrors[keyof PostUserCompanyErrors];

export type PostUserCompanyResponses = {
    /**
     * Successful Response
     */
    200: PersonBasicResponse;
};

export type PostUserCompanyResponse = PostUserCompanyResponses[keyof PostUserCompanyResponses];

export type PostUserTeamData = {
    body: UserTeamPayload;
    path: {
        /**
         * Person Uuid
         */
        person_uuid: string;
    };
    query?: never;
    url: '/api/v2/admin/users/{person_uuid}/teams';
};

export type PostUserTeamErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostUserTeamError = PostUserTeamErrors[keyof PostUserTeamErrors];

export type PostUserTeamResponses = {
    /**
     * Successful Response
     */
    200: PersonBasicResponse;
};

export type PostUserTeamResponse = PostUserTeamResponses[keyof PostUserTeamResponses];

export type DeleteCandidateCompanyMetadataData = {
    body?: never;
    path: {
        /**
         * Company Id
         */
        company_id: number;
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
    };
    query?: never;
    url: '/api/v2/admin/companies/{company_id}/candidates/{candidate_uuid}/metadata';
};

export type DeleteCandidateCompanyMetadataErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type DeleteCandidateCompanyMetadataError = DeleteCandidateCompanyMetadataErrors[keyof DeleteCandidateCompanyMetadataErrors];

export type DeleteCandidateCompanyMetadataResponses = {
    /**
     * Successful Response
     */
    200: CandidateCompanyMetadataResponse;
};

export type DeleteCandidateCompanyMetadataResponse = DeleteCandidateCompanyMetadataResponses[keyof DeleteCandidateCompanyMetadataResponses];

export type GetCandidateCompanyMetadataData = {
    body?: never;
    path: {
        /**
         * Company Id
         */
        company_id: number;
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
    };
    query?: never;
    url: '/api/v2/admin/companies/{company_id}/candidates/{candidate_uuid}/metadata';
};

export type GetCandidateCompanyMetadataErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetCandidateCompanyMetadataError = GetCandidateCompanyMetadataErrors[keyof GetCandidateCompanyMetadataErrors];

export type GetCandidateCompanyMetadataResponses = {
    /**
     * Successful Response
     */
    200: CandidateCompanyMetadataResponse;
};

export type GetCandidateCompanyMetadataResponse = GetCandidateCompanyMetadataResponses[keyof GetCandidateCompanyMetadataResponses];

export type UpsertCandidateCompanyMetadataData = {
    body: CandidateCompanyMetadataPayload;
    path: {
        /**
         * Company Id
         */
        company_id: number;
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
    };
    query?: never;
    url: '/api/v2/admin/companies/{company_id}/candidates/{candidate_uuid}/metadata';
};

export type UpsertCandidateCompanyMetadataErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type UpsertCandidateCompanyMetadataError = UpsertCandidateCompanyMetadataErrors[keyof UpsertCandidateCompanyMetadataErrors];

export type UpsertCandidateCompanyMetadataResponses = {
    /**
     * Successful Response
     */
    200: CandidateCompanyMetadataResponse;
};

export type UpsertCandidateCompanyMetadataResponse = UpsertCandidateCompanyMetadataResponses[keyof UpsertCandidateCompanyMetadataResponses];

export type ImportCandidateCompanyMetadataData = {
    body: CandidateCompanyMetadataImportPayload;
    path: {
        /**
         * Company Id
         */
        company_id: number;
    };
    query?: never;
    url: '/api/v2/admin/companies/{company_id}/candidate-metadata/import';
};

export type ImportCandidateCompanyMetadataErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type ImportCandidateCompanyMetadataError = ImportCandidateCompanyMetadataErrors[keyof ImportCandidateCompanyMetadataErrors];

export type ImportCandidateCompanyMetadataResponses = {
    /**
     * Successful Response
     */
    200: CandidateCompanyMetadataImportResponse;
};

export type ImportCandidateCompanyMetadataResponse = ImportCandidateCompanyMetadataResponses[keyof ImportCandidateCompanyMetadataResponses];

export type ListCandidateCompanyMetadataData = {
    body?: never;
    path: {
        /**
         * Company Id
         */
        company_id: number;
    };
    query?: {
        /**
         * Page
         *
         * Page number
         */
        page?: number;
        /**
         * Size
         *
         * Page size
         */
        size?: number;
    };
    url: '/api/v2/admin/companies/{company_id}/candidate-metadata';
};

export type ListCandidateCompanyMetadataErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type ListCandidateCompanyMetadataError = ListCandidateCompanyMetadataErrors[keyof ListCandidateCompanyMetadataErrors];

export type ListCandidateCompanyMetadataResponses = {
    /**
     * Successful Response
     */
    200: PageCandidateCompanyMetadataWithCandidateResponse;
};

export type ListCandidateCompanyMetadataResponse = ListCandidateCompanyMetadataResponses[keyof ListCandidateCompanyMetadataResponses];

export type PostCompanyPeopleFilterData = {
    body: CompanyPeopleFilterPayload;
    path: {
        /**
         * Company Id
         */
        company_id: number;
    };
    query?: never;
    url: '/api/v2/admin/companies/{company_id}/people/filter';
};

export type PostCompanyPeopleFilterErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostCompanyPeopleFilterError = PostCompanyPeopleFilterErrors[keyof PostCompanyPeopleFilterErrors];

export type PostCompanyPeopleFilterResponses = {
    /**
     * Successful Response
     */
    200: CompanyPeopleFilterResponse;
};

export type PostCompanyPeopleFilterResponse = PostCompanyPeopleFilterResponses[keyof PostCompanyPeopleFilterResponses];

export type PostActivateExecutiveData = {
    /**
     * Payload
     */
    body?: ActivateExecutivePayload | null;
    path: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
    };
    query?: never;
    url: '/api/v2/admin/executives/{candidate_uuid}/activate';
};

export type PostActivateExecutiveErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostActivateExecutiveError = PostActivateExecutiveErrors[keyof PostActivateExecutiveErrors];

export type PostActivateExecutiveResponses = {
    /**
     * Successful Response
     */
    200: ExecutiveResponse;
};

export type PostActivateExecutiveResponse = PostActivateExecutiveResponses[keyof PostActivateExecutiveResponses];

export type PostBulkActivateExecutivesData = {
    body: BulkActivateExecutivePayload;
    path?: never;
    query?: never;
    url: '/api/v2/admin/executives/bulk-activate';
};

export type PostBulkActivateExecutivesErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostBulkActivateExecutivesError = PostBulkActivateExecutivesErrors[keyof PostBulkActivateExecutivesErrors];

export type PostBulkActivateExecutivesResponses = {
    /**
     * Successful Response
     */
    200: BulkActivateResponse;
};

export type PostBulkActivateExecutivesResponse = PostBulkActivateExecutivesResponses[keyof PostBulkActivateExecutivesResponses];

export type DeleteExecutiveActivationData = {
    body?: never;
    path: {
        /**
         * Person Uuid
         */
        person_uuid: string;
    };
    query?: never;
    url: '/api/v2/admin/executives/{person_uuid}/activation';
};

export type DeleteExecutiveActivationErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type DeleteExecutiveActivationError = DeleteExecutiveActivationErrors[keyof DeleteExecutiveActivationErrors];

export type DeleteExecutiveActivationResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteExecutiveActivationResponse = DeleteExecutiveActivationResponses[keyof DeleteExecutiveActivationResponses];

export type PostSendExecutiveInvitationData = {
    body?: never;
    path: {
        /**
         * Person Uuid
         */
        person_uuid: string;
    };
    query?: never;
    url: '/api/v2/admin/executives/{person_uuid}/send-invitation';
};

export type PostSendExecutiveInvitationErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostSendExecutiveInvitationError = PostSendExecutiveInvitationErrors[keyof PostSendExecutiveInvitationErrors];

export type PostSendExecutiveInvitationResponses = {
    /**
     * Successful Response
     */
    200: ExecutiveResponse;
};

export type PostSendExecutiveInvitationResponse = PostSendExecutiveInvitationResponses[keyof PostSendExecutiveInvitationResponses];

export type GetExecutivesData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v2/admin/executives';
};

export type GetExecutivesResponses = {
    /**
     * Successful Response
     */
    200: ExecutiveListResponse;
};

export type GetExecutivesResponse = GetExecutivesResponses[keyof GetExecutivesResponses];

export type GetExecutiveData = {
    body?: never;
    path: {
        /**
         * Person Uuid
         */
        person_uuid: string;
    };
    query?: never;
    url: '/api/v2/admin/executives/{person_uuid}';
};

export type GetExecutiveErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetExecutiveError = GetExecutiveErrors[keyof GetExecutiveErrors];

export type GetExecutiveResponses = {
    /**
     * Successful Response
     */
    200: ExecutiveResponse;
};

export type GetExecutiveResponse = GetExecutiveResponses[keyof GetExecutiveResponses];

export type PatchExecutiveSettingsData = {
    body: UpdateExecutiveSettingsPayload;
    path: {
        /**
         * Person Uuid
         */
        person_uuid: string;
    };
    query?: never;
    url: '/api/v2/admin/executives/{person_uuid}/settings';
};

export type PatchExecutiveSettingsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PatchExecutiveSettingsError = PatchExecutiveSettingsErrors[keyof PatchExecutiveSettingsErrors];

export type PatchExecutiveSettingsResponses = {
    /**
     * Successful Response
     */
    200: ExecutiveResponse;
};

export type PatchExecutiveSettingsResponse = PatchExecutiveSettingsResponses[keyof PatchExecutiveSettingsResponses];

export type GetExecutiveClientSettingsData = {
    body?: never;
    path: {
        /**
         * Person Uuid
         */
        person_uuid: string;
    };
    query?: never;
    url: '/api/v2/clients/{person_uuid}/settings';
};

export type GetExecutiveClientSettingsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetExecutiveClientSettingsError = GetExecutiveClientSettingsErrors[keyof GetExecutiveClientSettingsErrors];

export type GetExecutiveClientSettingsResponses = {
    /**
     * Successful Response
     */
    200: ExecutiveClientSettingsResponse;
};

export type GetExecutiveClientSettingsResponse = GetExecutiveClientSettingsResponses[keyof GetExecutiveClientSettingsResponses];

export type PatchExecutiveClientSettingsData = {
    body: UpdateExecutiveClientSettingsPayload;
    path: {
        /**
         * Person Uuid
         */
        person_uuid: string;
    };
    query?: never;
    url: '/api/v2/clients/{person_uuid}/settings';
};

export type PatchExecutiveClientSettingsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PatchExecutiveClientSettingsError = PatchExecutiveClientSettingsErrors[keyof PatchExecutiveClientSettingsErrors];

export type PatchExecutiveClientSettingsResponses = {
    /**
     * Successful Response
     */
    200: ExecutiveClientSettingsResponse;
};

export type PatchExecutiveClientSettingsResponse = PatchExecutiveClientSettingsResponses[keyof PatchExecutiveClientSettingsResponses];

export type GetExecutiveClientTeamData = {
    body?: never;
    path: {
        /**
         * Person Uuid
         */
        person_uuid: string;
    };
    query?: never;
    url: '/api/v2/clients/{person_uuid}/team';
};

export type GetExecutiveClientTeamErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetExecutiveClientTeamError = GetExecutiveClientTeamErrors[keyof GetExecutiveClientTeamErrors];

export type GetExecutiveClientTeamResponses = {
    /**
     * Successful Response
     */
    200: ExecutiveTeamResponse;
};

export type GetExecutiveClientTeamResponse = GetExecutiveClientTeamResponses[keyof GetExecutiveClientTeamResponses];

export type PostExecutiveClientContactPipelinesData = {
    body: TeamCandidatePipelinesPayload;
    path: {
        /**
         * Person Uuid
         */
        person_uuid: string;
        /**
         * Contact Uuid
         */
        contact_uuid: string;
    };
    query?: never;
    url: '/api/v2/clients/{person_uuid}/contacts/{contact_uuid}/pipelines';
};

export type PostExecutiveClientContactPipelinesErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostExecutiveClientContactPipelinesError = PostExecutiveClientContactPipelinesErrors[keyof PostExecutiveClientContactPipelinesErrors];

export type PostExecutiveClientContactPipelinesResponses = {
    /**
     * Successful Response
     */
    200: SubmissionCandidatePipelinesResponse;
};

export type PostExecutiveClientContactPipelinesResponse = PostExecutiveClientContactPipelinesResponses[keyof PostExecutiveClientContactPipelinesResponses];

export type GetExecutiveClientPersonDetailData = {
    body?: never;
    path: {
        /**
         * Contact Person Uuid
         */
        contact_person_uuid: string;
        /**
         * Person Uuid
         */
        person_uuid: string;
    };
    query?: never;
    url: '/api/v2/clients/{person_uuid}/people/{contact_person_uuid}';
};

export type GetExecutiveClientPersonDetailErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetExecutiveClientPersonDetailError = GetExecutiveClientPersonDetailErrors[keyof GetExecutiveClientPersonDetailErrors];

export type GetExecutiveClientPersonDetailResponses = {
    /**
     * Successful Response
     */
    200: ExecutivePeopleDetailResponse;
};

export type GetExecutiveClientPersonDetailResponse = GetExecutiveClientPersonDetailResponses[keyof GetExecutiveClientPersonDetailResponses];

export type PostExecutiveClientPeopleSearchData = {
    body: ExecutiveClientPeopleSearchPayload;
    path: {
        /**
         * Person Uuid
         */
        person_uuid: string;
    };
    query?: never;
    url: '/api/v2/clients/{person_uuid}/people/search';
};

export type PostExecutiveClientPeopleSearchErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostExecutiveClientPeopleSearchError = PostExecutiveClientPeopleSearchErrors[keyof PostExecutiveClientPeopleSearchErrors];

export type PostExecutiveClientPeopleSearchResponses = {
    /**
     * Successful Response
     */
    200: ExecutivePeopleSearchResponse;
};

export type PostExecutiveClientPeopleSearchResponse = PostExecutiveClientPeopleSearchResponses[keyof PostExecutiveClientPeopleSearchResponses];

export type GetExecutiveClientCandidateData = {
    body?: never;
    path: {
        /**
         * Person Uuid
         */
        person_uuid: string;
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
    };
    query?: never;
    url: '/api/v2/clients/{person_uuid}/candidates/{candidate_uuid}';
};

export type GetExecutiveClientCandidateErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetExecutiveClientCandidateError = GetExecutiveClientCandidateErrors[keyof GetExecutiveClientCandidateErrors];

export type GetExecutiveClientCandidateResponses = {
    /**
     * Successful Response
     */
    200: CandidateExternalFullResponseWithTeamContext;
};

export type GetExecutiveClientCandidateResponse = GetExecutiveClientCandidateResponses[keyof GetExecutiveClientCandidateResponses];

export type GetExecutiveClientContactData = {
    body?: never;
    path: {
        /**
         * Contact Uuid
         */
        contact_uuid: string;
        /**
         * Person Uuid
         */
        person_uuid: string;
    };
    query?: never;
    url: '/api/v2/clients/{person_uuid}/contacts/{contact_uuid}';
};

export type GetExecutiveClientContactErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetExecutiveClientContactError = GetExecutiveClientContactErrors[keyof GetExecutiveClientContactErrors];

export type GetExecutiveClientContactResponses = {
    /**
     * Successful Response
     */
    200: ExecutiveContactDetailResponse;
};

export type GetExecutiveClientContactResponse = GetExecutiveClientContactResponses[keyof GetExecutiveClientContactResponses];

export type GetExecutiveClientTrendingData = {
    body?: never;
    path: {
        /**
         * Person Uuid
         */
        person_uuid: string;
    };
    query?: {
        /**
         * Search Id
         */
        search_id?: string | null;
    };
    url: '/api/v2/clients/{person_uuid}/trending';
};

export type GetExecutiveClientTrendingErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetExecutiveClientTrendingError = GetExecutiveClientTrendingErrors[keyof GetExecutiveClientTrendingErrors];

export type GetExecutiveClientTrendingResponses = {
    /**
     * Successful Response
     */
    200: TeamTrendingCandidatesResponse;
};

export type GetExecutiveClientTrendingResponse = GetExecutiveClientTrendingResponses[keyof GetExecutiveClientTrendingResponses];

export type GetExecutiveClientTrendingRecommendationData = {
    body?: never;
    path: {
        /**
         * Recommendation Uuid
         */
        recommendation_uuid: string;
        /**
         * Person Uuid
         */
        person_uuid: string;
    };
    query?: never;
    url: '/api/v2/clients/{person_uuid}/trending/{recommendation_uuid}';
};

export type GetExecutiveClientTrendingRecommendationErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetExecutiveClientTrendingRecommendationError = GetExecutiveClientTrendingRecommendationErrors[keyof GetExecutiveClientTrendingRecommendationErrors];

export type GetExecutiveClientTrendingRecommendationResponses = {
    /**
     * Successful Response
     */
    200: RecommendationExternalFullResponse;
};

export type GetExecutiveClientTrendingRecommendationResponse = GetExecutiveClientTrendingRecommendationResponses[keyof GetExecutiveClientTrendingRecommendationResponses];

export type PostAdminExecutiveClientActivityData = {
    body: ExecutiveActivityFilter;
    path: {
        /**
         * Person Uuid
         */
        person_uuid: string;
    };
    query?: {
        /**
         * Page
         *
         * Page number
         */
        page?: number;
        /**
         * Size
         *
         * Page size
         */
        size?: number;
    };
    url: '/api/v2/clients/{person_uuid}/activity';
};

export type PostAdminExecutiveClientActivityErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostAdminExecutiveClientActivityError = PostAdminExecutiveClientActivityErrors[keyof PostAdminExecutiveClientActivityErrors];

export type PostAdminExecutiveClientActivityResponses = {
    /**
     * Successful Response
     */
    200: PostPageActivityFeedItem;
};

export type PostAdminExecutiveClientActivityResponse = PostAdminExecutiveClientActivityResponses[keyof PostAdminExecutiveClientActivityResponses];

export type PostExecutiveClientActivityData = {
    body: ExecutiveActivityFilter;
    path: {
        /**
         * Contact Uuid
         */
        contact_uuid: string;
        /**
         * Person Uuid
         */
        person_uuid: string;
    };
    query?: {
        /**
         * Page
         *
         * Page number
         */
        page?: number;
        /**
         * Size
         *
         * Page size
         */
        size?: number;
    };
    url: '/api/v2/clients/{person_uuid}/contacts/{contact_uuid}/activity';
};

export type PostExecutiveClientActivityErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostExecutiveClientActivityError = PostExecutiveClientActivityErrors[keyof PostExecutiveClientActivityErrors];

export type PostExecutiveClientActivityResponses = {
    /**
     * Successful Response
     */
    200: PostPageActivityFeedItem;
};

export type PostExecutiveClientActivityResponse = PostExecutiveClientActivityResponses[keyof PostExecutiveClientActivityResponses];

export type ListExecutiveClientEventsData = {
    body?: never;
    path: {
        /**
         * Person Uuid
         */
        person_uuid: string;
    };
    query?: {
        /**
         * Sort
         *
         * Field to sort by: event_date or title (event name).
         */
        sort?: 'event_date' | 'title';
        /**
         * Direction
         *
         * Sort direction. Defaults to most recent / Z-A first.
         */
        direction?: 'asc' | 'desc';
        /**
         * Page
         *
         * Page number
         */
        page?: number;
        /**
         * Size
         *
         * Page size
         */
        size?: number;
    };
    url: '/api/v2/clients/{person_uuid}/events';
};

export type ListExecutiveClientEventsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type ListExecutiveClientEventsError = ListExecutiveClientEventsErrors[keyof ListExecutiveClientEventsErrors];

export type ListExecutiveClientEventsResponses = {
    /**
     * Successful Response
     */
    200: PageExecutiveClientEventListItem;
};

export type ListExecutiveClientEventsResponse = ListExecutiveClientEventsResponses[keyof ListExecutiveClientEventsResponses];

export type GetExecutiveClientEventData = {
    body?: never;
    path: {
        /**
         * Event Uuid
         */
        event_uuid: string;
        /**
         * Person Uuid
         */
        person_uuid: string;
    };
    query?: never;
    url: '/api/v2/clients/{person_uuid}/events/{event_uuid}';
};

export type GetExecutiveClientEventErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetExecutiveClientEventError = GetExecutiveClientEventErrors[keyof GetExecutiveClientEventErrors];

export type GetExecutiveClientEventResponses = {
    /**
     * Successful Response
     */
    200: ExecutiveClientEventResponse;
};

export type GetExecutiveClientEventResponse = GetExecutiveClientEventResponses[keyof GetExecutiveClientEventResponses];

export type UpdateExecutiveClientEventRsvpData = {
    body: ExecutiveClientRsvpUpdatePayload;
    path: {
        /**
         * Event Uuid
         */
        event_uuid: string;
        /**
         * Person Uuid
         */
        person_uuid: string;
    };
    query?: never;
    url: '/api/v2/clients/{person_uuid}/events/{event_uuid}/rsvp';
};

export type UpdateExecutiveClientEventRsvpErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type UpdateExecutiveClientEventRsvpError = UpdateExecutiveClientEventRsvpErrors[keyof UpdateExecutiveClientEventRsvpErrors];

export type UpdateExecutiveClientEventRsvpResponses = {
    /**
     * Successful Response
     */
    200: ExecutiveClientEventResponse;
};

export type UpdateExecutiveClientEventRsvpResponse = UpdateExecutiveClientEventRsvpResponses[keyof UpdateExecutiveClientEventRsvpResponses];

export type PostPeopleV2Data = {
    body: PeoplePost;
    path?: never;
    query?: never;
    url: '/api/v2/people';
};

export type PostPeopleV2Errors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostPeopleV2Error = PostPeopleV2Errors[keyof PostPeopleV2Errors];

export type PostPeopleV2Responses = {
    /**
     * Successful Response
     */
    201: UserInviteResponse;
};

export type PostPeopleV2Response = PostPeopleV2Responses[keyof PostPeopleV2Responses];

export type UpdatePersonMetadataV2Data = {
    body: ClerkUserPublicMetadataUpdate;
    path?: never;
    query?: never;
    url: '/api/v2/people/metadata';
};

export type UpdatePersonMetadataV2Errors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type UpdatePersonMetadataV2Error = UpdatePersonMetadataV2Errors[keyof UpdatePersonMetadataV2Errors];

export type UpdatePersonMetadataV2Responses = {
    /**
     * Successful Response
     */
    200: ClerkUserPublicMetadataUpdate;
};

export type UpdatePersonMetadataV2Response = UpdatePersonMetadataV2Responses[keyof UpdatePersonMetadataV2Responses];

export type PatchPeopleCurrentData = {
    body: PersonCurrentExternalPatch;
    path?: never;
    query?: never;
    url: '/api/v2/people/current';
};

export type PatchPeopleCurrentErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PatchPeopleCurrentError = PatchPeopleCurrentErrors[keyof PatchPeopleCurrentErrors];

export type PatchPeopleCurrentResponses = {
    /**
     * Successful Response
     */
    200: PersonCurrentExternalResponse;
};

export type PatchPeopleCurrentResponse = PatchPeopleCurrentResponses[keyof PatchPeopleCurrentResponses];

export type PutPeopleCurrentPasswordData = {
    body: PersonClerkPasswordPut;
    path?: never;
    query?: never;
    url: '/api/v2/people/current/password';
};

export type PutPeopleCurrentPasswordErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PutPeopleCurrentPasswordError = PutPeopleCurrentPasswordErrors[keyof PutPeopleCurrentPasswordErrors];

export type PutPeopleCurrentPasswordResponses = {
    /**
     * Successful Response
     */
    200: PersonPasswordUpdatedResponse;
};

export type PutPeopleCurrentPasswordResponse = PutPeopleCurrentPasswordResponses[keyof PutPeopleCurrentPasswordResponses];

export type PostPersonClerkUserData = {
    body: CreateClerkUserRequest;
    path: {
        /**
         * Person Uuid
         */
        person_uuid: string;
    };
    query?: never;
    url: '/api/v2/people/{person_uuid}/clerk-user';
};

export type PostPersonClerkUserErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostPersonClerkUserError = PostPersonClerkUserErrors[keyof PostPersonClerkUserErrors];

export type PostPersonClerkUserResponses = {
    /**
     * Successful Response
     */
    201: PostPersonClerkUserResponse;
};

export type PostPersonClerkUserResponse2 = PostPersonClerkUserResponses[keyof PostPersonClerkUserResponses];

export type PatchPersonEmailNotificationsData = {
    body: PersonEmailNotificationsPatch;
    path: {
        /**
         * Person Uuid
         */
        person_uuid: string;
    };
    query?: never;
    url: '/api/v2/people/{person_uuid}/email-notifications';
};

export type PatchPersonEmailNotificationsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PatchPersonEmailNotificationsError = PatchPersonEmailNotificationsErrors[keyof PatchPersonEmailNotificationsErrors];

export type PatchPersonEmailNotificationsResponses = {
    /**
     * Successful Response
     */
    200: PersonBasicResponse;
};

export type PatchPersonEmailNotificationsResponse = PatchPersonEmailNotificationsResponses[keyof PatchPersonEmailNotificationsResponses];

export type PatchPersonData = {
    body: PersonPatch;
    path: {
        /**
         * Person Uuid
         */
        person_uuid: string;
    };
    query?: never;
    url: '/api/v2/people/{person_uuid}';
};

export type PatchPersonErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PatchPersonError = PatchPersonErrors[keyof PatchPersonErrors];

export type PatchPersonResponses = {
    /**
     * Successful Response
     */
    200: PersonBasicResponse;
};

export type PatchPersonResponse = PatchPersonResponses[keyof PatchPersonResponses];

export type GetPeopleInternalData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v2/people/internal/customer_success';
};

export type GetPeopleInternalResponses = {
    /**
     * Response Get People Internal
     *
     * Successful Response
     */
    200: Array<PersonBasicResponse>;
};

export type GetPeopleInternalResponse = GetPeopleInternalResponses[keyof GetPeopleInternalResponses];

export type ResolvePersonCandidateByEmailData = {
    body: ResolvePersonCandidateByEmailRequest;
    path?: never;
    query?: never;
    url: '/api/v2/people/resolve-by-email';
};

export type ResolvePersonCandidateByEmailErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type ResolvePersonCandidateByEmailError = ResolvePersonCandidateByEmailErrors[keyof ResolvePersonCandidateByEmailErrors];

export type ResolvePersonCandidateByEmailResponses = {
    /**
     * Successful Response
     */
    200: ResolvePersonCandidateByEmailResponse;
};

export type ResolvePersonCandidateByEmailResponse2 = ResolvePersonCandidateByEmailResponses[keyof ResolvePersonCandidateByEmailResponses];

export type ListPersonContactTagsData = {
    body?: never;
    path: {
        /**
         * Person Uuid
         */
        person_uuid: string;
    };
    query?: {
        /**
         * Include Deleted
         */
        include_deleted?: boolean;
        /**
         * Include Usage Count
         */
        include_usage_count?: boolean;
        /**
         * Q
         */
        q?: string | null;
    };
    url: '/api/v2/people/{person_uuid}/contacts/tags';
};

export type ListPersonContactTagsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type ListPersonContactTagsError = ListPersonContactTagsErrors[keyof ListPersonContactTagsErrors];

export type ListPersonContactTagsResponses = {
    /**
     * Response List Person Contact Tags
     *
     * Successful Response
     */
    200: Array<PersonalTagDefinitionResponse>;
};

export type ListPersonContactTagsResponse = ListPersonContactTagsResponses[keyof ListPersonContactTagsResponses];

export type CreatePersonContactTagDefinitionData = {
    body: PersonalTagDefinitionCreatePayload;
    path: {
        /**
         * Person Uuid
         */
        person_uuid: string;
    };
    query?: never;
    url: '/api/v2/people/{person_uuid}/contacts/tags';
};

export type CreatePersonContactTagDefinitionErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type CreatePersonContactTagDefinitionError = CreatePersonContactTagDefinitionErrors[keyof CreatePersonContactTagDefinitionErrors];

export type CreatePersonContactTagDefinitionResponses = {
    /**
     * Successful Response
     */
    201: PersonalTagDefinitionResponse;
};

export type CreatePersonContactTagDefinitionResponse = CreatePersonContactTagDefinitionResponses[keyof CreatePersonContactTagDefinitionResponses];

export type DeletePersonContactTagDefinitionData = {
    body?: never;
    path: {
        /**
         * Tag Definition Uuid
         */
        tag_definition_uuid: string;
        /**
         * Person Uuid
         */
        person_uuid: string;
    };
    query?: never;
    url: '/api/v2/people/{person_uuid}/contacts/tags/{tag_definition_uuid}';
};

export type DeletePersonContactTagDefinitionErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type DeletePersonContactTagDefinitionError = DeletePersonContactTagDefinitionErrors[keyof DeletePersonContactTagDefinitionErrors];

export type DeletePersonContactTagDefinitionResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeletePersonContactTagDefinitionResponse = DeletePersonContactTagDefinitionResponses[keyof DeletePersonContactTagDefinitionResponses];

export type PatchPersonContactTagDefinitionData = {
    body: PersonalTagDefinitionPatchPayload;
    path: {
        /**
         * Tag Definition Uuid
         */
        tag_definition_uuid: string;
        /**
         * Person Uuid
         */
        person_uuid: string;
    };
    query?: never;
    url: '/api/v2/people/{person_uuid}/contacts/tags/{tag_definition_uuid}';
};

export type PatchPersonContactTagDefinitionErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PatchPersonContactTagDefinitionError = PatchPersonContactTagDefinitionErrors[keyof PatchPersonContactTagDefinitionErrors];

export type PatchPersonContactTagDefinitionResponses = {
    /**
     * Successful Response
     */
    200: PersonalTagDefinitionResponse;
};

export type PatchPersonContactTagDefinitionResponse = PatchPersonContactTagDefinitionResponses[keyof PatchPersonContactTagDefinitionResponses];

export type AddPersonContactTagData = {
    body: PersonalTagAssociationCreatePayload;
    path: {
        /**
         * Person Uuid
         */
        person_uuid: string;
        /**
         * Contact Uuid
         */
        contact_uuid: string;
    };
    query?: never;
    url: '/api/v2/people/{person_uuid}/contacts/{contact_uuid}/tags';
};

export type AddPersonContactTagErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type AddPersonContactTagError = AddPersonContactTagErrors[keyof AddPersonContactTagErrors];

export type AddPersonContactTagResponses = {
    /**
     * Successful Response
     */
    201: PersonalTagOnContactResponse;
};

export type AddPersonContactTagResponse = AddPersonContactTagResponses[keyof AddPersonContactTagResponses];

export type RemovePersonContactTagData = {
    body?: never;
    path: {
        /**
         * Contact Uuid
         */
        contact_uuid: string;
        /**
         * Person Uuid
         */
        person_uuid: string;
        /**
         * Tag Definition Uuid
         */
        tag_definition_uuid: string;
    };
    query?: never;
    url: '/api/v2/people/{person_uuid}/contacts/{contact_uuid}/tags/{tag_definition_uuid}';
};

export type RemovePersonContactTagErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type RemovePersonContactTagError = RemovePersonContactTagErrors[keyof RemovePersonContactTagErrors];

export type RemovePersonContactTagResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type RemovePersonContactTagResponse = RemovePersonContactTagResponses[keyof RemovePersonContactTagResponses];

export type PostPersonContactFavoriteData = {
    body: CandidateFavoritePayload;
    path: {
        /**
         * Person Uuid
         */
        person_uuid: string;
        /**
         * Contact Uuid
         */
        contact_uuid: string;
    };
    query?: never;
    url: '/api/v2/people/{person_uuid}/contacts/{contact_uuid}/favorite';
};

export type PostPersonContactFavoriteErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostPersonContactFavoriteError = PostPersonContactFavoriteErrors[keyof PostPersonContactFavoriteErrors];

export type PostPersonContactFavoriteResponses = {
    /**
     * Successful Response
     */
    200: ContactFavoriteResponse;
};

export type PostPersonContactFavoriteResponse = PostPersonContactFavoriteResponses[keyof PostPersonContactFavoriteResponses];

export type GetUserEventsData = {
    body?: never;
    path: {
        /**
         * User Uuid
         */
        user_uuid: string;
    };
    query?: {
        /**
         * Draft
         *
         * Filter by draft status (true=draft only, false=published only)
         */
        draft?: boolean | null;
        /**
         * Event Date After
         *
         * Return events with event_date >= this time (ISO 8601), plus events with no date (TBD). Use for future events.
         */
        event_date_after?: string | null;
        /**
         * Event Date Before
         *
         * Return only events with event_date <= this time (ISO 8601). Use for past events.
         */
        event_date_before?: string | null;
        /**
         * Sort Event Date
         *
         * Sort by event_date: asc = soonest first (e.g. upcoming), desc = most recent first (e.g. past).
         */
        sort_event_date?: 'asc' | 'desc';
        /**
         * Page
         *
         * Page number
         */
        page?: number;
        /**
         * Size
         *
         * Page size
         */
        size?: number;
    };
    url: '/api/v2/users/{user_uuid}/events';
};

export type GetUserEventsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetUserEventsError = GetUserEventsErrors[keyof GetUserEventsErrors];

export type GetUserEventsResponses = {
    /**
     * Successful Response
     */
    200: PageEventResponse;
};

export type GetUserEventsResponse = GetUserEventsResponses[keyof GetUserEventsResponses];

export type DeleteUserV2Data = {
    body?: never;
    path: {
        /**
         * User Uuid
         */
        user_uuid: string;
    };
    query?: never;
    url: '/api/v2/users/{user_uuid}';
};

export type DeleteUserV2Errors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type DeleteUserV2Error = DeleteUserV2Errors[keyof DeleteUserV2Errors];

export type DeleteUserV2Responses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteUserV2Response = DeleteUserV2Responses[keyof DeleteUserV2Responses];

export type GetUserV2Data = {
    body?: never;
    path: {
        /**
         * User Uuid
         */
        user_uuid: string;
    };
    query?: never;
    url: '/api/v2/users/{user_uuid}';
};

export type GetUserV2Errors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetUserV2Error = GetUserV2Errors[keyof GetUserV2Errors];

export type GetUserV2Responses = {
    /**
     * Successful Response
     */
    200: AdminUserDetailedResponse;
};

export type GetUserV2Response = GetUserV2Responses[keyof GetUserV2Responses];

export type PostUsersBulkDeleteV2Data = {
    body: UsersBulkDeletePayload;
    path?: never;
    query?: never;
    url: '/api/v2/users/bulk-delete';
};

export type PostUsersBulkDeleteV2Errors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostUsersBulkDeleteV2Error = PostUsersBulkDeleteV2Errors[keyof PostUsersBulkDeleteV2Errors];

export type PostUsersBulkDeleteV2Responses = {
    /**
     * Successful Response
     */
    200: UsersBulkDeleteResponse;
};

export type PostUsersBulkDeleteV2Response = PostUsersBulkDeleteV2Responses[keyof PostUsersBulkDeleteV2Responses];

export type PostUsersSearchV2Data = {
    body?: UsersSearchFilterSortPayload;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Page number
         */
        page?: number;
        /**
         * Size
         *
         * Page size
         */
        size?: number;
    };
    url: '/api/v2/users/search';
};

export type PostUsersSearchV2Errors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostUsersSearchV2Error = PostUsersSearchV2Errors[keyof PostUsersSearchV2Errors];

export type PostUsersSearchV2Responses = {
    /**
     * Successful Response
     */
    200: UsersPostPageUserDetailedResponse;
};

export type PostUsersSearchV2Response = PostUsersSearchV2Responses[keyof PostUsersSearchV2Responses];

export type ListEmailTemplatesData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Template Type
         *
         * Filter by template type (e.g. event_invite, event_rsvp_confirmation_accepted).
         */
        template_type?: EmailTemplateType | null;
        /**
         * Category
         *
         * Only types of this category (see GET /email-types).
         */
        category?: EmailCategory | null;
        /**
         * Page
         *
         * Page number
         */
        page?: number;
        /**
         * Size
         *
         * Page size
         */
        size?: number;
    };
    url: '/api/v2/email-templates';
};

export type ListEmailTemplatesErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type ListEmailTemplatesError = ListEmailTemplatesErrors[keyof ListEmailTemplatesErrors];

export type ListEmailTemplatesResponses = {
    /**
     * Successful Response
     */
    200: PageEmailTemplateResponse;
};

export type ListEmailTemplatesResponse = ListEmailTemplatesResponses[keyof ListEmailTemplatesResponses];

export type CreateEmailTemplateData = {
    body: EmailTemplateCreatePayload;
    path?: never;
    query?: never;
    url: '/api/v2/email-templates';
};

export type CreateEmailTemplateErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type CreateEmailTemplateError = CreateEmailTemplateErrors[keyof CreateEmailTemplateErrors];

export type CreateEmailTemplateResponses = {
    /**
     * Successful Response
     */
    201: EmailTemplateResponse;
};

export type CreateEmailTemplateResponse = CreateEmailTemplateResponses[keyof CreateEmailTemplateResponses];

export type ListEmailTemplateDefaultsData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Category
         *
         * Only types of this category (see GET /email-types).
         */
        category?: EmailCategory | null;
    };
    url: '/api/v2/email-templates/defaults';
};

export type ListEmailTemplateDefaultsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type ListEmailTemplateDefaultsError = ListEmailTemplateDefaultsErrors[keyof ListEmailTemplateDefaultsErrors];

export type ListEmailTemplateDefaultsResponses = {
    /**
     * Response List Email Template Defaults
     *
     * Successful Response
     */
    200: Array<EmailTemplateDefaultResponse>;
};

export type ListEmailTemplateDefaultsResponse = ListEmailTemplateDefaultsResponses[keyof ListEmailTemplateDefaultsResponses];

export type DeleteEmailTemplateDefaultData = {
    body?: never;
    path: {
        template_type: EmailTemplateType;
    };
    query?: never;
    url: '/api/v2/email-templates/defaults/{template_type}';
};

export type DeleteEmailTemplateDefaultErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type DeleteEmailTemplateDefaultError = DeleteEmailTemplateDefaultErrors[keyof DeleteEmailTemplateDefaultErrors];

export type DeleteEmailTemplateDefaultResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteEmailTemplateDefaultResponse = DeleteEmailTemplateDefaultResponses[keyof DeleteEmailTemplateDefaultResponses];

export type PutEmailTemplateDefaultData = {
    body: EmailTemplateDefaultPayload;
    path: {
        template_type: EmailTemplateType;
    };
    query?: never;
    url: '/api/v2/email-templates/defaults/{template_type}';
};

export type PutEmailTemplateDefaultErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PutEmailTemplateDefaultError = PutEmailTemplateDefaultErrors[keyof PutEmailTemplateDefaultErrors];

export type PutEmailTemplateDefaultResponses = {
    /**
     * Successful Response
     */
    200: EmailTemplateDefaultResponse;
};

export type PutEmailTemplateDefaultResponse = PutEmailTemplateDefaultResponses[keyof PutEmailTemplateDefaultResponses];

export type DeleteEmailTemplateData = {
    body?: never;
    path: {
        /**
         * Template Uuid
         */
        template_uuid: string;
    };
    query?: never;
    url: '/api/v2/email-templates/{template_uuid}';
};

export type DeleteEmailTemplateErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type DeleteEmailTemplateError = DeleteEmailTemplateErrors[keyof DeleteEmailTemplateErrors];

export type DeleteEmailTemplateResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteEmailTemplateResponse = DeleteEmailTemplateResponses[keyof DeleteEmailTemplateResponses];

export type GetEmailTemplateData = {
    body?: never;
    path: {
        /**
         * Template Uuid
         */
        template_uuid: string;
    };
    query?: never;
    url: '/api/v2/email-templates/{template_uuid}';
};

export type GetEmailTemplateErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetEmailTemplateError = GetEmailTemplateErrors[keyof GetEmailTemplateErrors];

export type GetEmailTemplateResponses = {
    /**
     * Successful Response
     */
    200: EmailTemplateResponse;
};

export type GetEmailTemplateResponse = GetEmailTemplateResponses[keyof GetEmailTemplateResponses];

export type UpdateEmailTemplateData = {
    body: EmailTemplateUpdatePayload;
    path: {
        /**
         * Template Uuid
         */
        template_uuid: string;
    };
    query?: never;
    url: '/api/v2/email-templates/{template_uuid}';
};

export type UpdateEmailTemplateErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type UpdateEmailTemplateError = UpdateEmailTemplateErrors[keyof UpdateEmailTemplateErrors];

export type UpdateEmailTemplateResponses = {
    /**
     * Successful Response
     */
    200: EmailTemplateResponse;
};

export type UpdateEmailTemplateResponse = UpdateEmailTemplateResponses[keyof UpdateEmailTemplateResponses];

export type ListEmailTypesData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v2/email-types';
};

export type ListEmailTypesResponses = {
    /**
     * Response List Email Types
     *
     * Successful Response
     */
    200: Array<EmailTypeResponse>;
};

export type ListEmailTypesResponse = ListEmailTypesResponses[keyof ListEmailTypesResponses];

export type GetEmailPreviewData = {
    body?: never;
    path: {
        /**
         * Key
         */
        key: string;
    };
    query?: never;
    url: '/api/v2/email-types/previews/{key}';
};

export type GetEmailPreviewErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetEmailPreviewError = GetEmailPreviewErrors[keyof GetEmailPreviewErrors];

export type GetEmailPreviewResponses = {
    /**
     * Successful Response
     */
    200: EmailPreviewResponse;
};

export type GetEmailPreviewResponse = GetEmailPreviewResponses[keyof GetEmailPreviewResponses];

export type ListEventEmailTemplatesData = {
    body?: never;
    path: {
        /**
         * Event Uuid
         */
        event_uuid: string;
    };
    query?: {
        /**
         * Template Type
         *
         * Only templates of this type.
         */
        template_type?: EmailTemplateType | null;
    };
    url: '/api/v2/events/{event_uuid}/email-templates';
};

export type ListEventEmailTemplatesErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type ListEventEmailTemplatesError = ListEventEmailTemplatesErrors[keyof ListEventEmailTemplatesErrors];

export type ListEventEmailTemplatesResponses = {
    /**
     * Response List Event Email Templates
     *
     * Successful Response
     */
    200: Array<EmailTemplateResponse>;
};

export type ListEventEmailTemplatesResponse = ListEventEmailTemplatesResponses[keyof ListEventEmailTemplatesResponses];

export type CreateEventEmailTemplateData = {
    body: EventEmailTemplateCreatePayload;
    path: {
        /**
         * Event Uuid
         */
        event_uuid: string;
    };
    query?: never;
    url: '/api/v2/events/{event_uuid}/email-templates';
};

export type CreateEventEmailTemplateErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type CreateEventEmailTemplateError = CreateEventEmailTemplateErrors[keyof CreateEventEmailTemplateErrors];

export type CreateEventEmailTemplateResponses = {
    /**
     * Successful Response
     */
    201: EmailTemplateResponse;
};

export type CreateEventEmailTemplateResponse = CreateEventEmailTemplateResponses[keyof CreateEventEmailTemplateResponses];

export type DeleteEventEmailTemplateData = {
    body?: never;
    path: {
        /**
         * Event Uuid
         */
        event_uuid: string;
        /**
         * Template Uuid
         */
        template_uuid: string;
    };
    query?: never;
    url: '/api/v2/events/{event_uuid}/email-templates/{template_uuid}';
};

export type DeleteEventEmailTemplateErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type DeleteEventEmailTemplateError = DeleteEventEmailTemplateErrors[keyof DeleteEventEmailTemplateErrors];

export type DeleteEventEmailTemplateResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteEventEmailTemplateResponse = DeleteEventEmailTemplateResponses[keyof DeleteEventEmailTemplateResponses];

export type UpdateEventEmailTemplateData = {
    body: EventEmailTemplateUpdatePayload;
    path: {
        /**
         * Event Uuid
         */
        event_uuid: string;
        /**
         * Template Uuid
         */
        template_uuid: string;
    };
    query?: never;
    url: '/api/v2/events/{event_uuid}/email-templates/{template_uuid}';
};

export type UpdateEventEmailTemplateErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type UpdateEventEmailTemplateError = UpdateEventEmailTemplateErrors[keyof UpdateEventEmailTemplateErrors];

export type UpdateEventEmailTemplateResponses = {
    /**
     * Successful Response
     */
    200: EmailTemplateResponse;
};

export type UpdateEventEmailTemplateResponse = UpdateEventEmailTemplateResponses[keyof UpdateEventEmailTemplateResponses];

export type PreviewEventEmailData = {
    body: EventEmailPreviewPayload;
    path: {
        /**
         * Event Uuid
         */
        event_uuid: string;
    };
    query?: never;
    url: '/api/v2/events/{event_uuid}/email-templates/preview';
};

export type PreviewEventEmailErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PreviewEventEmailError = PreviewEventEmailErrors[keyof PreviewEventEmailErrors];

export type PreviewEventEmailResponses = {
    /**
     * Successful Response
     */
    202: QueuedMessageResponse;
};

export type PreviewEventEmailResponse = PreviewEventEmailResponses[keyof PreviewEventEmailResponses];

export type PostPotentialGuestsData = {
    body?: PotentialGuestsSearchPayload;
    path?: never;
    query?: never;
    url: '/api/v2/events/potential-guests';
};

export type PostPotentialGuestsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostPotentialGuestsError = PostPotentialGuestsErrors[keyof PostPotentialGuestsErrors];

export type PostPotentialGuestsResponses = {
    /**
     * Successful Response
     */
    200: PotentialGuestsSearchResponse;
};

export type PostPotentialGuestsResponse = PostPotentialGuestsResponses[keyof PostPotentialGuestsResponses];

export type PostPotentialGuestsSelectionData = {
    body: PotentialGuestsEmailsPayload;
    path?: never;
    query?: never;
    url: '/api/v2/events/potential-guests/selection';
};

export type PostPotentialGuestsSelectionErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostPotentialGuestsSelectionError = PostPotentialGuestsSelectionErrors[keyof PostPotentialGuestsSelectionErrors];

export type PostPotentialGuestsSelectionResponses = {
    /**
     * Successful Response
     */
    200: PotentialGuestsEmailsResponse;
};

export type PostPotentialGuestsSelectionResponse = PostPotentialGuestsSelectionResponses[keyof PostPotentialGuestsSelectionResponses];

export type GetEventsData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Draft
         *
         * Filter by draft status (true=draft only, false=published only)
         */
        draft?: boolean | null;
        /**
         * Event Date After
         *
         * Return events with event_date >= this time (ISO 8601), plus events with no date (TBD). Use for future events.
         */
        event_date_after?: string | null;
        /**
         * Event Date Before
         *
         * Return only events with event_date <= this time (ISO 8601). Use for past events.
         */
        event_date_before?: string | null;
        /**
         * Sort Event Date
         *
         * Sort by event_date: asc = soonest first (e.g. upcoming), desc = most recent first (e.g. past).
         */
        sort_event_date?: 'asc' | 'desc';
        /**
         * Phrase
         *
         * Case-insensitive substring search across title, location, description, notes, additional_link, legacy category, and categories (when set).
         */
        phrase?: string | null;
        /**
         * Page
         *
         * Page number
         */
        page?: number;
        /**
         * Size
         *
         * Page size
         */
        size?: number;
    };
    url: '/api/v2/events';
};

export type GetEventsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetEventsError = GetEventsErrors[keyof GetEventsErrors];

export type GetEventsResponses = {
    /**
     * Successful Response
     */
    200: PageEventResponse;
};

export type GetEventsResponse = GetEventsResponses[keyof GetEventsResponses];

export type PostEventData = {
    body: EventCreatePayload;
    path?: never;
    query?: never;
    url: '/api/v2/events';
};

export type PostEventErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostEventError = PostEventErrors[keyof PostEventErrors];

export type PostEventResponses = {
    /**
     * Successful Response
     */
    200: EventResponse;
};

export type PostEventResponse = PostEventResponses[keyof PostEventResponses];

export type GetAllEventGuestsData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Page number
         */
        page?: number;
        /**
         * Size
         *
         * Page size
         */
        size?: number;
    };
    url: '/api/v2/events/guests';
};

export type GetAllEventGuestsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetAllEventGuestsError = GetAllEventGuestsErrors[keyof GetAllEventGuestsErrors];

export type GetAllEventGuestsResponses = {
    /**
     * Successful Response
     */
    200: PageEventGuestWithEventItemResponse;
};

export type GetAllEventGuestsResponse = GetAllEventGuestsResponses[keyof GetAllEventGuestsResponses];

export type GetEventRsvpLandingData = {
    body?: never;
    path?: never;
    query: {
        /**
         * Token
         *
         * Event RSVP token from the invite link
         */
        token: string;
    };
    url: '/api/v2/events/rsvp';
};

export type GetEventRsvpLandingErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetEventRsvpLandingError = GetEventRsvpLandingErrors[keyof GetEventRsvpLandingErrors];

export type GetEventRsvpLandingResponses = {
    /**
     * Successful Response
     */
    200: EventRsvpLandingResponse;
};

export type GetEventRsvpLandingResponse = GetEventRsvpLandingResponses[keyof GetEventRsvpLandingResponses];

export type PatchEventRsvpData = {
    body: RsvpUpdatePayload;
    path?: never;
    query?: never;
    url: '/api/v2/events/rsvp';
};

export type PatchEventRsvpErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PatchEventRsvpError = PatchEventRsvpErrors[keyof PatchEventRsvpErrors];

export type PatchEventRsvpResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type PatchEventRsvpResponse = PatchEventRsvpResponses[keyof PatchEventRsvpResponses];

export type GetEventRsvpLandingImageData = {
    body?: never;
    path?: never;
    query: {
        /**
         * Token
         *
         * Event RSVP token from the invite link
         */
        token: string;
    };
    url: '/api/v2/events/rsvp/landing-image';
};

export type GetEventRsvpLandingImageErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetEventRsvpLandingImageError = GetEventRsvpLandingImageErrors[keyof GetEventRsvpLandingImageErrors];

export type GetEventRsvpLandingImageResponses = {
    /**
     * Original landing image bytes.
     */
    200: unknown;
};

export type GetEventRsvpPreviewData = {
    body?: never;
    path: {
        /**
         * Event Uuid
         */
        event_uuid: string;
    };
    query?: never;
    url: '/api/v2/events/{event_uuid}/rsvp-preview';
};

export type GetEventRsvpPreviewErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetEventRsvpPreviewError = GetEventRsvpPreviewErrors[keyof GetEventRsvpPreviewErrors];

export type GetEventRsvpPreviewResponses = {
    /**
     * Successful Response
     */
    200: EventRsvpLandingResponse;
};

export type GetEventRsvpPreviewResponse = GetEventRsvpPreviewResponses[keyof GetEventRsvpPreviewResponses];

export type PostEventRsvpPreviewData = {
    body: RsvpPreviewPayload;
    path: {
        /**
         * Event Uuid
         */
        event_uuid: string;
    };
    query?: never;
    url: '/api/v2/events/{event_uuid}/rsvp-preview';
};

export type PostEventRsvpPreviewErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostEventRsvpPreviewError = PostEventRsvpPreviewErrors[keyof PostEventRsvpPreviewErrors];

export type PostEventRsvpPreviewResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type PostEventRsvpPreviewResponse = PostEventRsvpPreviewResponses[keyof PostEventRsvpPreviewResponses];

export type DeleteEventData = {
    body?: never;
    path: {
        /**
         * Event Uuid
         */
        event_uuid: string;
    };
    query?: never;
    url: '/api/v2/events/{event_uuid}';
};

export type DeleteEventErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type DeleteEventError = DeleteEventErrors[keyof DeleteEventErrors];

export type DeleteEventResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteEventResponse = DeleteEventResponses[keyof DeleteEventResponses];

export type GetEventData = {
    body?: never;
    path: {
        /**
         * Event Uuid
         */
        event_uuid: string;
    };
    query?: never;
    url: '/api/v2/events/{event_uuid}';
};

export type GetEventErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetEventError = GetEventErrors[keyof GetEventErrors];

export type GetEventResponses = {
    /**
     * Successful Response
     */
    200: EventResponse;
};

export type GetEventResponse = GetEventResponses[keyof GetEventResponses];

export type PatchEventData = {
    body: EventUpdatePayload;
    path: {
        /**
         * Event Uuid
         */
        event_uuid: string;
    };
    query?: never;
    url: '/api/v2/events/{event_uuid}';
};

export type PatchEventErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PatchEventError = PatchEventErrors[keyof PatchEventErrors];

export type PatchEventResponses = {
    /**
     * Successful Response
     */
    200: EventResponse;
};

export type PatchEventResponse = PatchEventResponses[keyof PatchEventResponses];

export type GetEventLandingImagePublicData = {
    body?: never;
    path: {
        /**
         * Event Uuid
         */
        event_uuid: string;
    };
    query?: never;
    url: '/api/v2/events/{event_uuid}/landing-image';
};

export type GetEventLandingImagePublicErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetEventLandingImagePublicError = GetEventLandingImagePublicErrors[keyof GetEventLandingImagePublicErrors];

export type GetEventLandingImagePublicResponses = {
    /**
     * Original landing image bytes.
     */
    200: unknown;
};

export type DeleteEventLandingImageData = {
    body?: never;
    path: {
        /**
         * Event Uuid
         */
        event_uuid: string;
    };
    query?: never;
    url: '/api/v2/events/{event_uuid}/image';
};

export type DeleteEventLandingImageErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type DeleteEventLandingImageError = DeleteEventLandingImageErrors[keyof DeleteEventLandingImageErrors];

export type DeleteEventLandingImageResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteEventLandingImageResponse = DeleteEventLandingImageResponses[keyof DeleteEventLandingImageResponses];

export type PostEventLandingImageData = {
    body: BodyPostEventLandingImage;
    path: {
        /**
         * Event Uuid
         */
        event_uuid: string;
    };
    query?: never;
    url: '/api/v2/events/{event_uuid}/image';
};

export type PostEventLandingImageErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostEventLandingImageError = PostEventLandingImageErrors[keyof PostEventLandingImageErrors];

export type PostEventLandingImageResponses = {
    /**
     * Successful Response
     */
    200: ImageUploadResponse;
};

export type PostEventLandingImageResponse = PostEventLandingImageResponses[keyof PostEventLandingImageResponses];

export type PostEventInviteSendData = {
    body?: SendInvitePayload;
    path: {
        /**
         * Event Uuid
         */
        event_uuid: string;
    };
    query?: never;
    url: '/api/v2/events/{event_uuid}/invite/send';
};

export type PostEventInviteSendErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostEventInviteSendError = PostEventInviteSendErrors[keyof PostEventInviteSendErrors];

export type PostEventInviteSendResponses = {
    /**
     * Successful Response
     */
    202: QueuedMessageResponse;
};

export type PostEventInviteSendResponse = PostEventInviteSendResponses[keyof PostEventInviteSendResponses];

export type PostEventInvitePreviewData = {
    body?: InvitePreviewPayload;
    path: {
        /**
         * Event Uuid
         */
        event_uuid: string;
    };
    query?: never;
    url: '/api/v2/events/{event_uuid}/invite/preview';
};

export type PostEventInvitePreviewErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostEventInvitePreviewError = PostEventInvitePreviewErrors[keyof PostEventInvitePreviewErrors];

export type PostEventInvitePreviewResponses = {
    /**
     * Successful Response
     */
    202: QueuedMessageResponse;
};

export type PostEventInvitePreviewResponse = PostEventInvitePreviewResponses[keyof PostEventInvitePreviewResponses];

export type PostEventReminderPreviewData = {
    body?: ReminderPreviewPayload;
    path: {
        /**
         * Event Uuid
         */
        event_uuid: string;
    };
    query?: never;
    url: '/api/v2/events/{event_uuid}/reminder/preview';
};

export type PostEventReminderPreviewErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostEventReminderPreviewError = PostEventReminderPreviewErrors[keyof PostEventReminderPreviewErrors];

export type PostEventReminderPreviewResponses = {
    /**
     * Successful Response
     */
    202: QueuedMessageResponse;
};

export type PostEventReminderPreviewResponse = PostEventReminderPreviewResponses[keyof PostEventReminderPreviewResponses];

export type PostEventReminderSendData = {
    body?: SendReminderPayload;
    path: {
        /**
         * Event Uuid
         */
        event_uuid: string;
    };
    query?: never;
    url: '/api/v2/events/{event_uuid}/reminder/send';
};

export type PostEventReminderSendErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostEventReminderSendError = PostEventReminderSendErrors[keyof PostEventReminderSendErrors];

export type PostEventReminderSendResponses = {
    /**
     * Successful Response
     */
    202: QueuedMessageResponse;
};

export type PostEventReminderSendResponse = PostEventReminderSendResponses[keyof PostEventReminderSendResponses];

export type PostEventAnnouncementSendData = {
    body: SendAnnouncementPayload;
    path: {
        /**
         * Event Uuid
         */
        event_uuid: string;
    };
    query?: never;
    url: '/api/v2/events/{event_uuid}/announcement/send';
};

export type PostEventAnnouncementSendErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostEventAnnouncementSendError = PostEventAnnouncementSendErrors[keyof PostEventAnnouncementSendErrors];

export type PostEventAnnouncementSendResponses = {
    /**
     * Successful Response
     */
    202: QueuedMessageResponse;
};

export type PostEventAnnouncementSendResponse = PostEventAnnouncementSendResponses[keyof PostEventAnnouncementSendResponses];

export type GetEventEmailEventsData = {
    body?: never;
    path: {
        /**
         * Event Uuid
         */
        event_uuid: string;
    };
    query?: {
        /**
         * Page
         *
         * Page number
         */
        page?: number;
        /**
         * Size
         *
         * Page size
         */
        size?: number;
        /**
         * To Identity Uuid
         *
         * Only the emails sent to this identity.
         */
        to_identity_uuid?: string | null;
        /**
         * Batch Uuid
         *
         * Only the emails of this send run.
         */
        batch_uuid?: string | null;
        /**
         * Email Event Type
         *
         * Only the emails of these types (invite, reminder, summary and so on). Several values match an email of any of them.
         */
        email_event_type?: Array<EmailEventType> | null;
        /**
         * Sendgrid Event
         *
         * Only the emails SendGrid reported at least one of these events for. Several values match an email with any of them.
         */
        sendgrid_event?: Array<SendGridEmailEvent> | null;
    };
    url: '/api/v2/events/{event_uuid}/email-events';
};

export type GetEventEmailEventsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetEventEmailEventsError = GetEventEmailEventsErrors[keyof GetEventEmailEventsErrors];

export type GetEventEmailEventsResponses = {
    /**
     * Successful Response
     */
    200: PageEventEmailEventItemResponse;
};

export type GetEventEmailEventsResponse = GetEventEmailEventsResponses[keyof GetEventEmailEventsResponses];

export type GetEventEmailBatchesData = {
    body?: never;
    path: {
        /**
         * Event Uuid
         */
        event_uuid: string;
    };
    query?: {
        /**
         * Page
         *
         * Page number
         */
        page?: number;
        /**
         * Size
         *
         * Page size
         */
        size?: number;
        /**
         * To Identity Uuid
         *
         * Only the emails sent to this identity.
         */
        to_identity_uuid?: string | null;
        /**
         * Batch Uuid
         *
         * Only the emails of this send run.
         */
        batch_uuid?: string | null;
        /**
         * Email Event Type
         *
         * Only the emails of these types (invite, reminder, summary and so on). Several values match an email of any of them.
         */
        email_event_type?: Array<EmailEventType> | null;
        /**
         * Sendgrid Event
         *
         * Only the emails SendGrid reported at least one of these events for. Several values match an email with any of them.
         */
        sendgrid_event?: Array<SendGridEmailEvent> | null;
    };
    url: '/api/v2/events/{event_uuid}/email-batches';
};

export type GetEventEmailBatchesErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetEventEmailBatchesError = GetEventEmailBatchesErrors[keyof GetEventEmailBatchesErrors];

export type GetEventEmailBatchesResponses = {
    /**
     * Successful Response
     */
    200: PageEventEmailBatchResponse;
};

export type GetEventEmailBatchesResponse = GetEventEmailBatchesResponses[keyof GetEventEmailBatchesResponses];

export type GetEventGuestsData = {
    body?: never;
    path: {
        /**
         * Event Uuid
         */
        event_uuid: string;
    };
    query?: {
        /**
         * Status
         */
        status?: EventGuestStatus | null;
        /**
         * Client Type
         */
        client_type?: Array<EventGuestClientType> | null;
        /**
         * Phrase
         */
        phrase?: string | null;
        /**
         * Sort
         */
        sort?: EventGuestsSortColumn | null;
        order?: SortDirection;
        /**
         * Page
         */
        page?: number;
        /**
         * Size
         */
        size?: number;
    };
    url: '/api/v2/events/{event_uuid}/guests';
};

export type GetEventGuestsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetEventGuestsError = GetEventGuestsErrors[keyof GetEventGuestsErrors];

export type GetEventGuestsResponses = {
    /**
     * Successful Response
     */
    200: EventGuestsPageEventGuestItemResponse;
};

export type GetEventGuestsResponse = GetEventGuestsResponses[keyof GetEventGuestsResponses];

export type PatchEventGuestsData = {
    body: EventGuestsBulkStatusPayload;
    path: {
        /**
         * Event Uuid
         */
        event_uuid: string;
    };
    query?: never;
    url: '/api/v2/events/{event_uuid}/guests';
};

export type PatchEventGuestsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PatchEventGuestsError = PatchEventGuestsErrors[keyof PatchEventGuestsErrors];

export type PatchEventGuestsResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type PatchEventGuestsResponse = PatchEventGuestsResponses[keyof PatchEventGuestsResponses];

export type PostEventGuestsData = {
    body: AddGuestsPayload;
    path: {
        /**
         * Event Uuid
         */
        event_uuid: string;
    };
    query?: never;
    url: '/api/v2/events/{event_uuid}/guests';
};

export type PostEventGuestsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostEventGuestsError = PostEventGuestsErrors[keyof PostEventGuestsErrors];

export type PostEventGuestsResponses = {
    /**
     * Successful Response
     */
    200: AddGuestsResponse;
};

export type PostEventGuestsResponse = PostEventGuestsResponses[keyof PostEventGuestsResponses];

export type PostEventGuestsSelectionData = {
    body: EventGuestsSelectionPayload;
    path: {
        /**
         * Event Uuid
         */
        event_uuid: string;
    };
    query?: never;
    url: '/api/v2/events/{event_uuid}/guests/selection';
};

export type PostEventGuestsSelectionErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostEventGuestsSelectionError = PostEventGuestsSelectionErrors[keyof PostEventGuestsSelectionErrors];

export type PostEventGuestsSelectionResponses = {
    /**
     * Successful Response
     */
    200: EventGuestsSelectionResponse;
};

export type PostEventGuestsSelectionResponse = PostEventGuestsSelectionResponses[keyof PostEventGuestsSelectionResponses];

export type DeleteEventGuestData = {
    body?: never;
    path: {
        /**
         * Event Uuid
         */
        event_uuid: string;
        /**
         * Guest Uuid
         */
        guest_uuid: string;
    };
    query?: never;
    url: '/api/v2/events/{event_uuid}/guests/{guest_uuid}';
};

export type DeleteEventGuestErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type DeleteEventGuestError = DeleteEventGuestErrors[keyof DeleteEventGuestErrors];

export type DeleteEventGuestResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteEventGuestResponse = DeleteEventGuestResponses[keyof DeleteEventGuestResponses];

export type PatchEventGuestData = {
    body: EventGuestPatchPayload;
    path: {
        /**
         * Event Uuid
         */
        event_uuid: string;
        /**
         * Guest Uuid
         */
        guest_uuid: string;
    };
    query?: never;
    url: '/api/v2/events/{event_uuid}/guests/{guest_uuid}';
};

export type PatchEventGuestErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PatchEventGuestError = PatchEventGuestErrors[keyof PatchEventGuestErrors];

export type PatchEventGuestResponses = {
    /**
     * Successful Response
     */
    200: EventGuestItemResponse;
};

export type PatchEventGuestResponse = PatchEventGuestResponses[keyof PatchEventGuestResponses];

export type PostTeamMembersV2Data = {
    body: TeamMembersPost;
    path: {
        /**
         * Team Slug
         */
        team_slug: string;
    };
    query?: never;
    url: '/api/v2/teams/{team_slug}/members';
};

export type PostTeamMembersV2Errors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostTeamMembersV2Error = PostTeamMembersV2Errors[keyof PostTeamMembersV2Errors];

export type PostTeamMembersV2Responses = {
    /**
     * Successful Response
     */
    200: unknown;
};

export type PostTeamMembersBulkV2Data = {
    body: TeamMembersBulkInvitePayload;
    path: {
        /**
         * Team Slug
         */
        team_slug: string;
    };
    query?: never;
    url: '/api/v2/teams/{team_slug}/members/bulk';
};

export type PostTeamMembersBulkV2Errors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostTeamMembersBulkV2Error = PostTeamMembersBulkV2Errors[keyof PostTeamMembersBulkV2Errors];

export type PostTeamMembersBulkV2Responses = {
    /**
     * Successful Response
     */
    200: unknown;
};

export type GetTeamIntroInsightData = {
    body?: never;
    path: {
        /**
         * Team Uuid
         */
        team_uuid: string;
    };
    query?: never;
    url: '/api/v2/teams/{team_uuid}/intro-insight';
};

export type GetTeamIntroInsightErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetTeamIntroInsightError = GetTeamIntroInsightErrors[keyof GetTeamIntroInsightErrors];

export type GetTeamIntroInsightResponses = {
    /**
     * Successful Response
     */
    200: TeamIntroInsight;
};

export type GetTeamIntroInsightResponse = GetTeamIntroInsightResponses[keyof GetTeamIntroInsightResponses];

export type PostSubmissionsData = {
    body: SubmissionV3Payload;
    path?: never;
    query?: never;
    url: '/api/v3/submissions';
};

export type PostSubmissionsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostSubmissionsError = PostSubmissionsErrors[keyof PostSubmissionsErrors];

export type PostSubmissionsResponses = {
    /**
     * Successful Response
     */
    200: SubmissionV3InternalResponse;
};

export type PostSubmissionsResponse = PostSubmissionsResponses[keyof PostSubmissionsResponses];

export type PostSubmissionEmailPreviewData = {
    body: SubmissionV3Payload;
    path?: never;
    query?: {
        /**
         * Team Uuid
         */
        team_uuid?: string | null;
    };
    url: '/api/v3/submissions/email-preview';
};

export type PostSubmissionEmailPreviewErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostSubmissionEmailPreviewError = PostSubmissionEmailPreviewErrors[keyof PostSubmissionEmailPreviewErrors];

export type PostSubmissionEmailPreviewResponses = {
    /**
     * Successful Response
     */
    200: string;
};

export type PostSubmissionEmailPreviewResponse = PostSubmissionEmailPreviewResponses[keyof PostSubmissionEmailPreviewResponses];

export type GetTrendingMessageKeysData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v3/submissions/trending-message-keys';
};

export type GetTrendingMessageKeysResponses = {
    /**
     * Successful Response
     */
    200: TrendingMessageKeysResponse;
};

export type GetTrendingMessageKeysResponse = GetTrendingMessageKeysResponses[keyof GetTrendingMessageKeysResponses];

export type GetTrendingMessagesData = {
    body?: never;
    path?: never;
    query: {
        /**
         * Candidate Uuid
         */
        candidate_uuid: string;
    };
    url: '/api/v3/submissions/trending-messages';
};

export type GetTrendingMessagesErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetTrendingMessagesError = GetTrendingMessagesErrors[keyof GetTrendingMessagesErrors];

export type GetTrendingMessagesResponses = {
    /**
     * Response Get Trending Messages
     *
     * Successful Response
     */
    200: Array<TrendingMessageResponse>;
};

export type GetTrendingMessagesResponse = GetTrendingMessagesResponses[keyof GetTrendingMessagesResponses];

export type PostSubmissionCancelRecommendationsData = {
    body?: never;
    path: {
        /**
         * Submission Uuid
         */
        submission_uuid: string;
    };
    query?: never;
    url: '/api/v3/submissions/{submission_uuid}/scheduled';
};

export type PostSubmissionCancelRecommendationsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostSubmissionCancelRecommendationsError = PostSubmissionCancelRecommendationsErrors[keyof PostSubmissionCancelRecommendationsErrors];

export type PostSubmissionCancelRecommendationsResponses = {
    /**
     * Successful Response
     */
    200: SubmissionV3InternalResponse;
};

export type PostSubmissionCancelRecommendationsResponse = PostSubmissionCancelRecommendationsResponses[keyof PostSubmissionCancelRecommendationsResponses];

export type DeleteSubmissionData = {
    body?: never;
    path: {
        /**
         * Submission Uuid
         */
        submission_uuid: string;
    };
    query?: never;
    url: '/api/v3/submissions/{submission_uuid}';
};

export type DeleteSubmissionErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type DeleteSubmissionError = DeleteSubmissionErrors[keyof DeleteSubmissionErrors];

export type DeleteSubmissionResponses = {
    /**
     * Successful Response
     */
    200: SubmissionV3InternalResponse;
};

export type DeleteSubmissionResponse = DeleteSubmissionResponses[keyof DeleteSubmissionResponses];

export type GetSubmissionData = {
    body?: never;
    path: {
        /**
         * Submission Uuid
         */
        submission_uuid: string;
    };
    query?: never;
    url: '/api/v3/submissions/{submission_uuid}';
};

export type GetSubmissionErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetSubmissionError = GetSubmissionErrors[keyof GetSubmissionErrors];

export type GetSubmissionResponses = {
    /**
     * Successful Response
     */
    200: SubmissionV3InternalEmailStatsResponse;
};

export type GetSubmissionResponse = GetSubmissionResponses[keyof GetSubmissionResponses];

export type PutSubmissionData = {
    body: SubmissionV3Payload;
    path: {
        /**
         * Submission Uuid
         */
        submission_uuid: string;
    };
    query?: never;
    url: '/api/v3/submissions/{submission_uuid}';
};

export type PutSubmissionErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PutSubmissionError = PutSubmissionErrors[keyof PutSubmissionErrors];

export type PutSubmissionResponses = {
    /**
     * Successful Response
     */
    200: SubmissionV3InternalResponse;
};

export type PutSubmissionResponse = PutSubmissionResponses[keyof PutSubmissionResponses];

export type PutSubmissionStatusData = {
    body: SubmissionStatusPayload;
    path: {
        /**
         * Submission Uuid
         */
        submission_uuid: string;
    };
    query?: never;
    url: '/api/v3/submissions/{submission_uuid}/status';
};

export type PutSubmissionStatusErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PutSubmissionStatusError = PutSubmissionStatusErrors[keyof PutSubmissionStatusErrors];

export type PutSubmissionStatusResponses = {
    /**
     * Successful Response
     */
    200: SubmissionV3InternalResponse;
};

export type PutSubmissionStatusResponse = PutSubmissionStatusResponses[keyof PutSubmissionStatusResponses];

export type PostTeamCandidatesFilterV3Data = {
    body: TeamCandidatesFilterPayloadV3;
    path: {
        /**
         * Team Uuid
         */
        team_uuid: string;
    };
    query?: never;
    url: '/api/v3/teams/{team_uuid}/candidates/filter';
};

export type PostTeamCandidatesFilterV3Errors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type PostTeamCandidatesFilterV3Error = PostTeamCandidatesFilterV3Errors[keyof PostTeamCandidatesFilterV3Errors];

export type PostTeamCandidatesFilterV3Responses = {
    /**
     * Successful Response
     */
    200: TeamCandidatesFilterResponse;
};

export type PostTeamCandidatesFilterV3Response = PostTeamCandidatesFilterV3Responses[keyof PostTeamCandidatesFilterV3Responses];
